import {
  AUDITOR_RULES,
  factRef,
  runAudit,
  type Advisory,
  type Severity,
  type SuggestedFix,
} from '@dl/auditor';
import {
  asCalendar,
  CalendarDefinitionSchema,
  formatDisplayDate,
  loadBundledCalendar,
  toDisplayDate,
  virtualInstances,
  type Calendar,
  type VirtualInstance,
} from '@dl/calendar';
import { project as projectChokepoint, memoryStore } from '@dl/projection';
import type {
  OpRecord,
  SubmittedOp,
  VisibilityAddedEnvelope,
  AuthoringAdvisory,
  AuthoringServerEvent,
} from '@dl/protocol';
import { AuthoringServerEventSchema, AuthoringCommitResponseSchema } from '@dl/protocol';
import {
  openBrowserReplica,
  SyncClient,
  type DraftWithConflict,
  type ReplicaLike,
} from '@dl/replica';
import { normalizeName } from '@dl/search';
import {
  AuditorRuleOverridesSchema,
  DEFAULT_AUDITOR_RULE_OVERRIDES,
  DEFAULT_FACT_OWNER,
  DEFAULT_FACT_STATUS,
  DEFAULT_LLM_PENDING_LANE_MODE,
  DEFAULT_PENDING_AUTO_RATIFY_POLICY,
  ENTITY_META_PREDICATE,
  PendingAutoRatifyPolicySchema,
  SeverityOverrideSchema,
  beliefOwner,
  beliefSegmentKey,
  splitSegmentKey,
  campaignViewers,
  type RevealTargetOption,
  currentFactValue,
  entityFacets,
  entityRevealRollup,
  entityAttachTrait,
  entityCreatePayload,
  entityDetachTrait,
  entitySoftDelete,
  entityRestore,
  factCreate,
  factEdit,
  factSetStatus,
  factSoftDelete,
  factSucceed,
  closeImpliedPredecessor,
  deriveImpliedSuccession,
  type DraftSegment,
  type ExistingSegment,
  type FactEditPayload,
  hasObjectUniquenessKey,
  factKey,
  foldBundles,
  BUNDLE_META_PREDICATE,
  type BundleRegistry,
  type BundleDepth,
  type BundleMode,
  type BundleSource,
  type BulkGenRequest,
  type BulkGenTarget,
  bundleDeclare,
  type FactReviewState,
  foldConfigOps,
  configSet,
  CAMPAIGN_CONFIG_SUBJECT,
  foldOpsIntoEntities,
  factInstanceKey,
  affineMap,
  foldPackOps,
  HOMEBREW_PACK_ID,
  isVocabId,
  llmAuthor,
  mintEntityId,
  mintVocabId,
  analyzeDirectedGraph,
  causalEdge,
  OFFICIAL_CORE_CAUSAL_PREDICATES,
  OFFICIAL_CORE_PREDICATE_IDS,
  RecurrenceRuleSchema,
  type RecurrenceRule,
  PACK_META_PREDICATE,
  packDefineDimension,
  packDefinePredicate,
  packDefineTrait,
  packDefineType,
  packDefineUnit,
  packRetireDimension,
  packRetirePredicate,
  packRetireTrait,
  packRetireType,
  packRetireUnit,
  revealEnvelopePredicate,
  revealFactPayload,
  splitFactKey,
  viewerKey as viewerKeyFn,
  type AiPromptExtensions,
  type ConfigState,
  type ConfigKey,
  type ConfigSetPayload,
  type RotationHistoryEntry,
  type DirectedEdge,
  type DimensionDefinition,
  type EntityRoot,
  type EntityState,
  type EnumValueDeclaration,
  type FactOwner,
  type FactState,
  type FactStatus,
  type FactValidity,
  type PackOpPayload,
  type UnitDefinition,
  type AuditorRuleOverrides,
  type PendingAutoRatifyPolicy,
  type SeverityOverrideValue,
  type PredicateDeclaration,
  type ResolvedFacet,
  type RevealSource,
  type TraitDeclaration,
  type TypeDefinition,
  type TupleFieldDeclaration,
  type TupleFieldKind,
  type ValueKind,
  type ViewerId,
  type AuthoringTarget,
  type TraitAuthoringArtifact,
  type ArtifactDiff,
  type ArtifactMemberChange,
  type ProposedTraitMember,
  type OpId,
} from '@dl/shared';
import {
  badge,
  bottomSheet,
  button as uiButton,
  type CalendarCursor,
  type CalendarDatePickerMessages,
  type CalendarDatePickerOverlay,
  card,
  dateField,
  type DateFieldMessages,
  emptyState,
  entityIcon,
  markdown,
  modal,
  pageHeader,
  sectionHeading,
  selectInput,
  statusPill,
  tabs,
  textArea,
  textInput,
  themeToggle,
  uiIcon,
} from '@dl/ui';
import {
  a,
  article,
  aside,
  branch,
  button,
  code,
  component,
  derived,
  div,
  each,
  footer,
  h1,
  h2,
  h3,
  h4,
  header,
  img,
  input,
  label,
  li,
  main,
  nav,
  ol,
  option,
  p,
  path,
  section,
  select,
  show,
  span,
  svg,
  text,
  time,
  ul,
  type EffectApi,
  type Mountable,
  type Renderable,
  type Send,
  type Signal,
} from '@llui/dom';

import { viewA11yPage } from './a11y-page.js';
import {
  INITIAL_ACCOUNT_MODEL,
  viewAccount,
  type AccountModel,
} from './account/account-view.js';
import { viewUserMenu } from './account/user-menu-view.js';
import { viewAdminShell } from './admin/admin-shell.js';
import { viewAdminAudit as viewAdminAuditImpl } from './admin/audit-view.js';
import { viewAdminLlm as viewAdminLlmImpl } from './admin/llm-view.js';
import { viewAdminSignups as viewAdminSignupsImpl } from './admin/signups-view.js';
import { viewAdminUsers as viewAdminUsersImpl } from './admin/users-view.js';
import {
  signOut as apiSignOut,
  confirmTotp,
  deleteAccount as apiDeleteAccount,
  fetchCurrentUser,
  markRecoveryDisclaimerSeen,
  requestPasswordReset,
  requestTotpDisable,
  setupTotp,
  updateRecoveryEmail,
} from './auth/api.js';
import { viewForgotPassword } from './auth/forgot-password-view.js';
import { viewResetPassword } from './auth/reset-password-view.js';
import { viewSignIn } from './auth/sign-in-view.js';
import { viewSignUp } from './auth/sign-up-view.js';
import { viewTwofaDisable } from './auth/twofa-disable-view.js';
import { viewVerifyEmail } from './auth/verify-email-view.js';
import { viewCampaignMembers, type CampaignMembersMsg } from './campaign-members.js';
import {
  listCampaigns as apiListCampaigns,
  type CampaignSummary,
} from './campaigns/api.js';
import {
  viewCampaignsPicker,
  enterCampaign,
  type CampaignsBoot,
} from './campaigns/picker-view.js';
import {
  clearActiveCampaignId,
  getActiveCampaignId,
  getPersistedCursorState,
  setActiveCampaignId,
  setPersistedCursor,
  touchLastActive,
} from './campaigns/storage.js';
import { viewCampaignSwitcher } from './campaigns/switcher-view.js';
import {
  type ActiveCalendarResolution,
  FALLBACK_CALENDAR_ID,
  resolveActiveCalendar,
  resolveActiveCalendarId,
} from './entities/active-calendar.js';
import { calendarDegradeNotice } from './entities/calendar-degrade-notice.js';
import { buildEntityChronology, type ChronologyEntry } from './entities/entity-chronology.js';
import {
  campaignUnitRegistry,
  facetTemplatesForTraits,
  formatNumberValueForDisplay,
  getTraitIndex,
  loadGmRegistry,
  predicateDefaultFacet,
  predicateIdsForTraits,
  predicateLabel,
  viewEntityFactRows,
  viewHistoryDialog,
  viewPlayerAuthorityFactRows,
  type FactRowHost,
} from './entities/fact-row-editor.js';
import { factIsEmptyAtPredicate, traitsAtId } from './entities/fact-row-state.js';
import { getHomebrewDefs, homebrewEpoch, setHomebrewDefs } from './entities/homebrew-defs.js';
import { entityMetaLine, pickCountForRoot, viewMultiRootEntityList } from './entities/list-view.js';
import { buildSegmentStrip } from './entities/segment-strip.js';
import {
  allTypeDeclarations,
  factCountForDetach,
  predicateUsage,
  traitLabel,
  traitsAvailableForRoot,
  traitUsage,
  viewTraitDetachConfirm,
  viewTraitPicker,
  viewTraitStrip,
  type TraitAttachHost,
  type TraitDetachConfirm,
} from './entities/trait-attach-view.js';
import { setUnitDisplayPrefs } from './entities/unit-display-prefs.js';
import { ALL_ENTITY_ROOTS } from './entity-roots.js';
import { t } from './i18n.js';
import {
  filterEntities,
  hasExactMatch as parsedHasExactMatch,
  parsePaletteQuery,
  quickCreateRoots,
  type PaletteResult,
  type ParsedPaletteQuery,
} from './palette/query.js';
import { viewRecoveryPage } from './recovery-page.js';
import {
  appRouteHref,
  appRouteLink,
  appRouteListener,
  appRouteNavigate,
  appRoutePush,
  initialRoute,
  isAuthRoute,
  isTableRoute,
  routeKey,
  routing,
  asSettingsTab,
  type AppRoute,
  type SettingsTab,
} from './router.js';
import { opChainKey, sequenceByKey } from './sequence-by-key.js';
import {
  buildCombatLogBundle,
  buildParticipantsTemplateValue,
  participantsFate,
  type CombatLogBundle,
  type ParticipantFate,
} from './session/combat-log.js';
import { roll, rollBreakdown, type DiceRoll, DiceParseError } from './session/dice.js';
import {
  addEntry,
  advanceTurn,
  applyHpDelta,
  mintEntryId,
  removeEntry,
  toggleCondition,
  EMPTY_INITIATIVE,
  type InitiativeEntry,
  type InitiativeState,
} from './session/initiative.js';
import {
  appendTurnLogEntry,
  buildConditionDescription,
  buildDamageDescription,
  buildEffectDescription,
  buildNarrationText,
  buildNpcActionDescription,
  buildReactionRollDescription,
  buildSaveDescription,
  buildTurnLogEntry,
  clearTurnLog,
  mintTurnLogEntryId,
  reactionDisposition,
  EMPTY_TURN_LOG,
  type NpcAction,
  type TurnLog,
  type TurnLogEntry,
} from './session/narrative-combat-panel.js';
import {
  designateFacilitator,
  handOffFacilitator,
  nextFairFacilitator,
  type RotationState,
} from './session/rotation.js';
import {
  blankWorkingCalendar,
  closeOpenCyclePayload,
  defineCalendarPayload,
  openCalendarForEdit,
  reduceWorkingCalendar,
  retireCalendarPayload,
  viewCalendarSettings,
  type CalendarEditorMsg,
  type CalendarEditorState,
  type OpenCycleHandle,
} from './settings/calendar-settings.js';
import { calendarChoices, viewCampaignSettings } from './settings/campaign-settings-view.js';
import {
  DEFAULT_TABLE_FOCUS,
  TABLE_CHANNEL_NAME,
  TABLE_RIPPLE_TTL_MS,
  parseTableBroadcast,
  reduceTableFocus,
  resolveDesignatedViewer,
  rowsForFocus,
  serialiseTableBroadcast,
  tableFocusEquals,
  type TableAttentionPointer,
  type TableAttentionRipple,
  type TableBroadcast,
  type TableFocus,
  type TableFocusAction,
  type TableProjectedRow,
} from './table-focus.js';
import { viewUiGallery } from './ui-gallery.js';
import { workerOrigin } from './worker-origin.js';

/**
 * Walking Skeleton client app — TEA via @llui/dom, URL-backed routing via
 * @llui/router, backed by the substrate (Replica + SyncClient).
 *
 * The op log inside the Campaign DO is the source of truth. The client's
 * `Model.entities` is a CACHE rebuilt by folding `replica.iterateOpLog()`
 * via `foldOpsIntoEntities`. Mutations build a `SubmittedOp`, hand it to
 * `SyncClient.submit()`, and rely on the substrate's `onOpApplied`
 * callback to re-fold and dispatch a `replica/entities-rebuilt` Msg.
 *
 * The `Replica` and `SyncClient` live at module scope so all components
 * can reach them from inside effect handlers (`appSend` is captured the
 * first time `onEffect` is invoked so async callbacks can dispatch
 * messages back into the loop).
 */

// ---------- module-scope substrate ----------

let replica:
  | (ReplicaLike & {
      getHead: (subject: string, predicate: string) => Promise<string | null>;
      iterateOpLog: () => Promise<OpRecord<unknown>[]>;
      getChainOps: (subject: string, predicate: string) => Promise<OpRecord<unknown>[]>;
    })
  | null = null;
let syncClient: SyncClient | null = null;
let appSend: ((msg: Msg) => void) | null = null;
let rebuildScheduled = false;

/**
 * #258 — the same-origin `BroadcastChannel` linking the GM main tab and the
 * Table View tab. A class instance, so it lives module-scope (never in `Model`
 * — the llui "no class instances in Model" constraint). Lazily opened the first
 * time either role needs it (`ensureTableChannel`). The GM tab POSTs already-
 * projected `TableBroadcast` frames; the Table tab subscribes and renders them.
 * `null` in non-browser (SSR / test) environments.
 */
let tableChannel: BroadcastChannel | null = null;
/** The most recent broadcast the GM tab emitted — re-sent when a Table tab says hello. */
let lastTableBroadcast: TableBroadcast | null = null;

// Bug 2 / Tier 7.12 — resolve the `?dl_replica=memory` test override at
// module-eval and PERSIST it in sessionStorage. The boot flow for a
// signed-in user with a campaign hard-redirects `/campaigns` → `/` (a full
// reload that strips the query string) BEFORE the substrate opens, so the
// flag is gone from the URL by the time `openSubstrate` runs — and even a
// module-eval snapshot is lost because the redirect re-evaluates the module
// on a query-less URL. sessionStorage survives the reload within the tab, so
// the override set on the first eval is still readable on the second.
const FORCE_MEMORY_REPLICA: boolean = (() => {
  try {
    const fromUrl = new URL(window.location.href).searchParams.get('dl_replica') === 'memory';
    if (fromUrl) sessionStorage.setItem('dl_replica', 'memory');
    return fromUrl || sessionStorage.getItem('dl_replica') === 'memory';
  } catch {
    return false;
  }
})();

/**
 * Substrate-open barrier. `openSubstrate` creates a fresh promise on
 * entry (or on each retry) and resolves it once both `replica` and
 * `syncClient` are set — or once the open failed and the substrate
 * banner has been flipped to `failed`. Effect handlers
 * (`submitFact`, `submitEntityCreate`, `submitTraitMutation`) await
 * this barrier before checking `replica`/`syncClient`, so a
 * user-initiated write that arrives during the boot race (the
 * modal-Save click before `openSubstrate` resolves) waits a tick
 * instead of falling into the "no substrate" early-return.
 *
 * Pre-fix shape: the click landed → `submitFact` saw `replica ===
 * null` → fired `fact-save-failed` with the "local storage is
 * unavailable" copy → user saw a hard error for what was really a
 * timing-only issue. The Playwright e2e (09-create-edit-navigate
 * 9.2) caught this consistently in headless Chromium where the
 * paint→click latency is microscopic.
 *
 * On failure, the barrier still resolves — handlers then see
 * `replica === null` and surface the same "could not save" error,
 * which is now the *correct* signal (substrate is genuinely
 * unavailable, not just opening).
 */
let resolveSubstrateReady: (() => void) | null = null;
const substrateReady: Promise<void> = new Promise<void>((resolve) => {
  resolveSubstrateReady = resolve;
});

function markSubstrateReady(): void {
  if (resolveSubstrateReady !== null) {
    resolveSubstrateReady();
    resolveSubstrateReady = null;
  }
}

/**
 * Race the substrate-open barrier against a short timeout. Effect
 * handlers that need `replica`/`syncClient` call this; if the barrier
 * resolves before the timeout the call returns immediately. The 5s
 * cap covers Chrome's worst-case OPFS open + sqlite-wasm
 * initialisation on a cold cache; longer than that and the user
 * deserves the "no substrate" error rather than a frozen UI.
 */
async function awaitSubstrate(): Promise<void> {
  await Promise.race([
    substrateReady,
    new Promise<void>((resolve) => setTimeout(resolve, 5_000)),
  ]);
}

const GM_AUTHOR = { kind: 'gm' } as const;

/**
 * Campaign id is resolved at boot from one of three sources,
 * in priority order:
 *   1. `?campaign=<id>` query string (deep-linking / dev overrides /
 *      Playwright harness via `clientUrl(campaignId)`) — an EXPLICIT
 *      deep link must win over a stale persisted choice, otherwise a
 *      shared `?campaign=<other>` link opens the previously-active
 *      campaign. The resolution is persisted so a later refresh without
 *      the query string lands on the same campaign.
 *   2. `localStorage['dl.lastCampaignId']` (persisted choice from a
 *      previous picker entry or last-active campaign)
 *   3. `window.__DL_CAMPAIGN_ID__` global (server-rendered shell)
 *
 * Returns `null` when none of the three resolve. The boot path then
 * fetches `GET /v1/account` and routes to the picker.
 *
 * The legacy `'demo'` fallback that pre-dated the Account+Campaign
 * split is gated behind `VITE_LEGACY_GM_SESSION=true` so Walking-Skeleton
 * tests keep running while production builds require a real campaign.
 */
export function resolveCampaignId(): string | null {
  if (typeof window === 'undefined') {
    return LEGACY_GM_SESSION_ENABLED ? 'demo_campaign' : null;
  }
  const url = new URL(window.location.href);
  const fromQuery = url.searchParams.get('campaign');
  if (fromQuery && fromQuery.length > 0) {
    // An explicit deep link wins over the persisted choice. Persist it so
    // a refresh without the query string lands on the same campaign.
    setActiveCampaignId(fromQuery);
    return fromQuery;
  }
  const fromStorage = getActiveCampaignId();
  if (fromStorage && fromStorage.length > 0) return fromStorage;
  const fromGlobal = (window as unknown as { __DL_CAMPAIGN_ID__?: string }).__DL_CAMPAIGN_ID__;
  if (fromGlobal && fromGlobal.length > 0) return fromGlobal;
  if (LEGACY_GM_SESSION_ENABLED) return 'demo_campaign';
  return null;
}

/**
 * Mutable campaign id. Surfaced via `currentCampaignId()`. Most code
 * reads the live value; tests and the substrate handshake read it once
 * and lock in for the session (we reload on campaign switch so the
 * stale cache is wiped).
 */
let CAMPAIGN_ID: string | null = null;
function currentCampaignId(): string | null {
  return CAMPAIGN_ID;
}

/**
 * III.B.1 (#65) — `Session` is the identity-driven app shell context.
 * `bootSession()` populates it from `GET /v1/me/campaigns/:campaignId/session`
 * (or falls back to a GM identity for the legacy `demo` campaign so
 * Walking-Skeleton tests keep running).
 */
export interface Session {
  readonly userId: string | null;
  readonly userEmail: string | null;
  readonly viewer: ViewerId;
  readonly campaignRole: 'owner' | 'admin' | 'member' | null;
  readonly controlledCharacters: readonly string[];
  /** Convenience: derived from `viewer.kind === 'gm'`. */
  readonly mode: 'gm' | 'player';
  /** System admin (users.is_system_admin) — gates the Admin link + /admin/llm. */
  readonly isSystemAdmin: boolean;
}

const LEGACY_GM_SESSION: Session = {
  userId: null,
  userEmail: null,
  viewer: { kind: 'gm', id: 'self' },
  campaignRole: 'owner',
  controlledCharacters: [],
  mode: 'gm',
  isSystemAdmin: false,
};

/**
 * #117 — when this build-time env var is `true`, the boot path keeps
 * the LEGACY_GM_SESSION fallback alive on 401. Otherwise an
 * unauthenticated boot redirects to `/sign-in`. The `demo` campaign
 * always bypasses real auth so the Walking-Skeleton fixture flow
 * survives even with real auth enforced.
 */
const LEGACY_GM_SESSION_ENABLED: boolean = ((): boolean => {
  const env = (import.meta as unknown as { env?: { VITE_LEGACY_GM_SESSION?: string } }).env ?? {};
  return env.VITE_LEGACY_GM_SESSION === 'true';
})();

// Resolve once at module load — must come AFTER the env-flag eval above
// because `resolveCampaignId` reads `LEGACY_GM_SESSION_ENABLED` for the
// SSR / no-env fallback. The campaign switcher overwrites this before
// reloading the page, so the value the rest of the module captures is
// always the current session's campaign id.
CAMPAIGN_ID = resolveCampaignId();

function scheduleRebuild(): void {
  if (rebuildScheduled || !replica || !appSend) return;
  rebuildScheduled = true;
  // Coalesce bursts of op_push (initial backfill) into one fold.
  queueMicrotask(() => {
    rebuildScheduled = false;
    void rebuildEntities();
  });
}

/**
 * LLM-never-canon invariant. Rebuild the per-fact status timeline that the
 * packages/shared fold discards (`FactVersion` retains no per-version status;
 * `fact.set_status` pushes no version), and capture, per fact chain, the last
 * value the fact held while its folded status was `canon`. That is the value a
 * later draft/pending SHADOWED — the ONLY value `reject-draft` may restore to
 * canon. Rejecting a fact with no prior canon returns no entry (⇒ soft-delete).
 *
 * Client-only derived cache; the op log stays canonical. Mirrors the fold's
 * fact-key derivation exactly (`factInstanceKey`) so repeatable-fact instances
 * key identically to `entities[*].facts`.
 */
export function computeShadowedCanon(
  ops: readonly { subject: string; predicate: string; instance?: string; payload: unknown }[],
): Record<string, Record<string, { value: unknown }>> {
  const KEY_SEP = ' ';
  const chains = new Map<
    string,
    { value: unknown; status: FactStatus; canon?: { value: unknown } }
  >();
  for (const op of ops) {
    const payload = op.payload as { kind?: unknown; value?: unknown; status?: FactStatus } | null;
    const kind = payload?.kind;
    if (typeof kind !== 'string' || !kind.startsWith('fact.')) continue;
    const key = `${op.subject}${KEY_SEP}${factInstanceKey(op.predicate, op.instance)}`;
    let chain = chains.get(key);
    if (!chain) {
      chain = { value: undefined, status: DEFAULT_FACT_STATUS };
      chains.set(key, chain);
    }
    switch (kind) {
      case 'fact.create':
        chain.value = payload?.value;
        chain.status = payload?.status ?? DEFAULT_FACT_STATUS;
        break;
      case 'fact.edit':
        chain.value = payload?.value;
        if (payload?.status !== undefined) chain.status = payload.status;
        break;
      case 'fact.set_status':
        if (payload?.status !== undefined) chain.status = payload.status;
        break;
      // fact.soft_delete / fact.restore leave value + canon snapshot untouched.
    }
    if (chain.status === 'canon') chain.canon = { value: chain.value };
  }
  const out: Record<string, Record<string, { value: unknown }>> = {};
  for (const [key, chain] of chains) {
    if (!chain.canon) continue;
    const sep = key.indexOf(KEY_SEP);
    const subject = key.slice(0, sep);
    const factKey = key.slice(sep + 1);
    (out[subject] ??= {})[factKey] = chain.canon;
  }
  return out;
}

async function rebuildEntities(): Promise<void> {
  if (!replica || !appSend) return;
  const ops = await replica.iterateOpLog();
  // ADR-0001 replay ordering: fold the homebrew Pack's `__pack`
  // definition ops FIRST so the registry/trait-index overlay is current
  // before the entity fold's predicate lookups run. `setHomebrewDefs`
  // bumps the homebrew epoch, invalidating the registry/trait memos that
  // the entity-page + trait-picker views read on the next commit.
  setHomebrewDefs(foldPackOps(ops));
  // Predicate ids are opaque and immutable (ADR-0004 §1 / #175): a fact
  // always folds onto its own id, so there is no read-time alias map.
  const entities = foldOpsIntoEntities(ops);
  // Campaign settings (#171/#180, ADR-0004 §5) fold from the same op log
  // into a disjoint "config" bucket — the `canonical_calendar` pointer here
  // drives the campaign-date cursor's active calendar (derived in the
  // reducer so the cursor reset stays in the pure update path).
  const config = foldConfigOps(ops);
  // Unit display preferences (#249, 08-units.md §5): push the campaign-default
  // map (a setting pointer, LWW) into the module-scope render holder so the
  // read-only number+unit chokepoint (`formatNumberValueForDisplay`) renders in
  // the preferred unit. The per-user override is User-scope (localStorage),
  // resolved over this default — never the op log. Canonical values untouched.
  setUnitDisplayPrefs(currentCampaignId(), config.unit_display_defaults ?? {});
  // #251 — fold the suggestion-set bundle registry from the SAME op log (the
  // bundle.declare meta-ops + every bundle-tagged fact member). A rebuildable
  // view; bundle open/resolved state is derived from member statuses.
  const bundles = foldBundles(ops);
  // LLM-never-canon: rebuild the shadowed-canon map from the same op log so
  // reject-draft can restore the last ratified value (not a positional guess).
  const shadowedCanon = computeShadowedCanon(ops);
  appSend({ type: 'replica/entities-rebuilt', entities, config, bundles, shadowedCanon });
}

/**
 * 226.1 (#245) — compute the Preview-as projection for a selected non-GM
 * Viewer by running the local replica op log through the `@dl/projection`
 * chokepoint (`project(viewer, t, store)`) — the SAME function the Worker /
 * DO use for the player stream. The GM sees EXACTLY what that Viewer's
 * projection reveals at the effective cursor; switching Viewers re-runs this.
 *
 * Phantom-Viewer invariant: this is a pure CONSUMER of the chokepoint. The
 * only `VisibleFact[]` instances originate inside `project()`; here we read
 * them by value and map to value-only `ProjectedFactRow`s. The client never
 * constructs a `VisibleFact`, never re-implements the reveal/belief filter, and
 * never widens visibility — a non-GM preview shows no unrevealed fact.
 *
 * The store mirrors the DO's `memoryStore(ops)` (sync-handler.ts), additionally
 * supplying the bundled-pack `uniqueness_key`s + the cursor so valid-time
 * succession resolves to the right segment (the DO leans on the homebrew-fold
 * fallback; the client has the full registry, so it can be exact).
 */
async function computePreviewProjection(
  effect: ComputePreviewProjectionEffect,
  send: (msg: Msg) => void,
): Promise<void> {
  if (!replica) return;
  const ops = await replica.iterateOpLog();
  // Refresh the homebrew/registry overlay from THIS op log before reading
  // uniqueness keys (a homebrew `pack.define_predicate` may declare one).
  setHomebrewDefs(foldPackOps(ops));
  const uniquenessKeys: Record<string, readonly string[]> = {};
  for (const decl of loadGmRegistry().list()) {
    if (decl.uniqueness_key !== undefined) uniquenessKeys[decl.id] = decl.uniqueness_key;
  }
  const rows = projectPreviewRows(effect.viewer, effect.day, ops, uniquenessKeys);
  send({
    type: 'preview-projection-computed',
    viewerKey: viewerKeyFn(effect.viewer),
    rows,
  });
}

/**
 * #258 — open (lazily) the same-origin Table View `BroadcastChannel`. Returns
 * `null` outside a browser (SSR / test). The Table tab attaches an `onmessage`
 * handler in {@link initTableChannel}; the GM tab only POSTs.
 */
function ensureTableChannel(): BroadcastChannel | null {
  if (tableChannel !== null) return tableChannel;
  if (typeof BroadcastChannel === 'undefined') return null;
  try {
    tableChannel = new BroadcastChannel(TABLE_CHANNEL_NAME);
  } catch {
    tableChannel = null;
  }
  return tableChannel;
}

/**
 * #258 — wire THIS tab's role onto the Table View channel.
 *
 *   - `table` role (the chrome-stripped renderer): subscribe to `TableBroadcast`
 *     frames (→ `table/broadcast-received`) and announce a `table-hello` so the
 *     GM tab re-broadcasts the current state to a freshly-opened display.
 *   - `gm` role: open the channel and listen for `table-hello` so a Table tab
 *     that opens AFTER the GM gets the current state immediately. The GM never
 *     adopts a broadcast (it owns the live replica).
 */
function initTableChannel(role: 'gm' | 'table', send: (msg: Msg) => void): void {
  const channel = ensureTableChannel();
  if (!channel) return;
  channel.onmessage = (event: MessageEvent): void => {
    const parsed = parseTableBroadcast(event.data);
    if (parsed === null) return;
    if (role === 'table') {
      if (parsed.kind === 'table-update') {
        send({ type: 'table/broadcast-received', broadcast: parsed });
      }
    } else {
      // GM role: a Table tab said hello → re-broadcast the last state so a
      // late-opened display catches up without waiting for the next edit.
      if (parsed.kind === 'table-hello' && lastTableBroadcast !== null) {
        channel.postMessage(serialiseTableBroadcast(lastTableBroadcast));
      }
    }
  };
  if (role === 'table') {
    channel.postMessage(serialiseTableBroadcast({ v: 1, kind: 'table-hello' }));
  }
}

/**
 * #258 — compute the designated-Viewer projection and broadcast it to the Table
 * View tab. Reads the local replica op log, runs it through the SAME chokepoint
 * helper as Preview-as (`projectPreviewRows` → `project()` in `@dl/projection`),
 * narrows to the current focus, and posts a `TableBroadcast`. The rows are
 * already reveal-filtered to the designated Viewer — the Table tab re-projects
 * nothing. A `null` designated Viewer broadcasts an empty set (placeholder,
 * never GM-truth).
 */
async function broadcastTable(effect: BroadcastTableEffect): Promise<void> {
  const channel = ensureTableChannel();
  if (!channel) return;
  let rows: TableProjectedRow[] = [];
  if (effect.designatedViewer !== null && replica) {
    const ops = await replica.iterateOpLog();
    setHomebrewDefs(foldPackOps(ops));
    const uniquenessKeys: Record<string, readonly string[]> = {};
    for (const decl of loadGmRegistry().list()) {
      if (decl.uniqueness_key !== undefined) uniquenessKeys[decl.id] = decl.uniqueness_key;
    }
    // THE chokepoint (via projectPreviewRows). The map is already reveal-
    // filtered for the designated Viewer; map to plain wire rows.
    const projected = projectPreviewRows(
      effect.designatedViewer,
      effect.day,
      ops,
      uniquenessKeys,
    );
    const registry = loadGmRegistry();
    const allRows: TableProjectedRow[] = Object.values(projected).map((r) => {
      const row: TableProjectedRow = {
        subject: r.subject,
        predicate: r.predicate,
        value: r.value,
      };
      if (r.pending === true) row.pending = true;
      // Asset / image facts carry an opaque asset id as their value; attach the
      // GM tab's already-resolved signed URL so the Table tab can render the
      // image without a replica / asset store of its own.
      const kind = registry.lookup(r.predicate)?.value_kind[0];
      if ((kind === 'asset' || kind === 'image') && typeof r.value === 'string') {
        const url = effect.assetUrls[r.value];
        if (url !== undefined) row.assetUrl = url;
      }
      return row;
    });
    rows = rowsForFocus(effect.focus, allRows);
  }
  const broadcast: TableBroadcast = {
    v: 1,
    kind: 'table-update',
    focus: effect.focus,
    designatedViewerNull: effect.designatedViewer === null,
    viewerLabel: effect.viewerLabel,
    rows,
  };
  // #271 — carry current attention signals in the full broadcast.
  if (effect.attentionPointer !== undefined) broadcast.attentionPointer = effect.attentionPointer;
  if (effect.attentionRipple !== undefined) broadcast.attentionRipple = effect.attentionRipple;
  lastTableBroadcast = broadcast;
  channel.postMessage(serialiseTableBroadcast(broadcast));
  // Same-tab side-by-side mode: the Table renderer in THIS window listens to
  // the in-process model, not the channel — but if a `/table` view is mounted
  // in the same tab, it reads `tableBroadcast` too. Dispatch locally so the
  // single-window split renders without a second tab. (BroadcastChannel does
  // not loop a message back to the sender.)
  if (appSend) appSend({ type: 'table/broadcast-received', broadcast });
}

/**
 * #271 — fast-path attention broadcast: post only the pointer/ripple without
 * re-reading the op log. Merges into `lastTableBroadcast` so a late-opening
 * Table tab also gets the current attention state via the `table-hello` path.
 */
function broadcastAttention(effect: BroadcastAttentionEffect): void {
  const channel = ensureTableChannel();
  if (!channel) return;
  // Merge the new attention fields into the last known full broadcast so the
  // Table tab receives a complete, correct frame (rows + attention together).
  const base = lastTableBroadcast ?? {
    v: 1 as const,
    kind: 'table-update' as const,
    focus: { kind: 'viewer' as const },
    designatedViewerNull: true,
    viewerLabel: '',
    rows: [],
  };
  const broadcast: TableBroadcast = { ...base };
  if (effect.attentionPointer !== null) {
    broadcast.attentionPointer = effect.attentionPointer;
  } else {
    delete broadcast.attentionPointer;
  }
  if (effect.attentionRipple !== null) {
    broadcast.attentionRipple = effect.attentionRipple;
  } else {
    delete broadcast.attentionRipple;
  }
  lastTableBroadcast = broadcast;
  channel.postMessage(serialiseTableBroadcast(broadcast));
  if (appSend) appSend({ type: 'table/broadcast-received', broadcast });
}

/**
 * 226.1 (#245) — pure Preview-as projection: run `ops` through the
 * `@dl/projection` chokepoint for `viewer` at `day`, then map the returned
 * `VisibleFact[]` to value-only rows keyed by `factKey`. Exported for tests
 * (the effect above wraps it with the live replica + registry); a pure helper
 * lets the phantom-Viewer guarantee be asserted without a substrate.
 *
 * Phantom-Viewer: the ONLY `VisibleFact` instances come from `project()` inside
 * `@dl/projection`; this helper constructs none, re-implements no reveal filter,
 * and therefore cannot widen visibility beyond what the Viewer is revealed.
 */
export function projectPreviewRows(
  viewer: ViewerId,
  day: number | null,
  ops: OpRecord<unknown>[],
  uniquenessKeys: Record<string, readonly string[]> = {},
): Record<string, ProjectedFactRow> {
  const store = memoryStore(ops, { currentCanonicalDay: day, uniquenessKeys });
  // THE chokepoint. `day` is the effective in-world `t` (browse cursor, else
  // session now, else null = timeless). Never widens past what the Viewer holds.
  const visible = projectChokepoint(viewer, day, store);
  const rows: Record<string, ProjectedFactRow> = {};
  for (const fact of visible) {
    const row: ProjectedFactRow = {
      subject: fact.subject,
      predicate: fact.predicate,
      value: fact.value,
      ...(fact.pending === true ? { pending: true as const } : {}),
    };
    rows[factKey(fact.subject, fact.predicate)] = row;
  }
  return rows;
}

// Sync-activity signal for the "Syncing…" indicator. Server ops stream
// in as op_push frames during initial backfill / reconnect catch-up;
// onOpApplied fires per op. We dispatch exactly two Msgs per burst —
// `sync/active` on the first op, then `sync/settled` after a short idle
// with no further ops — rather than one Msg per op (a 367-op backfill
// would otherwise thrash the reducer).
let syncActiveFlag = false;
let syncIdleTimer: ReturnType<typeof setTimeout> | null = null;
function signalSyncActivity(send: (msg: Msg) => void): void {
  if (!syncActiveFlag) {
    syncActiveFlag = true;
    send({ type: 'sync/active' });
  }
  if (syncIdleTimer) clearTimeout(syncIdleTimer);
  syncIdleTimer = setTimeout(() => {
    syncActiveFlag = false;
    syncIdleTimer = null;
    send({ type: 'sync/settled' });
  }, 700);
}

// ---------- State + Messages ----------

interface ConnectionState {
  online: boolean;
  pendingCount: number;
  conflictCount: number;
}

interface ProjectedFactRow {
  subject: string;
  predicate: string;
  value: unknown;
  pending?: true;
  /** Chain head op-id — used as `base_version` for conflict-aware player edits (§7.2). */
  version?: string;
}

interface PrivateNoteState {
  body: string;
  updatedAt: string | null;
  dirty: boolean;
  /** Whether the load fetched a row (vs returned the default empty). */
  loaded: boolean;
}

/**
 * #73 — one predicate field being authored in the trait editor. The
 * MVP covers the value kinds that need no extra config (`enum_values` /
 * ref-root scoping are a follow-up); `ref` is authored as any-root.
 */
export interface TraitEditorField {
  /**
   * Stable synthetic key for `h.each` reconciliation. The user-facing
   * `id` changes on every keystroke, so it can't key the row — a stable
   * key keeps the input mounted (focus preserved) while typing.
   */
  key: number;
  /** Predicate id slug (e.g. `mood`). */
  id: string;
  valueKind: ValueKind;
  cardinality: 'single' | 'many';
  /**
   * Comma-separated choices, used only when `valueKind === 'enum'`.
   * Parsed into `enum_values` on submit (#76). Optional — defaults to ''.
   */
  enumValues?: string;
  /**
   * Edit-mode provenance for enum options — the existing `{id, label}` decls,
   * in declaration order, as loaded from the folded overlay. Opaque `enm_` ids
   * are minted ONCE (ADR-0004 §1): on a re-save we reuse the existing id
   * positionally (a renamed option keeps its id and just updates its label),
   * minting a fresh id only for a genuinely new option. Without this every
   * re-save re-minted every option's id and orphaned every fact that stored
   * the old id. Undefined for freshly-added fields.
   */
  originalEnumValues?: readonly EnumValueDeclaration[];
  /**
   * Optional ref-root restriction, used only when `valueKind === 'ref'`.
   * Empty string = any root; otherwise sets the predicate's `ref_root`.
   * Multi-root (`ref_roots`) authoring is a future enhancement (#76).
   */
  refRoot?: '' | EntityRoot;
  /**
   * Optional reciprocal (inverse) label, used only when
   * `valueKind === 'ref'` (#backlinks) — e.g. `holds` → "held by". Drives
   * how this relationship reads as a backlink on the target's Connections
   * tab. Empty = none (backlinks fall back to a generic label).
   */
  reciprocal?: string;
  /**
   * Optional measurement dimension, used only when `valueKind === 'number'`.
   * Empty = unitless; otherwise the predicate carries `dimension` so the
   * per-fact editor offers that dimension's units (ADR-0002).
   */
  dimension?: string;
  /**
   * Optional per-state labels, used only when `valueKind === 'boolean'`
   * (e.g. "Walled" / "Open" instead of a bare checkbox). Either may be
   * blank; when both are blank no `boolean_labels` is emitted.
   */
  trueLabel?: string;
  falseLabel?: string;
  /**
   * Edit-mode provenance — set only for fields that already existed when
   * the editor opened (undefined for fields the GM adds in this session).
   * Drive the safe-edit guards (#73): the value kind is locked, the
   * cardinality may only widen, and a changed `id` is a rename that
   * carries `originalId` into the predicate's `previous_names` so facts
   * written under the old name re-home (see buildTraitDefinitionOps).
   */
  originalId?: string;
  originalKind?: ValueKind;
  originalCardinality?: 'single' | 'many';
  /** The existing predicate's accumulated previous_names (for rename). */
  previousNames?: readonly string[];
  /**
   * Sub-fields, used only when `valueKind === 'tuple'` (ADR-0002). Each is
   * a named field of a constrained kind (the Government role-per-entity
   * case: holder=ref, role=enum, note?=text). Parsed into the predicate's
   * `tuple_fields` on submit.
   */
  tupleFields?: TupleEditorField[];
  /**
   * True when this member's predicate id did NOT resolve in the folded
   * homebrew overlay (a dangling reference — e.g. a predicate an earlier
   * buggy commit never persisted). The row renders read-only "⚠ unresolved"
   * rather than a raw-id-as-name + default-Text field, and a save preserves
   * the reference (`originalId`) WITHOUT re-emitting a bogus define op.
   */
  unresolved?: boolean;
}

/**
 * One sub-field of a tuple value-kind in the trait editor (ADR-0002). A
 * flattened mirror of `TupleFieldDeclaration` — `enumValues` is the raw
 * comma-separated string (parsed on submit), `refRoot` the optional single
 * root restriction.
 */
export interface TupleEditorField {
  key: number;
  name: string;
  valueKind: TupleFieldKind;
  optional: boolean;
  enumValues?: string;
  refRoot?: '' | EntityRoot;
  /**
   * Edit-mode provenance — the existing sub-field's opaque `fld_` id, carried
   * so a re-save reuses it. The stored tuple record keys by this id
   * (predicate.ts), so re-minting on every save orphaned every tuple fact.
   * Undefined for sub-fields the GM adds in this session.
   */
  originalId?: string;
  /** Edit-mode provenance for this sub-field's enum options (see above). */
  originalEnumValues?: readonly EnumValueDeclaration[];
}

/**
 * #73 / ADR-0001 — trait-editor form state. Null when the editor is
 * closed. `forRoot` seeds the new trait's `applies_to` (the root of the
 * entity whose picker opened the editor). On submit the form assembles a
 * `pack.define_predicate` op per field plus one `pack.define_trait` op.
 */
export interface TraitEditorState {
  /** `create` defines a brand-new trait; `edit` revises an existing one. */
  mode: 'create' | 'edit';
  /**
   * Seed root for `applies_to` when the editor was opened from an entity's
   * trait picker (the entity's own root). Still used as the fallback when
   * `appliesTo` is empty so existing call-sites keep working.
   */
  forRoot: EntityRoot;
  /**
   * The roots this trait applies to. On the standalone settings page the GM
   * picks one-or-more roots explicitly; `buildTraitDefinitionOps` emits these
   * as the trait's `applies_to`. When absent/empty the build falls back to
   * `[forRoot]` (the picker-seeded single root).
   */
  appliesTo?: EntityRoot[];
  /**
   * The GM-typed trait **name** (ADR-0004 §1) — the display label, freely
   * duplicable. NOT the identity: the trait's opaque `trait_…` id is minted
   * on submit (create) or carried in `traitOriginalId` (edit). Kept as
   * `traitId` to avoid churning every call site that seeds the editor.
   */
  traitId: string;
  /**
   * The existing trait's opaque `trait_…` id in edit mode (ADR-0004 §1).
   * Undefined in create mode; the build mints a fresh id then. Identity is
   * decoupled from the editable name above.
   */
  traitOriginalId?: string;
  fields: TraitEditorField[];
  /** Monotonic source of stable field keys (pure — derived from state). */
  nextFieldKey: number;
  /**
   * #77 — explicit "I accept the risk" acknowledgement that unlocks the
   * otherwise-blocked lossy edits (changing an existing field's value
   * kind, narrowing its cardinality). Off by default; toggled in edit
   * mode. When on, the editor shows a persistent warning and the build
   * permits the change as a normal define op.
   */
  allowRisky: boolean;
  error: string | null;
}

/**
 * Pending schema-vocabulary retire, awaiting GM confirmation because the
 * definition is still in use. Mirrors `TraitDetachConfirm` but at the
 * vocabulary level (a trait/predicate *definition*, not one entity's
 * attachment). `entityCount` is always 0 for the `predicate` target —
 * predicates are referenced by facts, not attached to entities.
 */
export interface SchemaRetireConfirm {
  target: 'trait' | 'predicate';
  /** Opaque id of the definition being retired (`trait_…` / `pred_…`). */
  id: string;
  /** Resolved display label, snapshotted so the dialog reads cleanly. */
  label: string;
  /** Live entities currently attaching the trait (0 for predicates). */
  entityCount: number;
  /** Live facts referencing the definition's predicate(s). */
  factCount: number;
  /**
   * True when the retire was triggered from the inline trait editor's
   * delete button — on confirm we also close the editor (the settings-list
   * retire leaves no editor open, so it's false there).
   */
  fromEditor: boolean;
}

/** A fresh, empty predicate field with the given stable key. */
function emptyTraitEditorField(key: number): TraitEditorField {
  return { key, id: '', valueKind: 'text', cardinality: 'single', enumValues: '', refRoot: '' };
}

/**
 * Hydrate a `tuple` predicate's stored `tuple_fields` (ADR-0002) into the
 * trait editor's flat `TupleEditorField[]`, so editing an existing record
 * predicate shows ALL its previously-defined sub-fields — required AND
 * optional. Inverse of the submit-side build in `buildTraitDefinitionOps`
 * (the editor re-mints opaque ids on save). Returns `undefined` for non-tuple
 * or field-less declarations. Pure (exported for unit tests).
 */
export function tupleEditorFieldsFromDecl(
  decl: PredicateDeclaration | undefined,
): TupleEditorField[] | undefined {
  if (!decl || decl.value_kind[0] !== 'tuple' || !decl.tuple_fields) return undefined;
  return decl.tuple_fields.map((sub, j) => ({
    key: j,
    name: resolveLocalizedDisplay(sub.label),
    valueKind: sub.value_kind,
    optional: sub.optional ?? false,
    enumValues: sub.enum_values?.map((v) => resolveLocalizedDisplay(v.label)).join(', ') ?? '',
    refRoot: sub.ref_roots?.[0] ?? '',
    // Carry the opaque `fld_`/`enm_` ids so a re-save reuses them (ADR-0004 §1)
    // rather than orphaning existing tuple facts keyed by those ids.
    originalId: sub.id,
    ...(sub.enum_values ? { originalEnumValues: sub.enum_values } : {}),
  }));
}

/**
 * #73 — build an edit-mode `TraitEditorState` prefilled from an existing
 * homebrew trait definition (its fields carry `original*` provenance so
 * the safe-edit guards apply). Returns null when the id isn't a known
 * homebrew trait (core traits aren't editable). Reads the module-scope
 * homebrew overlay, which the op-log fold keeps in sync.
 */
function traitEditorStateForExisting(traitId: string): TraitEditorState | null {
  const defs = getHomebrewDefs();
  const trait = defs.traits.find((tr) => tr.id === traitId);
  if (!trait) return null;
  const fields: TraitEditorField[] = trait.predicates.map((pid, i) => {
    const decl = defs.predicates.find((p) => p.id === pid);
    const valueKind: ValueKind = decl?.value_kind[0] ?? 'text';
    // The editor's 'many' affordance now maps to the ADR-0005 {object}
    // uniqueness key (one segment per distinct value); 'single' = no key.
    const cardinality: 'single' | 'many' = hasObjectUniquenessKey(decl) ? 'many' : 'single';
    // Hydrate the structured-record sub-fields (ADR-0002) so editing an
    // existing `tuple` predicate shows its previously-defined fields rather
    // than an empty record (and a re-save preserves them — without this the
    // round-trip dropped every field, optional ones included).
    const tupleFields = tupleEditorFieldsFromDecl(decl);
    return {
      key: i,
      // The editable field shows the predicate's display NAME (ADR-0004 §1);
      // the opaque id is carried in `originalId` so the rebuild re-uses it.
      // A dangling reference (no decl) shows a clear "unresolved" label rather
      // than the raw opaque id masquerading as a name.
      id: decl === undefined ? t('trait_editor.field_unresolved') : resolveLocalizedDisplay(decl.label) || pid,
      valueKind,
      cardinality,
      // Enum options carry opaque ids now — prefill the comma-list with
      // their display names, and carry the existing `{id, label}` decls so a
      // re-save reuses the opaque `enm_` ids (ADR-0004 §1) instead of
      // orphaning every fact that stored them.
      enumValues: decl?.enum_values?.map((v) => resolveLocalizedDisplay(v.label)).join(', ') ?? '',
      ...(decl?.enum_values ? { originalEnumValues: decl.enum_values } : {}),
      // Single-root prefill; a multi-root predicate shows its first root
      // (the editor authors a single root — #76).
      refRoot: decl?.ref_root ?? decl?.ref_roots?.[0] ?? '',
      reciprocal: decl?.reciprocal ?? '',
      originalId: pid,
      originalKind: valueKind,
      originalCardinality: cardinality,
      // A dangling reference — the predicate isn't in the folded overlay.
      unresolved: decl === undefined,
      previousNames: decl?.previous_names ?? [],
      // Preserve the measurement dimension (opaque `dim_` id) when editing an
      // existing number predicate, so a re-save doesn't drop it (ADR-0007).
      ...(decl?.dimension ? { dimension: decl.dimension } : {}),
      ...(tupleFields ? { tupleFields } : {}),
    };
  });
  return {
    mode: 'edit',
    forRoot: trait.applies_to[0] ?? 'location',
    appliesTo: [...trait.applies_to],
    // The editable field shows the trait's display NAME; the opaque id is
    // carried separately so the rebuild re-uses it (ADR-0004 §1).
    traitId: resolveLocalizedDisplay(trait.label) || trait.id,
    traitOriginalId: trait.id,
    fields: fields.length > 0 ? fields : [emptyTraitEditorField(0)],
    nextFieldKey: fields.length,
    allowRisky: false,
    error: null,
  };
}

/** Predicate/trait id grammar — lowercase slug, leading letter. */
const TRAIT_EDITOR_SLUG_RE = /^[a-z][a-z0-9_]*$/;

/**
 * Parse the trait editor's comma-separated enum-option string into
 * `EnumValueDeclaration[]`, reusing existing opaque `enm_` ids POSITIONALLY.
 *
 * Opaque vocab ids are minted ONCE (ADR-0004 §1): the fact stores the id, so
 * re-minting on every trait re-save silently orphaned every existing fact. We
 * reuse the id of the original option at the same index (a renamed option
 * keeps its id and just updates its label); a genuinely new option (beyond the
 * originals) gets a freshly minted id. Returns [] when the string is empty
 * (the caller validates non-emptiness).
 */
export function buildEnumValueDeclarations(
  raw: string | undefined,
  original: readonly EnumValueDeclaration[] | undefined,
): EnumValueDeclaration[] {
  const names = (raw ?? '')
    .split(',')
    .map((v) => v.trim())
    .filter((v) => v.length > 0);
  return names.map((n, i) => {
    const prior = original?.[i];
    return { id: prior ? prior.id : mintVocabId('enum'), label: { en: n } };
  });
}

/**
 * #73 — validate the trait-editor form and assemble the homebrew Pack
 * ops: one `pack.define_predicate` per field (predicates first, per
 * ADR-0001 replay ordering) followed by the `pack.define_trait`. Pure so
 * the reducer stays testable. `ref` fields are authored as any-root for
 * the MVP (ref-root scoping + enum values are a follow-up).
 */
export function buildTraitDefinitionOps(
  state: TraitEditorState,
): { ok: true; payloads: PackOpPayload[] } | { ok: false; error: string } {
  // The GM types a display NAME (ADR-0004 §1); identity is an opaque minted
  // id, not the typed string. Names are freely duplicable, so the only
  // requirement is non-empty. The trait's opaque id is reused in edit mode
  // (`traitOriginalId`) or minted fresh on create.
  const traitName = state.traitId.trim();
  if (traitName.length === 0) {
    return { ok: false, error: t('trait_editor.error.trait_id') };
  }
  const traitId = state.traitOriginalId ?? mintVocabId('trait');
  if (state.fields.length === 0) {
    return { ok: false, error: t('trait_editor.error.no_fields') };
  }
  // Within ONE trait we still reject two fields with the same name (a UX
  // guard so the form is unambiguous) — but the name is no longer identity.
  const seen = new Set<string>();
  // Opaque predicate ids (minted or reused) in trait-declaration order.
  const predicateIds: string[] = [];
  const predicates: PredicateDeclaration[] = [];
  for (const field of state.fields) {
    // A dangling member (its predicate never resolved) is preserved by
    // reference only — keep the id in the trait's `predicates`, but do NOT
    // rebuild a define_predicate for it (we don't know its real declaration;
    // re-emitting would overwrite it with a bogus Text predicate). The GM
    // removes it explicitly via the row's delete button.
    if (field.unresolved && field.originalId !== undefined) {
      predicateIds.push(field.originalId);
      continue;
    }
    const name = field.id.trim();
    if (name.length === 0) {
      return { ok: false, error: t('trait_editor.error.field_id', { id: field.id }) };
    }
    if (seen.has(name)) {
      return { ok: false, error: t('trait_editor.error.dup_field', { id: name }) };
    }
    seen.add(name);
    // Reuse the existing opaque id on edit; mint a fresh one for new fields.
    const id = field.originalId ?? mintVocabId('predicate');
    predicateIds.push(id);

    // Safe-edit guards for fields that already existed (#73). The value
    // kind is immutable (changing it would re-interpret historical fact
    // values under a new type), and cardinality may only widen
    // (single → many treats the existing scalar as a singleton;
    // many → single would silently drop values). The UI also enforces
    // these, but the build guards them so a stale form can't slip a
    // lossy change through.
    //
    // `allowRisky` (#77) is the explicit GM acknowledgement that unlocks
    // both: the change is then permitted and recorded as a normal define
    // op (the op log IS the audit trail). Auditor surfacing of the
    // resulting type/cardinality mismatches against existing facts is a
    // Phase 2 follow-up.
    if (field.originalId !== undefined && !state.allowRisky) {
      if (field.originalKind !== undefined && field.valueKind !== field.originalKind) {
        return { ok: false, error: t('trait_editor.error.kind_locked', { id }) };
      }
      if (field.originalCardinality === 'many' && field.cardinality === 'single') {
        return { ok: false, error: t('trait_editor.error.cardinality_narrow', { id }) };
      }
    }

    // Opaque ids never change on rename (ADR-0004 §1), so `previous_names`
    // is no longer identity-bearing — it survives purely as former display
    // names for search/recall. We keep the existing history as-is.
    const previous_names = [...(field.previousNames ?? [])];

    // Enum options: each comma-separated entry is a display NAME; mint an
    // opaque `enm_` id per option (ADR-0004 §1). The fact stores the id.
    let enumValues: EnumValueDeclaration[] | null = null;
    if (field.valueKind === 'enum') {
      enumValues = buildEnumValueDeclarations(field.enumValues, field.originalEnumValues);
      if (enumValues.length === 0) {
        return { ok: false, error: t('trait_editor.error.enum_values', { id }) };
      }
    }
    const refRoot = field.valueKind === 'ref' && field.refRoot ? field.refRoot : null;
    const reciprocal =
      field.valueKind === 'ref' && field.reciprocal && field.reciprocal.trim().length > 0
        ? field.reciprocal.trim()
        : null;

    // Number → optional measurement dimension (ADR-0002). Drives the
    // per-fact unit picker; unitless when blank.
    const dimension =
      field.valueKind === 'number' && field.dimension && field.dimension.trim().length > 0
        ? field.dimension.trim()
        : null;

    // Yes/No → optional per-state labels. Emit `boolean_labels` only when at
    // least one side is labelled; a blank side falls back to the default.
    let booleanLabels: { true_label: string; false_label: string } | null = null;
    if (field.valueKind === 'boolean') {
      const tl = (field.trueLabel ?? '').trim();
      const fl = (field.falseLabel ?? '').trim();
      if (tl !== '' || fl !== '') {
        booleanLabels = { true_label: tl || 'Yes', false_label: fl || 'No' };
      }
    }

    // Tuple sub-fields (ADR-0002). Each becomes a TupleFieldDeclaration on
    // the predicate's `tuple_fields`. Validate names + per-field enum.
    let tupleFields: TupleFieldDeclaration[] | null = null;
    if (field.valueKind === 'tuple') {
      const subs = field.tupleFields ?? [];
      if (subs.length === 0) {
        return { ok: false, error: t('trait_editor.error.tuple_fields', { id }) };
      }
      const subSeen = new Set<string>();
      const built: TupleFieldDeclaration[] = [];
      for (const sub of subs) {
        // The GM types a field NAME; identity is an opaque minted `fld_` id
        // (ADR-0004 §1) that keys the stored tuple record.
        const subName = sub.name.trim();
        if (subName.length === 0) {
          return { ok: false, error: t('trait_editor.error.tuple_field_name', { id: sub.name }) };
        }
        if (subSeen.has(subName)) {
          return { ok: false, error: t('trait_editor.error.tuple_field_dup', { id: subName }) };
        }
        subSeen.add(subName);
        let subEnum: EnumValueDeclaration[] | undefined;
        if (sub.valueKind === 'enum') {
          const built = buildEnumValueDeclarations(sub.enumValues, sub.originalEnumValues);
          if (built.length === 0) {
            return { ok: false, error: t('trait_editor.error.tuple_field_enum', { id: subName }) };
          }
          subEnum = built;
        }
        const decl: TupleFieldDeclaration = {
          // Reuse the existing opaque `fld_` id on edit; mint a fresh one for a
          // genuinely new sub-field (ADR-0004 §1) — the stored tuple record
          // keys by this id, so re-minting orphans every existing tuple fact.
          id: sub.originalId ?? mintVocabId('field'),
          label: { en: subName },
          value_kind: sub.valueKind,
          ...(sub.optional ? { optional: true } : {}),
          ...(subEnum ? { enum_values: subEnum } : {}),
          ...(sub.valueKind === 'ref' && sub.refRoot ? { ref_roots: [sub.refRoot] } : {}),
        };
        built.push(decl);
      }
      tupleFields = built;
    }

    predicates.push({
      id,
      label: { en: name },
      value_kind: [field.valueKind],
      merge: 'lww',
      previous_names,
      // The editor's 'many' affordance maps to the ADR-0005 {object}
      // uniqueness key (set-per-value); 'single' carries no uniqueness key.
      ...(field.cardinality === 'many' ? { uniqueness_key: ['object'] } : {}),
      ...(enumValues ? { enum_values: enumValues } : {}),
      ...(refRoot ? { ref_root: refRoot } : {}),
      ...(reciprocal ? { reciprocal } : {}),
      ...(dimension ? { dimension } : {}),
      ...(booleanLabels ? { boolean_labels: booleanLabels } : {}),
      ...(tupleFields ? { tuple_fields: tupleFields } : {}),
    });
  }
  // `applies_to`: the standalone settings form drives `appliesTo` explicitly
  // (and must pick ≥1 root); picker-seeded / edit flows leave it undefined and
  // fall back to the seeded `forRoot`.
  let appliesTo: EntityRoot[];
  if (state.appliesTo !== undefined) {
    if (state.appliesTo.length === 0) {
      return { ok: false, error: t('trait_editor.error.no_roots') };
    }
    appliesTo = [...new Set(state.appliesTo)];
  } else {
    appliesTo = [state.forRoot];
  }
  const trait: TraitDeclaration = {
    id: traitId,
    label: { en: traitName },
    applies_to: appliesTo,
    predicates: predicateIds,
  };
  const payloads: PackOpPayload[] = [
    ...predicates.map((p) => packDefinePredicate(p)),
    packDefineTrait(trait),
  ];
  return { ok: true, payloads };
}

// ─── Entity-type editor (#schema-settings / 03a-entities.md §4) ─────────
//
// A type is a `(root, traits[])` quick-create template. The editor is a
// simple inline form on the settings Types tab: id, label, root, and a
// multi-select of traits available for that root. Submit assembles one
// `pack.define_type` op (ADR-0001), folded into the homebrew overlay.

/** Entity-type editor form state. Null when the inline editor is closed. */
export interface TypeEditorState {
  /** `create` defines a new type; `edit` revises an existing one (id locked). */
  mode: 'create' | 'edit';
  /** Type id slug (e.g. `city`). */
  typeId: string;
  /** Human label (optional; falls back to the id). */
  label: string;
  /** The entity root this type instantiates. */
  root: EntityRoot;
  /** The traits attached on quick-create from this type. */
  traits: string[];
  error: string | null;
}

/**
 * Build an edit-mode `TypeEditorState` from an existing homebrew type, or
 * null when the id isn't a known homebrew type (core types aren't editable).
 */
function typeEditorStateForExisting(typeId: string): TypeEditorState | null {
  const type = getHomebrewDefs().types.find((ty) => ty.id === typeId);
  if (!type) return null;
  return {
    mode: 'edit',
    typeId: type.id,
    label: localizedText(type.label) || '',
    root: type.root,
    traits: [...type.traits],
    error: null,
  };
}

/**
 * Validate the type-editor form and assemble the `pack.define_type` op
 * (pure, so the reducer stays testable). A type with zero traits is valid —
 * it's a bare-root template (still a useful named shortcut).
 */
export function buildTypeDefinitionOp(
  state: TypeEditorState,
): { ok: true; payload: PackOpPayload } | { ok: false; error: string } {
  const id = state.typeId.trim();
  if (!TRAIT_EDITOR_SLUG_RE.test(id)) {
    return { ok: false, error: t('type_editor.error.type_id') };
  }
  const label = state.label.trim();
  const declaration: TypeDefinition = {
    id,
    root: state.root,
    traits: [...new Set(state.traits)],
    ...(label ? { label } : {}),
  };
  return { ok: true, payload: packDefineType(declaration) };
}

// ─── Campaign units editor (ADR-0007) ──────────────────────────────
//
// Mirrors the trait editor: a modal form whose submit assembles
// `pack.define_dimension` / `pack.define_unit` meta-ops appended to the
// campaign-local homebrew Pack (same `(homebrew, __pack)` chain). The
// folded overlay then merges with the common unit registry to drive the
// number-trait unit picker (see `campaignUnitRegistry` in
// fact-row-editor.ts).
//
// ADR-0007 §2: dimensions/units are opaque-id definitions. The GM authors a
// NAME (+ symbol for units) and the client MINTS the id on submit — the same
// flow as predicates/traits/enums/calendars. No id is ever typed. The unit's
// owning dimension is chosen from a picker (its opaque `dim_` id), and the
// affine map factors (`to_base`, `offset`) are exact factor strings parsed by
// `quantity.ts` (a decimal or an `n/d` fraction).

/** #units / ADR-0007 — one new dimension being authored in the units editor. */
export interface UnitsEditorDimensionDraft {
  /** Human display name (e.g. "Wealth"). Identity is a minted `dim_` id. */
  name: string;
}

/** #units / ADR-0007 — one new unit being authored in the units editor. */
export interface UnitsEditorUnitDraft {
  /** Human display name (e.g. "Gold piece"). Identity is a minted `unit_` id. */
  name: string;
  /** Short display symbol (e.g. `gp`). */
  symbol: string;
  /** Owning dimension — an opaque `dim_` id chosen from the picker. */
  dimension: string;
  /** Affine factor to the dimension's canonical base (`canonical = value * to_base + offset`). */
  toBase: string;
  /** Optional affine offset factor (for scales like °F). Empty = 0. */
  offset: string;
  /** Marks this unit as its dimension's base unit (sets the dimension's `base_unit`). */
  isBase: boolean;
}

/**
 * #units / ADR-0007 — units-editor form state. Null when closed. The form
 * authors one new dimension and one new unit at a time (each independently
 * submittable); the existing campaign dimensions/units render as a read-only
 * list above the forms so the GM sees what's already defined.
 */
export interface UnitsEditorState {
  dimensionDraft: UnitsEditorDimensionDraft;
  unitDraft: UnitsEditorUnitDraft;
  error: string | null;
}

/** A fresh, empty dimension draft. */
function emptyDimensionDraft(): UnitsEditorDimensionDraft {
  return { name: '' };
}

/** A fresh, empty unit draft. */
function emptyUnitDraft(): UnitsEditorUnitDraft {
  return { name: '', symbol: '', dimension: '', toBase: '1', offset: '', isBase: false };
}

/**
 * #units / ADR-0007 — validate a dimension draft and assemble the
 * `pack.define_dimension` op with a freshly MINTED opaque id. Pure
 * (reducer-testable). The returned `dimensionId` lets the caller reference
 * the new dimension (e.g. to seed the unit form's dimension picker).
 */
export function buildDimensionDefinitionOp(
  draft: UnitsEditorDimensionDraft,
): { ok: true; payload: PackOpPayload; dimensionId: string } | { ok: false; error: string } {
  const name = draft.name.trim();
  if (name.length === 0) {
    return { ok: false, error: t('units_editor.error.dimension_name') };
  }
  const definition: DimensionDefinition = { id: mintVocabId('dimension'), name: { en: name } };
  return { ok: true, payload: packDefineDimension(definition), dimensionId: definition.id };
}

/**
 * #units / ADR-0007 — validate a unit draft and assemble the
 * `pack.define_unit` op with a freshly MINTED opaque id. Pure
 * (reducer-testable). `to_base` / `offset` are exact factor strings (decimal
 * or `n/d` fraction) validated through the `quantity.ts` affine-map parser,
 * so a malformed or non-invertible map is rejected here. The returned
 * `definition` lets the caller wire `isBase` to the dimension's `base_unit`.
 */
export function buildUnitDefinitionOp(
  draft: UnitsEditorUnitDraft,
): { ok: true; payload: PackOpPayload; definition: UnitDefinition } | { ok: false; error: string } {
  const name = draft.name.trim();
  if (name.length === 0) {
    return { ok: false, error: t('units_editor.error.unit_name') };
  }
  const symbol = draft.symbol.trim();
  if (symbol.length === 0) {
    return { ok: false, error: t('units_editor.error.unit_symbol') };
  }
  const dimension = draft.dimension.trim();
  if (!isVocabId('dimension', dimension)) {
    return { ok: false, error: t('units_editor.error.unit_dimension') };
  }
  const toBase = draft.toBase.trim();
  const offsetRaw = draft.offset.trim();
  if (toBase.length === 0) {
    return { ok: false, error: t('units_editor.error.unit_to_base') };
  }
  // Validate the affine map through the same chokepoint the engine uses;
  // catches a zero scale, a malformed factor, or a bad offset.
  try {
    affineMap(toBase, offsetRaw.length > 0 ? offsetRaw : undefined);
  } catch {
    return { ok: false, error: t('units_editor.error.unit_to_base') };
  }
  const definition: UnitDefinition = {
    id: mintVocabId('unit'),
    name: { en: name },
    symbol: { en: symbol },
    dimension,
    to_base: toBase,
    ...(offsetRaw.length > 0 ? { offset: offsetRaw } : {}),
  };
  return { ok: true, payload: packDefineUnit(definition), definition };
}

/**
 * The single open calendar-grid date picker's transient view-state
 * (`Model.datePicker`). `cursor`/`overlay` are the host-owned slices the
 * stateless `calendarDatePicker` atom consumes (see its docstring); `id`
 * identifies which `dateField` owns the open grid so every other field
 * renders closed.
 */
export interface DatePickerState {
  /** Field id of the open picker (e.g. `campaign-cursor`, `asof`). */
  id: string;
  /** Which (era, year, month-block) the grid currently shows. */
  cursor: CalendarCursor;
  /** Which inline overlay (month list / era) is open. */
  overlay: CalendarDatePickerOverlay;
}

/**
 * #251 — the bulk-gen config modal state (decision 6). One shape for both entry
 * surfaces; `target` distinguishes the entity-context action from the
 * campaign-bootstrap entry. `phase` walks the GM through config → generating —
 * there is no pre-run cost estimate; actual spend is metered per call and the
 * #218 budget wall is the guardrail.
 */
export interface BundleGenModalState {
  target: BulkGenTarget;
  prompt: string;
  depth: BundleDepth;
  /** `auto` lets the worker heuristic resolve; else a GM override. */
  mode: BundleMode;
  phase: 'config' | 'generating';
  /** Last error (over-budget refusal, provider failure) — keeps the modal open for retry. */
  error: string | null;
}

/**
 * ADR-0008 §6 — one tool "thinking" entry recorded while a live authoring turn
 * streams (a read tool like `search_entities`, or a stage-only `propose_*`). The
 * UI maps `name`/`status` to its own i18n string; `detail` is never rendered raw.
 */
export interface AuthoringToolEntry {
  name: string;
  status: 'started' | 'succeeded' | 'failed';
  detail?: string;
}

/**
 * One message in the authoring-session transcript. A `user` message is the GM's
 * steering `direction`; an `assistant` message accretes the streamed token
 * deltas (`text`) and the turn's tool trace (`tools`) as the SSE stream folds in
 * (ADR-0008 §6). JSON-serialisable — no Maps/Dates.
 */
export interface AuthoringMessage {
  role: 'user' | 'assistant';
  text: string;
  /** Tool "thinking" entries recorded during this turn (assistant messages only). */
  tools?: AuthoringToolEntry[];
}

/**
 * ADR-0008 §1/§6 — the LLM-led interactive authoring session (T7, #281). The
 * commit-bearing state is always the typed `artifact` (a trait draft for now);
 * `messages` is the editing conversation over it; `diff` is the current turn's
 * reviewable change; `advisories` surface a refused commit's auditor findings.
 *
 * `null` when no session is open. Fully JSON-serialisable so it round-trips
 * through the llui Model and (later) the DO scratch store. Three transports feed
 * it: the create POST fills `sessionId`; the `/turn` SSE stream folds
 * token/tool/artifact_diff/turn_done/error events in; the `/commit` POST
 * resolves it (close on ok, advisories on refusal).
 */
export interface AuthoringSessionState {
  /** The DO-scoped session id; `null` until the create POST resolves. */
  sessionId: string | null;
  /** What the artifact commits against (`existing_id: null` = brand-new). */
  target: AuthoringTarget;
  /** The refine conversation over the artifact (user directions + assistant turns). */
  messages: AuthoringMessage[];
  /** The live typed artifact (trait draft), or `null` before the first `propose_*`. */
  artifact: TraitAuthoringArtifact | null;
  /** The current turn's reviewable diff (mirrors `artifact.diff`), or `null`. */
  diff: ArtifactDiff | null;
  /** `idle` between turns, `streaming` during a live `/turn`, `committing` during `/commit`. */
  phase: 'idle' | 'streaming' | 'committing';
  /** The GM's in-progress direction text for the next turn. */
  pendingDirection: string;
  /** Auditor advisories from a refused commit (ADR-0008 §5, pack-import mode). */
  advisories: AuthoringAdvisory[];
  /** Last turn-fatal / commit error, or `null`. */
  error: string | null;
}

export interface Model {
  entities: Record<string, EntityState>;
  /**
   * LLM-never-canon invariant (`02-architecture.md §4.2`). Per fact chain
   * (`entityId → factKey → { value }`), the last value the fact held while its
   * folded status was `canon` — the value a later draft/pending SHADOWED.
   * `reject-draft` / `pending-reject` restore THIS rather than a position-in-
   * array guess (which can promote unratified LLM/GM-tweak content to canon).
   * Absent entry ⇒ the fact was never canon ⇒ reject soft-deletes instead of
   * promoting. Recomputed from the op log on every `replica/entities-rebuilt`
   * (the packages/shared fold discards per-version status, so this rebuilds
   * the status timeline client-side). A rebuildable derived view.
   */
  shadowedCanon: Record<string, Record<string, { value: unknown }>>;
  /**
   * Phase-2 auditor (04-llm-role.md §5). The deterministic rule engine's
   * advisories, recomputed from `entities` on every `replica/entities-rebuilt`.
   * Derived (rebuildable from the op log via `runAudit`), not authoritative —
   * but JSON-serialisable (id/severity/rule/message_key/params/ids), so it
   * lives safely in the Model. GM-only: the panel is the GM authoring
   * perspective; advisories never project to players.
   */
  advisories: Advisory[];
  /**
   * Suggestion-set bundle registry (#251) — `bundle_id → entry`, folded from the
   * op log (`foldBundles`) on every `replica/entities-rebuilt`, alongside
   * `entities`/`config`/`advisories`. A rebuildable view of the op log, never
   * authoritative (ADR-0004 §"derived views"). Drives the GM-only bundle-review
   * surface in the draft inbox: an open bundle groups its still-draft members as
   * one reviewable unit. Bundle `state` (open/resolved) is DERIVED in the fold
   * from member statuses — never an explicit op.
   */
  bundles: BundleRegistry;
  /**
   * #251 — per-bundle auto-promote-with-flag opt-in (decision 3 / §14.1).
   * **Off by default**: only a `bun_…` id present here with `true` makes a
   * bundle-accept stamp `review_state: 'unreviewed_bulk'` onto the GM-authored
   * canon ops. A device-local review preference (NOT canon, NOT the op log) —
   * the marker it produces IS op-log state, but the toggle that opts in is not.
   */
  bundleAutoPromote: Record<string, boolean>;
  /**
   * #251 — the bulk world-generation config modal (decision 6). `null` when
   * closed. The two entry surfaces (entity-context action + campaign-bootstrap
   * entry) both open this with a `BulkGenTarget`; the GM picks depth + an
   * optional mode override + a prompt, then the bulk-gen route runs directly
   * (no pre-run cost estimate — actual spend is metered and the #218 budget wall
   * guards it). The resulting drafts arrive via the DO sync broadcast and fold
   * into `bundles` — they are not appended by the client.
   */
  bundleGenModal: BundleGenModalState | null;
  /**
   * #251 §14.6 — the bundle-level regenerate-with-direction modal. `null` when
   * closed; otherwise carries the bundle being regenerated + the new-direction
   * prompt the GM is typing. Confirm dispatches a `bundle-regen` (a new
   * `regenerated_from`-linked BulkGenRequest + dismiss-the-old-drafts).
   */
  bundleRegenModal: { bundleId: string; prompt: string } | null;
  /**
   * ADR-0008 §1/§6 (T7, #281) — the LLM-led interactive authoring session, or
   * `null` when none is open. The turn-by-turn refine loop over a typed artifact
   * (a trait draft first). Fed by the create POST (`sessionId`), the `/turn` SSE
   * stream (token/tool/artifact_diff/turn_done/error), and the `/commit` POST.
   * Pre-canon working state — neither canon nor a rebuildable cache; the
   * committed `define_*` ops ride the ordinary op-log WS broadcast.
   */
  authoringSession: AuthoringSessionState | null;
  /**
   * Per-Campaign auditor rule severity overrides (§5.2.1). The GM's noise dial,
   * loaded from `/v1/campaigns/:cid/auditor-overrides` on boot and persisted on
   * change. Fed into every `runAudit` call so muted/downgraded/promoted rules
   * take effect immediately. `{}` = engine defaults.
   */
  auditorRuleOverrides: AuditorRuleOverrides;
  /**
   * III.B.1 / III.B.2 (#65 / #66) — boot-resolved session. Drives the
   * mode toggle (gm vs player) and informs the rest of the shell.
   * Starts as `LEGACY_GM_SESSION` for back-compat with the demo
   * Walking-Skeleton campaign; flips to whatever the boot endpoint
   * returns on real campaign mounts.
   */
  session: Session;
  bootStatus: 'idle' | 'pending' | 'loaded' | 'error';
  bootError: string | null;
  /**
   * III.B.2 (#66) — projected facts for non-GM viewers. Keyed by
   * `factKey(subject, predicate)`. Populated by the SyncClient's
   * `visibility_added` / `visibility_removed` callbacks. The chokepoint
   * has already filtered the values — the client never re-projects.
   *
   * Held as a `Record` (was `Map`) to satisfy llui's serializability
   * check on initial state — Maps fail SSR hydration, state replay,
   * and devtools snapshots. The accessor patterns (`Object.values`,
   * field-key lookup) all stay O(n) / O(1) like the Map version.
   */
  projectedFacts: Record<string, ProjectedFactRow>;
  /**
   * 226.1 (#245) — per-Viewer Preview-as picker. The GM (mode === 'gm')
   * selects a non-GM Viewer to preview EXACTLY what that Viewer's
   * projection (`project(viewer, t)`) reveals at the current cursor. This
   * is a GM-side affordance only; real players run `mode === 'player'` and
   * never touch this field.
   *
   * `null` = the default GM identity / GM-truth view (full canon). A
   * non-null `ViewerId` (always non-GM in practice; a GM selection collapses
   * back to `null`) puts the shell into preview mode: the topbar shows a
   * "Previewing as …" banner and the entity page renders a read-only panel
   * of the projected facts held in {@link previewProjection}.
   *
   * Phantom-Viewer: the projected values are produced ONLY by the
   * `@dl/projection` chokepoint (`project()` in the `compute-preview-
   * projection` effect); the client never constructs a `VisibleFact`, never
   * re-implements the reveal filter, and never reads GM-truth `entities`
   * while previewing. Switching Viewers re-projects (no reload).
   */
  previewViewer: ViewerId | null;
  /**
   * 226.1 (#245) — the chokepoint output for {@link previewViewer}, keyed by
   * `factKey(subject, predicate)` exactly like {@link projectedFacts}. `null`
   * when not previewing (or while a fresh projection is being computed in the
   * effect). Recomputed whenever the preview viewer, the op log, or the cursor
   * changes. The chokepoint has already filtered the values — the client never
   * re-projects from this map.
   */
  previewProjection: Record<string, ProjectedFactRow> | null;
  /**
   * #258 — the Table View **table focus** (ephemeral presentation state, like
   * {@link browseCursor}). A discriminated union: `viewer` (default full
   * designated-Viewer projection) | `entity` (pinned entity page) | `handout`
   * (pinned asset image). GM-controlled via Pin-to-Table / un-pin; never
   * persisted, never written to the op log, never synced through the substrate.
   * Pinning changes WHAT the table shows, never WHOSE eyes — every focus value
   * still renders through `project(designatedViewer, t)`.
   */
  tableFocus: TableFocus;
  /**
   * #258 — the GM's explicit designated-Viewer override for the Table View. The
   * EFFECTIVE designated Viewer is `resolveDesignatedViewer(override, entities)`
   * — `null` here means "use the default" (the party Viewer, practically the
   * first campaign Viewer). A GM/stale override collapses to the default; the
   * table never renders GM-truth. Ephemeral (never persisted/synced).
   */
  designatedViewerOverride: ViewerId | null;
  /**
   * #258 — the projected rows received over `BroadcastChannel` when THIS tab is
   * the Table View tab (the pure-renderer role). `null` until the first
   * broadcast lands (the table shows its "waiting for the GM" placeholder). The
   * GM main tab never reads this — it renders from its own live projection. The
   * rows are already chokepoint-filtered (the GM tab ran `project()` before
   * broadcasting), so the Table tab re-projects NOTHING and opens NO replica.
   */
  tableBroadcast: TableBroadcast | null;
  /**
   * #271 — true when the GM has enabled "attention mode" — the pointing cursor
   * and ripple affordances are active. When false the pointer overlay is hidden
   * on the Table tab and mouse-move events are not broadcast. Toggled by the GM
   * from the Table View controls. Ephemeral — never persisted.
   */
  tableAttentionMode: boolean;
  /**
   * #271 — ephemeral GM pointing cursor. Normalised [0,1]×[0,1] on the Table
   * surface. `null` when attention mode is not active or pointer is off-surface.
   * GM tab only; never persisted, never written to the op log.
   */
  tablePointer: TableAttentionPointer | null;
  /**
   * #271 — ephemeral "Look at this" ripple. The `seq` increments on each
   * trigger so the Table tab can detect a re-trigger at the same coordinates.
   * `null` when no ripple is active. Auto-cleared after `TABLE_RIPPLE_TTL_MS`.
   * GM tab only; never persisted, never written to the op log.
   */
  tableRipple: TableAttentionRipple | null;
  /**
   * #271 — monotonic counter for ripple triggers; incremented on every "Look at
   * this" action so the seq field in `tableRipple` is strictly increasing.
   */
  tableRippleSeq: number;
  /**
   * III.B.3 (#67) — per-entity private notes (player mode only). Keyed
   * by entity_id. The body is fetched lazily on entity-page mount.
   * `Record` shape — see `projectedFacts` for the rationale.
   */
  privateNotes: Record<string, PrivateNoteState>;
  route: AppRoute;
  /**
   * Recently-viewed entity ids, most-recent-first (cap {@link RECENT_CAP}).
   * Drives the left rail's "Recent" section. A per-device UI affordance —
   * NOT canon — so it persists to `localStorage` keyed by campaign (see
   * {@link readRecentEntities}/{@link writeRecentEntities}), never to the op
   * log. Pushed in the `navigate` arm whenever an entity page is opened.
   */
  recentEntityIds: string[];
  paletteOpen: boolean;
  paletteQuery: string;
  /**
   * #134 — active row index in the Cmd-K palette. The list is the
   * concatenation of result rows then quick-create rows; `0` is the
   * first result (or the first quick-create row when there are no
   * results). The arrow-key handlers clamp this to the rendered list
   * length so wraparound is predictable.
   */
  paletteActiveIndex: number;
  connection: ConnectionState;
  /**
   * True while server ops are actively streaming into the local replica
   * (initial backfill on first open, or a reconnect catch-up). Set on the
   * first applied op and cleared after a short idle with no further ops.
   * Drives the "Syncing…" indicator so the empty-during-backfill window
   * reads as loading, not an empty world.
   */
  syncing: boolean;
  inlineErrors: Record<string, string>;
  /**
   * Campaign-level pending auto-ratify policy (07-session-experience.md
   * §8.2). I.A.5 / #97 ships this as a Campaign-level setting that lives
   * on the Campaign Lore entity. This is a local-only setting
   * surfaced for the spec contract until the predicate lands.
   */
  pendingAutoRatifyPolicy: PendingAutoRatifyPolicy;
  /**
   * The folded campaign-settings record (#171, ADR-0004 §5) — the "config"
   * bucket of the three-bucket model, rebuilt from `config.set` ops on every
   * `replica/entities-rebuilt`. A `Partial`: only keys a GM has written are
   * present. Source of the `canonical_calendar` / `display_calendars`
   * pointers that drive {@link activeCalendarId}.
   */
  config: ConfigState;
  /**
   * Active calendar id for the campaign (#180, ADR-0004 §4/§5). DERIVED
   * from `config.canonical_calendar` (a setting pointer) resolved against
   * the folded calendar-definition registry; falls back to
   * {@link FALLBACK_CALENDAR_ID} when unset or dangling. NOT a fact / not a
   * lore entity — the calendar *choice* is a setting, the *definition*
   * lives in the registry.
   */
  activeCalendarId: string;
  /**
   * Whether the active calendar has been CONFIRMED by at least one op-log
   * fold (`replica/entities-rebuilt`). `false` during the brief pre-fold boot
   * window, where `activeCalendarId` is only a guess (the localStorage
   * restore or the Gregorian fallback). A canonical day count is meaningful
   * only against a specific calendar's coordinate system, so the cursor is
   * NOT writable until this is `true`: a date picked while the displayed
   * calendar is wrong encodes a different instant than the GM intends, and
   * (unlike a mis-keyed value) cannot be salvaged by re-keying after the
   * fold. Gating the write is the only correct fix. See the `set-cursor` /
   * `cursor-advance` / `cursor-rewind` arms and the cursor-pill `disabled`.
   */
  calendarConfirmed: boolean;
  /**
   * The Campaign's "now cursor" — a canonical day count for the active
   * calendar, or `null` while no in-world time has been set. Persistence
   * is local-only at Walking Skeleton scope; Phase 1 wires the durable
   * copy through the Campaign entity.
   */
  campaignCursor: number | null;
  /**
   * The EPHEMERAL in-world **browse cursor** (as-of scrubber — ADR-0005 §3,
   * #192). When non-null the GM is browsing the world AS OF this canonical
   * day: the entity page + timeline strip live-resolve temporal values at
   * this `t` instead of the session "now" (`campaignCursor`). It is
   * client-only state — never persisted, never synced (the high-frequency
   * scrub would churn the op log; only the session cursor persists).
   * `null` means "browsing now" — the cursor is NOT diverged.
   */
  browseCursor: number | null;
  /**
   * The single OPEN calendar-grid date picker, app-wide. Only one
   * `calendarDatePicker` popover/inline grid is ever interactive at a
   * time, so its transient view-state (which month is on screen, which
   * inline overlay is open) lives here as one bounded slice rather than
   * being replicated per call site / per fact row. `id` namespaces the
   * field (`campaign-cursor`, `asof`, `fact:<entity>:<predicate>:day`,
   * `validity:<entity>:<predicate>:from`, …); `null` = no grid open.
   * Pure transient UI state — never persisted, never synced.
   */
  datePicker: DatePickerState | null;
  /**
   * IV.B.2 (#78) — mobile entity-action sheet. Surfaces fact-level
   * affordances (reveal-this-fact, mark-visited) that would otherwise
   * be inline only on tablet+. The sheet is a `@dl/ui` BottomSheet
   * primitive (IV.B.1 / #77); it mounts conditionally below the phone
   * breakpoint based on this state.
   *
   *   - `closed` — no sheet open
   *   - `reveal` — picking a target Viewer to reveal a fact to
   *   - `visited` — confirming a visited-mark on a referenced Location
   */
  entitySheet:
    | { kind: 'closed' }
    | {
        kind: 'reveal';
        entityId: string;
        predicate: string;
      }
    | {
        kind: 'visited';
        /** Entity authoring the visited fact (typically a Character). */
        subjectId: string;
        /** Entity being marked as visited (typically a Location). */
        targetId: string;
      };
  /**
   * Per-attribute reveal (Phase 2 / `30-design-system.md §7.7`) — the
   * Viewer-Matrix popover open for one `(entityId, predicate)` fact on the
   * desktop Facts tab, or `null` when no popover is open. Clicking a fact's
   * reveal chip sets this; the popover lists the fact's current reveals and
   * a picker of remaining campaign Viewers. Reveals are write-once, so the
   * popover only ever ADDS targets.
   */
  revealMenu: { entityId: string; predicate: string } | null;
  /**
   * Routine facet bulk-reveal (Phase 2 / 30 §7.10.2) — the facet-reveal
   * popover open for a safe facet on the Reveals tab, or `null`. Picking a
   * Viewer reveals every (still-hidden) fact in the facet to them in one go,
   * each with `source.kind='facet'`. `predicates` is the facet's valued
   * member predicates captured when the popover opened.
   */
  facetRevealMenu:
    | { entityId: string; facetId: string; label: string; predicates: string[] }
    | null;
  /**
   * Reveal Composer (Phase 2 / 30 §7.10.3) — the gated flow for SENSITIVE
   * facets (`safe_to_bulk_reveal: false`). Unlike the one-click
   * `facetRevealMenu`, the Composer makes the GM pick a recipient, preview
   * the per-fact diff (excluding individual facts), and confirm before any
   * reveal commits. `viewer` is the chosen recipient; `excluded` holds the
   * predicates the GM unchecked in the preview. `null` when closed.
   */
  revealComposer:
    | {
        entityId: string;
        facetId: string;
        label: string;
        predicates: string[];
        viewer: ViewerId | null;
        excluded: string[];
      }
    | null;
  /**
   * Belief layer (Phase 2 / 03 §9) — the "Draft belief" modal. A belief is a
   * fact ON the entity (`entityId`) held BY a Viewer (`believer`) on a base
   * `predicate`, contradicting or supplementing canon in that Viewer's
   * perspective. `null` when closed.
   */
  beliefModal:
    | { entityId: string; believer: ViewerId | null; predicate: string; value: string }
    | null;
  /**
   * Belief clone-a-lie picker (Phase 2 / 03 §9 / B3). Open for one source
   * belief; picking a target Viewer clones the belief to them with a
   * `source_belief` pointer back to the origin (the "NPC tells PC a lie"
   * propagation chain). `null` when closed.
   */
  beliefCloneMenu:
    | { entityId: string; sourceKey: string; base: string; value: string; sourceBelieverKey: string }
    | null;
  /**
   * IV.B.4 (#80) — mobile private-notes sheet. The notes textarea is
   * the same surface as the inline panel in `viewPrivateNotesPanel`,
   * but rendered inside a bottom-sheet so the keyboard + sheet share
   * the bottom half of the viewport on phone.
   */
  notesSheetOpen: boolean;
  /** Entity whose notes are being edited in the mobile sheet (or null). */
  notesSheetEntityId: string | null;
  /**
   * IV.B.3 (#79) — when the GM taps "Rewind" in the mobile HUD a
   * confirmation prompt surfaces inline (per AC: "Confirmation step on
   * rewind only … advance is one-tap"). The flag is the staged-rewind
   * count; `null` means no confirmation pending.
   */
  hudRewindPending: number | null;
  /**
   * Entity page active tab (per 30 §7.2). The entity page splits into
   * Overview (story-first, curated infobox + prose), Facts (the
   * management view — the full trait-driven fact-row editor), with
   * Reveals / History / Connections placeholders for future phases.
   * Defaults to 'overview' so the first impression is reading, not
   * editing.
   */
  entityPageTab: 'overview' | 'facts' | 'reveals' | 'issues' | 'history' | 'connections';
  /**
   * Campaign boot state. Lifecycle:
   *   - `idle`    — boot path not yet entered (legacy demo / CAMPAIGN_ID
   *                 already resolved before the picker matters)
   *   - `loading` — GET /v1/account in flight
   *   - `empty`   — list returned zero rows → render create-first form
   *   - `picker`  — list returned ≥1 rows; mounted at `/#/campaigns`
   *   - `error`   — list failed; render retry copy
   */
  campaignsBoot: CampaignsBoot;
  /**
   * #005 (llui-hud) — the "Actions" overlay panel (open / closed). The crowded
   * topbar action cluster collapses into this panel behind a single ⚙ button;
   * the bar keeps a read-only summary strip + the avatar. ESC / backdrop /
   * navigation close it.
   */
  actionsOverlayOpen: boolean;
  /**
   * #152 — account / profile settings page slice. Loaded from
   * `GET /auth/user` via the `account/load` Effect; the view branches on
   * `account.status`. JSON-serialisable Records only (llui constraint).
   */
  account: AccountModel;
  /**
   * #89 — local, optimistic "the GM dismissed the recovery disclaimer
   * this session" flag. The disclaimer modal shows when the loaded user
   * has `recovery_disclaimer_seen === false` AND this is `false`; clicking
   * "Got it" (or "Export now") flips this to `true` immediately so the
   * modal never re-shows even before the server ACK lands. The server
   * persists the irrevocable acknowledgement separately (the flag on the
   * user record), so a reload reads `recovery_disclaimer_seen === true`.
   */
  recoveryDisclaimerDismissed: boolean;
  /**
   * #89 — client-tracked "last export" timestamps, keyed by campaign id
   * (ISO string). Hydrated from localStorage at boot and updated whenever
   * an export runs, so the Settings "Data & backup" section can show
   * "Last export: <relative time>". Record (not Map) per the llui
   * serialisability constraint.
   */
  lastExportAt: Record<string, string>;
  /**
   * #131 — root-picker dropdown for entity creation. When `open`, the
   * picker overlay renders inline below the New-entity button on
   * desktop / as a bottom-sheet on mobile (CSS-driven, same primitive
   * as the cursor picker).
   */
  rootPickerOpen: boolean;
  /**
   * Bug 4 — new-entity name modal. `null` when the modal is closed.
   * When open, holds the chosen root and the in-progress name. The
   * entity is NOT created in the substrate until the user submits —
   * unlike the legacy `create-entity` Msg which minted an id and
   * appended `name = "New Location"` ops eagerly, leaving the dirty
   * flag race that hid the save button. The modal also short-circuits
   * the root-picker: clicking a root in the picker transitions
   * straight to the name input rather than firing `create-entity`.
   */
  newEntityModal: { root: EntityRoot; name: string; traits: readonly string[] } | null;
  /**
   * #81 — "Create instance" modal/bottom-sheet. Open on a Lore entity;
   * captures the new entity's name + chosen root (default `character`).
   * Confirm creates the entity and writes an `instance_of` ref fact back
   * to `sourceId` (scope option (a): minimal ref, no template-trait copy).
   * Same primitive as `newEntityModal`: a CSS-driven modal on desktop /
   * bottom-sheet on phone.
   */
  instanceModal: { sourceId: string; root: EntityRoot; name: string } | null;
  /**
   * #139 — trait picker overlay. Holds the entity id the picker is
   * targeting (or null when closed). The picker lists every trait whose
   * `applies_to` includes the entity's root, minus traits already
   * attached. Attach fires `entity.attach_trait`; the reducer resolves
   * the target entity from this field, so the click handler doesn't
   * need a snapshot-on-click escape.
   */
  traitPickerEntityId: string | null;
  /**
   * The single open per-fact History dialog, or null. History moved out of an
   * inline `<details>` under each row (which made one fact read as three
   * stacked strips) into a dialog opened from the row's meta line.
   */
  historyOpen: { entityId: string; predicate: string } | null;
  /**
   * The trait the GM is pointing at in the trait strip, or null. Ephemeral,
   * presentational: it drives the chip→fact-row highlight so the strip and the
   * rows it manages are visibly connected.
   */
  highlightedTrait: string | null;
  /**
   * #139 — detach-confirm overlay. Surfaced when the GM removes a trait
   * that has ≥1 non-soft-deleted fact on a predicate that no other
   * attached trait still reaches. The detach itself is a non-destructive
   * op-log append (facts persist in the op log; the editor stops
   * rendering rows) — the confirm is pure UX safety.
   */
  traitDetachConfirm: TraitDetachConfirm | null;
  /**
   * Schema-vocabulary retire-confirm overlay. Surfaced from the settings
   * Traits/Predicates lists (and the inline editor's delete) when the GM
   * retires a definition that is still *in use* — attached to ≥1 entity or
   * referenced by ≥1 live fact. Like detach, retire is a non-destructive
   * op-log append (facts persist; the definition leaves the overlay), so
   * the confirm is pure UX safety. Null when no retire is pending; a
   * not-in-use retire skips the dialog and fires immediately.
   */
  schemaRetireConfirm: SchemaRetireConfirm | null;
  /**
   * Entity soft-delete confirm. Non-null while the GM is confirming a "move to
   * Trash" for one entity (reversible `entity.soft_delete`); the modal carries
   * the entity's display name for the prompt.
   */
  entityDeleteConfirm: { entityId: string; name: string } | null;
  /**
   * III.B.4 — GM "assign a player to a PC" picker. Non-null = open on this
   * Character entity. The binding is a `controlled_by` fact (op log), not a
   * D1 column; the projection unions the controlling User's Characters into
   * their effective Viewer set.
   */
  controllerPicker: { entityId: string } | null;
  /**
   * Campaign roster (#263): all members including the implicit owner
   * (role='owner', is_owner=true). Fetched from `GET /campaigns/:cid/members`.
   * Null = not yet loaded. The controller picker filters out the owner row
   * (the owner is implicit GM and not a `controlled_by` candidate).
   */
  campaignMembers: readonly { user_id: string; email: string; role: string; is_owner?: boolean }[] | null;
  /**
   * #264 — Per-member controlled Character map derived from the local replica.
   * Keyed by userId; value is `{ id, name }[]`. Recomputed whenever `entities`
   * changes via the subscriber in `entitySubscriberEffect`. Null = entities not
   * loaded yet (pre-substrate). The roster panel displays this; no extra API
   * call is needed.
   */
  memberControlledCharacters: Record<string, readonly { id: string; name: string }[]> | null;
  /** #007 — substring filter for the trait picker. Reset on open/close. */
  traitPickerQuery: string;
  /** #73 — trait-editor form (define a homebrew trait + its fields). Null when closed. */
  traitEditor: TraitEditorState | null;
  /**
   * #177 / ADR-0004 §1/§4 — custom-calendar editor on the settings Calendars
   * tab. Holds the in-progress `WorkingCalendar` draft + mode + origin. Null
   * when the list manager is showing (no calendar open in the section editor).
   */
  calendarEditor: CalendarEditorState | null;
  /** #units / ADR-0002 — units-editor form (define campaign dimensions + units). Null when closed. */
  unitsEditor: UnitsEditorState | null;
  /**
   * #schema-settings — entity-type editor form (define a homebrew
   * `(root, traits[])` quick-create template). Null when the inline editor
   * on the settings Types tab is closed.
   */
  typeEditor: TypeEditorState | null;
  /**
   * #156 — fact-row save affordance: per-row "dirty" flags.
   *
   * Keyed by `factKey(entityId, predicate)`. Set on `fact-input-dirty`
   * (every input keystroke), cleared on commit (`edit-fact` reducer
   * arm). The renderer surfaces a small visible "unsaved changes" cue
   * (border tint + leading dot) so the GM has confidence that typing
   * into a fact input changes something that must be saved.
   *
   * Persistence still happens via blur/Enter (the existing flow);
   * this is the visible signal that PROVED missing from the qualitative
   * validation ("no save option" → "I cannot even save a location").
   */
  factDirty: Record<string, true>;
  /**
   * Per-fact monotonic dirty epoch. Bumped each time the user types
   * into a fact input (the `fact-input-dirty` Msg). Submit effects
   * capture the epoch at submit time and the ACK Msg
   * (`fact-actually-saved`) carries it back; the reducer arm only
   * clears `factDirty[key]` when the ACK's captured epoch matches
   * the current epoch. Otherwise — the user typed *after* the submit
   * fired — the dirty flag stays set so the typed-but-unsaved value
   * remains visibly dirty. Lock-in: 2026-05-26 e2e #9.4 in
   * apps/client/e2e/09-create-edit-navigate.spec.ts.
   */
  factDirtyEpoch: Record<string, number>;
  /**
   * Controlled-input buffer for unsaved fact-row text. When the GM
   * types into a fact input, `onInput` writes the live DOM value into
   * `inputBuffer[key]` and the editor's reactive `value:` accessor
   * reads `inputBuffer[key] ?? persistedValue`. This is the canonical
   * llui "dirty-buffer accessor" pattern (per the 2026-05-27 thread
   * with the framework maintainer): the DOM's `el.value` is no longer
   * a side channel that the binding's lastValue cache can race
   * against — every typed change is in state, so the value accessor's
   * return value reflects what the user typed even mid-flight.
   *
   * Cleared by `fact-actually-saved` (the ACK clears it alongside
   * `factDirty[key]` — same epoch-comparison rules), `soft-delete-fact`,
   * and `rollback-fact`. Handles every variant of the value-vs-typed
   * race: load arrival mid-typing, server ACK arriving after the
   * user moved on, validation re-correction, etc.
   */
  inputBuffer: Record<string, string>;
  /**
   * #156 — `factKey → realtime epoch ms` for the most recent successful
   * save. The renderer compares against `Date.now()` and surfaces a
   * transient "saved ✓" badge for ~1500ms; aria-live="polite" announces
   * the save for screen-reader users.
   */
  factSavedAt: Record<string, number>;
  /**
   * `factKey → true` while a commit is in flight — set when `edit-fact`
   * dispatches the `submit-fact` effect, cleared by `fact-actually-saved`
   * (success) and `fact-save-failed`. Drives the transient "Saving…" cue in
   * the row's status rail (the in-flight step of the edit-state lifecycle,
   * between Unsaved and Saved). Independent of the `factDirtyEpoch` ACK
   * contract — it never gates the dirty/saved decision.
   */
  factSaving: Record<string, true>;
  /**
   * #156 — the `factKey` of the currently-focused fact input (if any).
   * Drives the first-focus inline hint: the hint surfaces below this
   * row, gated on `localStorage['dl.factEditHintDismissed']` being
   * unset. Cleared on blur or on dismiss.
   */
  focusedFactKey: string | null;
  /**
   * #156 — when `true`, the user has dismissed the inline hint or it
   * was already absent in localStorage. Cached on the model so the
   * renderer doesn't probe `localStorage` from a reactive accessor.
   */
  factHintDismissed: boolean;
  /**
   * Substrate-storage status. Distinct from `connection.online` because a
   * Chrome 148 user whose OPFS handle failed has a working network and
   * a broken local replica — the offline pill alone is too subtle for
   * that catastrophic state. Drives the top-of-page warning banner.
   *
   *   - `opening` — `openBrowserReplica()` in flight (boot or post-route)
   *   - `persistent` — OPFS opened cleanly; durable across reloads
   *   - `memory` — OPFS unavailable; substrate fell back to `:memory:`
   *                — single-session only, work is lost on reload
   *   - `failed` — substrate could not open at all; saves cannot happen
   */
  substrate:
    | { kind: 'opening' }
    | { kind: 'persistent' }
    | { kind: 'memory'; opfsError?: string }
    | { kind: 'failed'; error: string; errorName?: string };
  /**
   * When the substrate banner's "Why?" link is clicked we surface a small
   * dialog with the browser-version requirements + the active error
   * message (if any). `null` while the dialog is closed.
   */
  substrateDialogOpen: boolean;
  /**
   * LLM author mode (Phase B). Per-`factRowKey` flags for the ✨ "Draft
   * with AI" affordance: `aiDraftPending[key]` is set while the
   * suggest-prose request is in flight (disables the button + shows a
   * spinner); `aiDraftError[key]` carries the last failure message for
   * that row. Plain `Record`s — no Maps in Model (llui serialisability).
   */
  aiDraftPending: Record<string, true>;
  aiDraftError: Record<string, string>;
  /**
   * Live streaming preview for a per-fact ✨ prose draft. Tokens stream
   * here for visibility; the FINAL text is what's committed to the draft.
   * `phase`: 'thinking' (reasoning model warming up) → 'writing' (tokens
   * arriving). Null when no stream is active.
   */
  streamPreview: {
    entityId: string;
    predicate: string;
    text: string;
    phase: 'thinking' | 'writing';
  } | null;
  /**
   * LLM author mode (Phase C) — per-page "Draft with AI" modal. Null when
   * closed. `predicates` is the entity's empty prose predicates (computed
   * at open time); `selected` is the subset the GM has checked. Submit
   * fires one suggest-prose per selected predicate → drafts in the inbox.
   */
  pageDraftModal: {
    entityId: string;
    prompt: string;
    predicates: string[];
  } | null;
  /**
   * Phase D — resolved signed content URLs for generated images, keyed by
   * asset id. Populated when generate-image returns; read by the Draft
   * Inbox to render a thumbnail for asset-kind draft facts. (Signed URLs
   * expire ~1h — fine for the review window.)
   */
  assetUrls: Record<string, string>;
  /**
   * Per-campaign AI prompt extensions (GM-managed) — the layer on top of
   * the admin-managed global templates. Keyed by scope; loaded lazily when
   * the GM opens settings. `loaded` gates the editor's ready state; `dirty`
   * drives the per-row Save button (controlled-input pattern, like notes).
   */
  aiSettings: Record<AiScope, AiSettingState>;
  /**
   * #257 — Ephemeral session state: initiative tracker (07 §13.9.1).
   * Lives entirely in client memory; never persisted to the op log; cleared
   * when the user navigates away or resets. JSON-serialisable Records only
   * (llui constraint — no Maps/Sets/Dates/class instances).
   */
  initiative: InitiativeState;
  /**
   * #257 — Dice roller session state. The in-progress notation the GM is
   * typing + the most-recent rolls (last N, capped at {@link DICE_HISTORY_CAP}).
   * Entirely ephemeral; cleared with the initiative state.
   */
  diceInput: string;
  /** Last dice parse error message (null = no error). */
  diceError: string | null;
  /** Recent roll history, newest-first. Capped at {@link DICE_HISTORY_CAP}. */
  diceHistory: DiceRollEntry[];
  /**
   * #257 — Add-to-initiative form. Null when the form is closed; non-null
   * while the GM is typing a participant name + initiative value.
   */
  initiativeAddForm: { name: string; initiative: string; hp: string } | null;
  /**
   * #259 — End-encounter dropdown state. `null` when closed; `'open'` when
   * the GM has clicked "End encounter" and sees the Quick log / Open full form
   * choices.
   */
  combatLogMenu: 'open' | null;
  /**
   * #259 — Full-form combat log modal state. Non-null while the modal is open.
   * Pre-populated from the current initiative state at "Open full form" time.
   */
  combatLogForm: CombatLogFormState | null;
  /**
   * #260 — Ephemeral narrative combat panel turn log (07 §13.9.5).
   * Append-only within one encounter; cleared with initiative state on reset.
   * JSON-serialisable Records only (llui constraint).
   */
  narrativeCombatTurnLog: TurnLog;
  /**
   * #260 — Inline free-text input for the end-turn-with-effect action (affordance 6).
   * Null when the inline form is closed.
   */
  narrativeCombatEffectInput: string | null;
  /**
   * #260 — Inline dice notation input for the save/check roll (affordance 5).
   * Null when the form is closed; non-null while the GM types a spec (e.g. "1d20").
   * Keyed to the entry id being rolled so the panel knows which entry to roll for.
   */
  narrativeCombatSaveInput: { entryId: string; spec: string } | null;
  /**
   * #260 — Inline damage amount input for damage assignment (affordance 2).
   * Keyed to the entry id; null when not active.
   */
  narrativeCombatDamageInput: { entryId: string; amount: string } | null;
  /**
   * #263 — Campaign roster panel state: pending email invites + active links,
   * fetched from `GET /campaigns/:cid/invites` (GM-only). Null = not yet loaded.
   */
  inviteState: {
    emailInvites: readonly { id: string; invited_email: string; role: string; expires_at: string }[];
    links: readonly { id: string; role: string; created_at: string; expires_at: string | null; use_count: number }[];
  } | null;
  /**
   * #263 — Invite-by-email form state (settings Roster panel).
   */
  inviteEmailForm: { email: string; role: 'gm' | 'player'; sending: boolean } | null;
  /**
   * #265 — Rotating-facilitator modal: open state for the designate-/hand-off
   * badge dialog. `null` = closed. `mode` distinguishes owner-designating
   * (no current facilitator or owner action) vs facilitator-handing-off
   * (current facilitator passes the badge to another member).
   * `selectedUserId` is the user the GM has highlighted in the picker.
   */
  facilitatorModal: {
    mode: 'designate' | 'handoff';
    selectedUserId: string | null;
  } | null;
}

/**
 * #259 — Full-form combat log modal state (07 §13.9.2). Plain JSON-serialisable
 * (llui constraint — no Maps/Sets/Dates). Pre-populated from initiative state;
 * the GM can override fates, add loot/world-state notes before logging.
 */
export interface CombatLogFormState {
  /** Snapshot of the initiative at end-encounter time. */
  snapshot: InitiativeState;
  /** Entity ref for the combat location; empty string = no location. */
  locationRef: string;
  /** GM overrides for participant fates; keyed by entry id. */
  fateOverrides: Record<string, ParticipantFate | 'fled'>;
  /** Free-text loot notes (goes into world-state or as Item drafts). */
  lootNotes: string;
  /** Free-text world-state / narrative outcome notes. */
  worldStateNotes: string;
}

/** Per-campaign AI extension scopes (campaign-wide note + per-task). */
export type AiScope = 'global' | 'prose_suggest' | 'draft_entity' | 'image_generate';
export const AI_SCOPES: readonly AiScope[] = [
  'global',
  'prose_suggest',
  'draft_entity',
  'image_generate',
];
export interface AiSettingState {
  value: string;
  dirty: boolean;
  loaded: boolean;
}

/** Maximum number of recent dice rolls kept in the session history. */
const DICE_HISTORY_CAP = 20;

/**
 * One entry in the dice-roll history (#257). A JSON-serialisable snapshot of
 * a completed `DiceRoll` — no class instances, no Dates.
 */
export interface DiceRollEntry {
  /** Unique entry id (monotonic, client-only). */
  id: string;
  /** The notation string the GM entered. */
  notation: string;
  /** Per-die values in roll order. */
  dice: number[];
  /** Indices of kept dice. */
  keptIndices: number[];
  /** Final total after modifier. */
  total: number;
  /** Human-readable breakdown, e.g. "[3, 5] + 2 = 10". */
  breakdown: string;
}

/** Convert a `DiceRoll` + unique id into a serialisable history entry. */
function toDiceRollEntry(r: DiceRoll, id: string): DiceRollEntry {
  return {
    id,
    notation: r.notation,
    dice: [...r.dice],
    keptIndices: [...r.keptIndices],
    total: r.total,
    breakdown: rollBreakdown(r),
  };
}

let _diceEntryCounter = 1;
function mintDiceEntryId(): string {
  return `dice_${_diceEntryCounter++}`;
}

function emptyAiSettings(): Record<AiScope, AiSettingState> {
  return {
    global: { value: '', dirty: false, loaded: false },
    prose_suggest: { value: '', dirty: false, loaded: false },
    draft_entity: { value: '', dirty: false, loaded: false },
    image_generate: { value: '', dirty: false, loaded: false },
  };
}

/**
 * Hydrate the per-scope prompt-extension editor buffer from the folded op-log
 * config (#186 / ADR-0004 §5). The extensions are the single
 * `ai_prompt_extensions` config value; each scope is mapped to a buffer row
 * marked `loaded`. A row the GM is currently editing (`dirty`) is preserved as
 * is — exactly the `factDirty` no-stomp contract — so a config rebuild (the
 * optimistic post-save fold, or a cross-device update) never clobbers live
 * typing. The values are reactive: an unset scope or absent key reads as ''.
 */
function hydrateAiSettings(
  config: ConfigState,
  current: Record<AiScope, AiSettingState>,
): Record<AiScope, AiSettingState> {
  const ext = config.ai_prompt_extensions ?? {};
  const next = {} as Record<AiScope, AiSettingState>;
  for (const scope of AI_SCOPES) {
    const cur = current[scope];
    if (cur.dirty) {
      next[scope] = { ...cur, loaded: true };
      continue;
    }
    next[scope] = { value: ext[scope] ?? '', dirty: false, loaded: true };
  }
  return next;
}

export type Msg =
  | { type: 'navigate'; route: AppRoute }
  | { type: 'session/loaded'; session: Session }
  | { type: 'session/error'; message: string }
  // #152 — account / profile settings page.
  | { type: 'account/load-requested' }
  | { type: 'account/loaded'; user: import('./auth/api.js').CurrentUser }
  | { type: 'account/load-failed'; message: string }
  | { type: 'account/twofa-enable-requested' }
  | { type: 'account/twofa-setup-ready'; secretBase32: string; otpauthUri: string }
  | { type: 'account/twofa-code-input'; code: string }
  | { type: 'account/twofa-confirm-requested' }
  | { type: 'account/twofa-confirmed'; backupCodes: string[] }
  | { type: 'account/twofa-cancel' }
  | { type: 'account/twofa-disable-requested' }
  | { type: 'account/password-reset-requested' }
  | { type: 'account/recovery-email-input'; value: string }
  | { type: 'account/recovery-password-input'; value: string }
  | { type: 'account/recovery-email-save-requested' }
  | { type: 'account/recovery-email-saved'; recoveryEmail: string | null }
  | { type: 'account/delete-confirm-input'; value: string }
  | { type: 'account/delete-requested' }
  | { type: 'account/action-failed'; message: string }
  | { type: 'account/notice'; message: string }
  | { type: 'account/dismiss-notice' }
  // #89 — one-time GM recovery / backup disclaimer + export.
  | { type: 'recovery-disclaimer/dismiss' }
  | { type: 'recovery-disclaimer/export-now' }
  | { type: 'export/run'; campaignId: string }
  | { type: 'export/recorded'; campaignId: string; at: string }
  | {
      type: 'visibility/added';
      subject: string;
      predicate: string;
      value: unknown;
      pending?: true;
      version?: string;
    }
  | { type: 'visibility/removed'; subject: string; predicate: string }
  | {
      type: 'private-note/loaded';
      entityId: string;
      body: string;
      updatedAt: string | null;
    }
  | { type: 'private-note/edit'; entityId: string; body: string }
  | { type: 'private-note/save'; entityId: string }
  | { type: 'private-note/saved'; entityId: string; updatedAt: string }
  // #131 — generic entity creation. The root drives the per-root
  // default name template + the entity_create op payload's root field.
  | { type: 'create-entity'; root: EntityRoot }
  | { type: 'open-root-picker' }
  | { type: 'close-root-picker' }
  // Bug 4 — new-entity name modal. Opens directly from the list-view
  // shortcut button or from a root-picker selection; the substrate
  // mutation happens only on `submit-new-entity`, with the user-typed
  // name. The legacy `create-entity` Msg is preserved for the palette
  // quick-create path (different UX: the palette already has a typed
  // query, so eager creation is appropriate there).
  | { type: 'open-new-entity-modal'; root: EntityRoot; traits?: readonly string[] }
  | { type: 'close-new-entity-modal' }
  | { type: 'update-new-entity-name'; name: string }
  | { type: 'submit-new-entity' }
  // #81 — "Create instance" from a Lore. Open captures the source
  // Lore id; the sheet picks a root (default character) + name; submit
  // creates the entity and writes an `instance_of` ref back to the source.
  | { type: 'open-instance-modal'; sourceId: string }
  | { type: 'close-instance-modal' }
  | { type: 'update-instance-name'; name: string }
  | { type: 'set-instance-root'; root: EntityRoot }
  | { type: 'submit-instance' }
  // B4 (#237) — materialize a virtual recurrence instance into a real `event`
  // entity. `payload` carries the rule defaults copied as canon facts;
  // `trigger` (when present) is the auto-materialization op that "referenced"
  // the instance (a fact / reveal / LLM draft), now anchored to the new id.
  | { type: 'materialize-instance'; payload: MaterializePayload; trigger?: MaterializeTrigger }
  // #139 — trait attach/detach lifecycle.
  | { type: 'open-trait-picker'; entityId: string }
  | { type: 'history/open'; entityId: string; predicate: string }
  | { type: 'history/close' }
  | { type: 'trait/highlight'; traitId: string | null }
  | { type: 'close-trait-picker' }
  | { type: 'trait-picker-query'; query: string }
  | { type: 'attach-trait'; traitId: string }
  // Entity soft-delete (reversible "move to Trash") — request opens the
  // confirm; confirm appends `entity.soft_delete` + leaves the page; cancel
  // closes the dialog.
  | { type: 'request-delete-entity'; entityId: string }
  | { type: 'confirm-delete-entity' }
  | { type: 'cancel-delete-entity' }
  | { type: 'open-controller-picker'; entityId: string }
  | { type: 'close-controller-picker' }
  | {
      type: 'campaign-members/loaded';
      members: readonly { user_id: string; email: string; role: string; is_owner?: boolean }[];
    }
  | { type: 'set-controller'; entityId: string; userId: string }
  | { type: 'clear-controller'; entityId: string }
  | { type: 'restore-entity'; entityId: string }
  | { type: 'request-detach-trait'; entityId: string; traitId: string }
  | { type: 'confirm-detach-trait' }
  | { type: 'cancel-detach-trait' }
  // #73 — trait editor (define a homebrew trait + its predicate fields).
  // No payload — the reducer resolves `forRoot` from the entity the
  // trait picker is currently open for (mirrors attachTraitToPickerTarget).
  | { type: 'open-trait-editor' }
  | { type: 'close-trait-editor' }
  | { type: 'trait-editor-set-id'; value: string }
  | { type: 'trait-editor-add-field' }
  | { type: 'trait-editor-set-field'; index: number; patch: Partial<TraitEditorField> }
  | { type: 'trait-editor-remove-field'; index: number }
  // Tuple sub-field arms (ADR-0002). `field` indexes the parent tuple field;
  // `sub` indexes the sub-field within its `tupleFields`.
  | { type: 'trait-editor-add-tuple-field'; field: number }
  | { type: 'trait-editor-set-tuple-field'; field: number; sub: number; patch: Partial<TupleEditorField> }
  | { type: 'trait-editor-remove-tuple-field'; field: number; sub: number }
  | { type: 'trait-editor-toggle-risky'; value: boolean }
  | { type: 'trait-editor-submit' }
  // #schema-settings — open a blank trait-editor form on the settings page
  // (no picker target). Seeds create mode with an empty `appliesTo` so the
  // form shows the roots multi-select.
  | { type: 'open-trait-editor-blank' }
  // #schema-settings — toggle a root in the create-mode `applies_to` set.
  | { type: 'trait-editor-toggle-applies-to'; root: EntityRoot }
  | { type: 'edit-trait'; traitId: string }
  | { type: 'delete-current-trait' }
  | { type: 'retire-trait'; traitId: string }
  // #schema-settings — retire a homebrew predicate directly from the
  // traits/predicates list (predicates are otherwise authored as trait fields).
  | { type: 'retire-predicate'; predicateId: string }
  // #177 / ADR-0004 §1/§4 — custom-calendar authoring on the settings
  // Calendars tab. `calendar-editor` wraps every granular `ce-*`/`cal-*`
  // edit/lifecycle intent the stateless `calendarEditor` atom emits;
  // `calendar-save` folds the working draft to a `pack.define_calendar` op;
  // `calendar-retire` appends a `pack.retire_calendar`; `calendar-set-primary`
  // points the `canonical_calendar` setting at a definition (config.set).
  | { type: 'calendar-editor'; msg: CalendarEditorMsg }
  | { type: 'calendar-save' }
  | { type: 'calendar-retire'; calendarId: string }
  | { type: 'calendar-set-primary'; calendarId: string }
  // `close_at` an open cycle instance (§4.1, #232) — fills `end_day` via a
  // `pack.define_calendar` definition update (LWW), never a fact.
  | { type: 'calendar-close-cycle'; handle: OpenCycleHandle; closeDay: number }
  // Usage-aware retire confirm — `retire-trait` / `retire-predicate` /
  // `delete-current-trait` open this overlay instead of firing when the
  // definition is still in use; these resolve it.
  | { type: 'confirm-schema-retire' }
  | { type: 'cancel-schema-retire' }
  // #schema-settings — Entity Types tab CRUD (define `(root, traits[])`
  // quick-create templates). The editor renders inline on the Types tab.
  | { type: 'open-type-editor-blank' }
  | { type: 'edit-type'; typeId: string }
  | { type: 'close-type-editor' }
  | { type: 'type-editor-set'; patch: Partial<{ typeId: string; label: string; root: EntityRoot }> }
  | { type: 'type-editor-toggle-trait'; traitId: string }
  | { type: 'type-editor-submit' }
  | { type: 'retire-type'; typeId: string }
  // #units / ADR-0002 — campaign units editor (define dimensions + units).
  | { type: 'open-units-editor' }
  | { type: 'close-units-editor' }
  | { type: 'units-editor-set-dimension'; patch: Partial<UnitsEditorDimensionDraft> }
  | { type: 'units-editor-submit-dimension' }
  | { type: 'units-editor-set-unit'; patch: Partial<UnitsEditorUnitDraft> }
  | { type: 'units-editor-submit-unit' }
  | { type: 'retire-dimension'; dimensionId: string }
  | { type: 'retire-unit'; unitId: string }
  // Campaign boot lifecycle Msgs (fired by `bootChooseCampaign`).
  | { type: 'campaigns-boot/loading' }
  | { type: 'campaigns-boot/empty' }
  | { type: 'campaigns-boot/loaded'; campaigns: readonly CampaignSummary[] }
  | { type: 'campaigns-boot/error'; message: string }
  | { type: 'actions-overlay/toggle' }
  | { type: 'actions-overlay/close' }
  | { type: 'open-palette' }
  | { type: 'close-palette' }
  | { type: 'palette-query'; value: string }
  // #134 — arrow-key navigation. The reducer clamps to the rendered
  // list length so callers don't need to know it.
  | { type: 'palette-move'; delta: -1 | 1 }
  // #134 — Enter on the palette input. The reducer reads the active
  // row from the model and turns it into either a navigate or a
  // create-entity Msg.
  | { type: 'palette-activate' }
  | { type: 'edit-fact'; entityId: string; predicate: string; value: unknown }
  | { type: 'invalid-fact'; entityId: string; predicate: string; error: string }
  | { type: 'soft-delete-fact'; entityId: string; predicate: string }
  // LLM author mode (Phase B). `llm-suggest-prose` asks the worker to
  // draft prose for one (entity, predicate); the result lands as a
  // draft-status fact authored by the LLM, surfaced in the Draft Inbox.
  // `ratify-draft` / `reject-draft` are the GM's inbox actions.
  | { type: 'llm-suggest-prose'; entityId: string; predicate: string; instruction?: string }
  | { type: 'llm-suggest-succeeded'; entityId: string; predicate: string }
  | { type: 'llm-suggest-failed'; entityId: string; predicate: string; error: string }
  // Live streaming preview (per-fact ✨ prose).
  | { type: 'llm-stream-thinking' }
  | { type: 'llm-stream-delta'; delta: string }
  | { type: 'ratify-draft'; entityId: string; predicate: string }
  | { type: 'reject-draft'; entityId: string; predicate: string }
  // #246 — session-HUD pending lane: inline ratify (→canon, an explicit
  // per-fact GM gesture) / reject (→retract) on a `pending` fact during play.
  | { type: 'pending-ratify'; entityId: string; predicate: string }
  | { type: 'pending-reject'; entityId: string; predicate: string }
  // Phase C — per-page "Draft with AI" modal.
  | { type: 'open-page-draft'; entityId: string }
  | { type: 'page-draft-prompt'; value: string }
  | { type: 'page-draft-submit' }
  | { type: 'close-page-draft' }
  // #251 — bulk world-generation (decision 6). The config modal walks the GM
  // through target → prompt/depth/mode → generate (no pre-run cost estimate).
  // The drafts arrive via the DO sync broadcast and fold into `bundles`.
  | { type: 'open-bundle-gen'; target: BulkGenTarget }
  | { type: 'bundle-gen-prompt'; value: string }
  | { type: 'bundle-gen-depth'; value: BundleDepth }
  | { type: 'bundle-gen-mode'; value: BundleMode }
  | { type: 'bundle-gen-confirm' }
  | { type: 'bundle-gen-done' }
  | { type: 'bundle-gen-failed'; error: string }
  | { type: 'close-bundle-gen' }
  // ADR-0008 §1/§6 (T7, #281) — LLM-led interactive authoring session. `open`
  // starts the session (create POST → `authoring-session-created`); `send-turn`
  // opens the SSE `/turn` stream whose events fold in as
  // token/tool/artifact-diff/assistant-message/turn-done/error; `commit` ratifies
  // the artifact (commit-done closes / commit-refused surfaces advisories).
  | { type: 'open-authoring'; target: AuthoringTarget }
  | { type: 'authoring-session-created'; sessionId: string }
  | { type: 'authoring-direction-input'; value: string }
  | { type: 'authoring-send-turn' }
  | { type: 'authoring-token'; text: string }
  | { type: 'authoring-tool'; name: string; status: 'started' | 'succeeded' | 'failed'; detail?: string }
  | { type: 'authoring-artifact-diff'; artifact: TraitAuthoringArtifact }
  | { type: 'authoring-assistant-message'; text: string }
  | { type: 'authoring-turn-done' }
  | { type: 'authoring-error'; error: string }
  | { type: 'authoring-commit' }
  | { type: 'authoring-commit-done' }
  | { type: 'authoring-commit-refused'; advisories: AuthoringAdvisory[] }
  | { type: 'close-authoring' }
  // #251 — bundle review (decision 1/3/8). Accept = GM-authored set_status→canon
  // (the #223 backdoor stays closed); reject = the existing draft soft-delete;
  // auto-promote-with-flag is an opt-in per bundle; regen links a new bundle.
  | { type: 'bundle-accept-all'; bundleId: string }
  | { type: 'bundle-reject-all'; bundleId: string }
  | { type: 'bundle-accept-member'; entityId: string; predicate: string }
  | { type: 'bundle-reject-member'; entityId: string; predicate: string }
  | { type: 'bundle-toggle-auto-promote'; bundleId: string }
  | { type: 'open-bundle-regen'; bundleId: string }
  | { type: 'bundle-regen-prompt'; value: string }
  | { type: 'close-bundle-regen' }
  | { type: 'bundle-regen'; bundleId: string; prompt: string }
  // #251 §14.2 — clear the GM-only `unreviewed_bulk` marker (the review-pass).
  | { type: 'clear-review-state'; entityId: string; predicate: string }
  // Phase D — per-fact image generation.
  | { type: 'llm-generate-image'; entityId: string; predicate: string; instruction?: string }
  | { type: 'llm-image-succeeded'; entityId: string; predicate: string; assetId: string; url: string }
  | { type: 'llm-image-failed'; entityId: string; predicate: string; error: string }
  // Asset thumbnails — a resolved signed content URL for an asset id, so
  // asset-kind facts render as images in the editor + draft inbox.
  | { type: 'asset-url-resolved'; assetId: string; url: string }
  // Per-campaign AI settings (GM prompt extensions). The buffer hydrates from
  // the folded op-log config (`hydrateAiSettings`); `edit` marks a row dirty,
  // `save` writes the merged `ai_prompt_extensions` config.set op (#186).
  | { type: 'ai-setting/edit'; scope: AiScope; value: string }
  | { type: 'ai-setting/save'; scope: AiScope }
  // Auditor rule severity overrides (§5.2.1). `loaded` hydrates on boot;
  // `set` changes one rule (value 'default' clears the override).
  | { type: 'auditor-overrides-loaded'; overrides: AuditorRuleOverrides }
  | { type: 'set-auditor-override'; rule: string; value: SeverityOverrideValue | 'default' }
  // #186 / ADR-0004 §5 — a typed Campaign-Settings change. Carries the
  // already-built `config.set` payload (key-typed at the call site via
  // `configSet(key, value)`), which the reducer turns into a `submit-config`
  // effect. Optimistic: the value reads back from the folded `Model.config`
  // on the next `replica/entities-rebuilt`, so no Model field is set here.
  | { type: 'set-config'; payload: ConfigSetPayload }
  | { type: 'rollback-fact'; entityId: string; predicate: string; toIndex: number }
  // Bug 1 — the "Saved ✓" stamp only fires after `replica.appendLocal`
  // actually succeeds. The previous shape stamped the timestamp inside
  // the reducer arm BEFORE the submit effect ran, which lied to the GM
  // when the substrate was missing. `fact-actually-saved` is dispatched
  // by `submitFact` on the success path; `fact-save-failed` surfaces a
  // visible error and keeps the dirty flag set.
  | {
      type: 'fact-actually-saved';
      entityId: string;
      predicate: string;
      /** Captured `factDirtyEpoch[key]` from the dispatching effect; the
       * reducer compares against the model's current epoch and only
       * clears `factDirty[key]` when they match. Undefined means
       * "the originating effect did not intersect the dirty signal";
       * the reducer treats that as a stale ACK and keeps any current
       * dirty flag intact. */
      dirtyEpoch?: number;
    }
  | { type: 'fact-save-failed'; entityId: string; predicate: string; error: string }
  // #156 — visible save affordance. `fact-input-dirty` marks the row
  // as having unsaved input; `fact-saved-clear` clears the transient
  // "saved ✓" badge after the timeout fires. The optional `buffer`
  // field carries the live DOM value so the editor's `value`
  // accessor can reflect what the user typed (the canonical llui
  // dirty-buffer accessor pattern — see Model.inputBuffer).
  | {
      type: 'fact-input-dirty';
      entityId: string;
      predicate: string;
      buffer?: string;
    }
  | { type: 'fact-saved-clear'; entityId: string; predicate: string }
  | { type: 'fact-focus'; entityId: string; predicate: string }
  | { type: 'fact-blur'; entityId: string; predicate: string }
  | { type: 'fact-hint-dismiss' }
  | { type: 'set-connection'; state: ConnectionState }
  // Bug 3 — top-of-page substrate banner state. `set-substrate-status`
  // flips between opening / persistent / memory / failed; the dialog
  // toggle opens a small details surface with the browser-version
  // requirements + the active error message.
  | {
      type: 'set-substrate-status';
      status:
        | { kind: 'opening' }
        | { kind: 'persistent' }
        | { kind: 'memory'; opfsError?: string }
        | { kind: 'failed'; error: string; errorName?: string };
    }
  | { type: 'open-substrate-dialog' }
  | { type: 'close-substrate-dialog' }
  | {
      type: 'replica/entities-rebuilt';
      entities: Record<string, EntityState>;
      config: ConfigState;
      /** #251 — the bundle registry folded from the same op log. */
      bundles: BundleRegistry;
      /**
       * LLM-never-canon: per-fact last-canon value (see `Model.shadowedCanon`).
       * Optional — omitted by unit-test dispatches that don't exercise reject;
       * the reducer defaults it to `{}` (⇒ reject soft-deletes, never promotes).
       */
      shadowedCanon?: Record<string, Record<string, { value: unknown }>>;
    }
  | { type: 'sync/active' }
  | { type: 'sync/settled' }
  | { type: 'replica/conflict'; conflict: DraftWithConflict }
  | { type: 'set-pending-policy'; policy: PendingAutoRatifyPolicy }
  | { type: 'set-cursor'; cursor: number | null }
  // 226.1 (#245) — per-Viewer Preview-as picker. `viewer === null` (or a GM
  // viewer) returns to the GM identity view; a non-GM viewer enters preview
  // mode and triggers a `compute-preview-projection` effect.
  | { type: 'set-preview-viewer'; viewer: ViewerId | null }
  // The chokepoint output landed (mapped to value-only rows). Stored in
  // `previewProjection`. Stale results (the preview viewer changed mid-flight)
  // are dropped in the reducer by comparing the carried `viewerKey`.
  | { type: 'preview-projection-computed'; viewerKey: string; rows: Record<string, ProjectedFactRow> }
  // #258 — Table View. `table/focus` folds a Pin-to-Table / un-pin action into
  // the ephemeral table focus and re-broadcasts. `table/set-designated-viewer`
  // changes WHOSE projection the table renders (default = party Viewer; a GM/
  // stale override collapses to the default). `table/broadcast-received` lands
  // a broadcast frame when THIS tab is the Table tab (pure renderer).
  | { type: 'table/focus'; action: TableFocusAction }
  | { type: 'table/set-designated-viewer'; viewer: ViewerId | null }
  | { type: 'table/broadcast-received'; broadcast: TableBroadcast }
  // #271 — ephemeral attention signals. `table/attention-toggle` enables /
  // disables attention mode (the pointing cursor + "Look at this" button). When
  // disabled, any active pointer or ripple is cleared and the overlay is hidden.
  // `table/attention-pointer-move` tracks the GM's cursor on the table surface
  // (normalised [0,1]×[0,1]); `table/attention-pointer-clear` clears it when
  // the cursor leaves the surface. `table/attention-ripple` fires a "Look at
  // this" halo at the normalised coordinates. `table/attention-ripple-clear`
  // clears the ripple after the TTL; `seq` guards against clearing a newer ripple.
  | { type: 'table/attention-toggle' }
  | { type: 'table/attention-exit' }
  | { type: 'table/attention-pointer-move'; x: number; y: number }
  | { type: 'table/attention-pointer-clear' }
  | { type: 'table/attention-ripple'; x: number; y: number }
  | { type: 'table/attention-ripple-clear'; seq: number }
  // As-of scrubber (ADR-0005 §3, #192) — set / clear the EPHEMERAL browse
  // cursor. `browse-set` with `day === campaignCursor` (or null) collapses to
  // "browsing now" (browseCursor := null). Never persisted, never synced.
  | { type: 'browse-set'; day: number | null }
  | { type: 'browse-clear' }
  // Hydrate the cursor from the server session response. Sets the
  // model + local cache without re-PUTing (the value came FROM the
  // server). calendarId is the calendar the server cursor is keyed
  // to (null when no date set).
  | { type: 'cursor/hydrate'; cursor: number | null; calendarId: string | null }
  // Reconcile the Campaign date when the WS (re)connects. Role-aware:
  // the GM re-pushes its local value (authority — covers an edit made
  // while offline that never reached the server); a player re-pulls the
  // authoritative value from the server.
  | { type: 'cursor/reconnect-sync' }
  | { type: 'set-active-calendar'; calendarId: string }
  // ---- calendar-grid date picker (Model.datePicker) ----
  // Open the grid for field `id`, seeding the view cursor from the field's
  // current value (the host computes the seed cursor).
  | { type: 'date-picker-open'; id: string; cursor: CalendarCursor }
  // The user navigated the open grid (prev/next, month, year, era, "Now").
  | { type: 'date-picker-navigate'; cursor: CalendarCursor }
  // The user opened / closed / switched the inline month/era overlay.
  | { type: 'date-picker-overlay'; overlay: CalendarDatePickerOverlay }
  // Close the grid (a day was committed, dismissed, or another opened).
  | { type: 'date-picker-close' }
  | {
      type: 'set-fact-validity';
      entityId: string;
      predicate: string;
      validity: FactValidity | null;
    }
  // Valid-time succession (ADR-0005 §3 / #188) — "this changed as of day X".
  // Closes the predecessor segment + opens a new `seg_…` successor; two
  // ordinary submit-fact effects, never a silent merge.
  | {
      type: 'succeed-fact';
      entityId: string;
      predicate: string;
      atDay: number;
      value: unknown;
    }
  | { type: 'auth/sign-out' }
  // IV.B.2 (#78) — mobile entity-action sheet.
  | { type: 'open-entity-sheet-reveal'; entityId: string; predicate: string }
  | { type: 'open-entity-sheet-visited'; subjectId: string; targetId: string }
  | { type: 'close-entity-sheet' }
  // Per-attribute reveal (Phase 2 / 30 §7.7) — desktop Viewer-Matrix popover.
  | { type: 'open-reveal-menu'; entityId: string; predicate: string }
  | { type: 'close-reveal-menu' }
  | { type: 'reveal-fact'; entityId: string; predicate: string; viewer: ViewerId }
  // Routine facet bulk-reveal (Phase 2 / 30 §7.10.2).
  | {
      type: 'open-facet-reveal-menu';
      entityId: string;
      facetId: string;
      label: string;
      predicates: string[];
    }
  | { type: 'close-facet-reveal-menu' }
  | { type: 'reveal-facet'; entityId: string; predicates: string[]; viewer: ViewerId }
  // Reveal Composer for sensitive facets (Phase 2 / 30 §7.10.3).
  | {
      type: 'open-reveal-composer';
      entityId: string;
      facetId: string;
      label: string;
      predicates: string[];
    }
  | { type: 'close-reveal-composer' }
  | { type: 'composer-set-viewer'; viewer: ViewerId }
  | { type: 'composer-toggle-fact'; predicate: string }
  | { type: 'composer-confirm' }
  // Belief layer (Phase 2 / 03 §9) — "Draft belief" modal.
  | { type: 'open-belief-modal'; entityId: string }
  | { type: 'close-belief-modal' }
  | { type: 'belief-set-believer'; viewer: ViewerId }
  | { type: 'belief-set-predicate'; predicate: string }
  | { type: 'belief-set-value'; value: string }
  | { type: 'submit-belief' }
  // Clone-a-lie (Phase 2 / 03 §9 / B3).
  | { type: 'open-belief-clone'; entityId: string; sourceKey: string; base: string; value: string; sourceBelieverKey: string }
  | { type: 'close-belief-clone' }
  | { type: 'clone-belief'; viewer: ViewerId }
  // Mark-corrected (Phase 2 / 03 §9 / B4).
  | { type: 'mark-belief-corrected'; entityId: string; beliefKey: string; value: string }
  | {
      type: 'mark-visited';
      subjectId: string;
      targetId: string;
    }
  // IV.B.4 (#80) — mobile private-notes sheet.
  | { type: 'open-notes-sheet'; entityId: string }
  | { type: 'close-notes-sheet' }
  // IV.B.3 (#79) — session HUD cursor advance / rewind.
  | { type: 'cursor-advance'; days: number }
  | { type: 'cursor-rewind-confirm'; days: number }
  | { type: 'cursor-rewind-cancel' }
  | { type: 'cursor-rewind'; days: number }
  // Entity page tab switching (per 30 §7.2 Overview/Facts split).
  | {
      type: 'set-entity-page-tab';
      value: 'overview' | 'facts' | 'reveals' | 'issues' | 'history' | 'connections';
    }
  // #257 — Dice roller (07 §13.9.1, session HUD). Pure ephemeral: never persisted.
  | { type: 'dice-input'; value: string }
  | { type: 'dice-roll' }
  | { type: 'dice-clear-history' }
  // #257 — Initiative tracker (07 §13.9.1, session HUD). Pure ephemeral.
  | { type: 'initiative-open-add-form' }
  | { type: 'initiative-add-form-name'; value: string }
  | { type: 'initiative-add-form-initiative'; value: string }
  | { type: 'initiative-add-form-hp'; value: string }
  | { type: 'initiative-add-form-submit' }
  | { type: 'initiative-add-form-cancel' }
  | { type: 'initiative-remove'; id: string }
  | { type: 'initiative-advance-turn' }
  | { type: 'initiative-toggle-condition'; id: string; condition: string }
  | { type: 'initiative-hp-delta'; id: string; delta: number }
  | { type: 'initiative-reset' }
  // #259 — End-encounter actions (07 §13.9.2).
  | { type: 'combat-log-menu-open' }
  | { type: 'combat-log-menu-close' }
  | { type: 'combat-log-quick-log' }
  | { type: 'combat-log-open-full-form' }
  | { type: 'combat-log-form-fate'; entryId: string; fate: ParticipantFate | 'fled' }
  | { type: 'combat-log-form-loot'; value: string }
  | { type: 'combat-log-form-world-state'; value: string }
  | { type: 'combat-log-form-location'; value: string }
  | { type: 'combat-log-form-submit' }
  | { type: 'combat-log-form-cancel' }
  // #260 — Narrative combat panel (07 §13.9.5). All actions are ephemeral
  // session state EXCEPT draft-narration which emits a submit-fact effect.
  /** Affordance 1: reaction roll on a non-PC entry. Rolls 2d6, stamps disposition. */
  | { type: 'narrative-panel-reaction-roll'; entryId: string }
  /** Affordance 2: open/close/change the damage input for an entry. */
  | { type: 'narrative-panel-damage-open'; entryId: string }
  | { type: 'narrative-panel-damage-change'; amount: string }
  | { type: 'narrative-panel-damage-apply' }
  | { type: 'narrative-panel-damage-cancel' }
  /** Affordance 3: toggle a condition on an entry. */
  | { type: 'narrative-panel-condition-toggle'; entryId: string; condition: string }
  /** Affordance 4: NPC quick-pick action. */
  | { type: 'narrative-panel-npc-action'; entryId: string; action: NpcAction }
  /** Affordance 5: open/close/change/roll the save/check input. */
  | { type: 'narrative-panel-save-open'; entryId: string }
  | { type: 'narrative-panel-save-change'; spec: string }
  | { type: 'narrative-panel-save-roll' }
  | { type: 'narrative-panel-save-cancel' }
  /** Affordance 6: open/close/change/submit the end-turn-with-effect input. */
  | { type: 'narrative-panel-effect-open' }
  | { type: 'narrative-panel-effect-change'; value: string }
  | { type: 'narrative-panel-effect-submit'; entryId: string }
  | { type: 'narrative-panel-effect-cancel' }
  /** Draft narration: emit a draft rich_text fact for a turn-log entry. */
  | { type: 'narrative-panel-draft-narration'; entry: TurnLogEntry; entityId: string }
  // #263 — campaign roster + invite panel (settings General, GM-only).
  | {
      type: 'roster/invites-loaded';
      emailInvites: readonly { id: string; invited_email: string; role: string; expires_at: string }[];
      links: readonly { id: string; role: string; created_at: string; expires_at: string | null; use_count: number }[];
    }
  | { type: 'roster/invite-email-change'; email: string }
  | { type: 'roster/invite-email-role'; role: 'gm' | 'player' }
  | { type: 'roster/invite-email-send' }
  | { type: 'roster/invite-email-sent' }
  | { type: 'roster/invite-revoke'; inviteId: string }
  | { type: 'roster/link-create'; role: 'gm' | 'player' }
  | { type: 'roster/link-close'; linkId: string }
  | { type: 'roster/member-remove'; userId: string }
  // #265 — Rotating-facilitator badge lifecycle.
  // `open-facilitator-modal` opens either the designate or handoff picker.
  | { type: 'facilitator/open-modal'; mode: 'designate' | 'handoff' }
  | { type: 'facilitator/close-modal' }
  | { type: 'facilitator/select-user'; userId: string }
  // `designate` — owner sets the session's facilitator (any member).
  | { type: 'facilitator/designate'; userId: string }
  // `hand-off` — current facilitator passes the badge to another member.
  | { type: 'facilitator/hand-off'; toUserId: string };

interface BindKeyboardEffect {
  type: 'bind-keyboard';
}
interface BootSessionEffect {
  type: 'boot-session';
}
interface OpenSubstrateEffect {
  type: 'open-substrate';
}
// #152 — account / profile settings page Effects.
interface AccountLoadEffect {
  type: 'account/load';
}
// #89 — persist the GM's recovery-disclaimer acknowledgement.
interface RecoveryDisclaimerMarkSeenEffect {
  type: 'recovery-disclaimer/mark-seen';
}
// #89 — trigger a campaign export (JSON + Markdown) as a file download.
interface ExportCampaignEffect {
  type: 'export-campaign';
  campaignId: string;
}
interface AccountTwofaSetupEffect {
  type: 'account/twofa-setup';
}
interface AccountTwofaConfirmEffect {
  type: 'account/twofa-confirm';
  code: string;
}
interface AccountTwofaDisableEffect {
  type: 'account/twofa-disable';
}
interface AccountPasswordResetEffect {
  type: 'account/password-reset';
  email: string;
}
interface AccountRecoveryEmailSaveEffect {
  type: 'account/recovery-email-save';
  recoveryEmail: string | null;
  /**
   * #F8 re-authentication. Non-optional: the route rejects the request without
   * it, and the reducer refuses to emit this effect until the GM has typed one,
   * so an empty value can never reach here.
   */
  currentPassword: string;
}
interface AccountDeleteEffect {
  type: 'account/delete';
}
interface LoadPrivateNoteEffect {
  type: 'load-private-note';
  entityId: string;
}
interface SavePrivateNoteEffect {
  type: 'save-private-note';
  entityId: string;
  body: string;
}
/** Fetch the Campaign's members (for the controller picker). III.B.4. */
interface LoadCampaignMembersEffect {
  type: 'load-campaign-members';
}
/** #263 — Fetch pending invites + active links for the roster panel. GM-only. */
interface LoadInviteStateEffect {
  type: 'roster/load-invites';
}
/** #263 — Send an email invite for the campaign. */
interface SendEmailInviteEffect {
  type: 'roster/send-email-invite';
  email: string;
  role: 'gm' | 'player';
}
/** #263 — Revoke a pending email invite. */
interface RevokeEmailInviteEffect {
  type: 'roster/revoke-invite';
  inviteId: string;
}
/** #263 — Create a shareable join link. */
interface CreateInviteLinkEffect {
  type: 'roster/create-link';
  role: 'gm' | 'player';
}
/** #263 — Close a shareable join link. */
interface CloseInviteLinkEffect {
  type: 'roster/close-link';
  linkId: string;
}
/** #263 — Remove a member from the campaign. */
interface RemoveCampaignMemberEffect {
  type: 'roster/remove-member';
  userId: string;
}
interface AnnounceModeEffect {
  type: 'announce-mode';
  mode: 'gm' | 'player';
}
interface SignOutEffect {
  type: 'auth/sign-out';
}
interface SubmitEntityCreateEffect {
  type: 'submit-entity-create';
  subject: string;
  root: EntityRoot;
  traits: readonly string[];
}
/**
 * #259 — Submit a `bundle.declare` meta-op from the client (combat-log path).
 * The bulk-gen path uses the worker RPC; the combat-log path is client-emitted.
 */
interface SubmitBundleDeclareEffect {
  type: 'submit-bundle-declare';
  bundleId: string;
  source: BundleSource;
  prompt?: string;
}
interface SubmitFactEffect {
  type: 'submit-fact';
  subject: string;
  predicate: string;
  payload:
    // validity is tri-state — absent (preserve), a range (set), or
    // explicit null (clear to timeless). See FactMetaShape in
    // @dl/shared and the `set-fact-validity` reducer arm. `owner` carries a
    // belief owner when authoring a belief (Phase 2 / 03 §9); absent → canon.
    | {
        kind: 'fact.create' | 'fact.edit';
        value: unknown;
        validity?: FactValidity | null;
        owner?: FactOwner;
        /** Belief provenance (03 §9 / B3) — source belief key when cloned. */
        source_belief?: string;
        /** Bundle this fact belongs to (#251 / #259). */
        bundle_id?: string;
        /** Override the default `canon` status — use `'draft'` for bundle members. */
        status?: FactStatus;
      }
    /**
     * Set ONLY the validity of an existing fact, keeping its value.
     *
     * On the wire this is still a `fact.edit` — the fold pushes a new version
     * from `payload.value`, so there is no "preserve the value" wire signal and
     * a value must be supplied. The distinction is WHERE that value is read
     * from: this variant resolves it from the replica at submit time rather
     * than from the caller's folded view.
     *
     * That view (`model.entities`) is refreshed by an ASYNC refold, so for a
     * few hundred ms after a fact is created it does not yet contain that fact.
     * Sourcing the value from it there meant the write was dropped outright —
     * silently, with the popover already closed, so the field simply snapped
     * back to "Always". Measured: a day picked 0 ms after the save was lost,
     * 200 ms after it survived. When the view was merely STALE rather than
     * missing, the same read re-sent the stale value and reverted an edit the
     * user had just typed. The replica is authoritative and has neither
     * failure mode.
     */
    | { kind: 'fact.set-validity'; validity: FactValidity | null }
    | { kind: 'fact.soft_delete' };
  /**
   * §7.2 conflict-aware player edit: an explicit `base_version` to submit
   * instead of the locally-derived replica head. A player edits a fact that
   * lives in `projectedFacts` (value-only, NOT in their replica op-log), so the
   * replica head is null; we submit the projected chain head `version` so the
   * substrate's normal stale-detection fires (concurrent GM edit → reject_stale
   * → conflict; else canon). Absent → derive from the replica head (GM path).
   */
  baseVersionOverride?: string | null;
  /**
   * The model's `factDirtyEpoch[key]` value at the moment this
   * effect was dispatched. Carried through to `fact-actually-saved`
   * so the ACK can detect "user typed since submit" — the reducer
   * arm keeps the dirty flag set when the captured epoch is stale.
   * Optional so callers that don't intersect with the dirty signal
   * (e.g. `create-entity`, where the user never marked the row
   * dirty) can omit it; the reducer treats `undefined` as "always
   * stale" so it never wipes a freshly-typed dirty flag.
   */
  dirtyEpoch?: number;
}
/**
 * Per-attribute reveal (Phase 2 / 30 §7.7) — append a write-once `reveal`
 * op making one `(subject, predicate)` fact visible to `viewer`. The op's
 * envelope predicate is the synthetic `reveal:…` slot minted by
 * `revealEnvelopePredicate`; `base_version` is null (every reveal is its own
 * independent chain). There is no un-reveal.
 */
interface SubmitRevealEffect {
  type: 'submit-reveal';
  subject: string;
  predicate: string;
  viewer: ViewerId;
  /** Provenance for the reveal op; defaults to `gm_manual` when omitted. */
  source?: RevealSource;
}
/** #139 — append `entity.attach_trait` or `entity.detach_trait`. */
interface SubmitTraitMutationEffect {
  type: 'submit-trait-mutation';
  subject: string;
  op: 'attach' | 'detach';
  traitId: string;
}
/**
 * Soft-delete an entity (`entity.soft_delete`, ADR — reversible "move to
 * Trash"). Flows through the same `ENTITY_META_PREDICATE` chain as
 * create/attach; the fold sets `entity.soft_deleted` and every projection
 * already filters it out.
 */
interface SubmitEntitySoftDeleteEffect {
  type: 'submit-entity-soft-delete';
  subject: string;
}
/** Un-hide a soft-deleted entity (`entity.restore`) — the Trash → restore path. */
interface SubmitEntityRestoreEffect {
  type: 'submit-entity-restore';
  subject: string;
}
/**
 * #73 / ADR-0001 — append one or more `pack.define_*` / `pack.retire_*`
 * meta-ops to the campaign-local homebrew Pack. Submitted sequentially
 * (each chains off the prior on the single `(homebrew, __pack)` chain),
 * predicates before the trait that references them.
 */
interface SubmitPackOpsEffect {
  type: 'submit-pack-ops';
  payloads: readonly PackOpPayload[];
}
/**
 * #156 — schedule a `fact-saved-clear` Msg after `delayMs`. We use an
 * effect (rather than a setTimeout inside the reducer) so reducer
 * remains pure and side-effects are testable.
 */
interface ScheduleFactSavedClearEffect {
  type: 'schedule-fact-saved-clear';
  entityId: string;
  predicate: string;
  delayMs: number;
}
/**
 * Persist the Campaign date (session cursor) for the active campaign.
 * Keeps the cursor mutation reducers pure — the localStorage write
 * happens in the effect handler. Carries the calendar id so the
 * persisted value can be invalidated if the active calendar changes
 * (a canonical day means nothing across calendars).
 */
interface PersistCursorEffect {
  type: 'persist-cursor';
  calendarId: string;
  cursor: number | null;
  /**
   * When true (user-initiated change) the effect PUTs to the server so
   * the date syncs across devices / co-GMs, AND caches in localStorage.
   * When false (hydrate from server) it only refreshes the localStorage
   * cache — the value already came from the server, so re-PUTing would
   * be a redundant round-trip.
   */
  toServer: boolean;
}
/**
 * Persist the recently-viewed entity list for the active campaign to
 * localStorage. Keeps the `navigate` reducer pure — the write happens here.
 * Local-only UI state (rail "Recent"); never synced.
 */
interface PersistRecentEffect {
  type: 'persist-recent';
  recent: string[];
}
/**
 * Re-pull the Campaign date from the server (player reconnect path).
 * GETs /me/campaigns/:cid/session-cursor and dispatches cursor/hydrate.
 */
interface FetchSessionCursorEffect {
  type: 'fetch-session-cursor';
}
/**
 * 226.1 (#245) — compute the Preview-as projection for `viewer` at `day` (the
 * effective cursor) by reading the local replica op log and running it through
 * the `@dl/projection` chokepoint. The result is mapped to value-only rows and
 * dispatched as `preview-projection-computed`. `viewerKey` rides along so the
 * reducer can drop a stale result if the GM has since changed the picker. Only
 * dispatched for a non-GM viewer (GM = no projection needed; it sees canon).
 */
interface ComputePreviewProjectionEffect {
  type: 'compute-preview-projection';
  viewer: ViewerId;
  /** Effective cursor day (browse cursor, else session cursor); `null` = timeless. */
  day: number | null;
}
/**
 * #258 — (re)compute the designated-Viewer projection and BROADCAST it to the
 * Table View tab. Reads the local replica op log, runs it through the
 * `@dl/projection` chokepoint for `designatedViewer` at `day`, then posts a
 * `TableBroadcast` (the already-projected rows + the focus pointer) over the
 * same-origin `BroadcastChannel`. Only the GM main tab emits this; the Table tab
 * is a pure renderer. When `designatedViewer === null` (no Viewer yet) the
 * broadcast carries an empty row set + `designatedViewerNull: true` — the table
 * shows its placeholder, NEVER GM-truth.
 */
interface BroadcastTableEffect {
  type: 'broadcast-table';
  focus: TableFocus;
  designatedViewer: ViewerId | null;
  viewerLabel: string;
  day: number | null;
  /** Resolved asset-id → signed-URL map, so the Table tab can render handout images. */
  assetUrls: Record<string, string>;
  /** #271 — ephemeral pointer overlay (normalised [0,1]×[0,1]), or undefined. */
  attentionPointer?: TableAttentionPointer;
  /** #271 — ephemeral ripple trigger, or undefined when cleared. */
  attentionRipple?: TableAttentionRipple;
}
/**
 * #271 — broadcast ONLY the ephemeral attention signals (pointer + ripple).
 * Used by pointer-move and ripple-trigger so we don't re-read the op log on
 * every mouse-move — a fast path that posts an attention-only update onto the
 * channel without rerunning the projection.
 */
interface BroadcastAttentionEffect {
  type: 'broadcast-attention';
  attentionPointer: TableAttentionPointer | null;
  attentionRipple: TableAttentionRipple | null;
}
/**
 * #271 — schedule clearing the active ripple after the TTL has elapsed. The
 * `seq` guard ensures a later ripple (higher seq) prevents a stale clear from
 * landing.
 */
interface ScheduleAttentionRippleClearEffect {
  type: 'schedule-attention-ripple-clear';
  seq: number;
  delayMs: number;
}
/**
 * #258 — open + subscribe to the Table View `BroadcastChannel` for THIS tab's
 * role. The Table tab listens for `TableBroadcast` frames (→
 * `table/broadcast-received`) and announces a `table-hello` on open so the GM
 * tab re-broadcasts the current state to a freshly-opened display.
 */
interface TableChannelInitEffect {
  type: 'table-channel-init';
  role: 'gm' | 'table';
}
/**
 * LLM author mode (Phase B). POST the worker's suggest-prose route for
 * one (subject, predicate), then submit the returned text as a
 * draft-status fact authored by the LLM (the substrate accepts llm+draft;
 * the DO rejects llm canon/pending). The draft surfaces in the Draft
 * Inbox for GM ratification.
 */
interface LlmSuggestProseEffect {
  type: 'llm-suggest-prose';
  subject: string;
  predicate: string;
  instruction?: string;
}
/**
 * Deferral 3 — structured multi-fact. One call to the draft-entity route
 * returns several prose proposals; each lands as a draft fact. Per-key
 * pending is cleared via `llm-suggest-succeeded`/`-failed`.
 */
interface LlmDraftEntityEffect {
  type: 'llm-draft-entity';
  subject: string;
  predicates: string[];
  instruction?: string;
}
/**
 * Ratify / shelve a fact by appending a GM-authored `fact.set_status`
 * op (the LLM may never do this — the DO invariant blocks it). Kept
 * separate from `submit-fact` so the dirty-epoch ACK contract there
 * stays untouched.
 */
interface SubmitFactStatusEffect {
  type: 'submit-fact-status';
  subject: string;
  predicate: string;
  status: FactStatus;
  reason?: string;
  /**
   * Lossless reject — when present, the op also edits the value back to
   * this prior value (a single `fact.edit` carrying status), restoring the
   * canon a draft shadowed. Absent → a plain `fact.set_status` (ratify).
   */
  restoreValue?: { value: unknown };
  /**
   * #251 auto-promote-with-flag (decision 3). When present, the op is a
   * GM-authored `fact.edit` carrying (value + status + `review_state`) rather
   * than a bare `set_status` — because `fact.set_status` is `.strict()` and
   * cannot ride a `review_state`. The value must be supplied (`reviewValue`)
   * so the edit preserves the accepted draft's current value. This is still a
   * GM-authored canon transition — the #223 backdoor stays closed.
   */
  reviewState?: FactReviewState;
  /** The current draft value to carry on the auto-promote `fact.edit` (with `reviewState`). */
  reviewValue?: { value: unknown };
}
/**
 * #251 — call the worker's bulk-gen route. The worker grounds, runs ONE LLM
 * pass, caps fan-out, and ATOMICALLY appends the bundle (`bundle.declare` + N
 * llm-draft `fact.create` + entity stubs) via the DO `appendBundle` RPC. The
 * client does NOT append ops here — the drafts arrive via the sync broadcast
 * and fold into `bundles`. `regeneratedFrom` links a regen (decision 8).
 * Dispatches `bundle-gen-done` / `bundle-gen-failed`.
 */
interface BulkGenEffect {
  type: 'bulk-gen';
  request: BulkGenRequest;
  regeneratedFrom?: string;
}
/**
 * ADR-0008 §6 (T7, #281) — POST the create route to open an authoring session.
 * Body `{ target }`; the DO mints + persists the scratch session and returns
 * `{ session_id }`. Dispatches `authoring-session-created` / `authoring-error`.
 */
interface OpenAuthoringSessionEffect {
  type: 'open-authoring-session';
  target: AuthoringTarget;
}
/**
 * ADR-0008 §6 (T7, #281) — open the `/turn` SSE stream (`text/event-stream`,
 * `data: <json>\n\n` frames, each an {@link AuthoringServerEvent}). The consumer
 * reads `response.body`, splits on the blank-line frame boundary, and dispatches
 * a msg per event kind (token/tool/artifact_diff/assistant_message/turn_done/
 * error). Cancellable via the module-scope abort controller (a new turn or a
 * `close-authoring` aborts the in-flight stream).
 */
interface AuthoringTurnEffect {
  type: 'authoring-turn';
  sessionId: string;
  direction: string;
}
/**
 * ADR-0008 §5/§6 (T7, #281) — POST the `/commit` route to ratify the session's
 * artifact. On `{ ok: true }` dispatches `authoring-commit-done` (close); on
 * `{ ok: false, advisories }` dispatches `authoring-commit-refused` (surface the
 * blocking auditor findings, stay open).
 */
interface CommitAuthoringEffect {
  type: 'commit-authoring';
  sessionId: string;
}
/** ADR-0008 §6 (T7, #281) — abort the in-flight `/turn` SSE stream (no request). */
interface CancelAuthoringEffect {
  type: 'cancel-authoring';
}
/**
 * S9 (ADR-0005 §9, #196): compute the predecessor-CLOSE ops a draft's
 * succession implies, ready to apply when the GM ratifies. The LLM only ever
 * authored the additive dated draft; the close is *derived* here from the
 * draft's validity vs the existing canon segments of its base (owner-scoped,
 * uniqueness-key grouped) and applied on the GM's ratify click — never silently
 * merged. Pure (no registry/model coupling) so it unit-tests in isolation; the
 * caller supplies `uniquenessKey` from `loadGmRegistry()`.
 */
export function impliedClosesForRatify(
  facts: Record<string, FactState>,
  draftKey: string,
  uniquenessKey: readonly string[] | undefined,
): { key: string; payload: FactEditPayload }[] {
  const draftFact = facts[draftKey];
  if (!draftFact) return [];
  const base = splitSegmentKey(draftKey).base;
  const ownerKeyOf = (f: FactState): string =>
    f.owner.kind === 'belief' ? viewerKeyFn(f.owner.viewer) : 'canon';
  const valueOf = (f: FactState): unknown => f.versions[f.versions.length - 1]?.value;
  const draft: DraftSegment = {
    key: draftKey,
    value: valueOf(draftFact),
    validity: draftFact.validity,
    ownerKey: ownerKeyOf(draftFact),
  };
  const existing: ExistingSegment[] = [];
  for (const [key, f] of Object.entries(facts)) {
    if (key === draftKey || f.soft_deleted || f.status !== 'canon') continue;
    if (splitSegmentKey(key).base !== base) continue;
    existing.push({ key, value: valueOf(f), validity: f.validity, ownerKey: ownerKeyOf(f) });
  }
  return deriveImpliedSuccession(draft, existing, uniquenessKey).map((c) =>
    closeImpliedPredecessor(c, c.atDay),
  );
}
/**
 * Phase D — POST the worker's generate-image route, then submit the
 * returned asset id as a draft asset fact (LLM-authored). The signed
 * content URL is stashed so the inbox can render a thumbnail.
 */
interface LlmGenerateImageEffect {
  type: 'llm-generate-image';
  subject: string;
  predicate: string;
  /** Optional GM direction to steer the image (art style, framing, mood). */
  instruction?: string;
}
/** Resolve signed content URLs for asset ids so they render as thumbnails. */
interface ResolveAssetUrlsEffect {
  type: 'resolve-asset-urls';
  assetIds: string[];
}
/**
 * Load the per-campaign auditor rule severity overrides (§5.2.1) — read via
 * the DO's folded config (#179). Saving them is a `config.set` op
 * (`SubmitConfigEffect`, #186), not a dedicated PUT.
 */
interface LoadAuditorOverridesEffect {
  type: 'load-auditor-overrides';
}
/**
 * #186 / ADR-0004 §5 — submit one campaign `config.set` op over the WS sync
 * client. The op rides the reserved `__campaign` subject; the predicate is
 * the config key, so each key is its own LWW chain (its `base_version`
 * tracks only that key). No reveal/validity/belief envelope — config ops
 * are not facts. The DO write-gate (`assertConfigOpValid`, #176) re-validates
 * the key+value; the local fold (`foldConfigOps`) surfaces the change
 * optimistically before the server ACK.
 */
interface SubmitConfigEffect {
  type: 'submit-config';
  payload: ConfigSetPayload;
}
type SubstrateEffect =
  | ResolveAssetUrlsEffect
  | LoadAuditorOverridesEffect
  | SubmitConfigEffect
  | BindKeyboardEffect
  | BootSessionEffect
  | OpenSubstrateEffect
  | AccountLoadEffect
  | RecoveryDisclaimerMarkSeenEffect
  | ExportCampaignEffect
  | AccountTwofaSetupEffect
  | AccountTwofaConfirmEffect
  | AccountTwofaDisableEffect
  | AccountPasswordResetEffect
  | AccountRecoveryEmailSaveEffect
  | AccountDeleteEffect
  | SubmitEntityCreateEffect
  | SubmitBundleDeclareEffect
  | SubmitFactEffect
  | SubmitRevealEffect
  | SubmitTraitMutationEffect
  | SubmitEntitySoftDeleteEffect
  | SubmitEntityRestoreEffect
  | SubmitPackOpsEffect
  | LoadPrivateNoteEffect
  | LoadCampaignMembersEffect
  | LoadInviteStateEffect
  | SendEmailInviteEffect
  | RevokeEmailInviteEffect
  | CreateInviteLinkEffect
  | CloseInviteLinkEffect
  | RemoveCampaignMemberEffect
  | SavePrivateNoteEffect
  | AnnounceModeEffect
  | SignOutEffect
  | ScheduleFactSavedClearEffect
  | PersistCursorEffect
  | PersistRecentEffect
  | FetchSessionCursorEffect
  | LlmSuggestProseEffect
  | LlmDraftEntityEffect
  | SubmitFactStatusEffect
  | BulkGenEffect
  | OpenAuthoringSessionEffect
  | AuthoringTurnEffect
  | CommitAuthoringEffect
  | CancelAuthoringEffect
  | ComputePreviewProjectionEffect
  | BroadcastTableEffect
  | BroadcastAttentionEffect
  | ScheduleAttentionRippleClearEffect
  | TableChannelInitEffect
  | LlmGenerateImageEffect;
export type Effect = SubstrateEffect | ReturnType<typeof appRouteNavigate>;

const FACT_NAME = 'name';

const navMsg = (route: AppRoute): Msg => ({ type: 'navigate', route });

/**
 * B4 (#237) — the rule defaults a materialized virtual instance copies onto
 * its new `event` entity as canon facts (`06-timeline.md` §10.3). `name` is a
 * derived default (parent Lore name + firing date); `occurredAt` is the
 * computed `occurred_at` value; `location` / `participants` are inherited
 * refs. All editable afterward; the parent rule is untouched.
 */
interface MaterializePayload {
  /** The parent Lore id — the `instance_of` target + recurrence source. */
  readonly loreId: string;
  /** The new event's default name. */
  readonly name: string;
  /** The computed `occurred_at` value (game_date / range). */
  readonly occurredAt: unknown;
  /** Inherited `at_location` ref id, or undefined. */
  readonly location?: string;
  /** Inherited `participants` ref ids (possibly empty). */
  readonly participants: readonly string[];
}

/**
 * B4 (#237) — an auto-materialization trigger (`06-timeline.md` §10.3): the op
 * that "referenced" a virtual instance, re-anchored to the freshly-minted
 * event id. A `fact` (the GM logs something at the instance), a `reveal` (a
 * reveal targets the instance — reveals need an entity id), or an `llm-draft`
 * (an LLM draft references the instance). Materialization fires once on
 * reference; thereafter the trigger op is an ordinary op on a real entity.
 */
type MaterializeTrigger =
  | { kind: 'fact'; predicate: string; value: unknown }
  | { kind: 'reveal'; predicate: string; viewer: ViewerId }
  | { kind: 'llm-draft'; predicates: string[]; instruction?: string };

/**
 * B4 (#237) — the canon `occurred_at` value a materialized Event carries,
 * derived from a virtual instance's computed `occurred_at` (`@dl/calendar`'s
 * {@link VirtualInstance}). A point firing collapses to `game_date` /
 * `game_datetime`; a multi-day duration becomes a `game_date(time)_range`.
 * Mirrors the in-world value-kind shapes the date-picker authors (§6/§7), so
 * the materialized fact is editable like any other `occurred_at` afterward.
 */
function occurredAtValue(occ: VirtualInstance['occurred_at']): unknown {
  if (occ.kind === 'point') {
    return occ.seconds_into_day !== undefined
      ? { day: occ.day, seconds_into_day: occ.seconds_into_day }
      : { day: occ.day };
  }
  const from =
    occ.from.seconds_into_day !== undefined
      ? { day: occ.from.day, seconds_into_day: occ.from.seconds_into_day }
      : { day: occ.from.day };
  const to =
    occ.to.seconds_into_day !== undefined
      ? { day: occ.to.day, seconds_into_day: occ.to.seconds_into_day }
      : { day: occ.to.day };
  return { from, to };
}

/**
 * B4 (#237) — the shared "create an instance entity" effect list, reused by
 * the #81 "Create instance" flow (`submit-instance`) and the recurrence
 * materialization flow (`materialize-instance`). Writes the entity-create,
 * the `name` fact, and the `instance_of` ref back to the source so the
 * "Instance of" label + the "instances" reciprocal backlink resolve through
 * the registry. Callers append any extra defaults (the rule's `occurred_at` /
 * `location` / `participants`) themselves. DRY: the two flows differ only in
 * what canon defaults they copy, not in how the instance entity is born.
 */
function instanceCreateEffects(
  id: string,
  root: EntityRoot,
  name: string,
  sourceId: string,
): SubstrateEffect[] {
  return [
    { type: 'submit-entity-create', subject: id, root, traits: [] },
    {
      type: 'submit-fact',
      subject: id,
      predicate: FACT_NAME,
      payload: { kind: 'fact.create', value: name },
    },
    {
      type: 'submit-fact',
      subject: id,
      predicate: OFFICIAL_CORE_PREDICATE_IDS.instance_of,
      payload: { kind: 'fact.create', value: sourceId },
    },
  ];
}

/** Max entities held in the left-rail "Recent" list. */
const RECENT_CAP = 12;

/**
 * #133 — trait-driven fact-row editor host.
 *
 * The fact-row editor lives in `apps/client/src/entities/fact-row-editor.ts`
 * and is generic over Model/Msg. This host wires its accessors and
 * message constructors to the local `Model` shape so the editor can
 * read fact state and emit edits without importing `app.ts` (avoids
 * a circular import).
 */
const factRowHost: FactRowHost<Model, Msg> = {
  // Reactive state readers moved to apps/client/src/entities/fact-row-state.ts —
  // the llui v0.5.7 compile-time path analyzer treats `host.<method>(s, …)`
  // as opaque (only bare-Identifier callees get followed), so those
  // accessors were invisible to `__prefixes` emission and the commit
  // dirty mask never lit up for keystrokes (the e2e §9.4 regression).
  // The editor now calls `factDirtyAtKey(s, …)` / `factAtPredicate(s, …)` etc.
  // directly — those are bare-Identifier function declarations the
  // analyzer follows. Only Msg constructors + `activeCalendar` stay here.
  activeCalendar,
  campaignCursor: (s) => s.campaignCursor,
  browseCursor: (s) => s.browseCursor,
  datePicker: (s) => s.datePicker,
  datePickerMessages,
  openDatePicker: (id, cursor) => ({ type: 'date-picker-open', id, cursor }),
  navigateDatePicker: (cursor) => ({ type: 'date-picker-navigate', cursor }),
  overlayDatePicker: (overlay) => ({ type: 'date-picker-overlay', overlay }),
  closeDatePicker: () => ({ type: 'date-picker-close' }),
  editFact: (entityId, predicate, value) => ({
    type: 'edit-fact',
    entityId,
    predicate,
    value,
  }),
  softDeleteFact: (entityId, predicate) => ({
    type: 'soft-delete-fact',
    entityId,
    predicate,
  }),
  invalidFact: (entityId, predicate, error) => ({
    type: 'invalid-fact',
    entityId,
    predicate,
    error,
  }),
  rollbackFact: (entityId, predicate, toIndex) => ({
    type: 'rollback-fact',
    entityId,
    predicate,
    toIndex,
  }),
  openHistory: (entityId, predicate) => ({ type: 'history/open', entityId, predicate }),
  closeHistory: () => ({ type: 'history/close' }),
  setFactValidity: (entityId, predicate, validity) => ({
    type: 'set-fact-validity',
    entityId,
    predicate,
    validity,
  }),
  succeedFact: (entityId, predicate, atDay, value) => ({
    type: 'succeed-fact',
    entityId,
    predicate,
    atDay,
    value,
  }),
  markDirty: (entityId, predicate, buffer) =>
    buffer !== undefined
      ? { type: 'fact-input-dirty', entityId, predicate, buffer }
      : { type: 'fact-input-dirty', entityId, predicate },
  focusFact: (entityId, predicate) => ({
    type: 'fact-focus',
    entityId,
    predicate,
  }),
  blurFact: (entityId, predicate) => ({
    type: 'fact-blur',
    entityId,
    predicate,
  }),
  dismissHint: () => ({ type: 'fact-hint-dismiss' }),
  draftWithAi: (entityId, predicate) => ({
    type: 'llm-suggest-prose',
    entityId,
    predicate,
  }),
  generateImageFor: (entityId, predicate, instruction) => ({
    type: 'llm-generate-image',
    entityId,
    predicate,
    ...(instruction && instruction.trim() !== '' ? { instruction } : {}),
  }),
  ratifyDraft: (entityId, predicate) => ({ type: 'ratify-draft', entityId, predicate }),
  rejectDraft: (entityId, predicate) => ({ type: 'reject-draft', entityId, predicate }),
  clearReviewState: (entityId, predicate) => ({ type: 'clear-review-state', entityId, predicate }),
  openRevealMenu: (entityId, predicate) => ({ type: 'open-reveal-menu', entityId, predicate }),
  uploadAsset: (file) => uploadAssetToCampaign(file),
};

/**
 * Image/file upload (#8/#9). POSTs the binary to the campaign asset store
 * (`POST /v1/campaigns/:cid/assets`, multipart) — the worker enforces the
 * MIME allowlist + size cap + sha256 dedup — then resolves the new asset's
 * signed content URL so the editor can render it immediately. Rejects with
 * a human-readable message on failure; the fact-row editor surfaces it.
 */
async function uploadAssetToCampaign(file: Blob): Promise<{ id: string; url: string }> {
  const cid = CAMPAIGN_ID;
  if (cid === null) throw new Error(t('llm.draft_no_campaign'));
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`${workerOrigin()}/v1/campaigns/${encodeURIComponent(cid)}/assets`, {
    method: 'POST',
    credentials: 'include',
    body: form,
  });
  if (!res.ok) {
    let msg = t('entity.facts.upload_failed');
    try {
      const body = (await res.json()) as { error?: string };
      if (typeof body.error === 'string' && body.error !== '') msg = body.error;
    } catch {
      /* non-JSON error body — keep the generic message */
    }
    throw new Error(msg);
  }
  const data = (await res.json()) as { asset?: { id?: string } };
  const assetId = data.asset?.id;
  if (typeof assetId !== 'string' || assetId === '') {
    throw new Error(t('entity.facts.upload_failed'));
  }
  let url = '';
  try {
    const metaRes = await fetch(
      `${workerOrigin()}/v1/campaigns/${encodeURIComponent(cid)}/assets/${encodeURIComponent(assetId)}`,
      { credentials: 'include' },
    );
    if (metaRes.ok) {
      const meta = (await metaRes.json()) as { url?: string };
      if (typeof meta.url === 'string') url = assetContentUrl(meta.url);
    }
  } catch {
    /* preview URL is a nicety — the id commit is what matters */
  }
  return { id: assetId, url };
}

/**
 * #139 — trait attach/detach host.
 *
 * Same shape as `factRowHost` — the trait-attach view module lives in
 * `apps/client/src/entities/trait-attach-view.ts` and is generic over
 * Model/Msg. This host wires its accessors and message constructors to
 * the local `Model` shape.
 */
const traitAttachHost: TraitAttachHost<Msg> = {
  highlightTrait: (traitId) => ({ type: 'trait/highlight', traitId }),
  openPicker: (entityId) => ({ type: 'open-trait-picker', entityId }),
  closePicker: () => ({ type: 'close-trait-picker' }),
  setQuery: (query) => ({ type: 'trait-picker-query', query }),
  attachTraitToPickerTarget: (traitId) => ({ type: 'attach-trait', traitId }),
  requestDetach: (entityId, traitId) => ({
    type: 'request-detach-trait',
    entityId,
    traitId,
  }),
  confirmDetach: () => ({ type: 'confirm-detach-trait' }),
  cancelDetach: () => ({ type: 'cancel-detach-trait' }),
  openTraitEditor: () => ({ type: 'open-trait-editor' }),
  editTrait: (traitId) => ({ type: 'edit-trait', traitId }),
};

function nowIso(): string {
  return new Date().toISOString();
}

function currentValue(fact: FactState | undefined): unknown {
  return currentFactValue(fact);
}

function errorKey(entityId: string, predicate: string): string {
  return `${entityId}/${predicate}`;
}

/**
 * Resolve the active calendar from the model's id (#180, ADR-0004 §4/§5).
 * The id is the campaign's `canonical_calendar` *setting* pointer (or the
 * deterministic fallback); the calendar DEFINITION is looked up in the
 * folded registry overlay (`getHomebrewDefs().calendars`, a
 * `pack.define_calendar` chain) with a bundled-pack fallback. Cached per
 * (id, homebrew epoch) so a registry change invalidates the entry.
 */
function activeCalendar(model: Model): Calendar {
  // Route through the TOTAL resolver: a plugin-mode / unresolvable calendar
  // must NOT throw here (that white-screens the whole client — #242). Fall back
  // to the bundled deterministic default so date math keeps working; the
  // degrade notice ({@link activeCalendarResolution}) tells the GM the chosen
  // calendar isn't fully supported.
  const resolution = activeCalendarResolution(model);
  if (resolution.kind === 'structured') return resolution.calendar;
  return loadBundledCalendar(FALLBACK_CALENDAR_ID);
}

const calendarResolutionCache = new Map<string, ActiveCalendarResolution>();
/**
 * Resolve the model's active calendar to a total {@link ActiveCalendarResolution}
 * (#242) — `structured` renders normally; `plugin` / `unresolvable` DEGRADE
 * (the caller shows {@link calendarDegradeNotice}) instead of throwing. Cached
 * per (homebrew epoch, id) so a re-folded registry invalidates the entry.
 */
function activeCalendarResolution(model: Model): ActiveCalendarResolution {
  const calendarId = model.activeCalendarId;
  // Key on the homebrew epoch so a re-folded registry (a new/edited
  // `pack.define_calendar`) doesn't serve a stale parsed resolution.
  const cacheKey = `${homebrewEpoch()}:${calendarId}`;
  let cached = calendarResolutionCache.get(cacheKey);
  if (cached !== undefined) return cached;
  cached = resolveActiveCalendar(calendarId, getHomebrewDefs().calendars);
  calendarResolutionCache.set(cacheKey, cached);
  return cached;
}

/** True when the active calendar degrades (plugin-mode or unresolvable) — the host renders the notice. */
function activeCalendarDegrades(model: Model): boolean {
  return activeCalendarResolution(model).kind !== 'structured';
}

function formatCursor(calendar: Calendar, cursor: number | null): string {
  if (cursor === null) return t('calendar.cursor_unset');
  const r = toDisplayDate(calendar, cursor);
  if (!r.ok) return t('calendar.cursor_unset');
  return formatDisplayDate(calendar, r.value);
}

// ---------- update ----------

/**
 * Boot-time restore of the persisted Campaign date (06-timeline.md §13;
 * ADR-0005 §"Session cursor"). The localStorage cache exists so the date
 * the GM last worked in is shown INSTANTLY on reload — before the op log
 * backfills and folds.
 *
 * The cursor is a canonical day count keyed to a calendar, so the restore
 * must adopt the stored calendar id too — otherwise a day count gets
 * displayed in the wrong calendar's coordinate system (a different instant).
 * It can only safely adopt a BUNDLED calendar at boot, because a homebrew
 * `cal_*` definition does not resolve until its `pack.define_calendar` op
 * folds (`loadBundledCalendar` would throw, and the views that call
 * `loadActiveCalendar(activeCalendarId)` would crash). For a homebrew
 * calendar we keep the deterministic Gregorian fallback and leave the
 * cursor unset, deferring to `replica/entities-rebuilt` to hydrate both
 * from the folded `config.session_cursor` + calendar registry.
 *
 * Previously the boot read was hard-keyed to `FALLBACK_CALENDAR_ID`
 * (Gregorian) while writes key to the active calendar — so for any
 * non-Gregorian campaign the cache was dead on boot (the date flashed to
 * "unset" every reload, and an early cursor edit during that window wrote a
 * Gregorian-keyed value the fold then discarded). Restoring the stored pair
 * fixes that asymmetry.
 */
function restoreBootCursor(): { calendarId: string; cursor: number | null } {
  if (CAMPAIGN_ID === null) return { calendarId: FALLBACK_CALENDAR_ID, cursor: null };
  const persisted = getPersistedCursorState(CAMPAIGN_ID);
  if (persisted === null) return { calendarId: FALLBACK_CALENDAR_ID, cursor: null };
  try {
    loadBundledCalendar(persisted.calendarId);
  } catch {
    // Homebrew / unresolvable-at-boot calendar — defer to the op-log fold.
    return { calendarId: FALLBACK_CALENDAR_ID, cursor: null };
  }
  return { calendarId: persisted.calendarId, cursor: persisted.cursor };
}

export function init(): [Model, Effect[]] {
  const bootCursor = restoreBootCursor();
  const startRoute = initialRoute();
  // #258 — the Table View tab is a PURE renderer. It opens NO replica (no OPFS
  // sync-access-handle contention with the GM tab) and runs no session boot —
  // it only subscribes to the GM tab's broadcast over BroadcastChannel. So when
  // the tab opens directly on `/table`, skip boot-session / account/load and
  // wire the channel as the `table` role instead. The `keyboard` bind still
  // helps (Escape closes nothing on the table, but it is harmless).
  const isTable = isTableRoute(startRoute);
  // #152 — a direct load of /account must kick the account fetch (no
  // navigate Msg fires on the initial mount). Mark the slice loading and
  // queue the load alongside boot.
  const initEffects: Effect[] = isTable
    ? [{ type: 'table-channel-init', role: 'table' }]
    : [{ type: 'bind-keyboard' }, { type: 'boot-session' }];
  const startAccount: AccountModel =
    startRoute.kind === 'account'
      ? { ...INITIAL_ACCOUNT_MODEL, status: 'loading' }
      : INITIAL_ACCOUNT_MODEL;
  // #152 — a direct /account load needs the fetch. #89 — every other
  // (non-auth) boot also loads the signed-in user so the one-time recovery
  // disclaimer modal can decide whether to show, even for a campaign-less
  // GM (the no-campaign boot path never reaches `session/loaded`). Auth
  // routes (sign-in/up/etc.) skip it — there is no session to read yet.
  if (!isTable && (startRoute.kind === 'account' || !isAuthRoute(startRoute))) {
    initEffects.push({ type: 'account/load' });
  }
  return [
    {
      entities: {},
      shadowedCanon: {},
      advisories: [],
      bundles: {},
      bundleAutoPromote: {},
      bundleGenModal: null,
      bundleRegenModal: null,
      authoringSession: null,
      auditorRuleOverrides: DEFAULT_AUDITOR_RULE_OVERRIDES,
      session: LEGACY_GM_SESSION,
      bootStatus: 'pending',
      bootError: null,
      projectedFacts: {},
      // 226.1 (#245) — Preview-as defaults to GM identity (no preview).
      previewViewer: null,
      previewProjection: null,
      // #258 — Table View defaults: the full designated-Viewer projection, no
      // GM override (→ the default party/first-Viewer), no broadcast yet.
      tableFocus: DEFAULT_TABLE_FOCUS,
      designatedViewerOverride: null,
      tableBroadcast: null,
      // #271 — attention signal defaults: mode off, no pointer, no ripple, seq=0.
      tableAttentionMode: false,
      tablePointer: null,
      tableRipple: null,
      tableRippleSeq: 0,
      privateNotes: {},
      route: startRoute,
      paletteOpen: false,
      paletteQuery: '',
      paletteActiveIndex: 0,
      // Optimistic: start offline-presumed-online so the pill doesn't flash
      // on first paint; the open-substrate effect flips it to the real
      // connection state once the SyncClient ack arrives (or fails).
      connection: { online: true, pendingCount: 0, conflictCount: 0 },
      syncing: false,
      inlineErrors: {},
      pendingAutoRatifyPolicy: DEFAULT_PENDING_AUTO_RATIFY_POLICY,
      // Empty until the op log folds (#171); `replica/entities-rebuilt`
      // populates it and re-derives `activeCalendarId` from
      // `config.canonical_calendar`.
      config: {},
      // Restore the calendar + Campaign date the GM last worked in for this
      // campaign (localStorage cache) so the date shows instantly on reload.
      // A bundled calendar is adopted directly; a homebrew calendar falls back
      // to Gregorian until the entities-rebuilt fold swaps in the configured
      // calendar (#180). See `restoreBootCursor`.
      activeCalendarId: bootCursor.calendarId,
      // With no campaign (legacy/demo), there is no `canonical_calendar` to
      // fold — the Gregorian fallback is final, so the cursor is editable
      // immediately. A real campaign's calendar is only a guess until the
      // first op-log fold confirms it (`replica/entities-rebuilt`), so the
      // cursor pill stays read-only until then (an edit can't be keyed to the
      // wrong pre-fold calendar). See `Model.calendarConfirmed`.
      calendarConfirmed: CAMPAIGN_ID === null || CAMPAIGN_ID === 'demo_campaign',
      campaignCursor: bootCursor.cursor,
      // Ephemeral as-of scrubber (#192) — always starts at "now" (null).
      browseCursor: null,
      datePicker: null,
      entitySheet: { kind: 'closed' },
      revealMenu: null,
      facetRevealMenu: null,
      revealComposer: null,
      beliefModal: null,
      beliefCloneMenu: null,
      notesSheetOpen: false,
      notesSheetEntityId: null,
      hudRewindPending: null,
      entityPageTab: 'overview',
      campaignsBoot: { kind: 'loading' } as CampaignsBoot,
      actionsOverlayOpen: false,
      account: startAccount,
      recoveryDisclaimerDismissed: false,
      // #89 — hydrate the last-export timestamp for the current campaign
      // from localStorage so Settings shows it without a round-trip.
      lastExportAt: ((): Record<string, string> => {
        if (CAMPAIGN_ID === null) return {};
        const at = readLastExport(CAMPAIGN_ID);
        return at !== null ? { [CAMPAIGN_ID]: at } : {};
      })(),
      // Recently-viewed entities (left-rail "Recent"). Hydrated from
      // localStorage for the active campaign so the rail is populated on
      // first paint after a reload.
      recentEntityIds: CAMPAIGN_ID === null ? [] : readRecentEntities(CAMPAIGN_ID),
      rootPickerOpen: false,
      newEntityModal: null,
      instanceModal: null,
      traitPickerEntityId: null,
      historyOpen: null,
      highlightedTrait: null,
      traitDetachConfirm: null,
      schemaRetireConfirm: null,
      entityDeleteConfirm: null,
      controllerPicker: null,
      campaignMembers: null,
      memberControlledCharacters: null,
      traitPickerQuery: '',
      traitEditor: null,
      calendarEditor: null,
      unitsEditor: null,
      typeEditor: null,
      factDirty: {},
      factDirtyEpoch: {},
      aiDraftPending: {},
      aiDraftError: {},
      streamPreview: null,
      pageDraftModal: null,
      assetUrls: {},
      aiSettings: emptyAiSettings(),
      inputBuffer: {},
      factSavedAt: {},
      factSaving: {},
      focusedFactKey: null,
      // localStorage probe is one-shot at boot: if the user already
      // dismissed in a prior session, the hint is silent forever.
      factHintDismissed: ((): boolean => {
        if (typeof localStorage === 'undefined') return true;
        try {
          return localStorage.getItem('dl.factEditHintDismissed') === '1';
        } catch {
          return true;
        }
      })(),
      // Bug 3 — substrate starts in `opening` so the banner doesn't
      // flash a "failed" message during the normal boot path. The
      // `open-substrate` effect flips this to `persistent` / `memory`
      // / `failed` as soon as `openBrowserReplica()` resolves or throws.
      substrate: { kind: 'opening' },
      substrateDialogOpen: false,
      // #257 — ephemeral session-HUD state: initiative + dice roller.
      initiative: EMPTY_INITIATIVE,
      diceInput: '',
      diceError: null,
      diceHistory: [],
      initiativeAddForm: null,
      combatLogMenu: null,
      combatLogForm: null,
      // #260 — ephemeral narrative combat panel state (07 §13.9.5).
      narrativeCombatTurnLog: EMPTY_TURN_LOG,
      narrativeCombatEffectInput: null,
      narrativeCombatSaveInput: null,
      narrativeCombatDamageInput: null,
      // #263 — campaign roster + invite panel state.
      inviteState: null,
      inviteEmailForm: null,
      // #265 — rotating-facilitator modal (designate / hand-off).
      facilitatorModal: null,
    },
    initEffects,
  ];
}

/**
 * §7.2 prefill — overlay a controlling player's projected facts onto the
 * `entities` fold so the "Your character" editor PRE-FILLS with the PC's
 * current value.
 *
 * A player's local replica is sparse: it folds only the ops they themselves
 * authored, so a controlled PC's GM-set identity facts (name / appearance /
 * portrait) exist ONLY in `projectedFacts` — the value-only WS-projected
 * stream — never in `entities`. The fact-row editor reads `entities`
 * (`factAtPredicate`), so without this overlay every player-authority row
 * renders empty even though the projection knows the value.
 *
 * This is rebuildable display-only state: the op log stays canonical and the
 * overlay never feeds a write. The conflict-aware `base_version` still comes
 * straight from `projectedFacts[key].version` (see the `edit-fact` arm), not
 * from these synthesized rows. `projectedFacts` wins per predicate (it IS the
 * server's current projection for this viewer), so re-application is
 * idempotent — safe to call on both the full `entities-rebuilt` fold (clean
 * base) and each incremental `visibility/added` (per-predicate upsert).
 * `visibility/removed` is intentionally not overlaid: self-authority identity
 * facts on one's own PC are never un-revealed, and the next replica fold
 * reconciles any drift regardless.
 */
export function withControlledProjectionOverlay(
  entities: Record<string, EntityState>,
  projectedFacts: Record<string, ProjectedFactRow>,
  controlledCharacters: readonly string[],
): Record<string, EntityState> {
  if (controlledCharacters.length === 0) return entities;
  const controlled = new Set(controlledCharacters);
  let next: Record<string, EntityState> | null = null;
  for (const row of Object.values(projectedFacts)) {
    if (!controlled.has(row.subject)) continue;
    if (!next) next = { ...entities };
    const base = next[row.subject];
    const facts = base ? { ...base.facts } : {};
    facts[row.predicate] = {
      versions: [{ value: row.value, written_at_real: '', author: 'projection' }],
      soft_deleted: false,
      // Projected facts the player can author are revealed canon; honour a
      // `pending` flag for fidelity but default to canon.
      status: row.pending === true ? ('pending' as FactStatus) : DEFAULT_FACT_STATUS,
      owner: DEFAULT_FACT_OWNER,
      validity: null,
      reveals: [],
    };
    // A controlled PC is always a Character (it is bound via `controlled_by`,
    // which the projection resolves only for Characters). Preserve the folded
    // entity when present; synthesize a minimal Character shell otherwise.
    next[row.subject] = base
      ? { ...base, facts }
      : { id: row.subject, root: 'character', traits: [], facts, soft_deleted: false };
  }
  return next ?? entities;
}

export function update(model: Model, msg: Msg): [Model, Effect[]] {
  switch (msg.type) {
    case 'session/loaded': {
      cachedSessionViewer = msg.session.viewer;
      const nextModel = {
        ...model,
        session: msg.session,
        bootStatus: 'loaded' as const,
        bootError: null,
      };
      // open-substrate runs after the session has resolved so the
      // SyncClient knows the right viewerKey / token to use.
      const sessionEffects: Effect[] = [
        { type: 'open-substrate' },
        { type: 'announce-mode', mode: msg.session.mode },
      ];
      // Auditor overrides are a GM concern (the panel is GM-only); load them
      // once the campaign session resolves so the very first audit applies them.
      if (msg.session.mode === 'gm') {
        sessionEffects.push({ type: 'load-auditor-overrides' });
        // #258 — open the Table View channel in the GM role so a Table tab that
        // opens later gets the current state on `table-hello`. The GM never
        // adopts a broadcast (it owns the live replica).
        sessionEffects.push({ type: 'table-channel-init', role: 'gm' });
      }
      // #89 — the signed-in user (which carries `recovery_disclaimer_seen`)
      // is loaded once at boot via `init()`'s `account/load`, independent of
      // the campaign-resolution path; nothing extra to do here.
      return [nextModel, sessionEffects];
    }
    case 'session/error':
      // Boot failure → fall back to the legacy GM identity so the
      // Walking-Skeleton UI keeps running. The error is surfaced for
      // diagnostic UX but the substrate still opens.
      return [
        { ...model, bootStatus: 'error', bootError: msg.message },
        [
          { type: 'open-substrate' },
          { type: 'announce-mode', mode: model.session.mode },
        ],
      ];
    case 'visibility/added': {
      const row: ProjectedFactRow = {
        subject: msg.subject,
        predicate: msg.predicate,
        value: msg.value,
      };
      if (msg.pending) row.pending = true;
      if (msg.version !== undefined) row.version = msg.version;
      const nextProjectedFacts = {
        ...model.projectedFacts,
        [factKey(msg.subject, msg.predicate)]: row,
      };
      // §7.2 prefill — if this projected fact belongs to a PC the player
      // controls, also overlay it onto `entities` so the "Your character"
      // editor (which reads `entities`) prefills with the value. The full
      // fold reconciles on the next `entities-rebuilt`; this keeps a fact that
      // streams in AFTER the fold from leaving the row empty. No-op for the GM.
      const overlaidEntities = withControlledProjectionOverlay(
        model.entities,
        nextProjectedFacts,
        model.session.controlledCharacters,
      );
      // The PC's projected portrait may need a signed URL resolved (the asset
      // row renders a thumbnail). Only fire when a new asset id appears.
      const assetIds =
        overlaidEntities === model.entities
          ? []
          : assetIdsNeedingUrls(overlaidEntities, model.assetUrls);
      return [
        {
          ...model,
          projectedFacts: nextProjectedFacts,
          entities: overlaidEntities,
        },
        assetIds.length > 0 ? [{ type: 'resolve-asset-urls', assetIds }] : [],
      ];
    }
    case 'visibility/removed': {
      const k = factKey(msg.subject, msg.predicate);
      if (!(k in model.projectedFacts)) return [model, []];
      const { [k]: _removed, ...rest } = model.projectedFacts;
      void _removed;
      return [{ ...model, projectedFacts: rest }, []];
    }
    case 'private-note/loaded':
      return [
        {
          ...model,
          privateNotes: {
            ...model.privateNotes,
            [msg.entityId]: {
              body: msg.body,
              updatedAt: msg.updatedAt,
              dirty: false,
              loaded: true,
            },
          },
        },
        [],
      ];
    case 'private-note/edit': {
      const prior = model.privateNotes[msg.entityId] ?? {
        body: '',
        updatedAt: null,
        dirty: false,
        loaded: true,
      };
      return [
        {
          ...model,
          privateNotes: {
            ...model.privateNotes,
            [msg.entityId]: { ...prior, body: msg.body, dirty: true },
          },
        },
        [],
      ];
    }
    case 'private-note/save': {
      const noteState = model.privateNotes[msg.entityId];
      if (!noteState) return [model, []];
      return [
        model,
        [{ type: 'save-private-note', entityId: msg.entityId, body: noteState.body }],
      ];
    }
    case 'private-note/saved': {
      const prior = model.privateNotes[msg.entityId];
      if (!prior) return [model, []];
      return [
        {
          ...model,
          privateNotes: {
            ...model.privateNotes,
            [msg.entityId]: { ...prior, updatedAt: msg.updatedAt, dirty: false },
          },
        },
        [],
      ];
    }
    case 'navigate': {
      // III.B.2 / III.B.3 — when a player navigates to an entity page,
      // lazily fetch the private note for that entity.
      const effects: Effect[] = [];
      if (
        msg.route.kind === 'entity' &&
        model.session.mode === 'player' &&
        !(msg.route.id in model.privateNotes)
      ) {
        effects.push({ type: 'load-private-note', entityId: msg.route.id });
      }
      // #263 — landing on the settings page lazy-loads the invite state
      // (pending email invites + active shareable links). GM-only; non-GMs
      // never see the roster panel so the load is a no-op for them, but the
      // guard is in the effect handler on the server.
      if (msg.route.kind === 'settings' && model.inviteState === null) {
        effects.push({ type: 'roster/load-invites' });
      }
      // #152 — landing on the account page (re)loads the signed-in user.
      // The reducer arm below flips status to 'loading'; we mutate the
      // model in the return so the spinner shows immediately.
      let accountForNav = model.account;
      if (msg.route.kind === 'account') {
        accountForNav = { ...model.account, status: 'loading', error: null };
        effects.push({ type: 'account/load' });
      }
      // #186: the per-campaign AI extensions hydrate from the folded op-log
      // config on every `replica/entities-rebuilt` (`hydrateAiSettings`), so
      // there is no separate load on settings-open — the editor reads the live
      // `Model.aiSettings` buffer the fold maintains.
      // Push the URL alongside the model.route update. In-app row
      // click handlers in list-view.ts and the command palette
      // dispatch the navigate Msg directly (rather than going
      // through `routing.link`, which would change the URL itself).
      // Without this push, model.route advances but `location.hash`
      // stays — the new entity page renders BUT the URL still says
      // `/` (the back/forward stack and any deep-link share lose
      // the target). When the dispatch ORIGINATED from a URL change
      // (`routing.listener`'s hashchange handler), the hash is
      // already at the target so `routing.push` is a no-op — same
      // pattern hash-router consumers use elsewhere. The e2e
      // regression that caught this is
      // apps/client/e2e/09-create-edit-navigate.spec.ts §9.5.
      if (msg.route.kind !== model.route.kind || routeKey(msg.route) !== routeKey(model.route)) {
        effects.push(appRoutePush(msg.route));
      }
      // Track recently-viewed entities for the left-rail "Recent" section.
      // Most-recent-first, deduped, capped; persisted per-campaign via the
      // effect so the reducer stays pure.
      let recentForNav = model.recentEntityIds;
      if (msg.route.kind === 'entity') {
        const id = msg.route.id;
        recentForNav = [id, ...model.recentEntityIds.filter((x) => x !== id)].slice(0, RECENT_CAP);
        if (recentForNav.join(' ') !== model.recentEntityIds.join(' ')) {
          effects.push({ type: 'persist-recent', recent: recentForNav });
        }
      }
      // Close the campaign switcher on navigation — the dropdown is
      // page-level transient UI; leaving it open across routes leaves
      // the GM staring at a menu they have no other way to dismiss
      // once the anchor button has scrolled off.
      return [
        {
          ...model,
          route: msg.route,
          recentEntityIds: recentForNav,
          paletteOpen: false,
          actionsOverlayOpen: false,
          account: accountForNav,
        },
        effects,
      ];
    }
    // ─── #152 account / profile settings ─────────────────────────────
    case 'account/load-requested':
      return [
        { ...model, account: { ...model.account, status: 'loading', error: null } },
        [{ type: 'account/load' }],
      ];
    case 'account/loaded':
      return [
        {
          ...model,
          account: {
            ...model.account,
            status: 'loaded',
            error: null,
            user: msg.user,
            // Seed the recovery-email editor from the loaded value.
            recoveryEmailInput: msg.user.recovery_email ?? '',
          },
        },
        [],
      ];
    case 'account/load-failed':
      return [
        { ...model, account: { ...model.account, status: 'error', error: msg.message } },
        [],
      ];
    case 'account/twofa-enable-requested':
      return [
        { ...model, account: { ...model.account, busy: true, notice: null } },
        [{ type: 'account/twofa-setup' }],
      ];
    case 'account/twofa-setup-ready':
      return [
        {
          ...model,
          account: {
            ...model.account,
            busy: false,
            twofaSetup: {
              secretBase32: msg.secretBase32,
              otpauthUri: msg.otpauthUri,
              code: '',
              backupCodes: null,
            },
          },
        },
        [],
      ];
    case 'account/twofa-code-input': {
      if (model.account.twofaSetup === null) return [model, []];
      return [
        {
          ...model,
          account: {
            ...model.account,
            twofaSetup: { ...model.account.twofaSetup, code: msg.code },
          },
        },
        [],
      ];
    }
    case 'account/twofa-confirm-requested': {
      const code = model.account.twofaSetup?.code.trim() ?? '';
      if (code === '') return [model, []];
      return [
        { ...model, account: { ...model.account, busy: true } },
        [{ type: 'account/twofa-confirm', code }],
      ];
    }
    case 'account/twofa-confirmed': {
      const setup = model.account.twofaSetup;
      const user = model.account.user;
      return [
        {
          ...model,
          account: {
            ...model.account,
            busy: false,
            // Flip the local status to enabled + remember the backup count.
            user: user ? { ...user, totp_enabled: true, backup_codes_remaining: msg.backupCodes.length } : user,
            twofaSetup: setup
              ? { ...setup, backupCodes: msg.backupCodes }
              : {
                  secretBase32: '',
                  otpauthUri: '',
                  code: '',
                  backupCodes: msg.backupCodes,
                },
          },
        },
        [],
      ];
    }
    case 'account/twofa-cancel':
      return [{ ...model, account: { ...model.account, twofaSetup: null, busy: false } }, []];
    case 'account/twofa-disable-requested':
      return [
        { ...model, account: { ...model.account, busy: true, notice: null } },
        [{ type: 'account/twofa-disable' }],
      ];
    case 'account/password-reset-requested': {
      const email = model.account.user?.email;
      if (!email) return [model, []];
      return [
        { ...model, account: { ...model.account, busy: true, notice: null } },
        [{ type: 'account/password-reset', email }],
      ];
    }
    case 'account/recovery-email-input':
      return [
        { ...model, account: { ...model.account, recoveryEmailInput: msg.value } },
        [],
      ];
    case 'account/recovery-password-input':
      return [
        { ...model, account: { ...model.account, recoveryPasswordInput: msg.value } },
        [],
      ];
    case 'account/recovery-email-save-requested': {
      const primary = model.account.user?.email;
      if (!primary) return [model, []];
      const trimmed = model.account.recoveryEmailInput.trim();
      const recoveryEmail = trimmed.length > 0 ? trimmed : null;
      // #F8 — re-authenticate before repointing the recovery mailbox. Checked
      // here so the GM gets a message naming the field they missed; posting
      // without it earns a 400 whose text ("Invalid recovery email.") blames the
      // address instead. Applies to CLEARING too: removing the recovery address
      // also changes where a reset lands, and the server re-auths either way.
      const currentPassword = model.account.recoveryPasswordInput;
      if (currentPassword === '') {
        return [
          {
            ...model,
            account: {
              ...model.account,
              busy: false,
              notice: t('account.security.recovery.error_password_required'),
            },
          },
          [],
        ];
      }
      // Local mirror of the server's distinctness rule (avoids a round-trip).
      if (recoveryEmail !== null && recoveryEmail.toLowerCase() === primary.toLowerCase()) {
        return [
          {
            ...model,
            account: {
              ...model.account,
              busy: false,
              notice: t('account.security.recovery.error_same'),
            },
          },
          [],
        ];
      }
      return [
        { ...model, account: { ...model.account, busy: true, notice: null } },
        [{ type: 'account/recovery-email-save', recoveryEmail, currentPassword }],
      ];
    }
    case 'account/recovery-email-saved': {
      const user = model.account.user;
      return [
        {
          ...model,
          account: {
            ...model.account,
            busy: false,
            user: user ? { ...user, recovery_email: msg.recoveryEmail } : user,
            recoveryEmailInput: msg.recoveryEmail ?? '',
            // Drop the credential the moment it is spent — it must not outlive
            // the request that needed it, nor sit in any Model dump.
            recoveryPasswordInput: '',
            notice: t('account.security.recovery.saved'),
          },
        },
        [],
      ];
    }
    case 'account/delete-confirm-input':
      return [{ ...model, account: { ...model.account, deleteConfirm: msg.value } }, []];
    case 'account/delete-requested':
      if (model.account.deleteConfirm.trim() !== 'DELETE') return [model, []];
      return [
        { ...model, account: { ...model.account, busy: true, notice: null } },
        [{ type: 'account/delete' }],
      ];
    case 'account/action-failed':
      return [
        {
          ...model,
          account: {
            ...model.account,
            busy: false,
            error: null,
            notice: msg.message,
            // Clear the re-auth buffer on ANY account-action failure. A rejected
            // password (401 "Re-authentication failed.") must not stay in the box
            // for a blind retry, and no failure path is a reason to keep holding
            // a credential.
            recoveryPasswordInput: '',
          },
        },
        [],
      ];
    case 'account/notice':
      return [{ ...model, account: { ...model.account, busy: false, notice: msg.message } }, []];
    case 'account/dismiss-notice':
      return [{ ...model, account: { ...model.account, notice: null } }, []];
    case 'recovery-disclaimer/dismiss':
      // "Got it" — optimistically suppress the modal for the rest of this
      // session and persist the irrevocable acknowledgement server-side.
      return [
        { ...model, recoveryDisclaimerDismissed: true },
        [{ type: 'recovery-disclaimer/mark-seen' }],
      ];
    case 'recovery-disclaimer/export-now': {
      // "Export now" — also acknowledges (dismiss + persist), then triggers
      // the export download for the current campaign. With no campaign id
      // (legacy/demo), the export is a no-op but the acknowledgement still
      // lands so the modal doesn't re-show.
      const effects: Effect[] = [{ type: 'recovery-disclaimer/mark-seen' }];
      if (CAMPAIGN_ID !== null) {
        effects.push({ type: 'export-campaign', campaignId: CAMPAIGN_ID });
      }
      return [{ ...model, recoveryDisclaimerDismissed: true }, effects];
    }
    case 'export/run':
      // Settings "Export campaign" button — triggers the export download.
      return [model, [{ type: 'export-campaign', campaignId: msg.campaignId }]];
    case 'export/recorded':
      // The export effect fired; stamp the last-export time in the Model
      // (the effect also persisted it to localStorage).
      return [
        { ...model, lastExportAt: { ...model.lastExportAt, [msg.campaignId]: msg.at } },
        [],
      ];
    case 'create-entity': {
      // #131 + #139 — generic entity creation. Every root starts with
      // NO traits attached. Wave 1 (#131) inherited a Walking-Skeleton
      // `inhabited` default for new Locations from the era when
      // `inhabited` was the only trait; #139 ripped it out (alongside
      // shipping the trait-attach UI) so the entire core trait library
      // is uniformly progressive. Per `00-vision.md §1` (progressive
      // canonicity), `core.json` declares every trait with `required:
      // null` — defaulting any one of them at creation contradicts the
      // pack declaration and embeds genre-specific assumptions (a
      // wilderness Location does not want `inhabited`; an AI Character
      // does not want `mortal`). The GM attaches traits explicitly via
      // the trait-strip on the entity page.
      const id = mintEntityId(msg.root);
      const route: AppRoute = { kind: 'entity', id };
      const traits: readonly string[] = [];
      // Per-root default name template — i18n keys `entity.new.<root>`.
      const defaultName = t(`entity.new.${msg.root}`);
      // Optimistic route hop — substrate effects produce the entity in the
      // op log; the rebuild from onOpApplied refreshes the model's
      // entities cache. Until the rebuild lands the entity page shows
      // empty inputs (the same as a freshly-created entity with no facts
      // beyond `name`), so there is no visible flicker.
      return [
        // Land a freshly-created entity on the Facts tab (it's blank — the GM
        // wants to author it, not read an empty Overview). See submit-new-entity.
        { ...model, route, rootPickerOpen: false, paletteOpen: false, entityPageTab: 'facts' },
        [
          {
            type: 'submit-entity-create',
            subject: id,
            root: msg.root,
            traits,
          },
          {
            type: 'submit-fact',
            subject: id,
            predicate: FACT_NAME,
            payload: { kind: 'fact.create', value: defaultName },
          },
          appRoutePush(route),
        ],
      ];
    }
    case 'open-root-picker':
      return [{ ...model, rootPickerOpen: true }, []];
    case 'close-root-picker':
      return [{ ...model, rootPickerOpen: false }, []];
    case 'open-new-entity-modal':
      // Bug 4 — close the root-picker AND open the name-input modal in
      // one frame. Either entry path (list-view shortcut or root-picker
      // selection) ends at the same state.
      return [
        {
          ...model,
          rootPickerOpen: false,
          // #schema-settings — a type-seeded create carries the type's traits
          // so the new entity is born with them attached; a bare root carries none.
          newEntityModal: { root: msg.root, name: '', traits: msg.traits ?? [] },
        },
        [],
      ];
    case 'close-new-entity-modal':
      return [{ ...model, newEntityModal: null }, []];
    case 'update-new-entity-name':
      if (model.newEntityModal === null) return [model, []];
      return [
        {
          ...model,
          newEntityModal: { ...model.newEntityModal, name: msg.name },
        },
        [],
      ];
    case 'submit-new-entity': {
      // Bug 4 — mirrors `create-entity` but uses the user-typed name
      // from the modal instead of the i18n default ("New Location").
      // Empty/whitespace-only names guard at the reducer too even
      // though the button is disabled, so a stray Enter dispatch from
      // the keydown handler can't slip a blank name through.
      if (model.newEntityModal === null) return [model, []];
      const trimmed = model.newEntityModal.name.trim();
      if (trimmed === '') return [model, []];
      const root = model.newEntityModal.root;
      const id = mintEntityId(root);
      const route: AppRoute = { kind: 'entity', id };
      // #schema-settings — carry the type's traits (empty for a bare root).
      const traits: readonly string[] = model.newEntityModal.traits;
      // History mode: set `model.route` directly so the entity page renders
      // immediately, and push the URL for the address bar / back stack.
      // (Under the old hash router this relied on `routing.push` triggering a
      // `hashchange` → listener → navigate Msg to write `model.route`. History
      // pushState does NOT fire `popstate`, so that chain never ran and the
      // page failed to mount — see e2e 09 §9.2 / 12-trait-editor. Writing the
      // route here is the same pattern the `create-entity` arm + palette use.)
      return [
        {
          ...model,
          route,
          newEntityModal: null,
          paletteOpen: false,
          // A freshly-created entity is blank — land on the Facts tab so the
          // GM authors it immediately instead of staring at an empty Overview
          // and having to hunt for the editor. The `navigate` arm (fired by
          // the routing.push listener below) preserves entityPageTab.
          entityPageTab: 'facts',
        },
        [
          {
            type: 'submit-entity-create',
            subject: id,
            root,
            traits,
          },
          {
            type: 'submit-fact',
            subject: id,
            predicate: FACT_NAME,
            payload: { kind: 'fact.create', value: trimmed },
          },
          appRoutePush(route),
        ],
      ];
    }
    case 'open-instance-modal':
      // #81 — materialize an instance from a Lore. Default root is
      // `character` (the most common case: a recurring NPC archetype),
      // but any of the six roots is selectable.
      return [
        { ...model, instanceModal: { sourceId: msg.sourceId, root: 'character', name: '' } },
        [],
      ];
    case 'close-instance-modal':
      return [{ ...model, instanceModal: null }, []];
    case 'update-instance-name':
      if (model.instanceModal === null) return [model, []];
      return [
        { ...model, instanceModal: { ...model.instanceModal, name: msg.name } },
        [],
      ];
    case 'set-instance-root':
      if (model.instanceModal === null) return [model, []];
      return [
        { ...model, instanceModal: { ...model.instanceModal, root: msg.root } },
        [],
      ];
    case 'submit-instance': {
      // #81 — create the new entity (reusing the submit-entity-create +
      // name-fact path), then write an `instance_of` ref fact pointing at
      // the source Lore, then navigate to the new entity. Scope option
      // (a): minimal ref only — NO template-trait copying (Phase 2).
      // The reducer guards the empty name even though the button disables.
      if (model.instanceModal === null) return [model, []];
      const trimmed = model.instanceModal.name.trim();
      if (trimmed === '') return [model, []];
      const root = model.instanceModal.root;
      const sourceId = model.instanceModal.sourceId;
      const id = mintEntityId(root);
      const route: AppRoute = { kind: 'entity', id };
      return [
        {
          ...model,
          route,
          instanceModal: null,
          paletteOpen: false,
          // Land on Facts so the GM sees the `instance_of` link + can author.
          entityPageTab: 'facts',
        },
        [...instanceCreateEffects(id, root, trimmed, sourceId), appRoutePush(route)],
      ];
    }
    case 'materialize-instance': {
      // B4 (#237) — turn a virtual recurrence instance into a real `event`
      // entity (`06-timeline.md` §10.3). REUSES the #81 instance scaffold
      // (`instanceCreateEffects` → entity-create + name + `instance_of` ref),
      // then copies the rule's defaults as CANON facts (`occurred_at`,
      // `location`, `participants`) so subsequent edits override locally and
      // the parent rule is untouched. The auto-materialization triggers
      // (§10.3) — a fact subjected to the instance, a reveal targeting it, an
      // LLM draft referencing it — ride on the SAME materialize, appending the
      // triggering op against the freshly-minted entity id. Materialization is
      // ONE-WAY: the entity is created unconditionally; retracting the
      // triggering fact later (the ordinary soft-delete path) leaves the
      // entity standing.
      const p = msg.payload;
      const id = mintEntityId('event');
      const route: AppRoute = { kind: 'entity', id };
      const effects: Effect[] = instanceCreateEffects(id, 'event', p.name, p.loreId);
      // Copy the rule's defaults as canon facts on the new event.
      effects.push({
        type: 'submit-fact',
        subject: id,
        predicate: OFFICIAL_CORE_PREDICATE_IDS.occurred_at,
        payload: { kind: 'fact.create', value: p.occurredAt },
      });
      if (p.location !== undefined) {
        effects.push({
          type: 'submit-fact',
          subject: id,
          predicate: OFFICIAL_CORE_PREDICATE_IDS.location,
          payload: { kind: 'fact.create', value: p.location },
        });
      }
      for (const participant of p.participants) {
        effects.push({
          type: 'submit-fact',
          subject: id,
          predicate: OFFICIAL_CORE_PREDICATE_IDS.participants,
          payload: { kind: 'fact.create', value: participant },
        });
      }
      // Auto-materialization trigger (§10.3): the op that "referenced" the
      // instance now rides on the real entity id.
      const trigger = msg.trigger;
      if (trigger?.kind === 'fact') {
        effects.push({
          type: 'submit-fact',
          subject: id,
          predicate: trigger.predicate,
          payload: { kind: 'fact.create', value: trigger.value },
        });
      } else if (trigger?.kind === 'reveal') {
        effects.push({
          type: 'submit-reveal',
          subject: id,
          predicate: trigger.predicate,
          viewer: trigger.viewer,
        });
      } else if (trigger?.kind === 'llm-draft') {
        effects.push({
          type: 'llm-draft-entity',
          subject: id,
          predicates: trigger.predicates,
          ...(trigger.instruction !== undefined ? { instruction: trigger.instruction } : {}),
        });
      }
      effects.push(appRoutePush(route));
      return [
        {
          ...model,
          route,
          paletteOpen: false,
          // Land on Facts so the GM sees the copied defaults + can author.
          entityPageTab: 'facts',
        },
        effects,
      ];
    }
    case 'history/open':
      return [{ ...model, historyOpen: { entityId: msg.entityId, predicate: msg.predicate } }, []];

    case 'history/close':
      return [{ ...model, historyOpen: null }, []];

    case 'trait/highlight':
      // Cheap no-op guard: pointer moves fire this constantly, and an
      // unchanged model skips the whole reconcile pass.
      return model.highlightedTrait === msg.traitId
        ? [model, []]
        : [{ ...model, highlightedTrait: msg.traitId }, []];

    case 'open-trait-picker':
      return [
        { ...model, traitPickerEntityId: msg.entityId, traitPickerQuery: '' },
        [],
      ];
    case 'close-trait-picker':
      return [{ ...model, traitPickerEntityId: null, traitPickerQuery: '' }, []];
    case 'trait-picker-query':
      return [{ ...model, traitPickerQuery: msg.query }, []];
    case 'attach-trait': {
      // The picker's target entity is the Model field — the click
      // handler does not carry the entity id, so the reducer reads it.
      // If the picker is closed (race / stale dispatch) we no-op.
      const entityId = model.traitPickerEntityId;
      if (!entityId) return [model, []];
      const entity = model.entities[entityId];
      // Idempotent at the substrate layer too, but guard here so we
      // don't append a redundant op for an already-attached trait.
      if (entity && entity.traits.includes(msg.traitId)) {
        return [{ ...model, traitPickerEntityId: null, traitPickerQuery: '' }, []];
      }
      // Surface the attachment in the rebuildable Model cache immediately.
      // `submit-trait-mutation` appends to the local op log asynchronously;
      // without this optimistic copy, a fast attach -> Settings -> retire
      // sequence can inspect the pre-attach fold, misclassify the trait as
      // unused, and retire it without the in-use confirmation. The next
      // `replica/entities-rebuilt` remains authoritative and reconciles this
      // short-lived cache from the append-only log (including any rejection).
      const optimisticEntities = entity
        ? {
            ...model.entities,
            [entityId]: { ...entity, traits: [...entity.traits, msg.traitId] },
          }
        : model.entities;
      return [
        {
          ...model,
          entities: optimisticEntities,
          traitPickerEntityId: null,
          traitPickerQuery: '',
        },
        [
          {
            type: 'submit-trait-mutation',
            subject: entityId,
            op: 'attach',
            traitId: msg.traitId,
          },
        ],
      ];
    }
    case 'request-detach-trait': {
      const entity = model.entities[msg.entityId];
      if (!entity) return [model, []];
      const factCount = factCountForDetach(entity, msg.traitId);
      // No-prompt fast path — detaching a trait that hides zero facts
      // proceeds immediately. Confirms otherwise.
      if (factCount === 0) {
        return [
          model,
          [
            {
              type: 'submit-trait-mutation',
              subject: msg.entityId,
              op: 'detach',
              traitId: msg.traitId,
            },
          ],
        ];
      }
      return [
        {
          ...model,
          traitDetachConfirm: { entityId: msg.entityId, traitId: msg.traitId, factCount },
        },
        [],
      ];
    }
    case 'confirm-detach-trait': {
      const c = model.traitDetachConfirm;
      if (!c) return [model, []];
      return [
        { ...model, traitDetachConfirm: null },
        [
          {
            type: 'submit-trait-mutation',
            subject: c.entityId,
            op: 'detach',
            traitId: c.traitId,
          },
        ],
      ];
    }
    case 'cancel-detach-trait':
      return [{ ...model, traitDetachConfirm: null }, []];
    case 'request-delete-entity': {
      const entity = model.entities[msg.entityId];
      if (!entity || entity.soft_deleted) return [model, []];
      return [
        {
          ...model,
          entityDeleteConfirm: {
            entityId: msg.entityId,
            name: entityNameOrId(model, msg.entityId),
          },
        },
        [],
      ];
    }
    case 'confirm-delete-entity': {
      const c = model.entityDeleteConfirm;
      if (!c) return [model, []];
      // Append the soft-delete op and leave the (now-trashed) entity page for
      // the list — every projection filters `soft_deleted`, so the page would
      // otherwise read as an empty shell.
      const listRoute: AppRoute = { kind: 'list' };
      return [
        { ...model, entityDeleteConfirm: null, route: listRoute },
        [
          { type: 'submit-entity-soft-delete', subject: c.entityId },
          appRoutePush(listRoute),
        ],
      ];
    }
    case 'cancel-delete-entity':
      return [{ ...model, entityDeleteConfirm: null }, []];
    case 'open-controller-picker': {
      // Lazy-load the member list the first time the picker opens.
      const effects: Effect[] =
        model.campaignMembers === null ? [{ type: 'load-campaign-members' }] : [];
      return [{ ...model, controllerPicker: { entityId: msg.entityId } }, effects];
    }
    case 'close-controller-picker':
      return [{ ...model, controllerPicker: null }, []];
    case 'campaign-members/loaded':
      return [{ ...model, campaignMembers: msg.members }, []];

    // #263 — campaign roster + invite panel.
    case 'roster/invites-loaded':
      return [
        {
          ...model,
          inviteState: { emailInvites: msg.emailInvites, links: msg.links },
        },
        [],
      ];
    case 'roster/invite-email-change':
      return [
        {
          ...model,
          inviteEmailForm: { ...(model.inviteEmailForm ?? { role: 'player', sending: false }), email: msg.email },
        },
        [],
      ];
    case 'roster/invite-email-role':
      return [
        {
          ...model,
          inviteEmailForm: { ...(model.inviteEmailForm ?? { email: '', sending: false }), role: msg.role },
        },
        [],
      ];
    case 'roster/invite-email-send': {
      const form = model.inviteEmailForm;
      if (!form || !form.email || form.sending) return [model, []];
      return [
        { ...model, inviteEmailForm: { ...form, sending: true } },
        [{ type: 'roster/send-email-invite', email: form.email, role: form.role }],
      ];
    }
    case 'roster/invite-email-sent':
      return [
        { ...model, inviteEmailForm: null, inviteState: null },
        [{ type: 'roster/load-invites' }],
      ];
    case 'roster/invite-revoke':
      return [model, [{ type: 'roster/revoke-invite', inviteId: msg.inviteId }]];
    case 'roster/link-create':
      return [model, [{ type: 'roster/create-link', role: msg.role }]];
    case 'roster/link-close':
      return [model, [{ type: 'roster/close-link', linkId: msg.linkId }]];
    case 'roster/member-remove':
      return [
        {
          ...model,
          campaignMembers: model.campaignMembers?.filter((m) => m.user_id !== msg.userId) ?? null,
        },
        [{ type: 'roster/remove-member', userId: msg.userId }],
      ];

    // ── #265 — Rotating-facilitator badge lifecycle ───────────────────────
    case 'facilitator/open-modal':
      return [{ ...model, facilitatorModal: { mode: msg.mode, selectedUserId: null } }, []];

    case 'facilitator/close-modal':
      return [{ ...model, facilitatorModal: null }, []];

    case 'facilitator/select-user':
      if (!model.facilitatorModal) return [model, []];
      return [
        { ...model, facilitatorModal: { ...model.facilitatorModal, selectedUserId: msg.userId } },
        [],
      ];

    case 'facilitator/designate': {
      // Owner (or any user in the owner-seat) designates the session facilitator.
      // Builds the new rotation state from the current config bucket and writes
      // two `config.set` ops: `session_facilitator` + `rotation_history`.
      const currentRotation: RotationState = {
        session_facilitator: model.config.session_facilitator ?? null,
        rotation_history: (model.config.rotation_history as RotationHistoryEntry[] | undefined) ?? [],
      };
      const next = designateFacilitator(currentRotation, msg.userId, new Date().toISOString());
      return [
        { ...model, facilitatorModal: null },
        [
          {
            type: 'submit-config',
            payload: configSet('session_facilitator', next.session_facilitator),
          },
          {
            type: 'submit-config',
            payload: configSet('rotation_history', next.rotation_history),
          },
        ],
      ];
    }

    case 'facilitator/hand-off': {
      // Current facilitator passes the badge. The caller must be the current
      // facilitator — the UI guards this, but the pure reducer double-checks.
      const currentRotation: RotationState = {
        session_facilitator: model.config.session_facilitator ?? null,
        rotation_history: (model.config.rotation_history as RotationHistoryEntry[] | undefined) ?? [],
      };
      const callerId = model.session.userId;
      if (!callerId) return [{ ...model, facilitatorModal: null }, []];
      const result = handOffFacilitator(
        currentRotation,
        callerId,
        msg.toUserId,
        new Date().toISOString(),
      );
      if (!result.ok) {
        // Hand-off guard failed (caller is not the facilitator, etc.) — close modal silently.
        return [{ ...model, facilitatorModal: null }, []];
      }
      return [
        { ...model, facilitatorModal: null },
        [
          {
            type: 'submit-config',
            payload: configSet('session_facilitator', result.state.session_facilitator),
          },
          {
            type: 'submit-config',
            payload: configSet('rotation_history', result.state.rotation_history),
          },
        ],
      ];
    }

    case 'set-controller':
      // The binding is an ordinary `controlled_by` fact (value = the User id);
      // the projection's effective-Viewer union picks it up. Close the picker.
      return [
        { ...model, controllerPicker: null },
        [
          {
            type: 'submit-fact',
            subject: msg.entityId,
            predicate: OFFICIAL_CORE_PREDICATE_IDS.controlled_by,
            payload: { kind: 'fact.create', value: msg.userId },
          },
        ],
      ];
    case 'clear-controller':
      // Soft-deleting the controlled_by chain relinquishes control (the
      // resolver excludes soft-deleted chains).
      return [
        { ...model, controllerPicker: null },
        [
          {
            type: 'submit-fact',
            subject: msg.entityId,
            predicate: OFFICIAL_CORE_PREDICATE_IDS.controlled_by,
            payload: { kind: 'fact.soft_delete' },
          },
        ],
      ];
    case 'restore-entity': {
      // Un-delete from the Trash view — no confirm (restore is non-destructive).
      const entity = model.entities[msg.entityId];
      if (!entity || !entity.soft_deleted) return [model, []];
      return [model, [{ type: 'submit-entity-restore', subject: msg.entityId }]];
    }
    case 'open-trait-editor': {
      // #schema-settings — opened from an entity's trait picker. Seed the new
      // trait's `applies_to` with that entity's root, close the picker, and
      // navigate to the settings Traits tab where the editor renders inline.
      const targetId = model.traitPickerEntityId;
      const target = targetId ? model.entities[targetId] : undefined;
      const root: EntityRoot = target?.root ?? 'location';
      return [
        {
          ...model,
          route: { kind: 'settings', tab: 'traits' },
          traitPickerEntityId: null,
          traitEditor: {
            mode: 'create',
            forRoot: root,
            appliesTo: [root],
            traitId: '',
            fields: [emptyTraitEditorField(0)],
            nextFieldKey: 1,
            allowRisky: false,
            error: null,
          },
        },
        [appRoutePush({ kind: 'settings', tab: 'traits' })],
      ];
    }
    case 'edit-trait': {
      // Prefill the editor from the existing homebrew trait definition and
      // surface it on the settings Traits tab (the editor is no longer a
      // modal). Dispatched both from the entity-page picker (navigates) and
      // from the settings list (already there — the push is a no-op).
      // Reading the module-scope overlay here is a pragmatic cache read:
      // the overlay is rebuilt from the op log on every fold, so it's in
      // sync with `model`. Unknown / non-homebrew trait → ignore.
      const built = traitEditorStateForExisting(msg.traitId);
      if (!built) return [model, []];
      return [
        {
          ...model,
          route: { kind: 'settings', tab: 'traits' },
          traitPickerEntityId: null,
          traitEditor: built,
        },
        [appRoutePush({ kind: 'settings', tab: 'traits' })],
      ];
    }
    case 'close-trait-editor':
      return [{ ...model, traitEditor: null }, []];
    case 'trait-editor-set-id': {
      if (!model.traitEditor) return [model, []];
      return [
        { ...model, traitEditor: { ...model.traitEditor, traitId: msg.value, error: null } },
        [],
      ];
    }
    case 'trait-editor-add-field': {
      if (!model.traitEditor) return [model, []];
      return [
        {
          ...model,
          traitEditor: {
            ...model.traitEditor,
            fields: [
              ...model.traitEditor.fields,
              emptyTraitEditorField(model.traitEditor.nextFieldKey),
            ],
            nextFieldKey: model.traitEditor.nextFieldKey + 1,
            error: null,
          },
        },
        [],
      ];
    }
    case 'trait-editor-set-field': {
      if (!model.traitEditor) return [model, []];
      const fields = model.traitEditor.fields.map((f, i) =>
        i === msg.index ? { ...f, ...msg.patch } : f,
      );
      return [{ ...model, traitEditor: { ...model.traitEditor, fields, error: null } }, []];
    }
    case 'trait-editor-remove-field': {
      if (!model.traitEditor) return [model, []];
      const fields = model.traitEditor.fields.filter((_, i) => i !== msg.index);
      return [{ ...model, traitEditor: { ...model.traitEditor, fields, error: null } }, []];
    }
    case 'trait-editor-add-tuple-field': {
      if (!model.traitEditor) return [model, []];
      const fields = model.traitEditor.fields.map((f, i) => {
        if (i !== msg.field) return f;
        const subs = f.tupleFields ?? [];
        const nextKey = subs.reduce((m, s) => Math.max(m, s.key + 1), 0);
        return {
          ...f,
          tupleFields: [...subs, { key: nextKey, name: '', valueKind: 'text' as TupleFieldKind, optional: false }],
        };
      });
      return [{ ...model, traitEditor: { ...model.traitEditor, fields, error: null } }, []];
    }
    case 'trait-editor-set-tuple-field': {
      if (!model.traitEditor) return [model, []];
      const fields = model.traitEditor.fields.map((f, i) => {
        if (i !== msg.field) return f;
        const subs = (f.tupleFields ?? []).map((s, j) =>
          j === msg.sub ? { ...s, ...msg.patch } : s,
        );
        return { ...f, tupleFields: subs };
      });
      return [{ ...model, traitEditor: { ...model.traitEditor, fields, error: null } }, []];
    }
    case 'trait-editor-remove-tuple-field': {
      if (!model.traitEditor) return [model, []];
      const fields = model.traitEditor.fields.map((f, i) => {
        if (i !== msg.field) return f;
        return { ...f, tupleFields: (f.tupleFields ?? []).filter((_, j) => j !== msg.sub) };
      });
      return [{ ...model, traitEditor: { ...model.traitEditor, fields, error: null } }, []];
    }
    case 'trait-editor-toggle-risky': {
      if (!model.traitEditor) return [model, []];
      return [
        { ...model, traitEditor: { ...model.traitEditor, allowRisky: msg.value, error: null } },
        [],
      ];
    }
    case 'trait-editor-submit': {
      if (!model.traitEditor) return [model, []];
      const built = buildTraitDefinitionOps(model.traitEditor);
      if (!built.ok) {
        return [
          { ...model, traitEditor: { ...model.traitEditor, error: built.error } },
          [],
        ];
      }
      // Close the editor and append the homebrew Pack ops (predicates
      // first, then the trait — ADR-0001 replay ordering).
      return [
        { ...model, traitEditor: null },
        [{ type: 'submit-pack-ops', payloads: built.payloads }],
      ];
    }
    case 'delete-current-trait': {
      // Retire the trait the editor is currently editing. Non-destructive
      // (ADR-0001): retire removes the definition from the overlay so its
      // rows stop rendering, but the facts persist in the op log and re-
      // surface if the trait is re-defined. When the trait is still in use
      // (attached to entities or carrying live facts) we route through the
      // confirm overlay first instead of deleting silently.
      const editing = model.traitEditor;
      if (!editing || editing.mode !== 'edit' || !editing.traitOriginalId) {
        return [{ ...model, traitEditor: null }, []];
      }
      const traitId = editing.traitOriginalId;
      const usage = traitUsage(model.entities, traitId);
      if (usage.entityCount === 0 && usage.factCount === 0) {
        return [
          { ...model, traitEditor: null },
          [{ type: 'submit-pack-ops', payloads: [packRetireTrait(traitId)] }],
        ];
      }
      return [
        {
          ...model,
          schemaRetireConfirm: {
            target: 'trait',
            id: traitId,
            label: traitLabel(traitId),
            entityCount: usage.entityCount,
            factCount: usage.factCount,
            fromEditor: true,
          },
        },
        [],
      ];
    }
    case 'retire-trait': {
      // No-prompt fast path mirrors detach — retiring a trait that nothing
      // uses fires immediately; an in-use retire opens the confirm overlay.
      const usage = traitUsage(model.entities, msg.traitId);
      if (usage.entityCount === 0 && usage.factCount === 0) {
        return [model, [{ type: 'submit-pack-ops', payloads: [packRetireTrait(msg.traitId)] }]];
      }
      return [
        {
          ...model,
          schemaRetireConfirm: {
            target: 'trait',
            id: msg.traitId,
            label: traitLabel(msg.traitId),
            entityCount: usage.entityCount,
            factCount: usage.factCount,
            fromEditor: false,
          },
        },
        [],
      ];
    }
    case 'open-trait-editor-blank':
      // #schema-settings — open a fresh trait-editor form on the settings
      // page. No picker target, so `appliesTo` starts empty and the form
      // surfaces the roots multi-select (defaulting to `forRoot` only when
      // the GM leaves it untouched — buildTraitDefinitionOps falls back).
      return [
        {
          ...model,
          traitEditor: {
            mode: 'create',
            forRoot: 'location',
            appliesTo: [],
            traitId: '',
            fields: [emptyTraitEditorField(0)],
            nextFieldKey: 1,
            allowRisky: false,
            error: null,
          },
        },
        [],
      ];
    case 'trait-editor-toggle-applies-to': {
      if (!model.traitEditor) return [model, []];
      const current = model.traitEditor.appliesTo ?? [];
      const next = current.includes(msg.root)
        ? current.filter((r) => r !== msg.root)
        : [...current, msg.root];
      return [
        { ...model, traitEditor: { ...model.traitEditor, appliesTo: next, error: null } },
        [],
      ];
    }
    case 'retire-predicate': {
      // #schema-settings — retire a homebrew predicate from the list. Non-
      // destructive (ADR-0001): the definition leaves the overlay; facts
      // written against it persist and re-surface if it's re-defined. An
      // in-use predicate routes through the confirm overlay first.
      const factCount = predicateUsage(model.entities, msg.predicateId);
      if (factCount === 0) {
        return [
          model,
          [{ type: 'submit-pack-ops', payloads: [packRetirePredicate(msg.predicateId)] }],
        ];
      }
      return [
        {
          ...model,
          schemaRetireConfirm: {
            target: 'predicate',
            id: msg.predicateId,
            label: predicateLabel(msg.predicateId),
            entityCount: 0,
            factCount,
            fromEditor: false,
          },
        },
        [],
      ];
    }
    case 'confirm-schema-retire': {
      const c = model.schemaRetireConfirm;
      if (!c) return [model, []];
      const payload =
        c.target === 'trait' ? packRetireTrait(c.id) : packRetirePredicate(c.id);
      return [
        {
          ...model,
          schemaRetireConfirm: null,
          // A delete dispatched from the inline editor also closes it.
          traitEditor: c.fromEditor ? null : model.traitEditor,
        },
        [{ type: 'submit-pack-ops', payloads: [payload] }],
      ];
    }
    case 'cancel-schema-retire':
      return [{ ...model, schemaRetireConfirm: null }, []];
    // ── #177 / ADR-0004 §1/§4 — custom-calendar authoring ──────────────
    case 'calendar-editor': {
      const inner = msg.msg;
      // Lifecycle messages open/close the section editor (they touch the
      // wider Model slice); the `ce-*` edits patch the working draft and the
      // advanced toggle flips mode.
      switch (inner.type) {
        case 'cal-new':
          return [
            {
              ...model,
              calendarEditor: { working: blankWorkingCalendar(), mode: 'advanced', origin: 'create' },
            },
            [],
          ];
        case 'cal-edit':
        case 'cal-fork': {
          const opened = openCalendarForEdit(
            inner.id,
            inner.type === 'cal-edit' ? 'campaign' : 'bundled',
          );
          // Unresolvable id → leave the list showing (defensive).
          return opened ? [{ ...model, calendarEditor: opened }, []] : [model, []];
        }
        case 'cal-cancel':
          return [{ ...model, calendarEditor: null }, []];
        case 'ce-toggle-advanced': {
          if (!model.calendarEditor) return [model, []];
          const mode = model.calendarEditor.mode === 'advanced' ? 'simple' : 'advanced';
          return [{ ...model, calendarEditor: { ...model.calendarEditor, mode } }, []];
        }
        default: {
          // A `ce-*` field edit — patch the working draft.
          if (!model.calendarEditor) return [model, []];
          const working = reduceWorkingCalendar(model.calendarEditor.working, inner);
          return [{ ...model, calendarEditor: { ...model.calendarEditor, working } }, []];
        }
      }
    }
    case 'calendar-save': {
      // Fold the working draft to a `pack.define_calendar` op (create / edit /
      // fork all converge on define — last-writer-wins per opaque `cal_` id).
      // The atom gates Save on blocking errors, so the fold should succeed; a
      // null payload (defensive) leaves the editor open.
      if (!model.calendarEditor) return [model, []];
      const payload = defineCalendarPayload(model.calendarEditor.working);
      if (payload === null) return [model, []];
      return [
        { ...model, calendarEditor: null },
        [{ type: 'submit-pack-ops', payloads: [payload] }],
      ];
    }
    case 'calendar-retire':
      // Non-destructive (ADR-0001): the definition leaves the overlay; the
      // `canonical_calendar` pointer falls back to Gregorian if it pointed here.
      return [
        model,
        [{ type: 'submit-pack-ops', payloads: [retireCalendarPayload(msg.calendarId)] }],
      ];
    case 'calendar-set-primary':
      // The campaign's CHOICE of canonical calendar is a *setting* — a
      // `config.set canonical_calendar` op (the same path the typed Campaign
      // Settings picker uses), not a pack definition.
      return [model, [{ type: 'submit-config', payload: configSet('canonical_calendar', msg.calendarId) }]];
    case 'calendar-close-cycle': {
      // Closing an open cycle instance is a `pack.define_calendar` DEFINITION
      // update (LWW per `cal_` id) — never a fact (§4.1). A null payload
      // (unresolvable calendar / close-day before start) is a no-op.
      const payload = closeOpenCyclePayload(msg.handle, msg.closeDay);
      if (payload === null) return [model, []];
      return [model, [{ type: 'submit-pack-ops', payloads: [payload] }]];
    }
    case 'open-type-editor-blank':
      // #schema-settings — open a fresh entity-type form on the Types tab.
      return [
        {
          ...model,
          typeEditor: {
            mode: 'create',
            typeId: '',
            label: '',
            root: 'location',
            traits: [],
            error: null,
          },
        },
        [],
      ];
    case 'edit-type': {
      const built = typeEditorStateForExisting(msg.typeId);
      if (!built) return [model, []];
      return [{ ...model, typeEditor: built }, []];
    }
    case 'close-type-editor':
      return [{ ...model, typeEditor: null }, []];
    case 'type-editor-set': {
      if (!model.typeEditor) return [model, []];
      return [
        { ...model, typeEditor: { ...model.typeEditor, ...msg.patch, error: null } },
        [],
      ];
    }
    case 'type-editor-toggle-trait': {
      if (!model.typeEditor) return [model, []];
      const current = model.typeEditor.traits;
      const next = current.includes(msg.traitId)
        ? current.filter((tr) => tr !== msg.traitId)
        : [...current, msg.traitId];
      return [{ ...model, typeEditor: { ...model.typeEditor, traits: next, error: null } }, []];
    }
    case 'type-editor-submit': {
      if (!model.typeEditor) return [model, []];
      const built = buildTypeDefinitionOp(model.typeEditor);
      if (!built.ok) {
        return [{ ...model, typeEditor: { ...model.typeEditor, error: built.error } }, []];
      }
      // Close the editor and append the define-type op.
      return [
        { ...model, typeEditor: null },
        [{ type: 'submit-pack-ops', payloads: [built.payload] }],
      ];
    }
    case 'retire-type':
      return [model, [{ type: 'submit-pack-ops', payloads: [packRetireType(msg.typeId)] }]];
    case 'open-units-editor':
      return [
        {
          ...model,
          unitsEditor: {
            dimensionDraft: emptyDimensionDraft(),
            unitDraft: emptyUnitDraft(),
            error: null,
          },
        },
        [],
      ];
    case 'close-units-editor':
      return [{ ...model, unitsEditor: null }, []];
    case 'units-editor-set-dimension': {
      if (!model.unitsEditor) return [model, []];
      return [
        {
          ...model,
          unitsEditor: {
            ...model.unitsEditor,
            dimensionDraft: { ...model.unitsEditor.dimensionDraft, ...msg.patch },
            error: null,
          },
        },
        [],
      ];
    }
    case 'units-editor-submit-dimension': {
      if (!model.unitsEditor) return [model, []];
      const built = buildDimensionDefinitionOp(model.unitsEditor.dimensionDraft);
      if (!built.ok) {
        return [
          { ...model, unitsEditor: { ...model.unitsEditor, error: built.error } },
          [],
        ];
      }
      // Reset the dimension draft and pre-select the freshly-minted dimension
      // in the unit form (so the GM can immediately add its units), then
      // append the define-dimension op.
      return [
        {
          ...model,
          unitsEditor: {
            ...model.unitsEditor,
            dimensionDraft: emptyDimensionDraft(),
            unitDraft: { ...model.unitsEditor.unitDraft, dimension: built.dimensionId },
            error: null,
          },
        },
        [{ type: 'submit-pack-ops', payloads: [built.payload] }],
      ];
    }
    case 'units-editor-set-unit': {
      if (!model.unitsEditor) return [model, []];
      return [
        {
          ...model,
          unitsEditor: {
            ...model.unitsEditor,
            unitDraft: { ...model.unitsEditor.unitDraft, ...msg.patch },
            error: null,
          },
        },
        [],
      ];
    }
    case 'units-editor-submit-unit': {
      if (!model.unitsEditor) return [model, []];
      const draft = model.unitsEditor.unitDraft;
      const built = buildUnitDefinitionOp(draft);
      if (!built.ok) {
        return [{ ...model, unitsEditor: { ...model.unitsEditor, error: built.error } }, []];
      }
      const payloads: PackOpPayload[] = [built.payload];
      // When the GM marks this unit as the dimension's base, re-define the
      // dimension to point `base_unit` at the freshly-minted unit id
      // (preserving its name). The canonical base value is then denominated in
      // this unit (ADR-0007 §1).
      if (draft.isBase) {
        const dim = campaignUnitRegistry().dimension(built.definition.dimension);
        if (dim) {
          payloads.push(packDefineDimension({ ...dim, base_unit: built.definition.id }));
        }
      }
      return [
        {
          ...model,
          unitsEditor: { ...model.unitsEditor, unitDraft: emptyUnitDraft(), error: null },
        },
        [{ type: 'submit-pack-ops', payloads }],
      ];
    }
    case 'retire-dimension':
      return [
        model,
        [{ type: 'submit-pack-ops', payloads: [packRetireDimension(msg.dimensionId)] }],
      ];
    case 'retire-unit':
      return [model, [{ type: 'submit-pack-ops', payloads: [packRetireUnit(msg.unitId)] }]];
    case 'campaigns-boot/loading':
      return [{ ...model, campaignsBoot: { kind: 'loading' } }, []];
    case 'campaigns-boot/empty':
      return [{ ...model, campaignsBoot: { kind: 'empty' } }, []];
    case 'campaigns-boot/loaded':
      return [
        {
          ...model,
          campaignsBoot: { kind: 'picker', campaigns: msg.campaigns },
        },
        [],
      ];
    case 'campaigns-boot/error':
      return [
        { ...model, campaignsBoot: { kind: 'error', message: msg.message } },
        [],
      ];
    case 'actions-overlay/toggle':
      return [{ ...model, actionsOverlayOpen: !model.actionsOverlayOpen }, []];
    case 'actions-overlay/close':
      return [{ ...model, actionsOverlayOpen: false }, []];
    case 'open-palette':
      return [{ ...model, paletteOpen: true, paletteQuery: '', paletteActiveIndex: 0 }, []];
    case 'close-palette':
      return [{ ...model, paletteOpen: false }, []];
    case 'palette-query':
      // #134 — every keystroke resets the active row to 0 so the user
      // doesn't end up on a stale index after the result set shrinks.
      return [{ ...model, paletteQuery: msg.value, paletteActiveIndex: 0 }, []];
    case 'palette-move': {
      const rows = paletteRowCount(model);
      if (rows === 0) return [model, []];
      // Wrap around at both ends — keeps keyboard nav predictable on
      // short result sets.
      const next = ((model.paletteActiveIndex + msg.delta) % rows + rows) % rows;
      return [{ ...model, paletteActiveIndex: next }, []];
    }
    case 'palette-activate': {
      const row = paletteRowAt(model, model.paletteActiveIndex);
      if (!row) return [model, []];
      if (row.kind === 'result') {
        const route: AppRoute = { kind: 'entity', id: row.id };
        return [{ ...model, paletteOpen: false, route }, [appRoutePush(route)]];
      }
      // Quick-create: delegate to the `create-entity` Msg so the
      // existing op-submission path runs unchanged.
      return update(model, { type: 'create-entity', root: row.root });
    }
    case 'edit-fact': {
      // Bug 1 — clear inline errors optimistically, but do NOT stamp
      // `factSavedAt` or clear `factDirty` until the substrate ACKs the
      // write via `fact-actually-saved`. The earlier shape lied to the
      // GM whenever `submitFact` early-returned (substrate failed to
      // open) because the reducer stamped the timestamp unconditionally.
      const newErrors = { ...model.inlineErrors };
      const key = errorKey(msg.entityId, msg.predicate);
      delete newErrors[key];
      const existing = model.entities[msg.entityId]?.facts[msg.predicate];
      // §7.2: a player editing a projected fact (value lives in `projectedFacts`,
      // not their replica) supersedes it as an `fact.edit` against the projected
      // chain head `version`, so the substrate's stale-detection makes it
      // conflict-aware (concurrent GM edit → reject; else canon).
      const projected =
        model.session.mode === 'player'
          ? model.projectedFacts[factKey(msg.entityId, msg.predicate)]
          : undefined;
      const opKind =
        (existing && existing.versions.length > 0) || projected !== undefined
          ? 'fact.edit'
          : 'fact.create';
      // As-of authoring gate (ADR-0005 §3, #192): creating a NEW value while
      // the browse cursor diverges is locked (it would be mis-stamped at the
      // wrong in-world day). Editing an EXISTING fact in place (fact.edit) is a
      // correct/refine and stays allowed. The UI also disables the input + shows
      // a lock notice; this is the authoritative reducer-side guard.
      if (opKind === 'fact.create' && model.browseCursor !== null) {
        return [{ ...model, inlineErrors: newErrors }, []];
      }
      // Snapshot the dirty epoch at submit time. The ACK
      // (`fact-actually-saved`) carries this back and the reducer arm
      // uses it to decide whether the user has typed *more* since
      // we initiated the submit — if so, the dirty flag stays.
      const dirtyEpoch = model.factDirtyEpoch[key] ?? 0;
      return [
        // Mark the row in-flight → the rail shows "Saving…" until the ACK
        // (`fact-actually-saved`) or failure (`fact-save-failed`) clears it.
        { ...model, inlineErrors: newErrors, factSaving: { ...model.factSaving, [key]: true } },
        [
          {
            type: 'submit-fact',
            subject: msg.entityId,
            predicate: msg.predicate,
            payload: { kind: opKind, value: msg.value },
            dirtyEpoch,
            ...(projected !== undefined
              ? { baseVersionOverride: projected.version ?? null }
              : {}),
          },
        ],
      ];
    }
    case 'fact-actually-saved': {
      // Fired from `submitFact` after the local append + queue step
      // actually succeeds. Clears the dirty flag and surfaces the
      // "Saved ✓" badge — but ONLY when the captured `dirtyEpoch`
      // matches the model's current epoch. If the user typed *after*
      // this submit fired (epoch advanced) or the submit didn't carry
      // an epoch at all (auto-create flows where the user never
      // marked the row dirty), keep `factDirty[key]` untouched so a
      // typed-but-unsaved value stays visibly dirty. The 2026-05-26
      // e2e (#9.4) regression-locks this against a stale ACK
      // stomping fresh typing.
      const key = errorKey(msg.entityId, msg.predicate);
      const now = Date.now();
      const nextSavedAt = { ...model.factSavedAt, [key]: now };
      const currentEpoch = model.factDirtyEpoch[key] ?? 0;
      const ackEpoch = msg.dirtyEpoch;
      const stale = ackEpoch === undefined || ackEpoch !== currentEpoch;
      const nextDirty = stale ? model.factDirty : { ...model.factDirty };
      // Same epoch-comparison gate for the input buffer: clear it
      // only on a fresh ACK; a stale ACK leaves the buffer in place
      // so the user's mid-flight typing survives.
      const nextBuffer = stale ? model.inputBuffer : { ...model.inputBuffer };
      if (!stale) {
        delete (nextDirty as Record<string, true>)[key];
        delete (nextBuffer as Record<string, string>)[key];
      }
      // The round-trip finished → clear the in-flight flag regardless of
      // staleness (a newer pending edit, if any, re-arms it on its own commit).
      const nextSaving = { ...model.factSaving };
      delete nextSaving[key];
      return [
        {
          ...model,
          factDirty: nextDirty,
          inputBuffer: nextBuffer,
          factSavedAt: nextSavedAt,
          factSaving: nextSaving,
        },
        [
          {
            type: 'schedule-fact-saved-clear',
            entityId: msg.entityId,
            predicate: msg.predicate,
            delayMs: 1500,
          },
        ],
      ];
    }
    case 'fact-save-failed': {
      // Bug 1 — surface a visible error in the existing inline-error
      // slot. The dirty flag intentionally stays SET so the row stays
      // visibly unsaved (the GM has not committed anything to durable
      // storage; the value in the input is the only remaining copy).
      // The `factSavedAt` stamp is NOT set — the badge does not surface
      // for a failed save.
      const key = errorKey(msg.entityId, msg.predicate);
      const nextSaving = { ...model.factSaving };
      delete nextSaving[key];
      return [
        {
          ...model,
          inlineErrors: { ...model.inlineErrors, [key]: msg.error },
          factSaving: nextSaving,
        },
        [],
      ];
    }
    case 'invalid-fact':
      return [
        {
          ...model,
          inlineErrors: {
            ...model.inlineErrors,
            [errorKey(msg.entityId, msg.predicate)]: msg.error,
          },
        },
        [],
      ];
    case 'fact-input-dirty': {
      // #156 — dirty-flag set + epoch bump. The reducer is called on
      // every keystroke that lands in a fact input. We bump the
      // per-key epoch unconditionally so a stale ACK
      // (`fact-actually-saved`) carrying an older captured epoch
      // can't clear the freshly-typed dirty signal (2026-05-26
      // e2e #9.4). Setting `factDirty[key]` is otherwise idempotent;
      // the epoch is the source of "user-input-since-submit" truth.
      const key = errorKey(msg.entityId, msg.predicate);
      const nextEpoch = (model.factDirtyEpoch[key] ?? 0) + 1;
      // Setting dirty also clears any stale "saved ✓" badge — the GM
      // is mid-edit; the prior save is no longer the relevant signal.
      const nextSavedAt = { ...model.factSavedAt };
      delete nextSavedAt[key];
      // Dirty-buffer accessor: when the keystroke handler captured
      // the live DOM value, fold it into `inputBuffer[key]` so the
      // editor's reactive `value:` reads back what the user typed —
      // not the persisted fact value. Omitted `buffer` (e.g. a
      // structural dirty flip without a typed character) leaves the
      // existing buffer entry untouched.
      const nextBuffer =
        msg.buffer !== undefined
          ? { ...model.inputBuffer, [key]: msg.buffer }
          : model.inputBuffer;
      return [
        {
          ...model,
          factDirty: { ...model.factDirty, [key]: true },
          factDirtyEpoch: { ...model.factDirtyEpoch, [key]: nextEpoch },
          inputBuffer: nextBuffer,
          factSavedAt: nextSavedAt,
        },
        [],
      ];
    }
    case 'fact-saved-clear': {
      // #156 — the scheduled clear fires after the badge timeout. We
      // only clear if the saved-at timestamp hasn't been bumped by a
      // newer save (which would have re-scheduled another clear).
      const key = errorKey(msg.entityId, msg.predicate);
      if (!(key in model.factSavedAt)) return [model, []];
      const nextSavedAt = { ...model.factSavedAt };
      delete nextSavedAt[key];
      return [{ ...model, factSavedAt: nextSavedAt }, []];
    }
    case 'fact-focus': {
      // #156 — focused input drives the first-focus hint placement.
      const key = errorKey(msg.entityId, msg.predicate);
      if (model.focusedFactKey === key) return [model, []];
      return [{ ...model, focusedFactKey: key }, []];
    }
    case 'fact-blur': {
      // #156 — only clear if we still hold focus for THIS row; another
      // input may have stolen focus and already updated focusedFactKey.
      const key = errorKey(msg.entityId, msg.predicate);
      if (model.focusedFactKey !== key) return [model, []];
      return [{ ...model, focusedFactKey: null }, []];
    }
    case 'fact-hint-dismiss': {
      // #156 — persist the dismissal so the hint never re-surfaces
      // in this browser. The model flag is also flipped so the
      // current render hides the hint synchronously.
      if (typeof localStorage !== 'undefined') {
        try {
          localStorage.setItem('dl.factEditHintDismissed', '1');
        } catch {
          // See `init()` — same fall-through. Dismissal still applies
          // for this session; persistence just lapsed.
        }
      }
      return [{ ...model, factHintDismissed: true }, []];
    }
    case 'soft-delete-fact': {
      // Bug 1 — soft-delete mirrors the edit-fact arm: schedule the
      // submit and wait for the substrate ACK before stamping the
      // saved-at badge. The `fact-actually-saved` reducer arm handles
      // the success path; on failure the dirty flag stays set so the
      // GM knows the clear did not persist.
      return [
        model,
        [
          {
            type: 'submit-fact',
            subject: msg.entityId,
            predicate: msg.predicate,
            payload: { kind: 'fact.soft_delete' },
          },
        ],
      ];
    }
    // ── LLM author mode (Phase B) ──────────────────────────────────
    case 'llm-suggest-prose': {
      const key = `${msg.entityId}/${msg.predicate}`;
      if (model.aiDraftPending[key]) return [model, []]; // already in flight
      const aiDraftError = { ...model.aiDraftError };
      delete aiDraftError[key];
      return [
        {
          ...model,
          aiDraftPending: { ...model.aiDraftPending, [key]: true },
          aiDraftError,
          // Open the live preview (starts in the thinking phase).
          streamPreview: { entityId: msg.entityId, predicate: msg.predicate, text: '', phase: 'thinking' },
        },
        [
          {
            type: 'llm-suggest-prose',
            subject: msg.entityId,
            predicate: msg.predicate,
            ...(msg.instruction !== undefined ? { instruction: msg.instruction } : {}),
          },
        ],
      ];
    }
    case 'llm-stream-thinking': {
      if (model.streamPreview === null) return [model, []];
      return [{ ...model, streamPreview: { ...model.streamPreview, phase: 'thinking' } }, []];
    }
    case 'llm-stream-delta': {
      if (model.streamPreview === null) return [model, []];
      return [
        {
          ...model,
          streamPreview: {
            ...model.streamPreview,
            text: model.streamPreview.text + msg.delta,
            phase: 'writing',
          },
        },
        [],
      ];
    }
    case 'llm-suggest-succeeded': {
      const key = `${msg.entityId}/${msg.predicate}`;
      const aiDraftPending = { ...model.aiDraftPending };
      delete aiDraftPending[key];
      // The drafted fact arrives via the normal entities-rebuilt path
      // (syncClient.submit folds it); close the live preview.
      return [{ ...model, aiDraftPending, streamPreview: null }, []];
    }
    case 'llm-suggest-failed': {
      const key = `${msg.entityId}/${msg.predicate}`;
      const aiDraftPending = { ...model.aiDraftPending };
      delete aiDraftPending[key];
      return [
        {
          ...model,
          aiDraftPending,
          aiDraftError: { ...model.aiDraftError, [key]: msg.error },
          streamPreview: null,
        },
        [],
      ];
    }
    case 'ratify-draft': {
      // GM-authored fact.set_status → canon. After the op folds, the row's
      // status flips and it drops out of the inbox automatically. S9 (#196):
      // ratifying a draft whose validity implies a succession ALSO closes the
      // predecessor segment(s) — the GM's ratify click confirms the
      // system-derived close (the LLM never authored it). No silent merge.
      const closes = impliedClosesForRatify(
        model.entities[msg.entityId]?.facts ?? {},
        msg.predicate,
        loadGmRegistry().lookup(splitSegmentKey(msg.predicate).base)?.uniqueness_key,
      );
      return [
        model,
        [
          {
            type: 'submit-fact-status',
            subject: msg.entityId,
            predicate: msg.predicate,
            status: 'canon',
          },
          ...closes.map(
            (c): SubmitFactEffect => ({
              type: 'submit-fact',
              subject: msg.entityId,
              predicate: c.key,
              // closeImpliedPredecessor stamps value + a half-open `to`; build
              // the submit payload as a literal (the inline-literal idiom used
              // elsewhere; sidesteps FactEditPayload's looser optional types).
              payload: {
                kind: 'fact.edit',
                value: c.payload.value,
                validity: c.payload.validity ?? null,
              },
            }),
          ),
        ],
      ];
    }
    case 'reject-draft': {
      // Lossless reject that UPHOLDS the LLM-never-canon invariant. Restore the
      // last value the fact held while it was CANON (`shadowedCanon`) — never a
      // position-in-`versions` guess. A draft chain accrues draft versions (a
      // GM inline-tweak, an LLM re-draft) and `versions[len-2]` can itself be
      // unratified content, so promoting it would sneak unratified data into
      // canon. When the fact was never canon (LLM drafted onto an empty field),
      // soft-delete back to empty instead of promoting anything.
      const shadowed = model.shadowedCanon[msg.entityId]?.[msg.predicate];
      if (shadowed) {
        return [
          model,
          [
            {
              type: 'submit-fact-status',
              subject: msg.entityId,
              predicate: msg.predicate,
              status: 'canon',
              restoreValue: { value: shadowed.value },
            },
          ],
        ];
      }
      return [
        model,
        [
          {
            type: 'submit-fact',
            subject: msg.entityId,
            predicate: msg.predicate,
            payload: { kind: 'fact.soft_delete' },
          },
        ],
      ];
    }
    // #246 — session-HUD pending-lane resolution. Ratify promotes the
    // `pending` fact to `canon` via a GM-AUTHORED `fact.set_status` (the same
    // `submit-fact-status` effect the inbox uses, `GM_AUTHOR` in the effect
    // handler). This is the ONLY canon transition for a #223-promoted LLM
    // pending fact, and it is an explicit per-fact GM gesture — never an LLM /
    // system author, never a standing auto-ratify policy. The LLM→canon
    // backdoor stays closed. Reject is the lossless retract (restore the prior
    // shadowed canon, or soft-delete back to empty) — symmetric with
    // `reject-draft`.
    case 'pending-ratify': {
      const closes = impliedClosesForRatify(
        model.entities[msg.entityId]?.facts ?? {},
        msg.predicate,
        loadGmRegistry().lookup(splitSegmentKey(msg.predicate).base)?.uniqueness_key,
      );
      return [
        model,
        [
          {
            type: 'submit-fact-status',
            subject: msg.entityId,
            predicate: msg.predicate,
            status: 'canon',
          },
          ...closes.map(
            (c): SubmitFactEffect => ({
              type: 'submit-fact',
              subject: msg.entityId,
              predicate: c.key,
              payload: {
                kind: 'fact.edit',
                value: c.payload.value,
                validity: c.payload.validity ?? null,
              },
            }),
          ),
        ],
      ];
    }
    case 'pending-reject': {
      // Symmetric with `reject-draft`: restore the last CANON value the fact
      // held (`shadowedCanon`), never a positional `versions` guess that could
      // promote an unratified draft/pending value to canon. No prior canon ⇒
      // soft-delete back to empty.
      const shadowed = model.shadowedCanon[msg.entityId]?.[msg.predicate];
      if (shadowed) {
        return [
          model,
          [
            {
              type: 'submit-fact-status',
              subject: msg.entityId,
              predicate: msg.predicate,
              status: 'canon',
              restoreValue: { value: shadowed.value },
            },
          ],
        ];
      }
      return [
        model,
        [
          {
            type: 'submit-fact',
            subject: msg.entityId,
            predicate: msg.predicate,
            payload: { kind: 'fact.soft_delete' },
          },
        ],
      ];
    }
    // ── Phase D — per-fact image generation ─────────────────────────
    case 'llm-generate-image': {
      const key = `${msg.entityId}/${msg.predicate}`;
      if (model.aiDraftPending[key]) return [model, []];
      const aiDraftError = { ...model.aiDraftError };
      delete aiDraftError[key];
      return [
        { ...model, aiDraftPending: { ...model.aiDraftPending, [key]: true }, aiDraftError },
        [
          {
            type: 'llm-generate-image',
            subject: msg.entityId,
            predicate: msg.predicate,
            ...(msg.instruction && msg.instruction.trim() !== ''
              ? { instruction: msg.instruction.trim() }
              : {}),
          },
        ],
      ];
    }
    case 'llm-image-succeeded': {
      const key = `${msg.entityId}/${msg.predicate}`;
      const aiDraftPending = { ...model.aiDraftPending };
      delete aiDraftPending[key];
      return [
        { ...model, aiDraftPending, assetUrls: { ...model.assetUrls, [msg.assetId]: msg.url } },
        [],
      ];
    }
    case 'llm-image-failed': {
      const key = `${msg.entityId}/${msg.predicate}`;
      const aiDraftPending = { ...model.aiDraftPending };
      delete aiDraftPending[key];
      return [
        { ...model, aiDraftPending, aiDraftError: { ...model.aiDraftError, [key]: msg.error } },
        [],
      ];
    }
    case 'asset-url-resolved': {
      if (model.assetUrls[msg.assetId] === msg.url) return [model, []];
      return [{ ...model, assetUrls: { ...model.assetUrls, [msg.assetId]: msg.url } }, []];
    }
    case 'ai-setting/edit': {
      const cur = model.aiSettings[msg.scope];
      return [
        {
          ...model,
          aiSettings: {
            ...model.aiSettings,
            [msg.scope]: { value: msg.value, dirty: true, loaded: cur.loaded },
          },
        },
        [],
      ];
    }
    case 'ai-setting/save': {
      // #186 / ADR-0004 §5 — persist the per-campaign prompt extensions as a
      // single `config.set ai_prompt_extensions` op. Merge the edited scope's
      // current buffer value into the existing folded record (the value is one
      // object — LWW as a whole), then submit over WS. An empty scope is
      // dropped from the record so a cleared extension contributes nothing and
      // the worker composer reads ''.
      //
      // The optimistic value is written straight into `model.config` here —
      // exactly like `set-auditor-override` writes `model.auditorRuleOverrides`
      // and `set-pending-policy` writes `model.pendingAutoRatifyPolicy`. The
      // earlier code cleared the dirty flag but left `model.config` to catch up
      // only on the next async op-fold (`onOpApplied → scheduleRebuild →
      // rebuildEntities`). That fold is a Comlink round-trip behind a coalesced
      // timer, so a GM that saved and immediately navigated/reloaded raced the
      // fold and saw the note vanish. Setting `config` synchronously makes the
      // optimistic value the single in-Model source of truth the moment Save is
      // clicked (the server `config.set` op then reconciles it on the next fold).
      const merged: AiPromptExtensions = { ...(model.config.ai_prompt_extensions ?? {}) };
      const edited = model.aiSettings[msg.scope].value.trim();
      if (edited === '') delete merged[msg.scope];
      else merged[msg.scope] = edited;
      const cur = model.aiSettings[msg.scope];
      return [
        {
          ...model,
          config: { ...model.config, ai_prompt_extensions: merged },
          aiSettings: {
            ...model.aiSettings,
            [msg.scope]: { value: cur.value, dirty: false, loaded: cur.loaded },
          },
        },
        [{ type: 'submit-config', payload: configSet('ai_prompt_extensions', merged) }],
      ];
    }
    case 'auditor-overrides-loaded': {
      // Hydrated from the server — adopt, then re-run the audit so the panel +
      // badges reflect the persisted overrides without waiting for the next
      // op-driven rebuild.
      const advisories = computeAdvisories(model.entities, msg.overrides, model.bundles);
      return [{ ...model, auditorRuleOverrides: msg.overrides, advisories }, []];
    }
    case 'set-auditor-override': {
      // 'default' clears the rule's override (drop the key); any other value
      // sets it. Recompute advisories immediately + persist the new map.
      const next: AuditorRuleOverrides = { ...model.auditorRuleOverrides };
      if (msg.value === 'default') delete next[msg.rule];
      else next[msg.rule] = msg.value;
      const advisories = computeAdvisories(model.entities, next, model.bundles);
      // #186 / ADR-0004 §5: the per-rule auditor overrides are a `config.set`
      // op (the D1 PUT was removed in #179). Set the Model + re-run advisories
      // optimistically AND submit the full map as one config.set; the DO folds
      // its own log to resolve the overrides server-side (#176).
      return [
        { ...model, auditorRuleOverrides: next, advisories },
        [{ type: 'submit-config', payload: configSet('auditor_rule_overrides', next) }],
      ];
    }
    case 'set-config': {
      // #186 / ADR-0004 §5 — a typed Campaign-Settings change. Submit a
      // `config.set` op over WS; the authoritative value arrives back on the
      // next `replica/entities-rebuilt` fold (live across devices). The op log
      // remains the single source of truth for settings.
      //
      // The value is ALSO written into `Model.config` here, optimistically. This
      // arm used to set no Model field at all, on the reasoning that the fold
      // would supply it — true, but not synchronously: the round-trip is
      // reducer → effect → awaitSubstrate → getHead → submit → appendLocal →
      // onOpApplied → re-fold → Model. That gap silently lost updates for any
      // COMPOSITE setting, because `config.set` is LWW-per-key and a composite
      // stores every field in one object under one key — so the settings view
      // does a read-modify-write per field. A GM tabbing from `tone` to
      // `content_boundaries` in `gm_pref_profile` committed the second field
      // from a pre-first-commit object, and the first field was gone. (e2e
      // `30-gm-pref-profile.spec.ts` 30.1; see `config-optimistic.test.ts`.)
      //
      // This is the same optimistic-local-fold treatment facts already get (see
      // the note in `SyncClient.submit`): a short-lived cache write that the
      // authoritative fold overwrites moments later, so a GM's own successive
      // edits compose instead of racing. `sequence-by-key.ts` handles the
      // matching hazard one layer down — two ops forking off the same
      // `base_version`.
      const optimistic = { ...model.config, [msg.payload.key]: msg.payload.value };
      return [
        { ...model, config: optimistic },
        [{ type: 'submit-config', payload: msg.payload }],
      ];
    }
    // ── Phase C — per-page "Draft with AI" modal ────────────────────
    case 'open-page-draft': {
      const predicates = emptyProsePredicates(model, msg.entityId);
      return [{ ...model, pageDraftModal: { entityId: msg.entityId, prompt: '', predicates } }, []];
    }
    case 'page-draft-prompt': {
      if (model.pageDraftModal === null) return [model, []];
      return [{ ...model, pageDraftModal: { ...model.pageDraftModal, prompt: msg.value } }, []];
    }
    case 'close-page-draft':
      return [{ ...model, pageDraftModal: null }, []];
    case 'page-draft-submit': {
      const pageModal = model.pageDraftModal;
      if (pageModal === null) return [model, []];
      if (pageModal.predicates.length === 0) return [{ ...model, pageDraftModal: null }, []];
      const instruction = pageModal.prompt.trim();
      const aiDraftPending = { ...model.aiDraftPending };
      for (const pred of pageModal.predicates) {
        aiDraftPending[`${pageModal.entityId}/${pred}`] = true;
      }
      // ONE structured call → several proposals (Deferral 3), then close
      // and route to the inbox so the GM sees the drafts land.
      return [
        { ...model, pageDraftModal: null, aiDraftPending },
        [
          {
            type: 'llm-draft-entity',
            subject: pageModal.entityId,
            predicates: pageModal.predicates,
            ...(instruction !== '' ? { instruction } : {}),
          },
          appRouteNavigate({ kind: 'draft-inbox' }),
        ],
      ];
    }
    // ── #251 bulk world-generation modal (decision 6 / §14.8) ──────────
    case 'open-bundle-gen':
      return [
        {
          ...model,
          bundleGenModal: {
            target: msg.target,
            prompt: '',
            depth: 'sparse',
            mode: 'auto',
            phase: 'config',
            error: null,
          },
        },
        [],
      ];
    case 'bundle-gen-prompt':
      if (model.bundleGenModal === null) return [model, []];
      return [{ ...model, bundleGenModal: { ...model.bundleGenModal, prompt: msg.value } }, []];
    case 'bundle-gen-depth':
      if (model.bundleGenModal === null) return [model, []];
      return [{ ...model, bundleGenModal: { ...model.bundleGenModal, depth: msg.value } }, []];
    case 'bundle-gen-mode':
      if (model.bundleGenModal === null) return [model, []];
      return [{ ...model, bundleGenModal: { ...model.bundleGenModal, mode: msg.value } }, []];
    case 'bundle-gen-confirm': {
      const m = model.bundleGenModal;
      if (m === null || m.prompt.trim() === '') return [model, []];
      const request = bundleRequestFromModal(m);
      return [
        { ...model, bundleGenModal: { ...m, phase: 'generating', error: null } },
        [{ type: 'bulk-gen', request }],
      ];
    }
    case 'bundle-gen-done':
      // The drafts arrive via the DO sync broadcast + fold into `bundles`; just
      // close the modal and route to the inbox so the GM sees the bundle land.
      return [{ ...model, bundleGenModal: null }, [appRouteNavigate({ kind: 'draft-inbox' })]];
    case 'bundle-gen-failed': {
      const m = model.bundleGenModal;
      if (m === null) return [model, []];
      // Keep the modal open at `config` so the GM can adjust + retry (e.g. an
      // over-budget #218 refusal, or a provider error).
      return [{ ...model, bundleGenModal: { ...m, phase: 'config', error: msg.error } }, []];
    }
    case 'close-bundle-gen':
      return [{ ...model, bundleGenModal: null }, []];
    // ── ADR-0008 §1/§6 (T7, #281) LLM-led interactive authoring ─────────
    case 'open-authoring':
      // Open a fresh session shell + kick off the create POST. The SSE turn
      // stream + commit ride later msgs; the session id arrives async.
      return [
        {
          ...model,
          authoringSession: {
            sessionId: null,
            target: msg.target,
            messages: [],
            artifact: null,
            diff: null,
            phase: 'idle',
            pendingDirection: '',
            advisories: [],
            error: null,
          },
        },
        [{ type: 'open-authoring-session', target: msg.target }],
      ];
    case 'authoring-session-created': {
      const s = model.authoringSession;
      if (s === null) return [model, []];
      return [{ ...model, authoringSession: { ...s, sessionId: msg.sessionId } }, []];
    }
    case 'authoring-direction-input': {
      const s = model.authoringSession;
      if (s === null) return [model, []];
      return [{ ...model, authoringSession: { ...s, pendingDirection: msg.value } }, []];
    }
    case 'authoring-send-turn': {
      const s = model.authoringSession;
      // No-op until the session exists and the GM has typed a direction.
      if (s === null || s.sessionId === null || s.pendingDirection.trim() === '') return [model, []];
      const direction = s.pendingDirection;
      return [
        {
          ...model,
          authoringSession: {
            ...s,
            phase: 'streaming',
            pendingDirection: '',
            error: null,
            advisories: [],
            // Push the GM's message + an empty in-flight assistant message the
            // streamed tokens/tools accrete into.
            messages: [
              ...s.messages,
              { role: 'user', text: direction },
              { role: 'assistant', text: '' },
            ],
          },
        },
        [{ type: 'authoring-turn', sessionId: s.sessionId, direction }],
      ];
    }
    case 'authoring-token': {
      const s = model.authoringSession;
      if (s === null) return [model, []];
      return [
        { ...model, authoringSession: { ...s, messages: appendTokenToLastAssistant(s.messages, msg.text) } },
        [],
      ];
    }
    case 'authoring-tool': {
      const s = model.authoringSession;
      if (s === null) return [model, []];
      const entry: AuthoringToolEntry =
        msg.detail !== undefined
          ? { name: msg.name, status: msg.status, detail: msg.detail }
          : { name: msg.name, status: msg.status };
      return [
        { ...model, authoringSession: { ...s, messages: addToolToLastAssistant(s.messages, entry) } },
        [],
      ];
    }
    case 'authoring-artifact-diff': {
      const s = model.authoringSession;
      if (s === null) return [model, []];
      // The artifact embeds its own diff (ADR-0008 §1); mirror it into `diff`.
      return [
        { ...model, authoringSession: { ...s, artifact: msg.artifact, diff: msg.artifact.diff } },
        [],
      ];
    }
    case 'authoring-assistant-message': {
      const s = model.authoringSession;
      if (s === null) return [model, []];
      // The full assembled reply (the token stream is the incremental form) —
      // set the in-flight assistant message text to it.
      return [
        { ...model, authoringSession: { ...s, messages: setLastAssistantText(s.messages, msg.text) } },
        [],
      ];
    }
    case 'authoring-turn-done': {
      const s = model.authoringSession;
      if (s === null) return [model, []];
      return [{ ...model, authoringSession: { ...s, phase: 'idle' } }, []];
    }
    case 'authoring-error': {
      const s = model.authoringSession;
      if (s === null) return [model, []];
      return [{ ...model, authoringSession: { ...s, phase: 'idle', error: msg.error } }, []];
    }
    case 'authoring-commit': {
      const s = model.authoringSession;
      // Commit needs a session + a materialised artifact to ratify.
      if (s === null || s.sessionId === null || s.artifact === null) return [model, []];
      return [
        { ...model, authoringSession: { ...s, phase: 'committing', error: null, advisories: [] } },
        [{ type: 'commit-authoring', sessionId: s.sessionId }],
      ];
    }
    case 'authoring-commit-done':
      // Committed to the op log; the define_* ops ride the WS broadcast + fold
      // into the registry live. Close the session.
      return [{ ...model, authoringSession: null }, []];
    case 'authoring-commit-refused': {
      const s = model.authoringSession;
      if (s === null) return [model, []];
      // A blocking_error refused the commit; surface the advisories, stay idle
      // so the GM can fix-or-cancel (ADR-0008 §5).
      return [{ ...model, authoringSession: { ...s, phase: 'idle', advisories: msg.advisories } }, []];
    }
    case 'close-authoring':
      // Abort any in-flight SSE turn and drop the session.
      return [{ ...model, authoringSession: null }, [{ type: 'cancel-authoring' }]];
    // ── #251 bundle review (decisions 1/3/8) ───────────────────────────
    case 'bundle-accept-all': {
      // Accept every still-open draft member as canon via N individual
      // GM-authored ops (NOT a bundle-level op — that would break per-(subject,
      // predicate) base_version causality). The #223 LLM→canon backdoor stays
      // closed: every canon transition is authored by the GM who clicked accept.
      const auto = model.bundleAutoPromote[msg.bundleId] === true;
      const fx = bundleReviewMembers(model, msg.bundleId).map((row) =>
        acceptMemberEffect(model, row.entityId, row.predicate, auto),
      );
      return [model, fx];
    }
    case 'bundle-accept-member': {
      // A bundle-level auto-promote toggle also governs a single-member accept
      // for that bundle (looked up via the fact's bundle_id provenance).
      const bundleId = model.entities[msg.entityId]?.facts[msg.predicate]?.bundle_id;
      const auto = bundleId !== undefined && model.bundleAutoPromote[bundleId] === true;
      return [model, [acceptMemberEffect(model, msg.entityId, msg.predicate, auto)]];
    }
    case 'bundle-reject-all': {
      // Reject every open draft member via the existing lossless draft-reject
      // path (soft-delete — the op chain is retained). A new-stub member has no
      // prior shadowed value, so the reject is a soft-delete.
      const fx = bundleReviewMembers(model, msg.bundleId).map((row) =>
        rejectMemberEffect(model, row.entityId, row.predicate),
      );
      return [model, fx];
    }
    case 'bundle-reject-member':
      return [model, [rejectMemberEffect(model, msg.entityId, msg.predicate)]];
    case 'bundle-toggle-auto-promote': {
      const next = model.bundleAutoPromote[msg.bundleId] !== true;
      return [
        { ...model, bundleAutoPromote: { ...model.bundleAutoPromote, [msg.bundleId]: next } },
        [],
      ];
    }
    case 'open-bundle-regen':
      return [{ ...model, bundleRegenModal: { bundleId: msg.bundleId, prompt: '' } }, []];
    case 'bundle-regen-prompt':
      if (model.bundleRegenModal === null) return [model, []];
      return [{ ...model, bundleRegenModal: { ...model.bundleRegenModal, prompt: msg.value } }, []];
    case 'close-bundle-regen':
      return [{ ...model, bundleRegenModal: null }, []];
    case 'bundle-regen': {
      // Regenerate-with-direction (decision 8): a NEW BulkGenRequest producing a
      // NEW bundle linked via `regenerated_from`, AND dismiss the old bundle's
      // still-unaccepted drafts (the draft-reject path). The new bundle's target
      // mirrors the original declare's intent — bootstrap when the old bundle
      // declared bootstrap, else the first member's entity.
      const old = model.bundles[msg.bundleId];
      const members = bundleReviewMembers(model, msg.bundleId);
      const target = bundleRegenTarget(old, members);
      const request: BulkGenRequest = {
        target,
        prompt: msg.prompt,
        depth: old?.metadata?.depth ?? 'sparse',
        mode: old?.metadata?.mode ?? 'auto',
      };
      const dismiss = members.map((row) => rejectMemberEffect(model, row.entityId, row.predicate));
      return [
        { ...model, bundleRegenModal: null },
        [{ type: 'bulk-gen', request, regeneratedFrom: msg.bundleId }, ...dismiss],
      ];
    }
    case 'clear-review-state': {
      // The review-pass (§14.2): drop the `unreviewed_bulk` marker with an
      // ordinary GM-authored `fact.edit` carrying the current value + status,
      // omitting `review_state` (the fold clears it on a last-write edit). The
      // `submit-fact-status` effect's `restoreValue` builds exactly that
      // value-bearing edit (`factEdit(value, { status })`) — no review_state.
      const fact = model.entities[msg.entityId]?.facts[msg.predicate];
      if (!fact) return [model, []];
      const latest = fact.versions[fact.versions.length - 1];
      return [
        model,
        [
          {
            type: 'submit-fact-status',
            subject: msg.entityId,
            predicate: msg.predicate,
            status: fact.status,
            restoreValue: { value: latest?.value },
          },
        ],
      ];
    }
    case 'rollback-fact': {
      const fact = model.entities[msg.entityId]?.facts[msg.predicate];
      const historical = fact?.versions[msg.toIndex];
      if (!fact || !historical) return [model, []];
      // Rollback is a fresh edit op with the historical value (per the
      // op-log invariant — no history rewrites; the substrate folds the
      // chain forward and the new tip wins).
      return [
        model,
        [
          {
            type: 'submit-fact',
            subject: msg.entityId,
            predicate: msg.predicate,
            payload: { kind: 'fact.edit', value: historical.value },
          },
        ],
      ];
    }
    case 'set-connection':
      return [{ ...model, connection: msg.state }, []];
    case 'set-substrate-status':
      // Bug 3 — flip the substrate banner state. The `failed` case is
      // catastrophic (no saves possible); `memory` is degraded (saves
      // work but are lost on reload). The banner copy in `viewSubstrateBanner`
      // dispatches on this value.
      return [{ ...model, substrate: msg.status }, []];
    case 'open-substrate-dialog':
      return [{ ...model, substrateDialogOpen: true }, []];
    case 'close-substrate-dialog':
      return [{ ...model, substrateDialogOpen: false }, []];
    case 'replica/entities-rebuilt': {
      // §7.2 prefill — overlay a controlling player's projected PC facts onto
      // the freshly-folded `entities` (no-op for the GM). Computed once at the
      // top so the window snapshot, asset-URL resolution, and the editor all
      // see the same value; for the GM `overlaidEntities === msg.entities`.
      const overlaidEntities = withControlledProjectionOverlay(
        msg.entities,
        model.projectedFacts,
        model.session.controlledCharacters,
      );
      // IV.B.2 (#78) — also mirror the entity cache on `window` so the
      // click-time `currentTargetForVisited` accessor (called from
      // inside a button click handler outside the reactive accessor
      // graph) can walk the cache without re-folding. This is a
      // snapshot, not a source of truth; the op log remains canonical.
      if (typeof window !== 'undefined') {
        (window as unknown as { __DL_ENTITY_SNAPSHOT__?: Record<string, EntityState> })
          .__DL_ENTITY_SNAPSHOT__ = overlaidEntities;
      }
      // Asset-kind facts store an opaque `asset_id`; resolve each to a
      // signed content URL (once) so the editor + inbox render thumbnails —
      // including a controlled PC's projected `portrait` for the player.
      const assetIds = assetIdsNeedingUrls(overlaidEntities, model.assetUrls);
      const fx: Effect[] = assetIds.length > 0 ? [{ type: 'resolve-asset-urls', assetIds }] : [];
      // Phase-2 auditor (§5.3 on-edit timing): re-run the deterministic rule
      // engine over the rebuilt projection. Pure + synchronous; the result is
      // rebuildable derived state, mirrored into the Model for the panel +
      // nav badge. Per-Campaign severity overrides (§5.2.1) are applied.
      //
      // #179/#186 (ADR-0004 §5): the auditor overrides moved to the config
      // bucket (`config.set auditor_rule_overrides`). The folded
      // `msg.config.auditor_rule_overrides` is now the single source of truth;
      // it carries the persisted value across reloads. Adopt it into
      // `model.auditorRuleOverrides` (which drives the picker UI + later
      // recomputes) and audit against it. Fall back to the existing model value
      // only when the config bucket has no entry yet (fresh campaign, or before
      // the first config op folds — the legacy D1 GET still primes it).
      const auditorRuleOverrides = msg.config.auditor_rule_overrides ?? model.auditorRuleOverrides;
      // #259 — pass the bundle registry so the combat.quick_log_unreviewed rule
      // can flag open combat_log bundles older than 30 days.
      const advisories = computeAdvisories(overlaidEntities, auditorRuleOverrides, msg.bundles);
      // #180 (ADR-0004 §4/§5): derive the ACTIVE calendar from the folded
      // `canonical_calendar` setting pointer resolved against the folded
      // calendar-definition registry (the registry overlay was refreshed by
      // `setHomebrewDefs` earlier in the same fold). Falls back deterministically
      // when the pointer is unset or dangles.
      const nextCalendarId = resolveActiveCalendarId(msg.config, getHomebrewDefs().calendars);
      // ADR-0005 §"Session cursor": the cursor VALUE is a `config.set`
      // setting (`config.session_cursor`), folded from the op log — the
      // canonical, live-multi-GM-synced source of truth.
      //
      // ADR-0006 (governing truth): the canonical day axis is SHARED and
      // calendar-independent, so a folded `day` is meaningful regardless of
      // which calendar it was stamped against (`calendar_id` is provenance,
      // not a coordinate system). We therefore adopt the folded `day` as-is —
      // including across a calendar switch, where the SAME `day` simply
      // re-renders in the newly-active calendar. No reset; "now" is never lost.
      // When NO op exists yet (fresh campaign, or a legacy campaign whose only
      // record is the demoted D1 cache), leave the current value untouched so
      // the one-shot D1-GET boot fallback (`cursor/hydrate`) still primes it.
      const foldedCursor = msg.config.session_cursor ?? null;
      const cursorFromConfig =
        foldedCursor === null ? model.campaignCursor : foldedCursor.day;
      // #186 — hydrate the per-campaign AI prompt-extension editor buffer from
      // the folded `ai_prompt_extensions` config value (no-stomp on dirty rows).
      // The op log is the source of truth; this surfaces the saved value
      // optimistically + cross-device.
      const aiSettings = hydrateAiSettings(msg.config, model.aiSettings);
      const rebuilt = {
        ...model,
        entities: overlaidEntities,
        // LLM-never-canon: adopt the rebuilt shadowed-canon map so a later
        // reject restores the last ratified value rather than a positional one.
        shadowedCanon: msg.shadowedCanon ?? {},
        advisories,
        // #251 — adopt the folded bundle registry (a rebuildable view).
        bundles: msg.bundles,
        auditorRuleOverrides,
        config: msg.config,
        aiSettings,
        activeCalendarId: nextCalendarId,
        calendarConfirmed: true,
        // Hydrate "now" from the folded session-cursor setting (preserved
        // across calendar switches per ADR-0006). Cache it to localStorage
        // too (offline boot read) without re-submitting.
        campaignCursor: cursorFromConfig,
        // #264 — recompute member→Character map whenever the entity store
        // changes so the roster panel reflects real-time binding changes.
        memberControlledCharacters: computeMemberControlledCharacters(overlaidEntities),
      };
      return [
        rebuilt,
        [
          ...fx,
          {
            type: 'persist-cursor',
            calendarId: nextCalendarId,
            cursor: cursorFromConfig,
            toServer: false,
          },
          // 226.1 (#245) — the op log just changed; re-project the Preview-as
          // view so a revealed/un-revealed/edited fact reflects immediately.
          ...previewRefreshEffects(rebuilt),
          // #258 — the op log changed; re-broadcast the designated-Viewer
          // projection so the Table View reflects edits/reveals live.
          ...tableBroadcastEffects(rebuilt),
        ],
      ];
    }
    case 'sync/active':
      return model.syncing ? [model, []] : [{ ...model, syncing: true }, []];
    case 'sync/settled':
      return model.syncing ? [{ ...model, syncing: false }, []] : [model, []];
    case 'set-pending-policy': {
      // Defence-in-depth: the same Zod enum validates the wire shape.
      // Parsing here keeps the Model field internally consistent even if a
      // future call site bypasses the typed Msg constructor.
      // #186 / ADR-0004 §5: the pending policy is now a `config.set` op (its
      // D1 PUT was removed in #179). Set the Model optimistically AND submit
      // the op over WS — the DO folds its own log to enforce the policy at
      // arbitration time (#176), and the value reads back via `Model.config`.
      const policy = PendingAutoRatifyPolicySchema.parse(msg.policy);
      return [
        { ...model, pendingAutoRatifyPolicy: policy },
        [{ type: 'submit-config', payload: configSet('pending_auto_ratify_policy', policy) }],
      ];
    }
    case 'replica/conflict':
      // Surface as a counted pill at WS scope; the resolution UI lands as
      // a Phase 1 ticket. The op the user just authored is preserved in
      // the local replica via the SyncClient draft-with-conflict path.
      return [
        {
          ...model,
          connection: {
            ...model.connection,
            conflictCount: model.connection.conflictCount + 1,
          },
        },
        [],
      ];
    case 'set-cursor': {
      // Defence-in-depth: the cursor pill is `disabled` until the calendar is
      // confirmed, but never persist a date keyed to an unconfirmed (pre-fold)
      // calendar — it would encode a different in-world instant than intended.
      if (!model.calendarConfirmed) return [model, []];
      const next = { ...model, campaignCursor: msg.cursor };
      return [
        next,
        [
          {
            type: 'persist-cursor',
            calendarId: model.activeCalendarId,
            cursor: msg.cursor,
            toServer: true,
          },
          // 226.1 (#245) — the cursor IS the projection's `t`; re-project so
          // the preview reflects what the Viewer sees at the new in-world time.
          ...previewRefreshEffects(next),
          // #258 — the table renders at the same `t`; re-broadcast.
          ...tableBroadcastEffects(next),
        ],
      ];
    }
    case 'set-preview-viewer': {
      // 226.1 (#245) — only the GM previews; a real player is already scoped.
      // A GM viewer (or null) collapses back to the GM identity view (no
      // projection — the GM sees full canon directly from `entities`). A
      // non-GM viewer enters preview mode and kicks off the chokepoint effect.
      if (model.session.mode !== 'gm') return [model, []];
      const viewer = msg.viewer;
      if (viewer === null || isGmViewer(viewer)) {
        return [{ ...model, previewViewer: null, previewProjection: null }, []];
      }
      // Drop any prior projection so the panel shows "Projecting…" rather than
      // stale rows from the previously-selected Viewer while the effect runs.
      const next = { ...model, previewViewer: viewer, previewProjection: null };
      return [next, previewRefreshEffects(next)];
    }
    case 'preview-projection-computed': {
      // Drop a stale result: the GM may have changed the picker (or returned to
      // GM view) while the async projection was in flight. Only adopt rows whose
      // `viewerKey` matches the CURRENT preview viewer.
      if (model.previewViewer === null) return [model, []];
      if (viewerKeyFn(model.previewViewer) !== msg.viewerKey) return [model, []];
      return [{ ...model, previewProjection: msg.rows }, []];
    }
    case 'table/focus': {
      // #258 — fold a Pin-to-Table / un-pin action into the ephemeral table
      // focus, then re-broadcast the (unchanged-Viewer) projection narrowed to
      // the new focus. GM-only — only the GM tab owns the replica + the focus.
      if (model.session.mode !== 'gm') return [model, []];
      const tableFocus = reduceTableFocus(model.tableFocus, msg.action);
      if (tableFocusEquals(tableFocus, model.tableFocus)) return [model, []];
      const next = { ...model, tableFocus };
      return [next, tableBroadcastEffects(next)];
    }
    case 'table/set-designated-viewer': {
      // #258 — change WHOSE projection the table renders. A GM/null selection
      // collapses to the default (party Viewer); the table never renders
      // GM-truth. Re-broadcast so the Table tab updates.
      if (model.session.mode !== 'gm') return [model, []];
      const viewer = msg.viewer;
      const designatedViewerOverride =
        viewer === null || viewer.kind === 'gm' ? null : viewer;
      const next = { ...model, designatedViewerOverride };
      return [next, tableBroadcastEffects(next)];
    }
    case 'table/broadcast-received': {
      // #258 — THIS tab is the Table View tab (pure renderer). Adopt the
      // already-projected broadcast frame. No replica, no re-projection — the
      // GM tab ran the chokepoint before sending.
      return [{ ...model, tableBroadcast: msg.broadcast }, []];
    }
    case 'table/attention-toggle': {
      // #271 — enable / disable attention mode. When disabling, clear any active
      // pointer and ripple so the Table tab hides the overlays immediately.
      if (model.session.mode !== 'gm') return [model, []];
      const tableAttentionMode = !model.tableAttentionMode;
      if (!tableAttentionMode) {
        // Turning off — clear pointer and ripple.
        const next = { ...model, tableAttentionMode, tablePointer: null, tableRipple: null };
        return [next, attentionBroadcastEffects(next)];
      }
      return [{ ...model, tableAttentionMode }, []];
    }
    case 'table/attention-exit': {
      // #003 (llui-hud) — idempotent exit (Escape). Unlike the toggle this only
      // ever turns attention mode OFF, so dispatching it from the global Escape
      // handler is a no-op when the mode is already off (it never toggles on).
      if (!model.tableAttentionMode) return [model, []];
      const next = { ...model, tableAttentionMode: false, tablePointer: null, tableRipple: null };
      return [next, attentionBroadcastEffects(next)];
    }
    case 'table/attention-pointer-move': {
      // #271 — GM tab: track the pointer on the Table surface (normalised [0,1]).
      // Attention mode must be active. Only the GM tab emits; re-broadcast.
      if (model.session.mode !== 'gm' || !model.tableAttentionMode) return [model, []];
      const tablePointer: TableAttentionPointer = { x: msg.x, y: msg.y };
      const next = { ...model, tablePointer };
      return [next, attentionBroadcastEffects(next)];
    }
    case 'table/attention-pointer-clear': {
      // #271 — GM tab: cursor left the Table surface; clear the pointer overlay.
      if (model.session.mode !== 'gm') return [model, []];
      if (model.tablePointer === null) return [model, []];
      const next = { ...model, tablePointer: null };
      return [next, attentionBroadcastEffects(next)];
    }
    case 'table/attention-ripple': {
      // #271 — GM tab: trigger a "Look at this" halo at normalised (x, y).
      // The seq increments each time so the Table tab detects re-triggers at
      // the same position. A `schedule-attention-ripple-clear` effect clears
      // the model after the TTL (the CSS animation fades on the Table tab
      // independently). Attention mode must be active.
      if (model.session.mode !== 'gm' || !model.tableAttentionMode) return [model, []];
      const seq = model.tableRippleSeq + 1;
      const tableRipple: TableAttentionRipple = { x: msg.x, y: msg.y, seq };
      const next = { ...model, tableRipple, tableRippleSeq: seq };
      return [
        next,
        [
          ...attentionBroadcastEffects(next),
          { type: 'schedule-attention-ripple-clear', seq, delayMs: TABLE_RIPPLE_TTL_MS },
        ],
      ];
    }
    case 'table/attention-ripple-clear': {
      // #271 — auto-clear the ripple after the TTL. Guard on seq: if a newer
      // ripple (higher seq) has been triggered, this stale clear is a no-op.
      if (model.tableRipple === null) return [model, []];
      if (model.tableRipple.seq !== msg.seq) return [model, []];
      const next = { ...model, tableRipple: null };
      return [next, attentionBroadcastEffects(next)];
    }
    case 'browse-set': {
      // As-of scrubber (#192). The browse cursor is EPHEMERAL — no effect,
      // no persistence, no sync. Browsing to the session "now" (or clearing)
      // collapses to null so the divergence checks read "not diverged".
      const day = msg.day;
      const browse = day === null || day === model.campaignCursor ? null : day;
      const next = { ...model, browseCursor: browse };
      // 226.1 (#245) — the browse cursor overrides `t`, so re-project the
      // preview at the scrubbed in-world time. #258 — the table follows the
      // GM's as-of cursor too (the shared screen scrubs with the GM).
      return [next, [...previewRefreshEffects(next), ...tableBroadcastEffects(next)]];
    }
    case 'browse-clear': {
      const next = { ...model, browseCursor: null };
      return [next, [...previewRefreshEffects(next), ...tableBroadcastEffects(next)]];
    }
    case 'cursor/hydrate': {
      // Server-supplied cursor (the demoted D1 boot cache). ADR-0006: the
      // canonical day axis is shared, so the cursor's `day` is meaningful
      // independent of which calendar it was stamped against — adopt it as-is
      // (the active calendar just renders it). A null cursor leaves the bound
      // unset. Cache to localStorage but DON'T re-PUT (toServer:false).
      const next = msg.cursor;
      return [
        { ...model, campaignCursor: next },
        [{ type: 'persist-cursor', calendarId: model.activeCalendarId, cursor: next, toServer: false }],
      ];
    }
    case 'cursor/reconnect-sync': {
      // On (re)connect, reconcile the Campaign date. ADR-0005: the cursor is
      // now an op-log `config.set session_cursor` op, so a GM's offline change
      // is already queued in the replica op-queue and flushes automatically on
      // reconnect (with retry) — no manual re-push needed (and re-pushing would
      // mint a redundant LWW config op every reconnect, churn ADR-0005 avoids).
      // A player re-pulls the demoted D1 cache as a cheap fallback; the
      // authoritative value also arrives via the op-log backfill + fold.
      if (model.session.mode === 'gm') {
        return [model, []];
      }
      return [model, [{ type: 'fetch-session-cursor' }]];
    }
    case 'set-active-calendar':
      // Switch the campaign's canonical calendar — dispatched by the
      // calendar-type switcher inside the date picker panel. The choice is a
      // *setting* (#180): the canonical write is a `config.set canonical_calendar`
      // op, which the fold re-derives `activeCalendarId` from. We also update
      // `activeCalendarId` optimistically for instant re-render.
      //
      // ADR-0006 (governing truth): the canonical day axis is SHARED and
      // calendar-independent — every calendar is a bijection onto the same
      // integer line. So the campaign cursor (a `CanonicalDay`) is PRESERVED
      // across the switch; only its display re-derives in the new calendar.
      // We re-stamp the `session_cursor` setting's provenance `calendar_id` to
      // the newly-active calendar (the `day` is unchanged) so the stored op
      // stays coherent. No reset — switching calendars never loses "now".
      return [
        { ...model, activeCalendarId: msg.calendarId },
        [
          { type: 'submit-config', payload: configSet('canonical_calendar', msg.calendarId) },
          ...(model.campaignCursor !== null
            ? [
                {
                  type: 'persist-cursor' as const,
                  calendarId: msg.calendarId,
                  cursor: model.campaignCursor,
                  toServer: true,
                },
              ]
            : []),
        ],
      ];
    case 'date-picker-open':
      return [
        { ...model, datePicker: { id: msg.id, cursor: msg.cursor, overlay: 'none' } },
        [],
      ];
    case 'date-picker-navigate':
      // Ignore navigation for a picker that is not open (stale event after a
      // close); otherwise update only the view cursor, leaving the overlay.
      return model.datePicker === null
        ? [model, []]
        : [{ ...model, datePicker: { ...model.datePicker, cursor: msg.cursor } }, []];
    case 'date-picker-overlay':
      return model.datePicker === null
        ? [model, []]
        : [{ ...model, datePicker: { ...model.datePicker, overlay: msg.overlay } }, []];
    case 'date-picker-close':
      return [{ ...model, datePicker: null }, []];
    case 'set-fact-validity': {
      // A validity update is a fact.edit op carrying the current value
      // plus the new validity. We reuse the fact-edit pathway so the op
      // log invariant (every mutation is an append) holds.
      //
      // CRITICAL: when clearing to timeless (`msg.validity === null`) we
      // send `validity: null` EXPLICITLY, not an omitted field. The fold
      // only updates validity when the payload carries the field
      // (`if (payload.validity !== undefined)`); an omitted field means
      // "preserve" (so value-only edits don't wipe a range). Omitting it
      // here used to make clearing silently no-op — the user clicked
      // "Set to open-ended" and the date stayed fixed. Explicit null is
      // the wire signal for "clear" (see FactMetaShape — validity is
      // tri-state: absent=preserve, range=set, null=clear).
      // The fact's CURRENT VALUE is resolved in the effect, from the replica —
      // deliberately not read from `model.entities` here. That map is rebuilt by
      // an async refold, so right after a fact is created it does not contain
      // that fact yet, and this arm used to `return [model, []]` on the
      // resulting `undefined`: the validity write vanished with no op, no error
      // and no UI cue, while the picker had already closed. It reproduced 4/4
      // when a day was picked immediately after the save and 0/4 once 200 ms had
      // passed. Reading a merely-stale entry was no better — it re-sent that
      // stale value alongside the new validity and reverted a just-typed edit.
      return [
        model,
        [
          {
            type: 'submit-fact',
            subject: msg.entityId,
            predicate: msg.predicate,
            payload: { kind: 'fact.set-validity', validity: msg.validity },
          },
        ],
      ];
    }
    case 'succeed-fact': {
      // ADR-0005 §3 — "this changed as of day X". Close the predecessor
      // segment (its inclusive `to = atDay − 1`) and open a NEW `seg_…`
      // successor (`from = atDay`). Sugar over two ordinary fact ops on two
      // chains — submitted independently (no cross-chain transaction); the
      // destructive predecessor-close is the action the GM confirmed in the UI.
      //
      // As-of authoring gate (ADR-0005 §3, #192): a succession OPENS a new
      // successor segment — "new at now" authoring — so it is locked while the
      // browse cursor diverges. (The change day is the session cursor, but the
      // GM is browsing elsewhere; require returning to now to avoid the
      // timestamp ambiguity §3 guards against.)
      if (model.browseCursor !== null) return [model, []];
      const entity = model.entities[msg.entityId];
      if (!entity) return [model, []];
      // The predecessor is the segment covering "now" — the latest-starting
      // live segment for this base (the timeline strip's last bar).
      const strip = buildSegmentStrip(entity, msg.predicate);
      const predecessor = strip.bars[strip.bars.length - 1];
      if (!predecessor) return [model, []];
      const predFact = entity.facts[predecessor.key];
      const predValue = currentFactValue(predFact);
      if (predValue === undefined) return [model, []];
      const ops = factSucceed({
        predicate: msg.predicate,
        predecessorKey: predecessor.key,
        predecessorValue: predValue,
        predecessorValidity: predFact?.validity ?? null,
        atDay: msg.atDay,
        value: msg.value,
      });
      // The successor lands on its own `<predicate>#seg_…` op-log key. Map the
      // builder payloads onto the SubmitFactEffect shape (value + validity);
      // `factSucceed` always stamps an explicit validity on both halves.
      const closeValidity: FactValidity | null = ops.close.payload.validity ?? null;
      const openValidity: FactValidity | null = ops.open.payload.validity ?? null;
      return [
        model,
        [
          {
            type: 'submit-fact',
            subject: msg.entityId,
            predicate: predecessor.key,
            payload: {
              kind: 'fact.edit',
              value: ops.close.payload.value,
              validity: closeValidity,
            },
          },
          {
            type: 'submit-fact',
            subject: msg.entityId,
            predicate: ops.open.key,
            payload: {
              kind: 'fact.create',
              value: ops.open.payload.value,
              validity: openValidity,
            },
          },
        ],
      ];
    }
    case 'auth/sign-out':
      return [model, [{ type: 'auth/sign-out' }]];
    case 'open-entity-sheet-reveal':
      return [
        {
          ...model,
          entitySheet: { kind: 'reveal', entityId: msg.entityId, predicate: msg.predicate },
        },
        [],
      ];
    case 'open-entity-sheet-visited':
      if (typeof window !== 'undefined') {
        (window as unknown as {
          __DL_SHEET_VISITED__?: { subjectId: string; targetId: string };
        }).__DL_SHEET_VISITED__ = { subjectId: msg.subjectId, targetId: msg.targetId };
      }
      return [
        {
          ...model,
          entitySheet: { kind: 'visited', subjectId: msg.subjectId, targetId: msg.targetId },
        },
        [],
      ];
    case 'close-entity-sheet':
      return [{ ...model, entitySheet: { kind: 'closed' } }, []];
    case 'open-reveal-menu':
      return [
        { ...model, revealMenu: { entityId: msg.entityId, predicate: msg.predicate } },
        [],
      ];
    case 'close-reveal-menu':
      return [{ ...model, revealMenu: null }, []];
    case 'reveal-fact':
      // Append the write-once reveal op. Keep the popover open so the GM can
      // reveal to several Viewers in one pass; the optimistic local fold
      // re-renders the popover's "already revealed" list as each lands.
      return [
        model,
        [
          {
            type: 'submit-reveal',
            subject: msg.entityId,
            predicate: msg.predicate,
            viewer: msg.viewer,
          },
        ],
      ];
    case 'open-facet-reveal-menu':
      return [
        {
          ...model,
          facetRevealMenu: {
            entityId: msg.entityId,
            facetId: msg.facetId,
            label: msg.label,
            predicates: msg.predicates,
          },
        },
        [],
      ];
    case 'close-facet-reveal-menu':
      return [{ ...model, facetRevealMenu: null }, []];
    case 'reveal-facet': {
      // Routine facet reveal (30 §7.10.2) — one reveal op per facet fact that
      // the Viewer can't already see, each tagged `source.kind='facet'`.
      // Filtering to still-hidden facts keeps reveals write-once (no
      // stale-conflict from re-revealing an existing chain) and idempotent.
      const entity = model.entities[msg.entityId];
      const viewerK = viewerKeyFn(msg.viewer);
      const toReveal = msg.predicates.filter((predicate) => {
        const fact = entity?.facts[predicate];
        if (!fact) return false;
        return !fact.reveals.some((v) => viewerKeyFn(v) === viewerK);
      });
      const fx: Effect[] = toReveal.map((predicate) => ({
        type: 'submit-reveal',
        subject: msg.entityId,
        predicate,
        viewer: msg.viewer,
        source: { kind: 'facet', ref: msg.entityId },
      }));
      return [model, fx];
    }
    case 'open-reveal-composer':
      return [
        {
          ...model,
          revealComposer: {
            entityId: msg.entityId,
            facetId: msg.facetId,
            label: msg.label,
            predicates: msg.predicates,
            viewer: null,
            excluded: [],
          },
        },
        [],
      ];
    case 'close-reveal-composer':
      return [{ ...model, revealComposer: null }, []];
    case 'composer-set-viewer': {
      if (!model.revealComposer) return [model, []];
      return [{ ...model, revealComposer: { ...model.revealComposer, viewer: msg.viewer } }, []];
    }
    case 'composer-toggle-fact': {
      const c = model.revealComposer;
      if (!c) return [model, []];
      const excluded = c.excluded.includes(msg.predicate)
        ? c.excluded.filter((p) => p !== msg.predicate)
        : [...c.excluded, msg.predicate];
      return [{ ...model, revealComposer: { ...c, excluded } }, []];
    }
    case 'composer-confirm': {
      const c = model.revealComposer;
      if (!c || !c.viewer) return [model, []];
      const viewer = c.viewer;
      const viewerK = viewerKeyFn(viewer);
      const entity = model.entities[c.entityId];
      // Reveal the INCLUDED facts the recipient can't already see — gated,
      // per-fact, source 'facet' (the Composer is still a facet reveal).
      const toReveal = c.predicates.filter((predicate) => {
        if (c.excluded.includes(predicate)) return false;
        const fact = entity?.facts[predicate];
        if (!fact || currentFactValue(fact) === undefined) return false;
        return !fact.reveals.some((v) => viewerKeyFn(v) === viewerK);
      });
      const fx: Effect[] = toReveal.map((predicate) => ({
        type: 'submit-reveal',
        subject: c.entityId,
        predicate,
        viewer,
        source: { kind: 'facet', ref: c.entityId },
      }));
      return [{ ...model, revealComposer: null }, fx];
    }
    case 'open-belief-modal': {
      // §7.3 — a player authors beliefs owned BY their PC, so pre-select it (the
      // believer list is constrained to controlled PCs for players — see
      // `beliefBelieverRows`). The GM may attribute a belief to any Viewer, so
      // their selection starts empty.
      const pc = model.session.controlledCharacters;
      const believer: ViewerId | null =
        model.session.mode === 'player' && pc.length > 0
          ? { kind: 'character', id: pc[0]! }
          : null;
      return [
        {
          ...model,
          beliefModal: { entityId: msg.entityId, believer, predicate: '', value: '' },
        },
        [],
      ];
    }
    case 'close-belief-modal':
      return [{ ...model, beliefModal: null }, []];
    case 'belief-set-believer':
      if (!model.beliefModal) return [model, []];
      return [{ ...model, beliefModal: { ...model.beliefModal, believer: msg.viewer } }, []];
    case 'belief-set-predicate':
      if (!model.beliefModal) return [model, []];
      return [{ ...model, beliefModal: { ...model.beliefModal, predicate: msg.predicate } }, []];
    case 'belief-set-value':
      if (!model.beliefModal) return [model, []];
      return [{ ...model, beliefModal: { ...model.beliefModal, value: msg.value } }, []];
    case 'submit-belief': {
      const b = model.beliefModal;
      if (!b || !b.believer || b.predicate === '' || b.value.trim() === '') return [model, []];
      // Author the belief as an owner-tagged SEGMENT (ADR-0005 §8): an
      // ordinary `<predicate>#seg_…` chain whose owner is the believing Viewer,
      // so it overlays canon only in that Viewer's perspective (03 §9 / B1).
      return [
        { ...model, beliefModal: null },
        [
          {
            type: 'submit-fact',
            subject: b.entityId,
            predicate: beliefSegmentKey(b.predicate, b.believer),
            payload: { kind: 'fact.create', value: b.value, owner: beliefOwner(b.believer) },
          },
        ],
      ];
    }
    case 'open-belief-clone':
      return [
        {
          ...model,
          beliefCloneMenu: {
            entityId: msg.entityId,
            sourceKey: msg.sourceKey,
            base: msg.base,
            value: msg.value,
            sourceBelieverKey: msg.sourceBelieverKey,
          },
        },
        [],
      ];
    case 'close-belief-clone':
      return [{ ...model, beliefCloneMenu: null }, []];
    case 'mark-belief-corrected':
      // The believer learned the truth at the current in-world cursor (0 when
      // unset → "corrected as of now"). ADR-0005 §8 collapses correction into a
      // CLOSED belief-segment `to`: a value-preserving edit that stamps the
      // belief segment's exclusive upper bound. The belief stays as history but
      // stops overriding canon from `to` onward (the projection's owner overlay
      // resolves it away once the segment no longer covers the day).
      {
        // Preserve the belief segment's existing `from` (its start) and only
        // stamp the exclusive upper bound — a value+from-preserving close.
        const existingFrom =
          model.entities[msg.entityId]?.facts[msg.beliefKey]?.validity?.from;
        const to = model.campaignCursor ?? 0;
        const validity =
          existingFrom !== undefined ? { from: existingFrom, to } : { to };
        return [
          model,
          [
            {
              type: 'submit-fact',
              subject: msg.entityId,
              predicate: msg.beliefKey,
              payload: { kind: 'fact.edit', value: msg.value, validity },
            },
          ],
        ];
      }
    case 'clone-belief': {
      const c = model.beliefCloneMenu;
      if (!c) return [model, []];
      // Clone the source belief to the target Viewer, same value, with a
      // `source_belief` pointer back to the origin (03 §9 provenance chain).
      return [
        { ...model, beliefCloneMenu: null },
        [
          {
            type: 'submit-fact',
            subject: c.entityId,
            predicate: beliefSegmentKey(c.base, msg.viewer),
            payload: {
              kind: 'fact.create',
              value: c.value,
              owner: beliefOwner(msg.viewer),
              source_belief: c.sourceKey,
            },
          },
        ],
      ];
    }
    case 'mark-visited': {
      // IV.B.2 (#78) — append a `visited` fact on the SUBJECT (the
      // visiting entity, typically a Character) with the OBJECT being
      // the target entity id. The predicate MUST be the opaque official-core
      // id (ADR-0004 §1); the raw `'visited'` token is not a declared
      // predicate, so a fact written under it never resolves in the registry
      // and the visit is invisible canon. The op log captures the action; the
      // fact rolls up in the chain-fold and surfaces on the subject's page.
      return [
        { ...model, entitySheet: { kind: 'closed' } },
        [
          {
            type: 'submit-fact',
            subject: msg.subjectId,
            predicate: OFFICIAL_CORE_PREDICATE_IDS.visited,
            payload: { kind: 'fact.create', value: msg.targetId },
          },
        ],
      ];
    }
    case 'open-notes-sheet':
      if (typeof window !== 'undefined') {
        (window as unknown as { __DL_NOTES_SHEET_ID__?: string }).__DL_NOTES_SHEET_ID__ =
          msg.entityId;
      }
      return [
        { ...model, notesSheetOpen: true, notesSheetEntityId: msg.entityId },
        // Lazy-load if we haven't fetched yet.
        msg.entityId in model.privateNotes
          ? []
          : [{ type: 'load-private-note', entityId: msg.entityId }],
      ];
    case 'close-notes-sheet':
      return [{ ...model, notesSheetOpen: false }, []];
    case 'cursor-advance': {
      if (!model.calendarConfirmed) return [model, []];
      const next = (model.campaignCursor ?? 0) + msg.days;
      return [
        { ...model, campaignCursor: next },
        [
          {
            type: 'persist-cursor',
            calendarId: model.activeCalendarId,
            cursor: next,
            toServer: true,
          },
        ],
      ];
    }
    case 'cursor-rewind-confirm':
      return [{ ...model, hudRewindPending: msg.days }, []];
    case 'cursor-rewind-cancel':
      return [{ ...model, hudRewindPending: null }, []];
    case 'cursor-rewind': {
      if (!model.calendarConfirmed) return [{ ...model, hudRewindPending: null }, []];
      const next = (model.campaignCursor ?? 0) - msg.days;
      return [
        { ...model, campaignCursor: next, hudRewindPending: null },
        [
          {
            type: 'persist-cursor',
            calendarId: model.activeCalendarId,
            cursor: next,
            toServer: true,
          },
        ],
      ];
    }
    case 'set-entity-page-tab':
      return [{ ...model, entityPageTab: msg.value }, []];

    // ---- #257 — Dice roller ----
    case 'dice-input':
      return [{ ...model, diceInput: msg.value, diceError: null }, []];

    case 'dice-roll': {
      const notation = model.diceInput.trim();
      if (!notation) return [model, []];
      try {
        const result = roll(notation);
        const entry = toDiceRollEntry(result, mintDiceEntryId());
        const diceHistory = [entry, ...model.diceHistory].slice(0, DICE_HISTORY_CAP);
        return [{ ...model, diceHistory, diceError: null, diceInput: '' }, []];
      } catch (e) {
        const diceError = e instanceof DiceParseError ? e.message : t('dice.error.unknown');
        return [{ ...model, diceError }, []];
      }
    }

    case 'dice-clear-history':
      return [{ ...model, diceHistory: [], diceError: null }, []];

    // ---- #257 — Initiative tracker ----
    case 'initiative-open-add-form':
      return [{ ...model, initiativeAddForm: { name: '', initiative: '', hp: '' } }, []];

    case 'initiative-add-form-name': {
      const f = model.initiativeAddForm;
      if (!f) return [model, []];
      return [{ ...model, initiativeAddForm: { ...f, name: msg.value } }, []];
    }

    case 'initiative-add-form-initiative': {
      const f = model.initiativeAddForm;
      if (!f) return [model, []];
      return [{ ...model, initiativeAddForm: { ...f, initiative: msg.value } }, []];
    }

    case 'initiative-add-form-hp': {
      const f = model.initiativeAddForm;
      if (!f) return [model, []];
      return [{ ...model, initiativeAddForm: { ...f, hp: msg.value } }, []];
    }

    case 'initiative-add-form-submit': {
      const f = model.initiativeAddForm;
      if (!f) return [model, []];
      const trimmedName = f.name.trim();
      if (!trimmedName) return [model, []];
      const initiative = parseInt(f.initiative, 10);
      if (isNaN(initiative)) return [model, []];
      const hpRaw = parseInt(f.hp, 10);
      const hasHp = !isNaN(hpRaw) && hpRaw > 0;
      const entry: InitiativeEntry = {
        id: mintEntryId(),
        display_name: trimmedName,
        initiative,
        conditions: [],
        ...(hasHp ? { hp_scratch: { current: hpRaw, max: hpRaw } } : {}),
      };
      return [
        { ...model, initiative: addEntry(model.initiative, entry), initiativeAddForm: null },
        [],
      ];
    }

    case 'initiative-add-form-cancel':
      return [{ ...model, initiativeAddForm: null }, []];

    case 'initiative-remove':
      return [{ ...model, initiative: removeEntry(model.initiative, msg.id) }, []];

    case 'initiative-advance-turn':
      return [{ ...model, initiative: advanceTurn(model.initiative) }, []];

    case 'initiative-toggle-condition':
      return [
        { ...model, initiative: toggleCondition(model.initiative, msg.id, msg.condition) },
        [],
      ];

    case 'initiative-hp-delta':
      return [
        { ...model, initiative: applyHpDelta(model.initiative, msg.id, msg.delta) },
        [],
      ];

    case 'initiative-reset':
      return [
        {
          ...model,
          initiative: EMPTY_INITIATIVE,
          initiativeAddForm: null,
          // #260 — clear the narrative panel state with the initiative.
          narrativeCombatTurnLog: clearTurnLog(),
          narrativeCombatEffectInput: null,
          narrativeCombatSaveInput: null,
          narrativeCombatDamageInput: null,
        },
        [],
      ];

    // ---- #259 — End-encounter / post-combat log (07 §13.9.2) ----
    case 'combat-log-menu-open':
      return [{ ...model, combatLogMenu: 'open' }, []];

    case 'combat-log-menu-close':
      return [{ ...model, combatLogMenu: null }, []];

    case 'combat-log-quick-log': {
      if (model.initiative.entries.length === 0) return [model, []];
      const bundle = buildCombatLogBundle(model.initiative);
      const effects = buildCombatLogEffects(bundle, model.initiative, null, {});
      return [
        {
          ...model,
          combatLogMenu: null,
          initiative: EMPTY_INITIATIVE,
          initiativeAddForm: null,
          narrativeCombatTurnLog: clearTurnLog(),
          narrativeCombatEffectInput: null,
          narrativeCombatSaveInput: null,
          narrativeCombatDamageInput: null,
        },
        effects,
      ];
    }

    case 'combat-log-open-full-form': {
      if (model.initiative.entries.length === 0) return [model, []];
      const fateOverrides: Record<string, ParticipantFate | 'fled'> = {};
      for (const entry of model.initiative.entries) {
        fateOverrides[entry.id] = participantsFate(entry);
      }
      const form: CombatLogFormState = {
        snapshot: model.initiative,
        locationRef: '',
        fateOverrides,
        lootNotes: '',
        worldStateNotes: '',
      };
      return [{ ...model, combatLogMenu: null, combatLogForm: form }, []];
    }

    case 'combat-log-form-fate': {
      if (!model.combatLogForm) return [model, []];
      return [
        {
          ...model,
          combatLogForm: {
            ...model.combatLogForm,
            fateOverrides: { ...model.combatLogForm.fateOverrides, [msg.entryId]: msg.fate },
          },
        },
        [],
      ];
    }

    case 'combat-log-form-loot': {
      if (!model.combatLogForm) return [model, []];
      return [{ ...model, combatLogForm: { ...model.combatLogForm, lootNotes: msg.value } }, []];
    }

    case 'combat-log-form-world-state': {
      if (!model.combatLogForm) return [model, []];
      return [
        { ...model, combatLogForm: { ...model.combatLogForm, worldStateNotes: msg.value } },
        [],
      ];
    }

    case 'combat-log-form-location': {
      if (!model.combatLogForm) return [model, []];
      return [
        { ...model, combatLogForm: { ...model.combatLogForm, locationRef: msg.value } },
        [],
      ];
    }

    case 'combat-log-form-submit': {
      if (!model.combatLogForm) return [model, []];
      const form = model.combatLogForm;
      const bundle = buildCombatLogBundle(form.snapshot, form.locationRef || undefined);
      const effects = buildCombatLogEffects(
        bundle,
        form.snapshot,
        form.locationRef || null,
        form.fateOverrides,
      );
      return [
        {
          ...model,
          combatLogMenu: null,
          combatLogForm: null,
          initiative: EMPTY_INITIATIVE,
          initiativeAddForm: null,
          narrativeCombatTurnLog: clearTurnLog(),
          narrativeCombatEffectInput: null,
          narrativeCombatSaveInput: null,
          narrativeCombatDamageInput: null,
        },
        effects,
      ];
    }

    case 'combat-log-form-cancel':
      return [{ ...model, combatLogForm: null }, []];

    // ---- #260 — Narrative combat panel (07 §13.9.5) ----
    // All panel actions are ephemeral except 'narrative-panel-draft-narration'
    // which emits a submit-fact effect (GM-authored draft, status: 'draft').

    case 'narrative-panel-reaction-roll': {
      // Affordance 1 — 2d6 reaction roll for a non-PC entry.
      const entry = model.initiative.entries.find((e) => e.id === msg.entryId);
      if (!entry) return [model, []];
      let rollResult: DiceRoll;
      try {
        rollResult = roll('2d6');
      } catch {
        return [model, []];
      }
      const total = rollResult.total;
      const disposition = reactionDisposition(total);
      const description = buildReactionRollDescription(entry.display_name, total, disposition);
      const tlogEntry = buildTurnLogEntry({
        id: mintTurnLogEntryId(),
        round: model.initiative.round,
        actorEntryId: entry.id,
        actorName: entry.display_name,
        kind: 'reaction_roll',
        description,
      });
      const diceEntry = toDiceRollEntry(rollResult, mintDiceEntryId());
      const diceHistory = [diceEntry, ...model.diceHistory].slice(0, DICE_HISTORY_CAP);
      return [
        {
          ...model,
          narrativeCombatTurnLog: appendTurnLogEntry(model.narrativeCombatTurnLog, tlogEntry),
          diceHistory,
        },
        [],
      ];
    }

    case 'narrative-panel-damage-open':
      return [
        { ...model, narrativeCombatDamageInput: { entryId: msg.entryId, amount: '' } },
        [],
      ];

    case 'narrative-panel-damage-change': {
      if (!model.narrativeCombatDamageInput) return [model, []];
      return [
        {
          ...model,
          narrativeCombatDamageInput: { ...model.narrativeCombatDamageInput, amount: msg.amount },
        },
        [],
      ];
    }

    case 'narrative-panel-damage-apply': {
      // Affordance 2 — assign damage to the current entry.
      const inp = model.narrativeCombatDamageInput;
      if (!inp) return [model, []];
      const amount = parseInt(inp.amount, 10);
      if (isNaN(amount) || amount <= 0) return [model, []];
      const entry = model.initiative.entries.find((e) => e.id === inp.entryId);
      if (!entry) return [model, []];
      const newInitiative = applyHpDelta(model.initiative, inp.entryId, -amount);
      const updatedEntry = newInitiative.entries.find((e) => e.id === inp.entryId);
      const currentHp = updatedEntry?.hp_scratch?.current ?? -(amount);
      const maxHp = updatedEntry?.hp_scratch?.max;
      const description = buildDamageDescription(entry.display_name, amount, currentHp, maxHp);
      const tlogEntry = buildTurnLogEntry({
        id: mintTurnLogEntryId(),
        round: model.initiative.round,
        actorEntryId: entry.id,
        actorName: entry.display_name,
        kind: 'damage',
        description,
      });
      return [
        {
          ...model,
          initiative: newInitiative,
          narrativeCombatTurnLog: appendTurnLogEntry(model.narrativeCombatTurnLog, tlogEntry),
          narrativeCombatDamageInput: null,
        },
        [],
      ];
    }

    case 'narrative-panel-damage-cancel':
      return [{ ...model, narrativeCombatDamageInput: null }, []];

    case 'narrative-panel-condition-toggle': {
      // Affordance 3 — toggle a condition on an entry.
      const entry = model.initiative.entries.find((e) => e.id === msg.entryId);
      if (!entry) return [model, []];
      const norm = msg.condition.trim().toLowerCase();
      const had = entry.conditions.some((c) => c.trim().toLowerCase() === norm);
      const newInitiative = toggleCondition(model.initiative, msg.entryId, msg.condition);
      const description = buildConditionDescription(entry.display_name, msg.condition, !had);
      const tlogEntry = buildTurnLogEntry({
        id: mintTurnLogEntryId(),
        round: model.initiative.round,
        actorEntryId: entry.id,
        actorName: entry.display_name,
        kind: 'condition',
        description,
      });
      return [
        {
          ...model,
          initiative: newInitiative,
          narrativeCombatTurnLog: appendTurnLogEntry(model.narrativeCombatTurnLog, tlogEntry),
        },
        [],
      ];
    }

    case 'narrative-panel-npc-action': {
      // Affordance 4 — NPC turn quick-pick.
      const entry = model.initiative.entries.find((e) => e.id === msg.entryId);
      if (!entry) return [model, []];
      const description = buildNpcActionDescription(entry.display_name, msg.action);
      const tlogEntry = buildTurnLogEntry({
        id: mintTurnLogEntryId(),
        round: model.initiative.round,
        actorEntryId: entry.id,
        actorName: entry.display_name,
        kind: 'effect',
        description,
      });
      return [
        {
          ...model,
          narrativeCombatTurnLog: appendTurnLogEntry(model.narrativeCombatTurnLog, tlogEntry),
        },
        [],
      ];
    }

    case 'narrative-panel-save-open':
      return [
        { ...model, narrativeCombatSaveInput: { entryId: msg.entryId, spec: '' } },
        [],
      ];

    case 'narrative-panel-save-change': {
      if (!model.narrativeCombatSaveInput) return [model, []];
      return [
        {
          ...model,
          narrativeCombatSaveInput: { ...model.narrativeCombatSaveInput, spec: msg.spec },
        },
        [],
      ];
    }

    case 'narrative-panel-save-roll': {
      // Affordance 5 — roll a save/skill check.
      const inp = model.narrativeCombatSaveInput;
      if (!inp) return [model, []];
      const notation = inp.spec.trim();
      if (!notation) return [model, []];
      const entry = model.initiative.entries.find((e) => e.id === inp.entryId);
      if (!entry) return [model, []];
      let saveResult: import('./session/dice.js').DiceRoll;
      try {
        saveResult = roll(notation);
      } catch {
        return [model, []];
      }
      const description = buildSaveDescription(entry.display_name, notation, saveResult.total);
      const tlogEntry = buildTurnLogEntry({
        id: mintTurnLogEntryId(),
        round: model.initiative.round,
        actorEntryId: entry.id,
        actorName: entry.display_name,
        kind: 'save',
        description,
      });
      const diceEntry = toDiceRollEntry(saveResult, mintDiceEntryId());
      const diceHistory = [diceEntry, ...model.diceHistory].slice(0, DICE_HISTORY_CAP);
      return [
        {
          ...model,
          narrativeCombatTurnLog: appendTurnLogEntry(model.narrativeCombatTurnLog, tlogEntry),
          narrativeCombatSaveInput: null,
          diceHistory,
        },
        [],
      ];
    }

    case 'narrative-panel-save-cancel':
      return [{ ...model, narrativeCombatSaveInput: null }, []];

    case 'narrative-panel-effect-open':
      return [{ ...model, narrativeCombatEffectInput: '' }, []];

    case 'narrative-panel-effect-change': {
      if (model.narrativeCombatEffectInput === null) return [model, []];
      return [{ ...model, narrativeCombatEffectInput: msg.value }, []];
    }

    case 'narrative-panel-effect-submit': {
      // Affordance 6 — end-turn-with-effect tag.
      if (model.narrativeCombatEffectInput === null) return [model, []];
      const effectText = model.narrativeCombatEffectInput.trim();
      if (!effectText) return [model, []];
      const entry = model.initiative.entries.find((e) => e.id === msg.entryId);
      if (!entry) return [model, []];
      const description = buildEffectDescription(entry.display_name, effectText);
      const tlogEntry = buildTurnLogEntry({
        id: mintTurnLogEntryId(),
        round: model.initiative.round,
        actorEntryId: entry.id,
        actorName: entry.display_name,
        kind: 'effect',
        description,
      });
      return [
        {
          ...model,
          narrativeCombatTurnLog: appendTurnLogEntry(model.narrativeCombatTurnLog, tlogEntry),
          narrativeCombatEffectInput: null,
        },
        [],
      ];
    }

    case 'narrative-panel-effect-cancel':
      return [{ ...model, narrativeCombatEffectInput: null }, []];

    case 'narrative-panel-draft-narration': {
      // Draft narration — emit a GM-authored draft rich_text fact.
      // Status is always 'draft'; the GM ratifies via the draft inbox.
      // INVARIANT: LLM never writes canon/pending (check-llm-pending-guard.sh).
      const entityId = msg.entityId;
      if (!entityId) return [model, []];
      const narrationText = buildNarrationText(msg.entry);
      const effect: SubmitFactEffect = {
        type: 'submit-fact',
        subject: entityId,
        predicate: 'description',
        payload: { kind: 'fact.create', value: narrationText, status: 'draft' },
      };
      return [model, [effect]];
    }
    default:
      // Defensive: a message whose `type` matches no case must NEVER make the
      // reducer fall through to an `undefined` return — that nukes the whole
      // model and the root `branch` discriminant then crashes on `s.route`
      // (a real, shipped white-screen; see auth-view `routing.link` mappers
      // that once emitted `{ kind: 'navigate' }` instead of `{ type: … }`).
      // The `never` parameter keeps compile-time exhaustiveness intact for
      // well-typed `Msg`s; this guards only against runtime type-lies.
      return assertNeverMsg(msg, model);
  }
}

/**
 * Exhaustiveness guard for the `update` reducer. For a well-typed `Msg` this is
 * statically unreachable (so `msg: never` enforces that every variant has a
 * case). At runtime it safely returns the model unchanged for any message that
 * slipped past the type system, instead of letting `update` return `undefined`.
 */
function assertNeverMsg(msg: never, model: Model): [Model, Effect[]] {
  if (import.meta.env?.DEV) {
    console.warn('update(): ignored unrecognized message', msg);
  }
  return [model, []];
}

// ---------- view ----------

function view(state: Signal<Model>, send: Send<Msg>): Renderable {
  // The signal runtime tracks reactive dependencies automatically — no
  // manual path registration (the old `track<Model>({ deps })` call) is
  // needed. Each `state.map(...)` / `state.at(...)` slot subscribes to
  // exactly the fields it reads.
  //
  // Top-level shell split. Pre-login auth surfaces (sign-in / sign-up /
  // forgot / reset / verify / 2fa-disable) must NOT show the full
  // authenticated app chrome (entity-list links, campaign date, mobile
  // HUD, bottom nav) — a first-impression sign-in page wearing the GM
  // toolkit's chrome reads as broken. The `branch` keys on a coarse
  // "auth" | "app" discriminator so the runtime tears down one shell and
  // mounts the other when the route crosses the boundary (sign-in →
  // entity list after a successful login, etc.).
  // #258 — the Table View is a THIRD top-level shell (`table`): a chrome-
  // stripped, large-display, player-facing renderer with no GM toolkit. It is
  // mutually exclusive with the auth + app shells, so it joins the same coarse
  // `branch` discriminator.
  return [
    ...appRouteListener(send, navMsg),
    branch(
      state.map((s) =>
        isTableRoute(s.route)
          ? 'table'
          : isAuthRoute(s.route)
            ? 'auth'
            : isBooting(s)
              ? 'booting'
              : 'app',
      ),
      {
        table: () => [viewTableShell(state, send)],
        auth: () => [viewAuthShell(state, send)],
        booting: () => [viewBootShell()],
        app: () => viewAppShell(state, send),
      },
    ),
  ];
}

/**
 * Public info routes — they render without a session, so `bootSession()`
 * returns early for them and never resolves `bootStatus`. They must never
 * wait behind the boot gate or they would hang on the spinner forever.
 */
export function isPublicRoute(route: AppRoute): boolean {
  return route.kind === 'a11y' || route.kind === 'recovery' || route.kind === 'ui-gallery';
}

/**
 * The boot gate.
 *
 * The session probe is async, but the shell used to mount with the optimistic
 * `LEGACY_GM_SESSION` seed and paint the full GM toolkit immediately. A
 * signed-out visitor therefore saw the authenticated app for a frame before the
 * 401 bounced them to /sign-in — the shell flashing behind the login page.
 *
 * Gate the app shell on the probe instead: while `bootStatus` is still
 * `'pending'` we render a neutral loading shell, so the first paint is either
 * the loader, the auth shell, or the real app — never the wrong one.
 *
 * Two route families are exempt because they legitimately never resolve
 * `bootStatus`:
 *   - public info pages (`isPublicRoute`) — no session is probed at all;
 *   - the campaigns picker — `bootChooseCampaign()` settles into
 *     `campaigns-boot/*` and never dispatches `session/loaded`. The picker
 *     owns its own loading state (`campaigns.boot.loading`).
 */
export function isBooting(s: Model): boolean {
  if (s.bootStatus !== 'pending') return false;
  if (s.route.kind === 'campaigns') return false;
  return !isPublicRoute(s.route);
}

/**
 * The neutral boot shell. Deliberately chrome-free: no rail, no topbar, no
 * entity links — nothing that would leak the authenticated layout to a visitor
 * who turns out to be signed out. `role="status"` + `aria-live` announce the
 * wait; the spinner itself is decorative and respects reduced-motion via CSS.
 */
function viewBootShell(): Mountable {
  return div(
    {
      class: 'boot-shell',
      'data-testid': 'boot-shell',
      role: 'status',
      'aria-live': 'polite',
    },
    [
      span({ class: 'boot-spinner', 'aria-hidden': 'true' }, []),
      span({ class: 'boot-shell-label' }, [text(t('app.booting'))]),
    ],
  );
}

// ---------- #258 Table View (shared-display surface) ----------

/**
 * #258 — one display group for the Table View: an entity (or the synthetic
 * "subject") with its visible fact rows, ready to render. Derived purely from
 * the broadcast rows — the Table tab holds no entities map.
 */
interface TableGroup {
  subject: string;
  /** The entity's `name` row value when present, else the raw subject id. */
  title: string;
  rows: TableProjectedRow[];
}

/** Pull the broadcast rows from the model (Table tab), narrowed to focus. */
function tableRows(s: Model): TableProjectedRow[] {
  const b = s.tableBroadcast;
  if (b === null) return [];
  return rowsForFocus(b.focus, b.rows);
}

/**
 * #258 — group the broadcast rows by subject for display. The `name` fact (if
 * present in the revealed set) becomes the group title; otherwise the subject
 * id is shown (an entity may be referenced-but-unnamed for this Viewer). Pure;
 * deterministic (groups + rows sorted) so the shared screen is stable.
 */
function tableGroups(s: Model): TableGroup[] {
  const rows = tableRows(s);
  const bySubject = new Map<string, TableProjectedRow[]>();
  for (const row of rows) {
    const list = bySubject.get(row.subject);
    if (list) list.push(row);
    else bySubject.set(row.subject, [row]);
  }
  const groups: TableGroup[] = [];
  for (const [subject, subjectRows] of bySubject) {
    const nameRow = subjectRows.find((r) => r.predicate === FACT_NAME);
    const title =
      nameRow && typeof nameRow.value === 'string' && nameRow.value !== ''
        ? nameRow.value
        : subject;
    // Drop the name row from the body (it is the title) and sort the rest.
    const body = subjectRows
      .filter((r) => r.predicate !== FACT_NAME)
      .sort((a, b) => a.predicate.localeCompare(b.predicate));
    groups.push({ subject, title, rows: body });
  }
  groups.sort((a, b) => a.title.localeCompare(b.title));
  return groups;
}

/** True when no broadcast has landed yet (waiting for the GM tab). */
function tableWaiting(s: Model): boolean {
  return s.tableBroadcast === null;
}

/** True when a broadcast landed but the designated Viewer is unset (empty). */
function tableNoViewer(s: Model): boolean {
  return s.tableBroadcast !== null && s.tableBroadcast.designatedViewerNull;
}

/** The designated-Viewer label carried by the broadcast (for the header chip). */
function tableViewerLabel(s: Model): string {
  return s.tableBroadcast?.viewerLabel ?? '';
}

/** True when there is a broadcast, a Viewer, but no visible rows for the focus. */
function tableEmpty(s: Model): boolean {
  return !tableWaiting(s) && !tableNoViewer(s) && tableRows(s).length === 0;
}

/**
 * #258 — the chrome-stripped **Table View** shell. A large-display, player-
 * facing renderer of the current table focus through the designated Viewer's
 * projection. No GM toolkit chrome (no header rail, no cursor HUD, no bottom
 * nav). The body renders the broadcast rows ONLY — this tab never reads canon,
 * never opens a replica, never re-projects. a11y: large type via the
 * `.table-view` class scale, a `role="main"`, an `aria-live="polite"` status
 * for the focus change, and a non-color "Showing: <Viewer>" cue.
 *
 * #271 — the Table tab also renders ephemeral attention overlays (pointer +
 * ripple) on top of the content. The overlays are driven by the broadcast; the
 * Table tab constructs no `VisibleFact` and re-projects nothing. The overlays
 * are arm-agnostic (coordinates on the table surface) and compose with any
 * focus arm.
 */
function viewTableShell(state: Signal<Model>, send: Send<Msg>): Mountable {
  return main({ class: 'table-view', 'data-testid': 'table-view' }, [
    div({ class: 'table-view-header' }, [
      h1({ class: 'table-view-heading' }, [
        span({ class: 'table-view-brand', 'aria-hidden': 'true' }, [text('◆')]),
        span({ class: 'table-view-heading-text' }, [text(t('table_view.heading'))]),
      ]),
      span(
        {
          class: 'table-view-viewer-chip',
          'data-testid': 'table-view-viewer',
          role: 'status',
          'aria-live': 'polite',
        },
        [
          text(
            state.map((s) =>
              tableNoViewer(s)
                ? t('table_view.no_viewer')
                : tableWaiting(s)
                  ? t('table_view.waiting')
                  : t('table_view.showing', { viewer: tableViewerLabel(s) }),
            ),
          ),
        ],
      ),
    ]),
    // Waiting / empty / no-viewer placeholders, else the grouped rows.
    branch(
      state.map((s) =>
        tableWaiting(s)
          ? 'waiting'
          : tableNoViewer(s)
            ? 'no-viewer'
            : tableEmpty(s)
              ? 'empty'
              : 'content',
      ),
      {
        waiting: () => [
          div({ class: 'table-view-placeholder', 'data-testid': 'table-view-waiting' }, [
            text(t('table_view.waiting_body')),
          ]),
        ],
        'no-viewer': () => [
          div({ class: 'table-view-placeholder', 'data-testid': 'table-view-no-viewer' }, [
            text(t('table_view.no_viewer_body')),
          ]),
        ],
        empty: () => [
          div({ class: 'table-view-placeholder', 'data-testid': 'table-view-empty' }, [
            text(t('table_view.empty_body')),
          ]),
        ],
        content: () => [viewTableContent(state, send)],
      },
    ),
    // #271 — attention overlays: rendered on top of content, pointer-events:none
    // so they never block interaction. Both signals are read from the broadcast;
    // the Table tab constructs no VisibleFact and calls no projection function.
    viewTableAttentionOverlays(state),
  ]);
}

/**
 * #271 — the attention overlay layer for the Table View tab.
 *
 * **Pointer overlay:** a CSS cursor arrow rendered at the normalised (x,y)
 * position when `attentionPointer` is present in the broadcast. Pairs the visual
 * arrow with an `aria-live` announcement so screen-reader users are not left out
 * (non-color cue: the arrow also has a "◆ Pointing here" SR-only label).
 *
 * **Ripple overlay:** a CSS keyframe animation (expanding ring + halo) centred
 * at the normalised (x,y) position when `attentionRipple` is present. The
 * animation is keyed on `seq` so a re-trigger at the same coordinates restarts
 * the animation. Non-color: the ring also emits a `role="status"` announcement
 * ("GM is highlighting this area") and has a distinct shape (ring ≠ pointer).
 *
 * The overlay container fills the viewport with `position:fixed; inset:0;
 * pointer-events:none` so it never blocks interaction on the table or GM primary.
 */
function viewTableAttentionOverlays(state: Signal<Model>): Mountable {
  // The pointer overlay: rendered when the broadcast carries `attentionPointer`.
  const hasPointer = state.map(
    (s) => s.tableBroadcast?.attentionPointer !== undefined,
  );
  const pointerX = state.map(
    (s) => `${((s.tableBroadcast?.attentionPointer?.x ?? 0) * 100).toFixed(2)}%`,
  );
  const pointerY = state.map(
    (s) => `${((s.tableBroadcast?.attentionPointer?.y ?? 0) * 100).toFixed(2)}%`,
  );

  // The ripple overlay: a keyed `each` so a new seq restarts the CSS animation.
  const rippleItems = state.map((s) => {
    const r = s.tableBroadcast?.attentionRipple;
    return r ? [{ x: r.x, y: r.y, seq: r.seq }] : [];
  });

  return div(
    {
      class: 'table-attention-layer',
      'aria-hidden': 'true', // children carry their own aria-live announcements
      'data-testid': 'table-attention-layer',
    },
    [
      // Pointer: visible when attentionPointer is present.
      show(hasPointer, () => [
        div(
          {
            class: 'table-attention-pointer',
            'data-testid': 'table-attention-pointer',
            style: state.map(
              (s) => {
                const p = s.tableBroadcast?.attentionPointer;
                if (!p) return 'display:none';
                return `left:${(p.x * 100).toFixed(2)}%;top:${(p.y * 100).toFixed(2)}%`;
              },
            ),
            role: 'status',
            'aria-live': 'off', // position updates are not announced (too noisy)
            'aria-label': t('table_view.attention_pointer_label'),
          },
          [
            span({ class: 'table-attention-pointer-icon', 'aria-hidden': 'true' }, [text('◆')]),
          ],
        ),
      ]),
      // Ripple: keyed on seq so a re-trigger restarts the animation.
      each(rippleItems, {
        key: (r) => String(r.seq),
        render: (item) => [
          div(
            {
              class: 'table-attention-ripple',
              'data-testid': 'table-attention-ripple',
              style: item.map(
                (r) => `left:${(r.x * 100).toFixed(2)}%;top:${(r.y * 100).toFixed(2)}%`,
              ),
              role: 'status',
              'aria-live': 'polite',
              'aria-label': t('table_view.attention_ripple_label'),
            },
            [
              // Three concentric rings for the ripple animation (CSS keyframes).
              span({ class: 'table-attention-ripple-ring table-attention-ripple-ring--1', 'aria-hidden': 'true' }, []),
              span({ class: 'table-attention-ripple-ring table-attention-ripple-ring--2', 'aria-hidden': 'true' }, []),
              span({ class: 'table-attention-ripple-ring table-attention-ripple-ring--3', 'aria-hidden': 'true' }, []),
            ],
          ),
        ],
      }),
      // A11y: suppress the pointer-x/y text from the accessible tree (we
      // don't need aria-live for position updates — only for ripple).
      // The ripple's own aria-live="polite" is the only announcement.
      span(
        { class: 'sr-only', 'aria-live': 'off', 'aria-atomic': 'true' },
        [text(pointerX), text(', '), text(pointerY)],
      ),
    ],
  );
}

/**
 * #271 — the GM-side attention surface overlay. When attention mode is active
 * the GM's primary tab shows a transparent overlay over its content area that
 * captures `pointermove` and `click` events and dispatches them as normalised
 * attention signals. The overlay is `pointer-events:all` when active (so it
 * intercepts input) but passes through when inactive (`display:none`).
 *
 * The normalised coordinates map the viewport's width/height to [0,1]. This is
 * arm-agnostic — the same [0,1] convention is used on both sides of the channel.
 *
 * Seam note (#268 / #269): the `map` and `book` arms will pass their own
 * coordinate origin when they land; the overlay intercepts at the same surface
 * (viewport) so the normalisation is already arm-agnostic. No changes required
 * here when those arms ship.
 */
function viewGmAttentionSurface(state: Signal<Model>, send: Send<Msg>): Mountable {
  return show(
    state.map((s) => s.session.mode === 'gm' && s.tableAttentionMode),
    () => [
      div(
        {
          class: 'gm-attention-surface',
          'data-testid': 'gm-attention-surface',
          // Interaction surface: role="application" so AT announces it as
          // a custom widget; tabindex allows keyboard focus; the sr-only child
          // describes its purpose to screen-reader users.
          role: 'application',
          tabindex: '0',
          'aria-label': t('table_view.attention_surface_instructions'),
          onPointerMove: (ev: PointerEvent) => {
            const x = ev.clientX / window.innerWidth;
            const y = ev.clientY / window.innerHeight;
            send({ type: 'table/attention-pointer-move', x, y });
          },
          onPointerLeave: () => {
            send({ type: 'table/attention-pointer-clear' });
          },
          onClick: (ev: MouseEvent) => {
            const x = ev.clientX / window.innerWidth;
            const y = ev.clientY / window.innerHeight;
            send({ type: 'table/attention-ripple', x, y });
          },
          onKeyDown: (ev: KeyboardEvent) => {
            // Space/Enter: trigger a centred ripple for keyboard-only GMs.
            if (ev.key === ' ' || ev.key === 'Enter') {
              ev.preventDefault();
              send({ type: 'table/attention-ripple', x: 0.5, y: 0.5 });
            } else if (ev.key === 'Escape') {
              // #003 (llui-hud) — Escape exits pointing mode (also handled
              // globally; here too so a focused surface responds directly).
              ev.preventDefault();
              send({ type: 'table/attention-exit' });
            }
          },
        },
        [
          // #003 (llui-hud) — once pointing, it was unclear how to leave. Show a
          // persistent hint with both exits (Esc / the toggle button).
          div(
            { class: 'gm-attention-hint', 'data-testid': 'gm-attention-hint', 'aria-hidden': 'true' },
            [text(t('table_view.attention_exit_hint'))],
          ),
        ],
      ),
    ],
  );
}

/** #258 — the grouped, projected rows of the Table View body. */
function viewTableContent(state: Signal<Model>, _send: Send<Msg>): Mountable {
  return section({ class: 'table-view-content', 'data-testid': 'table-view-content' }, [
    each(state.map((s) => tableGroups(s)), {
      key: (g) => g.subject,
      render: (group) => [
        article({ class: 'table-view-group' }, [
          h2({ class: 'table-view-group-title' }, [text(group.at('title'))]),
          ul({ class: 'table-view-rows' }, [
            each(
              group.at('rows'),
              {
                key: (r) => `${r.subject}/${r.predicate}`,
                render: (row) => [viewTableRow(row)],
              },
            ),
          ]),
        ]),
      ],
    }),
  ]);
}

/** #258 — one projected fact row on the Table View (image inline, else text). */
function viewTableRow(row: Signal<TableProjectedRow>): Mountable {
  return li({ class: 'table-view-row' }, [
    branch(
      row.map((r) => (typeof r.assetUrl === 'string' && r.assetUrl !== '' ? 'image' : 'text')),
      {
        image: () => [
          img({
            class: 'table-view-image',
            src: row.map((r) => r.assetUrl ?? ''),
            alt: row.map((r) => predicateLabel(r.predicate)),
          }),
        ],
        text: () => [
          span({ class: 'table-view-row-label' }, [text(row.map((r) => predicateLabel(r.predicate)))]),
          span({ class: 'table-view-row-value' }, [text(row.map((r) => formatFactValue(r.value)))]),
        ],
      },
    ),
  ]);
}

/**
 * The focused pre-login shell (#150). A minimal brand-only header + a
 * single theme toggle, the routed auth view, and a slim footer with the
 * accessibility link. Deliberately omits the entity-list chrome, the
 * campaign cursor, the mobile session HUD, and the bottom nav — none of
 * those are meaningful before a session exists, and showing them makes
 * the sign-in page feel like a half-loaded app.
 */
function viewAuthShell(state: Signal<Model>, send: Send<Msg>): Mountable {
  return div({ class: 'auth-chrome', 'data-testid': 'auth-chrome' }, [
    viewSubstrateBanner(state, send),
    viewAuthHeader(),
    branch(state.map((s) => s.route.kind), {
      'sign-in': () => [viewSignIn(state, send)],
      'sign-up': () => [viewSignUp(state, send)],
      'forgot-password': () => [viewForgotPassword(state, send)],
      'verify-email': () => {
        const model = derived([state], (s) => s);
        const route = model.peek().route;
        const token = route.kind === 'verify-email' ? route.token : '';
        return [viewVerifyEmail(state, send, token)];
      },
      'reset-password': () => {
        const model = derived([state], (s) => s);
        const route = model.peek().route;
        const token = route.kind === 'reset-password' ? route.token : '';
        return [viewResetPassword(state, send, token)];
      },
      '2fa-disable': () => {
        const model = derived([state], (s) => s);
        const route = model.peek().route;
        const token = route.kind === '2fa-disable' ? route.token : '';
        return [viewTwofaDisable(state, send, token)];
      },
    }),
    viewAuthFooter(send),
  ]);
}

/** Minimal brand-only header for the pre-login auth shell. */
function viewAuthHeader(): Mountable {
  return header({ class: 'auth-chrome-header', role: 'banner' }, [
    a(
      {
        class: 'auth-brand',
        href: '/sign-in',
        'data-testid': 'auth-brand-link',
        'aria-label': t('app.brand_home_label'),
      },
      [
        span({ class: 'auth-brand-mark', 'aria-hidden': 'true' }, [text('◆')]),
        span({ class: 'auth-brand-word' }, [text(t('app.title'))]),
      ],
    ),
    themeToggle({
      testid: 'theme-toggle',
      labels: {
        light: t('theme.light'),
        dark: t('theme.dark'),
        system: t('theme.system'),
      },
    }),
  ]);
}

/** Slim footer for the pre-login auth shell — a11y link only. */
function viewAuthFooter<Msg2>(send: Send<Msg2>): Mountable {
  return footer({ class: 'auth-chrome-footer', role: 'contentinfo' }, [
    appRouteLink(
      send,
      { kind: 'a11y' } as AppRoute,
      { 'data-testid': 'footer-a11y-link' },
      [text(t('a11y.footer_link'))],
      (route) => navMsg(route) as unknown as Msg2,
    ),
  ]);
}

/**
 * The full authenticated app shell — the original `view` body. Header,
 * routed content, footer, mobile HUD, bottom nav, and every overlay.
 */
function viewAppShell(state: Signal<Model>, send: Send<Msg>): Renderable {
  return [
    // Bug 3 — substrate-storage banner. Mounted ABOVE the header so
    // the warning is impossible to miss; non-dismissable while the
    // state is active. Hidden on the happy path (substrate kind is
    // `opening` or `persistent`) so the chrome is clean.
    viewSubstrateBanner(state, send),
    viewHeader(state, send),
    // #005 (llui-hud) — the Actions overlay (sibling of the header so its fixed
    // backdrop/panel layer over the whole shell). Hidden unless opened.
    viewActionsOverlay(state, send),
    // 226.1 (#245) — Preview-as banner. Below the topbar, above the content
    // (mirrors the browse-mode banner placement in 30 §7.20). Only present
    // while previewing as a non-GM Viewer; the unmistakable "you are not
    // seeing full canon" signal so the GM never confuses a reveal-scoped view
    // with the truth.
    viewPreviewAsBanner(state, send),
    // App body. On in-campaign content routes (list / entity / drafts /
    // auditor / settings) this is a two-column grid — the persistent left
    // navigation rail + the routed `<main>`. Account / admin / campaign-
    // picker / a11y / gallery routes drop the rail (`.shell` stays single-
    // column) so they render full-width, matching the mocks (workspace-home
    // and admin have no global nav rail). The rail is hidden < 900px (CSS);
    // mobile keeps the fixed bottom-nav below.
    div(
      {
        class: state.map((s) => (routeHasRail(s.route) ? 'shell has-rail' : 'shell')),
      },
      [
        show(state.map((s) => routeHasRail(s.route)), () => [viewLeftRail(state, send)]),
        // Route dispatch keyed by route kind. The signal runtime re-mounts
        // the matched arm whenever `route.kind` changes. The `entity` arm
        // wraps its body in a single-row keyed `each` so navigating
        // entity→entity (same kind, different id) still remounts the
        // subtree — preserving the old `entity:${id}` scope-key semantics.
        branch(state.map((s) => s.route.kind), {
      // The `list` arm is keyed on the active root filter so switching the
      // Browse rail (`/` ↔ `/browse/character`) remounts the list view with
      // the new filter (same pattern as the `entity` arm's id remount).
      list: () => [
        each(
          state.map((s) => (s.route.kind === 'list' ? [s.route.root ?? '__all'] : [])),
          {
            key: (k) => k,
            render: () => {
              const model = derived([state], (s) => s);
              const route = model.peek().route;
              // "All roots" is modelled directly as `null` (no filter) rather than
              // round-tripping through an `'__all'` sentinel — keeps `.root` out of
              // a non-root string comparison (EntityRoot-closure guard, II.B.5).
              const activeRoot = route.kind === 'list' ? (route.root ?? null) : null;
              return [viewLocationsList(state, send, activeRoot)];
            },
          },
        ),
      ],
      entity: () => [
        each(
          state.map((s) => (s.route.kind === 'entity' ? [s.route.id] : [])),
          {
            key: (id) => id,
            render: () => {
              const model = derived([state], (s) => s);
              const route = model.peek().route;
              const entityId = route.kind === 'entity' ? route.id : '';
              return [viewEntityPage(state, send, entityId)];
            },
          },
        ),
      ],
      settings: () => [viewSettings(state, send)],
      a11y: () => [viewA11yPage(state, send)],
      // #85 — design-system component explorer.
      'ui-gallery': () => [viewUiGallery(state, send)],
      // #89 — GM recovery / backup posture explainer.
      recovery: () => [viewRecoveryPage(state, send)],
      'draft-inbox': () => [viewDraftInbox(state, send)],
      trash: () => [viewTrash(state, send)],
      auditor: () => [viewAuditorPanel(state, send)],
      'admin-users': () => [viewAdminShell(state, send, 'users', [viewAdminUsersImpl(state, send)])],
      'admin-llm': () => [viewAdminShell(state, send, 'llm', [viewAdminLlmImpl(state, send)])],
      'admin-signups': () => [
        viewAdminShell(state, send, 'signups', [viewAdminSignupsImpl(state, send)]),
      ],
      'admin-audit': () => [
        viewAdminShell(state, send, 'audit', [viewAdminAuditImpl(state, send)]),
      ],
      // Campaign picker / create-first surface.
      campaigns: () => [viewCampaignsPicker(state, send)],
      // #152 — account / profile settings page.
      account: () => [
        viewAccount(state, send, {
          enableTwofa: () => ({ type: 'account/twofa-enable-requested' }) as Msg,
          twofaCodeInput: (code: string) => ({ type: 'account/twofa-code-input', code }) as Msg,
          confirmTwofa: () => ({ type: 'account/twofa-confirm-requested' }) as Msg,
          cancelTwofa: () => ({ type: 'account/twofa-cancel' }) as Msg,
          disableTwofa: () => ({ type: 'account/twofa-disable-requested' }) as Msg,
          sendPasswordReset: () => ({ type: 'account/password-reset-requested' }) as Msg,
          recoveryEmailInput: (value: string) =>
            ({ type: 'account/recovery-email-input', value }) as Msg,
          recoveryPasswordInput: (value: string) =>
            ({ type: 'account/recovery-password-input', value }) as Msg,
          saveRecoveryEmail: () => ({ type: 'account/recovery-email-save-requested' }) as Msg,
          deleteConfirmInput: (value: string) =>
            ({ type: 'account/delete-confirm-input', value }) as Msg,
          deleteAccount: () => ({ type: 'account/delete-requested' }) as Msg,
          dismissNotice: () => ({ type: 'account/dismiss-notice' }) as Msg,
        }),
      ],
      // #117 — auth routes render in the dedicated pre-login auth shell
      // (`viewAuthShell`), not here; the top-level `branch` routes them
      // away before this dispatcher is reached. These arms exist only so
      // the keyed branch stays exhaustive while a navigate Msg is mid-
      // flight (the route flips a tick before the shell re-mounts).
      'sign-in': () => [viewSignIn(state, send)],
      'sign-up': () => [viewSignUp(state, send)],
      'forgot-password': () => [viewForgotPassword(state, send)],
      'verify-email': () => {
        const model = derived([state], (s) => s);
        const route = model.peek().route;
        const token = route.kind === 'verify-email' ? route.token : '';
        return [viewVerifyEmail(state, send, token)];
      },
      'reset-password': () => {
        const model = derived([state], (s) => s);
        const route = model.peek().route;
        const token = route.kind === 'reset-password' ? route.token : '';
        return [viewResetPassword(state, send, token)];
      },
      '2fa-disable': () => {
        const model = derived([state], (s) => s);
        const route = model.peek().route;
        const token = route.kind === '2fa-disable' ? route.token : '';
        return [viewTwofaDisable(state, send, token)];
      },
        }),
      ],
    ),
    viewFooter(state, send),
    // llui-hud #008/#009 — the session-play HUDs (mobile cursor HUD, pending
    // lane, rotating-facilitator panel, dice roller + initiative tracker) are
    // IN-CAMPAIGN tools. They used to render on EVERY authed route — including
    // the campaigns picker / account / admin — where there is no active
    // campaign, appending an unstyled stack at the foot of those pages. Gate the
    // whole group on `routeHasRail` (the in-campaign content routes: list /
    // entity / drafts / trash / auditor / settings); they still persist across
    // navigation between those routes, which is the point.
    show(
      state.map((s) => routeHasRail(s.route)),
      () => [
        // IV.B.3 (#79) — mobile session HUD docked to the top of the
        // viewport on phone. CSS hides it above 480px.
        viewMobileSessionHud(state, send),
        // #246 — the session-HUD pending lane. Visible on BOTH desktop and
        // phone GM surfaces (the cursor HUD itself is phone-only; the pending
        // section is needed during live play on any viewport). Hidden when
        // empty via `data-empty` CSS.
        viewHudPending(state, send),
        // #265 — rotating-facilitator HUD panel. CSS hides it (`data-empty`)
        // when `facilitation_mode !== 'rotating'` or the user is not GM.
        // Zero-projection: reads only the GM-truth config bucket.
        viewHudFacilitator(state, send),
        // #257 — Dice roller + initiative tracker (07 §13.9.1). GM-only,
        // ephemeral. Behind SESSION_TOOLS_ENABLED (currently off) until the
        // docked session-tools HUD styling lands — see the const's note.
        show(
          state.map((s) => SESSION_TOOLS_ENABLED && s.session.mode === 'gm'),
          () => [
            viewDiceRoller(state, send),
            viewInitiativeTracker(state, send),
            // #260 — Narrative combat panel (07 §13.9.5). Shown only when
            // (1) config enables it AND (2) initiative has at least one entry.
            show(
              state.map(
                (s) =>
                  (s.config.narrative_combat_panel_enabled ?? false) &&
                  s.initiative.entries.length > 0,
              ),
              () => [viewNarrativeCombatPanel(state, send)],
            ),
            // #259 — Full-form post-combat log modal.
            show(
              state.at('combatLogForm').map((f) => f !== null),
              () => [viewCombatLogFullForm(state, send)],
            ),
          ],
        ),
      ],
    ),
    // IV.B.1 / #77 — bottom nav anchored to the phone breakpoint via
    // CSS (the `display: none` rule above 480px is in styles.css). The
    // node is always rendered so the route's `aria-current` state is
    // server-correct without a hydration step; the visual on/off is a
    // pure CSS toggle.
    viewBottomNav(state, send),
    show(
      state.at('paletteOpen'),
      () => [viewPalette(state, send)],
    ),
    // IV.B.2 (#78) — entity-action bottom sheet.
    show(
      state.map((s) => s.entitySheet.kind !== 'closed'),
      () => [viewEntitySheet(state, send)],
    ),
    // IV.B.4 (#80) — private-notes bottom sheet (mobile).
    show(
      state.at('notesSheetOpen'),
      () => [viewNotesSheet(state, send)],
    ),
    // Phase 2 / 30 §7.7 — per-attribute reveal Viewer-Matrix popover.
    show(
      state.map((s) => s.revealMenu !== null),
      () => [viewRevealMenu(state, send)],
    ),
    // Phase 2 / 30 §7.10.2 — routine facet bulk-reveal popover.
    show(
      state.map((s) => s.facetRevealMenu !== null),
      () => [viewFacetRevealMenu(state, send)],
    ),
    // Phase 2 / 30 §7.10.3 — Reveal Composer (sensitive facets).
    show(
      state.map((s) => s.revealComposer !== null),
      () => [viewRevealComposer(state, send)],
    ),
    // Phase 2 / 03 §9 — Draft-belief modal.
    show(
      state.map((s) => s.beliefModal !== null),
      () => [viewBeliefModal(state, send)],
    ),
    // Phase 2 / 03 §9 — clone-a-lie picker.
    show(
      state.map((s) => s.beliefCloneMenu !== null),
      () => [viewBeliefCloneMenu(state, send)],
    ),
    // #131 — root picker (generic entity-creation overlay).
    show(
      state.at('rootPickerOpen'),
      () => [viewRootPicker(state, send)],
    ),
    // Bug 4 — new-entity name modal (mounted whenever
    // `newEntityModal !== null`; supersedes the eager-create flow).
    show(
      state.map((s) => s.newEntityModal !== null),
      () => [viewNewEntityModal(state, send)],
    ),
    // #81 — "Create instance" modal/bottom-sheet (mounted whenever
    // `instanceModal !== null`).
    show(
      state.map((s) => s.instanceModal !== null),
      () => [viewInstanceModal(state, send)],
    ),
    // Phase C — per-page "Draft with AI" modal.
    show(
      state.map((s) => s.pageDraftModal !== null),
      () => [viewPageDraftModal(state, send)],
    ),
    // #251 — bulk-gen config + cost-confirm modal (both entry surfaces).
    show(
      state.map((s) => s.bundleGenModal !== null),
      () => [viewBundleGenModal(state, send)],
    ),
    // #251 §14.6 — bundle regenerate-with-direction modal.
    show(
      state.map((s) => s.bundleRegenModal !== null),
      () => [viewBundleRegenModal(state, send)],
    ),
    // ADR-0008 §1/§5 (T8, #282) — LLM-led interactive trait-authoring panel.
    show(
      state.map((s) => s.authoringSession !== null),
      () => [viewAuthoringPanel(state, send)],
    ),
    // Live streaming preview for per-fact ✨ prose.
    show(
      state.map((s) => streamPreviewActive(s)),
      () => [viewStreamPreview(state, send)],
    ),
    // Per-fact History (entity-page overlay; modal). Rendered top-level with
    // the other overlays so it covers every page surface instead of expanding
    // the fact row it belongs to.
    //
    // The `show()` gate is LOAD-BEARING, not redundant with the modal's own
    // `open` signal: an LLui dialog that is present in the tree marks the
    // background content `inert` + `aria-hidden` for focus containment. Mount
    // it unconditionally and the page is permanently click-dead — every fact
    // row's meta line rendered as `<div class="fact-meta" inert>` and no
    // button in it could fire. (Same mechanism as the bottom-nav incident
    // noted in styles.css: `inert` stuck true and left the nav click-dead.)
    // Mount the dialog only while it is open, exactly like the trait picker.
    show(
      state.map((s) => s.historyOpen !== null),
      () => [viewHistoryDialog(state, send, factRowHost)],
    ),
    // #139 — trait picker (entity-page overlay; modal). Rendered at the
    // top of the tree so it covers every page surface; gated on
    // `traitPickerEntityId` being non-null.
    show(
      state.map((s) => s.traitPickerEntityId !== null),
      () => [viewTraitPicker(state, send, traitAttachHost)],
    ),
    // #139 — destructive detach confirm. Only mounted when the GM
    // attempted to detach a trait that hides ≥1 fact (factCountForDetach
    // > 0 in the reducer); zero-fact detaches skip the confirm.
    show(
      state.map((s) => s.traitDetachConfirm !== null),
      () => [viewTraitDetachConfirm(state, send, traitAttachHost)],
    ),
    // Usage-aware retire confirm — mounted when the GM retires a homebrew
    // trait/predicate that is still in use (attached to entities or carrying
    // live facts). Not-in-use retires skip it (reducer fast path).
    show(
      state.map((s) => s.schemaRetireConfirm !== null),
      () => [viewSchemaRetireConfirm(state, send)],
    ),
    show(
      state.map((s) => s.entityDeleteConfirm !== null),
      () => [viewEntityDeleteConfirm(state, send)],
    ),
    show(
      state.map((s) => s.controllerPicker !== null),
      () => [viewControllerPicker(state, send)],
    ),
    // #73 / #schema-settings — the trait editor is no longer a modal; it
    // renders inline in the settings Traits tab (`viewTraitEditorInline`).
    // #units / ADR-0002 — campaign units editor (define dimensions + units).
    show(
      state.map((s) => s.unitsEditor !== null),
      () => [viewUnitsEditor(state, send)],
    ),
    // Bug 3 — substrate banner "Why?" dialog.
    show(
      state.at('substrateDialogOpen'),
      () => [viewSubstrateDialog(state, send)],
    ),
    // #89 — one-time GM recovery / backup disclaimer. Shows after sign-in
    // when the loaded user has not yet acknowledged it AND the session has
    // not dismissed it locally. The route is irrelevant (it overlays the
    // whole shell); the gate is purely the acknowledgement state.
    show(
      state.map((s) => shouldShowRecoveryDisclaimer(s)),
      () => [viewRecoveryDisclaimer(state, send)],
    ),
    // #271 — GM attention surface overlay. Captures pointermove/click when
    // attention mode is active and dispatches normalised attention signals.
    // Rendered last so it sits above all other overlays; pointer-events:all
    // when active, display:none when not — the GM's cursor still works normally
    // when attention mode is off.
    viewGmAttentionSurface(state, send),
  ];
}

/**
 * #89 — should the one-time recovery disclaimer modal show right now?
 * True only when the signed-in user record has loaded with
 * `recovery_disclaimer_seen === false` AND the GM has not dismissed it in
 * this session. GM-only (the account user only loads in GM mode), and the
 * auth routes are excluded so it never overlays the sign-in surface.
 */
function shouldShowRecoveryDisclaimer(s: Model): boolean {
  if (s.recoveryDisclaimerDismissed) return false;
  if (isAuthRoute(s.route)) return false;
  if (s.session.mode !== 'gm') return false;
  const user = s.account.user;
  return user !== null && user.recovery_disclaimer_seen === false;
}

/**
 * #89 — the one-time recovery / backup disclaimer modal. Approved copy is
 * verbatim (`recovery.disclaimer.*` keys). Two actions: "Export now"
 * (acknowledge + trigger export) and "Got it" (acknowledge + dismiss).
 * A "Read more about recovery" link deep-links to `/recovery`. The
 * `@dl/ui` modal atom owns focus-trap / ESC / scrim; dismissing via ESC
 * or scrim maps to "Got it" (the irrevocable acknowledgement) so the
 * modal never re-shows.
 */
function viewRecoveryDisclaimer(state: Signal<Model>, send: Send<Msg>): Mountable {
  return modal({
    open: state.map((s) => shouldShowRecoveryDisclaimer(s)),
    onDismiss: () => send({ type: 'recovery-disclaimer/dismiss' }),
    labelId: 'recovery-disclaimer-heading',
    describedById: 'recovery-disclaimer-body',
    role: 'alertdialog',
    panelClass: 'recovery-disclaimer',
    testid: 'recovery-disclaimer',
    size: 'md',
    labels: { close: t('recovery.disclaimer.got_it') },
    children: [
      h2({ id: 'recovery-disclaimer-heading', class: 'recovery-disclaimer-title' }, [
        text(t('recovery.disclaimer.title')),
      ]),
      div({ id: 'recovery-disclaimer-body', class: 'recovery-disclaimer-body' }, [
        p([text(t('recovery.disclaimer.body_1'))]),
        p([text(t('recovery.disclaimer.body_2'))]),
        p([text(t('recovery.disclaimer.body_3'))]),
        p({ class: 'recovery-disclaimer-readmore' }, [
          a(
            {
              class: 'recovery-disclaimer-link',
              href: '/recovery',
              'data-testid': 'recovery-disclaimer-readmore',
              // SPA navigate (keeps the session); preventDefault on the
              // anchor so we don't hard-load. The modal stays open is
              // undesirable — navigating to /recovery is enough context, so
              // we also acknowledge-dismiss so it doesn't reappear.
              onClick: (e: Event) => {
                e.preventDefault();
                send({ type: 'recovery-disclaimer/dismiss' });
                send({ type: 'navigate', route: { kind: 'recovery' } });
              },
            },
            [text(t('recovery.disclaimer.read_more'))],
          ),
        ]),
      ]),
      div({ class: 'recovery-disclaimer-actions' }, [
        uiButton({
          variant: 'primary',
          testid: 'recovery-disclaimer-export',
          onClick: () => send({ type: 'recovery-disclaimer/export-now' }),
          children: [text(t('recovery.disclaimer.export_now'))],
        }),
        uiButton({
          variant: 'ghost',
          testid: 'recovery-disclaimer-dismiss',
          onClick: () => send({ type: 'recovery-disclaimer/dismiss' }),
          children: [text(t('recovery.disclaimer.got_it'))],
        }),
      ]),
    ],
  });
}

/**
 * Bug 3 — substrate-storage banner. Renders a non-dismissable warning
 * above the app chrome when the local replica failed to open or fell
 * back to in-memory. The container is announced once via
 * `role="status" aria-live="polite"` (not `role="alert"` — we don't
 * want it to interrupt every page transition); the "Why?" link opens
 * a small dialog with the browser-version requirements + the active
 * error message.
 *
 * Hidden on the happy path (substrate kind is `opening` or `persistent`).
 * The kind discrimination drives the banner copy via two `h.show` arms.
 */
function viewSubstrateBanner(state: Signal<Model>, send: Send<Msg>): Mountable {
  return show(
      state.map((s) => s.substrate.kind === 'failed' || s.substrate.kind === 'memory'),
      () => [
      div(
        {
          class: 'substrate-banner',
          'data-testid': 'substrate-banner',
          'data-substrate-state': state.map((s) => s.substrate.kind),
          role: 'status',
          'aria-live': 'polite',
          'aria-atomic': 'true',
        },
        [
          span({ class: 'substrate-banner-icon', 'aria-hidden': 'true' }, ['⚠']),
          span({ class: 'substrate-banner-message' }, [
            text(state.map((s) => s.substrate.kind === 'failed'
                ? t('substrate.banner.failed')
                : s.substrate.kind === 'memory'
                  ? t('substrate.banner.memory')
                  : '',)),
          ]),
          uiButton({
            variant: 'ghost',
            className: 'substrate-banner-why',
            testid: 'substrate-banner-why',
            ariaLabel: t('substrate.banner.why_aria'),
            onClick: () => send({ type: 'open-substrate-dialog' }),
            children: [text(t('substrate.banner.why'))],
          }),
        ],
      ),
    ],
    );
}

function viewSubstrateDialog(state: Signal<Model>, send: Send<Msg>): Mountable {
  // The @dl/ui/modal atom owns focus trap, ESC dismiss, scrim click
  // dismiss, and focus restoration. The explicit Close button below
  // remains the primary dismiss affordance; ESC and scrim-click are
  // the keyboard / pointer fallbacks. The Msg dispatched on dismiss
  // is `close-substrate-dialog` (same Msg the close button sends).
  return modal({
    open: state.at('substrateDialogOpen'),
    onDismiss: () => send({ type: 'close-substrate-dialog' }),
    labelId: 'substrate-dialog-heading',
    panelClass: 'substrate-dialog',
    testid: 'substrate-dialog',
    size: 'md',
    labels: { close: t('substrate.dialog.close') },
    children: [
      h2({ id: 'substrate-dialog-heading' }, [
        text(t('substrate.dialog.heading')),
      ]),
          p([text(t('substrate.dialog.requirements'))]),
          show(
      state.map((s) => s.substrate.kind === 'failed'),
      () => [
              p({ class: 'substrate-dialog-error' }, [
                span({ class: 'substrate-dialog-error-label' }, [
                  text(`${t('substrate.dialog.error_label')}: `),
                ]),
                // The error name (e.g. `OpfsUnavailableError`,
                // `SubstrateOpenError`) goes inline as a code token so the
                // GM can grep / paste it into a bug report verbatim.
                code(
                  {
                    class: 'substrate-dialog-error-name',
                    'data-testid': 'substrate-dialog-error-name',
                  },
                  [
                    text(state.map((s) => s.substrate.kind === 'failed' && s.substrate.errorName !== undefined
                        ? s.substrate.errorName
                        : 'Error',)),
                  ],
                ),
                span({ 'aria-hidden': 'true' }, [' — ']),
                // The full error message renders inside a <code> block so
                // wrapping is preserved verbatim and the styling cue makes
                // it obviously copy-pasteable diagnostic text.
                code(
                  {
                    class: 'substrate-dialog-error-message',
                    'data-testid': 'substrate-dialog-error-message',
                  },
                  [
                    text(state.map((s) => s.substrate.kind === 'failed' ? s.substrate.error : '',)),
                  ],
                ),
              ]),
            ],
    ),
          // Bug 2 follow-up — when the substrate fell back to `:memory:`
          // we now carry the original OPFS rejection through to the UI
          // (see `OpfsUnavailableError.cause`). Render it verbatim so
          // dev users can see whether the cause is recoverable (stale
          // worker handle, missing COOP/COEP headers) vs structural
          // (private-mode Safari, browser predating OPFS).
          show(
      state.map((s) => s.substrate.kind === 'memory' && s.substrate.opfsError !== undefined),
      () => [
              p({ class: 'substrate-dialog-error' }, [
                span({ class: 'substrate-dialog-error-label' }, [
                  text(`${t('substrate.dialog.opfs_cause_label')}: `),
                ]),
                code(
                  {
                    class: 'substrate-dialog-error-message',
                    'data-testid': 'substrate-dialog-opfs-cause',
                  },
                  [
                    text(state.map((s) => s.substrate.kind === 'memory' && s.substrate.opfsError !== undefined
                        ? s.substrate.opfsError
                        : '',)),
                  ],
                ),
              ]),
            ],
    ),
      uiButton({
        variant: 'primary',
        className: 'substrate-dialog-close',
        testid: 'substrate-dialog-close',
        onClick: () => send({ type: 'close-substrate-dialog' }),
        children: [text(t('substrate.dialog.close'))],
      }),
    ],
  });
}

function viewFooter(state: Signal<Model>, send: Send<Msg>): Mountable {
  return footer({ class: 'app-footer', role: 'contentinfo' }, [
    appRouteLink(
      send,
      { kind: 'a11y' },
      { 'data-testid': 'footer-a11y-link' },
      [text(t('a11y.footer_link'))],
      navMsg,
    ),
  ]);
}

/** Is any overlay modal open (so the mobile bottom-nav must not take taps)? */
function bottomNavBlocked(s: Model): boolean {
  return (
    s.paletteOpen ||
    s.entitySheet.kind !== 'closed' ||
    s.notesSheetOpen ||
    s.rootPickerOpen ||
    s.newEntityModal !== null ||
    s.instanceModal !== null ||
    s.pageDraftModal !== null ||
    s.bundleGenModal !== null ||
    s.bundleRegenModal !== null ||
    s.traitPickerEntityId !== null ||
    s.traitDetachConfirm !== null
  );
}

function viewBottomNav(state: Signal<Model>, send: Send<Msg>): Mountable {
  return nav(
    {
      // #120 — when any modal overlay is open (palette, cursor picker,
      // and the IV.B.2/IV.B.3/IV.B.4 bottom-sheets) the bottom-nav must not
      // receive taps near the bottom edge — they should route to the modal's
      // close button sitting underneath.
      //
      // This guard is driven off a reactive CLASS, NOT the `inert` /
      // `aria-hidden` attributes. Those did not reconcile: once set true they
      // stuck across in-app state changes (the attribute reconciler did not
      // remove a string-valued attribute when the binding later emitted
      // `undefined`). A stuck native `inert` made the subtree non-interactive
      // regardless of CSS — leaving the mobile nav permanently click-dead
      // after any modal had opened (blocking Trash/restore + every nav item —
      // a mobile-parity regression); a stuck `aria-hidden` on a nav holding
      // focusable links is itself a serious axe violation (`aria-hidden-focus`).
      // A `class` binding reconciles reliably (same as the nav's `aria-current`):
      // `.is-modal-blocked` applies `pointer-events: none` (+ `visibility:
      // hidden` to drop the dimmed nav from the a11y tree / focus order while a
      // modal owns focus) and clears the instant the modal closes.
      class: state.map((s) => (bottomNavBlocked(s) ? 'bottom-nav is-modal-blocked' : 'bottom-nav')),
      role: 'navigation',
      'aria-label': t('mobile.nav.list'),
      'data-testid': 'bottom-nav',
    },
    [
      appRouteLink(
        send,
        { kind: 'list' },
        {
          'data-testid': 'bottom-nav-list',
          'aria-current': state.map((s) => (s.route.kind === 'list' ? 'page' : undefined)),
        },
        [text(t('mobile.nav.list'))],
        navMsg,
      ),
      // Drafts inbox is GM-only (mirrors the header link, III.C.5/#73), so
      // the phone nav surfaces it only in GM mode — players have no inbox.
      show(
      state.map((s) => s.session.mode === 'gm'),
      () => [
          appRouteLink(
            send,
            { kind: 'draft-inbox' },
            {
              'data-testid': 'bottom-nav-drafts',
              'aria-current': state.map((s) => (s.route.kind === 'draft-inbox' ? 'page' : undefined)),
            },
            // #266 — flat mode: "Session prep" instead of "Drafts" in the mobile nav.
            [text(state.map((s) =>
              s.config.facilitation_mode === 'flat'
                ? t('inbox.flat.open')
                : t('mobile.nav.drafts'),
            ))],
            navMsg,
          ),
          appRouteLink(
            send,
            { kind: 'auditor' },
            {
              'data-testid': 'bottom-nav-auditor',
              'aria-current': state.map((s) => (s.route.kind === 'auditor' ? 'page' : undefined)),
            },
            [text(t('mobile.nav.auditor'))],
            navMsg,
          ),
          // Trash — soft-deleted entities + restore. Mobile parity for the
          // desktop side-rail Trash item (the rail is display:none on phones).
          // Shown only when something is trashed, mirroring the rail.
          show(
            state.map((s) => trashCount(s) > 0),
            () => [
              appRouteLink(
                send,
                { kind: 'trash' },
                {
                  'data-testid': 'bottom-nav-trash',
                  'aria-current': state.map((s) => (s.route.kind === 'trash' ? 'page' : undefined)),
                },
                [text(t('trash.open'))],
                navMsg,
              ),
            ],
          ),
        ],
    ),
      appRouteLink(
        send,
        { kind: 'settings' },
        {
          'data-testid': 'bottom-nav-settings',
          'aria-current': state.map((s) => (s.route.kind === 'settings' ? 'page' : undefined)),
        },
        [text(t('mobile.nav.settings'))],
        navMsg,
      ),
    ],
  );
}

/**
 * IV.B.3 (#79) — mobile session HUD.
 *
 * Always-rendered top-of-viewport bar with the cursor display +
 * advance / rewind. CSS hides it above 480px (the desktop cursor pill
 * in `viewHeader` covers that breakpoint). The HUD persists across
 * page navigation because it lives at the top of the `view` tree —
 * outside the route subtree — so the route scope re-mount does not
 * tear it down.
 *
 * Advance is one-tap; rewind stages a confirmation per AC. Both
 * dispatch through the standard `set-cursor` pipeline (via
 * `cursor-advance` / `cursor-rewind` Msgs), so the substrate is
 * unchanged and the calendar runtime renders the new value.
 */
function viewMobileSessionHud(state: Signal<Model>, send: Send<Msg>): Mountable {
  return div(
    {
      class: 'mobile-session-hud',
      role: 'region',
      'aria-label': t('mobile.hud.heading'),
      'data-testid': 'mobile-session-hud',
    },
    [
      span({ class: 'mobile-hud-label' }, [text(`${t('calendar.cursor')}: `)]),
      span({ class: 'mobile-hud-value', 'data-testid': 'mobile-hud-cursor-value' }, [
        text(state.map((s) => formatCursor(activeCalendar(s), s.campaignCursor))),
      ]),
      div({ class: 'mobile-hud-controls' }, [
        uiButton({
          variant: 'icon',
          className: 'mobile-hud-rewind',
          testid: 'mobile-hud-rewind',
          ariaLabel: t('mobile.hud.rewind'),
          // Gated until the fold confirms the active calendar (see
          // `Model.calendarConfirmed`).
          disabled: state.map((s) => !s.calendarConfirmed),
          onClick: () => send({ type: 'cursor-rewind-confirm', days: 1 }),
          children: ['◀'],
        }),
        uiButton({
          variant: 'icon',
          className: 'mobile-hud-advance',
          testid: 'mobile-hud-advance',
          ariaLabel: t('mobile.hud.advance'),
          disabled: state.map((s) => !s.calendarConfirmed),
          onClick: () => send({ type: 'cursor-advance', days: 1 }),
          children: ['▶'],
        }),
      ]),
      // #242 — when the active calendar is plugin-mode / unresolvable the
      // cursor value falls back to the bundled default; surface the degrade
      // notice so the GM knows the chosen calendar isn't fully supported.
      show(
        state.map((s) => activeCalendarDegrades(s)),
        () => [calendarDegradeNotice()],
      ),
      // Confirmation strip — surfaces below the controls when rewind
      // is staged. The Yes/No buttons resolve the staged state.
      show(
      state.map((s) => s.hudRewindPending !== null),
      () => [
          div(
            {
              class: 'mobile-hud-confirm',
              role: 'alertdialog',
              'aria-label': t('mobile.hud.rewind_confirm'),
              'data-testid': 'mobile-hud-rewind-confirm',
            },
            [
              p({ class: 'mobile-hud-confirm-prompt' }, [
                text(t('mobile.hud.rewind_confirm')),
              ]),
              div({ class: 'mobile-hud-confirm-actions' }, [
                uiButton({
                  variant: 'primary',
                  className: 'mobile-hud-confirm-yes',
                  testid: 'mobile-hud-rewind-yes',
                  onClick: () => send({ type: 'cursor-rewind', days: 1 }),
                  children: [text(t('mobile.hud.rewind_yes'))],
                }),
                uiButton({
                  variant: 'ghost',
                  className: 'mobile-hud-confirm-no',
                  testid: 'mobile-hud-rewind-no',
                  onClick: () => send({ type: 'cursor-rewind-cancel' }),
                  children: [text(t('mobile.hud.rewind_no'))],
                }),
              ]),
            ],
          ),
        ],
    ),
    ],
  );
}

/**
 * #246 — the session-HUD pending section. Lists every `pending` canon fact
 * (the #223 lane's silently/surfaced-promoted LLM drafts + any player pending)
 * with an inline ratify (→canon, an explicit per-fact GM gesture) / reject
 * (→retract). The count badge is the one the #223 `surface` mode increments;
 * `silent` keeps the section + inline count but renders no attention badge.
 *
 * GM-only by construction (the HUD mounts only in GM chrome) and reads only
 * the GM-side fold — no `VisibleFact` is constructed here, so the projection
 * chokepoint is untouched. Ratify emits a GM-authored op (see `pending-ratify`
 * + `submitFactStatus`), never an LLM/system canon write.
 */
function viewHudPending(state: Signal<Model>, send: Send<Msg>): Mountable {
  return div(
    {
      class: 'hud-pending',
      role: 'region',
      'aria-label': t('hud.pending.heading'),
      'data-testid': 'hud-pending',
      // Hidden via CSS unless the GM has pending facts to clean up. Non-GM
      // (player) sessions never surface the ratify/reject affordances.
      'data-empty': state.map((s) => (hudPendingVisible(s) ? 'false' : 'true')),
    },
    [
      div({ class: 'hud-pending-head' }, [
        span({ class: 'hud-pending-heading' }, [text(t('hud.pending.heading'))]),
        // The attention-grabbing count badge. Only non-empty under `surface`
        // mode (#223) — `silent` suppresses it (count 0 → empty text). Paired
        // with `data-notify` so the visual emphasis is never colour-only.
        span(
          {
            class: 'hud-pending-count',
            role: 'status',
            'aria-live': 'polite',
            'data-testid': 'hud-pending-count',
            'data-notify': state.map((s) => (pendingNotifyCount(s) > 0 ? 'true' : 'false')),
          },
          [
            text(
              state.map((s) =>
                pendingNotifyCount(s) > 0 ? t('hud.pending.count', { n: pendingNotifyCount(s) }) : '',
              ),
            ),
          ],
        ),
      ]),
      // Empty copy — shown to the GM when there are no pending facts.
      show(
        state.map((s) => s.session.mode === 'gm' && pendingCount(s) === 0),
        () => [
          p({ class: 'hud-pending-empty', 'data-testid': 'hud-pending-empty' }, [
            text(t('hud.pending.empty')),
          ]),
        ],
      ),
      show(
        state.map((s) => hudPendingVisible(s)),
        () => [
          ul(
            { role: 'list', class: 'hud-pending-list', 'data-testid': 'hud-pending-list' },
            [
              each(state.map((s) => pendingRows(s)), {
                key: (row) => row.key,
                render: (item) => [
                  li(
                    {
                      class: 'hud-pending-row',
                      'data-testid': 'hud-pending-row',
                      'data-predicate': item.at('predicate'),
                      'data-author': item.at('author'),
                    },
                    [
                      div({ class: 'hud-pending-row-head' }, [
                        // Provenance badge — pairs the dot hue with a text
                        // label so colour is never the only signal (a11y).
                        span(
                          {
                            class: 'hud-pending-author',
                            'data-author': item.at('author'),
                            'data-testid': 'hud-pending-author',
                          },
                          [text(item.at('authorLabel'))],
                        ),
                        span({ class: 'hud-pending-entity' }, [text(item.at('entityName'))]),
                        span({ class: 'hud-pending-predicate' }, [
                          text(item.at('predicateLabel')),
                        ]),
                        span({ class: 'hud-pending-value' }, [text(item.at('value'))]),
                      ]),
                      div({ class: 'hud-pending-actions' }, [
                        uiButton({
                          variant: 'primary',
                          className: 'hud-pending-ratify',
                          testid: 'hud-pending-ratify',
                          ariaLabel: item.map((r) =>
                            t('hud.pending.row_label', {
                              entity: r.entityName,
                              predicate: r.predicateLabel,
                            }),
                          ),
                          attrs: { title: t('hud.pending.ratify') },
                          onClick: () => {
                            const row = item.peek();
                            send({
                              type: 'pending-ratify',
                              entityId: row.entityId,
                              predicate: row.predicate,
                            });
                          },
                          children: [text(t('hud.pending.ratify'))],
                        }),
                        uiButton({
                          variant: 'danger',
                          className: 'hud-pending-reject',
                          testid: 'hud-pending-reject',
                          ariaLabel: item.map((r) =>
                            t('hud.pending.row_label', {
                              entity: r.entityName,
                              predicate: r.predicateLabel,
                            }),
                          ),
                          attrs: { title: t('hud.pending.reject') },
                          onClick: () => {
                            const row = item.peek();
                            send({
                              type: 'pending-reject',
                              entityId: row.entityId,
                              predicate: row.predicate,
                            });
                          },
                          children: [text(t('hud.pending.reject'))],
                        }),
                      ]),
                    ],
                  ),
                ],
              }),
            ],
          ),
        ],
      ),
    ],
  );
}

// ---------- #265 — Rotating-facilitator HUD ----------------------
//
// Visible only when `facilitation_mode === 'rotating'`. Shows the current
// badge-holder and fairness counts; the owner can designate and the current
// facilitator can hand off. Zero-projection: reads only the GM-truth config
// bucket (no VisibleFact, no chokepoint involvement).

/** Derive members eligible to be designated facilitator (all roster members). */
function facilitatorCandidates(
  s: Model,
): readonly { user_id: string; email: string }[] {
  return s.campaignMembers ?? [];
}

/** Whether the current user is the campaign owner. */
function isCurrentUserOwner(s: Model): boolean {
  return s.session.campaignRole === 'owner';
}

/** Whether the current user is the current session facilitator. */
function isCurrentUserFacilitator(s: Model): boolean {
  const fac = s.config.session_facilitator;
  if (!fac || !s.session.userId) return false;
  return fac === s.session.userId;
}

/** Per-member session counts for the fairness display. */
function facilitatorCounts(s: Model): Record<string, number> {
  const members = facilitatorCandidates(s);
  const ids = members.map((m) => m.user_id);
  const history = (s.config.rotation_history as RotationHistoryEntry[] | undefined) ?? [];
  const counts: Record<string, number> = {};
  for (const id of ids) counts[id] = 0;
  for (const entry of history) {
    if (Object.prototype.hasOwnProperty.call(counts, entry.user_id)) {
      counts[entry.user_id] = (counts[entry.user_id] ?? 0) + 1;
    }
  }
  return counts;
}

/** The suggested next-fair facilitator user_id. */
function suggestedNextFacilitator(s: Model): string | null {
  const members = facilitatorCandidates(s);
  const rotation: RotationState = {
    session_facilitator: s.config.session_facilitator ?? null,
    rotation_history: (s.config.rotation_history as RotationHistoryEntry[] | undefined) ?? [],
  };
  return nextFairFacilitator(rotation, members.map((m) => m.user_id));
}

/**
 * #265 — Rotating-facilitator HUD panel. Rendered as a persistent sibling
 * of `viewHudPending` when `facilitation_mode === 'rotating'`. GM-only.
 *
 * The panel is always rendered (CSS `data-empty` hides it) so the DOM is
 * stable across mode changes without a branch remount.
 */
function viewHudFacilitator(state: Signal<Model>, send: Send<Msg>): Mountable {
  return div(
    {
      class: 'hud-facilitator',
      role: 'region',
      'aria-label': t('hud.facilitator.heading'),
      'data-testid': 'hud-facilitator',
      // Hidden when not in rotating mode or not GM.
      'data-empty': state.map((s) =>
        s.session.mode === 'gm' && s.config.facilitation_mode === 'rotating' ? 'false' : 'true',
      ),
    },
    [
      // ── Heading row ──
      div({ class: 'hud-facilitator-head' }, [
        span({ class: 'hud-facilitator-heading' }, [text(t('hud.facilitator.heading'))]),
      ]),

      // ── Current badge-holder ──
      div({ class: 'hud-facilitator-current', 'data-testid': 'hud-facilitator-current' }, [
        span({ class: 'hud-facilitator-label' }, [text(t('hud.facilitator.current') + ': ')]),
        span(
          {
            class: 'hud-facilitator-badge',
            'data-testid': 'hud-facilitator-badge',
            'aria-label': state.map((s) => {
              const fac = s.config.session_facilitator;
              if (!fac) return t('hud.facilitator.none');
              const member = (s.campaignMembers ?? []).find((m) => m.user_id === fac);
              return member ? member.email : fac;
            }),
          },
          [
            text(
              state.map((s) => {
                const fac = s.config.session_facilitator;
                if (!fac) return t('hud.facilitator.none');
                const member = (s.campaignMembers ?? []).find((m) => m.user_id === fac);
                const label = member ? member.email : fac;
                // Mark if the current user is the facilitator.
                const isSelf = fac === s.session.userId;
                return isSelf ? `${label} (${t('hud.facilitator.you')})` : label;
              }),
            ),
          ],
        ),
      ]),

      // ── Fairness counts ──
      div({ class: 'hud-facilitator-balance', 'data-testid': 'hud-facilitator-balance' }, [
        span({ class: 'hud-facilitator-balance-label' }, [
          text(t('hud.facilitator.rotation_balance') + ':'),
        ]),
        div(
          { class: 'hud-facilitator-balance-list' },
          [
            each(
              state.map((s) => {
                const counts = facilitatorCounts(s);
                return (s.campaignMembers ?? []).map((m) => ({
                  user_id: m.user_id,
                  email: m.email,
                  count: counts[m.user_id] ?? 0,
                }));
              }),
              {
                key: (row) => row.user_id,
                render: (item) => [
                  div({ class: 'hud-facilitator-balance-row' }, [
                    span({ class: 'hud-facilitator-balance-email' }, [text(item.at('email'))]),
                    span({ class: 'hud-facilitator-balance-count' }, [
                      text(
                        item.map((r) => t('hud.facilitator.rotation_count', { count: r.count })),
                      ),
                    ]),
                  ]),
                ],
              },
            ),
          ],
        ),
      ]),

      // ── Suggested next ──
      show(
        state.map((s) => suggestedNextFacilitator(s) !== null),
        () => [
          p({ class: 'hud-facilitator-suggestion', 'data-testid': 'hud-facilitator-suggestion' }, [
            text(
              state.map((s) => {
                const suggested = suggestedNextFacilitator(s);
                if (!suggested) return '';
                const member = (s.campaignMembers ?? []).find((m) => m.user_id === suggested);
                const label = member ? member.email : suggested;
                return t('hud.facilitator.next_suggestion', { name: label });
              }),
            ),
          ]),
        ],
      ),

      // ── Actions ──
      div({ class: 'hud-facilitator-actions' }, [
        // Owner can designate when there is no facilitator yet.
        show(
          state.map((s) => isCurrentUserOwner(s) && !s.config.session_facilitator),
          () => [
            uiButton({
              variant: 'primary',
              className: 'hud-facilitator-set',
              testid: 'hud-facilitator-set',
              onClick: () => send({ type: 'facilitator/open-modal', mode: 'designate' }),
              children: [text(t('hud.facilitator.set'))],
            }),
          ],
        ),
        // Current facilitator can hand off. Owner can also re-designate if they
        // hold the badge.
        show(
          state.map((s) => isCurrentUserFacilitator(s)),
          () => [
            uiButton({
              variant: 'secondary',
              className: 'hud-facilitator-hand-off',
              testid: 'hud-facilitator-hand-off',
              onClick: () => send({ type: 'facilitator/open-modal', mode: 'handoff' }),
              children: [text(t('hud.facilitator.hand_off'))],
            }),
          ],
        ),
        // Owner can re-designate even when a facilitator is already set (in case
        // the current facilitator is unavailable).
        show(
          state.map((s) => isCurrentUserOwner(s) && !!s.config.session_facilitator && !isCurrentUserFacilitator(s)),
          () => [
            uiButton({
              variant: 'secondary',
              className: 'hud-facilitator-set',
              testid: 'hud-facilitator-designate',
              onClick: () => send({ type: 'facilitator/open-modal', mode: 'designate' }),
              children: [text(t('hud.facilitator.set'))],
            }),
          ],
        ),
      ]),

      // ── Designate/hand-off modal ──
      show(
        state.map((s) => s.facilitatorModal !== null),
        () => [
          viewFacilitatorModal(state, send),
        ],
      ),
    ],
  );
}

/**
 * #265 — Modal overlay for designating or handing off the facilitator badge.
 * Rendered inline inside the HUD (not a portal) so it inherits the HUD's
 * `role="region"` and aria context.
 */
function viewFacilitatorModal(state: Signal<Model>, send: Send<Msg>): Mountable {
  return div(
    {
      class: 'hud-facilitator-modal',
      role: 'dialog',
      'aria-modal': 'true',
      'aria-label': t('hud.facilitator.modal_aria'),
      'data-testid': 'hud-facilitator-modal',
    },
    [
      div({ class: 'hud-facilitator-modal-head' }, [
        p({ class: 'hud-facilitator-modal-title' }, [
          text(
            state.map((s) =>
              s.facilitatorModal?.mode === 'handoff'
                ? t('hud.facilitator.hand_off_title')
                : t('hud.facilitator.designate_title'),
            ),
          ),
        ]),
      ]),
      // Member list — pick one.
      ul(
        {
          class: 'hud-facilitator-modal-list',
          role: 'listbox',
          'aria-label': t('hud.facilitator.modal_aria'),
        },
        [
          each(
            state.map((s) => {
              const currentFac = s.config.session_facilitator ?? null;
              const mode = s.facilitatorModal?.mode ?? 'designate';
              // In handoff mode, exclude the current facilitator (can't pass to self).
              return (s.campaignMembers ?? [])
                .filter((m) => mode === 'designate' || m.user_id !== currentFac)
                .map((m) => ({
                  user_id: m.user_id,
                  email: m.email,
                  selected: s.facilitatorModal?.selectedUserId === m.user_id,
                }));
            }),
            {
              key: (row) => row.user_id,
              render: (item) => [
                li(
                  {
                    class: 'hud-facilitator-modal-row',
                    role: 'option',
                    'aria-selected': item.at('selected').map((v) => (v ? 'true' : 'false')),
                    'data-testid': 'hud-facilitator-modal-row',
                    'data-selected': item.at('selected').map((v) => String(v)),
                  },
                  [
                    uiButton({
                      variant: 'ghost',
                      className: 'hud-facilitator-modal-row-btn',
                      onClick: () => {
                        const row = item.peek();
                        send({ type: 'facilitator/select-user', userId: row.user_id });
                      },
                      children: [text(item.at('email'))],
                    }),
                  ],
                ),
              ],
            },
          ),
        ],
      ),
      // Confirm / cancel actions.
      div({ class: 'hud-facilitator-modal-actions' }, [
        uiButton({
          variant: 'primary',
          className: 'hud-facilitator-modal-confirm',
          testid: 'hud-facilitator-modal-confirm',
          disabled: state.map((s) => s.facilitatorModal?.selectedUserId == null),
          onClick: () => {
            const s = state.peek();
            const modal = s.facilitatorModal;
            if (!modal?.selectedUserId) return;
            if (modal.mode === 'handoff') {
              send({ type: 'facilitator/hand-off', toUserId: modal.selectedUserId });
            } else {
              send({ type: 'facilitator/designate', userId: modal.selectedUserId });
            }
          },
          children: [
            text(
              state.map((s) =>
                s.facilitatorModal?.mode === 'handoff'
                  ? t('hud.facilitator.hand_off_confirm')
                  : t('hud.facilitator.designate_confirm'),
              ),
            ),
          ],
        }),
        uiButton({
          variant: 'secondary',
          className: 'hud-facilitator-modal-cancel',
          testid: 'hud-facilitator-modal-cancel',
          onClick: () => send({ type: 'facilitator/close-modal' }),
          children: [
            text(
              state.map((s) =>
                s.facilitatorModal?.mode === 'handoff'
                  ? t('hud.facilitator.hand_off_cancel')
                  : t('hud.facilitator.designate_cancel'),
              ),
            ),
          ],
        }),
      ]),
    ],
  );
}

// ---------- Per-attribute reveal (Phase 2 / 30 §7.7) ----------
//
// Bare-Identifier accessors so the llui dep analyzer traces the reactive
// reads through `s.revealMenu` + `s.entities`. The Viewer-Matrix popover
// lists a fact's current reveals and the remaining campaign Viewers it can
// still be revealed to.

/** The FactState the open reveal-menu targets, or undefined. */
function revealMenuFact(s: Model): FactState | undefined {
  const m = s.revealMenu;
  if (!m) return undefined;
  return s.entities[m.entityId]?.facts[m.predicate];
}

/** Popover heading: "<EntityName> · <Predicate label>". */
function revealMenuTitle(s: Model): string {
  const m = s.revealMenu;
  if (!m) return '';
  const entity = s.entities[m.entityId];
  const entityName =
    (entity && (currentFactValue(entity.facts.name) as string | undefined)) || m.entityId;
  return `${entityName} · ${predicateLabel(m.predicate)}`;
}

/** Build viewerKey → display label from the campaign roster. */
function revealLabelByViewerKey(s: Model): Map<string, string> {
  const map = new Map<string, string>();
  for (const opt of campaignViewers(s.entities)) map.set(viewerKeyFn(opt.viewer), opt.label);
  return map;
}

interface RevealMenuRow {
  key: string;
  label: string;
  viewerKind: ViewerId['kind'];
  viewerId: string;
}

/** Viewers this fact is ALREADY revealed to (labelled), op-log order. */
function revealMenuRevealedRows(s: Model): RevealMenuRow[] {
  const fact = revealMenuFact(s);
  if (!fact) return [];
  const labels = revealLabelByViewerKey(s);
  return fact.reveals.map((v) => {
    const key = viewerKeyFn(v);
    return { key, label: labels.get(key) ?? v.id, viewerKind: v.kind, viewerId: v.id };
  });
}

/** Remaining campaign Viewers this fact can still be revealed to. */
function revealMenuCandidateRows(s: Model): RevealMenuRow[] {
  const fact = revealMenuFact(s);
  const already = new Set((fact?.reveals ?? []).map(viewerKeyFn));
  return campaignViewers(s.entities)
    .filter((opt) => !already.has(viewerKeyFn(opt.viewer)))
    .map((opt) => ({
      key: viewerKeyFn(opt.viewer),
      label: opt.label,
      viewerKind: opt.viewer.kind,
      viewerId: opt.viewer.id,
    }));
}

/** True when the campaign has no Viewers to reveal to yet (no Characters). */
function revealMenuHasNoViewers(s: Model): boolean {
  return campaignViewers(s.entities).length === 0;
}

/** True when every candidate Viewer has already been revealed to. */
function revealMenuAllRevealed(s: Model): boolean {
  return !revealMenuHasNoViewers(s) && revealMenuCandidateRows(s).length === 0;
}

/**
 * Viewer-Matrix popover (30 §7.7). Lists the fact's current reveals (direct)
 * and a one-click picker of the remaining campaign Viewers. Reveals are
 * write-once, so the picker only ADDS — there is no un-reveal control. The
 * popover stays open across reveals so the GM can target several Viewers in
 * one pass; the optimistic local fold moves each picked Viewer from the
 * picker into the "Visible to" list as its op lands.
 */
function viewRevealMenu(state: Signal<Model>, send: Send<Msg>): Mountable {
  return div({ 'data-testid': 'reveal-menu-host' }, [
    modal({
      open: state.map((s) => s.revealMenu !== null),
      onDismiss: () => send({ type: 'close-reveal-menu' }),
      labelId: 'reveal-menu-heading',
      panelClass: 'reveal-menu',
      size: 'sm',
      testid: 'reveal-menu',
      labels: { close: t('reveal.menu.close') },
      children: [
        h3({ id: 'reveal-menu-heading', class: 'reveal-menu-title' }, [
          text(state.map(revealMenuTitle)),
        ]),
        // Currently visible-to list (direct reveals).
        section({ class: 'reveal-menu-section', 'aria-label': t('reveal.menu.visible_to') }, [
          h4({ class: 'reveal-menu-subhead' }, [text(t('reveal.menu.visible_to'))]),
          show(
            state.map((s) => revealMenuRevealedRows(s).length === 0),
            () => [
              emptyState({
                testid: 'reveal-menu-hidden',
                message: text(t('reveal.menu.hidden_from_all')),
              }),
            ],
          ),
          ul(
            { class: 'reveal-menu-revealed-list', 'data-testid': 'reveal-menu-revealed' },
            [
              each(state.map((s) => revealMenuRevealedRows(s)), {
                key: (row) => row.key,
                render: (item) => [
                  li({ class: 'reveal-menu-revealed-row' }, [
                    span({ class: 'reveal-menu-viewer-name' }, [text(item.at('label'))]),
                  ]),
                ],
              }),
            ],
          ),
        ]),
        // Reveal-to picker (remaining Viewers).
        section({ class: 'reveal-menu-section', 'aria-label': t('reveal.menu.reveal_to') }, [
          h4({ class: 'reveal-menu-subhead' }, [text(t('reveal.menu.reveal_to'))]),
          show(
            state.map((s) => revealMenuHasNoViewers(s)),
            () => [
              emptyState({
                testid: 'reveal-menu-no-viewers',
                message: text(t('reveal.menu.no_viewers')),
              }),
            ],
          ),
          show(
            state.map((s) => revealMenuAllRevealed(s)),
            () => [
              emptyState({
                testid: 'reveal-menu-all-revealed',
                message: text(t('reveal.menu.all_revealed')),
              }),
            ],
          ),
          ul(
            { class: 'reveal-menu-candidate-list', 'data-testid': 'reveal-menu-candidates' },
            [
              each(state.map((s) => revealMenuCandidateRows(s)), {
                key: (row) => row.key,
                render: (item) => [
                  li({ class: 'reveal-menu-candidate-row' }, [
                    uiButton({
                      variant: 'ghost',
                      className: 'reveal-menu-reveal-btn',
                      testid: 'reveal-menu-reveal-btn',
                      onClick: () => {
                        const m = state.peek().revealMenu;
                        if (!m) return;
                        send({
                          type: 'reveal-fact',
                          entityId: m.entityId,
                          predicate: m.predicate,
                          viewer: {
                            kind: item.at('viewerKind').peek(),
                            id: item.at('viewerId').peek(),
                          },
                        });
                      },
                      children: [text(item.at('label'))],
                    }),
                  ]),
                ],
              }),
            ],
          ),
        ]),
        div({ class: 'reveal-menu-footer' }, [
          uiButton({
            variant: 'ghost',
            className: 'reveal-menu-done',
            testid: 'reveal-menu-done',
            onClick: () => send({ type: 'close-reveal-menu' }),
            children: [text(t('reveal.menu.done'))],
          }),
        ]),
      ],
    }),
  ]);
}

// ---------- Routine facet bulk-reveal (Phase 2 / 30 §7.10.2) ----------
//
// Bare-Identifier accessors over `s.entities` + `s.facetRevealMenu`. A safe
// facet (`safe_to_bulk_reveal: true`) reveals all its facts to a Viewer in
// one pass; sensitive facets route through the Composer (3c).

/** The entity's resolved facets (templates from its traits × valued facts). */
function entityFacetsFor(s: Model, entityId: string): ResolvedFacet[] {
  const entity = s.entities[entityId];
  if (!entity) return [];
  return entityFacets(entity, facetTemplatesForTraits(entity.traits), predicateDefaultFacet);
}

interface FacetChipRow {
  key: string;
  facetId: string;
  label: string;
  factCount: number;
  predicates: string[];
  /** true → one-click reveal (§7.10.2); false → gated Composer (§7.10.3). */
  safe: boolean;
}

/** All facet chips for the entity's Reveals-tab bulk-reveal bar. */
function facetChipRows(s: Model, entityId: string): FacetChipRow[] {
  return entityFacetsFor(s, entityId).map((f) => ({
    key: f.id,
    facetId: f.id,
    label: resolveLocalizedDisplay(f.label) || f.id,
    factCount: f.predicates.length,
    predicates: f.predicates,
    safe: f.safe_to_bulk_reveal,
  }));
}

/** True when the entity has ≥1 facet to bulk-reveal (safe or sensitive). */
function entityHasFacetChips(s: Model, entityId: string): boolean {
  return facetChipRows(s, entityId).length > 0;
}

/** Viewer keys that already see EVERY fact in the open facet. */
function facetRevealedViewerKeys(s: Model): Set<string> {
  const out = new Set<string>();
  const m = s.facetRevealMenu;
  if (!m) return out;
  const entity = s.entities[m.entityId];
  if (!entity || m.predicates.length === 0) return out;
  for (const opt of campaignViewers(s.entities)) {
    const vk = viewerKeyFn(opt.viewer);
    const seesAll = m.predicates.every((p) =>
      (entity.facts[p]?.reveals ?? []).some((v) => viewerKeyFn(v) === vk),
    );
    if (seesAll) out.add(vk);
  }
  return out;
}

/** Viewers who already see the whole facet (the "Visible to" list). */
function facetRevealFullRows(s: Model): RevealMenuRow[] {
  const fully = facetRevealedViewerKeys(s);
  const labels = revealLabelByViewerKey(s);
  return campaignViewers(s.entities)
    .filter((o) => fully.has(viewerKeyFn(o.viewer)))
    .map((o) => {
      const key = viewerKeyFn(o.viewer);
      return { key, label: labels.get(key) ?? o.label, viewerKind: o.viewer.kind, viewerId: o.viewer.id };
    });
}

/** Viewers who don't yet see the whole facet (the picker). */
function facetRevealCandidateRows(s: Model): RevealMenuRow[] {
  const fully = facetRevealedViewerKeys(s);
  const labels = revealLabelByViewerKey(s);
  return campaignViewers(s.entities)
    .filter((o) => !fully.has(viewerKeyFn(o.viewer)))
    .map((o) => {
      const key = viewerKeyFn(o.viewer);
      return { key, label: labels.get(key) ?? o.label, viewerKind: o.viewer.kind, viewerId: o.viewer.id };
    });
}

/** Popover heading: `Reveal "<facet>" · N facts`. */
function facetRevealMenuTitle(s: Model): string {
  const m = s.facetRevealMenu;
  if (!m) return '';
  return t('reveal.facet.title', { facet: m.label, count: String(m.predicates.length) });
}

function facetRevealMenuNoViewers(s: Model): boolean {
  return campaignViewers(s.entities).length === 0;
}

/**
 * Routine facet bulk-reveal popover (30 §7.10.2). Picking a Viewer reveals
 * every still-hidden fact in the facet to them at once (`source.kind='facet'`).
 * Mirrors the per-fact Viewer-Matrix but operates on the whole facet.
 */
function viewFacetRevealMenu(state: Signal<Model>, send: Send<Msg>): Mountable {
  return div({ 'data-testid': 'facet-reveal-menu-host' }, [
    modal({
      open: state.map((s) => s.facetRevealMenu !== null),
      onDismiss: () => send({ type: 'close-facet-reveal-menu' }),
      labelId: 'facet-reveal-menu-heading',
      panelClass: 'reveal-menu',
      size: 'sm',
      testid: 'facet-reveal-menu',
      labels: { close: t('reveal.menu.close') },
      children: [
        h3({ id: 'facet-reveal-menu-heading', class: 'reveal-menu-title' }, [
          text(state.map(facetRevealMenuTitle)),
        ]),
        section({ class: 'reveal-menu-section', 'aria-label': t('reveal.menu.visible_to') }, [
          h4({ class: 'reveal-menu-subhead' }, [text(t('reveal.menu.visible_to'))]),
          show(
            state.map((s) => facetRevealFullRows(s).length === 0),
            () => [
              emptyState({
                testid: 'facet-reveal-menu-none',
                message: text(t('reveal.menu.hidden_from_all')),
              }),
            ],
          ),
          ul({ class: 'reveal-menu-revealed-list', 'data-testid': 'facet-reveal-menu-revealed' }, [
            each(state.map((s) => facetRevealFullRows(s)), {
              key: (row) => row.key,
              render: (item) => [
                li({ class: 'reveal-menu-revealed-row' }, [
                  span({ class: 'reveal-menu-viewer-name' }, [text(item.at('label'))]),
                ]),
              ],
            }),
          ]),
        ]),
        section({ class: 'reveal-menu-section', 'aria-label': t('reveal.menu.reveal_to') }, [
          h4({ class: 'reveal-menu-subhead' }, [text(t('reveal.menu.reveal_to'))]),
          show(
            state.map((s) => facetRevealMenuNoViewers(s)),
            () => [
              emptyState({
                testid: 'facet-reveal-menu-no-viewers',
                message: text(t('reveal.menu.no_viewers')),
              }),
            ],
          ),
          ul({ class: 'reveal-menu-candidate-list', 'data-testid': 'facet-reveal-menu-candidates' }, [
            each(state.map((s) => facetRevealCandidateRows(s)), {
              key: (row) => row.key,
              render: (item) => [
                li({ class: 'reveal-menu-candidate-row' }, [
                  uiButton({
                    variant: 'ghost',
                    className: 'reveal-menu-reveal-btn',
                    testid: 'facet-reveal-menu-reveal-btn',
                    onClick: () => {
                      const m = state.peek().facetRevealMenu;
                      if (!m) return;
                      send({
                        type: 'reveal-facet',
                        entityId: m.entityId,
                        predicates: m.predicates,
                        viewer: {
                          kind: item.at('viewerKind').peek(),
                          id: item.at('viewerId').peek(),
                        },
                      });
                    },
                    children: [text(item.at('label'))],
                  }),
                ]),
              ],
            }),
          ]),
        ]),
        div({ class: 'reveal-menu-footer' }, [
          uiButton({
            variant: 'ghost',
            className: 'reveal-menu-done',
            testid: 'facet-reveal-menu-done',
            onClick: () => send({ type: 'close-facet-reveal-menu' }),
            children: [text(t('reveal.menu.done'))],
          }),
        ]),
      ],
    }),
  ]);
}

// ---------- Reveal Composer for sensitive facets (Phase 2 / 30 §7.10.3) ----------
//
// Bare-Identifier accessors over `s.revealComposer` + `s.entities`. Sensitive
// facets (`safe_to_bulk_reveal: false`) gate behind explicit preview: pick a
// recipient, review the per-fact diff (exclude any), confirm. Row state
// (`included` / `alreadyVisible`) is precomputed into the row data so the
// `each` body never reads component state directly (the nested-row caveat).

function composerValuePreview(value: unknown): string {
  if (value === null || value === undefined) return '';
  const s = typeof value === 'string' ? value : JSON.stringify(value);
  return s.length > 48 ? `${s.slice(0, 47)}…` : s;
}

interface ComposerViewerRow {
  key: string;
  label: string;
  viewerKind: ViewerId['kind'];
  viewerId: string;
  selected: boolean;
}

/** Recipient options for the Composer (campaign Viewers + selected flag). */
function composerViewerRows(s: Model): ComposerViewerRow[] {
  const c = s.revealComposer;
  if (!c) return [];
  const labels = revealLabelByViewerKey(s);
  const sel = c.viewer ? viewerKeyFn(c.viewer) : null;
  return campaignViewers(s.entities).map((o) => {
    const key = viewerKeyFn(o.viewer);
    return {
      key,
      label: labels.get(key) ?? o.label,
      viewerKind: o.viewer.kind,
      viewerId: o.viewer.id,
      selected: key === sel,
    };
  });
}

interface ComposerFactRow {
  key: string;
  predicate: string;
  label: string;
  valuePreview: string;
  alreadyVisible: boolean;
  included: boolean;
}

/** Per-fact diff rows for the Composer preview. */
function composerFactRows(s: Model): ComposerFactRow[] {
  const c = s.revealComposer;
  if (!c) return [];
  const entity = s.entities[c.entityId];
  const viewerK = c.viewer ? viewerKeyFn(c.viewer) : null;
  return c.predicates.map((predicate) => {
    const fact = entity?.facts[predicate];
    const value = fact ? currentFactValue(fact) : undefined;
    const alreadyVisible = !!(viewerK && fact?.reveals.some((v) => viewerKeyFn(v) === viewerK));
    return {
      key: predicate,
      predicate,
      label: predicateLabel(predicate),
      valuePreview: composerValuePreview(value),
      alreadyVisible,
      included: !c.excluded.includes(predicate) && !alreadyVisible && value !== undefined,
    };
  });
}

/** How many facts the confirm will reveal (included, recipient chosen). */
function composerRevealCount(s: Model): number {
  if (!s.revealComposer?.viewer) return 0;
  return composerFactRows(s).filter((r) => r.included).length;
}

function composerHasViewer(s: Model): boolean {
  return s.revealComposer?.viewer != null;
}

function composerTitle(s: Model): string {
  const c = s.revealComposer;
  return c ? t('reveal.composer.title', { facet: c.label }) : '';
}

function composerConfirmLabel(s: Model): string {
  return t('reveal.composer.confirm', { count: String(composerRevealCount(s)) });
}

/**
 * Reveal Composer (30 §7.10.3). A gated modal for sensitive facets: pick a
 * recipient, preview every fact that would become visible (exclude any),
 * then commit. Nothing reveals until "Reveal N facts" is pressed.
 */
function viewRevealComposer(state: Signal<Model>, send: Send<Msg>): Mountable {
  return div({ 'data-testid': 'reveal-composer-host' }, [
    modal({
      open: state.map((s) => s.revealComposer !== null),
      onDismiss: () => send({ type: 'close-reveal-composer' }),
      labelId: 'reveal-composer-heading',
      testid: 'reveal-composer',
      size: 'md',
      panelClass: 'reveal-composer',
      labels: { close: t('reveal.menu.close') },
      children: [
        h3({ id: 'reveal-composer-heading', class: 'reveal-composer-title' }, [
          text(state.map(composerTitle)),
        ]),
        p({ class: 'reveal-composer-gate' }, [text(t('reveal.composer.gate'))]),
        // Step 1 — recipient.
        section({ class: 'reveal-composer-section', 'aria-label': t('reveal.composer.recipient') }, [
          h4({ class: 'reveal-menu-subhead' }, [text(t('reveal.composer.recipient'))]),
          show(
            state.map((s) => composerViewerRows(s).length === 0),
            () => [
              emptyState({
                testid: 'reveal-composer-no-viewers',
                message: text(t('reveal.menu.no_viewers')),
              }),
            ],
          ),
          div({ class: 'reveal-composer-recipients', 'data-testid': 'reveal-composer-recipients' }, [
            each(state.map((s) => composerViewerRows(s)), {
              key: (row) => row.key,
              render: (item) => [
                uiButton({
                  variant: 'ghost',
                  className: item.map((r) =>
                    r.selected
                      ? 'reveal-composer-recipient is-selected'
                      : 'reveal-composer-recipient',
                  ),
                  testid: 'reveal-composer-recipient',
                  attrs: {
                    'aria-pressed': item.map((r) => (r.selected ? 'true' : 'false')),
                  },
                  onClick: () =>
                    send({
                      type: 'composer-set-viewer',
                      viewer: {
                        kind: item.at('viewerKind').peek(),
                        id: item.at('viewerId').peek(),
                      },
                    }),
                  children: [text(item.at('label'))],
                }),
              ],
            }),
          ]),
        ]),
        // Step 2 — diff preview.
        section({ class: 'reveal-composer-section', 'aria-label': t('reveal.composer.preview') }, [
          h4({ class: 'reveal-menu-subhead' }, [text(t('reveal.composer.preview'))]),
          ul({ class: 'reveal-composer-facts', 'data-testid': 'reveal-composer-facts' }, [
            each(state.map((s) => composerFactRows(s)), {
              key: (row) => row.key,
              render: (item) => [
                li({ class: 'reveal-composer-fact', 'data-testid': 'reveal-composer-fact' }, [
                  label({ class: 'reveal-composer-fact-label' }, [
                    input({
                      type: 'checkbox',
                      class: 'reveal-composer-fact-check',
                      'data-testid': 'reveal-composer-fact-check',
                      checked: item.at('included'),
                      disabled: item.at('alreadyVisible'),
                      onChange: () =>
                        send({ type: 'composer-toggle-fact', predicate: item.at('predicate').peek() }),
                    }),
                    span({ class: 'reveal-composer-fact-name' }, [text(item.at('label'))]),
                    span({ class: 'reveal-composer-fact-value' }, [text(item.at('valuePreview'))]),
                  ]),
                  show(
                    item.at('alreadyVisible'),
                    () => [
                      span({ class: 'reveal-composer-fact-annot' }, [
                        text(t('reveal.composer.already_visible')),
                      ]),
                    ],
                  ),
                ]),
              ],
            }),
          ]),
        ]),
        // Actions.
        div({ class: 'reveal-composer-actions' }, [
          uiButton({
            variant: 'ghost',
            className: 'reveal-composer-cancel',
            testid: 'reveal-composer-cancel',
            onClick: () => send({ type: 'close-reveal-composer' }),
            children: [text(t('reveal.composer.cancel'))],
          }),
          uiButton({
            variant: 'primary',
            className: 'reveal-composer-confirm',
            testid: 'reveal-composer-confirm',
            disabled: state.map((s) => !composerHasViewer(s) || composerRevealCount(s) === 0),
            onClick: () => send({ type: 'composer-confirm' }),
            children: [text(state.map(composerConfirmLabel))],
          }),
        ]),
      ],
    }),
  ]);
}

// ---------- Belief layer (Phase 2 / 03 §9) ----------
//
// Bare-Identifier accessors over `s.entities` + `s.beliefModal`. A belief is a
// fact on the entity, held by a Viewer, keyed `<predicate>#belief:<viewerKey>`
// (B1). The GM authors them here and sees them as attributed entries; each
// belief projects into ONLY its believer's perspective (over canon).

interface BeliefRow {
  key: string;
  base: string;
  believerKey: string;
  believerLabel: string;
  predicateLabel: string;
  value: string;
  /** When cloned, the source believer's display label (provenance "via …"). */
  via: string;
  /** True once the believer has been marked as having learned the truth (B4). */
  corrected: boolean;
}

/** Authored beliefs on the entity → believer + predicate + value rows.
 *  ADR-0005 §8: a belief is a SEGMENT whose owner is a non-canon Viewer — the
 *  believer is read off `fact.owner`, never a key suffix; the base is the
 *  segment key's base. "Corrected" = the belief segment is closed (`to` set). */
function entityBeliefRows(s: Model, entityId: string): BeliefRow[] {
  const entity = s.entities[entityId];
  if (!entity) return [];
  const labels = new Map<string, string>();
  for (const opt of campaignViewers(s.entities)) labels.set(viewerKeyFn(opt.viewer), opt.label);
  const out: BeliefRow[] = [];
  for (const [factKeyStr, fact] of Object.entries(entity.facts)) {
    if (fact.owner.kind !== 'belief') continue;
    const value = currentFactValue(fact);
    if (value === undefined) continue;
    const base = splitSegmentKey(factKeyStr).base;
    const believerKey = viewerKeyFn(fact.owner.viewer);
    // Provenance: look up the source belief fact's OWNER for the "via" label
    // (the source key is an opaque segment key now, not a believer-bearing key).
    let via = '';
    if (fact.source_belief) {
      const src = entity.facts[fact.source_belief];
      if (src && src.owner.kind === 'belief') {
        const srcKey = viewerKeyFn(src.owner.viewer);
        via = labels.get(srcKey) ?? srcKey;
      }
    }
    out.push({
      key: factKeyStr,
      base,
      believerKey,
      believerLabel: labels.get(believerKey) ?? believerKey,
      predicateLabel: predicateLabel(base),
      value: typeof value === 'string' ? value : JSON.stringify(value),
      via,
      corrected: fact.validity?.to !== undefined,
    });
  }
  out.sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));
  return out;
}

interface BeliefCloneRow {
  key: string;
  label: string;
  viewerKind: ViewerId['kind'];
  viewerId: string;
}

/** Viewers a belief can be cloned to (campaign Viewers who don't already hold
 *  it and aren't the source believer). */
function beliefCloneCandidateRows(s: Model): BeliefCloneRow[] {
  const c = s.beliefCloneMenu;
  if (!c) return [];
  const entity = s.entities[c.entityId];
  const held = new Set<string>();
  if (entity) {
    for (const [k, f] of Object.entries(entity.facts)) {
      if (f.owner.kind !== 'belief') continue;
      if (splitSegmentKey(k).base === c.base && currentFactValue(f) !== undefined) {
        held.add(viewerKeyFn(f.owner.viewer));
      }
    }
  }
  return campaignViewers(s.entities)
    .filter((o) => {
      const vk = viewerKeyFn(o.viewer);
      return vk !== c.sourceBelieverKey && !held.has(vk);
    })
    .map((o) => ({
      key: viewerKeyFn(o.viewer),
      label: o.label,
      viewerKind: o.viewer.kind,
      viewerId: o.viewer.id,
    }));
}

function beliefCloneTitle(s: Model): string {
  const c = s.beliefCloneMenu;
  return c ? t('belief.clone.title', { value: c.value }) : '';
}

function beliefCloneNoCandidates(s: Model): boolean {
  return s.beliefCloneMenu != null && beliefCloneCandidateRows(s).length === 0;
}

/** True when the entity has ≥1 authored belief. */
function entityHasBeliefs(s: Model, entityId: string): boolean {
  return entityBeliefRows(s, entityId).length > 0;
}

interface BeliefBelieverRow {
  key: string;
  label: string;
  viewerKind: ViewerId['kind'];
  viewerId: string;
  selected: boolean;
}

/** Believer (Viewer) options for the Draft-belief modal. */
function beliefBelieverRows(s: Model): BeliefBelieverRow[] {
  const b = s.beliefModal;
  if (!b) return [];
  const sel = b.believer ? viewerKeyFn(b.believer) : null;
  // §7.3 — a player may only author beliefs owned by a PC they control, so the
  // believer list is built from the SESSION's `controlledCharacters` (always
  // available, unlike the sparse player replica) rather than `campaignViewers`,
  // which the GM uses to attribute a belief to any Viewer.
  if (s.session.mode === 'player') {
    return s.session.controlledCharacters.map((id) => {
      const viewer: ViewerId = { kind: 'character', id };
      const key = viewerKeyFn(viewer);
      return {
        key,
        label: controlledPcName(s, id),
        viewerKind: 'character',
        viewerId: id,
        selected: key === sel,
      };
    });
  }
  return campaignViewers(s.entities).map((o) => {
    const key = viewerKeyFn(o.viewer);
    return {
      key,
      label: o.label,
      viewerKind: o.viewer.kind,
      viewerId: o.viewer.id,
      selected: key === sel,
    };
  });
}

/**
 * §7.3 — display name for a controlled PC, resolved from the value-only
 * `projectedFacts` name (the player's replica is sparse, so `entities` may not
 * hold the PC). Falls back to the entity id.
 */
function controlledPcName(s: Model, id: string): string {
  const row = s.projectedFacts[factKey(id, FACT_NAME)];
  return typeof row?.value === 'string' && row.value !== '' ? row.value : id;
}

interface BeliefPredicateRow {
  key: string;
  predicate: string;
  label: string;
  selected: boolean;
}

/** Base-predicate options for the Draft-belief modal (the entity's predicates). */
function beliefPredicateRows(s: Model): BeliefPredicateRow[] {
  const b = s.beliefModal;
  if (!b) return [];
  const registry = loadGmRegistry();
  const ids = predicateIdsForTraits(traitsAtId(s, b.entityId), registry, getTraitIndex());
  return ['name', ...ids]
    .filter((p, i, arr) => arr.indexOf(p) === i)
    .map((p) => ({ key: p, predicate: p, label: predicateLabel(p), selected: p === b.predicate }));
}

function beliefModalValue(s: Model): string {
  return s.beliefModal?.value ?? '';
}

function beliefModalCanSubmit(s: Model): boolean {
  const b = s.beliefModal;
  return !!b && b.believer != null && b.predicate !== '' && b.value.trim() !== '';
}

function beliefModalNoViewers(s: Model): boolean {
  // §7.3 — for a player the believer set is their controlled PCs (session
  // truth), not the campaign-wide viewer list derived from the sparse replica.
  if (s.session.mode === 'player') return s.session.controlledCharacters.length === 0;
  return campaignViewers(s.entities).length === 0;
}

/**
 * Beliefs section on the Facts tab (03 §9). A "Draft belief" button opens the
 * authoring modal; below it, the entity's authored beliefs list as attributed
 * entries ("<Believer> believes <predicate>: <value>"). Beliefs are never
 * canon — each only reaches its believer's perspective.
 */
function viewBeliefsSection(state: Signal<Model>, send: Send<Msg>, entityId: string): Mountable {
  return section(
    { class: 'entity-beliefs-section', 'data-testid': 'entity-beliefs', 'aria-label': t('belief.section.heading') },
    [
      // level 2 (not 3): the entity title is the page h1 and the Facts tab
      // has no intervening h2, so an h3 here would skip a level (axe
      // `heading-order`). Beliefs is a top-level section of the tab.
      sectionHeading({
        title: text(t('belief.section.heading')),
        level: 2,
        actions: uiButton({
          variant: 'secondary',
          className: 'entity-beliefs-add',
          testid: 'belief-draft-button',
          onClick: () => send({ type: 'open-belief-modal', entityId }),
          children: [text(t('belief.section.draft'))],
        }),
      }),
      show(
        state.map((s) => !entityHasBeliefs(s, entityId)),
        () => [
          emptyState({
            testid: 'belief-empty',
            message: text(t('belief.section.empty')),
          }),
        ],
      ),
      ul({ class: 'entity-beliefs-list', 'data-testid': 'belief-list' }, [
        each(state.map((s) => entityBeliefRows(s, entityId)), {
          key: (row) => row.key,
          render: (item) => [
            li({ class: 'entity-belief-row', 'data-testid': 'belief-row' }, [
              span({ class: 'entity-belief-believer' }, [text(item.at('believerLabel'))]),
              span({ class: 'entity-belief-verb' }, [text(t('belief.row.believes'))]),
              span({ class: 'entity-belief-predicate' }, [text(item.at('predicateLabel'))]),
              span({ class: 'entity-belief-sep' }, [': ']),
              span({ class: 'entity-belief-value' }, [text(item.at('value'))]),
              // Provenance — "via <source believer>" when this belief was cloned.
              show(
                item.map((r) => r.via !== ''),
                () => [
                  span({ class: 'entity-belief-via', 'data-testid': 'belief-via' }, [
                    text(item.map((r) => t('belief.row.via', { source: r.via }))),
                  ]),
                ],
              ),
              // Corrected beliefs stay as history but no longer apply — a badge
              // REPLACES the clone / correct actions. One `branch`, not two
              // sibling `show`s, and that is load-bearing rather than stylistic.
              //
              // As two `show`s this dropped the badge intermittently (~20% —
              // e2e 23.4, long carried as a flake). Both blocks flip in the
              // same commit: one removes the two buttons, the other inserts the
              // badge. Measured in the failing state, an attribute bound to the
              // SAME `item.at('corrected')` signal read `yes` while the badge's
              // `show` had inserted nothing — so the signal was right and the
              // structural insert was lost, next to a sibling removal. The row
              // ended up with neither the actions nor the badge: a belief the
              // GM had just corrected looked uncorrected and offered no way to
              // correct it again.
              //
              // A `branch` states what is actually true — the two states are
              // mutually exclusive — and gives one structural block per row
              // instead of two that can disagree.
              branch(
                item.at('corrected').map((c) => (c ? 'corrected' : 'active')),
                {
                  corrected: () => [
                    span({ class: 'entity-belief-corrected', 'data-testid': 'belief-corrected' }, [
                      text(t('belief.row.corrected')),
                    ]),
                  ],
                  active: () => [
                    uiButton({
                      variant: 'ghost',
                      className: 'entity-belief-clone',
                      testid: 'belief-clone-button',
                      onClick: () =>
                        send({
                          type: 'open-belief-clone',
                          entityId,
                          sourceKey: item.at('key').peek(),
                          base: item.at('base').peek(),
                          value: item.at('value').peek(),
                          sourceBelieverKey: item.at('believerKey').peek(),
                        }),
                      children: [text(t('belief.row.clone'))],
                    }),
                    uiButton({
                      variant: 'ghost',
                      className: 'entity-belief-correct',
                      testid: 'belief-correct-button',
                      onClick: () =>
                        send({
                          type: 'mark-belief-corrected',
                          entityId,
                          beliefKey: item.at('key').peek(),
                          value: item.at('value').peek(),
                        }),
                      children: [text(t('belief.row.correct'))],
                    }),
                  ],
                },
              ),
            ]),
          ],
        }),
      ]),
    ],
  );
}

/**
 * Draft-belief modal (03 §9). The GM picks a believing Viewer, a base
 * predicate, and a value; on submit a belief fact is authored that only the
 * believer (and the GM) will perceive, overlaying canon in their view.
 */
function viewBeliefModal(state: Signal<Model>, send: Send<Msg>): Mountable {
  return div({ 'data-testid': 'belief-modal-host' }, [
    modal({
      open: state.map((s) => s.beliefModal !== null),
      onDismiss: () => send({ type: 'close-belief-modal' }),
      labelId: 'belief-modal-heading',
      testid: 'belief-modal',
      size: 'md',
      panelClass: 'belief-modal',
      labels: { close: t('reveal.menu.close') },
      children: [
        h3({ id: 'belief-modal-heading', class: 'belief-modal-title' }, [
          text(t('belief.modal.title')),
        ]),
        p({ class: 'belief-modal-gate' }, [text(t('belief.modal.help'))]),
        show(
          state.map((s) => beliefModalNoViewers(s)),
          () => [
            emptyState({
              testid: 'belief-modal-no-viewers',
              message: text(t('belief.modal.no_viewers')),
            }),
          ],
        ),
        // Believer.
        section({ class: 'belief-modal-section', 'aria-label': t('belief.modal.believer') }, [
          h4({ class: 'reveal-menu-subhead' }, [text(t('belief.modal.believer'))]),
          div({ class: 'belief-modal-believers', 'data-testid': 'belief-modal-believers' }, [
            each(state.map((s) => beliefBelieverRows(s)), {
              key: (row) => row.key,
              render: (item) => [
                uiButton({
                  variant: 'ghost',
                  className: item.map((r) =>
                    r.selected ? 'belief-modal-believer is-selected' : 'belief-modal-believer',
                  ),
                  testid: 'belief-modal-believer',
                  attrs: {
                    'aria-pressed': item.map((r) => (r.selected ? 'true' : 'false')),
                  },
                  onClick: () =>
                    send({
                      type: 'belief-set-believer',
                      viewer: { kind: item.at('viewerKind').peek(), id: item.at('viewerId').peek() },
                    }),
                  children: [text(item.at('label'))],
                }),
              ],
            }),
          ]),
        ]),
        // Predicate.
        section({ class: 'belief-modal-section', 'aria-label': t('belief.modal.predicate') }, [
          h4({ class: 'reveal-menu-subhead' }, [text(t('belief.modal.predicate'))]),
          div({ class: 'belief-modal-predicates', 'data-testid': 'belief-modal-predicates' }, [
            each(state.map((s) => beliefPredicateRows(s)), {
              key: (row) => row.key,
              render: (item) => [
                uiButton({
                  variant: 'ghost',
                  className: item.map((r) =>
                    r.selected ? 'belief-modal-predicate is-selected' : 'belief-modal-predicate',
                  ),
                  testid: 'belief-modal-predicate',
                  attrs: {
                    'data-predicate': item.at('predicate'),
                    'aria-pressed': item.map((r) => (r.selected ? 'true' : 'false')),
                  },
                  onClick: () =>
                    send({ type: 'belief-set-predicate', predicate: item.at('predicate').peek() }),
                  children: [text(item.at('label'))],
                }),
              ],
            }),
          ]),
        ]),
        // Value.
        section({ class: 'belief-modal-section', 'aria-label': t('belief.modal.value') }, [
          h4({ class: 'reveal-menu-subhead' }, [text(t('belief.modal.value'))]),
          textInput({
            className: 'belief-modal-value-input',
            testid: 'belief-modal-value',
            value: state.map(beliefModalValue),
            onInput: (value: string) => send({ type: 'belief-set-value', value }),
          }),
        ]),
        div({ class: 'belief-modal-actions' }, [
          uiButton({
            variant: 'ghost',
            className: 'belief-modal-cancel',
            testid: 'belief-modal-cancel',
            onClick: () => send({ type: 'close-belief-modal' }),
            children: [text(t('belief.modal.cancel'))],
          }),
          uiButton({
            variant: 'primary',
            className: 'belief-modal-confirm',
            testid: 'belief-modal-confirm',
            disabled: state.map((s) => !beliefModalCanSubmit(s)),
            onClick: () => send({ type: 'submit-belief' }),
            children: [text(t('belief.modal.confirm'))],
          }),
        ]),
      ],
    }),
  ]);
}

/**
 * Clone-a-lie picker (03 §9 / B3). Picking a target Viewer clones the source
 * belief to them — same value, with a `source_belief` pointer back to the
 * origin — so misinformation propagates with a traceable provenance chain.
 */
function viewBeliefCloneMenu(state: Signal<Model>, send: Send<Msg>): Mountable {
  return div({ 'data-testid': 'belief-clone-menu-host' }, [
    modal({
      open: state.map((s) => s.beliefCloneMenu !== null),
      onDismiss: () => send({ type: 'close-belief-clone' }),
      labelId: 'belief-clone-heading',
      panelClass: 'reveal-menu',
      size: 'sm',
      testid: 'belief-clone-menu',
      labels: { close: t('reveal.menu.close') },
      children: [
        h3({ id: 'belief-clone-heading', class: 'reveal-menu-title' }, [
          text(state.map(beliefCloneTitle)),
        ]),
        section({ class: 'reveal-menu-section', 'aria-label': t('belief.clone.to') }, [
          h4({ class: 'reveal-menu-subhead' }, [text(t('belief.clone.to'))]),
          show(
            state.map((s) => beliefCloneNoCandidates(s)),
            () => [
              emptyState({
                testid: 'belief-clone-none',
                message: text(t('belief.clone.none')),
              }),
            ],
          ),
          ul({ class: 'reveal-menu-candidate-list', 'data-testid': 'belief-clone-candidates' }, [
            each(state.map((s) => beliefCloneCandidateRows(s)), {
              key: (row) => row.key,
              render: (item) => [
                li({ class: 'reveal-menu-candidate-row' }, [
                  uiButton({
                    variant: 'ghost',
                    className: 'reveal-menu-reveal-btn',
                    testid: 'belief-clone-target',
                    onClick: () =>
                      send({
                        type: 'clone-belief',
                        viewer: { kind: item.at('viewerKind').peek(), id: item.at('viewerId').peek() },
                      }),
                    children: [text(item.at('label'))],
                  }),
                ]),
              ],
            }),
          ]),
        ]),
        div({ class: 'reveal-menu-footer' }, [
          uiButton({
            variant: 'ghost',
            className: 'reveal-menu-done',
            testid: 'belief-clone-done',
            onClick: () => send({ type: 'close-belief-clone' }),
            children: [text(t('reveal.menu.done'))],
          }),
        ]),
      ],
    }),
  ]);
}

// ---------- Reveals entity tab (Phase 2 / 30 §7.2/§7.7) ----------
//
// Bare-Identifier accessors so the llui dep analyzer traces the reads
// through `s.entities`. The tab is a read-only per-Viewer rollup of an
// entity's reveals; reveal *management* lives on the Facts tab.

interface RevealsTabViewerRow {
  key: string;
  viewerLabel: string;
  /** Display labels of the predicates this Viewer can see, comma-joined. */
  predicatesText: string;
}

/** Per-Viewer rollup rows for the entity (Viewer → the facts it can see). */
function revealsTabViewerRows(s: Model, entityId: string): RevealsTabViewerRow[] {
  const entity = s.entities[entityId];
  if (!entity) return [];
  const labels = new Map<string, string>();
  for (const opt of campaignViewers(s.entities)) labels.set(viewerKeyFn(opt.viewer), opt.label);
  return entityRevealRollup(entity).byViewer.map((g) => {
    const key = viewerKeyFn(g.viewer);
    return {
      key,
      viewerLabel: labels.get(key) ?? g.viewer.id,
      predicatesText: g.predicates.map(predicateLabel).join(', '),
    };
  });
}

/** Comma-joined display labels of facts revealed to nobody (GM-only). */
function revealsTabHiddenLabels(s: Model, entityId: string): string {
  const entity = s.entities[entityId];
  if (!entity) return '';
  return entityRevealRollup(entity).hiddenPredicates.map(predicateLabel).join(', ');
}

/** True when ≥1 Viewer has a reveal on this entity. */
function revealsTabHasReveals(s: Model, entityId: string): boolean {
  return revealsTabViewerRows(s, entityId).length > 0;
}

/** True when ≥1 valued fact is revealed to nobody. */
function revealsTabHasHidden(s: Model, entityId: string): boolean {
  const entity = s.entities[entityId];
  return entity ? entityRevealRollup(entity).hiddenPredicates.length > 0 : false;
}

/** True when the entity has at least one fact with a current value. */
function entityHasValuedFacts(s: Model, entityId: string): boolean {
  const entity = s.entities[entityId];
  if (!entity) return false;
  return Object.values(entity.facts).some((f) => currentFactValue(f) !== undefined);
}

/**
 * Reveals tab body — a read-only audit of what's been disclosed on this
 * entity, grouped by Viewer, plus a "hidden from all" summary. Per-fact
 * reveal management (the chip + Viewer-Matrix popover) stays on the Facts
 * tab; this tab answers "who can see what here?" at a glance.
 */
function viewRevealsTab(state: Signal<Model>, send: Send<Msg>, entityId: string): Mountable {
  void send;
  return div(
    {
      id: 'entity-page-reveals-panel',
      role: 'tabpanel',
      'aria-labelledby': 'entity-page-reveals-tab',
      tabindex: 0,
      class: 'entity-page-panel entity-page-panel--reveals',
      'data-testid': 'entity-page-panel-reveals',
    },
    [
      p({ class: 'entity-page-reveals-hint' }, [text(t('entity.tab.reveals.hint'))]),
      // Bulk reveal — one chip per facet (30 §7.10). Safe facets reveal in
      // one click via the facet popover (§7.10.2); sensitive facets (a lock
      // glyph) gate through the Reveal Composer (§7.10.3).
      show(
        state.map((s) => entityHasFacetChips(s, entityId)),
        () => [
          section({ class: 'entity-reveals-section', 'aria-label': t('reveal.facet.bulk_heading') }, [
            // level 2: the entity title is the page h1 and the Reveals tab has
            // no intervening h2, so an h3 here would skip a level (axe
            // `heading-order`).
            sectionHeading({ title: text(t('reveal.facet.bulk_heading')), level: 2 }),
            div({ class: 'entity-reveals-facet-bar', 'data-testid': 'reveals-tab-facets' }, [
              each(state.map((s) => facetChipRows(s, entityId)), {
                key: (row) => row.key,
                render: (item) => [
                  uiButton({
                    variant: 'ghost',
                    className: item.map((r) =>
                      r.safe
                        ? 'entity-reveals-facet-chip'
                        : 'entity-reveals-facet-chip is-sensitive',
                    ),
                    testid: 'reveals-tab-facet-chip',
                    attrs: {
                      'data-sensitive': item.map((r) => (r.safe ? 'false' : 'true')),
                    },
                    onClick: () => {
                      const facetId = item.at('facetId').peek();
                      const label = item.at('label').peek();
                      const predicates = item.at('predicates').peek();
                      if (item.at('safe').peek()) {
                        send({ type: 'open-facet-reveal-menu', entityId, facetId, label, predicates });
                      } else {
                        send({ type: 'open-reveal-composer', entityId, facetId, label, predicates });
                      }
                    },
                    children: [
                      show(
                        item.map((r) => !r.safe),
                        () => [span({ class: 'entity-reveals-facet-lock', 'aria-hidden': 'true' }, ['🔒'])],
                      ),
                      span({ class: 'entity-reveals-facet-name' }, [text(item.at('label'))]),
                      badge({
                        tone: 'neutral',
                        children: [
                          text(item.map((r) => t('reveal.facet.chip_count', { count: String(r.factCount) }))),
                        ],
                      }),
                    ],
                  }),
                ],
              }),
            ]),
          ]),
        ],
      ),
      // Visible-to: one row per Viewer with reveals on this entity.
      show(
        state.map((s) => revealsTabHasReveals(s, entityId)),
        () => [
          section({ class: 'entity-reveals-section', 'aria-label': t('entity.tab.reveals.visible_to') }, [
            sectionHeading({ title: text(t('entity.tab.reveals.visible_to')), level: 2 }),
            ul(
              { class: 'entity-reveals-viewer-list', 'data-testid': 'reveals-tab-viewers' },
              [
                each(state.map((s) => revealsTabViewerRows(s, entityId)), {
                  key: (row) => row.key,
                  render: (item) => [
                    li({ class: 'entity-reveals-viewer-row', 'data-testid': 'reveals-tab-viewer-row' }, [
                      span({ class: 'entity-reveals-viewer-name' }, [text(item.at('viewerLabel'))]),
                      span({ class: 'entity-reveals-viewer-preds' }, [text(item.at('predicatesText'))]),
                    ]),
                  ],
                }),
              ],
            ),
          ]),
        ],
      ),
      // Hidden from all (GM-only) — valued facts revealed to nobody.
      show(
        state.map((s) => revealsTabHasHidden(s, entityId)),
        () => [
          section(
            { class: 'entity-reveals-section', 'aria-label': t('entity.tab.reveals.hidden_heading') },
            [
              sectionHeading({ title: text(t('entity.tab.reveals.hidden_heading')), level: 2 }),
              p({ class: 'entity-reveals-hidden', 'data-testid': 'reveals-tab-hidden' }, [
                text(state.map((s) => revealsTabHiddenLabels(s, entityId))),
              ]),
            ],
          ),
        ],
      ),
      // Honest empty state — the entity has no facts to reveal yet.
      show(
        state.map((s) => !entityHasValuedFacts(s, entityId)),
        () => [
          emptyState({
            testid: 'reveals-tab-empty',
            message: text(t('entity.tab.reveals.empty')),
          }),
        ],
      ),
    ],
  );
}

/**
 * IV.B.2 (#78) — entity-action bottom-sheet. Hosts the visited-mark
 * confirmation (Phase 1 — the reveal-target picker is scaffolded but
 * its full target list is a Phase 2 surface).
 */
function viewEntitySheet(state: Signal<Model>, send: Send<Msg>): Mountable {
  return div({ 'data-testid': 'entity-sheet-host' }, [
    bottomSheet({
      open: state.map((s) => s.entitySheet.kind !== 'closed'),
      onDismiss: () => send({ type: 'close-entity-sheet' }),
      labelId: 'entity-sheet-heading',
      testid: 'entity-sheet',
      labels: {
        close: t('mobile.bottom_sheet.close'),
        dragHint: t('mobile.bottom_sheet.drag_hint'),
      },
      children: [
        h2({ id: 'entity-sheet-heading' }, [
          // Heading depends on which slice is staged. The DOM emitted
          // here is constructed once per sheet-open; the visible label
          // matches the `entitySheet.kind` at construction.
          // Note: this is constructed inside an `h.show` accessor, so
          // by the time we read the model the sheet kind is settled.
          show(
      state.map((s) => s.entitySheet.kind === 'visited'),
      () => [text(t('mobile.entity.visited_heading'))],
    ),
          show(
      state.map((s) => s.entitySheet.kind === 'reveal'),
      () => [text(t('mobile.entity.reveal_heading'))],
    ),
        ]),
        show(
      state.map((s) => s.entitySheet.kind === 'visited'),
      () => [
            p({ class: 'sheet-body' }, [text(t('mobile.entity.visited_confirm'))]),
            div({ class: 'sheet-actions' }, [
              uiButton({
                variant: 'primary',
                testid: 'entity-sheet-visited-confirm',
                onClick: () => {
                  if (!appSend) return;
                  // We need subject + target from the sheet state.
                  // Use a small accessor through the snapshot.
                  const snap = (window as unknown as {
                    __DL_SHEET_VISITED__?: { subjectId: string; targetId: string };
                  }).__DL_SHEET_VISITED__;
                  if (!snap) return;
                  appSend({
                    type: 'mark-visited',
                    subjectId: snap.subjectId,
                    targetId: snap.targetId,
                  });
                },
                children: [text(t('mobile.entity.visited_submit'))],
              }),
            ]),
          ],
    ),
        show(
      state.map((s) => s.entitySheet.kind === 'reveal'),
      () => [
            p({ class: 'sheet-body' }, [text(t('mobile.entity.reveal_help'))]),
            // Reveal-target picker scaffold. Phase 1: the substrate
            // does not yet expose a `Viewer[]` enumeration through the
            // replica, so the picker shows the empty-state copy. The
            // sheet is here so the contract (per #78 AC: "Bottom-sheet
            // for reveal-target selection (uses IV.B.1)") is met
            // structurally — the full picker lands with III.A.3.
            emptyState({
              testid: 'reveal-empty-state',
              message: text(t('mobile.entity.reveal_no_targets')),
            }),
          ],
    ),
      ],
    }),
  ]);
}

/**
 * IV.B.4 (#80) — private-notes bottom-sheet (mobile).
 *
 * Renders the same textarea as the inline `viewPrivateNotesPanel` but
 * inside the BottomSheet primitive. Autosave (2s debounce + blur)
 * dispatches `private-note/save`; the substrate handles offline
 * queueing through the replica.
 */
function viewNotesSheet(state: Signal<Model>, send: Send<Msg>): Mountable {
  return div({ 'data-testid': 'notes-sheet-host' }, [
    bottomSheet({
      open: state.at('notesSheetOpen'),
      onDismiss: () => send({ type: 'close-notes-sheet' }),
      labelId: 'notes-sheet-heading',
      testid: 'notes-sheet',
      labels: {
        close: t('mobile.bottom_sheet.close'),
        dragHint: t('mobile.bottom_sheet.drag_hint'),
      },
      children: [
        h2({ id: 'notes-sheet-heading' }, [
          text(t('mobile.entity.notes_sheet_heading')),
        ]),
        p({ class: 'sheet-body' }, [text(t('player.notes_help'))]),
        textArea({
          className: 'private-notes-textarea',
          testid: 'notes-sheet-textarea',
          ariaLabel: t('player.notes_heading'),
          value: state.map((s) => {
            const id = s.notesSheetEntityId;
            if (id === null) return '';
            return s.privateNotes[id]?.body ?? '';
          }),
          onInput: (value: string) => {
            if (!appSend) return;
            const id = (window as unknown as { __DL_NOTES_SHEET_ID__?: string })
              .__DL_NOTES_SHEET_ID__;
            if (!id) return;
            appSend({
              type: 'private-note/edit',
              entityId: id,
              body: value,
            });
            // Autosave debounce — 2s of inactivity, plus on blur.
            scheduleNotesAutosave(id);
          },
          attrs: {
            onBlur: () => {
              const id = (window as unknown as { __DL_NOTES_SHEET_ID__?: string })
                .__DL_NOTES_SHEET_ID__;
              if (!id || !appSend) return;
              flushNotesAutosave(id);
            },
          },
        }),
        div({ class: 'sheet-actions' }, [
          uiButton({
            variant: 'primary',
            testid: 'notes-sheet-save',
            onClick: () => {
              const id = (window as unknown as { __DL_NOTES_SHEET_ID__?: string })
                .__DL_NOTES_SHEET_ID__;
              if (!id || !appSend) return;
              flushNotesAutosave(id);
            },
            children: [text(t('player.notes_save'))],
          }),
        ]),
      ],
    }),
  ]);
}

let notesAutosaveTimer: ReturnType<typeof setTimeout> | undefined;
function scheduleNotesAutosave(entityId: string): void {
  clearTimeout(notesAutosaveTimer);
  notesAutosaveTimer = setTimeout(() => flushNotesAutosave(entityId), 2000);
}
function flushNotesAutosave(entityId: string): void {
  clearTimeout(notesAutosaveTimer);
  if (!appSend) return;
  appSend({ type: 'private-note/save', entityId });
}

/**
 * The localized message catalog for the `calendarDatePicker` grid + the
 * `dateField` shell, threaded from the app's `t()` (the atoms ship English
 * defaults; the i18n invariant routes every shipped string through `t()`).
 */
function datePickerMessages(): Partial<CalendarDatePickerMessages & DateFieldMessages> {
  return {
    prev: t('calendar.grid.prev'),
    next: t('calendar.grid.next'),
    month: t('calendar.month'),
    yearEra: t('calendar.grid.year_era'),
    grid: t('calendar.grid.days'),
    now: t('calendar.grid.now'),
    selectYear: t('calendar.grid.select_year'),
    clear: t('calendar.clear'),
    set: t('calendar.grid.set'),
    unset: t('calendar.grid.no_date'),
    time: t('calendar.grid.time'),
    intercalary: t('calendar.grid.intercalary'),
    canonicalDay: t('calendar.grid.canonical_day'),
    dayN: t('calendar.grid.day_n'),
    backwardEraHint: t('calendar.grid.backward_era_hint'),
    granularity: t('calendar.grid.granularity'),
    granularityOptions: {
      day: t('calendar.grid.granularity.day'),
      month: t('calendar.grid.granularity.month'),
      season: t('calendar.grid.granularity.season'),
      year: t('calendar.grid.granularity.year'),
    },
    placeholder: t('calendar.field.placeholder'),
    trigger: t('calendar.field.trigger'),
    close: t('calendar.field.close'),
  };
}

/**
 * Render a `dateField` (the grid-picker shell) bound to the app's single
 * open-picker slice (`Model.datePicker`). Only one grid is open app-wide, so
 * `id` selects which field owns it. Wrapped in a keyed `each` digesting the
 * calendar, the value, and the open view-state so the (build-once) atom
 * remounts on navigation — the established calendar-row pattern.
 */
function viewDateField(
  state: Signal<Model>,
  send: Send<Msg>,
  params: {
    id: string;
    testid: string;
    /** The committed canonical day for this field. */
    value: (s: Model) => number | null;
    /** Commit a picked day. */
    onSelect: (day: number) => void;
    /** Whether the grid exposes a Clear (open-ended) affordance. */
    clearable?: boolean;
    /** Clear handler (only meaningful when `clearable`). */
    onClear?: () => void;
    /** Popover anchor edge — `end` for triggers near the viewport's edge. */
    align?: 'start' | 'end';
    /** Render the field inert (no popover opens) while this returns true. */
    disabled?: (s: Model) => boolean;
    /**
     * Render a calendar-type switcher inside the popover, above the grid.
     * Switching is a `canonical_calendar` setting change; the picked
     * canonical day is PRESERVED across the switch (ADR-0006: the day axis
     * is shared and calendar-independent — only the display re-derives).
     */
    calendarSwitcher?: boolean;
    /** Show the grid's footer "Now" jump. Default true; false for the cursor. */
    showNow?: boolean;
  },
): Mountable {
  const { id } = params;
  // Snapshot the host slice through a `derived` whose identifier is not the
  // raw item signal — the keyed `each` re-mounts whenever the row key (and
  // thus the snapshot) changes, so a one-shot `model.peek()` at the render
  // block-body top is the sanctioned static-value read (mirrors the
  // `@dl/ui` calendar-atom pattern in `ui-gallery.ts`).
  const rowOf = (s: Model) => {
    const dp = s.datePicker;
    const open = dp !== null && dp.id === id;
    const value = params.value(s);
    const disabled = params.disabled?.(s) ?? false;
    const viewKey = open
      ? `${dp.cursor.eraId}|${dp.cursor.year}|${dp.cursor.monthIndex}|${dp.overlay}`
      : 'closed';
    return {
      key: `${s.activeCalendarId}:${value ?? 'unset'}:${disabled ? 'off' : 'on'}:${viewKey}`,
      calendarId: s.activeCalendarId,
      value,
      open,
      disabled,
      cursor: open ? dp.cursor : null,
      overlay: open ? dp.overlay : null,
      today: s.campaignCursor,
    };
  };
  const model = derived([state], (s) => s);
  return each(
    model.map((s) => [rowOf(s).key]),
    {
      key: (k) => k,
      render: () => {
        const snap = model.peek();
        // A plugin-mode / unresolvable calendar cannot drive the structured
        // date grid (`asCalendar` throws — #242). Degrade to the notice instead
        // of white-screening the field.
        const resolution = activeCalendarResolution(snap);
        if (resolution.kind !== 'structured') {
          return [calendarDegradeNotice()];
        }
        const { calendarId, value, open, disabled, cursor, overlay, today } = rowOf(snap);
        return [
          dateField({
            calendar: resolution.calendar,
            value,
            open,
            disabled,
            ...(cursor !== null ? { cursor } : {}),
            ...(overlay !== null ? { overlay } : {}),
            ...(today !== null ? { today } : {}),
            ...(params.showNow !== undefined ? { showNow: params.showNow } : {}),
            ...(params.calendarSwitcher === true
              ? { header: viewCalendarSwitcher(send, calendarId, params.testid) }
              : {}),
            id,
            testid: params.testid,
            clearable: params.clearable ?? false,
            ...(params.align !== undefined ? { align: params.align } : {}),
            locale: 'en',
            messages: datePickerMessages(),
            onOpen: (seed) => send({ type: 'date-picker-open', id, cursor: seed }),
            onClose: () => send({ type: 'date-picker-close' }),
            onNavigate: (next) => send({ type: 'date-picker-navigate', cursor: next }),
            onOverlay: (next) => send({ type: 'date-picker-overlay', overlay: next }),
            onSelect: params.onSelect,
            ...(params.onClear !== undefined ? { onClear: params.onClear } : {}),
          }),
        ];
      },
    },
  );
}

/**
 * The calendar-type switcher dropped into a date picker's popover header. A
 * `<select>` of every resolvable calendar definition (bundled + folded
 * `pack.define_calendar`); picking one points the campaign's
 * `canonical_calendar` *setting* at it (the same canonical path as Campaign
 * Settings). Per ADR-0006 the campaign cursor (a shared-axis `CanonicalDay`)
 * is PRESERVED across the switch — only the grid's rendering re-derives in the
 * newly-active calendar. Built inside `viewDateField`'s keyed `each`, so it
 * re-mounts (and re-reflects the active id) whenever the calendar changes.
 */
function viewCalendarSwitcher(send: Send<Msg>, calendarId: string, testid: string): Mountable {
  return div({ class: 'dl-date-field-switcher' }, [
    span({ class: 'dl-date-field-switcher-label' }, [text(t('calendar.switch_type'))]),
    selectInput({
      id: `${testid}-calendar-type`,
      testid: `${testid}-calendar-type`,
      ariaLabel: t('calendar.switch_type'),
      value: calendarId,
      size: 'full',
      options: calendarChoices().map((c) => ({ value: c.id, label: c.name })),
      onChange: (next) => {
        if (next !== '' && next !== calendarId) send({ type: 'set-active-calendar', calendarId: next });
      },
    }),
  ]);
}

// ---------- 226.1 (#245) Preview-as-Viewer picker ----------

/** Selectable Viewers for the Preview-as picker (sorted; GM excluded). */
function previewViewerOptions(s: Model): RevealTargetOption[] {
  return campaignViewers(s.entities);
}

/**
 * A stable signature for the picker's keyed remount: the current selection key
 * plus the roster shape. Changing either (GM picks a Viewer, or a new Character
 * appears) remounts the native `<select>` so `selectInput` reflects the right
 * value/options (it builds once, per its own contract).
 */
function previewPickerSignature(s: Model): string {
  const sel = s.previewViewer === null ? 'gm' : viewerKeyFn(s.previewViewer);
  const roster = previewViewerOptions(s)
    .map((o) => `${viewerKeyFn(o.viewer)}=${o.label}`)
    .join('|');
  // #256 - include the facilitation mode so the picker remounts when solo mode
  // toggles the default-identity option label.
  const mode = s.config.facilitation_mode ?? 'gm';
  return `${sel} ${roster} ${mode}`;
}

/** Decode a picker `<option>` value back into a Msg (GM sentinel → null). */
const PREVIEW_GM_VALUE = '__gm__';
function previewSelectMsg(value: string): Msg {
  if (value === PREVIEW_GM_VALUE) return { type: 'set-preview-viewer', viewer: null };
  // Option values are `kind:id` viewer keys; campaignViewers only mints
  // `character` viewers today, but decode generically so party/user widen
  // cleanly when the roster grows.
  const sep = value.indexOf(':');
  const kind = value.slice(0, sep) as ViewerId['kind'];
  const id = value.slice(sep + 1);
  return { type: 'set-preview-viewer', viewer: { kind, id } };
}

/**
 * 226.1 (#245) — the "Viewing as:" picker (GM topbar). Lists the GM identity
 * (default) plus every selectable campaign Viewer; selecting one drives the
 * projection chokepoint so the GM previews that Viewer's revealed canon.
 *
 * A11y: a native `<select>` (keyboard + SR + mobile-native picker for free),
 * paired with a visible label and an `aria-label`. The preview STATE is also
 * signalled by the banner (text + icon, not color alone) so the picker is not
 * the only cue.
 */
function viewPreviewAsPicker(state: Signal<Model>, send: Send<Msg>): Mountable {
  return div({ class: 'preview-as-picker', 'data-testid': 'preview-as-picker' }, [
    span({ class: 'preview-as-picker-label', 'aria-hidden': 'true' }, [
      text(`${t('preview_as.label')}: `),
    ]),
    each(state.map((s) => [previewPickerSignature(s)]), {
      key: (sig) => sig,
      render: () => {
        const model = derived([state], (s) => s);
        const snap = model.peek();
        // #256 — in solo facilitation mode, the default-identity option drops
        // the GM/Player hierarchy framing ("GM (full canon)" → "Everything"),
        // per 07-session-experience.md §2.1/§2.2. The mode is folded from the op
        // log into the config bucket; default `gm` keeps the GM framing.
        const defaultOptionLabel =
          snap.config.facilitation_mode === 'solo'
            ? t('preview_as.self_option')
            : t('preview_as.gm_option');
        const options = [
          { value: PREVIEW_GM_VALUE, label: defaultOptionLabel },
          ...previewViewerOptions(snap).map((o) => ({
            value: viewerKeyFn(o.viewer),
            label: o.label,
          })),
        ];
        const selected =
          snap.previewViewer === null ? PREVIEW_GM_VALUE : viewerKeyFn(snap.previewViewer);
        return [
          selectInput({
            value: selected,
            options,
            onChange: (value) => send(previewSelectMsg(value)),
            ariaLabel: t('preview_as.aria'),
            testid: 'preview-as-select',
            size: 'md',
          }),
        ];
      },
    }),
  ]);
}

// #258 — sentinel for the "Party (default)" option in the designated-Viewer
// picker. Decodes back to a null override (→ `resolveDesignatedViewer` default).
const TABLE_DEFAULT_VIEWER_VALUE = '__default__';

/** A stable signature for the designated-Viewer picker's keyed remount. */
function tableDesignatedSignature(s: Model): string {
  const sel =
    s.designatedViewerOverride === null
      ? TABLE_DEFAULT_VIEWER_VALUE
      : viewerKeyFn(s.designatedViewerOverride);
  const roster = campaignViewers(s.entities)
    .map((o) => `${viewerKeyFn(o.viewer)}=${o.label}`)
    .join('|');
  return `${sel} ${roster}`;
}

/** Decode a designated-Viewer picker value back into a Msg. */
function tableDesignatedMsg(value: string): Msg {
  if (value === TABLE_DEFAULT_VIEWER_VALUE) {
    return { type: 'table/set-designated-viewer', viewer: null };
  }
  const sep = value.indexOf(':');
  const kind = value.slice(0, sep) as ViewerId['kind'];
  const id = value.slice(sep + 1);
  return { type: 'table/set-designated-viewer', viewer: { kind, id } };
}

/**
 * #258 — GM topbar Table View controls: a designated-Viewer picker (whose
 * projection the shared screen renders; default = the party Viewer) and an
 * "Open Table View" link launching the chrome-stripped `/table` route in a new
 * tab (the dual-screen transport). a11y: a native `<select>` + visible label,
 * and a labelled link.
 */
function viewTableViewControls(state: Signal<Model>, send: Send<Msg>): Mountable {
  // The row used to carry a visible "Table shows:" caption, a bare text link,
  // and two icon+text buttons at three different sizes. The caption is now the
  // select's accessible name only (it already had one), and every action is an
  // icon button with a tooltip — so the cluster reads as controls, not prose.
  return div({ class: 'table-view-controls', 'data-testid': 'table-view-controls' }, [
    each(state.map((s) => [tableDesignatedSignature(s)]), {
      key: (sig) => sig,
      render: () => {
        const model = derived([state], (s) => s);
        const snap = model.peek();
        const options = [
          { value: TABLE_DEFAULT_VIEWER_VALUE, label: t('table_view.designated_default') },
          ...campaignViewers(snap.entities).map((o) => ({
            value: viewerKeyFn(o.viewer),
            label: o.label,
          })),
        ];
        const selected =
          snap.designatedViewerOverride === null
            ? TABLE_DEFAULT_VIEWER_VALUE
            : viewerKeyFn(snap.designatedViewerOverride);
        return [
          selectInput({
            value: selected,
            options,
            onChange: (value) => send(tableDesignatedMsg(value)),
            ariaLabel: t('table_view.designated_aria'),
            testid: 'table-designated-select',
            size: 'md',
          }),
        ];
      },
    }),
    // Opening the shared display is an action, not a sentence. Still a real
    // anchor (middle-click / "open in new window" keep working); only the
    // presentation changed from link text to an icon button.
    a(
      {
        class: 'btn btn-ghost dl-button-icon table-view-open-link',
        href: '/table',
        target: '_blank',
        rel: 'noopener',
        'data-testid': 'table-view-open',
        'aria-label': t('table_view.open_aria'),
        title: t('table_view.open_aria'),
      },
      [uiIcon('external-link', { size: 18 })],
    ),
    // #271 — attention mode toggle (pointing cursor + "Look at this" button).
    // `aria-pressed` + the tooltip carry the state and the name; the visible
    // text label is gone.
    button(
      {
        type: 'button',
        class: state.map((s) =>
          s.tableAttentionMode
            ? 'btn btn-ghost dl-button-icon table-attention-toggle table-attention-toggle--active'
            : 'btn btn-ghost dl-button-icon table-attention-toggle',
        ),
        'data-testid': 'table-attention-toggle',
        'aria-pressed': state.map((s) => String(s.tableAttentionMode)),
        'aria-label': t('table_view.attention_toggle_aria'),
        title: t('table_view.attention_toggle_aria'),
        onClick: () => send({ type: 'table/attention-toggle' }),
      },
      [uiIcon('crosshair', { size: 18 })],
    ),
    // #271 — "Look at this" ripple button (visible only when attention mode is on).
    show(
      state.map((s) => s.session.mode === 'gm' && s.tableAttentionMode),
      () => [
        button(
          {
            type: 'button',
            class: 'btn btn-ghost dl-button-icon table-attention-ripple-btn',
            'data-testid': 'table-attention-ripple-btn',
            'aria-label': t('table_view.attention_ripple_aria'),
            title: t('table_view.attention_ripple_aria'),
            onClick: () => {
              // Trigger a centred ripple when the GM clicks "Look at this" without
              // a specific coordinate — the pointer position is used if known, else
              // the centre of the table (0.5, 0.5).
              send({ type: 'table/attention-ripple', x: 0.5, y: 0.5 });
            },
          },
          [uiIcon('target', { size: 18 })],
        ),
      ],
    ),
  ]);
}

/** Human label for the currently-previewed Viewer (falls back to its id). */
function previewViewerLabel(s: Model): string {
  if (s.previewViewer === null) return '';
  const key = viewerKeyFn(s.previewViewer);
  for (const opt of previewViewerOptions(s)) {
    if (viewerKeyFn(opt.viewer) === key) return opt.label;
  }
  return s.previewViewer.id;
}

/**
 * 226.1 (#245) — top-of-content banner shown while previewing as a non-GM
 * Viewer. Carries the state by BOTH an icon and text (never color alone) and a
 * keyboard-reachable "Return to GM view" button. Absent on the GM identity view.
 */
function viewPreviewAsBanner(state: Signal<Model>, send: Send<Msg>): Mountable {
  return show(
    state.map((s) => s.session.mode === 'gm' && s.previewViewer !== null),
    () => [
      div(
        {
          class: 'preview-as-banner',
          'data-testid': 'preview-as-banner',
          role: 'status',
          'aria-live': 'polite',
        },
        [
          span({ class: 'preview-as-banner-icon', 'aria-hidden': 'true' }, [
            uiIcon('eye', { size: 16 }),
          ]),
          span({ class: 'preview-as-banner-text' }, [
            text(
              state.map((s) => t('preview_as.banner', { viewer: previewViewerLabel(s) })),
            ),
          ]),
          uiButton({
            variant: 'ghost',
            className: 'preview-as-banner-return',
            testid: 'preview-as-return',
            onClick: () => send({ type: 'set-preview-viewer', viewer: null }),
            // #256 — neutral copy in solo mode (no "GM view" framing).
            children: [
              text(
                state.map((s) =>
                  s.config.facilitation_mode === 'solo'
                    ? t('preview_as.return_self')
                    : t('preview_as.return'),
                ),
              ),
            ],
          }),
        ],
      ),
    ],
  );
}

/**
 * As-of scrubber (ADR-0005 §3, #192) — the in-world **browse cursor**.
 *
 * A keyboard-reachable day input that drives `Model.browseCursor` (ephemeral,
 * client-only). When the browse cursor diverges from the session "now"
 * (`campaignCursor`) the entity page + timeline strip live-resolve their
 * temporal values AS OF the browsed day, and "new at now" authoring locks
 * (existing-segment edits stay allowed — they carry explicit validity).
 *
 * A11y: the status is carried by BOTH a glyph and text (never color alone —
 * `data-diverged` styles the chip but the `●` / `◑` glyph + label are the
 * non-color cue); the input and the return-to-now button are native focusable
 * controls; an `aria-live` region announces the present/past state change.
 */
function viewAsOfScrubber(state: Signal<Model>, send: Send<Msg>): Mountable {
  const divergedSig = state.map((s) => s.browseCursor !== null);
  return div(
    {
      class: 'asof-scrubber',
      'data-testid': 'asof-scrubber',
      'data-diverged': divergedSig.map((d) => (d ? 'true' : 'false')),
      role: 'group',
      'aria-label': t('scrubber.label'),
    },
    [
        // Non-color status: glyph + text, paired (WCAG-AA).
        span(
          {
            class: 'asof-status',
            'data-testid': 'asof-status',
            'aria-live': 'polite',
          },
          [
            span({ class: 'asof-glyph', 'aria-hidden': 'true' }, [
              text(divergedSig.map((d) => (d ? t('scrubber.glyph_past') : t('scrubber.glyph_now')))),
            ]),
            span({ class: 'asof-status-text' }, [
              text(divergedSig.map((d) => (d ? t('scrubber.browsing_past') : t('scrubber.browsing_now')))),
            ]),
          ],
        ),
        // Group heading (not a control `for=` label — the picker below is a
        // `role=group` of part-selects, each with its own visually-hidden label).
        span({ class: 'asof-day-label' }, [text(t('scrubber.heading'))]),
        // A DATE picker, not a raw day number: the `dateField` grid renders the
        // active calendar and converts the picked date back to a canonical day.
        // The browse cursor stays a canonical-day integer internally; only the
        // surface is dated. Inside this gated call site at least one of
        // browse/session cursor is non-null, so the `?? 0` fallback is never
        // reached (no fabricated day 0).
        viewDateField(state, send, {
          id: 'asof',
          testid: 'asof-day-input',
          // The browse pill sits at the top bar's inline-end; open the 336px
          // grid inward so it never overflows the viewport's right edge.
          align: 'end',
          value: (s) => s.browseCursor ?? s.campaignCursor ?? 0,
          // `browse-set` collapses `day === campaignCursor` back to null, so
          // picking the present date returns to "now".
          onSelect: (day) => send({ type: 'browse-set', day }),
        }),
        show(divergedSig, () => [
          uiButton({
            variant: 'ghost',
            className: 'asof-return-now',
            testid: 'asof-return-now',
            attrs: { title: t('scrubber.return_to_now') },
            onClick: () => send({ type: 'browse-clear' }),
            children: [text(t('scrubber.now'))],
          }),
      ]),
    ],
  );
}



/**
 * Which routes render the persistent left navigation rail. In-campaign
 * content routes get it (list / entity / drafts / auditor / settings); the
 * account-scoped + admin + campaign-picker + standalone pages render full-
 * width without it (mocks: workspace-home and admin have no global nav rail).
 * Auth routes never reach `viewAppShell`, but are excluded for safety.
 */
const RAIL_ROUTE_KINDS: ReadonlySet<AppRoute['kind']> = new Set([
  'list',
  'entity',
  'draft-inbox',
  'trash',
  'auditor',
  'settings',
]);
function routeHasRail(route: AppRoute): boolean {
  return !isAuthRoute(route) && RAIL_ROUTE_KINDS.has(route.kind);
}

/**
 * Session-tools HUD kill switch (#257 dice roller + initiative tracker, #260
 * narrative-combat panel, #259 combat-log). These shipped without any HUD CSS
 * (no `.dice-roller` / `.initiative-tracker` styles + no positioning), so in GM
 * mode they rendered unstyled inline at the foot of every in-campaign page.
 * Disabled until the docked session-tools HUD is finished — the state,
 * reducers, effects, and view functions are all intact, so re-enabling is
 * flipping this to `true`. Gates the render only; nothing else changes.
 */
const SESSION_TOOLS_ENABLED = false;

/**
 * Left navigation rail (per `mocks/entity-page.html`). The persistent global
 * nav: a campaign-switcher card, a Recent list (recently-viewed entities), a
 * Browse list (the six entity roots, each linking to its single-root view),
 * and a Work list (Drafts live; Sessions / Maps / Adventurers Book disabled
 * placeholders). Hidden < 900px (CSS) — mobile uses the fixed bottom-nav.
 */
function viewLeftRail(state: Signal<Model>, send: Send<Msg>): Mountable {
  return aside(
    { class: 'left-rail', role: 'navigation', 'aria-label': t('rail.nav_label') },
    [
      // #006 (llui-hud) — the campaign switcher is pinned to the rail FOOTER
      // (bottom of this aside); the sections above scroll independently when the
      // rail is too short. The rail leads with Recent. On mobile the rail is
      // hidden, so the switcher's home there is a "Switch campaign" entry in the
      // user menu (see `viewUserMenu`) — reverses the old D19 topbar placement.
      //
      // Recent — recently-viewed entities (most-recent-first). Hidden until
      // at least one entity has been opened.
      show(
        state.map((s) => recentRailRows(s).length > 0),
        () => [
          div({ class: 'rail-section' }, [
            div({ class: 'rail-label' }, [span([text(t('rail.recent'))])]),
            ul(
              { class: 'rail-list', 'data-testid': 'rail-recent' },
              [
                each(
                  state.map((s) => recentRailRows(s)),
                  {
                    key: (row) => row.id,
                    render: (item) => [
                      li({ class: 'rail-li' }, [
                        a(
                          {
                            class: item
                              .at('active')
                              .map((on) => (on ? 'rail-item is-active' : 'rail-item')),
                            href: item.at('id').map((id) => appRouteHref({ kind: 'entity', id })),
                            'data-testid': 'rail-recent-item',
                            'data-root': item.at('root'),
                            'aria-current': item.at('active').map((on) => (on ? 'page' : undefined)),
                            onClick: (event: MouseEvent) => {
                              event.preventDefault();
                              const id = item.at('id').peek();
                              if (typeof id === 'string') send(navMsg({ kind: 'entity', id }));
                            },
                          },
                          [
                            span({ class: 'rail-item-icon', 'aria-hidden': 'true' }, [
                              // The row carries its root; render the matching
                              // entity icon via a keyed branch so the literal
                              // root drives the icon (no reactive icon swap).
                              branch(item.at('root'), {
                                location: () => [entityIcon('location', { size: 14 })],
                                character: () => [entityIcon('character', { size: 14 })],
                                faction: () => [entityIcon('faction', { size: 14 })],
                                event: () => [entityIcon('event', { size: 14 })],
                                item: () => [entityIcon('item', { size: 14 })],
                                lore: () => [entityIcon('lore', { size: 14 })],
                                '': () => [entityIcon('lore', { size: 14 })],
                              }),
                            ]),
                            span({ class: 'rail-item-label' }, [text(item.at('name'))]),
                          ],
                        ),
                      ]),
                    ],
                  },
                ),
              ],
            ),
          ]),
        ],
      ),
      // Browse — the six entity roots, each linking to its single-root view
      // (`/browse/:root`); active when that root filter is on.
      div({ class: 'rail-section' }, [
        div({ class: 'rail-label' }, [span([text(t('rail.browse'))])]),
        ul({ class: 'rail-list', 'data-testid': 'rail-browse' }, viewBrowseRailItems(state, send)),
      ]),
      // Work — GM-only operational surfaces. Drafts is live; Sessions / Maps /
      // Adventurers Book are disabled placeholders (features not yet built).
      show(
        state.map((s) => s.session.mode === 'gm'),
        () => [
          div({ class: 'rail-section' }, [
            div({ class: 'rail-label' }, [span([text(t('rail.work'))])]),
            ul({ class: 'rail-list', 'data-testid': 'rail-work' }, [
              li({ class: 'rail-li' }, [
                appRouteLink(
                  send,
                  { kind: 'draft-inbox' },
                  {
                    class: state.map((s) =>
                      s.route.kind === 'draft-inbox' ? 'rail-item is-active' : 'rail-item',
                    ),
                    'data-testid': 'inbox-link',
                    // #266 — flat mode: "Session prep" instead of "Drafts".
                    'aria-label': state.map((s) =>
                      s.config.facilitation_mode === 'flat'
                        ? t('inbox.flat.open')
                        : t('inbox.open'),
                    ),
                    'aria-current': state.map((s) =>
                      s.route.kind === 'draft-inbox' ? 'page' : undefined,
                    ),
                  },
                  [
                    span({ class: 'rail-item-icon', 'aria-hidden': 'true' }, [
                      uiIcon('inbox', { size: 14 }),
                    ]),
                    // #266 — flat mode: "Session prep" label instead of "Drafts".
                    span({ class: 'rail-item-label' }, [
                      text(state.map((s) =>
                        s.config.facilitation_mode === 'flat'
                          ? t('inbox.flat.open')
                          : t('inbox.open'),
                      )),
                    ]),
                  ],
                  navMsg,
                ),
              ]),
              // Trash — soft-deleted entities, with restore. Hidden when empty;
              // a count badge surfaces how many entities are trashed.
              show(
                state.map((s) => trashCount(s) > 0),
                () => [
                  li({ class: 'rail-li' }, [
                    appRouteLink(
                      send,
                      { kind: 'trash' },
                      {
                        class: state.map((s) =>
                          s.route.kind === 'trash' ? 'rail-item is-active' : 'rail-item',
                        ),
                        'data-testid': 'trash-link',
                        'aria-label': t('trash.open'),
                        'aria-current': state.map((s) =>
                          s.route.kind === 'trash' ? 'page' : undefined,
                        ),
                      },
                      [
                        span({ class: 'rail-item-icon', 'aria-hidden': 'true' }, [
                          uiIcon('trash-2', { size: 14 }),
                        ]),
                        span({ class: 'rail-item-label' }, [text(t('trash.open'))]),
                        span({ class: 'rail-item-count' }, [
                          text(state.map((s) => String(trashCount(s)))),
                        ]),
                      ],
                      navMsg,
                    ),
                  ]),
                ],
              ),
              railDisabledItem('play-circle', t('rail.sessions')),
              railDisabledItem('map', t('rail.maps')),
              railDisabledItem('book-open', t('rail.adventurers_book')),
            ]),
          ]),
        ],
      ),
      // #006 (llui-hud) — campaign switcher pinned to the rail footer. Sticky +
      // `margin-block-start:auto` keeps it flush to the bottom while the sections
      // above scroll. Gated like the old topbar instance (a real campaign list).
      show(
        state.map((s) => {
          const cb = s.campaignsBoot;
          return cb.kind === 'picker' && cb.campaigns.length > 0;
        }),
        () => [
          div({ class: 'rail-footer' }, [
            viewCampaignSwitcher(state, send, currentCampaignId(), {
              navigateToPicker: () => ({ type: 'navigate', route: { kind: 'campaigns' } }) as Msg,
            }),
          ]),
        ],
      ),
    ],
  );
}

/** A disabled (not-yet-built) Work-section rail item: dimmed, inert, labelled. */
function railDisabledItem(icon: Parameters<typeof uiIcon>[0], label: string): Mountable {
  return li({ class: 'rail-li' }, [
    span(
      {
        class: 'rail-item is-disabled',
        'aria-disabled': 'true',
        title: t('rail.coming_soon'),
      },
      [
        span({ class: 'rail-item-icon', 'aria-hidden': 'true' }, [uiIcon(icon, { size: 14 })]),
        span({ class: 'rail-item-label' }, [text(label)]),
        span({ class: 'rail-item-soon' }, [text(t('rail.coming_soon'))]),
      ],
    ),
  ]);
}

/** Build the six Browse-section root links (static order; reactive count + active). */
function viewBrowseRailItems(state: Signal<Model>, send: Send<Msg>): Mountable[] {
  return ALL_ENTITY_ROOTS.map((root) => {
    const rootLabel = t(`entity.root.${root}`);
    return li({ class: 'rail-li' }, [
      appRouteLink(
        send,
        { kind: 'list', root },
        {
          class: state.map((s) =>
            s.route.kind === 'list' && s.route.root === root ? 'rail-item is-active' : 'rail-item',
          ),
          'data-testid': 'rail-browse-item',
          'data-root': root,
          'aria-current': state.map((s) =>
            s.route.kind === 'list' && s.route.root === root ? 'page' : undefined,
          ),
        },
        [
          span({ class: 'rail-item-icon', 'aria-hidden': 'true' }, [entityIcon(root, { size: 14 })]),
          span({ class: 'rail-item-label' }, [text(rootLabel)]),
          span({ class: 'rail-item-count', 'aria-hidden': 'true' }, [
            text(state.map((s) => String(pickCountForRoot(s, root)))),
          ]),
        ],
        navMsg,
      ),
    ]);
  });
}

interface RecentRailRow {
  id: string;
  name: string;
  root: EntityRoot | '';
  active: boolean;
}
/** Resolve the rail's Recent list to renderable rows (drops soft-deleted / unknown). */
function recentRailRows(s: Model): RecentRailRow[] {
  const activeId = s.route.kind === 'entity' ? s.route.id : null;
  const rows: RecentRailRow[] = [];
  for (const id of s.recentEntityIds) {
    const entity = s.entities[id];
    if (entity === undefined || entity.soft_deleted) continue;
    rows.push({
      id,
      name: entityNameOrId(s, id),
      root: entity.root,
      active: id === activeId,
    });
  }
  return rows;
}

function viewHeader(state: Signal<Model>, send: Send<Msg>): Mountable {
  return header({ class: 'app-chrome', role: 'banner' }, [
    // Zone 1 — brand (fills the rail-width column; its right border lines up
    // with the rail's). #010 — title links back to the GM home (entity list).
    // Kept as the global `h1` (the established convention; page titles are
    // `h2`/`h3`) so every shell route has a top-level heading.
    div({ class: 'topbar-brand' }, [
      h1([
        a(
          {
            class: 'app-brand-link',
            href: appRouteHref({ kind: 'list' }),
            'data-testid': 'app-brand-link',
            'aria-label': t('app.brand_home_label'),
            onClick: (event: MouseEvent) => {
              event.preventDefault();
              send(navMsg({ kind: 'list' }));
            },
          },
          [
            span({ class: 'topbar-brand-mark', 'aria-hidden': 'true' }, [text('▲')]),
            span({ class: 'topbar-brand-name' }, [text(t('app.title'))]),
          ],
        ),
      ]),
    ]),
    // Zone 2 — read-only summary strip (#005 llui-hud). The crowded action
    // cluster moved into the Actions overlay; the bar now carries only at-a-
    // glance context (campaign · live status · issue count).
    show(
      state.map((s) => !isAuthRoute(s.route)),
      () => [viewChromeSummary(state)],
    ),
    // Zone 3 — date controls + Actions trigger + account menu (#005 llui-hud).
    // Most of the old cluster (campaign switcher, Preview-as, Table View, auditor,
    // theme, search) moved into `viewActionsOverlay`; the campaign switcher moved
    // to the left rail's pinned footer (#006 llui-hud). The date cursor + as-of
    // scrubber stay INLINE: they open large calendar-grid popovers that overflow
    // the compact overlay panel, so they need the header's viewport room.
    div({ class: 'app-chrome-actions' }, [
      // #007 (llui-hud) — the campaign-date cursor + as-of scrubber share ONE
      // container so they read as a single date-context control group (they were
      // two loose siblings in the action cluster). Each still opens its own large
      // calendar-grid popover; only the chrome wrapper is shared. Gated until the
      // active calendar folds (a date against an unconfirmed calendar = wrong
      // instant) — the cursor field is `disabled` until `calendarConfirmed`.
      show(
        state.map((s) => !isAuthRoute(s.route)),
        () => [
          div({ class: 'topbar-dates', 'data-testid': 'topbar-dates', role: 'group', 'aria-label': t('topbar.dates') }, [
            div({ class: 'cursor-pill', 'data-testid': 'campaign-cursor-pill' }, [
              span({ class: 'cursor-pill-label' }, [text(`${t('calendar.cursor')}: `)]),
              viewDateField(state, send, {
                id: 'campaign-cursor',
                testid: 'campaign-cursor',
                calendarSwitcher: true,
                showNow: false,
                disabled: (s) => !s.calendarConfirmed,
                value: (s) => s.campaignCursor,
                onSelect: (day) => send({ type: 'set-cursor', cursor: day }),
              }),
            ]),
            // As-of scrubber — shown once an in-world now exists.
            show(
              state.map((s) => s.campaignCursor !== null || s.browseCursor !== null),
              () => [viewAsOfScrubber(state, send)],
            ),
          ]),
        ],
      ),
      // Table View controls — inline (GM). The attention toggle raises a
      // full-screen surface that would cover the Actions overlay, so these can't
      // live inside it.
      show(
        state.map((s) => s.session.mode === 'gm'),
        () => [viewTableViewControls(state, send)],
      ),
      show(
        state.map((s) => !isAuthRoute(s.route)),
        () => [
          button(
            {
              type: 'button',
              class: state.map((s) =>
                s.actionsOverlayOpen
                  ? 'btn btn-ghost dl-button-icon topbar-actions-toggle is-open'
                  : 'btn btn-ghost dl-button-icon topbar-actions-toggle',
              ),
              'data-testid': 'actions-toggle',
              'aria-haspopup': 'dialog',
              'aria-expanded': state.map((s) => String(s.actionsOverlayOpen)),
              'aria-label': t('topbar.actions'),
              title: t('topbar.actions'),
              onClick: () => send({ type: 'actions-overlay/toggle' }),
            },
            // Icon-only, like the rest of the header cluster. The `aria-label`
            // above and the tooltip carry the name; the ⚙ text character (which
            // rendered at the body font's weight, unlike every Lucide glyph
            // beside it) is gone.
            [uiIcon('sliders-horizontal', { size: 18 })],
          ),
        ],
      ),
      // Account menu — avatar dropdown holding the signed-in email, Campaign
      // settings (GM), Account, the Admin entry (system admins only), and
      // sign-out. Hidden on auth routes (no session yet).
      show(
        state.map((s) => !isAuthRoute(s.route)),
        () => [
          viewUserMenu(state, send, {
            navigateToAccount: () =>
              ({ type: 'navigate', route: { kind: 'account' } }) as Msg,
            navigateToSettings: () =>
              ({ type: 'navigate', route: { kind: 'settings' } }) as Msg,
            navigateToCampaigns: () =>
              ({ type: 'navigate', route: { kind: 'campaigns' } }) as Msg,
            navigateToAdmin: () =>
              ({ type: 'navigate', route: { kind: 'admin-users' } }) as Msg,
            signOut: () => ({ type: 'auth/sign-out' }) as Msg,
          }),
        ],
      ),
    ]),
  ]);
}

/** #005 (llui-hud) — current campaign's display name (empty when none active). */
function currentCampaignLabel(s: Model): string {
  const cb = s.campaignsBoot;
  if (cb.kind !== 'picker') return '';
  const id = currentCampaignId();
  return cb.campaigns.find((c) => c.id === id)?.name ?? '';
}

/**
 * #005 (llui-hud) — the read-only summary strip that replaces the crowded topbar
 * action cluster. At-a-glance context only: campaign name, live status pills
 * (moved verbatim from the old action zone), player-mode chip, issue count. No
 * interactive controls — those live in `viewActionsOverlay`.
 */
function viewChromeSummary(state: Signal<Model>): Mountable {
  return div({ class: 'app-chrome-summary', 'data-testid': 'app-chrome-summary' }, [
    show(
      state.map((s) => currentCampaignLabel(s) !== ''),
      () => [
        span({ class: 'summary-campaign', 'data-testid': 'summary-campaign' }, [
          text(state.map((s) => currentCampaignLabel(s))),
        ]),
      ],
    ),
    show(
      state.map((s) => aiDraftInFlight(s) > 0),
      () => [
        span(
          {
            class: 'status-pill',
            'data-state': 'thinking',
            'data-testid': 'ai-thinking',
            'aria-live': 'polite',
          },
          [text(t('llm.thinking'))],
        ),
      ],
    ),
    show(
      state.at('syncing'),
      () => [
        span(
          {
            class: 'status-pill',
            'data-state': 'syncing',
            'data-testid': 'sync-status',
            'aria-live': 'polite',
          },
          [text(t('connection.syncing'))],
        ),
      ],
    ),
    show(
      state.map((s) => !s.connection.online),
      () => [
        span({ class: 'status-pill', 'data-state': 'offline', 'aria-live': 'polite' }, [
          text(t('connection.offline')),
        ]),
      ],
    ),
    show(
      state.map((s) => s.connection.pendingCount > 0),
      () => [
        span({ class: 'status-pill', 'aria-live': 'polite' }, [
          text(state.map((s) => t('connection.queue_pending', { n: s.connection.pendingCount }))),
        ]),
      ],
    ),
    show(
      state.map((s) => s.connection.conflictCount > 0),
      () => [
        span({ class: 'status-pill', 'data-state': 'conflict', 'aria-live': 'polite' }, [
          text(state.map((s) => t('connection.conflict_pending', { n: s.connection.conflictCount }))),
        ]),
      ],
    ),
    show(
      state.map((s) => s.session.mode === 'player'),
      () => [
        span({ class: 'mode-chip', 'data-testid': 'mode-chip', 'data-mode': 'player' }, [
          text(t('mode.chip_player')),
        ]),
      ],
    ),
    // Auditor issue count — read-only indicator (the link itself lives in the
    // overlay). Colour paired with the ⚠ glyph + count, never colour alone.
    show(
      state.map((s) => s.session.mode === 'gm' && advisoryCount(s) > 0),
      () => [
        span({ class: 'summary-issues', 'data-testid': 'summary-issues' }, [
          span({ class: 'audit-chip-icon', 'aria-hidden': 'true' }, [
            uiIcon('alert-triangle', { size: 13 }),
          ]),
          text(state.map((s) => String(advisoryCount(s)))),
        ]),
      ],
    ),
  ]);
}

/**
 * #005 (llui-hud) — the Actions overlay. Houses every control that crowded the
 * topbar: search, campaign switcher, date cursor + as-of scrubber, Preview-as,
 * Table View, auditor, theme. Backdrop click / Escape / navigation close it
 * (`actions-overlay/close`). Rendered as a sibling of the header in the shell.
 */
function viewActionsOverlay(state: Signal<Model>, send: Send<Msg>): Mountable {
  return show(
    state.map((s) => s.actionsOverlayOpen && !isAuthRoute(s.route)),
    () => [
      div(
        {
          class: 'actions-overlay-backdrop',
          'data-testid': 'actions-overlay-backdrop',
          // Click-to-dismiss is a redundant convenience — Escape + the panel's
          // close button are the keyboard-accessible paths — so the backdrop
          // exposes no functionality of its own (a11y: role=presentation).
          role: 'presentation',
          onClick: () => send({ type: 'actions-overlay/close' }),
        },
        [],
      ),
      div(
        {
          class: 'actions-overlay-panel',
          role: 'dialog',
          'aria-modal': 'true',
          'aria-label': t('topbar.actions_panel'),
          'data-testid': 'actions-overlay',
        },
        [
          div({ class: 'actions-overlay-header' }, [
            span({ class: 'actions-overlay-title' }, [text(t('topbar.actions_panel'))]),
            button(
              {
                type: 'button',
                class: 'actions-overlay-close',
                'data-testid': 'actions-overlay-close',
                'aria-label': t('modal.close'),
                onClick: () => send({ type: 'actions-overlay/close' }),
              },
              [text('×')],
            ),
          ]),
          div({ class: 'actions-overlay-body' }, [
            // Search → opens the ⌘K palette (and closes this panel).
            button(
              {
                type: 'button',
                class: 'actions-overlay-row topbar-search',
                'data-testid': 'topbar-search',
                'aria-label': t('topbar.search_label'),
                onClick: () => {
                  send({ type: 'actions-overlay/close' });
                  send({ type: 'open-palette' });
                },
              },
              [
                span({ class: 'topbar-search-icon', 'aria-hidden': 'true' }, [
                  uiIcon('search', { size: 15 }),
                ]),
                span({ class: 'topbar-search-text' }, [text(t('topbar.search_placeholder'))]),
                span({ class: 'topbar-search-kbd', 'aria-hidden': 'true' }, [text('⌘K')]),
              ],
            ),
            // NB: the campaign switcher, campaign-date cursor + as-of scrubber are
            // NOT here — they open their own popovers (a close-aware dropdown / a
            // calendar grid) that overflow this compact panel or conflict with its
            // backdrop, so they stay inline in the header. Only controls WITHOUT a
            // custom popover live in this overlay (search, Preview-as native
            // select, auditor link, theme toggle). The Table View controls stay
            // inline too — their attention toggle raises a full-screen surface
            // that would cover (and block) this panel.
            // Preview-as picker (GM).
            show(
              state.map((s) => s.session.mode === 'gm'),
              () => [div({ class: 'actions-overlay-row' }, [viewPreviewAsPicker(state, send)])],
            ),
            // Auditor link (GM) — the issue count also shows read-only in the summary.
            show(
              state.map((s) => s.session.mode === 'gm'),
              () => [
                appRouteLink(
                  send,
                  { kind: 'auditor' },
                  {
                    class: state.map((s) =>
                      advisoryCount(s) > 0
                        ? 'actions-overlay-row audit-chip has-issues'
                        : 'actions-overlay-row audit-chip',
                    ),
                    'data-testid': 'auditor-link',
                    'aria-label': t('auditor.title'),
                    'aria-current': state.map((s) =>
                      s.route.kind === 'auditor' ? 'page' : undefined,
                    ),
                  },
                  [
                    span({ class: 'audit-chip-icon', 'aria-hidden': 'true' }, [
                      uiIcon('alert-triangle', { size: 14 }),
                    ]),
                    span([text(t('auditor.title'))]),
                    show(
                      state.map((s) => advisoryCount(s) > 0),
                      () => [
                        badge({
                          tone: 'warn',
                          testid: 'auditor-badge',
                          children: [text(state.map((s) => String(advisoryCount(s))))],
                        }),
                      ],
                    ),
                  ],
                  navMsg,
                ),
              ],
            ),
            // Theme toggle (labelled — the bare icon read as orphaned).
            div({ class: 'actions-overlay-row actions-overlay-labelled' }, [
              span({ class: 'actions-overlay-row-label' }, [text(t('theme.label'))]),
              themeToggle({
                testid: 'theme-toggle',
                labels: {
                  light: t('theme.light'),
                  dark: t('theme.dark'),
                  system: t('theme.system'),
                },
              }),
            ]),
          ]),
        ],
      ),
    ],
  );
}

// ── Per-campaign AI settings — bare-Identifier reactive accessors ──────
function aiSettingValue(s: Model, scope: AiScope): string {
  return s.aiSettings[scope].value;
}
function aiSettingDirty(s: Model, scope: AiScope): boolean {
  return s.aiSettings[scope].dirty;
}
function aiSettingsLoaded(s: Model): boolean {
  return s.aiSettings.global.loaded;
}

/** One scope's extension editor (campaign-wide note or a per-task addition). */
function viewAiSettingRow(state: Signal<Model>, send: Send<Msg>, scope: AiScope): Mountable {
  const fieldId = `ai-setting-${scope}`;
  return div({ class: 'ai-setting-row', 'data-testid': `ai-setting-${scope}` }, [
    label({ for: fieldId, class: 'settings-label' }, [
      text(t(`settings.ai.scope.${scope}`)),
    ]),
    p({ class: 'settings-help' }, [text(t(`settings.ai.scope_help.${scope}`))]),
    textArea({
      className: 'ai-setting-textarea',
      testid: `ai-setting-${scope}-input`,
      rows: 3,
      placeholder: t('settings.ai.placeholder'),
      ariaLabel: t(`settings.ai.scope.${scope}`),
      value: state.map((s) => aiSettingValue(s, scope)),
      onInput: (value: string) => {
        send({ type: 'ai-setting/edit', scope, value });
      },
      attrs: { id: fieldId },
    }),
    div({ class: 'ai-setting-actions' }, [
      uiButton({
        variant: 'primary',
        className: 'ai-setting-save',
        testid: `ai-setting-${scope}-save`,
        disabled: state.map((s) => !aiSettingDirty(s, scope)),
        onClick: () => send({ type: 'ai-setting/save', scope }),
        children: [text(t('settings.ai.save'))],
      }),
      span({ class: 'ai-setting-status' }, [
        text(state.map((s) => (aiSettingDirty(s, scope) ? t('settings.ai.dirty') : t('settings.ai.saved')))),
      ]),
    ]),
  ]);
}

/** The settings sub-tab the route currently selects (default general). */
function settingsTabOf(s: Model): SettingsTab {
  // Read `s.route` as one unit (the bitmask path analyzer collapses the
  // `.kind`/`.tab` sub-reads into the single `s.route` access shared with
  // routeKey/sample — keeps the App component under the 62-path budget).
  const r = s.route;
  return r.kind === 'settings' ? (r.tab ?? 'general') : 'general';
}

/**
 * #schema-settings — tabbed Campaign Settings page. `general` keeps the
 * pre-existing pending-policy + AI-extension content; `traits` and
 * `entities` are GM-only CRUD surfaces (the traits tab replaces the old
 * trait-editor modal). Tab activation NAVIGATES (`#/settings/<tab>`), so the
 * route scope re-mounts the active body and the trait picker can deep-link in.
 */
function viewSettings(state: Signal<Model>, send: Send<Msg>): Mountable {
  return main({ role: 'main', class: 'settings-page' }, [
    appRouteLink(
      send,
      { kind: 'list' },
      { 'data-testid': 'back-link' },
      [text(`← ${t('entity.back')}`)],
      navMsg,
    ),
    pageHeader({ title: text(t('settings.title')) }),
    // Single-row keyed `each` on the (tab|mode) composite — re-mounts the
    // tab strip + active panel when either changes. Replaces the old
    // `h.scope` keyed on the same composite string.
    // `branch` on the effective (tab + GM-gate) discriminant re-mounts the
    // tab strip + active panel when either changes. Each arm carries the
    // active tab + GM flag as literals, so no render-body `.peek()` is needed
    // (peek-in-slot lint). Schema/entity CRUD is GM-only; a player landing on
    // #/settings/traits|types falls back to general.
    branch(state.map((s) => settingsTabDiscriminant(s)), {
      'general:gm': () => settingsTabContent(state, send, 'general', true),
      'general:player': () => settingsTabContent(state, send, 'general', false),
      campaign: () => settingsTabContent(state, send, 'campaign', true),
      traits: () => settingsTabContent(state, send, 'traits', true),
      types: () => settingsTabContent(state, send, 'types', true),
      calendars: () => settingsTabContent(state, send, 'calendars', true),
    }),
  ]);
}

type SettingsTabDiscriminant =
  | 'general:gm'
  | 'general:player'
  | 'campaign'
  | 'traits'
  | 'types'
  | 'calendars';

/** Effective settings-tab discriminant: applies the GM-gate (players only see
 * `general`) and folds the GM flag into the key so a `branch` can re-mount.
 * The typed Campaign Settings tab (#186) is GM-only, like traits/types. */
function settingsTabDiscriminant(s: Model): SettingsTabDiscriminant {
  const isGm = s.session.mode === 'gm';
  const requested = settingsTabOf(s);
  const activeTab: SettingsTab = !isGm && requested !== 'general' ? 'general' : requested;
  if (activeTab === 'campaign') return 'campaign';
  if (activeTab === 'traits') return 'traits';
  if (activeTab === 'types') return 'types';
  if (activeTab === 'calendars') return 'calendars';
  return isGm ? 'general:gm' : 'general:player';
}

/** Settings tab strip + active panel. The active tab + GM flag are passed as
 * literals by the `branch` arm (no render-body `.peek()`). */
function settingsTabContent(
  state: Signal<Model>,
  send: Send<Msg>,
  activeTab: SettingsTab,
  isGm: boolean,
): Renderable {
  const items = [
    { value: 'general', label: t('settings.tab.general'), active: activeTab === 'general' },
    ...(isGm
      ? [
          {
            value: 'campaign',
            label: t('settings.tab.campaign'),
            active: activeTab === 'campaign',
          },
          { value: 'traits', label: t('settings.tab.traits'), active: activeTab === 'traits' },
          { value: 'types', label: t('settings.tab.types'), active: activeTab === 'types' },
          {
            value: 'calendars',
            label: t('settings.tab.calendars'),
            active: activeTab === 'calendars',
          },
        ]
      : []),
  ];
  return [
    tabs({
      ariaLabel: t('settings.tab.list_label'),
      testid: 'settings-tabs',
      idBase: 'settings',
      className: 'settings-tabs',
      onActivate: (value) =>
        send(navMsg({ kind: 'settings', tab: asSettingsTab(value) }) as Msg),
      items,
    }),
    activeTab === 'campaign'
      ? viewCampaignSettings<Model, Msg>(state, send, (key, value) => ({
          type: 'set-config',
          payload: configSet(key as ConfigKey, value as never),
        }))
      : activeTab === 'traits'
        ? viewSettingsTraits(state, send)
        : activeTab === 'types'
          ? viewSettingsTypes(state, send)
          : activeTab === 'calendars'
            ? viewCalendarSettings<Model, Msg>(state, send, {
                onEditorMsg: (cm) => ({ type: 'calendar-editor', msg: cm }),
                onSave: () => ({ type: 'calendar-save' }),
                onRetire: (id) => ({ type: 'calendar-retire', calendarId: id }),
                onSetPrimary: (id) => ({ type: 'calendar-set-primary', calendarId: id }),
                onCloseOpenCycle: (handle, closeDay) => ({
                  type: 'calendar-close-cycle',
                  handle,
                  closeDay,
                }),
              })
            : viewSettingsGeneral(state, send),
  ];
}

/** Pending-policy select (static-value atom). The active value is passed as a
 * literal by the `branch` arm so each remount has the right value without a
 * render-body `.peek()`. */
function pendingPolicySelect(
  send: Send<Msg>,
  policyOptions: { value: PendingAutoRatifyPolicy; labelKey: string }[],
  value: PendingAutoRatifyPolicy,
): Mountable {
  return selectInput({
    id: 'settings-pending-policy-select',
    name: 'pending_auto_ratify_policy',
    testid: 'pending-policy-select',
    ariaLabel: t('settings.pending_policy.heading'),
    value,
    size: 'full',
    options: policyOptions.map((opt) => ({ value: opt.value, label: t(opt.labelKey) })),
    onChange: (next) => {
      const parsed = PendingAutoRatifyPolicySchema.safeParse(next);
      if (parsed.success) send({ type: 'set-pending-policy', policy: parsed.data });
    },
  });
}

/** The five auditor override choices the picker offers ('default' = no
 * override). `blocking_error` is intentionally NOT offered — the engine clamps
 * it un-overridable, so promoting a rule to it would be a no-op. */
type AuditorOverrideChoice = 'default' | 'error' | 'warning' | 'info' | 'off';

/** Which picker value to show for a rule: its stored override, or 'default'.
 * A stored `blocking_error` (never set via this UI) normalises to 'default'. */
function auditorOverrideChoice(s: Model, ruleId: string): AuditorOverrideChoice {
  const v = s.auditorRuleOverrides[ruleId];
  if (v === 'error' || v === 'warning' || v === 'info' || v === 'off') return v;
  return 'default';
}

function auditorOverrideSelect(
  send: Send<Msg>,
  ruleId: string,
  value: AuditorOverrideChoice,
): Mountable {
  return selectInput({
    id: `settings-auditor-${ruleId}`,
    name: `auditor_override_${ruleId}`,
    testid: `auditor-override-${ruleId}`,
    ariaLabel: t('settings.auditor.rule_aria', { rule: t(`auditor.rule.${ruleId}`) }),
    value,
    size: 'full',
    options: [
      { value: 'default', label: t('settings.auditor.option.default') },
      { value: 'error', label: t('auditor.severity.error') },
      { value: 'warning', label: t('auditor.severity.warning') },
      { value: 'info', label: t('auditor.severity.info') },
      { value: 'off', label: t('settings.auditor.option.off') },
    ],
    onChange: (next) => {
      if (next === 'default') {
        send({ type: 'set-auditor-override', rule: ruleId, value: 'default' });
        return;
      }
      const parsed = SeverityOverrideSchema.safeParse(next);
      if (parsed.success) send({ type: 'set-auditor-override', rule: ruleId, value: parsed.data });
    },
  });
}

/** General settings tab — pending auto-ratify policy + AI prompt extensions. */
function viewSettingsGeneral(state: Signal<Model>, send: Send<Msg>): Mountable {
  const policyOptions: { value: PendingAutoRatifyPolicy; labelKey: string }[] = [
    { value: 'manual', labelKey: 'settings.pending_policy.option.manual' },
    { value: 'auto_ratify', labelKey: 'settings.pending_policy.option.auto_ratify' },
    {
      value: 'appear_as_pending',
      labelKey: 'settings.pending_policy.option.appear_as_pending',
    },
  ];

  return div(
    {
      class: 'settings-tab-panel',
      'data-testid': 'settings-tab-general',
      id: 'settings-general-panel',
      role: 'tabpanel',
    },
    [
    card({ padding: 'lg', className: 'settings-section', testid: 'settings-pending-policy', children: [
      label({ for: 'settings-pending-policy-select', class: 'settings-label' }, [
        text(t('settings.pending_policy.heading')),
      ]),
      p({ class: 'settings-help' }, [text(t('settings.pending_policy.help'))]),
      // Design-system selectInput atom takes a static value, so a `branch`
      // on the policy re-mounts it when the value changes. Each arm passes
      // its literal value — no render-body `.peek()` (peek-in-slot lint).
      branch(state.at('pendingAutoRatifyPolicy'), {
        manual: () => [pendingPolicySelect(send, policyOptions, 'manual')],
        auto_ratify: () => [pendingPolicySelect(send, policyOptions, 'auto_ratify')],
        appear_as_pending: () => [pendingPolicySelect(send, policyOptions, 'appear_as_pending')],
      }),
    ] }),
    // Per-campaign AI prompt extensions (GM-only). Layered on top of the
    // admin-managed global templates by composeChatPrompt/composeImagePrompt.
    show(
      state.map((s) => s.session.mode === 'gm'),
      () => [
        card({ padding: 'lg', className: 'settings-section', testid: 'settings-ai', children: [
          h2({ class: 'settings-label' }, [text(t('settings.ai.heading'))]),
          p({ class: 'settings-help' }, [text(t('settings.ai.help'))]),
          show(
      state.map((s) => !aiSettingsLoaded(s)),
      () => [
              p({ class: 'settings-help', 'data-testid': 'ai-settings-loading' }, [
                text(t('settings.ai.loading')),
              ]),
            ],
    ),
          show(
      state.map((s) => aiSettingsLoaded(s)),
      () => AI_SCOPES.map((scope) => viewAiSettingRow(state, send, scope)),
    ),
        ] }),
      ],
    ),
    // Auditor rule severity overrides (§5.2.1) — GM-only noise dial. Each rule
    // gets a select: Default (engine default), Error / Warning / Info
    // (re-target), or Muted (off). A `branch` per row re-mounts the static-
    // value selectInput when the override changes.
    show(
      state.map((s) => s.session.mode === 'gm'),
      () => [
        card({ padding: 'lg', className: 'settings-section', testid: 'settings-auditor', children: [
          h2({ class: 'settings-label' }, [text(t('settings.auditor.heading'))]),
          p({ class: 'settings-help' }, [text(t('settings.auditor.help'))]),
          ...AUDITOR_RULES.map((rule) =>
            div(
              {
                class: 'settings-auditor-rule',
                'data-testid': `auditor-rule-${rule.id}`,
                'data-rule': rule.id,
              },
              [
                label(
                  { for: `settings-auditor-${rule.id}`, class: 'settings-auditor-rule-name' },
                  [text(t(`auditor.rule.${rule.id}`))],
                ),
                branch(state.map((s) => auditorOverrideChoice(s, rule.id)), {
                  default: () => [auditorOverrideSelect(send, rule.id, 'default')],
                  error: () => [auditorOverrideSelect(send, rule.id, 'error')],
                  warning: () => [auditorOverrideSelect(send, rule.id, 'warning')],
                  info: () => [auditorOverrideSelect(send, rule.id, 'info')],
                  off: () => [auditorOverrideSelect(send, rule.id, 'off')],
                }),
              ],
            ),
          ),
        ] }),
      ],
    ),
    // #89 — Data & backup. GM-only; only meaningful with a real campaign
    // (legacy/demo has no export endpoint). Shows "Last export: <relative>"
    // and a one-click export button (free at every tier — never gated).
    show(
      state.map((s) => s.session.mode === 'gm' && CAMPAIGN_ID !== null),
      () => [viewSettingsBackupCard(state, send)],
    ),
    // #263 — Campaign roster + invite management. GM-only; shown for any
    // real campaign (CAMPAIGN_ID !== null). Uses `branch` on `campaignRole`
    // so each arm receives a static literal `isOwner` value (no peek-in-slot).
    show(
      state.map((s) => s.session.mode === 'gm' && CAMPAIGN_ID !== null),
      () => [
        branch(
          state.map((s) => {
            const role = s.session.campaignRole;
            // Normalise: only 'owner' → true; anything else → false.
            return (role === 'owner' ? 'owner' : 'member') as 'owner' | 'member';
          }),
          {
            owner: () => [viewCampaignMembers(state, send as (msg: CampaignMembersMsg) => void, true)],
            member: () => [viewCampaignMembers(state, send as (msg: CampaignMembersMsg) => void, false)],
          },
        ),
      ],
    ),
  ]);
}

/** #89 — the Settings "Data & backup" card (GM-only, real campaign). */
function viewSettingsBackupCard(state: Signal<Model>, send: Send<Msg>): Mountable {
  const campaignId = CAMPAIGN_ID ?? '';
  return card({
    padding: 'lg',
    className: 'settings-section',
    testid: 'settings-backup',
    children: [
      h2({ class: 'settings-label' }, [text(t('settings.backup.heading'))]),
      p({ class: 'settings-help' }, [text(t('settings.backup.help'))]),
      div({ class: 'settings-backup-status' }, [
        span({ class: 'settings-backup-label' }, [
          text(`${t('settings.backup.last_export_label')}: `),
        ]),
        span(
          {
            class: 'settings-backup-value',
            'data-testid': 'settings-last-export',
          },
          [
            text(
              state.map((s) => formatRelativeTime(s.lastExportAt[campaignId] ?? null)),
            ),
          ],
        ),
      ]),
      div({ class: 'settings-actions' }, [
        uiButton({
          variant: 'primary',
          testid: 'settings-export-button',
          onClick: () => send({ type: 'export/run', campaignId }),
          children: [text(t('settings.backup.export_button'))],
        }),
        a(
          {
            class: 'settings-backup-link',
            href: '/recovery',
            'data-testid': 'settings-backup-readmore',
            onClick: (e: Event) => {
              e.preventDefault();
              send({ type: 'navigate', route: { kind: 'recovery' } });
            },
          },
          [text(t('settings.backup.read_more'))],
        ),
      ]),
    ],
  });
}

// ─── Settings: Custom traits & predicates tab (#schema-settings) ───────
//
// Replaces the trait-editor modal. When the form is open
// (`traitEditor !== null`) it renders inline; otherwise a list of the
// campaign's homebrew traits + predicates with Edit / Retire actions and a
// "+ New trait" button.

interface HomebrewTraitRow {
  id: string;
  label: string;
  rootsText: string;
  predicatesText: string;
  /** Localised "in use" badge text, or '' when nothing references it. */
  usageText: string;
}

interface HomebrewPredicateRow {
  id: string;
  label: string;
  kindText: string;
  cardinalityText: string;
  /** Localised "in use" badge text, or '' when no live fact references it. */
  usageText: string;
}

/**
 * Homebrew trait rows for the settings list. The definitions live in the
 * module-scope overlay (`getHomebrewDefs`), so this touches `s.entities` to
 * take a reactive dependency on the op-log fold — every pack op rebuilds
 * entities AND the overlay in the same `rebuildEntities` pass, so the list
 * re-evaluates whenever a definition changes.
 */
function homebrewTraitRows(s: Model): HomebrewTraitRow[] {
  return getHomebrewDefs().traits.map((tr) => {
    const usage = traitUsage(s.entities, tr.id);
    return {
      id: tr.id,
      label: localizedText(tr.label) || tr.id,
      rootsText: tr.applies_to.map((r) => t(`entity.root.${r}`)).join(', '),
      predicatesText: tr.predicates.join(', '),
      usageText:
        usage.entityCount === 0 && usage.factCount === 0
          ? ''
          : t('settings.traits.in_use', {
              entities: usage.entityCount,
              facts: usage.factCount,
            }),
    };
  });
}

function homebrewPredicateRows(s: Model): HomebrewPredicateRow[] {
  return getHomebrewDefs().predicates.map((pred) => {
    const factCount = predicateUsage(s.entities, pred.id);
    return {
      id: pred.id,
      label: localizedText(pred.label) || pred.id,
      kindText: pred.value_kind.join(' | '),
      cardinalityText: hasObjectUniquenessKey(pred) ? 'many' : 'single',
      usageText:
        factCount === 0
          ? ''
          : t('settings.predicates.in_use', { facts: factCount }),
    };
  });
}

// ─── Shared schema-list row (traits / predicates / types) ──────────────
//
// The three managed-vocabulary lists (custom traits, custom predicates,
// custom entity types) render structurally identical rows: a name, up to two
// metadata caption lines, an optional "in use" badge, and a trailing action
// group (Edit / Retire, or Retire-only for predicates). `viewSchemaListRow`
// is the single source of that markup so the three lists stay visually
// locked together; only the field mapping + action set differ per caller.

/** The string-valued field names of a row interface — the only fields the
 * row template renders as text. */
type StringFieldOf<Row> = {
  [K in keyof Row]: Row[K] extends string ? K & string : never;
}[keyof Row];

/** A string field on the row item, rendered as a metadata caption line. */
interface SchemaRowMetaLine<Row> {
  field: StringFieldOf<Row>;
  testid?: string;
}

/** A trailing action button on a schema row. `onActivate` receives the row
 * id (peeked from the item) so callers don't re-derive it. */
interface SchemaRowAction {
  labelKey: string;
  testid: string;
  /** `'secondary'` = neutral (Edit); `'retire'` = destructive ghost. */
  variant: 'secondary' | 'retire';
  onActivate: (id: string) => void;
}

/**
 * Render one schema-list `<li>`. Shared by the traits / predicates / types
 * lists. `metaName` + `metaLines` map item fields to the name + caption
 * lines; `usageField` (when present) renders the amber "in use" badge;
 * `actions` is the trailing button group.
 */
function viewSchemaListRow<Row extends { id: string }>(
  item: Signal<Row>,
  send: Send<Msg>,
  opts: {
    rowClass: string;
    rowTestid: string;
    metaName: StringFieldOf<Row>;
    metaLines: readonly SchemaRowMetaLine<Row>[];
    usageField?: StringFieldOf<Row>;
    usageTestid?: string;
    actions: readonly SchemaRowAction[];
  },
): Mountable {
  // Row item fields are read via `item.map` (not `item.at`): the generic
  // `Row` can't prove a `ValidPath` to the path-typed `.at` overload, but a
  // `.map` computed body is row-local and type-safe here.
  const fieldText = (field: StringFieldOf<Row>): Signal<string> =>
    item.map((r) => r[field] as string);
  const metaChildren: Mountable[] = [
    span({ class: 'settings-trait-name' }, [text(fieldText(opts.metaName))]),
    ...opts.metaLines.map((line, idx) =>
      span(
        {
          class: idx === 0 ? 'settings-trait-roots' : 'settings-trait-predicates',
          ...(line.testid ? { 'data-testid': line.testid } : {}),
        },
        [text(fieldText(line.field))],
      ),
    ),
  ];
  if (opts.usageField) {
    const usageField = opts.usageField;
    metaChildren.push(
      show(
        fieldText(usageField).map((u) => u !== ''),
        () => [
          badge({
            tone: 'warn',
            ...(opts.usageTestid ? { testid: opts.usageTestid } : {}),
            children: [text(fieldText(usageField))],
          }),
        ],
      ),
    );
  }
  return li({ class: opts.rowClass, 'data-testid': opts.rowTestid }, [
    div({ class: 'settings-trait-meta' }, metaChildren),
    div({ class: 'settings-trait-actions' }, [
      ...opts.actions.map((action) =>
        uiButton({
          variant: action.variant === 'retire' ? 'danger' : 'secondary',
          ...(action.variant === 'retire' ? { className: 'settings-trait-retire' } : {}),
          testid: action.testid,
          onClick: () => action.onActivate(item.map((r) => r.id).peek()),
          children: [text(t(action.labelKey))],
        }),
      ),
    ]),
  ]);
}

function viewSettingsTraits(state: Signal<Model>, send: Send<Msg>): Mountable {
  return div(
    {
      class: 'settings-tab-panel',
      'data-testid': 'settings-tab-traits',
      id: 'settings-traits-panel',
      role: 'tabpanel',
    },
    [
    // The editor renders ABOVE the list when open — the list stays visible so
    // existing traits never vanish while you author/edit one (list-first CRUD
    // surface). Previously the editor *replaced* the list, which read as "my
    // custom trait isn't here" whenever the create/edit form was open.
    show(
      state.map((s) => s.traitEditor !== null),
      () => [viewTraitEditorInline(state, send)],
    ),
    viewTraitsList(state, send),
  ]);
}

function viewTraitsList(state: Signal<Model>, send: Send<Msg>): Mountable {
  return div({ class: 'settings-traits-list' }, [
    // level 2: top-level section of the settings page (under the page h1).
    // The "+ New trait" action is hidden while the editor is open above — a
    // second button there would discard the in-progress form.
    sectionHeading({
      title: text(t('settings.traits.heading')),
      level: 2,
      actions: show(
        state.map((s) => s.traitEditor === null),
        () => [
          uiButton({
            variant: 'secondary',
            testid: 'settings-open-units',
            onClick: () => send({ type: 'open-units-editor' }),
            children: [text(t('settings.traits.units'))],
          }),
          // T9 (#283) — ADR-0008 §3 entry point: launch an LLM-led
          // interactive authoring session for a brand-new trait
          // (`existing_id: null`). GM-only surface (the whole settings Traits
          // tab is GM). AI is optional — this sits alongside "+ New trait",
          // never replacing the manual editor. No client-side LLM-capability
          // gate exists (mirroring the sibling ✨ entries — bundle-gen /
          // page-draft — which also render unconditionally); the server + the
          // T8 panel surface `capability_unsupported` as an error event when
          // no `function_calling`-capable binding is configured.
          uiButton({
            variant: 'secondary',
            testid: 'settings-trait-author-ai',
            className: 'settings-trait-author-ai',
            onClick: () => send({ type: 'open-authoring', target: { existing_id: null } }),
            children: [text(t('authoring.entry_trait'))],
          }),
          uiButton({
            variant: 'primary',
            testid: 'settings-new-trait',
            onClick: () => send({ type: 'open-trait-editor-blank' }),
            children: [text(t('settings.traits.new'))],
          }),
        ],
      ),
    }),
    p({ class: 'settings-help' }, [text(t('settings.traits.help'))]),
    show(
      state.map((s) => homebrewTraitRows(s).length === 0),
      () => [
        emptyState({
          testid: 'settings-traits-empty',
          message: text(t('settings.traits.empty')),
        }),
      ],
    ),
    ul(
      { class: 'settings-trait-rows', 'data-testid': 'settings-trait-rows' },
      [each(state.map((s) => homebrewTraitRows(s)), {
        key: (row) => row.id,
        render: (item) => [
          viewSchemaListRow<HomebrewTraitRow>(item, send, {
            rowClass: 'settings-trait-row',
            rowTestid: 'settings-trait-row',
            metaName: 'label',
            metaLines: [{ field: 'rootsText' }, { field: 'predicatesText' }],
            usageField: 'usageText',
            usageTestid: 'settings-trait-usage',
            actions: [
              {
                labelKey: 'settings.traits.edit',
                testid: 'settings-trait-edit',
                variant: 'secondary',
                onActivate: (id) => send({ type: 'edit-trait', traitId: id }),
              },
              {
                labelKey: 'settings.traits.retire',
                testid: 'settings-trait-retire',
                variant: 'retire',
                onActivate: (id) => send({ type: 'retire-trait', traitId: id }),
              },
            ],
          }),
        ],
      })],
    ),
    // Custom-predicates group — a second labelled list inside the same card,
    // separated by a hairline divider for visual grouping.
    div({ class: 'settings-predicates-divider' }, [
      // level 2: a sibling labelled group within the traits tab.
      sectionHeading({ title: text(t('settings.predicates.heading')), level: 2 }),
      p({ class: 'settings-help' }, [text(t('settings.predicates.help'))]),
      show(
        state.map((s) => homebrewPredicateRows(s).length === 0),
        () => [
          emptyState({
            testid: 'settings-predicates-empty',
            message: text(t('settings.predicates.empty')),
          }),
        ],
      ),
      ul(
        { class: 'settings-predicate-rows', 'data-testid': 'settings-predicate-rows' },
        [each(state.map((s) => homebrewPredicateRows(s)), {
          key: (row) => row.id,
          render: (item) => [
            viewSchemaListRow<HomebrewPredicateRow>(item, send, {
              rowClass: 'settings-predicate-row',
              rowTestid: 'settings-predicate-row',
              metaName: 'label',
              metaLines: [{ field: 'kindText' }, { field: 'cardinalityText' }],
              usageField: 'usageText',
              usageTestid: 'settings-predicate-usage',
              actions: [
                {
                  labelKey: 'settings.predicates.retire',
                  testid: 'settings-predicate-retire',
                  variant: 'retire',
                  onActivate: (id) => send({ type: 'retire-predicate', predicateId: id }),
                },
              ],
            }),
          ],
        })],
      ),
    ]),
  ]);
}

/**
 * Usage-aware retire confirmation. Mounted when `schemaRetireConfirm` is
 * set — i.e. the GM asked to retire a trait/predicate that is still in use.
 * Mirrors `viewTraitDetachConfirm`: an alertdialog that names the
 * definition, quotes how much depends on it, and stresses that retire is
 * non-destructive (facts persist, reappear on re-define).
 */
function viewSchemaRetireConfirm(state: Signal<Model>, send: Send<Msg>): Mountable {
  return modal({
    open: state.map((s) => s.schemaRetireConfirm !== null),
    onDismiss: () => send({ type: 'cancel-schema-retire' }),
    labels: { close: t('settings.schema.retire_cancel') },
    labelId: 'schema-retire-heading',
    describedById: 'schema-retire-body',
    role: 'alertdialog',
    testid: 'schema-retire-confirm',
    panelClass: 'schema-retire-panel',
    size: 'sm',
    children: [
      h2({ id: 'schema-retire-heading' }, [
        text(
          state.map((s) => {
            const c = s.schemaRetireConfirm;
            if (!c) return '';
            return t(`settings.schema.retire_heading_${c.target}`);
          }),
        ),
      ]),
      p(
        {
          id: 'schema-retire-body',
          class: 'schema-retire-body',
          'data-testid': 'schema-retire-body',
        },
        [
          text(
            state.map((s) => {
              const c = s.schemaRetireConfirm;
              if (!c) return '';
              return c.target === 'trait'
                ? t('settings.schema.retire_body_trait', {
                    label: c.label,
                    entities: c.entityCount,
                    facts: c.factCount,
                  })
                : t('settings.schema.retire_body_predicate', {
                    label: c.label,
                    facts: c.factCount,
                  });
            }),
          ),
        ],
      ),
      div({ class: 'schema-retire-actions' }, [
        uiButton({
          variant: 'ghost',
          className: 'schema-retire-cancel',
          testid: 'schema-retire-cancel',
          onClick: () => send({ type: 'cancel-schema-retire' }),
          children: [text(t('settings.schema.retire_cancel'))],
        }),
        uiButton({
          variant: 'danger',
          className: 'schema-retire-confirm',
          testid: 'schema-retire-confirm-button',
          onClick: () => send({ type: 'confirm-schema-retire' }),
          children: [text(t('settings.schema.retire_confirm'))],
        }),
      ]),
    ],
  });
}

/** Entity soft-delete confirm (reversible "move to Trash"). */
function viewEntityDeleteConfirm(state: Signal<Model>, send: Send<Msg>): Mountable {
  return modal({
    open: state.map((s) => s.entityDeleteConfirm !== null),
    onDismiss: () => send({ type: 'cancel-delete-entity' }),
    labels: { close: t('entity.delete.cancel') },
    labelId: 'entity-delete-heading',
    describedById: 'entity-delete-body',
    role: 'alertdialog',
    testid: 'entity-delete-confirm',
    panelClass: 'entity-delete-panel',
    size: 'sm',
    children: [
      h2({ id: 'entity-delete-heading' }, [text(t('entity.delete.heading'))]),
      p({ id: 'entity-delete-body', class: 'entity-delete-body' }, [
        text(
          state.map((s) =>
            t('entity.delete.body', { name: s.entityDeleteConfirm?.name ?? '' }),
          ),
        ),
      ]),
      div({ class: 'entity-delete-actions' }, [
        uiButton({
          variant: 'ghost',
          className: 'entity-delete-cancel',
          testid: 'entity-delete-cancel',
          onClick: () => send({ type: 'cancel-delete-entity' }),
          children: [text(t('entity.delete.cancel'))],
        }),
        uiButton({
          variant: 'danger',
          className: 'entity-delete-confirm-btn',
          testid: 'entity-delete-confirm-button',
          onClick: () => send({ type: 'confirm-delete-entity' }),
          children: [text(t('entity.delete.confirm'))],
        }),
      ]),
    ],
  });
}

// ─── Controller picker (III.B.4 — assign a player to a PC) ─────────────────

/**
 * #264 — Derive the member→Character map from the local replica's entity
 * facts. Called whenever `entities` changes (replica/entities-rebuilt) so the
 * roster panel shows up-to-date Character assignments without an extra API
 * call. The data lives in the op log / local replica already.
 *
 * Returns `Record<userId, { id, name }[]>`. Only non-soft-deleted Character
 * entities with a non-empty `controlled_by` value are included. If no
 * Characters exist yet (pre-seed or fresh campaign) the map is empty `{}`.
 */
function computeMemberControlledCharacters(
  entities: Record<string, EntityState>,
): Record<string, readonly { id: string; name: string }[]> {
  const out: Record<string, { id: string; name: string }[]> = {};
  for (const entity of Object.values(entities)) {
    if (entity.root !== 'character') continue;
    if (entity.soft_deleted) continue;
    const userId = currentFactValue(entity.facts[OFFICIAL_CORE_PREDICATE_IDS.controlled_by]);
    if (typeof userId !== 'string' || userId.length === 0) continue;
    const rawName = currentFactValue(entity.facts.name);
    const name = typeof rawName === 'string' && rawName.length > 0 ? rawName : entity.id;
    if (!out[userId]) out[userId] = [];
    out[userId].push({ id: entity.id, name });
  }
  // Sort each member's Characters by name for deterministic display.
  for (const chars of Object.values(out)) {
    chars.sort((a, b) => a.name.localeCompare(b.name));
  }
  return out;
}

/** The User id currently bound as this Character's controller, or null. */
function controllerCurrentUserId(s: Model, entityId: string): string | null {
  const e = s.entities[entityId];
  if (!e) return null;
  const v = currentFactValue(e.facts[OFFICIAL_CORE_PREDICATE_IDS.controlled_by]);
  return typeof v === 'string' && v.length > 0 ? v : null;
}

/** Map a User id to a member email (falls back to the raw id pre-load). */
function controllerEmailFor(s: Model, userId: string): string {
  return s.campaignMembers?.find((m) => m.user_id === userId)?.email ?? userId;
}

/** Header button label: the bound controller's email, or the "assign" action. */
function controllerButtonLabel(s: Model, entityId: string): string {
  const uid = controllerCurrentUserId(s, entityId);
  return uid
    ? t('controller.assigned', { name: controllerEmailFor(s, uid) })
    : t('entity.set_controlled_by');
}

interface ControllerMemberRow {
  key: string;
  entityId: string;
  userId: string;
  label: string;
  isCurrent: boolean;
}

/** Member rows for the picker, marking the current controller. */
function controllerMemberRows(s: Model): ControllerMemberRow[] {
  const eid = s.controllerPicker?.entityId;
  if (!eid) return [];
  const current = controllerCurrentUserId(s, eid);
  // Exclude the implicit owner (is_owner=true) — they're the GM and are
  // not a controlled_by candidate (controlling PCs is a player affordance).
  return (s.campaignMembers ?? [])
    .filter((m) => !m.is_owner)
    .map((m) => ({
      key: m.user_id,
      entityId: eid,
      userId: m.user_id,
      label: `${m.email} · ${m.role}`,
      isCurrent: m.user_id === current,
    }));
}

/** GM-only modal: pick a Campaign member to control this PC (or clear). */
function viewControllerPicker(state: Signal<Model>, send: Send<Msg>): Mountable {
  return modal({
    open: state.map((s) => s.controllerPicker !== null),
    onDismiss: () => send({ type: 'close-controller-picker' }),
    labels: { close: t('controller.cancel') },
    labelId: 'controller-picker-heading',
    role: 'dialog',
    testid: 'controller-picker',
    panelClass: 'controller-picker-panel',
    size: 'sm',
    children: [
      h2({ id: 'controller-picker-heading' }, [text(t('controller.heading'))]),
      p({ class: 'controller-picker-hint' }, [text(t('controller.hint'))]),
      show(
        state.map((s) => (s.campaignMembers?.length ?? 0) === 0),
        () => [
          p({ class: 'controller-picker-empty', 'data-testid': 'controller-picker-empty' }, [
            text(t('controller.empty')),
          ]),
        ],
      ),
      div({ class: 'controller-picker-list', 'data-testid': 'controller-picker-list' }, [
        each(state.map((s) => controllerMemberRows(s)), {
          key: (row) => row.key,
          render: (item) => [
            uiButton({
              variant: 'ghost',
              className: item.map((r) =>
                r.isCurrent ? 'controller-option is-current' : 'controller-option',
              ),
              testid: 'controller-option',
              attrs: {
                'data-user': item.at('userId'),
                'aria-pressed': item.map((r) => (r.isCurrent ? 'true' : 'false')),
              },
              onClick: () =>
                send({
                  type: 'set-controller',
                  entityId: item.at('entityId').peek(),
                  userId: item.at('userId').peek(),
                }),
              children: [text(item.at('label'))],
            }),
          ],
        }),
      ]),
      div({ class: 'controller-picker-actions' }, [
        show(
          state.map((s) => controllerCurrentUserId(s, s.controllerPicker?.entityId ?? '') !== null),
          () => [
            uiButton({
              variant: 'ghost',
              className: 'controller-clear',
              testid: 'controller-clear',
              onClick: () => {
                const eid = state.peek().controllerPicker?.entityId;
                if (eid) send({ type: 'clear-controller', entityId: eid });
              },
              children: [text(t('controller.clear'))],
            }),
          ],
        ),
        uiButton({
          variant: 'ghost',
          className: 'controller-cancel',
          testid: 'controller-cancel',
          onClick: () => send({ type: 'close-controller-picker' }),
          children: [text(t('controller.cancel'))],
        }),
      ]),
    ],
  });
}

/** Inline trait-editor form (the modal's body, rendered in the traits tab). */
function viewTraitEditorInline(state: Signal<Model>, send: Send<Msg>): Mountable {
  return section(
    {
      class: 'settings-section trait-editor-inline',
      'data-testid': 'trait-editor',
      'aria-label': t('trait_editor.heading'),
    },
    [
      sectionHeading({
        title: text(state.map((s) => t(s.traitEditor?.mode === 'edit' ? 'trait_editor.heading_edit' : 'trait_editor.heading'))),
        level: 2,
      }),
      ...traitEditorBody(state, send),
    ],
  );
}

/**
 * #schema-settings — `applies_to` roots multi-select for the inline trait
 * editor. The standalone page is not seeded from an entity, so the GM picks
 * the target roots here; `buildTraitDefinitionOps` reads `appliesTo`.
 */
function traitEditorAppliesToSelector(state: Signal<Model>, send: Send<Msg>): Mountable {
  return div({ class: 'trait-editor-applies-to', 'data-testid': 'trait-editor-applies-to' }, [
    span({ class: 'trait-editor-label' }, [text(t('trait_editor.applies_to_label'))]),
    div(
      { class: 'trait-editor-applies-to-options' },
      ALL_ENTITY_ROOTS.map((root) =>
        label({ class: 'trait-editor-applies-to-option' }, [
          input({
            type: 'checkbox',
            'data-testid': `trait-editor-applies-to-${root}`,
            checked: state.map((s) => (s.traitEditor?.appliesTo ?? []).includes(root)),
            onChange: () => send({ type: 'trait-editor-toggle-applies-to', root }),
          }),
          text(t(`entity.root.${root}`)),
        ]),
      ),
    ),
  ]);
}

// ─── Settings: Entity types tab (#schema-settings / 03a-entities.md §4) ──
//
// Custom entity types are `(root, traits[])` quick-create templates. When the
// editor form is open (`typeEditor !== null`) it renders inline; otherwise a
// list of the campaign's homebrew types with Edit / Retire and a "+ New type"
// button. Picking a type in the root picker pre-attaches its traits (see
// viewRootPicker). All `s.typeEditor` reads route through whole-object helpers
// so the App component stays within the 62-path bitmask budget.

function typeEditorField(s: Model, key: 'typeId' | 'label'): string {
  const te = s.typeEditor;
  return te ? te[key] : '';
}
function typeEditorRoot(s: Model): EntityRoot {
  const te = s.typeEditor;
  return te ? te.root : 'location';
}
function typeEditorIsEdit(s: Model): boolean {
  const te = s.typeEditor;
  return te?.mode === 'edit';
}
function typeEditorErrorText(s: Model): string {
  const te = s.typeEditor;
  return te?.error ?? '';
}
function typeEditorHasError(s: Model): boolean {
  const te = s.typeEditor;
  return te != null && te.error != null;
}
function typeEditorHasTrait(s: Model, traitId: string): boolean {
  const te = s.typeEditor;
  return (te?.traits ?? []).includes(traitId);
}
function typeEditorClosed(s: Model): boolean {
  return s.typeEditor === null;
}

interface HomebrewTypeRow {
  id: string;
  label: string;
  /** Raw root + traits — used by the root-picker quick-create section. */
  root: EntityRoot;
  traits: readonly string[];
  /** Display strings for the settings list. */
  rootText: string;
  traitsText: string;
}

/**
 * Homebrew type rows (reactive via the `s.entities` touch). Shared by the
 * settings Types list and the root-picker quick-create section.
 */
function homebrewTypeRows(s: Model): HomebrewTypeRow[] {
  void s.entities;
  return getHomebrewDefs().types.map(typeToRow);
}

/**
 * ALL types (bundled core + homebrew) — used by the root-picker quick-create
 * section so core templates like "Dragon"/"Mount" are offered too. The
 * settings Types list stays homebrew-only (core types aren't editable).
 */
function allTypeRows(s: Model): HomebrewTypeRow[] {
  void s.entities;
  return [...allTypeDeclarations()].map(typeToRow);
}

function typeToRow(ty: TypeDefinition): HomebrewTypeRow {
  return {
    id: ty.id,
    label: localizedText(ty.label) || ty.id,
    root: ty.root,
    traits: ty.traits,
    rootText: t(`entity.root.${ty.root}`),
    traitsText: ty.traits.join(', '),
  };
}

function viewSettingsTypes(state: Signal<Model>, send: Send<Msg>): Mountable {
  return div(
    {
      class: 'settings-tab-panel',
      'data-testid': 'settings-tab-types',
      id: 'settings-types-panel',
      role: 'tabpanel',
    },
    [
      show(
      state.map((s) => !typeEditorClosed(s)),
      () => [viewTypeEditorInline(state, send)],
    ),
      show(
      state.map((s) => typeEditorClosed(s)),
      () => [viewTypesList(state, send)],
    ),
    ],
  );
}

function viewTypesList(state: Signal<Model>, send: Send<Msg>): Mountable {
  return div({ class: 'settings-types-list' }, [
    // level 2: top-level section of the settings page (under the page h1).
    sectionHeading({
      title: text(t('settings.types.heading')),
      level: 2,
      actions: uiButton({
        variant: 'primary',
        testid: 'settings-new-type',
        onClick: () => send({ type: 'open-type-editor-blank' }),
        children: [text(t('settings.types.new'))],
      }),
    }),
    p({ class: 'settings-help' }, [text(t('settings.types.help'))]),
    show(
      state.map((s) => homebrewTypeRows(s).length === 0),
      () => [
        emptyState({
          testid: 'settings-types-empty',
          message: text(t('settings.types.empty')),
        }),
      ],
    ),
    ul(
      { class: 'settings-trait-rows', 'data-testid': 'settings-type-rows' },
      [each(state.map((s) => homebrewTypeRows(s)), {
        key: (row) => row.id,
        render: (item) => [
          viewSchemaListRow<HomebrewTypeRow>(item, send, {
            rowClass: 'settings-trait-row',
            rowTestid: 'settings-type-row',
            metaName: 'label',
            metaLines: [{ field: 'rootText' }, { field: 'traitsText' }],
            actions: [
              {
                labelKey: 'settings.types.edit',
                testid: 'settings-type-edit',
                variant: 'secondary',
                onActivate: (id) => send({ type: 'edit-type', typeId: id }),
              },
              {
                labelKey: 'settings.types.retire',
                testid: 'settings-type-retire',
                variant: 'retire',
                onActivate: (id) => send({ type: 'retire-type', typeId: id }),
              },
            ],
          }),
        ],
      })],
    ),
  ]);
}

/** Type-editor root select + root-scoped traits list. The active root is
 * passed as a literal by the `branch` arm (no render-body `.peek()`). */
function typeEditorRootPicker(
  state: Signal<Model>,
  send: Send<Msg>,
  rootValue: EntityRoot,
): Renderable {
  return [
    selectInput({
      id: 'type-editor-root',
      name: 'type_editor_root',
      testid: 'type-editor-root',
      ariaLabel: t('type_editor.root_label'),
      value: rootValue,
      size: 'md',
      options: ALL_ENTITY_ROOTS.map((r) => ({ value: r, label: t(`entity.root.${r}`) })),
      onChange: (next) => send({ type: 'type-editor-set', patch: { root: next as EntityRoot } }),
    }),
    viewTypeEditorTraits(state, send, rootValue),
  ];
}

/** Inline type-editor form (rendered in the Types tab when typeEditor !== null). */
function viewTypeEditorInline(state: Signal<Model>, send: Send<Msg>): Mountable {
  return section(
    {
      class: 'settings-section type-editor-inline',
      'data-testid': 'type-editor',
      'aria-label': t('type_editor.heading'),
    },
    [
      sectionHeading({
        title: text(state.map((s) => t(typeEditorIsEdit(s) ? 'type_editor.heading_edit' : 'type_editor.heading'))),
        level: 2,
      }),
      p({ class: 'settings-help' }, [text(t('type_editor.description'))]),
      label({ for: 'type-editor-id', class: 'trait-editor-label' }, [
        text(t('type_editor.type_id_label')),
      ]),
      textInput({
        testid: 'type-editor-id',
        value: state.map((s) => typeEditorField(s, 'typeId')),
        // The id is the stable handle; locked in edit mode.
        disabled: state.map((s) => typeEditorIsEdit(s)),
        placeholder: t('type_editor.type_id_placeholder'),
        ariaLabel: t('type_editor.type_id_label'),
        onInput: (value: string) => send({ type: 'type-editor-set', patch: { typeId: value } }),
        attrs: { id: 'type-editor-id' },
      }),
      label({ for: 'type-editor-label', class: 'trait-editor-label' }, [
        text(t('type_editor.label_label')),
      ]),
      textInput({
        testid: 'type-editor-label',
        value: state.map((s) => typeEditorField(s, 'label')),
        placeholder: t('type_editor.label_placeholder'),
        ariaLabel: t('type_editor.label_label'),
        onInput: (value: string) => send({ type: 'type-editor-set', patch: { label: value } }),
        attrs: { id: 'type-editor-label' },
      }),
      label({ class: 'trait-editor-label' }, [text(t('type_editor.root_label'))]),
      // Root select + the traits list re-mount together when the root changes
      // (available traits are root-scoped). selectInput takes a static value,
      // so a `branch` keyed on the root drives the re-mount; the arm key gives
      // the active root as a literal (no render-body `.peek()`).
      branch(state.map((s) => typeEditorRoot(s)), {
        location: () => typeEditorRootPicker(state, send, 'location'),
        character: () => typeEditorRootPicker(state, send, 'character'),
        faction: () => typeEditorRootPicker(state, send, 'faction'),
        event: () => typeEditorRootPicker(state, send, 'event'),
        item: () => typeEditorRootPicker(state, send, 'item'),
        lore: () => typeEditorRootPicker(state, send, 'lore'),
      }),
      show(
      state.map((s) => typeEditorHasError(s)),
      () => [
          p({ class: 'trait-editor-error', role: 'alert', 'data-testid': 'type-editor-error' }, [
            text(state.map((s) => typeEditorErrorText(s))),
          ]),
        ],
    ),
      div({ class: 'trait-editor-actions' }, [
        uiButton({
          variant: 'secondary',
          testid: 'type-editor-cancel',
          onClick: () => send({ type: 'close-type-editor' }),
          children: [text(t('type_editor.cancel'))],
        }),
        uiButton({
          variant: 'primary',
          testid: 'type-editor-save',
          onClick: () => send({ type: 'type-editor-submit' }),
          children: [text(state.map((s) => t(typeEditorIsEdit(s) ? 'type_editor.save_edit' : 'type_editor.save')))],
        }),
      ]),
    ],
  );
}

/** Trait checkboxes for the type editor, filtered to the chosen root. */
function viewTypeEditorTraits(state: Signal<Model>, send: Send<Msg>, root: EntityRoot): Mountable {
  const available = traitsAvailableForRoot(root);
  return div({ class: 'type-editor-traits', 'data-testid': 'type-editor-traits' }, [
    span({ class: 'trait-editor-label' }, [text(t('type_editor.traits_label'))]),
    ...(available.length === 0
      ? [p({ class: 'settings-help' }, [text(t('type_editor.traits_empty'))])]
      : [
          div(
            { class: 'type-editor-traits-options' },
            available.map((tr) =>
              label({ class: 'type-editor-trait-option' }, [
                input({
                  type: 'checkbox',
                  'data-testid': `type-editor-trait-${tr.id}`,
                  checked: state.map((s) => typeEditorHasTrait(s, tr.id)),
                  onChange: () => send({ type: 'type-editor-toggle-trait', traitId: tr.id }),
                }),
                text(localizedText(tr.label) || tr.id),
              ]),
            ),
          ),
        ]),
  ]);
}


// ---------- #266 — flat co-author surfacing (07 §2.2, workspace-home-flat.html) ----------
//
// The `flat` auto-canon path is enforced by #256 (member writes land with
// `authored_by:"player"`, no ratification; the LLM still routes through
// draft/pending). This surface makes co-authorship LEGIBLE:
//   - Recent player-authored CANON facts with author provenance chip.
//   - Per-member canon fact count (no role labels — co-authors, not GM/Player).
//
// Pure selectors over Model → stable plain objects (llui-safe, no Maps/Sets).
// The view is shown at the top of `viewLocationsList` when
// `config.facilitation_mode === 'flat'`.

/** Maximum number of recent co-authored facts to surface in the flat home panel. */
const FLAT_HOME_MAX_RECENT = 8;

/**
 * One row for the "recent additions" section of the flat Campaign home.
 * `author` is the display label for the authoring member (email or user id
 * for player-authored facts; never 'GM' or 'AI' — flat mode co-authors only).
 */
export interface FlatRecentRow {
  key: string;
  entityId: string;
  entityName: string;
  root: EntityRoot | '';
  predicate: string;
  predicateLabel: string;
  value: string;
  /** Display label for the authoring member (email or user id fallback). */
  author: string;
  /** The raw author id from the `player` author object (empty when unavailable). */
  authorId: string;
}

/** Internal extended row used while sorting by timestamp before stripping it. */
interface FlatRecentRowSortable extends FlatRecentRow {
  written_at_real: string;
}

/**
 * Resolve the most-recent player-authored canon facts for the flat Campaign
 * home surface. Scans `entities`, selects facts with:
 *   - `status === 'canon'`
 *   - head version `author` contains `"player"` (case-insensitive substring of
 *     the stored JSON-stringified Author)
 * Returns up to {@link FLAT_HOME_MAX_RECENT} rows, ordered by
 * `written_at_real` descending (most recent first).
 *
 * Pure selector — no side effects, JSON-serialisable output.
 */
export function flatRecentCanonRows(s: Model): FlatRecentRow[] {
  const registry = loadGmRegistry();
  const rows: FlatRecentRowSortable[] = [];
  for (const entity of Object.values(s.entities)) {
    if (entity.soft_deleted) continue;
    for (const [pred, fact] of Object.entries(entity.facts)) {
      if (fact.status !== 'canon' || fact.soft_deleted) continue;
      const latest = fact.versions[fact.versions.length - 1];
      if (!latest) continue;
      // Only surface player-authored facts (the flat auto-canon path).
      const authorRaw = latest.author.toLowerCase();
      if (!authorRaw.includes('"player"') && !authorRaw.includes("'player'")) continue;
      // Parse the author id from the stored JSON (best-effort; falls back to '').
      let authorId = '';
      try {
        const parsed = JSON.parse(latest.author) as { kind?: string; id?: string };
        if (parsed.kind === 'player' && typeof parsed.id === 'string') authorId = parsed.id;
      } catch {
        // Malformed author — skip id extraction, keep the row.
      }
      const displayAuthor = authorId
        ? (s.campaignMembers?.find((m) => m.user_id === authorId)?.email ?? authorId)
        : authorId || '?';
      const v = latest.value;
      // Route through formatFactValue so structured values (tuples, dimensioned
      // numbers, objects) render legibly instead of `[object Object]`. The
      // null/undefined guard stays '' (not '—') so asset-id lookups + the empty
      // cell keep working; a string value passes through unchanged.
      const value = v === undefined || v === null ? '' : formatFactValue(v);
      const base = splitSegmentKey(pred).base;
      const decl = registry.lookup(base);
      rows.push({
        key: `${entity.id}/${pred}`,
        entityId: entity.id,
        entityName: entityNameOrId(s, entity.id),
        root: entity.root,
        predicate: pred,
        predicateLabel: decl?.label ? localizedText(decl.label) : pred,
        value,
        author: displayAuthor,
        authorId,
        written_at_real: latest.written_at_real,
      });
    }
  }
  // Sort most-recent-first; cap at FLAT_HOME_MAX_RECENT; strip sort key.
  return rows
    .sort((a, b) => b.written_at_real.localeCompare(a.written_at_real))
    .slice(0, FLAT_HOME_MAX_RECENT)
    .map(({ written_at_real: _written, ...r }) => r);
}

/** One row for the "co-authors" contribution summary in the flat Campaign home. */
export interface FlatMemberRow {
  key: string;
  /** Display label (email or user id fallback). */
  label: string;
  /** Total player-authored canon fact count for this member. */
  count: number;
  /** Initials for the avatar tile (first character, uppercased). */
  initials: string;
}

/**
 * Per-member player-authored canon fact count for the flat Campaign home.
 * Counts every `(entity, fact)` pair where `status === 'canon'` and the head
 * author is `kind: 'player'` with a matching `id`. Members without any facts
 * are included (count = 0) so the roster is always visible.
 *
 * Pure selector — no side effects, JSON-serialisable output.
 */
export function flatMemberContributionRows(s: Model): FlatMemberRow[] {
  const members = s.campaignMembers;
  if (!members || members.length === 0) return [];

  // Count player-authored canon facts per author id.
  const counts: Record<string, number> = {};
  for (const entity of Object.values(s.entities)) {
    if (entity.soft_deleted) continue;
    for (const fact of Object.values(entity.facts)) {
      if (fact.status !== 'canon' || fact.soft_deleted) continue;
      const latest = fact.versions[fact.versions.length - 1];
      if (!latest) continue;
      const authorRaw = latest.author.toLowerCase();
      if (!authorRaw.includes('"player"') && !authorRaw.includes("'player'")) continue;
      let authorId = '';
      try {
        const parsed = JSON.parse(latest.author) as { kind?: string; id?: string };
        if (parsed.kind === 'player' && typeof parsed.id === 'string') authorId = parsed.id;
      } catch {
        // Malformed author — skip count.
      }
      if (authorId) counts[authorId] = (counts[authorId] ?? 0) + 1;
    }
  }

  // Build one row per campaign member (sorted: highest count first, then by label).
  return members
    .map((m) => ({
      key: m.user_id,
      label: m.email || m.user_id,
      count: counts[m.user_id] ?? 0,
      initials: (m.email || m.user_id).charAt(0).toUpperCase(),
    }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
}

/**
 * #266 — Flat Campaign home panel (07 §2.2, workspace-home-flat.html).
 *
 * Surfaces co-authorship without hierarchical framing:
 *   - A mode notice strip (mirrors the flat-mock's green info band).
 *   - Recent player-authored canon facts with per-fact author chip.
 *   - Per-member contribution count (co-authors, not GM/Players).
 *
 * Rendered at the top of the entity list when `facilitation_mode === 'flat'`.
 * GM-only by construction (the list surface is GM-only in its own `show(mode=gm)`).
 *
 * a11y: every section has a heading; the provenance chip uses text+colour
 * (colour is never the only signal). Mobile: stacks as a single column
 * (the host `list` page is already responsive).
 */
function viewCampaignHomeFlat(state: Signal<Model>, _send: Send<Msg>): Mountable {
  return section(
    {
      class: 'flat-home',
      'data-testid': 'flat-home',
      'aria-label': t('flat_home.mode_notice_label'),
    },
    [
      // Mode notice strip — no hierarchical framing, quick explanation.
      div({ class: 'flat-home-notice', 'data-testid': 'flat-home-notice', role: 'note' }, [
        span({ class: 'flat-home-notice-icon', 'aria-hidden': 'true' }, [
          uiIcon('git-merge', { size: 16 }),
        ]),
        span({ class: 'flat-home-notice-text' }, [text(t('flat_home.mode_notice'))]),
      ]),

      // Recent additions — player-authored canon facts.
      div({ class: 'flat-home-recent', 'data-testid': 'flat-home-recent' }, [
        h3({ class: 'flat-home-section-heading' }, [text(t('flat_home.heading'))]),
        p({ class: 'flat-home-section-sub' }, [text(t('flat_home.subheading'))]),

        // Syncing empty state.
        show(
          state.map((s) => s.syncing && flatRecentCanonRows(s).length === 0),
          () => [
            p({ class: 'flat-home-empty', 'data-testid': 'flat-home-recent-syncing' }, [
              text(t('flat_home.syncing')),
            ]),
          ],
        ),
        // No-data empty state.
        show(
          state.map((s) => !s.syncing && flatRecentCanonRows(s).length === 0),
          () => [
            p({ class: 'flat-home-empty', 'data-testid': 'flat-home-recent-empty' }, [
              text(t('flat_home.empty')),
            ]),
          ],
        ),

        // Fact rows — most recent first.
        show(
          state.map((s) => flatRecentCanonRows(s).length > 0),
          () => [
            ul(
              {
                class: 'flat-home-fact-list',
                role: 'list',
                'data-testid': 'flat-home-fact-list',
              },
              [
                each(state.map((s) => flatRecentCanonRows(s)), {
                  key: (r) => r.key,
                  render: (item) => [
                    li({ class: 'flat-home-fact-row', 'data-testid': 'flat-home-fact-row' }, [
                      // Entity + predicate context.
                      div({ class: 'flat-home-fact-entity' }, [
                        // Entity icon (branch on literal root for the compiler).
                        span({ class: 'flat-home-fact-icon', 'aria-hidden': 'true' }, [
                          branch(item.at('root'), {
                            location: () => [entityIcon('location', { size: 12 })],
                            character: () => [entityIcon('character', { size: 12 })],
                            faction: () => [entityIcon('faction', { size: 12 })],
                            event: () => [entityIcon('event', { size: 12 })],
                            item: () => [entityIcon('item', { size: 12 })],
                            lore: () => [entityIcon('lore', { size: 12 })],
                            '': () => [entityIcon('lore', { size: 12 })],
                          }),
                        ]),
                        span({ class: 'flat-home-fact-meta' }, [
                          span({ class: 'flat-home-fact-entity-name' }, [
                            text(item.at('entityName')),
                          ]),
                          span({ class: 'flat-home-fact-predicate' }, [
                            text(item.at('predicateLabel')),
                          ]),
                        ]),
                      ]),
                      // Fact value.
                      span({ class: 'flat-home-fact-value' }, [text(item.at('value'))]),
                      // Provenance chips: canon status + author (text + colour = dual signal).
                      div({ class: 'flat-home-fact-chips', 'aria-label': item.at('author') }, [
                        statusPill({ kind: 'canon', label: t('fact.status.canon') }),
                        span(
                          {
                            class: 'dl-provenance-badge flat-home-author-chip',
                            'data-kind': 'player',
                            'data-testid': 'flat-home-author-chip',
                          },
                          [text(item.at('author'))],
                        ),
                      ]),
                    ]),
                  ],
                }),
              ],
            ),
          ],
        ),
      ]),

      // Co-author contribution summary.
      show(
        state.map((s) => (s.campaignMembers?.length ?? 0) > 0),
        () => [
          div({ class: 'flat-home-members', 'data-testid': 'flat-home-members' }, [
            h3({ class: 'flat-home-section-heading' }, [text(t('flat_home.members_heading'))]),
            p({ class: 'flat-home-section-sub' }, [text(t('flat_home.members_subheading'))]),
            show(
              state.map((s) => flatMemberContributionRows(s).length === 0),
              () => [
                p({ class: 'flat-home-empty', 'data-testid': 'flat-home-members-empty' }, [
                  text(t('flat_home.members_empty')),
                ]),
              ],
            ),
            show(
              state.map((s) => flatMemberContributionRows(s).length > 0),
              () => [
                ul(
                  {
                    class: 'flat-home-member-list',
                    role: 'list',
                    'data-testid': 'flat-home-member-list',
                  },
                  [
                    each(state.map((s) => flatMemberContributionRows(s)), {
                      key: (r) => r.key,
                      render: (item) => [
                        li(
                          { class: 'flat-home-member-row', 'data-testid': 'flat-home-member-row' },
                          [
                            // Avatar initials tile (colour purely decorative; initials = text cue).
                            span(
                              { class: 'flat-home-member-avatar', 'aria-hidden': 'true' },
                              [text(item.at('initials'))],
                            ),
                            // Member label.
                            span({ class: 'flat-home-member-label' }, [text(item.at('label'))]),
                            // Fact count (context, not authority).
                            span({ class: 'flat-home-member-count' }, [
                              text(
                                item.map((r) => t('flat_home.member_facts', { count: r.count })),
                              ),
                            ]),
                          ],
                        ),
                      ],
                    }),
                  ],
                ),
              ],
            ),
          ]),
        ],
      ),
    ],
  );
}

function viewLocationsList(
  state: Signal<Model>,
  send: Send<Msg>,
  activeRoot: EntityRoot | null = null,
): Mountable {
  return main({ role: 'main' }, [
    div({ class: 'list-header' }, [
      // #141 — list heading is root-agnostic. #132 turned this surface
      // into a six-section multi-root list; calling the page
      // "Locations" embedded a Walking-Skeleton-era assumption that
      // collided with new Characters / Factions / etc. When the Browse
      // rail filters to one root, the heading names it.
      h2([text(activeRoot === null ? t('entity.list.title') : t(`entity.root.${activeRoot}`))]),
      // #N002 — the legacy "New Location" secondary button was
      // removed; per-section "+" buttons in the multi-root list now
      // handle direct creation. The Location section's "+" carries
      // the `data-testid="new-location"` anchor so existing tests
      // keep passing. The primary "+ New entity" picker stays because
      // it's the discoverable cross-root entrypoint (root-picker
      // overlay) and the unit tests (app.test.ts) verify the picker
      // surface.
      show(
      state.map((s) => s.session.mode === 'gm'),
      () => [
          // Both header buttons live in ONE flex cluster so the `.list-header`
          // stays a clean 2-column (title | actions) layout — the cluster wraps
          // INTERNALLY on a narrow phone instead of the header growing taller
          // (which would push the last list row under the fixed bottom-nav).
          div({ class: 'list-header-actions' }, [
            uiButton({
              variant: 'primary',
              onClick: () => send({ type: 'open-root-picker' }),
              testid: 'new-entity',
              attrs: {
                'aria-haspopup': 'menu',
                'aria-expanded': state.map((s) => (s.rootPickerOpen ? 'true' : 'false')),
              },
              children: [text(t('entity.new.button'))],
            }),
            // #251 — campaign-bootstrap entry (decision 6): seed a region/world
            // from a prompt as a suggestion set. GM-only; AI is optional (this
            // sits alongside, never replaces, manual entity creation).
            uiButton({
              variant: 'secondary',
              onClick: () => send({ type: 'open-bundle-gen', target: { kind: 'bootstrap' } }),
              testid: 'bootstrap-with-ai',
              className: 'list-bootstrap-ai',
              children: [text(t('bundle.bootstrap_action'))],
            }),
          ]),
        ],
    ),
    ]),
    // #266 — flat home panel: rendered above the entity list when
    // `facilitation_mode === 'flat'` and no root filter is active. GM-only
    // (the outer `show(mode === 'gm')` gates this; the inner `show` further
    // gates on mode + null root so filtered-root views stay clean).
    show(
      state.map(
        (s) =>
          s.session.mode === 'gm' &&
          s.config.facilitation_mode === 'flat' &&
          activeRoot === null,
      ),
      () => [viewCampaignHomeFlat(state, send)],
    ),
    show(
      state.map((s) => s.session.mode === 'gm'),
      () => [viewGmEntitiesList(state, send, activeRoot)],
    ),
    show(
      state.map((s) => s.session.mode === 'player'),
      () => [viewPlayerEntitiesList(state, send)],
    ),
  ]);
}

/**
 * #132 — multi-root GM entity list. The previous single-root
 * Locations list (which filtered to `e.root === 'location'`) has been
 * replaced with a six-section accordion (see `entities/list-view.ts`
 * for the layout-choice ADR).
 *
 * This thin wrapper builds the row-shape the list view expects, in
 * one O(N) pass over `model.entities`, and passes the `router.href` +
 * `navMsg` injectors so the list view stays decoupled from the app
 * shell's router instance.
 *
 * The row-shape mirrors what the legacy list emitted (`id` + `name`),
 * minus the population tagline — population is a Locations-only
 * predicate and would look out of place against the five other roots.
 * The full predicate set surfaces on the entity page (#133 owns the
 * fact-row editor; this slice just lists entities).
 */
// The list view now constrains `<Model extends EntityListStateShape>`
// and reads `entities` directly via bare-Identifier accessors — no
// closure-parameter to thread.
const entityListNav = {
  href: (id: string) => appRouteHref({ kind: 'entity', id }),
  navigateMsg: (id: string) => navMsg({ kind: 'entity', id }),
  createForRootMsg: (root: EntityRoot): Msg => ({
    type: 'open-new-entity-modal',
    root,
  }),
};

function viewGmEntitiesList(
  state: Signal<Model>,
  send: Send<Msg>,
  activeRoot: EntityRoot | null = null,
): Mountable {
  return div({ class: 'gm-entities-list' }, [
    // While the initial backfill is streaming and nothing has folded
    // yet, show a syncing line so the empty list reads as loading, not
    // an empty world (the op log arrives over the WS; large campaigns
    // take a few seconds).
    show(
      state.map((s) => s.syncing && Object.keys(s.entities).length === 0),
      () => [
        p({ class: 'entities-syncing', 'data-testid': 'entities-syncing', 'aria-live': 'polite' }, [
          text(t('entities.syncing')),
        ]),
      ],
    ),
    viewMultiRootEntityList(state, send, entityListNav, activeRoot),
  ]);
}

/**
 * III.B.2 (#66) — Player entity list.
 *
 * Sources entities from `projectedFacts` (populated by the SyncClient's
 * `visibility_added` callback). An entity appears in the list if the
 * Viewer has ANY visible fact on it (or that references it). The list
 * shows the entity's `name` predicate when revealed, falling back to
 * the entity id chip for entities the Viewer can see-but-not-name.
 */
function viewPlayerEntitiesList(state: Signal<Model>, send: Send<Msg>): Mountable {
  return div([
    show(
      state.map((s) => playerEntities(s).length === 0),
      () => [
        emptyState({
          testid: 'player-empty',
          message: text(t('player.entities_empty')),
        }),
      ],
    ),
    show(
      state.map((s) => playerEntities(s).length > 0),
      () => [
        ul(
          { class: 'entity-list', 'data-testid': 'player-entity-list' },
          [each(state.map((s) => playerEntities(s)), {
        key: (row) => row.id,
        render: (item) => [
              li({ 'data-testid': 'entity-row' }, [
                a(
                  {
                    href: item.map((row) => appRouteHref({ kind: 'entity', id: row.id })),
                    onClick: (event: MouseEvent) => {
                      event.preventDefault();
                      // See list-view.ts:240 — avoid `item.at('current').peek()`
                      // under llui v0.5.7 `item-dedup`. Read field
                      // accessors directly.
                      const id = item.at('id').peek();
                      if (typeof id !== 'string') return;
                      send(navMsg({ kind: 'entity', id }));
                    },
                  },
                  [text(item.at('name'))],
                ),
              ]),
            ],
      })],
        ),
      ],
    ),
  ]);
}

function playerEntities(s: Model): { id: string; name: string }[] {
  const byId = new Map<string, string>();
  for (const fact of Object.values(s.projectedFacts)) {
    if (fact.predicate === FACT_NAME && typeof fact.value === 'string') {
      byId.set(fact.subject, fact.value);
    } else if (!byId.has(fact.subject)) {
      byId.set(fact.subject, fact.subject);
    }
  }
  return Array.from(byId.entries())
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Read the display name for an entity from Model. Three cases:
 *
 *  - entity not yet in Model (substrate still folding) → returns the
 *    empty string. The h1 keeps its reserved height via CSS so layout
 *    doesn't shift; a skeleton shimmer (CSS) fills the space until
 *    the substrate fold lands. Avoids the "id flashes then becomes
 *    name" flicker the previous fallback produced on page refresh.
 *  - entity loaded but never given a name → returns the entity id as
 *    a stable fallback (matches the list-view convention).
 *  - entity loaded with a name fact → returns the latest value.
 *
 * Why a same-module bare-Identifier helper: the llui cross-file
 * walker needs to register the reactive paths
 * (`s.entities[*].facts.name.versions[*].value`) so a substrate
 * fold-rebuild propagates into the entity-page title. The
 * factAtPredicate / authorAtPredicate helpers in
 * `entities/fact-row-state.ts` follow this exact pattern.
 */
function entityNameOrId(s: Model, entityId: string): string {
  // 226.1 (#245) — while previewing as a non-GM Viewer, the displayed name must
  // come from the projection (the chokepoint), not GM-truth: a Viewer not
  // revealed an entity's `name` sees its id, never the canonical name. Reads
  // only `previewProjection` here — never `entities` — so no GM-truth leaks.
  if (s.previewViewer !== null) {
    const projected = s.previewProjection?.[factKey(entityId, FACT_NAME)];
    const pv = projected?.value;
    return pv === undefined || pv === null ? entityId : String(pv);
  }
  const entity = s.entities[entityId];
  if (!entity) return '';
  const fact = entity.facts.name;
  if (!fact) return entityId;
  const latest = fact.versions[fact.versions.length - 1];
  if (latest === undefined) return entityId;
  const v = latest.value;
  return v === undefined || v === null ? entityId : String(v);
}

/** Soft-deleted ("trashed") entities, name-sorted, for the Trash view. */
interface TrashRow {
  id: string;
  name: string;
  root: EntityRoot;
}
function softDeletedEntityRows(s: Model): TrashRow[] {
  const rows: TrashRow[] = [];
  for (const [id, entity] of Object.entries(s.entities)) {
    if (!entity || !entity.soft_deleted) continue;
    rows.push({ id, name: entityNameOrId(s, id), root: entity.root });
  }
  rows.sort((a, b) => a.name.localeCompare(b.name));
  return rows;
}
/** Count of trashed entities — drives the rail badge + the empty state. */
function trashCount(s: Model): number {
  let n = 0;
  for (const e of Object.values(s.entities)) if (e?.soft_deleted) n += 1;
  return n;
}

/**
 * Trash route — soft-deleted entities with a one-click Restore (ADR — delete is
 * reversible; the op log keeps everything). GM-only; reachable from the rail's
 * Work section (the rail item is hidden when the trash is empty).
 */
function viewTrash(state: Signal<Model>, send: Send<Msg>): Mountable {
  return main({ role: 'main', class: 'trash-page', 'data-testid': 'trash-page' }, [
    div({ class: 'trash-header' }, [
      h1({ class: 'trash-title' }, [text(t('trash.heading'))]),
      p({ class: 'trash-subtitle' }, [text(t('trash.subtitle'))]),
    ]),
    show(
      state.map((s) => trashCount(s) === 0),
      () => [
        p({ class: 'trash-empty', 'data-testid': 'trash-empty' }, [text(t('trash.empty'))]),
      ],
    ),
    show(
      state.map((s) => trashCount(s) > 0),
      () => [
        ul({ class: 'trash-list', 'data-testid': 'trash-list' }, [
          each(state.map((s) => softDeletedEntityRows(s)), {
            key: (r) => r.id,
            render: (item) => [
              li({ class: 'trash-row', 'data-testid': 'trash-row' }, [
                span({ class: 'trash-row-icon', 'aria-hidden': 'true' }, [
                  branch(item.at('root'), {
                    location: () => [entityIcon('location', { size: 16 })],
                    character: () => [entityIcon('character', { size: 16 })],
                    faction: () => [entityIcon('faction', { size: 16 })],
                    event: () => [entityIcon('event', { size: 16 })],
                    item: () => [entityIcon('item', { size: 16 })],
                    lore: () => [entityIcon('lore', { size: 16 })],
                  }),
                ]),
                div({ class: 'trash-row-text' }, [
                  span({ class: 'trash-row-name' }, [text(item.at('name'))]),
                  span({ class: 'trash-row-root' }, [
                    text(item.at('root').map((r) => t(`entity.root.${r}`))),
                  ]),
                ]),
                uiButton({
                  variant: 'secondary',
                  className: 'trash-restore',
                  testid: 'trash-restore',
                  ariaLabel: t('trash.restore'),
                  onClick: () => {
                    const id = item.at('id').peek();
                    if (typeof id === 'string') send({ type: 'restore-entity', entityId: id });
                  },
                  children: [text(t('trash.restore'))],
                }),
              ]),
            ],
          }),
        ]),
      ],
    ),
  ]);
}

/**
 * #146 (design-recon entity-page header) — header chrome helpers.
 *
 * The mock (`mocks/entity-page.html`) frames the page with a large
 * root icon + a meta row (root label · fact count). These bare-
 * Identifier helpers read `s.entities.*` so the cross-file reactive
 * walker registers the paths (per CLAUDE.md "Where things live").
 */
function entityRootOf(s: Model, entityId: string): EntityRoot | null {
  return s.entities[entityId]?.root ?? null;
}

/** Localised root label ("Location", "Character", …) for the meta row. */
function entityRootLabelOf(s: Model, entityId: string): string {
  const root = entityRootOf(s, entityId);
  return root ? t(`entity.root.${root}`) : '';
}

/** Count of the entity's live (non-empty, non-soft-deleted) fact slots —
 * shown in the meta row and the Facts tab count badge. */
function entityFactCountOf(s: Model, entityId: string): number {
  const entity = s.entities[entityId];
  if (!entity || entity.soft_deleted) return 0;
  let n = 0;
  for (const fact of Object.values(entity.facts)) {
    const latest = fact.versions[fact.versions.length - 1];
    if (latest === undefined) continue;
    const v = latest.value;
    if (v !== undefined && v !== null && String(v) !== '') n += 1;
  }
  return n;
}

/**
 * LLM author mode (Phase B) — Draft Inbox rows.
 *
 * A pure projection of `Model.entities`: every `status:'draft'`,
 * non-soft-deleted fact across all entities becomes one row. Drafts
 * already live in the folded entity cache (the projection chokepoint
 * keeps them out of player views, never out of the GM's own). Plain
 * arrays/objects only — llui-safe for `h.each`. Bare-Identifier function
 * so the cross-file walker registers the `s.entities.*` reactive paths.
 */
interface DraftRow {
  key: string;
  entityId: string;
  entityName: string;
  /** The subject entity's root — drives the entity-card icon. */
  root: string;
  predicate: string;
  predicateLabel: string;
  value: string;
  isLlm: boolean;
  /**
   * Authoring source for the provenance badge (per 30 §3.4). `gm` is the
   * default; `llm` carries the ✨ marker; `player` carries the green hue.
   */
  author: 'gm' | 'llm' | 'player';
  /** Localised provenance-badge label ("AI" / "Player" / "GM"). */
  authorLabel: string;
  /** Phase D — asset-kind draft (renders as a thumbnail, not text). */
  isAsset: boolean;
  /** Resolved signed thumbnail URL (empty until generate-image resolves). */
  assetUrl: string;
  /**
   * Rich-text drafts render their markdown as formatted prose in the inbox
   * (the GM reviews rendered text, never raw markup) — the view renders
   * `value` reactively through @llui/markdown's `markdown()`.
   */
  isRichText: boolean;
  /**
   * S9 (#196): when ratifying this draft would imply a succession (its validity
   * precedes/overlaps an existing same-key segment), a human note naming the
   * predecessor-close the ratify will also apply — the confirmable surface.
   * Empty when the draft implies no close.
   */
  succeedsNote: string;
  /**
   * Pre-emit auditor advisories this draft introduces (#164). The deterministic
   * auditor runs over the folded projection (drafts included), so any
   * contradiction / dangling ref / cardinality violation a draft causes is
   * surfaced inline at review — before the GM ratifies. Editorial only: an
   * advisory here never blocks ratification.
   */
  advisories: AdvisoryRow[];
}
/** Number of ✨ draft/image requests currently in flight (for the thinking pill). */
function aiDraftInFlight(s: Model): number {
  return Object.keys(s.aiDraftPending).length;
}

// ── Live streaming preview (per-fact ✨ prose) ─────────────────────────
function streamPreviewActive(s: Model): boolean {
  return s.streamPreview !== null;
}
function streamPreviewText(s: Model): string {
  return s.streamPreview === null ? '' : s.streamPreview.text;
}
function streamPreviewThinking(s: Model): boolean {
  return s.streamPreview !== null && s.streamPreview.phase === 'thinking';
}
function streamPreviewTitle(s: Model): string {
  if (s.streamPreview === null) return '';
  return t('llm.stream_title', {
    predicate: predicateLabel(s.streamPreview.predicate),
    entity: entityNameOrId(s, s.streamPreview.entityId),
  });
}

/**
 * Floating preview that shows ✨ prose streaming in live. The final text
 * is what gets committed to the draft; this panel is purely for
 * visibility and closes when the draft lands (or on error).
 */
function viewStreamPreview(state: Signal<Model>, _send: Send<Msg>): Mountable {
  return div(
    { class: 'stream-preview', role: 'status', 'aria-live': 'polite', 'data-testid': 'stream-preview' },
    [
      div({ class: 'stream-preview-head' }, [
        span({ class: 'stream-preview-spark', 'aria-hidden': 'true' }, ['✨']),
        span({ class: 'stream-preview-title' }, [text(state.map((s) => streamPreviewTitle(s)))]),
      ]),
      show(
      state.map((s) => streamPreviewThinking(s)),
      () => [
          p({ class: 'stream-preview-thinking' }, [text(t('llm.stream_thinking'))]),
        ],
    ),
      p({ class: 'stream-preview-text', 'data-testid': 'stream-preview-text' }, [
        text(state.map((s) => streamPreviewText(s))),
      ]),
    ],
  );
}

/**
 * Asset ids referenced by asset-kind facts that don't yet have a resolved
 * signed URL. Scans canon + draft facts so both the published thumbnail and
 * an in-flight draft render. Bounded by the number of asset facts.
 */
function assetIdsNeedingUrls(
  entities: Record<string, EntityState>,
  assetUrls: Record<string, string>,
): string[] {
  const registry = loadGmRegistry();
  const out = new Set<string>();
  for (const entity of Object.values(entities)) {
    if (entity.soft_deleted) continue;
    for (const [pred, fact] of Object.entries(entity.facts)) {
      if (fact.soft_deleted) continue;
      const kind = registry.lookup(pred)?.value_kind[0];
      // `image` is asset-backed too (ADR-0002) — resolve its URL so the
      // editor preview + list thumbnail render. `file` is not previewed.
      if (kind !== 'asset' && kind !== 'image') continue;
      const latest = fact.versions[fact.versions.length - 1];
      const v = latest?.value;
      if (typeof v === 'string' && v !== '' && assetUrls[v] === undefined) out.add(v);
    }
  }
  return [...out];
}

// ---------- #259 post-combat log helpers (07 §13.9.2) ----------

/**
 * Build the Effect[] for a combat-log bundle emission. Emits:
 *  1. `submit-bundle-declare` (combat_log source)
 *  2. `submit-entity-create` for the encounter Event
 *  3. `submit-fact` for the Event name (draft, bundle_id-tagged)
 *  4. `submit-fact` for `participants_template` on fodder entries (no character_ref)
 *  5. `submit-fact` for outcome facts on named (character_ref) participants
 *  6. `submit-fact` for `at_location` on the Event (if locationRef provided)
 *
 * All facts land as `draft`, tagged with the bundle id, so the GM reviews them
 * via the existing #251 bundle-review UI before they become canon.
 */
export function buildCombatLogEffects(
  bundle: CombatLogBundle,
  state: InitiativeState,
  locationRef: string | null,
  fateOverrides: Record<string, ParticipantFate | 'fled'>,
): Effect[] {
  const effects: Effect[] = [];

  // 1. bundle.declare (combat_log source).
  effects.push({ type: 'submit-bundle-declare', bundleId: bundle.bundleId, source: 'combat_log' });

  // 2. Event entity.create.
  effects.push({
    type: 'submit-entity-create',
    subject: bundle.eventEntityId,
    root: 'event',
    traits: [],
  });

  // 3. Event name fact — draft, bundle-tagged.
  effects.push({
    type: 'submit-fact',
    subject: bundle.eventEntityId,
    predicate: 'name',
    payload: {
      kind: 'fact.create',
      value: bundle.title,
      status: 'draft',
      bundle_id: bundle.bundleId,
    },
  });

  // 4. participants_template for fodder entries (no per-goblin entities).
  const ptValue = buildParticipantsTemplateValue(
    state.entries,
    // fateOverrides is Record<string, ParticipantFate | 'fled'>; only ParticipantFate
    // makes sense here (fled is valid but we map it through). Pass through as-is —
    // the template records whatever the GM chose.
    Object.fromEntries(
      Object.entries(fateOverrides).map(([k, v]) => [k, v === 'fled' ? 'survived' : v]),
    ) as Record<string, ParticipantFate>,
  );
  if (ptValue !== null) {
    // Include 'fled' entries in the template value by re-deriving from raw overrides.
    const ptValueWithFled = {
      ...ptValue,
      fates: state.entries
        .filter((e) => !e.character_ref)
        .map((e) => ({
          display_name: e.display_name,
          fate: (fateOverrides[e.id] ?? participantsFate(e)) as ParticipantFate | 'fled',
        })),
    };
    effects.push({
      type: 'submit-fact',
      subject: bundle.eventEntityId,
      // TODO(ADR-0004 §1): `participants_template` is not a declared predicate
      // (no official-core id, no combat pack yet), so this fact is written
      // under a raw token and cannot resolve in the registry — invisible canon.
      // A dedicated combat pack must declare an opaque id for it; use that here.
      predicate: 'participants_template',
      payload: {
        kind: 'fact.create',
        value: ptValueWithFled,
        status: 'draft',
        bundle_id: bundle.bundleId,
      },
    });
  }

  // 5. Outcome facts on named (character_ref) participants.
  for (const entry of state.entries) {
    if (!entry.character_ref) continue;
    const fate = fateOverrides[entry.id] ?? participantsFate(entry);
    let outcomeValue: string;
    if (fate === 'killed') {
      outcomeValue = `died in combat (round ${state.round})`;
    } else if (
      entry.hp_scratch !== undefined &&
      entry.hp_scratch.current < (entry.hp_scratch.max ?? entry.hp_scratch.current + 1)
    ) {
      outcomeValue = `survived (wounded: ${entry.hp_scratch.current}/${entry.hp_scratch.max ?? '?'} hp)`;
    } else {
      outcomeValue = 'survived';
    }
    effects.push({
      type: 'submit-fact',
      subject: entry.character_ref,
      // TODO(ADR-0004 §1): `combat_outcome` is not a declared predicate (no
      // official-core id, no combat pack yet), so this fact is written under a
      // raw token and cannot resolve in the registry — invisible canon. A
      // dedicated combat pack must declare an opaque id for it; use that here.
      predicate: 'combat_outcome',
      payload: {
        kind: 'fact.create',
        value: outcomeValue,
        status: 'draft',
        bundle_id: bundle.bundleId,
      },
    });
  }

  // 6. Location fact on event (when auto-filled or GM-provided). The event's
  // location is the declared official-core `location` predicate — the raw
  // `at_location` token is not a declared predicate, so a fact written under it
  // would be invisible canon (ADR-0004 §1).
  if (locationRef) {
    effects.push({
      type: 'submit-fact',
      subject: bundle.eventEntityId,
      predicate: OFFICIAL_CORE_PREDICATE_IDS.location,
      payload: {
        kind: 'fact.create',
        value: locationRef,
        status: 'draft',
        bundle_id: bundle.bundleId,
      },
    });
  }

  return effects;
}

// ---------- #251 suggestion-set bundle review (04-llm-role.md §14) ----------
//
// The GM-only surface that lands a bulk-gen bundle as ONE reviewable unit in the
// draft inbox. Everything here is a pure selector over the folded Model
// (`entities` + `bundles` + `advisories`) — derived, never stored. Accept =
// GM-authored set_status→canon (the #223 LLM→canon backdoor stays closed by
// construction); reject = the existing lossless draft soft-delete.

/** One member draft row inside a bundle-review group. */
export interface BundleMemberRow {
  key: string;
  entityId: string;
  entityName: string;
  /** Subject entity root — drives the entity-card icon (closed enum). */
  root: string;
  predicate: string;
  predicateLabel: string;
  value: string;
  /** LLM self-rated confidence 0–1 (#251 §14.6); null when the draft carries none. */
  confidence: number | null;
  /** A coarse confidence band for a non-colour cue (a11y) + threshold styling. */
  confidenceBand: 'high' | 'medium' | 'low' | 'none';
  isAsset: boolean;
  assetUrl: string;
  isRichText: boolean;
  /** Pre-emit auditor advisories this member introduces (#164 / §14.4), by fact ref. */
  advisories: AdvisoryRow[];
}

/** One open bundle, grouped for review. */
export interface BundleReviewGroup {
  bundleId: string;
  /** The generation prompt from `bundle.declare` (empty when no declare folded). */
  prompt: string;
  /** Resolved grounding mode label, when recorded. */
  mode: BundleMode | null;
  /** Requested depth, when recorded. */
  depth: BundleDepth | null;
  memberCount: number;
  /** Members, sorted by confidence descending (highest-confidence first). */
  members: BundleMemberRow[];
  /** Whether auto-promote-with-flag is opted-in for this bundle (off by default). */
  autoPromote: boolean;
}

/** Coarse confidence band for the non-colour cue (a11y) + sort tie-break. */
function confidenceBandOf(c: number | null): BundleMemberRow['confidenceBand'] {
  if (c === null) return 'none';
  if (c >= 0.75) return 'high';
  if (c >= 0.5) return 'medium';
  return 'low';
}

/**
 * The ids of every OPEN bundle (≥1 still-un-acted-on draft member), in
 * registry order. Resolved bundles (all members accepted / rejected) drop out.
 */
export function openBundleIds(s: Model): string[] {
  return Object.values(s.bundles)
    .filter((b) => b.state === 'open')
    .map((b) => b.bundle_id);
}

/**
 * The still-open draft members of one bundle, as review rows joined with their
 * pre-emit advisories (by fact ref) and sorted by confidence (desc). A member
 * key is `subject/predicate`; we read its current `FactState` from `entities`
 * and only surface members still in `draft` (accepted/rejected members drop).
 */
export function bundleReviewMembers(s: Model, bundleId: string): BundleMemberRow[] {
  const registry = loadGmRegistry();
  const entry = s.bundles[bundleId];
  if (!entry) return [];
  const rows: BundleMemberRow[] = [];
  for (const memberKey of entry.memberKeys) {
    // memberKey is `factKey(subject, predicate)` = `subject/predicate`.
    const slash = memberKey.indexOf('/');
    if (slash === -1) continue;
    const entityId = memberKey.slice(0, slash);
    const pred = memberKey.slice(slash + 1);
    const entity = s.entities[entityId];
    if (!entity || entity.soft_deleted) continue;
    const fact = entity.facts[pred];
    // Only still-open drafts are reviewable; an accepted/rejected member drops.
    if (!fact || fact.soft_deleted || fact.status !== 'draft') continue;
    const latest = fact.versions[fact.versions.length - 1];
    if (!latest) continue;
    const v = latest.value;
    // Route through formatFactValue so structured values (tuples, dimensioned
    // numbers, objects) render legibly instead of `[object Object]`. The
    // null/undefined guard stays '' (not '—') so asset-id lookups + the empty
    // cell keep working; a string value passes through unchanged.
    const value = v === undefined || v === null ? '' : formatFactValue(v);
    const kind = registry.lookup(pred)?.value_kind[0];
    const isAsset = kind === 'asset' || kind === 'image';
    const isRichText = kind === 'rich_text';
    const factId = factRef(entityId, pred);
    const advisories = s.advisories
      .filter((a) => a.fact_ids.includes(factId))
      .map((a) => toAdvisoryRow(s, a));
    const confidence = typeof fact.confidence === 'number' ? fact.confidence : null;
    rows.push({
      key: memberKey,
      entityId,
      entityName: entityNameOrId(s, entityId),
      root: entity.root,
      predicate: pred,
      predicateLabel: predicateLabel(pred),
      value,
      confidence,
      confidenceBand: confidenceBandOf(confidence),
      isAsset,
      assetUrl: isAsset ? (s.assetUrls[value] ?? '') : '',
      isRichText,
      advisories,
    });
  }
  // Sort by confidence desc; a null confidence sorts last. Stable tie-break by
  // predicate so the order is deterministic.
  return rows.sort(
    (a, b) =>
      (b.confidence ?? -1) - (a.confidence ?? -1) || a.predicate.localeCompare(b.predicate),
  );
}

/**
 * Every open bundle grouped for the inbox review surface, in registry order.
 * Each group carries the declare metadata + its sorted member rows. A bundle
 * whose open members all happen to be filtered out (soft-deleted entity) yields
 * an empty `members` list but is omitted (nothing to review).
 */
export function bundleReviewGroups(s: Model): BundleReviewGroup[] {
  const groups: BundleReviewGroup[] = [];
  for (const id of openBundleIds(s)) {
    const entry = s.bundles[id]!;
    const members = bundleReviewMembers(s, id);
    if (members.length === 0) continue;
    groups.push({
      bundleId: id,
      prompt: entry.metadata?.prompt ?? '',
      mode: entry.metadata?.mode ?? null,
      depth: entry.metadata?.depth ?? null,
      memberCount: members.length,
      members,
      autoPromote: s.bundleAutoPromote[id] === true,
    });
  }
  return groups;
}

/** Total open-bundle review count — drives the inbox section heading. */
function openBundleCount(s: Model): number {
  return bundleReviewGroups(s).length;
}

/** Whether the bulk-gen modal targets a campaign bootstrap (vs an entity). */
function bundleGenIsBootstrap(s: Model): boolean {
  return s.bundleGenModal?.target.kind === 'bootstrap';
}


/**
 * Build the `submit-fact-status` effect that accepts ONE bundle member as canon.
 * Always GM-authored (the effect handler stamps `GM_AUTHOR`) — the #223 backdoor
 * stays closed. Auto-promote-with-flag rides a `review_state: 'unreviewed_bulk'`
 * (carried as a value-bearing `fact.edit` since `set_status` can't carry it).
 */
function acceptMemberEffect(
  model: Model,
  entityId: string,
  predicate: string,
  autoPromote: boolean,
): SubmitFactStatusEffect {
  if (!autoPromote) {
    return { type: 'submit-fact-status', subject: entityId, predicate, status: 'canon' };
  }
  const fact = model.entities[entityId]?.facts[predicate];
  const latest = fact?.versions[fact.versions.length - 1];
  return {
    type: 'submit-fact-status',
    subject: entityId,
    predicate,
    status: 'canon',
    reviewState: 'unreviewed_bulk',
    reviewValue: { value: latest?.value },
  };
}

/**
 * Build the reject effect for ONE bundle member — the existing lossless
 * draft-reject path. A bundle member is a fresh draft (no prior shadowed canon
 * in the common new-stub case), so this is a soft-delete; if the draft DID
 * shadow a prior canon value, restore that instead (symmetric with
 * `reject-draft`).
 */
function rejectMemberEffect(
  model: Model,
  entityId: string,
  predicate: string,
): SubmitFactEffect | SubmitFactStatusEffect {
  const versions = model.entities[entityId]?.facts[predicate]?.versions ?? [];
  if (versions.length >= 2) {
    const prior = versions[versions.length - 2];
    return {
      type: 'submit-fact-status',
      subject: entityId,
      predicate,
      status: 'canon',
      restoreValue: { value: prior?.value },
    };
  }
  return {
    type: 'submit-fact',
    subject: entityId,
    predicate,
    payload: { kind: 'fact.soft_delete' },
  };
}

/** Build a BulkGenRequest from the modal state (defaults are already on the modal). */
function bundleRequestFromModal(m: BundleGenModalState): BulkGenRequest {
  return {
    target: m.target,
    prompt: m.prompt.trim(),
    depth: m.depth,
    mode: m.mode,
  };
}

// ── ADR-0008 §6 (T7, #281) authoring-transcript folds ─────────────────────────
// Pure helpers that fold a streamed SSE event into the in-flight assistant
// message. Immutable — each returns a fresh `messages` array (llui Model rule).

/** Append a streamed token delta to the last assistant message (creating one if absent). */
function appendTokenToLastAssistant(
  messages: AuthoringMessage[],
  token: string,
): AuthoringMessage[] {
  const idx = messages.length - 1;
  const last = messages[idx];
  if (last && last.role === 'assistant') {
    return [...messages.slice(0, idx), { ...last, text: last.text + token }];
  }
  return [...messages, { role: 'assistant', text: token }];
}

/** Record a tool "thinking" entry on the last assistant message (creating one if absent). */
function addToolToLastAssistant(
  messages: AuthoringMessage[],
  entry: AuthoringToolEntry,
): AuthoringMessage[] {
  const idx = messages.length - 1;
  const last = messages[idx];
  if (last && last.role === 'assistant') {
    return [...messages.slice(0, idx), { ...last, tools: [...(last.tools ?? []), entry] }];
  }
  return [...messages, { role: 'assistant', text: '', tools: [entry] }];
}

/** Replace the last assistant message text with a full assembled reply (creating one if absent). */
function setLastAssistantText(messages: AuthoringMessage[], full: string): AuthoringMessage[] {
  const idx = messages.length - 1;
  const last = messages[idx];
  if (last && last.role === 'assistant') {
    return [...messages.slice(0, idx), { ...last, text: full }];
  }
  return [...messages, { role: 'assistant', text: full }];
}

/**
 * The regen target (decision 8): a bundle that declared a bootstrap mode regen-s
 * as bootstrap; otherwise the regen fans out FROM the first member's entity (an
 * in-world refinement of the same neighborhood). Falls back to bootstrap when
 * there is no member to anchor on.
 */
function bundleRegenTarget(
  old: BundleRegistry[string] | undefined,
  members: BundleMemberRow[],
): BulkGenTarget {
  if (old?.metadata?.mode === 'bootstrap') return { kind: 'bootstrap' };
  const anchor = members[0];
  if (anchor) return { kind: 'entity', entity_id: anchor.entityId };
  return { kind: 'bootstrap' };
}

export function draftRows(s: Model): DraftRow[] {
  const registry = loadGmRegistry();
  const out: DraftRow[] = [];
  for (const entity of Object.values(s.entities)) {
    if (entity.soft_deleted) continue;
    for (const [pred, fact] of Object.entries(entity.facts)) {
      if (fact.status !== 'draft' || fact.soft_deleted) continue;
      const latest = fact.versions[fact.versions.length - 1];
      if (!latest) continue;
      const v = latest.value;
      // Route through formatFactValue so structured values (tuples, dimensioned
      // numbers, objects) render legibly instead of `[object Object]`. The
      // null/undefined guard stays '' (not '—') so asset-id lookups + the empty
      // cell keep working; a string value passes through unchanged.
      const value = v === undefined || v === null ? '' : formatFactValue(v);
      const kind = registry.lookup(pred)?.value_kind[0];
      const isAsset = kind === 'asset' || kind === 'image';
      const isRichText = kind === 'rich_text';
      // S9 (#196): does ratifying this draft imply a predecessor-close?
      const closes = impliedClosesForRatify(
        entity.facts,
        pred,
        registry.lookup(splitSegmentKey(pred).base)?.uniqueness_key,
      );
      const succeedsNote =
        closes.length > 0
          ? t('inbox.succeeds_note', {
              count: String(closes.length),
              day: String(closes[0]!.payload.validity?.to ?? ''),
            })
          : '';
      // #164 — pre-emit advisories this draft introduces (the auditor folds
      // drafts into the projection too), attributed to this row by fact ref.
      const factId = factRef(entity.id, pred);
      const advisories = s.advisories
        .filter((a) => a.fact_ids.includes(factId))
        .map((a) => toAdvisoryRow(s, a));
      const authorTag = latest.author.toLowerCase();
      const author: 'gm' | 'llm' | 'player' =
        authorTag === 'llm' ? 'llm' : authorTag === 'player' ? 'player' : 'gm';
      out.push({
        key: `${entity.id}/${pred}`,
        succeedsNote,
        entityId: entity.id,
        entityName: entityNameOrId(s, entity.id),
        root: entity.root,
        predicate: pred,
        predicateLabel: predicateLabel(pred),
        value,
        isLlm: author === 'llm',
        author,
        authorLabel: t(`inbox.author.${author}`),
        isAsset,
        assetUrl: isAsset ? (s.assetUrls[value] ?? '') : '',
        isRichText,
        advisories,
      });
    }
  }
  return out.sort(
    (a, b) => a.entityName.localeCompare(b.entityName) || a.predicate.localeCompare(b.predicate),
  );
}

// ---------- Session HUD pending lane (#246 / 07 §8.3 / 09 §3.5) ----------
//
// The GM live-play surface for the #223 pending lane. #223 silently promotes
// allowlisted LLM drafts to `pending` (a SEPARATE `system`-authored
// `fact.set_status` — the LLM never authored `pending`); this surface lists
// those facts (plus any player-authored pending) so the GM can ratify (→canon,
// an explicit per-fact GM gesture) or reject (→retract) inline during play.
//
// The LLM→canon backdoor stays closed: ratify here emits a GM-authored
// `submit-fact-status` op (exactly the inbox path, `GM_AUTHOR`), never an
// LLM/system author and never a standing auto-ratify policy. Pending LLM facts
// carry ZERO reveal ops (#223), so the projection chokepoint already keeps them
// out of every non-GM view — this surface is GM-only by construction.

/**
 * One pending fact row for the session HUD. Plain object/array (llui-safe),
 * mirroring {@link DraftRow}. `isLlm` drives the provenance dot + label so the
 * GM can see at a glance which pending facts came from the LLM lane.
 */
export interface PendingRow {
  key: string;
  entityId: string;
  entityName: string;
  predicate: string;
  predicateLabel: string;
  value: string;
  /** Authoring source of the head version — `llm` is a #223-lane promotion. */
  author: 'gm' | 'llm' | 'player';
  /** True when the head version was authored by the LLM (the #223 lane). */
  isLlm: boolean;
  /** Localised provenance-badge label ("AI" / "Player" / "GM"). */
  authorLabel: string;
}

/**
 * Every canon-bound `pending` fact across the world, sorted for a stable HUD
 * list. Mirrors {@link draftRows} but filters `status === 'pending'`. Belief-
 * owned pending facts are excluded — the HUD pending lane is the GM cleanup
 * queue for canon-track facts (07 §8.3); beliefs are not GM-ratifiable canon.
 */
export function pendingRows(s: Model): PendingRow[] {
  const out: PendingRow[] = [];
  for (const entity of Object.values(s.entities)) {
    if (entity.soft_deleted) continue;
    for (const [pred, fact] of Object.entries(entity.facts)) {
      if (fact.status !== 'pending' || fact.soft_deleted) continue;
      if (fact.owner.kind !== 'canon') continue;
      const latest = fact.versions[fact.versions.length - 1];
      if (!latest) continue;
      const v = latest.value;
      // Route through formatFactValue so structured values (tuples, dimensioned
      // numbers, objects) render legibly instead of `[object Object]`. The
      // null/undefined guard stays '' (not '—') so asset-id lookups + the empty
      // cell keep working; a string value passes through unchanged.
      const value = v === undefined || v === null ? '' : formatFactValue(v);
      const authorTag = latest.author.toLowerCase();
      const author: 'gm' | 'llm' | 'player' =
        authorTag === 'llm' ? 'llm' : authorTag === 'player' ? 'player' : 'gm';
      out.push({
        key: `${entity.id}/${pred}`,
        entityId: entity.id,
        entityName: entityNameOrId(s, entity.id),
        predicate: pred,
        predicateLabel: predicateLabel(pred),
        value,
        author,
        isLlm: author === 'llm',
        authorLabel: t(`hud.pending.from_${author}`),
      });
    }
  }
  return out.sort(
    (a, b) => a.entityName.localeCompare(b.entityName) || a.predicate.localeCompare(b.predicate),
  );
}

/** The number of pending facts the HUD lists (count + section always exist). */
export function pendingCount(s: Model): number {
  return pendingRows(s).length;
}

/**
 * Whether the session-HUD pending lane (the ratify/reject cleanup surface)
 * renders. GM-only: the inline ratify→canon gesture is a GM action, so the
 * whole surface is gated on GM mode. Players never see the cleanup affordances
 * even though a pending fact may be presentationally canonical in their
 * projection (07 §8.3 — the GM cleanup chip is GM-side).
 */
export function hudPendingVisible(s: Model): boolean {
  return s.session.mode === 'gm' && pendingCount(s) > 0;
}

/**
 * The attention-grabbing pending count badge value — the count the `surface`
 * lane mode increments (#223). `surface` returns the live pending count;
 * `silent` (and lane-off) returns 0 so the section renders without a
 * notification badge (the "don't interrupt me" live-play mode). The section +
 * its inline count still render regardless — only the attention cue is gated.
 */
export function pendingNotifyCount(s: Model): number {
  if (s.session.mode !== 'gm') return 0;
  const mode = s.config.llm_pending_lane_mode ?? DEFAULT_LLM_PENDING_LANE_MODE;
  return mode === 'surface' ? pendingCount(s) : 0;
}

/**
 * Phase C — the entity's EMPTY prose (single rich_text) predicates: the
 * candidates a per-page "Draft with AI" run can fill. Same emptiness rule
 * as the per-fact ✨ (don't shadow existing canon). Bare-Identifier so the
 * cross-file walker registers the reads.
 */
function emptyProsePredicates(s: Model, entityId: string): string[] {
  const registry = loadGmRegistry();
  const ids = predicateIdsForTraits(traitsAtId(s, entityId), registry, getTraitIndex());
  const out: string[] = [];
  for (const pred of ids) {
    const decl = registry.lookup(pred);
    if (!decl || hasObjectUniquenessKey(decl) || decl.value_kind[0] !== 'rich_text') continue;
    if (factIsEmptyAtPredicate(s, entityId, pred)) out.push(pred);
  }
  return out;
}

/**
 * One read-only row of the Overview infobox: a predicate's display
 * label paired with its current value, formatted for reading.
 */
/** One segment of an Overview value — a link (refTo set) or plain text. */
interface OverviewLink {
  text: string;
  /** Target entity id when this segment links to another entity; else null. */
  refTo: string | null;
}

interface OverviewRow {
  predicate: string;
  label: string;
  /**
   * Plain (non-relationship) rendering of the value — text/number/enum.
   * null when the value is a relationship (use `links` instead).
   */
  plain: string | null;
  /**
   * Pre-rendered HTML for a `rich_text` value (Markdown → safe HTML via
   * `markdownToHtml`). null for every non-prose row. When set, the row
   * renders this through `h.unsafeHtml` instead of escaping `plain` — so
   * the Overview shows formatted prose, not raw Markdown characters.
   */
  html: string | null;
  /**
   * Relationship segments. A single-cardinality ref yields one; a
   * many-cardinality ref (allies, members, holds…) yields one per
   * target. Each renders as its own navigable link. Empty for plain rows.
   */
  links: OverviewLink[];
  /** Non-canon status ('draft' | 'pending' | 'hypothetical') or null. */
  status: string | null;
  /**
   * Asset-kind rows render the image, not the raw `asset_…` handle. The
   * resolved signed URL ('' while pending) when this row is an asset fact;
   * null for every non-asset row.
   */
  assetUrl: string | null;
}

/**
 * Format a primitive fact value for the read-only Overview. Returns
 * `null` for values we don't render inline (asset handles, datetime
 * ranges, other objects) — those stay in the Facts tab. Ref resolution
 * (entity-id → name + link) is handled by the caller, which has `s`.
 */
function formatOverviewValue(v: unknown): string | null {
  if (v === undefined || v === null) return null;
  if (typeof v === 'string') return v.trim().length === 0 ? null : v;
  if (typeof v === 'number') return Number.isFinite(v) ? v.toLocaleString() : null;
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  // Dimensioned number value (ADR-0007) → "<value> <symbol>".
  return formatNumberValueForDisplay(v);
}

function isPlainRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/**
 * Humanise a tuple (structured-record) value for the read-only Overview —
 * e.g. a Government record `{ holder: 'ent_…', role: 'mayor', notes: '…' }`
 * renders as "Holder: Aldric · Role: mayor · Notes: …". Each field uses its
 * declared label (falling back to the field name); `ref`-typed fields
 * resolve the stored entity id to that entity's NAME. Returns null when no
 * field renders (an empty record), so the caller can skip the row.
 *
 * Field labels are author-supplied `LocalizedString`s (not i18n keys), so
 * they resolve through `resolveLocalizedField`, not `t()`.
 */
function formatTupleValue(
  decl: PredicateDeclaration,
  record: Record<string, unknown>,
  s: Model,
): string | null {
  const fields = decl.tuple_fields;
  if (!fields || fields.length === 0) return null;
  const parts: string[] = [];
  for (const field of fields) {
    // Tuple records are keyed by the field's opaque `fld_` id (ADR-0004 §1).
    const raw = record[field.id];
    if (raw === undefined || raw === null || raw === '') continue;
    const fieldLabel = resolveLocalizedField(field) || field.id;
    let valueText: string;
    if (field.value_kind === 'ref' && typeof raw === 'string') {
      valueText = s.entities[raw] !== undefined ? entityNameOrId(s, raw) : raw;
    } else if (field.value_kind === 'enum' && typeof raw === 'string') {
      // Enum values store the opaque `enm_` id; resolve to its display name.
      const opt = field.enum_values?.find((v) => v.id === raw);
      valueText = opt ? resolveLocalizedDisplay(opt.label) || raw : raw;
    } else {
      const f = formatOverviewValue(raw);
      if (f === null) continue;
      valueText = f;
    }
    parts.push(`${fieldLabel}: ${valueText}`);
  }
  return parts.length === 0 ? null : parts.join(' · ');
}

/**
 * Resolve a tuple field's author-supplied `LocalizedString` label to a
 * display string via the locale chain (preferred → 'en' → first value).
 * Returns '' when the field declares no label (caller falls back to the
 * field name). NOT an i18n catalog lookup — these are content, not keys.
 */
function resolveLocalizedField(field: TupleFieldDeclaration): string {
  return resolveLocalizedDisplay(field.label);
}

/**
 * Resolve an author-supplied `LocalizedString` (boolean state labels, tuple
 * field labels, …) to a display string via the locale chain
 * (preferred → 'en' → first value). Returns '' when absent. NOT an i18n
 * catalog lookup — these are user content, not keys.
 */
function resolveLocalizedDisplay(
  label: string | Record<string, string> | undefined,
): string {
  if (label === undefined) return '';
  if (typeof label === 'string') return label;
  const locale =
    typeof navigator !== 'undefined' && navigator.language ? navigator.language : 'en';
  const short = locale.split('-')[0] ?? 'en';
  return label[locale] ?? label[short] ?? label['en'] ?? Object.values(label)[0] ?? '';
}

/**
 * Build the Overview infobox rows for an entity: the `name` fact
 * first (it's the title's source but also belongs in the infobox),
 * then every other fact with a renderable current value, ordered by
 * display label. Reads `s.entities` directly (bare-Identifier helper)
 * so the each-items accessor re-runs on every fold rebuild.
 *
 * Relationship resolution: a string value that matches another
 * entity's id renders as that entity's NAME with `refTo` set (a link).
 * An array of such ids (multi-cardinality relationships, e.g. allies)
 * renders as a comma-joined list of names (links deferred for the
 * multi case). This is what makes relationships readable on the
 * story-first Overview rather than showing raw ids.
 */
function entityOverviewRows(s: Model, entityId: string): OverviewRow[] {
  const entity = s.entities[entityId];
  if (!entity || entity.soft_deleted) return [];
  const registry = loadGmRegistry();
  const rows: OverviewRow[] = [];
  for (const [factKey, fact] of Object.entries(entity.facts)) {
    if (!fact || fact.soft_deleted) continue;
    const latest = fact.versions[fact.versions.length - 1];
    const raw = latest?.value;
    // A repeatable predicate's fact lands under `predicate#instance`
    // (ADR-0002); split so lookup/label use the bare predicate id while
    // the row key stays the full (unique-per-instance) `factKey`.
    const { predicate } = splitFactKey(factKey);

    let plain: string | null = null;
    let html: string | null = null;
    let links: OverviewLink[] = [];
    let assetUrl: string | null = null;
    const decl = registry.lookup(predicate);
    const overviewKind = decl?.value_kind[0];
    if (overviewKind === 'asset' || overviewKind === 'image') {
      // Asset/image-kind: render the image (resolved signed URL), never
      // the opaque handle. Skip the row entirely when there's no value.
      if (typeof raw !== 'string' || raw === '') continue;
      assetUrl = s.assetUrls[raw] ?? '';
    } else if (overviewKind === 'rich_text' && typeof raw === 'string' && raw.trim() !== '') {
      // Prose: carry the RAW markdown source; the view renders it reactively
      // through @llui/markdown's `markdown()` (not a pre-serialized string).
      html = raw;
    } else if (overviewKind === 'tuple' && decl && isPlainRecord(raw)) {
      // Structured record (e.g. a Government holder/role/notes tuple):
      // humanise as "Label: value" pairs, resolving ref fields to entity
      // names. Skip the row when nothing renders (empty record).
      plain = formatTupleValue(decl, raw, s);
      if (plain === null) continue;
    } else if (typeof raw === 'string' && s.entities[raw] !== undefined) {
      // Single relationship → one link segment.
      links = [{ text: entityNameOrId(s, raw), refTo: raw }];
    } else if (Array.isArray(raw)) {
      // Many-cardinality: one segment per item. Ref ids resolve to the
      // target's name + link; other strings render as plain segments.
      links = raw
        .map((item): OverviewLink | null => {
          if (typeof item === 'string') {
            return s.entities[item] !== undefined
              ? { text: entityNameOrId(s, item), refTo: item }
              : { text: item, refTo: null };
          }
          const f = formatOverviewValue(item);
          return f === null ? null : { text: f, refTo: null };
        })
        .filter((l): l is OverviewLink => l !== null && l.text !== '');
      if (links.length === 0) continue;
    } else {
      plain = formatOverviewValue(raw);
      if (plain === null) continue;
    }
    rows.push({
      // Full (instance-unique) key so the `h.each` row key is unique even
      // when several repeatable instances share one predicate.
      predicate: factKey,
      label: predicateLabel(predicate),
      plain,
      html,
      links,
      status: fact.status === 'canon' ? null : fact.status,
      assetUrl,
    });
  }
  // `name` first (it anchors the entry), then the rest alphabetically
  // by label so the infobox order is stable and predictable.
  rows.sort((a, b) => {
    if (a.predicate === 'name') return -1;
    if (b.predicate === 'name') return 1;
    return a.label.localeCompare(b.label);
  });
  return rows;
}

function entityHasOverviewRows(s: Model, entityId: string): boolean {
  return entityOverviewRows(s, entityId).length > 0;
}

/**
 * Backlinks (#backlinks) — incoming relationships. Scans every other
 * entity's facts for a ref (single value or array element) pointing at
 * `entityId`, and groups them by the source predicate. Each group is
 * rendered exactly like an Overview row (label + link segments), so the
 * Connections tab reuses the same renderer. The group label is the
 * predicate's `reciprocal` (e.g. `holds` → "held by"), falling back to a
 * generic "referenced as …" when none is declared.
 *
 * Computed from the GM's folded `s.entities` (the op log is the single
 * source — no inverse fact is stored). Player-perspective backlinks
 * (over projected facts) arrive with player mode.
 */
function entityBacklinks(s: Model, entityId: string): OverviewRow[] {
  const target = s.entities[entityId];
  if (!target || target.soft_deleted) return [];
  const registry = loadGmRegistry();
  const byPredicate = new Map<string, OverviewLink[]>();
  for (const [sourceId, source] of Object.entries(s.entities)) {
    if (sourceId === entityId || source.soft_deleted) continue;
    for (const [predicate, fact] of Object.entries(source.facts)) {
      if (!fact || fact.soft_deleted) continue;
      const raw = fact.versions[fact.versions.length - 1]?.value;
      const hits =
        (typeof raw === 'string' && raw === entityId) ||
        (Array.isArray(raw) && raw.includes(entityId));
      if (!hits) continue;
      const arr = byPredicate.get(predicate) ?? [];
      arr.push({ text: entityNameOrId(s, sourceId), refTo: sourceId });
      byPredicate.set(predicate, arr);
    }
  }
  const rows: OverviewRow[] = [];
  for (const [predicate, sources] of byPredicate) {
    const decl = registry.lookup(predicate);
    const label = decl?.reciprocal ?? t('connections.referenced_as', { label: predicateLabel(predicate) });
    sources.sort((a, b) => a.text.localeCompare(b.text));
    rows.push({ predicate, label, plain: null, html: null, links: sources, status: null, assetUrl: null });
  }
  rows.sort((a, b) => a.label.localeCompare(b.label));
  return rows;
}

function entityHasBacklinks(s: Model, entityId: string): boolean {
  return entityBacklinks(s, entityId).length > 0;
}

/** One Event in the causal chain, in causal order. */
interface CausalChainEntry {
  id: string;
  name: string;
  /** The Event whose page we're on (rendered as the current step, not a link). */
  focal: boolean;
}

/**
 * Build the campaign-wide causal/precedence graph from the folded canon facts,
 * normalising `caused_by` (cause → effect) and `precedes` (earlier → later)
 * into one "happens-before" edge set (#222, `06-timeline.md` §8). Pure
 * projection over the op log — no inverse facts are stored. Canon-only, so the
 * timeline matches the auditor's `causal.cycle` graph and stays free of belief
 * noise.
 */
function causalGraph(s: Model): {
  edges: DirectedEdge[];
  undirected: Map<string, Set<string>>;
} {
  const directionByPredicate = new Map(
    OFFICIAL_CORE_CAUSAL_PREDICATES.map((spec) => [spec.predicate, spec.direction] as const),
  );
  const edges: DirectedEdge[] = [];
  const undirected = new Map<string, Set<string>>();
  const link = (a: string, b: string): void => {
    (undirected.get(a) ?? undirected.set(a, new Set()).get(a)!).add(b);
    (undirected.get(b) ?? undirected.set(b, new Set()).get(b)!).add(a);
  };
  for (const [sourceId, source] of Object.entries(s.entities)) {
    if (!source || source.soft_deleted) continue;
    for (const [fk, fact] of Object.entries(source.facts)) {
      if (!fact || fact.soft_deleted || fact.owner.kind !== 'canon') continue;
      const direction = directionByPredicate.get(splitFactKey(fk).predicate);
      if (direction === undefined) continue;
      const value = fact.versions[fact.versions.length - 1]?.value;
      if (typeof value !== 'string' || value.length === 0) continue;
      const target = s.entities[value];
      if (!target || target.soft_deleted) continue;
      edges.push(causalEdge(sourceId, value, direction));
      link(sourceId, value);
    }
  }
  return { edges, undirected };
}

/** The causal chain of an entity: its ordered component + whether it loops. */
interface CausalComponentAnalysis {
  /**
   * The entity's connected component in causal order (causes first). Empty
   * unless it participates in a chain of ≥ 2 Events (a lone Event has no chain).
   */
  entries: CausalChainEntry[];
  /** The component contains a directed loop, so the order is only partial. */
  hasCycle: boolean;
}

// Single-entry memo: the three reactive Overview accessors below
// (`entityHasCausalChain` / `causalChainEntries` / `causalChainHasCycle`) each
// run in their own signal slot within one render, but all receive the same
// `Model` — so they share `s.entities` by reference and the same focal id. The
// fold rebuilds `s.entities` into a fresh object on every change, so identity
// equality is a sound cache key: it hits across the three slots of one render
// (and across renders while the world is unchanged) and misses the instant the
// op log advances.
let causalComponentCacheKey: { entities: Record<string, EntityState>; entityId: string } | null =
  null;
let causalComponentCacheVal: CausalComponentAnalysis | null = null;

/**
 * The causal chain `entityId` belongs to — the undirected connected component
 * of the happens-before graph containing it, topologically sorted (causes
 * first), plus whether that component loops. The single chokepoint that walks
 * the graph; the per-field accessors below are thin readers over it.
 */
function analyzeCausalComponent(s: Model, entityId: string): CausalComponentAnalysis {
  if (
    causalComponentCacheKey !== null &&
    causalComponentCacheVal !== null &&
    causalComponentCacheKey.entities === s.entities &&
    causalComponentCacheKey.entityId === entityId
  ) {
    return causalComponentCacheVal;
  }

  let result: CausalComponentAnalysis;
  const focal = s.entities[entityId];
  if (!focal || focal.soft_deleted) {
    result = { entries: [], hasCycle: false };
  } else {
    const { edges, undirected } = causalGraph(s);
    // Undirected BFS for the connected component containing the focal event.
    const component = new Set<string>([entityId]);
    const queue: string[] = [entityId];
    while (queue.length > 0) {
      const n = queue.shift()!;
      for (const m of undirected.get(n) ?? []) {
        if (!component.has(m)) {
          component.add(m);
          queue.push(m);
        }
      }
    }
    if (component.size < 2) {
      result = { entries: [], hasCycle: false };
    } else {
      const compEdges = edges.filter((e) => component.has(e.from) && component.has(e.to));
      const { order, cycles } = analyzeDirectedGraph(component, compEdges);
      result = {
        entries: order.map((id) => ({ id, name: entityNameOrId(s, id), focal: id === entityId })),
        hasCycle: cycles.length > 0,
      };
    }
  }

  causalComponentCacheKey = { entities: s.entities, entityId };
  causalComponentCacheVal = result;
  return result;
}

function causalChainEntries(s: Model, entityId: string): CausalChainEntry[] {
  return analyzeCausalComponent(s, entityId).entries;
}

function entityHasCausalChain(s: Model, entityId: string): boolean {
  return analyzeCausalComponent(s, entityId).entries.length > 0;
}

function causalChainHasCycle(s: Model, entityId: string): boolean {
  return analyzeCausalComponent(s, entityId).hasCycle;
}

// ---------- #257 Dice roller + Initiative tracker (07 §13.9.1) ----------
//
// Ephemeral live-play session affordances — never persisted to the op log.
// Lives in `Model.diceInput / diceHistory / initiative / initiativeAddForm`.
// GM-only: mounted only in GM chrome (session mode guard in viewAppShell
// is sufficient; these functions need not repeat it).

/**
 * Dice roller widget — notation input + roll button + recent history.
 * Keyboard: Enter on the input fires `dice-roll`; Tab reaches Roll button.
 */
function viewDiceRoller(state: Signal<Model>, send: Send<Msg>): Mountable {
  return div(
    {
      class: 'dice-roller',
      role: 'region',
      'aria-label': t('dice.heading'),
      'data-testid': 'dice-roller',
    },
    [
      h2({ class: 'dice-roller-heading' }, [text(t('dice.heading'))]),
      // Input row: notation field + roll button.
      div({ class: 'dice-roller-input-row' }, [
        label({ class: 'dice-roller-label', for: 'dice-notation-input' }, [
          text(t('dice.input.label')),
        ]),
        input({
          id: 'dice-notation-input',
          class: 'dice-roller-input',
          type: 'text',
          placeholder: t('dice.input.placeholder'),
          'aria-label': t('dice.input.label'),
          'aria-describedby': 'dice-error-msg',
          'data-testid': 'dice-input',
          value: state.at('diceInput'),
          onInput: (e: Event) => {
            const target = e.target as HTMLInputElement;
            send({ type: 'dice-input', value: target.value });
          },
          onKeyDown: (e: KeyboardEvent) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              send({ type: 'dice-roll' });
            }
          },
        }),
        uiButton({
          variant: 'primary',
          className: 'dice-roller-roll-btn',
          testid: 'dice-roll-btn',
          ariaLabel: t('dice.roll'),
          onClick: () => send({ type: 'dice-roll' }),
          children: [text(t('dice.roll'))],
        }),
      ]),
      // Error message — paired with the input via aria-describedby.
      show(
        state.map((s) => s.diceError !== null),
        () => [
          p({
            id: 'dice-error-msg',
            class: 'dice-roller-error',
            role: 'alert',
            'aria-live': 'polite',
            'data-testid': 'dice-error',
          }, [
            text(state.map((s) => s.diceError ?? '')),
          ]),
        ],
      ),
      // Roll history.
      div(
        {
          class: 'dice-history',
          role: 'log',
          'aria-label': t('dice.history.label'),
          'aria-live': 'polite',
          'data-testid': 'dice-history',
        },
        [
          show(
            state.map((s) => s.diceHistory.length === 0),
            () => [
              p({ class: 'dice-history-empty', 'data-testid': 'dice-history-empty' }, [
                text(t('dice.history.empty')),
              ]),
            ],
          ),
          show(
            state.map((s) => s.diceHistory.length > 0),
            () => [
              div({ class: 'dice-history-clear-row' }, [
                uiButton({
                  variant: 'ghost',
                  className: 'dice-history-clear',
                  testid: 'dice-history-clear',
                  ariaLabel: t('dice.clear'),
                  onClick: () => send({ type: 'dice-clear-history' }),
                  children: [text(t('dice.clear'))],
                }),
              ]),
              ol({ class: 'dice-history-list', role: 'list' }, [
                each(state.at('diceHistory'), {
                  key: (row) => row.id,
                  render: (item) => [
                    li(
                      {
                        class: 'dice-history-row',
                        'data-testid': 'dice-history-row',
                        'aria-label': item.map((r) =>
                          t('dice.history.row_label', {
                            notation: r.notation,
                            breakdown: r.breakdown,
                          }),
                        ),
                      },
                      [
                        span({ class: 'dice-history-notation' }, [
                          text(item.at('notation')),
                        ]),
                        span(
                          {
                            class: 'dice-history-total',
                            'aria-hidden': 'true',
                          },
                          [text(item.map((r) => String(r.total)))],
                        ),
                        span({ class: 'dice-history-breakdown' }, [
                          text(item.at('breakdown')),
                        ]),
                      ],
                    ),
                  ],
                }),
              ]),
            ],
          ),
        ],
      ),
    ],
  );
}

/**
 * One initiative entry row — current turn highlight, conditions, HP scratch,
 * remove button. Used inside the `each` in `viewInitiativeTracker`.
 */
function viewInitiativeEntryRow(
  item: Signal<InitiativeEntry>,
  activeId: Signal<string | undefined>,
  send: Send<Msg>,
): Mountable {
  const isActive = derived([item, activeId], (e, aid) => e.id === aid);
  return li(
    {
      class: 'initiative-entry',
      'data-testid': 'initiative-entry',
      'data-active': isActive.map((a) => (a ? 'true' : 'false')),
      'aria-label': item.map((e) =>
        t('initiative.entry.label', {
          name: e.display_name,
          initiative: String(e.initiative),
        }),
      ),
      'aria-current': isActive.map((a) => (a ? 'step' : 'false')),
    },
    [
      div({ class: 'initiative-entry-header' }, [
        // Initiative value badge.
        span(
          {
            class: 'initiative-entry-initiative',
            'aria-hidden': 'true',
          },
          [text(item.map((e) => String(e.initiative)))],
        ),
        // Name.
        span({ class: 'initiative-entry-name' }, [text(item.at('display_name'))]),
        // Remove button.
        uiButton({
          variant: 'ghost',
          className: 'initiative-entry-remove',
          testid: 'initiative-remove',
          ariaLabel: item.map((e) =>
            t('initiative.remove_label', { name: e.display_name }),
          ),
          onClick: () => {
            const e = item.peek();
            send({ type: 'initiative-remove', id: e.id });
          },
          children: [text(t('initiative.remove'))],
        }),
      ]),
      // HP scratch (only when set).
      show(
        item.map((e) => e.hp_scratch !== undefined),
        () => [
          div({ class: 'initiative-entry-hp' }, [
            span({ class: 'initiative-entry-hp-label' }, [
              text(
                item.map((e) =>
                  e.hp_scratch?.max !== undefined
                    ? t('initiative.hp_of', {
                        current: String(e.hp_scratch.current),
                        max: String(e.hp_scratch.max),
                      })
                    : t('initiative.hp', { current: String(e.hp_scratch?.current ?? 0) }),
                ),
              ),
            ]),
            uiButton({
              variant: 'ghost',
              className: 'initiative-entry-hp-dmg',
              testid: 'initiative-hp-dmg',
              ariaLabel: t('initiative.damage'),
              attrs: { title: t('initiative.hp_damage') },
              onClick: () => {
                const e = item.peek();
                send({ type: 'initiative-hp-delta', id: e.id, delta: -1 });
              },
              children: [text(t('initiative.damage'))],
            }),
            uiButton({
              variant: 'ghost',
              className: 'initiative-entry-hp-heal',
              testid: 'initiative-hp-heal',
              ariaLabel: t('initiative.heal'),
              attrs: { title: t('initiative.hp_heal') },
              onClick: () => {
                const e = item.peek();
                send({ type: 'initiative-hp-delta', id: e.id, delta: 1 });
              },
              children: [text(t('initiative.heal'))],
            }),
          ]),
        ],
      ),
      // Conditions (shown only when non-empty).
      show(
        item.map((e) => e.conditions.length > 0),
        () => [
          div(
            {
              class: 'initiative-entry-conditions',
              'aria-label': t('initiative.conditions.label'),
            },
            [
              each(
                item.map((e) => e.conditions.map((c, i) => ({ id: `${e.id}_c${i}`, value: c }))),
                {
                  key: (c) => c.id,
                  render: (cond) => [
                    span({ class: 'initiative-condition-chip' }, [
                      text(cond.at('value')),
                    ]),
                  ],
                },
              ),
            ],
          ),
        ],
      ),
    ],
  );
}

/**
 * Initiative tracker widget: participant list + turn controls + add form.
 * The design spec (07 §13.9.1) keeps this entirely ephemeral — no op-log
 * writes, no persist.
 */
function viewInitiativeTracker(state: Signal<Model>, send: Send<Msg>): Mountable {
  const activeId = state.map(
    (s) => s.initiative.entries[s.initiative.active_index]?.id,
  );

  return div(
    {
      class: 'initiative-tracker',
      role: 'region',
      'aria-label': t('initiative.heading'),
      'data-testid': 'initiative-tracker',
    },
    [
      div({ class: 'initiative-header' }, [
        h2({ class: 'initiative-heading' }, [text(t('initiative.heading'))]),
        // Round badge (only when in progress).
        show(
          state.map((s) => s.initiative.entries.length > 0),
          () => [
            span(
              {
                class: 'initiative-round',
                role: 'status',
                'aria-live': 'polite',
                'data-testid': 'initiative-round',
              },
              [
                text(state.map((s) => t('initiative.round', { n: String(s.initiative.round) }))),
              ],
            ),
          ],
        ),
      ]),
      // Active-turn announcement for screen readers.
      show(
        state.map((s) => s.initiative.entries.length > 0),
        () => [
          p(
            {
              class: 'initiative-active-announce',
              role: 'status',
              'aria-live': 'assertive',
              'data-testid': 'initiative-active-announce',
            },
            [
              text(
                derived([state], (s) => {
                  const e = s.initiative.entries[s.initiative.active_index];
                  return e ? t('initiative.active_label', { name: e.display_name }) : '';
                }),
              ),
            ],
          ),
        ],
      ),
      // Participant list.
      show(
        state.map((s) => s.initiative.entries.length === 0 && s.initiativeAddForm === null),
        () => [
          p({ class: 'initiative-empty', 'data-testid': 'initiative-empty' }, [
            text(t('initiative.empty')),
          ]),
        ],
      ),
      show(
        state.map((s) => s.initiative.entries.length > 0),
        () => [
          ol(
            {
              class: 'initiative-list',
              role: 'list',
              'data-testid': 'initiative-list',
            },
            [
              each(state.map((s) => s.initiative.entries), {
                key: (e) => e.id,
                render: (item) => [viewInitiativeEntryRow(item, activeId, send)],
              }),
            ],
          ),
        ],
      ),
      // Add-participant form (inline, below the list).
      show(
        state.map((s) => s.initiativeAddForm !== null),
        () => [
          div(
            {
              class: 'initiative-add-form',
              role: 'form',
              'aria-label': t('initiative.add'),
              'data-testid': 'initiative-add-form',
            },
            [
              // Name field.
              div({ class: 'initiative-add-form-field' }, [
                label({ class: 'initiative-add-label', for: 'initiative-add-name' }, [
                  text(t('initiative.add_form.name_label')),
                ]),
                input({
                  id: 'initiative-add-name',
                  class: 'initiative-add-input',
                  type: 'text',
                  placeholder: t('initiative.add_form.name_placeholder'),
                  'aria-label': t('initiative.add_form.name_label'),
                  'data-testid': 'initiative-add-name',
                  value: state.map((s) => s.initiativeAddForm?.name ?? ''),
                  onInput: (e: Event) => {
                    const target = e.target as HTMLInputElement;
                    send({ type: 'initiative-add-form-name', value: target.value });
                  },
                  onKeyDown: (e: KeyboardEvent) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      send({ type: 'initiative-add-form-submit' });
                    }
                    if (e.key === 'Escape') {
                      send({ type: 'initiative-add-form-cancel' });
                    }
                  },
                }),
              ]),
              // Initiative value field.
              div({ class: 'initiative-add-form-field' }, [
                label({ class: 'initiative-add-label', for: 'initiative-add-init' }, [
                  text(t('initiative.add_form.initiative_label')),
                ]),
                input({
                  id: 'initiative-add-init',
                  class: 'initiative-add-input',
                  type: 'number',
                  'aria-label': t('initiative.add_form.initiative_label'),
                  'data-testid': 'initiative-add-initiative',
                  value: state.map((s) => s.initiativeAddForm?.initiative ?? ''),
                  onInput: (e: Event) => {
                    const target = e.target as HTMLInputElement;
                    send({ type: 'initiative-add-form-initiative', value: target.value });
                  },
                  onKeyDown: (e: KeyboardEvent) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      send({ type: 'initiative-add-form-submit' });
                    }
                    if (e.key === 'Escape') {
                      send({ type: 'initiative-add-form-cancel' });
                    }
                  },
                }),
              ]),
              // HP (optional).
              div({ class: 'initiative-add-form-field' }, [
                label({ class: 'initiative-add-label', for: 'initiative-add-hp' }, [
                  text(t('initiative.add_form.hp_label')),
                ]),
                input({
                  id: 'initiative-add-hp',
                  class: 'initiative-add-input',
                  type: 'number',
                  'aria-label': t('initiative.add_form.hp_label'),
                  'data-testid': 'initiative-add-hp',
                  value: state.map((s) => s.initiativeAddForm?.hp ?? ''),
                  onInput: (e: Event) => {
                    const target = e.target as HTMLInputElement;
                    send({ type: 'initiative-add-form-hp', value: target.value });
                  },
                  onKeyDown: (e: KeyboardEvent) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      send({ type: 'initiative-add-form-submit' });
                    }
                    if (e.key === 'Escape') {
                      send({ type: 'initiative-add-form-cancel' });
                    }
                  },
                }),
              ]),
              div({ class: 'initiative-add-form-actions' }, [
                uiButton({
                  variant: 'primary',
                  className: 'initiative-add-submit',
                  testid: 'initiative-add-submit',
                  ariaLabel: t('initiative.add_form.submit'),
                  onClick: () => send({ type: 'initiative-add-form-submit' }),
                  children: [text(t('initiative.add_form.submit'))],
                }),
                uiButton({
                  variant: 'ghost',
                  className: 'initiative-add-cancel',
                  testid: 'initiative-add-cancel',
                  ariaLabel: t('initiative.add_form.cancel'),
                  onClick: () => send({ type: 'initiative-add-form-cancel' }),
                  children: [text(t('initiative.add_form.cancel'))],
                }),
              ]),
            ],
          ),
        ],
      ),
      // Turn controls + utility buttons.
      div({ class: 'initiative-controls' }, [
        uiButton({
          variant: 'primary',
          className: 'initiative-advance',
          testid: 'initiative-advance',
          ariaLabel: t('initiative.advance'),
          disabled: state.map((s) => s.initiative.entries.length === 0),
          onClick: () => send({ type: 'initiative-advance-turn' }),
          children: [text(t('initiative.advance'))],
        }),
        show(
          state.map((s) => s.initiativeAddForm === null),
          () => [
            uiButton({
              variant: 'ghost',
              className: 'initiative-add-btn',
              testid: 'initiative-add-btn',
              ariaLabel: t('initiative.add'),
              onClick: () => send({ type: 'initiative-open-add-form' }),
              children: [text(t('initiative.add'))],
            }),
          ],
        ),
        show(
          state.map((s) => s.initiative.entries.length > 0),
          () => [
            uiButton({
              variant: 'ghost',
              className: 'initiative-reset',
              testid: 'initiative-reset',
              ariaLabel: t('initiative.reset'),
              onClick: () => send({ type: 'initiative-reset' }),
              children: [text(t('initiative.reset'))],
            }),
          ],
        ),
        // #259 — End encounter chip + dropdown (07 §13.9.2).
        show(
          state.map((s) => s.initiative.entries.length > 0),
          () => [
            div({ class: 'combat-log-end-encounter', 'data-testid': 'combat-log-end-encounter' }, [
              uiButton({
                variant: 'primary',
                className: 'combat-log-end-btn',
                testid: 'combat-log-end-btn',
                ariaLabel: t('combat_log.end_encounter'),
                onClick: () => send({ type: 'combat-log-menu-open' }),
                children: [text(t('combat_log.end_encounter'))],
              }),
              show(
                state.at('combatLogMenu').map((m) => m === 'open'),
                () => [
                  div(
                    {
                      class: 'combat-log-menu',
                      role: 'menu',
                      'aria-label': t('combat_log.end_encounter_menu_label'),
                      'data-testid': 'combat-log-menu',
                    },
                    [
                      uiButton({
                        variant: 'ghost',
                        className: 'combat-log-quick-log-btn',
                        testid: 'combat-log-quick-log-btn',
                        ariaLabel: t('combat_log.quick_log'),
                        onClick: () => send({ type: 'combat-log-quick-log' }),
                        children: [text(t('combat_log.quick_log'))],
                      }),
                      uiButton({
                        variant: 'ghost',
                        className: 'combat-log-open-form-btn',
                        testid: 'combat-log-open-form-btn',
                        ariaLabel: t('combat_log.open_full_form'),
                        onClick: () => send({ type: 'combat-log-open-full-form' }),
                        children: [text(t('combat_log.open_full_form'))],
                      }),
                      uiButton({
                        variant: 'ghost',
                        className: 'combat-log-menu-cancel-btn',
                        testid: 'combat-log-menu-cancel-btn',
                        ariaLabel: t('combat_log.cancel'),
                        onClick: () => send({ type: 'combat-log-menu-close' }),
                        children: [text(t('combat_log.cancel'))],
                      }),
                    ],
                  ),
                ],
              ),
            ]),
          ],
        ),
      ]),
    ],
  );
}

/**
 * #259 — Full-form post-combat log modal (07 §13.9.2). The GM reviews
 * participant fates, adds loot notes, and optionally links a location before
 * emitting the `combat_log` bundle. All facts land as drafts; the GM
 * reviews them via the existing #251 bundle-review UI.
 */
function viewCombatLogFullForm(state: Signal<Model>, send: Send<Msg>): Mountable {
  return div(
    {
      class: 'combat-log-modal modal-overlay',
      role: 'dialog',
      'aria-modal': 'true',
      'aria-label': t('combat_log.full_form_title'),
      'data-testid': 'combat-log-full-form',
    },
    [
      div({ class: 'modal-panel' }, [
        h2({ class: 'modal-title' }, [text(t('combat_log.full_form_title'))]),
        // Participants section — named entries only (fodder is aggregated below).
        show(
          derived([state], (s) => (s.combatLogForm?.snapshot.entries.filter((e) => !!e.character_ref).length ?? 0) > 0),
          () => [
            div({ class: 'combat-log-section' }, [
              h3({ class: 'combat-log-section-title' }, [
                text(t('combat_log.participants_section')),
              ]),
              div(
                { class: 'combat-log-participant-list', 'data-testid': 'combat-log-participant-list' },
                [
                  each(
                    derived([state], (s) =>
                      (s.combatLogForm?.snapshot.entries ?? []).filter((e) => !!e.character_ref),
                    ),
                    {
                      key: (e) => e.id,
                      render: (item) => [
                        div({ class: 'combat-log-participant-row', 'data-testid': 'combat-log-participant-row' }, [
                          span({ class: 'combat-log-participant-name' }, [
                            text(derived([item], (e) => e.display_name)),
                          ]),
                          select({
                            class: 'combat-log-fate-select',
                            'aria-label': derived([item], (e) =>
                              t('combat_log.fate_for', { name: e.display_name }),
                            ),
                            'data-testid': 'combat-log-fate-select',
                            value: derived([state, item], (s, e) =>
                              s.combatLogForm?.fateOverrides[e.id] ?? 'survived',
                            ),
                            onChange: (ev: Event) => {
                              const target = ev.target as HTMLSelectElement;
                              const entryId = item.peek().id;
                              send({
                                type: 'combat-log-form-fate',
                                entryId,
                                fate: target.value as ParticipantFate | 'fled',
                              });
                            },
                          }, [
                            option({ value: 'survived' }, [text(t('combat_log.fate.survived'))]),
                            option({ value: 'killed' }, [text(t('combat_log.fate.killed'))]),
                            option({ value: 'fled' }, [text(t('combat_log.fate.fled'))]),
                          ]),
                        ]),
                      ],
                    },
                  ),
                ],
              ),
            ]),
          ],
        ),
        // Location field.
        div({ class: 'combat-log-section' }, [
          label({ class: 'combat-log-label', for: 'combat-log-location' }, [
            text(t('combat_log.location_label')),
          ]),
          input({
            id: 'combat-log-location',
            class: 'combat-log-input',
            type: 'text',
            placeholder: t('combat_log.location_placeholder'),
            'aria-label': t('combat_log.location_label'),
            'data-testid': 'combat-log-location',
            value: derived([state], (s) => s.combatLogForm?.locationRef ?? ''),
            onInput: (e: Event) => {
              const target = e.target as HTMLInputElement;
              send({ type: 'combat-log-form-location', value: target.value });
            },
          }),
        ]),
        // Loot notes.
        div({ class: 'combat-log-section' }, [
          label({ class: 'combat-log-label', for: 'combat-log-loot' }, [
            text(t('combat_log.loot_label')),
          ]),
          textArea({
            className: 'combat-log-textarea',
            testid: 'combat-log-loot',
            rows: 3,
            placeholder: t('combat_log.loot_placeholder'),
            ariaLabel: t('combat_log.loot_label'),
            value: derived([state], (s) => s.combatLogForm?.lootNotes ?? ''),
            onInput: (value: string) => {
              send({ type: 'combat-log-form-loot', value });
            },
            attrs: { id: 'combat-log-loot' },
          }),
        ]),
        // World-state notes.
        div({ class: 'combat-log-section' }, [
          label({ class: 'combat-log-label', for: 'combat-log-world-state' }, [
            text(t('combat_log.world_state_label')),
          ]),
          textArea({
            className: 'combat-log-textarea',
            testid: 'combat-log-world-state',
            rows: 3,
            placeholder: t('combat_log.world_state_placeholder'),
            ariaLabel: t('combat_log.world_state_label'),
            value: derived([state], (s) => s.combatLogForm?.worldStateNotes ?? ''),
            onInput: (value: string) => {
              send({ type: 'combat-log-form-world-state', value });
            },
            attrs: { id: 'combat-log-world-state' },
          }),
        ]),
        // Action buttons.
        div({ class: 'modal-actions' }, [
          uiButton({
            variant: 'primary',
            className: 'combat-log-submit-btn',
            testid: 'combat-log-submit-btn',
            ariaLabel: t('combat_log.submit'),
            onClick: () => send({ type: 'combat-log-form-submit' }),
            children: [text(t('combat_log.submit'))],
          }),
          uiButton({
            variant: 'ghost',
            className: 'combat-log-cancel-btn',
            testid: 'combat-log-cancel-btn',
            ariaLabel: t('combat_log.cancel'),
            onClick: () => send({ type: 'combat-log-form-cancel' }),
            children: [text(t('combat_log.cancel'))],
          }),
        ]),
      ]),
    ],
  );
}

// ---------- narrative combat panel (#260 / 07 §13.9.5) ----------

/**
 * Narrative combat panel — GM-only, ephemeral session aid (07 §13.9.5).
 *
 * Six affordances: (1) reaction roll, (2) damage, (3) condition, (4) NPC
 * quick-pick, (5) save/check roll, (6) end-turn-with-effect. All non-roll
 * actions emit a turn-log entry; rolling also pushes to the dice-history.
 * Draft-narration button emits a `submit-fact` effect (status: 'draft') via
 * the op log — GM ratifies through the draft inbox.
 *
 * Never constructs a VisibleFact / VisibleOp (projection chokepoint).
 * No combat resolution — no hit/damage adjudication.
 */
function viewNarrativeCombatPanel(state: Signal<Model>, send: Send<Msg>): Mountable {
  // `state` is the top-level Model signal; signal-runtime constraint: do NOT
  // call .peek() in a reactive slot. All reactive reads go through .map/.at.
  return div(
    {
      class: 'narrative-combat-panel',
      role: 'region',
      'aria-label': t('narrative_panel.heading'),
      'data-testid': 'narrative-combat-panel',
    },
    [
      h2({ class: 'narrative-combat-panel__heading' }, [
        text(t('narrative_panel.heading')),
      ]),
      // Active entry: the combatant whose turn it is.
      div({ class: 'narrative-combat-panel__active' }, [
        each(
          state.map((s) => {
            const active = s.initiative.entries[s.initiative.active_index];
            return active ? [active] : [];
          }),
          {
            key: (e) => e.id,
            render: (entrySignal: Signal<InitiativeEntry>) => {
              // Combine state + entry into a single derived signal so we never
              // need .peek() inside reactive slots (peek-in-slot lint constraint).
              const entryState = derived([entrySignal], (e) => e);
              const entryAndState = derived([entrySignal, state], (e, s) => ({ e, s }));
              return [div({ class: 'narrative-combat-panel__entry-section' }, [
                div({ class: 'narrative-combat-panel__entry-header' }, [
                  span(
                    { class: 'narrative-combat-panel__actor-name' },
                    [text(entryState.map((e) => e.display_name))],
                  ),
                  // Affordance 1 — Reaction roll (2d6).
                  uiButton({
                    variant: 'ghost',
                    className: 'narrative-combat-panel__reaction-btn',
                    testid: 'narrative-panel-reaction-btn',
                    ariaLabel: entryState.map((e) =>
                      t('narrative_panel.reaction_roll.label', { name: e.display_name }),
                    ),
                    onClick: () => {
                      const e = entryState.peek();
                      send({ type: 'narrative-panel-reaction-roll', entryId: e.id });
                    },
                    children: [text(t('narrative_panel.reaction_roll'))],
                  }),
                ]),
                // Affordance 2 — Damage assignment.
                div({ class: 'narrative-combat-panel__affordance' }, [
                  show(
                    entryAndState.map(
                      ({ e, s }) =>
                        s.narrativeCombatDamageInput !== null &&
                        s.narrativeCombatDamageInput.entryId === e.id,
                    ),
                    () => [
                      div({ class: 'narrative-combat-panel__inline-form' }, [
                        label(
                          { for: 'narrative-damage-input', class: 'narrative-combat-panel__label' },
                          [text(t('narrative_panel.damage.input_label'))],
                        ),
                        input({
                          id: 'narrative-damage-input',
                          type: 'number',
                          min: '1',
                          class: 'narrative-combat-panel__input',
                          placeholder: t('narrative_panel.damage.input_placeholder'),
                          value: state
                            .at('narrativeCombatDamageInput')
                            .map((d) => d?.amount ?? ''),
                          onInput: (ev) => {
                            const val = (ev.target as HTMLInputElement).value;
                            send({ type: 'narrative-panel-damage-change', amount: val });
                          },
                          'aria-label': t('narrative_panel.damage.input_label'),
                        }),
                        uiButton({
                          variant: 'primary',
                          className: 'narrative-combat-panel__apply-btn',
                          testid: 'narrative-panel-damage-apply',
                          ariaLabel: entryState.map((e) =>
                            t('narrative_panel.damage.draft_label', { name: e.display_name }),
                          ),
                          onClick: () => send({ type: 'narrative-panel-damage-apply' }),
                          children: [text(t('narrative_panel.damage.apply'))],
                        }),
                        uiButton({
                          variant: 'ghost',
                          className: 'narrative-combat-panel__cancel-btn',
                          testid: 'narrative-panel-damage-cancel',
                          ariaLabel: t('combat_log.cancel'),
                          onClick: () => send({ type: 'narrative-panel-damage-cancel' }),
                          children: [text(t('combat_log.cancel'))],
                        }),
                      ]),
                    ],
                  ),
                  show(
                    entryAndState.map(
                      ({ e, s }) =>
                        s.narrativeCombatDamageInput === null ||
                        s.narrativeCombatDamageInput.entryId !== e.id,
                    ),
                    () => [
                      uiButton({
                        variant: 'ghost',
                        className: 'narrative-combat-panel__damage-btn',
                        testid: 'narrative-panel-damage-open',
                        ariaLabel: entryState.map((e) =>
                          t('narrative_panel.damage.label', { name: e.display_name }),
                        ),
                        onClick: () => {
                          const e = entryState.peek();
                          send({ type: 'narrative-panel-damage-open', entryId: e.id });
                        },
                        children: [text(t('narrative_panel.damage.label', { name: '' }))],
                      }),
                    ],
                  ),
                ]),
                // Affordance 3 — Apply/remove condition.
                div({ class: 'narrative-combat-panel__affordance narrative-combat-panel__conditions' }, [
                  span({ class: 'narrative-combat-panel__label' }, [
                    text(entryState.map((e) =>
                      t('narrative_panel.condition.label', { name: e.display_name }),
                    )),
                  ]),
                  ...(
                    [
                      ['prone', 'narrative_panel.condition.prone'],
                      ['grappled', 'narrative_panel.condition.grappled'],
                      ['frightened', 'narrative_panel.condition.frightened'],
                      ['poisoned', 'narrative_panel.condition.poisoned'],
                      ['bleeding', 'narrative_panel.condition.bleeding'],
                      ['staggered', 'narrative_panel.condition.staggered'],
                    ] as [string, string][]
                  ).map(([cond, key]) =>
                    uiButton({
                      variant: 'ghost',
                      className: 'narrative-combat-panel__cond-btn',
                      testid: `narrative-panel-cond-${cond}`,
                      ariaLabel: t(key as Parameters<typeof t>[0]),
                      onClick: () => {
                        const e = entryState.peek();
                        send({ type: 'narrative-panel-condition-toggle', entryId: e.id, condition: cond });
                      },
                      children: [text(t(key as Parameters<typeof t>[0]))],
                    })
                  ),
                ]),
                // Affordance 4 — NPC turn quick-pick.
                div({ class: 'narrative-combat-panel__affordance narrative-combat-panel__npc-actions' }, [
                  span({ class: 'narrative-combat-panel__label' }, [
                    text(entryState.map((e) =>
                      t('narrative_panel.npc_action.label', { name: e.display_name }),
                    )),
                  ]),
                  ...(
                    [
                      ['attack', 'narrative_panel.npc_action.attack'],
                      ['move', 'narrative_panel.npc_action.move'],
                      ['use_ability', 'narrative_panel.npc_action.use_ability'],
                      ['flee', 'narrative_panel.npc_action.flee'],
                      ['yield', 'narrative_panel.npc_action.yield'],
                    ] as [NpcAction, string][]
                  ).map(([action, key]) =>
                    uiButton({
                      variant: 'ghost',
                      className: 'narrative-combat-panel__npc-btn',
                      testid: `narrative-panel-npc-${action}`,
                      ariaLabel: t(key as Parameters<typeof t>[0]),
                      onClick: () => {
                        const e = entryState.peek();
                        send({ type: 'narrative-panel-npc-action', entryId: e.id, action });
                      },
                      children: [text(t(key as Parameters<typeof t>[0]))],
                    })
                  ),
                ]),
                // Affordance 5 — Save/skill check.
                div({ class: 'narrative-combat-panel__affordance' }, [
                  show(
                    entryAndState.map(
                      ({ e, s }) =>
                        s.narrativeCombatSaveInput !== null &&
                        s.narrativeCombatSaveInput.entryId === e.id,
                    ),
                    () => [
                      div({ class: 'narrative-combat-panel__inline-form' }, [
                        label(
                          { for: 'narrative-save-input', class: 'narrative-combat-panel__label' },
                          [text(t('narrative_panel.save.input_label'))],
                        ),
                        input({
                          id: 'narrative-save-input',
                          type: 'text',
                          class: 'narrative-combat-panel__input',
                          placeholder: t('narrative_panel.save.input_placeholder'),
                          value: state
                            .at('narrativeCombatSaveInput')
                            .map((s) => s?.spec ?? ''),
                          onInput: (ev) => {
                            const val = (ev.target as HTMLInputElement).value;
                            send({ type: 'narrative-panel-save-change', spec: val });
                          },
                          'aria-label': t('narrative_panel.save.input_label'),
                        }),
                        uiButton({
                          variant: 'primary',
                          className: 'narrative-combat-panel__roll-btn',
                          testid: 'narrative-panel-save-roll',
                          ariaLabel: t('narrative_panel.save.roll'),
                          onClick: () => send({ type: 'narrative-panel-save-roll' }),
                          children: [text(t('narrative_panel.save.roll'))],
                        }),
                        uiButton({
                          variant: 'ghost',
                          className: 'narrative-combat-panel__cancel-btn',
                          testid: 'narrative-panel-save-cancel',
                          ariaLabel: t('narrative_panel.save.cancel'),
                          onClick: () => send({ type: 'narrative-panel-save-cancel' }),
                          children: [text(t('narrative_panel.save.cancel'))],
                        }),
                      ]),
                    ],
                  ),
                  show(
                    entryAndState.map(
                      ({ e, s }) =>
                        s.narrativeCombatSaveInput === null ||
                        s.narrativeCombatSaveInput.entryId !== e.id,
                    ),
                    () => [
                      uiButton({
                        variant: 'ghost',
                        className: 'narrative-combat-panel__save-open-btn',
                        testid: 'narrative-panel-save-open',
                        ariaLabel: entryState.map((e) =>
                          t('narrative_panel.save.label', { name: e.display_name }),
                        ),
                        onClick: () => {
                          const e = entryState.peek();
                          send({ type: 'narrative-panel-save-open', entryId: e.id });
                        },
                        children: [text(t('narrative_panel.save.label', { name: '' }))],
                      }),
                    ],
                  ),
                ]),
                // Affordance 6 — End-turn-with-effect.
                div({ class: 'narrative-combat-panel__affordance' }, [
                  show(
                    state.at('narrativeCombatEffectInput').map((v) => v !== null),
                    () => [
                      div({ class: 'narrative-combat-panel__inline-form' }, [
                        label(
                          { for: 'narrative-effect-input', class: 'narrative-combat-panel__label' },
                          [text(t('narrative_panel.effect.input_label'))],
                        ),
                        input({
                          id: 'narrative-effect-input',
                          type: 'text',
                          class: 'narrative-combat-panel__input',
                          placeholder: t('narrative_panel.effect.input_placeholder'),
                          value: state
                            .at('narrativeCombatEffectInput')
                            .map((v) => v ?? ''),
                          onInput: (ev) => {
                            const val = (ev.target as HTMLInputElement).value;
                            send({ type: 'narrative-panel-effect-change', value: val });
                          },
                          'aria-label': t('narrative_panel.effect.input_label'),
                        }),
                        uiButton({
                          variant: 'primary',
                          className: 'narrative-combat-panel__effect-submit-btn',
                          testid: 'narrative-panel-effect-submit',
                          ariaLabel: t('narrative_panel.effect.submit'),
                          onClick: () => {
                            const e = entryState.peek();
                            send({ type: 'narrative-panel-effect-submit', entryId: e.id });
                          },
                          children: [text(t('narrative_panel.effect.submit'))],
                        }),
                        uiButton({
                          variant: 'ghost',
                          className: 'narrative-combat-panel__cancel-btn',
                          testid: 'narrative-panel-effect-cancel',
                          ariaLabel: t('narrative_panel.effect.cancel'),
                          onClick: () => send({ type: 'narrative-panel-effect-cancel' }),
                          children: [text(t('narrative_panel.effect.cancel'))],
                        }),
                      ]),
                    ],
                  ),
                  show(
                    state.at('narrativeCombatEffectInput').map((v) => v === null),
                    () => [
                      uiButton({
                        variant: 'ghost',
                        className: 'narrative-combat-panel__effect-open-btn',
                        testid: 'narrative-panel-effect-open',
                        ariaLabel: entryState.map((e) =>
                          t('narrative_panel.effect.label', { name: e.display_name }),
                        ),
                        onClick: () => send({ type: 'narrative-panel-effect-open' }),
                        children: [text(t('narrative_panel.effect.label', { name: '' }))],
                      }),
                    ],
                  ),
                ]),
              ])];
            },
          },
        ),
      ]),
      // Turn log — ephemeral, per-encounter, collapsible.
      div(
        {
          class: 'narrative-combat-panel__turn-log',
          'aria-label': t('narrative_panel.turn_log.heading'),
        },
        [
          h3({ class: 'narrative-combat-panel__subheading' }, [
            text(t('narrative_panel.turn_log.heading')),
          ]),
          show(
            state.at('narrativeCombatTurnLog').map((log) => log.entries.length === 0),
            () => [
              p({ class: 'narrative-combat-panel__empty' }, [
                text(t('narrative_panel.turn_log.empty')),
              ]),
            ],
          ),
          show(
            state.at('narrativeCombatTurnLog').map((log) => log.entries.length > 0),
            () => [
              each(
                state.at('narrativeCombatTurnLog').at('entries'),
                {
                  key: (e) => e.id,
                  render: (entrySignal: Signal<TurnLogEntry>) => {
                    const e = derived([entrySignal], (x) => x);
                    // Current entity id for the draft-narration action.
                    const currentEntityId = state.map((s) =>
                      s.route.kind === 'entity' ? s.route.id : '',
                    );
                    return [div({ class: 'narrative-combat-panel__log-entry' }, [
                      span({ class: 'narrative-combat-panel__log-actor' }, [
                        text(e.map((x) => x.actorName)),
                      ]),
                      span({ class: 'narrative-combat-panel__log-desc' }, [
                        text(e.map((x) => x.description)),
                      ]),
                      show(
                        currentEntityId.map((id) => !!id),
                        () => [
                          uiButton({
                            variant: 'ghost',
                            className: 'narrative-combat-panel__draft-btn',
                            testid: 'narrative-panel-draft-btn',
                            ariaLabel: t('narrative_panel.draft_narration.label'),
                            onClick: () => {
                              const entry = e.peek();
                              const entityId = currentEntityId.peek();
                              if (entityId) {
                                send({
                                  type: 'narrative-panel-draft-narration',
                                  entry,
                                  entityId,
                                });
                              }
                            },
                            children: [text(t('narrative_panel.draft_narration'))],
                          }),
                        ],
                      ),
                    ])];
                  },
                },
              ),
            ],
          ),
        ],
      ),
    ],
  );
}

// ---------- recurrence (virtual instances) Overview section (#236) ----------

/**
 * One synthesized recurrence firing, rendered as an Overview row. A virtual
 * instance (`06-timeline.md` §10.3) has NO entity id — it is a computed
 * record — so the row key is a deterministic synthetic handle (the parent
 * `recurs_as` fact key + the structural occurrence index), NOT an entity id.
 * Materialization into a real Event is B4 (#237): each row carries the
 * {@link MaterializePayload} that "Materialize" copies onto a new `event`.
 */
interface RecurrenceRow {
  /** Deterministic synthetic key (factKey + occurrence index). Not an id. */
  readonly key: string;
  /** Formatted firing date (display calendar), or a range "from – to". */
  readonly label: string;
  /** Inherited `at_location` resolved to an entity name, or null. */
  readonly location: string | null;
  /** B4 (#237) — the rule defaults this firing would materialize into. */
  readonly materialize: MaterializePayload;
}

/**
 * How many structural years forward of the cursor the Overview lists virtual
 * instances for. The list is bounded by the canonical-day range derived from
 * this window, and `virtualInstances` walks O(occurrences) — so a long-lived
 * festival never explodes. A small backward margin keeps the most recent past
 * firing visible alongside the upcoming ones.
 */
const RECURRENCE_FORWARD_YEARS = 10;
/** Hard cap on rendered firings (defensive against a degenerate sub-day rule). */
const RECURRENCE_ROW_CAP = 200;

/** Structural (leap-unaware) days in one year of the active calendar. */
function calendarYearDays(calendar: Calendar): number {
  const months = calendar.cycles.year.months;
  const total = months.reduce((sum, m) => sum + m.length, 0);
  return total > 0 ? total : 365;
}

/** The effective cursor day the Overview centres the recurrence window on. */
function effectiveCursorDay(s: Model): number {
  return s.browseCursor ?? s.campaignCursor ?? 0;
}

/**
 * 226.1 (#245) — the projection `t` for the Preview-as view: the EPHEMERAL
 * browse cursor when diverged, else the session "now". `null` (no cursor set)
 * means a timeless read — exactly what the chokepoint's `t === null` resolves
 * to (no validity filter), matching the server's `project(viewer, null, …)`.
 */
function previewCursorDay(s: Model): number | null {
  return s.browseCursor ?? s.campaignCursor ?? null;
}

/** 226.1 (#245) — a GM Viewer selection collapses Preview-as back to GM view. */
function isGmViewer(viewer: ViewerId): boolean {
  return viewer.kind === 'gm';
}

/**
 * 226.1 (#245) — the effect(s) needed to (re)compute the Preview-as projection
 * for the model's current preview viewer. Empty unless a non-GM viewer is
 * selected — the GM identity view reads canon directly and needs no projection.
 * Centralised so every `t`-affecting reducer arm (cursor / browse / op-log
 * rebuild) refreshes the preview through one path.
 */
function previewRefreshEffects(model: Model): Effect[] {
  const viewer = model.previewViewer;
  if (viewer === null || isGmViewer(viewer)) return [];
  return [{ type: 'compute-preview-projection', viewer, day: previewCursorDay(model) }];
}

/**
 * #258 — the EFFECTIVE designated Viewer for the Table View: the GM's override
 * when valid, else the default (party Viewer, practically the first campaign
 * Viewer). `null` when the campaign has no selectable Viewer (the table renders
 * its placeholder; it NEVER falls back to GM-truth). Pure — delegates to the
 * unit-tested `resolveDesignatedViewer`.
 */
function effectiveDesignatedViewer(s: Model): ViewerId | null {
  return resolveDesignatedViewer(s.designatedViewerOverride, s.entities);
}

/** #258 — true when the Table View focus is pinned to THIS entity. */
function isEntityPinnedToTable(s: Model, entityId: string): boolean {
  return s.tableFocus.kind === 'entity' && s.tableFocus.entity_id === entityId;
}

/** #258 — human label for a campaign Viewer (its `name` fact, else its id). */
function designatedViewerLabel(s: Model, viewer: ViewerId | null): string {
  if (viewer === null) return '';
  const key = viewerKeyFn(viewer);
  for (const opt of campaignViewers(s.entities)) {
    if (viewerKeyFn(opt.viewer) === key) return opt.label;
  }
  return viewer.id;
}

/**
 * #258 — the effect(s) to (re)broadcast the Table View state. Only the GM main
 * tab broadcasts (`session.mode === 'gm'`); a player / table tab never owns the
 * replica. Centralised so every `t`- or op-log-affecting reducer arm refreshes
 * the shared display through one path (mirrors `previewRefreshEffects`).
 */
function tableBroadcastEffects(model: Model): Effect[] {
  if (model.session.mode !== 'gm') return [];
  const designatedViewer = effectiveDesignatedViewer(model);
  const effect: BroadcastTableEffect = {
    type: 'broadcast-table',
    focus: model.tableFocus,
    designatedViewer,
    viewerLabel: designatedViewerLabel(model, designatedViewer),
    day: previewCursorDay(model),
    assetUrls: model.assetUrls,
  };
  // #271 — carry current attention signals in the full broadcast too, so a
  // Table tab that opens while a pointer/ripple is active gets the current state.
  if (model.tablePointer !== null) effect.attentionPointer = model.tablePointer;
  if (model.tableRipple !== null) effect.attentionRipple = model.tableRipple;
  return [effect];
}

/**
 * #271 — fast-path broadcast: post only the current attention signals without
 * re-reading the op log. Used by pointer-move (high frequency) and ripple
 * actions. The `broadcast-attention` effect handler posts a `table-attention`
 * message that the Table tab merges into its current broadcast without touching
 * the projected rows.
 */
function attentionBroadcastEffects(model: Model): Effect[] {
  if (model.session.mode !== 'gm') return [];
  return [
    {
      type: 'broadcast-attention',
      attentionPointer: model.tablePointer,
      attentionRipple: model.tableRipple,
    },
  ];
}

/** Format a single virtual-instance firing into a display-date label. */
function formatVirtualInstance(calendar: Calendar, occ: VirtualInstance['occurred_at']): string {
  const fmt = (day: number): string => {
    const r = toDisplayDate(calendar, day);
    return r.ok ? formatDisplayDate(calendar, r.value) : String(day);
  };
  if (occ.kind === 'point') return fmt(occ.day);
  const from = fmt(occ.from.day);
  const to = fmt(occ.to.day);
  return from === to ? from : `${from} – ${to}`;
}

/**
 * The virtual instances of every `recurs_as` rule on `entityId`, across the
 * Overview's visible canonical-day window. Reads the entity's canon
 * `recurs_as` facts, validates each value as a `RecurrenceRule`
 * (`RecurrenceRuleSchema`), and synthesizes firings via the `@dl/calendar`
 * engine. Pure projection — no entity is created (materialization is #237).
 */
export function entityRecurrenceRows(s: Model, entityId: string): RecurrenceRow[] {
  const entity = s.entities[entityId];
  if (!entity || entity.soft_deleted) return [];
  const recursAs = OFFICIAL_CORE_PREDICATE_IDS.recurs_as;
  const calendar = activeCalendar(s);
  const yearDays = calendarYearDays(calendar);
  const cursor = effectiveCursorDay(s);
  const fromDay = cursor - yearDays;
  const toDay = cursor + RECURRENCE_FORWARD_YEARS * yearDays;

  const loreName = entityNameOrId(s, entityId);
  const rows: RecurrenceRow[] = [];
  for (const [factKeyStr, fact] of Object.entries(entity.facts)) {
    if (!fact || fact.soft_deleted || fact.owner.kind !== 'canon') continue;
    if (splitFactKey(factKeyStr).predicate !== recursAs) continue;
    const raw = fact.versions[fact.versions.length - 1]?.value;
    const parsed = RecurrenceRuleSchema.safeParse(raw);
    if (!parsed.success) continue;
    const rule: RecurrenceRule = parsed.data;
    for (const inst of virtualInstances(calendar, rule, fromDay, toDay)) {
      if (rows.length >= RECURRENCE_ROW_CAP) return rows;
      const location =
        inst.at_location !== undefined ? entityNameOrId(s, inst.at_location) : null;
      const dateLabel = formatVirtualInstance(calendar, inst.occurred_at);
      rows.push({
        key: `${factKeyStr}#${String(inst.occurrence_index)}`,
        label: dateLabel,
        location,
        // B4 (#237) — the canon defaults this firing materializes into. The
        // default name marries the parent Lore name to the firing date
        // ("Yule — 5 Hammer, 1065") so the GM lands on a recognisable event.
        materialize: {
          loreId: entityId,
          name: `${loreName} — ${dateLabel}`,
          occurredAt: occurredAtValue(inst.occurred_at),
          ...(inst.at_location !== undefined ? { location: inst.at_location } : {}),
          participants: inst.participants ?? [],
        },
      });
    }
  }
  return rows;
}

function entityHasRecurrences(s: Model, entityId: string): boolean {
  return entityRecurrenceRows(s, entityId).length > 0;
}

/**
 * Shared infobox renderer for the Overview (forward facts) and
 * Connections (backlinks) tabs. `rowsFn` supplies the rows; each row's
 * value is either plain text or one navigable link per ref target
 * (nested each, re-derived by row index via the same `rowsFn`).
 */
function viewInfoboxRows(
  state: Signal<Model>,
  send: Send<Msg>,
  entityId: string,
  mode: 'overview' | 'connections',
): Renderable {
  // Returns the each rows (caller supplies the wrapping div + a LITERAL
  // data-testid — a param in a static attribute would force a file-wide
  // full-mask). The compiler needs RESOLVABLE callees in the `items`
  // accessors (a function parameter is opaque), so we branch on the
  // `mode` const to call the concrete row function inline.
  return [each(state.map((s) => mode === 'connections' ? entityBacklinks(s, entityId) : entityOverviewRows(s, entityId)), {
        key: (row) => row.predicate,
        render: (item, index) => [
        div({ class: 'entity-overview-row' }, [
          span({ class: 'entity-overview-label' }, [text(item.at('label'))]),
          span(
            {
              class: 'entity-overview-value',
              // `rich` is distinct from `plain` so the always-present prose
              // span is hidden on non-prose rows — otherwise its empty,
              // full-width markdown wrapper claimed a flex line and pushed
              // ref / multi-value values down ("strange spacing before").
              'data-kind': item.map((r) => r.assetUrl !== null ? 'asset' : r.html !== null ? 'rich' : r.links.length > 0 ? 'ref' : 'plain',),
            },
            [
              img({
                class: 'entity-overview-image',
                alt: item.at('label'),
                src: item.map((r) => r.assetUrl ?? ''),
              }),
              span({ class: 'entity-overview-plain' }, [text(item.map((r) => r.plain ?? ''))]),
              span(
                { class: 'entity-overview-rich', 'data-testid': 'overview-rich' },
                [markdown(item.map((r) => r.html ?? ''))],
              ),
              span(
                { class: 'entity-overview-links' },
                [each(derived([state, index], (s, idx) => (mode === 'connections'
                      ? entityBacklinks(s, entityId)
                      : entityOverviewRows(s, entityId))[idx]?.links ?? []), {
        key: (lk) => lk.refTo ?? lk.text,
        render: (lk) => [
                    a(
                      {
                        class: 'entity-overview-link',
                        'data-link': lk.map((l) => (l.refTo ? 'true' : 'false')),
                        href: lk.map((l) => l.refTo ? appRouteHref({ kind: 'entity', id: l.refTo }) : '#',),
                        onClick: (event: MouseEvent) => {
                          event.preventDefault();
                          const target = lk.at('refTo').peek();
                          if (typeof target === 'string') {
                            send(navMsg({ kind: 'entity', id: target }));
                          }
                        },
                      },
                      [text(lk.at('text'))],
                    ),
                  ],
      })],
              ),
              span(
                {
                  class: 'entity-overview-status',
                  'data-status': item.map((r) => r.status ?? ''),
                },
                [text(item.map((r) => r.status ?? ''))],
              ),
              // Inline draft actions (#14): when a row's fact is a draft, the
              // GM can ratify / reject it right here in the Overview without
              // opening the Draft Inbox. Hidden by CSS unless `data-status`
              // is `draft`; reuses the inbox's ratify-draft / reject-draft
              // Msgs. preventDefault keeps the row from navigating.
              span(
                {
                  class: 'entity-overview-draft-actions',
                  'data-status': item.map((r) => r.status ?? ''),
                },
                [
                  uiButton({
                    variant: 'primary',
                    className: 'entity-overview-ratify',
                    testid: 'overview-ratify',
                    ariaLabel: t('inbox.ratify'),
                    attrs: { title: t('inbox.ratify') },
                    onClick: (event: MouseEvent) => {
                      event.preventDefault();
                      const predicate = item.at('predicate').peek();
                      if (typeof predicate === 'string') {
                        send({ type: 'ratify-draft', entityId, predicate });
                      }
                    },
                    children: [text(t('inbox.ratify'))],
                  }),
                  uiButton({
                    variant: 'danger',
                    className: 'entity-overview-reject',
                    testid: 'overview-reject',
                    ariaLabel: t('inbox.reject'),
                    attrs: { title: t('inbox.reject') },
                    onClick: (event: MouseEvent) => {
                      event.preventDefault();
                      const predicate = item.at('predicate').peek();
                      if (typeof predicate === 'string') {
                        send({ type: 'reject-draft', entityId, predicate });
                      }
                    },
                    children: [text(t('inbox.reject'))],
                  }),
                ],
              ),
            ],
          ),
        ]),
      ],
      })];
}

/**
 * Returns true while the entity-page title is in its loading state
 * (the substrate hasn't folded the entity yet). The CSS uses this
 * via `data-loading` to render a subtle skeleton placeholder so the
 * h1 doesn't visually pop into existence when the fold lands.
 */
function entityTitleLoading(s: Model, entityId: string): boolean {
  return s.entities[entityId] === undefined;
}

type EntityPageTab = 'overview' | 'facts' | 'reveals' | 'issues' | 'history' | 'connections';

/**
 * Composite remount key for the entity-page tabs strip — encodes the active
 * tab plus the live counts so the keyed `each` in `viewEntityPage` remounts
 * the (static-value) strip whenever any of them change. Bare-Identifier
 * helper so the cross-file reactive walker registers the `s.*` reads.
 */
function entityTabsStripKey(s: Model, entityId: string): string {
  return `${s.entityPageTab}|${entityFactCountOf(s, entityId)}|${entityIssueCount(s, entityId)}`;
}

/**
 * Entity-page tabs strip (static-value `tabs` atom). The active tab + counts
 * are passed as literals by the keyed `each` in `viewEntityPage` (which
 * remounts on `entityTabsStripKey` change), so each remount carries the
 * correct `active` flags + count badges without a render-body `.peek()`.
 *
 * #146 — Facts always shows its count (mock "Facts 31"); Issues only when
 * there are advisories (a "0" badge there would just duplicate the header).
 */
function entityPageTabsStrip(
  send: Send<Msg>,
  active: EntityPageTab,
  factCount: number,
  issueCount: number,
): Mountable {
  return tabs({
    ariaLabel: t('entity.tab.list_label'),
    testid: 'entity-page-tabs',
    idBase: 'entity-page',
    className: 'entity-page-tabs',
    onActivate: (value) =>
      send({
        type: 'set-entity-page-tab',
        value: value as EntityPageTab,
      } as Msg),
    items: [
      { value: 'overview', label: t('entity.tab.overview'), active: active === 'overview' },
      { value: 'facts', label: t('entity.tab.facts'), active: active === 'facts', count: factCount },
      { value: 'reveals', label: t('entity.tab.reveals'), active: active === 'reveals' },
      {
        value: 'issues',
        label: t('entity.tab.issues'),
        active: active === 'issues',
        ...(issueCount > 0 ? { count: issueCount } : {}),
      },
      { value: 'history', label: t('entity.tab.history'), active: active === 'history' },
    ],
  });
}

function viewEntityPage(state: Signal<Model>, send: Send<Msg>, entityId: string): Mountable {
  return main({ role: 'main', 'data-testid': 'entity-page', class: 'entity-page' }, [
    // #141 — back-link copy is root-agnostic. The multi-root list (#132)
    // surfaces every root in its own accordion section; the previous
    // "← Locations" copy made every entity page (Character, Faction,
    // Item, …) feel like a Locations affordance.
    appRouteLink(
      send,
      { kind: 'list' },
      { 'data-testid': 'back-link' },
      [text(`← ${t('entity.back')}`)],
      navMsg,
    ),
    // Entity display title (per 30-design-system.md §7.2 "story-first
    // Overview"). Reads from the `name` predicate via displayName()
    // so the convention matches the multi-root list. The fact-row
    // editor below still renders the editable name row — duplication
    // is intentional: the title is read-first chrome, the fact row is
    // the management surface. Entities without a name fall back to
    // their id (same rule as the list).
    // #146 — entity header to mock fidelity (mocks/entity-page.html).
    // A flex header: a large root icon, the title + meta column, and an
    // action cluster. The icon/meta read the entity root via bare-
    // Identifier helpers so the cross-file reactive walker tracks them.
    div({ class: 'entity-header', 'data-testid': 'entity-header' }, [
      div(
        {
          class: 'entity-icon-lg',
          // Per-root tint (paired with the icon, not colour-only) — reuses
          // the per-root token map. Empty during the boot fold so the box
          // renders a neutral placeholder, not a flash.
          'data-root': state.map((s) => entityRootOf(s, entityId) ?? ''),
          'aria-hidden': 'true',
        },
        // §5 — the real Lucide per-root icon (replaces the geometric glyph).
        // Keyed by the root so it remounts the correct mark; the empty arm
        // (boot fold) renders no icon so the tile stays a clean placeholder.
        [
          branch(state.map((s) => entityRootOf(s, entityId) ?? ''), {
            '': () => [],
            location: () => [entityIcon('location', { size: 26 })],
            character: () => [entityIcon('character', { size: 26 })],
            faction: () => [entityIcon('faction', { size: 26 })],
            event: () => [entityIcon('event', { size: 26 })],
            item: () => [entityIcon('item', { size: 26 })],
            lore: () => [entityIcon('lore', { size: 26 })],
          }),
        ],
      ),
      div({ class: 'entity-header-text' }, [
        h1(
          {
            class: 'entity-display-title',
            'data-testid': 'entity-display-title',
            // CSS reads data-loading to render a skeleton placeholder so
            // the title doesn't flash the entity id before the substrate
            // fold lands. Reactive helpers are bare-Identifier same-
            // module functions per CLAUDE.md.
            'data-loading': state.map((s) => (entityTitleLoading(s, entityId) ? 'true' : 'false')),
          },
          [text(state.map((s) => entityNameOrId(s, entityId)))],
        ),
        // Meta row — root label · live fact count (mock: "Location · …").
        // Hidden during the boot fold (mirrors the title skeleton) so it
        // doesn't briefly read "· 0 facts" with an empty root label.
        div(
          {
            class: 'entity-meta',
            'data-testid': 'entity-meta',
            'data-loading': state.map((s) => (entityTitleLoading(s, entityId) ? 'true' : 'false')),
          },
          [
            span(
              {
                class: 'entity-meta-root',
                'data-root': state.map((s) => entityRootOf(s, entityId) ?? ''),
              },
              [text(state.map((s) => entityRootLabelOf(s, entityId)))],
            ),
            span({ class: 'entity-meta-sep', 'aria-hidden': 'true' }, [text('·')]),
            span({ class: 'entity-meta-facts' }, [
              text(
                state.map((s) =>
                  t('entity.meta.fact_count', { n: entityFactCountOf(s, entityId) }),
                ),
              ),
            ]),
          ],
        ),
      ]),
      // Action cluster — auditor badge + AI-draft entry point. Both are
      // GM-only and conditional; the cluster collapses to empty otherwise.
      div({ class: 'entity-header-actions' }, [
        // #258 — Pin-to-Table affordance (GM-only, not while previewing). Pins
        // THIS entity to the shared Table View (reveal-filtered to the
        // designated Viewer), or un-pins it when it is already the focus.
        show(
          state.map((s) => s.session.mode === 'gm' && s.previewViewer === null),
          () => [
            uiButton({
              variant: 'ghost',
              className: 'entity-pin-to-table',
              testid: 'entity-pin-to-table',
              ariaLabel: t('table_view.pin_aria'),
              onClick: () =>
                send({
                  type: 'table/focus',
                  // `state.peek()` is valid in an event handler (not a reactive
                  // slot) — toggle pin/un-pin based on the live focus.
                  action: isEntityPinnedToTable(state.peek(), entityId)
                    ? { kind: 'unpin' }
                    : { kind: 'pin-entity', entity_id: entityId },
                }),
              children: [
                text(
                  state.map((s) =>
                    t(isEntityPinnedToTable(s, entityId) ? 'table_view.unpin' : 'table_view.pin'),
                  ),
                ),
              ],
            }),
          ],
        ),
        // Per-entity auditor cue (Phase 2 / §5). GM-only, reactive: a chip
        // showing the live advisory count for THIS entity, jumping to the
        // Issues tab. Count paired with a label + icon, not colour alone.
        show(
          // Hidden while previewing (#245): authoring/inspection chrome is GM-truth.
          state.map(
            (s) => s.session.mode === 'gm' && s.previewViewer === null && entityIssueCount(s, entityId) > 0,
          ),
          () => [
            uiButton({
              variant: 'ghost',
              className: 'entity-issues-badge',
              testid: 'entity-issues-badge',
              ariaLabel: t('entity.issues.badge_label'),
              onClick: () => send({ type: 'set-entity-page-tab', value: 'issues' }),
              children: [
                span({ class: 'entity-issues-badge-icon', 'aria-hidden': 'true' }, [text('⚠')]),
                text(
                  state.map((s) => t('entity.issues.badge', { n: entityIssueCount(s, entityId) })),
                ),
              ],
            }),
          ],
        ),
        // Phase C — entity-level "✨ Draft with AI" (prompt → several facts).
        // GM-only; shown only when the entity has empty prose fields to fill.
        show(
          state.map((s) =>
            s.session.mode === 'gm' && s.previewViewer === null && emptyProsePredicates(s, entityId).length > 0),
          () => [
            uiButton({
              variant: 'secondary',
              className: 'entity-page-ai-draft',
              testid: 'entity-ai-draft',
              onClick: () => send({ type: 'open-page-draft', entityId }),
              children: [text(t('entity.page.ai_draft'))],
            }),
          ],
        ),
        // #251 — entity-context bulk generation ("Populate with AI"). GM-only,
        // hidden while previewing. Fans out FROM this entity into a suggestion
        // set (decision 6); always available (not gated on empty fields — it
        // generates NEW entities + facts, not just fills this one's blanks).
        show(
          state.map((s) => s.session.mode === 'gm' && s.previewViewer === null),
          () => [
            uiButton({
              variant: 'secondary',
              className: 'entity-page-bulk-gen',
              testid: 'entity-bulk-gen',
              onClick: () => send({ type: 'open-bundle-gen', target: { kind: 'entity', entity_id: entityId } }),
              children: [text(t('bundle.entity_action'))],
            }),
          ],
        ),
        // #81 — "Create instance" — GM-only, Lore-only. Materializes a
        // new entity carrying an `instance_of` ref back to this Lore.
        // Same Msg path as the mobile bottom-sheet action; the modal/sheet
        // primitive is CSS-driven (modal on desktop, bottom-sheet on phone).
        show(
          state.map(
            (s) => s.session.mode === 'gm' && s.previewViewer === null && entityRootOf(s, entityId) === 'lore',
          ),
          () => [
            uiButton({
              variant: 'secondary',
              className: 'entity-page-create-instance',
              testid: 'entity-create-instance',
              onClick: () => send({ type: 'open-instance-modal', sourceId: entityId }),
              children: [text(t('instance.action'))],
            }),
          ],
        ),
        // Assign controller (III.B.4) — GM-only, Character-only. Binds a
        // Campaign member (User) to this PC via a `controlled_by` fact; the
        // player then projects as this Character. Button label reflects the
        // current binding.
        show(
          state.map(
            (s) => s.session.mode === 'gm' && s.previewViewer === null && entityRootOf(s, entityId) === 'character',
          ),
          () => [
            uiButton({
              variant: 'secondary',
              className: 'entity-page-controller',
              testid: 'entity-set-controller',
              onClick: () => send({ type: 'open-controller-picker', entityId }),
              children: [text(state.map((s) => controllerButtonLabel(s, entityId)))],
            }),
          ],
        ),
        // Delete (soft, reversible) — GM-only, hidden while previewing (#245).
        // Opens a confirm; the actual `entity.soft_delete` op + navigate-to-list
        // happen on confirm.
        show(
          state.map((s) => s.session.mode === 'gm' && s.previewViewer === null),
          () => [
            uiButton({
              variant: 'ghost',
              className: 'entity-page-delete',
              testid: 'entity-delete',
              ariaLabel: t('entity.delete.action'),
              onClick: () => send({ type: 'request-delete-entity', entityId }),
              children: [text(t('entity.delete.action'))],
            }),
          ],
        ),
      ]),
    ]),
    // Tabs strip (per 30 §7.2 — Overview / Facts / Reveals / History /
    // Connections). Overview is story-first; Facts is the management
    // view. Reveals / History / Connections are placeholders disabled
    // until their features land. Hidden in player mode (the player
    // view has its own surface below).
    //
    // The tabs() atom takes per-item booleans (no cross-file reactive
    // accessors — that pattern triggers the llui compiler's opaque-
    // accessor warning + FULL_MASK fallback). Wrapping in h.scope
    // keyed on entityPageTab re-mounts the strip when the active tab
    // changes; the rebuild is cheap (≤5 buttons) and the savings on
    // the file-wide prefix mask are worth it (per the 31-path cap
    // app.ts already warns about).
    // 226.1 (#245) — Preview-as: while previewing AS a non-GM Viewer, the GM
    // management surface (tabs + editable facts + reveal chips) is replaced by a
    // read-only panel of EXACTLY that Viewer's projection. This is the GM-view
    // gate (truth + authoring); the preview gate is its complement below.
    show(
      state.map((s) => s.session.mode === 'gm' && s.previewViewer === null),
      () => [
        // A single-row keyed `each` re-mounts the (static-value) tabs strip
        // whenever `entityTabsStripKey` changes — i.e. on tab switch OR when
        // the Facts / Issues counts change. The render body takes a one-shot
        // `model.peek()` snapshot (the sanctioned keyed-remount escape; the
        // peek-in-slot lint keys on the identifier `state`, so a `derived`
        // snapshot whose identifier is not `state` is allowed).
        each(state.map((s) => [entityTabsStripKey(s, entityId)]), {
          key: (k) => k,
          render: () => {
            const model = derived([state], (s) => s);
            const s = model.peek();
            return [
              entityPageTabsStrip(
                send,
                s.entityPageTab,
                entityFactCountOf(s, entityId),
                entityIssueCount(s, entityId),
              ),
            ];
          },
        }),
        // Overview tab — story-first reading view (per §7.2). Renders
        // the entity's current facts as a read-only infobox (label :
        // value). The richer curated/trait-driven infobox + prose
        // description are a later refinement; this shows REAL data now
        // rather than promising a future feature. Management (editing,
        // reveal, validity) lives in the Facts tab — the link at the
        // bottom routes there.
        show(
      state.map((s) => s.entityPageTab === 'overview'),
      () => [
            div(
              {
                id: 'entity-page-overview-panel',
                role: 'tabpanel',
                'aria-labelledby': 'entity-page-overview-tab',
                tabindex: 0,
                class: 'entity-page-panel entity-page-panel--overview',
                'data-testid': 'entity-page-panel-overview',
              },
              [
                // Infobox — one row per renderable fact (shared renderer).
                show(
      state.map((s) => entityHasOverviewRows(s, entityId)),
      () => [
                    div(
                      { class: 'entity-overview-infobox', 'data-testid': 'entity-overview-infobox' },
                      viewInfoboxRows(state, send, entityId, 'overview'),
                    ),
                  ],
    ),
                // Honest empty state — only when there genuinely are no
                // facts. No promise of an unbuilt feature.
                show(
      state.map((s) => !entityHasOverviewRows(s, entityId)),
      () => [
                    emptyState({
                      message: text(t('entity.tab.overview.empty')),
                    }),
                  ],
    ),
                // Connections (backlinks) — incoming relationships from
                // other entities, folded into the reading view rather than
                // a separate tab. Only shown when something links here.
                show(
      state.map((s) => entityHasBacklinks(s, entityId)),
      () => [
                    div({ class: 'entity-overview-connections' }, [
                      // level 2 (not 3): the entity title is the page h1 and
                      // there is no intervening h2, so an h3 here would skip a
                      // level (axe `heading-order`). The "Connections" rollup is
                      // a top-level section of the overview, so h2 is correct.
                      sectionHeading({
                        title: text(t('entity.overview.connections_heading')),
                        level: 2,
                      }),
                      div(
                        {
                          class: 'entity-overview-infobox',
                          'data-testid': 'entity-connections-infobox',
                        },
                        viewInfoboxRows(state, send, entityId, 'connections'),
                      ),
                    ]),
                  ],
    ),
                // Causal chain (#222 / 06-timeline.md §8) — the run of Events
                // linked by caused_by / precedes that this entity belongs to,
                // in causal order (causes first). A pure projection over the
                // op log (no inverse facts), folded into Overview like
                // Connections. Only shown when the entity is part of a chain.
                show(
      state.map((s) => entityHasCausalChain(s, entityId)),
      () => [
                    div(
                      { class: 'entity-overview-causal-chain', 'data-testid': 'entity-causal-chain' },
                      [
                        sectionHeading({
                          title: text(t('entity.overview.causal_chain_heading')),
                          level: 2,
                        }),
                        p({ class: 'entity-causal-chain-caption' }, [
                          text(t('entity.overview.causal_chain_caption')),
                        ]),
                        ul(
                          {
                            class: 'entity-causal-chain-list',
                            'aria-label': t('entity.overview.causal_chain_heading'),
                          },
                          [
                            each(state.map((s) => causalChainEntries(s, entityId)), {
        key: (e) => e.id,
        render: (item) => [
                              li(
                                {
                                  class: 'entity-causal-chain-step',
                                  'data-testid': 'causal-chain-step',
                                  'data-focal': item.map((e) => (e.focal ? 'true' : 'false')),
                                },
                                [
                                  branch(item.map((e) => (e.focal ? 'focal' : 'link')), {
                                    focal: () => [
                                      span(
                                        {
                                          class: 'entity-causal-chain-current',
                                          'aria-current': 'true',
                                        },
                                        [text(item.at('name'))],
                                      ),
                                    ],
                                    link: () => [
                                      a(
                                        {
                                          class: 'entity-causal-chain-link',
                                          href: item.map((e) =>
                                            appRouteHref({ kind: 'entity', id: e.id }),
                                          ),
                                          onClick: (event: MouseEvent) => {
                                            event.preventDefault();
                                            const id = item.at('id').peek();
                                            if (typeof id === 'string') {
                                              send(navMsg({ kind: 'entity', id }));
                                            }
                                          },
                                        },
                                        [text(item.at('name'))],
                                      ),
                                    ],
                                  }),
                                ],
                              ),
                            ],
      }),
                          ],
                        ),
                        // A loop makes the order only partial — point at the
                        // Auditor, which carries the break-the-loop fix.
                        show(
      state.map((s) => causalChainHasCycle(s, entityId)),
      () => [
                            p(
                              {
                                class: 'entity-causal-chain-cycle-note',
                                'data-testid': 'causal-chain-cycle-note',
                                role: 'note',
                              },
                              [text(t('entity.overview.causal_chain_cycle_note'))],
                            ),
                          ],
    ),
                      ],
                    ),
                  ],
    ),
                // Recurring events (#236 / 06-timeline.md §10.3) — the virtual
                // instances synthesized from this Lore's `recurs_as` rules
                // across the visible range around the cursor. Computed
                // records (no entity id / fact stream / reveal log) until
                // materialized (#237). Only shown when a rule fires here.
                show(
      state.map((s) => entityHasRecurrences(s, entityId)),
      () => [
                    div(
                      {
                        class: 'entity-overview-recurrences',
                        'data-testid': 'entity-recurrences',
                      },
                      [
                        sectionHeading({
                          title: text(t('entity.overview.recurrences_heading')),
                          level: 2,
                        }),
                        p({ class: 'entity-recurrences-caption' }, [
                          text(t('entity.overview.recurrences_caption')),
                        ]),
                        ul(
                          {
                            class: 'entity-recurrences-list',
                            'aria-label': t('entity.overview.recurrences_heading'),
                          },
                          [
                            each(state.map((s) => entityRecurrenceRows(s, entityId)), {
        key: (r) => r.key,
        render: (item) => [
                              li(
                                {
                                  class: 'entity-recurrence-instance',
                                  'data-testid': 'recurrence-instance',
                                },
                                [
                                  span({ class: 'entity-recurrence-date' }, [
                                    text(item.at('label')),
                                  ]),
                                  show(
        item.map((r) => r.location !== null),
        () => [
                                      span({ class: 'entity-recurrence-location' }, [
                                        text(item.map((r) => r.location ?? '')),
                                      ]),
                                    ],
      ),
                                  // B4 (#237) — explicit "convert this instance
                                  // into a real event" (§10.3). Reads the row's
                                  // materialize payload at click (peek is an
                                  // event-handler escape) and dispatches the
                                  // shared materialize action — same scaffold as
                                  // the #81 "Create instance" flow.
                                  uiButton({
                                    variant: 'ghost',
                                    className: 'entity-recurrence-materialize',
                                    testid: 'recurrence-materialize',
                                    onClick: () =>
                                      send({
                                        type: 'materialize-instance',
                                        payload: item.peek().materialize,
                                      }),
                                    children: [text(t('recurrence.materialize'))],
                                  }),
                                ],
                              ),
                            ],
      }),
                          ],
                        ),
                      ],
                    ),
                  ],
    ),
                // Link down into the Facts tab (per §7.2 "Overview links
                // down into the Facts tab").
                uiButton({
                  variant: 'ghost',
                  className: 'entity-overview-manage-link',
                  testid: 'entity-overview-manage-link',
                  onClick: () => send({ type: 'set-entity-page-tab', value: 'facts' }),
                  children: [text(t('entity.overview.manage_link'))],
                }),
              ],
            ),
          ],
    ),
        // Facts tab — management view (the existing trait strip +
        // fact-row editor). #139 — trait strip sits above the editor so
        // the GM can attach / detach traits without leaving the page.
        show(
      state.map((s) => s.entityPageTab === 'facts'),
      () => [
            div(
              {
                id: 'entity-page-facts-panel',
                role: 'tabpanel',
                'aria-labelledby': 'entity-page-facts-tab',
                tabindex: 0,
                class: 'entity-page-panel entity-page-panel--facts',
                'data-testid': 'entity-page-panel-facts',
              },
              [
                p({ class: 'entity-page-facts-hint' }, [
                  text(t('entity.tab.facts.manage_hint')),
                ]),
                viewTraitStrip(state, send, entityId, traitAttachHost),
                ...viewEntityFactRows(state, send, entityId, factRowHost),
                // Belief layer (03 §9) — author NPC beliefs about this entity
                // + list existing ones (attributed, never canon).
                viewBeliefsSection(state, send, entityId),
              ],
            ),
          ],
    ),
        // Reveals tab — per-Viewer rollup of what's been disclosed on this
        // entity (Phase 2 / 30 §7.2/§7.7). Read-only audit; per-fact reveal
        // management stays on the Facts tab (chip → Viewer-Matrix popover).
        show(
      state.map((s) => s.entityPageTab === 'reveals'),
      () => [viewRevealsTab(state, send, entityId)],
    ),
        // Issues tab — this entity's auditor advisories (Phase 2 / §5). Brings
        // the deterministic-consistency surface to where the GM is working.
        show(
      state.map((s) => s.entityPageTab === 'issues'),
      () => [viewEntityIssuesTab(state, send, entityId)],
    ),
        // History tab — whole-entity op-log chronology (#247 / 30 §7.14).
        // GM-truth; reads the folded `EntityState.versions` (same op-log data
        // the per-fact rollback panel reads), flattened newest-first. Rendered
        // only inside this GM-view gate; under Preview-as the whole strip is
        // replaced by the reveal-filtered panel below — so no leak path.
        show(
      state.map((s) => s.entityPageTab === 'history'),
      () => [viewEntityHistoryTab(state, send, entityId)],
    ),
      ],
    ),
    // 226.1 (#245) — Preview-as panel: the complement of the GM-view gate
    // above. Rendered ONLY while the GM is previewing a non-GM Viewer; shows
    // exactly `project(viewer, t)` for this entity, read-only.
    show(
      state.map((s) => s.session.mode === 'gm' && s.previewViewer !== null),
      () => [viewPreviewAsPanel(state, send, entityId)],
    ),
    // IV.B.2 (#78) — GM-only mobile affordances: a "Mark visited"
    // button that appends a `visited` fact targeting another Location,
    // and a notes-sheet open button (mobile mirror of the player notes
    // panel). These are always rendered in the DOM; CSS hides them
    // above 480px (`.mobile-entity-actions { display: none; }` on
    // tablet+). Hidden while previewing (authoring is disabled in preview).
    show(
      state.map((s) => s.session.mode === 'gm' && s.previewViewer === null),
      () => [viewMobileEntityActions(state, send, entityId)],
    ),
    show(
      state.map((s) => s.session.mode === 'player'),
      () => [viewPlayerEntityPage(state, send, entityId)],
    ),
  ]);
}

/**
 * IV.B.2 (#78) — mobile entity-action cluster (GM mode).
 *
 * Visible only on the phone breakpoint (`@media (max-width: 479px)`).
 * The "Mark visited" button opens the visited bottom-sheet which
 * confirms before appending a `visited` fact. Reveal-target picking
 * surfaces from the same sheet host but is dispatched from per-fact
 * "Reveal" buttons inline in `viewFactRow` — Phase 1 ships the
 * scaffolding here; the per-fact button lives next to the validity
 * toggle and is hidden above 480px.
 *
 * Decision (per AC): the reveal affordance is an **explicit "Reveal"
 * button in the fact-row overflow** (no long-press) — long-press has
 * no a11y equivalent and conflicts with the iOS text-selection menu.
 * The button is `min-height: 44px` to meet the touch-target floor.
 */
/**
 * First "other" location entity — used as the implicit target for the
 * mobile-entity-actions "Visited" button (Phase 1: the simplest
 * shape; multi-cardinality picker arrives in Phase 2). Module-level
 * function declaration so the llui cross-file walker resolves the
 * `s.entities` read directly — was a local arrow inside
 * `viewMobileEntityActions` that the analyzer flagged as opaque
 * (closure-captured `entityId` + `Object.values()`).
 */
function visitedTargetForEntity(s: Model, entityId: string): string | null {
  for (const e of Object.values(s.entities)) {
    if (e.root === 'location' && e.id !== entityId && !e.soft_deleted) return e.id;
  }
  return null;
}

function viewMobileEntityActions(state: Signal<Model>, send: Send<Msg>, entityId: string): Mountable {
  return div({ class: 'mobile-entity-actions', 'data-testid': 'mobile-entity-actions' }, [
    uiButton({
        variant: 'secondary',
        className: 'mobile-action-button',
        testid: 'mobile-visited-button',
        disabled: state.map((s) => visitedTargetForEntity(s, entityId) === null),
        onClick: () => {
          // Capture the target lazily — `send` is a normal dispatch,
          // we read the model only when the user taps. The model
          // accessor here is the same-module `visitedTarget` const, so
          // the compiler resolves the callee statically.
          // We can't sample model from inside the button handler without
          // a passed-in accessor; the typical TEA pattern is to wire a
          // Msg that figures out the target server-side. Phase 1: send
          // a sentinel Msg and resolve the target in the update
          // handler. To keep this slice tight we resolve at click time
          // via the global appSend + a temporary model snapshot.
          if (!appSend) return;
          // The cleanest path: dispatch open-entity-sheet-visited with
          // the resolved target. We rely on the snapshot-on-click
          // pattern, which mirrors what the cursor-advance button does.
          const target = currentTargetForVisited(entityId);
          if (target === null) return;
          send({ type: 'open-entity-sheet-visited', subjectId: entityId, targetId: target });
        },
        children: [text(t('mobile.entity.visited_action'))],
    }),
    uiButton({
        variant: 'secondary',
        className: 'mobile-action-button',
        testid: 'mobile-notes-button',
        onClick: () => send({ type: 'open-notes-sheet', entityId }),
        children: [text(t('mobile.entity.notes_open'))],
    }),
    // #81 — "Create instance" — Lore-only. Opens the instance
    // bottom-sheet (name + root picker → new entity with an `instance_of`
    // ref). Gated reactively so it only shows on Lore entities.
    show(
      state.map((s) => entityRootOf(s, entityId) === 'lore'),
      () => [
        uiButton({
          variant: 'secondary',
          className: 'mobile-action-button',
          testid: 'mobile-create-instance-button',
          onClick: () => send({ type: 'open-instance-modal', sourceId: entityId }),
          children: [text(t('instance.action'))],
        }),
      ],
    ),
  ]);
}

/**
 * Snapshot-style accessor invoked from inside a click handler.
 * `appSend` is captured during the first `onEffect` invocation, and
 * the model state lives behind that dispatcher; this helper digs into
 * the substrate's last-rebuilt entity cache (via the same `replica`
 * we already query elsewhere) to find a sibling location entity. The
 * full target picker is a Phase 2 surface.
 */
function currentTargetForVisited(self: string): string | null {
  // Phase 1: walk the in-memory entity cache attached to `window`.
  // The runtime mirror is updated on every `replica/entities-rebuilt`
  // dispatch via `__DL_ENTITY_SNAPSHOT__`. If the snapshot is missing,
  // return null and disable the action — the button is `disabled`
  // bound to the same `visitedTarget` accessor.
  if (typeof window === 'undefined') return null;
  const snap = (window as unknown as { __DL_ENTITY_SNAPSHOT__?: Record<string, EntityState> })
    .__DL_ENTITY_SNAPSHOT__;
  if (!snap) return null;
  for (const e of Object.values(snap)) {
    if (e.root === 'location' && e.id !== self && !e.soft_deleted) return e.id;
  }
  return null;
}

/**
 * III.B.2 (#66) — Player entity page.
 *
 * Read-only — every projected fact renders as a (predicate, value)
 * chip pair with no edit affordances. Editor=Reader invariant is
 * upheld: this component lives next to the GM `viewFactRow` and the
 * skeleton (heading, back link, layout) is identical; the GM path
 * just adds the input + validity + history surfaces.
 */
function viewPlayerEntityPage(state: Signal<Model>, send: Send<Msg>, entityId: string): Mountable {
  return div({ class: 'player-entity-page', 'data-testid': 'player-entity-page' }, [
    section({ class: 'projected-facts' }, [
      h2([text(t('player.entity_facts'))]),
      show(
      state.map((s) => projectedFactsForEntity(s, entityId).length === 0),
      () => [
          emptyState({
            testid: 'player-entity-empty',
            message: text(t('player.entity_no_facts')),
          }),
        ],
    ),
      ul(
        { class: 'fact-list', 'data-testid': 'player-fact-list' },
        [each(state.map((s) => projectedFactsForEntity(s, entityId).map((f) => ({
              key: factKey(f.subject, f.predicate),
              predicate: f.predicate,
              valueDisplay: formatFactValue(f.value),
              pending: f.pending === true,
            }))), {
        key: (row) => row.key,
        render: (item) => [
            li({ class: 'fact-chip', 'data-testid': 'player-fact-chip' }, [
              span({ class: 'fact-predicate' }, [text(item.at('predicate'))]),
              span({ class: 'fact-value' }, [text(item.at('valueDisplay'))]),
              show(
      item.at('pending'),
      () => [
                  span({ class: 'status-chip', 'data-status': 'pending' }, [
                    text(t('fact.pending_chip')),
                  ]),
                ],
    ),
              span({ class: 'status-chip', 'data-status': 'canon' }, [
                text(t('player.status_canon')),
              ]),
              span({ class: 'validity-chip' }, [text(t('player.validity_timeless'))]),
            ]),
          ],
      })],
      ),
    ]),
    // §7.2 — a player who CONTROLS this PC may edit its identity facts directly
    // (name / appearance / portrait). These write `controlled_by`-gated canon
    // ops; the DO op-accept gate enforces the authority server-side.
    show(
      state.map((s) => s.session.controlledCharacters.includes(entityId)),
      () => [
        section({ class: 'player-identity-editor', 'data-testid': 'player-identity-editor' }, [
          h2([text(t('player.your_character'))]),
          p({ class: 'player-identity-hint' }, [text(t('player.your_character_hint'))]),
          viewPlayerAuthorityFactRows(state, send, entityId, factRowHost),
        ]),
      ],
    ),
    // §7.3 — a player who controls a PC may record a BELIEF about this entity,
    // owned by their PC. Beliefs are subjective (may contradict canon) and need
    // no GM ratification; only the believing PC and the GM see them (the
    // projection chokepoint enforces this — no leak to other players). The
    // recorded value then appears in "What you know" above, overlaying canon for
    // the player. Visible on ANY entity page (theories about NPCs, etc.).
    show(
      state.map((s) => s.session.mode === 'player' && s.session.controlledCharacters.length > 0),
      () => [
        section({ class: 'player-belief-author', 'data-testid': 'player-belief-author' }, [
          h2([text(t('player.belief.heading'))]),
          p({ class: 'player-belief-hint' }, [text(t('player.belief.hint'))]),
          uiButton({
            variant: 'secondary',
            className: 'player-belief-record',
            testid: 'player-belief-record',
            onClick: () => send({ type: 'open-belief-modal', entityId }),
            children: [text(t('player.belief.record'))],
          }),
        ]),
      ],
    ),
    viewPrivateNotesPanel(state, send, entityId),
  ]);
}

function projectedFactsForEntity(s: Model, entityId: string): ProjectedFactRow[] {
  const rows: ProjectedFactRow[] = [];
  for (const fact of Object.values(s.projectedFacts)) {
    if (fact.subject === entityId) rows.push(fact);
  }
  rows.sort((a, b) => a.predicate.localeCompare(b.predicate));
  return rows;
}

/**
 * 226.1 (#245) — the Preview-as projected rows for one entity, sorted. Reads
 * `Model.previewProjection` (the chokepoint output for the selected Viewer);
 * empty while a projection is still computing or the Viewer sees nothing here.
 */
function previewFactsForEntity(s: Model, entityId: string): ProjectedFactRow[] {
  const rows: ProjectedFactRow[] = [];
  const proj = s.previewProjection;
  if (!proj) return rows;
  for (const fact of Object.values(proj)) {
    if (fact.subject === entityId) rows.push(fact);
  }
  rows.sort((a, b) => a.predicate.localeCompare(b.predicate));
  return rows;
}

/**
 * #247 — entity-level History / chronology selector accessor. A bare-Identifier
 * same-module function so the cross-file reactive walker registers the
 * `s.entities[*]` read directly (a local arrow closing over `entityId` would be
 * opaque). Delegates to the pure, unit-tested `buildEntityChronology`.
 */
function entityChronologyOf(s: Model, entityId: string): EntityChronology {
  return buildEntityChronology(s.entities[entityId]);
}

/**
 * One chronology row, flattened for the keyed `each`. `key` is unique per
 * (predicateKey, versionIndex) so the row list reconciles without churn.
 */
interface ChronologyRow {
  key: string;
  predicateLabel: string;
  valueDisplay: string;
  author: string;
  when: string;
  whenIso: string;
  state: 'latest' | 'past' | 'deleted';
  stateLabel: string;
  status: string;
  isBelief: boolean;
}

/** Reuse `EntityChronology` for the accessor return type without re-importing. */
type EntityChronology = ReturnType<typeof buildEntityChronology>;

/**
 * Format an op-log write instant as an absolute, locale-aware date-time for the
 * History log. Absolute (not relative) — a chronology wants "when did this
 * happen", and `Intl.DateTimeFormat` keeps it i18n/RTL-safe. Returns the raw
 * ISO on a parse failure rather than throwing.
 */
function formatChronologyInstant(iso: string): string {
  if (!iso) return '';
  const ms = new Date(iso).getTime();
  if (Number.isNaN(ms)) return iso;
  const locale =
    typeof navigator !== 'undefined' && navigator.language ? navigator.language : 'en';
  try {
    return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(ms);
  } catch {
    return new Intl.DateTimeFormat('en', { dateStyle: 'medium', timeStyle: 'short' }).format(ms);
  }
}

/** Map a folded `ChronologyEntry` to its display row. */
function chronologyRowOf(entry: ChronologyEntry): ChronologyRow {
  const rowState: ChronologyRow['state'] = entry.softDeleted
    ? 'deleted'
    : entry.isLatest
      ? 'latest'
      : 'past';
  const stateLabel =
    rowState === 'deleted'
      ? t('entity.history.deleted')
      : rowState === 'latest'
        ? t('entity.history.latest')
        : t('entity.history.past');
  return {
    key: `${entry.predicateKey}#${entry.versionIndex}`,
    predicateLabel: predicateLabel(entry.basePredicate),
    valueDisplay: formatFactValue(entry.value),
    author: entry.author,
    when: formatChronologyInstant(entry.writtenAtReal),
    whenIso: entry.writtenAtReal,
    state: rowState,
    stateLabel,
    status: entry.status,
    isBelief: entry.ownerKind === 'belief',
  };
}

/**
 * #247 / 30 §7.14 — the entity-page **History** tab: the whole-entity op-log
 * chronology. The canonical view for "how did this entity change over the
 * campaign" — every recorded version of every fact, newest-first.
 *
 * Distinct from the per-*fact* rollback panel (`fact-row-editor.ts:viewHistory`,
 * scoped to one predicate on the Facts tab); both read the SAME folded op-log
 * data (`EntityState.facts[*].versions`). Read-only (rollback stays on the Facts
 * tab where the per-predicate context lives).
 *
 * Chokepoint: GM-truth read of the folded `entities` cache — constructs no
 * `VisibleFact` and re-implements no reveal filter. Rendered only inside the
 * GM-view gate; under Preview-as the entire surface is replaced by the
 * reveal-filtered panel, so a non-GM Viewer never reaches this chronology.
 */
function viewEntityHistoryTab(state: Signal<Model>, _send: Send<Msg>, entityId: string): Mountable {
  return div(
    {
      id: 'entity-page-history-panel',
      role: 'tabpanel',
      'aria-labelledby': 'entity-page-history-tab',
      tabindex: 0,
      class: 'entity-page-panel entity-page-panel--history',
      'data-testid': 'entity-page-panel-history',
    },
    [
      p({ class: 'entity-page-history-hint' }, [text(t('entity.tab.history.hint'))]),
      // Honest empty state — only when the entity genuinely has no recorded
      // changes (no facts have ever carried a value).
      show(
        state.map((s) => entityChronologyOf(s, entityId).isEmpty),
        () => [
          emptyState({
            testid: 'entity-history-empty',
            message: text(t('entity.tab.history.empty')),
          }),
        ],
      ),
      show(
        state.map((s) => !entityChronologyOf(s, entityId).isEmpty),
        () => [
          p({ class: 'entity-history-summary', 'data-testid': 'entity-history-summary' }, [
            text(
              state.map((s) => {
                const chron = entityChronologyOf(s, entityId);
                return t('entity.tab.history.summary', {
                  changes: chron.changeCount,
                  predicates: chron.predicateCount,
                });
              }),
            ),
          ]),
          ol(
            {
              class: 'entity-history-list',
              'data-testid': 'entity-history-list',
              'aria-label': t('entity.tab.history'),
            },
            [
              each(
                state.map((s) => entityChronologyOf(s, entityId).entries.map(chronologyRowOf)),
                {
                  key: (row) => row.key,
                  render: (item) => [
                    li(
                      {
                        class: 'entity-history-entry',
                        'data-testid': 'entity-history-entry',
                        // Non-color cue paired with the chip: the row state is
                        // also a data attr so CSS + SR + tests can read it.
                        'data-state': item.at('state'),
                      },
                      [
                        div({ class: 'entity-history-entry-main' }, [
                          // Predicate → value (the change itself).
                          span({ class: 'entity-history-entry-label' }, [
                            text(
                              item.map((r) =>
                                t('entity.history.entry', {
                                  predicate: r.predicateLabel,
                                  value: r.valueDisplay,
                                }),
                              ),
                            ),
                          ]),
                          // State chip — non-color signal (text) + color.
                          badge({
                            tone: 'neutral',
                            attrs: { 'data-state': '', 'data-testid': 'entity-history-state' },
                            children: [text(item.at('stateLabel'))],
                          }),
                          // Belief provenance chip — only on non-canon rows.
                          show(item.at('isBelief'), () => [
                            badge({
                              tone: 'neutral',
                              attrs: { 'data-kind': 'belief' },
                              children: [text(t('entity.history.belief_label'))],
                            }),
                          ]),
                        ]),
                        // Meta line — author + absolute timestamp (in a <time>
                        // with a machine-readable datetime for a11y).
                        div({ class: 'entity-history-entry-meta' }, [
                          time(
                            { datetime: item.at('whenIso'), class: 'entity-history-entry-when' },
                            [
                              text(
                                item.map((r) =>
                                  t('entity.history.meta', { author: r.author, when: r.when }),
                                ),
                              ),
                            ],
                          ),
                        ]),
                      ],
                    ),
                  ],
                },
              ),
            ],
          ),
        ],
      ),
    ],
  );
}

/**
 * 226.1 (#245) — the entity-page Preview-as panel. Replaces the GM management
 * surfaces while previewing as a non-GM Viewer: it shows EXACTLY the facts that
 * `project(viewer, t)` reveals on this entity, read-only, at the current cursor.
 *
 * Phantom-Viewer: the rows come only from the chokepoint (`previewProjection`);
 * there is no edit affordance, no reveal chip, and no path back to GM-truth
 * `entities` here — the GM sees neither unrevealed values nor authoring hooks.
 */
function viewPreviewAsPanel(state: Signal<Model>, send: Send<Msg>, entityId: string): Mountable {
  return section(
    { class: 'preview-as-panel', 'data-testid': 'preview-as-panel' },
    [
      h2([text(state.map((s) => t('preview_as.panel_title', { viewer: previewViewerLabel(s) })))]),
      // While the async chokepoint is in flight `previewProjection` is null —
      // show a brief "Projecting…" status rather than a misleading empty state.
      show(
        state.map((s) => s.previewProjection === null),
        () => [
          p({ class: 'preview-as-computing', 'data-testid': 'preview-as-computing' }, [
            text(t('preview_as.computing')),
          ]),
        ],
      ),
      show(
        state.map((s) => s.previewProjection !== null && previewFactsForEntity(s, entityId).length === 0),
        () => [
          emptyState({
            testid: 'preview-as-empty',
            message: text(t('preview_as.panel_empty')),
          }),
        ],
      ),
      ul(
        { class: 'fact-list', 'data-testid': 'preview-as-fact-list' },
        [
          each(
            state.map((s) =>
              previewFactsForEntity(s, entityId).map((f) => ({
                key: factKey(f.subject, f.predicate),
                predicate: predicateLabel(f.predicate),
                valueDisplay: formatFactValue(f.value),
                pending: f.pending === true,
              })),
            ),
            {
              key: (row) => row.key,
              render: (item) => [
                li({ class: 'fact-chip', 'data-testid': 'preview-as-fact-chip' }, [
                  span({ class: 'fact-predicate' }, [text(item.at('predicate'))]),
                  span({ class: 'fact-value' }, [text(item.at('valueDisplay'))]),
                  show(item.at('pending'), () => [
                    span({ class: 'status-chip', 'data-status': 'pending' }, [
                      text(t('fact.pending_chip')),
                    ]),
                  ]),
                ]),
              ],
            },
          ),
        ],
      ),
    ],
  );
}

function formatFactValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  const dimensioned = formatNumberValueForDisplay(value);
  if (dimensioned !== null) return dimensioned;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

/**
 * III.B.3 (#67) — Private-notes panel.
 *
 * Loaded lazily on entity-page mount (the navigate Msg handler queues
 * a `load-private-note` effect for player-mode entity routes). The
 * textarea is uncontrolled-ish: edits dispatch `private-note/edit`
 * Msgs locally; the explicit Save button dispatches `private-note/save`
 * which PUTs to the worker.
 */
/**
 * Module-level same-file declaration so the cross-file walker
 * resolves the `s.privateNotes` read directly. Previously a local
 * arrow inside `viewPrivateNotesPanel` that closure-captured
 * `entityId` — the walker flagged that shape as opaque.
 */
function privateNoteAtEntity(s: Model, entityId: string): PrivateNoteState | undefined {
  return s.privateNotes[entityId];
}

function viewPrivateNotesPanel(state: Signal<Model>, send: Send<Msg>, entityId: string): Mountable {
  return aside({ class: 'private-notes-panel', 'data-testid': 'private-notes' }, [
    h3([text(t('player.notes_heading'))]),
    p({ class: 'private-notes-help' }, [text(t('player.notes_help'))]),
    textArea({
      className: 'private-notes-textarea',
      testid: 'private-notes-textarea',
      ariaLabel: t('player.notes_heading'),
      value: state.map((s) => privateNoteAtEntity(s, entityId)?.body ?? ''),
      onInput: (value: string) => {
        send({
          type: 'private-note/edit',
          entityId,
          body: value,
        });
      },
    }),
    div({ class: 'private-notes-footer' }, [
      uiButton({
        variant: 'primary',
        testid: 'private-notes-save',
        disabled: state.map((s) => !(privateNoteAtEntity(s, entityId)?.dirty ?? false)),
        onClick: () => send({ type: 'private-note/save', entityId }),
        children: [text(t('player.notes_save'))],
      }),
      span({ class: 'private-notes-status' }, [
        text(state.map((s) => {
          const noteState = privateNoteAtEntity(s, entityId);
          if (!noteState) return t('player.notes_loading');
          if (noteState.dirty) return t('player.notes_dirty');
          if (noteState.updatedAt === null) return t('player.notes_unsaved');
          return t('player.notes_saved');
        })),
      ]),
    ]),
  ]);
}

// #133 — `viewFactRow` (and its sibling primitives `viewFactChips`,
// `viewValidityRow`, `viewHistory`) were lifted into
// `apps/client/src/entities/fact-row-editor.ts` as part of the
// trait-driven editor extraction. The entity page now calls
// `viewEntityFactRows(state, send, entityId, factRowHost)` which derives the
// per-trait predicate list from `entity.traits[]` and dispatches the
// appropriate input shape per `value_kind`. Shared shell pieces
// (chips, validity, history) live alongside in that module.

// #133 — chips/validity/history primitives moved alongside the
// trait-driven fact-row editor in
// `apps/client/src/entities/fact-row-editor.ts`.

/**
 * #134 — Cmd-K palette spans every root + supports `type:<root>` and
 * `is:<predicate>` operator prefixes.
 *
 * The palette renders synchronously against the in-memory
 * `model.entities` snapshot (see `paletteMatches` doc-comment); the
 * operator parser, the filter, and the quick-create gating live in
 * `./palette/query.ts` so they can be unit-tested in isolation. This
 * view stays declarative — it just wires the parsed query through the
 * llui render loop and dresses each row with the right chip + a11y
 * scaffolding.
 *
 * Render contract:
 *   - Operator chips (one per `recognisedOperators` entry) above the
 *     result list, so the user sees what the parser consumed.
 *   - Each result row carries an entity-root chip (color + glyph per
 *     `30-design-system.md §10.7.1`) — no shared `<RootChip>` primitive
 *     exists yet (see #132); we use the existing `.chip[data-kind=
 *     'entity-root']` CSS hooks already shipped in styles.css.
 *   - Quick-create rows: one per root by default; collapse to a single
 *     row when `type:<root>` is set.
 *   - Keyboard nav: ↑↓ traverses the active row; Enter activates;
 *     Escape closes (the global handler in `bind-keyboard` covers Esc
 *     even outside the input — kept untouched).
 */
function viewPalette(state: Signal<Model>, send: Send<Msg>): Mountable {
  // The @dl/ui/modal atom owns scrim dismiss + focus trap + ESC. The
  // combobox semantics (role=combobox on input, aria-activedescendant,
  // arrow-key nav) stay inside — they're orthogonal to the modal
  // primitive. The 'xl' size variant gives the palette a wider panel
  // (52rem) so result lists don't crowd the operator chip strip.
  return modal({
    open: state.at('paletteOpen'),
    onDismiss: () => send({ type: 'close-palette' }),
    labels: { close: t('palette.help') },
    testid: 'palette',
    size: 'xl',
    panelClass: 'palette-panel',
    children: [
      // Input row (command-palette.html `.palette-input-row`): leading
      // decorative search glyph, the combobox input, a trailing `esc`
      // kbd hint. The glyph is `aria-hidden` (decorative — the input's
      // own `aria-label` carries the spoken purpose); the esc hint pairs
      // a styled key with an accessible label so it isn't glyph-only.
      div({ class: 'palette-input-row' }, [
        span({ class: 'palette-search-glyph', 'aria-hidden': 'true' }, [text('⌕')]),
        textInput({
          variant: 'bare',
          placeholder: t('palette.placeholder'),
          value: state.at('paletteQuery'),
          testid: 'palette-input',
          ariaLabel: t('palette.placeholder'),
          className: 'palette-input',
          onInput: (value: string) => {
            send({ type: 'palette-query', value });
          },
          attrs: {
            // The listbox is rendered below; aria-controls / activedescendant
            // wire the input to the active row for screen-reader users.
            role: 'combobox',
            'aria-controls': 'palette-listbox',
            'aria-autocomplete': 'list',
            'aria-expanded': 'true',
            'aria-activedescendant': state.map((s) => {
              const row = paletteRowAt(s, s.paletteActiveIndex);
              return row ? paletteRowId(row) : '';
            }),
            autofocus: true,
            onKeyDown: (event: KeyboardEvent) => {
              if (event.key === 'ArrowDown') {
                event.preventDefault();
                send({ type: 'palette-move', delta: 1 });
              } else if (event.key === 'ArrowUp') {
                event.preventDefault();
                send({ type: 'palette-move', delta: -1 });
              } else if (event.key === 'Enter') {
                event.preventDefault();
                send({ type: 'palette-activate' });
              }
            },
          },
        }),
        span(
          { class: 'palette-kbd palette-input-esc', 'aria-label': t('palette.input.dismiss_key') },
          [text('esc')],
        ),
      ]),
        // Operator chips — one per recognised operator. Only renders
        // when the parser consumed at least one operator so the happy
        // path (plain query) shows no extra chrome.
        show(
      state.map((s) => paletteRecognisedOperators(s).length > 0),
      () => [
            div(
              {
                class: 'palette-operator-chips',
                'data-testid': 'palette-operator-chips',
                role: 'status',
                'aria-live': 'polite',
              },
              [each(state.map((s) => paletteRecognisedOperators(s).map((op) => ({
                    key:
                      op.kind === 'type'
                        ? `type:${op.root}`
                        : `is:${op.negate ? '!' : ''}${op.predicate}`,
                    label: operatorChipLabel(op),
                  }))), {
        key: (it) => it.key,
        render: (item) => [
                  badge({
                    tone: 'info',
                    testid: 'palette-operator-chip',
                    attrs: { 'data-kind': 'palette-operator' },
                    children: [text(item.at('label'))],
                  }),
                ],
      })],
            ),
          ],
    ),
        // Lane header (command-palette.html palette-lane-h). The palette is the
        // name-match lane; the count mirrors the mock's per-lane tally. FTS /
        // graph / vector lanes arrive with the async search-router migration.
        show(
      state.map((s) => paletteMatches(s).length > 0),
      () => [
            div({ class: 'palette-lane-h', 'data-testid': 'palette-lane-header' }, [
              // Lane badge — the name lane is the only lane that ships in
              // Phase 1. The badge color pairs with the SR-readable label
              // text inside it, so color is never the only cue.
              badge({
                tone: 'accent',
                attrs: { 'data-lane': 'name' },
                children: [text(t('palette.lane.name'))],
              }),
              span({ class: 'palette-lane-h-caption' }, [text(t('palette.lane.name_caption'))]),
              badge({
                tone: 'neutral',
                children: [text(state.map((s) => String(paletteMatches(s).length)))],
              }),
            ]),
          ],
    ),
        ul({ id: 'palette-listbox', class: 'entity-list', role: 'listbox' }, [
          show(
      state.map((s) => paletteMatches(s).length === 0 && !paletteShowsQuickCreate(s)),
      () => [li([text(t('palette.no_matches'))])],
    ),
          each(state.map((s) => {
              const results = paletteMatches(s);
              const activeRow = paletteRowAt(s, s.paletteActiveIndex);
              const activeId =
                activeRow && activeRow.kind === 'result' ? activeRow.id : null;
              return results.map((r) => {
                const rootLabel = t(`entity.root.${r.root}`);
                // #B — enrich the palette result meta with the same cheap
                // scalar descriptor the entity list uses (root label · short
                // facts), resolved from the already-folded entity. Falls back
                // to the bare root label when the entity isn't in the bag or
                // carries no descriptor-worthy facts.
                const entity = s.entities[r.id];
                return {
                  id: r.id,
                  name: r.name,
                  root: r.root,
                  rootLabel,
                  meta: entity ? entityMetaLine(entity, rootLabel) : rootLabel,
                  domId: `palette-row-result-${r.id}`,
                  active: r.id === activeId,
                };
              });
            }), {
        key: (m) => m.id,
        render: (item) => [
              li(
                {
                  id: item.at('domId'),
                  role: 'option',
                  tabindex: 0,
                  'data-testid': 'palette-result-row',
                  'data-root': item.at('root'),
                  'aria-selected': item.map((m) => (m.active ? 'true' : 'false')),
                  class: item.map((m) => m.active ? 'palette-result-row palette-active' : 'palette-result-row',),
                },
                [
                  a(
                    {
                      href: item.map((m) => appRouteHref({ kind: 'entity', id: m.id })),
                      class: 'palette-result-link',
                      onClick: (event: MouseEvent) => {
                        event.preventDefault();
                        // See list-view.ts:240 — avoid `item.at('current').peek()`
                        // under llui v0.5.7 `item-dedup`.
                        const id = item.at('id').peek();
                        if (typeof id !== 'string') return;
                        send(navMsg({ kind: 'entity', id }));
                      },
                    },
                    [
                      // Root-glyph icon square (command-palette.html result-icon).
                      // §5 — the real Lucide per-root icon in the tile
                      // (replaces the `::before` geometric glyph). Decorative
                      // (aria-hidden); the SR-visible root identity is the meta
                      // line below — so color is never the only cue. Keyed by
                      // the row's root so the correct mark mounts.
                      span(
                        {
                          class: 'palette-result-icon',
                          'data-root': item.at('root'),
                          'data-testid': 'palette-root-chip',
                          'aria-hidden': 'true',
                        },
                        [
                          branch(item.at('root'), {
                            location: () => [entityIcon('location')],
                            character: () => [entityIcon('character')],
                            faction: () => [entityIcon('faction')],
                            event: () => [entityIcon('event')],
                            item: () => [entityIcon('item')],
                            lore: () => [entityIcon('lore')],
                          }),
                        ],
                      ),
                      span({ class: 'palette-result-body' }, [
                        span({ class: 'palette-result-name' }, [text(item.at('name'))]),
                        span({ class: 'palette-result-meta' }, [text(item.at('meta'))]),
                      ]),
                      // Trail (command-palette.html `.result-trail`): a lane
                      // tag (the non-color cue that pairs with the lane badge)
                      // plus an "open ↵" affordance that the CSS reveals on the
                      // active/hovered row only.
                      span({ class: 'palette-result-trail' }, [
                        badge({
                          tone: 'neutral',
                          children: [text(t('palette.result.lane_tag'))],
                        }),
                        span({ class: 'palette-result-open', 'aria-hidden': 'true' }, [
                          text(t('palette.result.open_hint')),
                          span({ class: 'palette-result-open-key' }, [text('↵')]),
                        ]),
                      ]),
                    ],
                  ),
                ],
              ),
            ],
      }),
          // Quick-create rows — surfaced when the query is non-empty
          // AND no exact match exists. The roots shown are gated by
          // any `type:` operator so a `type:character` query offers
          // exactly one create-row instead of all six.
          show(
      state.map((s) => paletteShowsQuickCreate(s)),
      () => [
              each(state.map((s) => {
                  const parsed = parsedQueryFromModel(s);
                  const activeRow = paletteRowAt(s, s.paletteActiveIndex);
                  const activeRoot =
                    activeRow && activeRow.kind === 'create' ? activeRow.root : null;
                  const literal = parsed.literal.trim();
                  return quickCreateRoots(parsed).map((root) => ({
                    root,
                    domId: `palette-row-create-${root}`,
                    active: root === activeRoot,
                    literal,
                  }));
                }), {
        key: (it) => it.root,
        render: (item) => [
                  li(
                    {
                      id: item.at('domId'),
                      role: 'option',
                      tabindex: 0,
                      class: item.map((it) => it.active
                          ? 'palette-create-row palette-active'
                          : 'palette-create-row',),
                      'data-testid': 'palette-create-row',
                      'data-root': item.at('root'),
                      'aria-selected': item.map((it) => (it.active ? 'true' : 'false')),
                      onClick: () => {
                        // See list-view.ts:240 — `item.at('current').peek()` is
                        // broken under llui v0.5.7; read field
                        // accessors directly.
                        const r = item.at('root').peek();
                        if (typeof r !== 'string') return;
                        send({ type: 'create-entity', root: r as EntityRoot });
                      },
                    },
                    [
                      text(item.map((it) => {
                          // The query text shown in the row reflects what
                          // the user actually typed (literal, with operators
                          // stripped). When the literal is empty (a pure
                          // operator query) we shouldn't reach here because
                          // `paletteShowsQuickCreate` gates on non-empty
                          // literal text — but be defensive.
                          // The literal can't be read here cheaply via the
                          // per-item helper because it depends on the Model
                          // not the item. We expose it on the item shape
                          // instead — see the `items:` projector below.
                          return t('palette.create_for_root', {
                            root: t(`entity.root.${it.root}`),
                            query: it.literal,
                          });
                        }),),
                    ],
                  ),
                ],
      }),
            ],
    ),
        ]),
        // Keyboard-hint footer (command-palette.html mock). Purely
        // informative for keyboard users, so it carries a single
        // `aria-label` and hides its glyphs from the SR (they read as
        // noise); on touch devices it collapses (no physical keys).
        footer(
          {
            class: 'palette-footer',
            'data-testid': 'palette-footer',
            'aria-label': t('palette.footer.aria'),
          },
          [
            paletteFooterHint(['↑', '↓'], t('palette.footer.navigate')),
            paletteFooterHint(['↵'], t('palette.footer.select')),
            paletteFooterHint(['esc'], t('palette.footer.dismiss')),
            // Right-aligned result/perspective summary (command-palette.html
            // shows "8 results · perspective: GM · 4 lanes"). Phase 1 ships
            // the GM perspective + the single name lane.
            span({ class: 'palette-footer-summary' }, [
              text(state.map((s) => t('palette.footer.summary', { n: paletteMatches(s).length }))),
            ]),
          ],
        ),
    ],
  });
}

/**
 * One keyboard hint in the palette footer: a cluster of `<kbd>`-styled
 * key glyphs followed by the action label. Glyphs are `aria-hidden`
 * (the footer's `aria-label` carries the spoken form); the visible label
 * pairs each shortcut with a word so the cue isn't glyph-only.
 */
function paletteFooterHint(keys: string[], label: string): Mountable {
  return span({ class: 'palette-footer-hint' }, [
    ...keys.map((k) => span({ class: 'palette-kbd', 'aria-hidden': 'true' }, [text(k)])),
    span({ class: 'palette-footer-label' }, [text(label)]),
  ]);
}

/**
 * Translation for one recognised operator. Surfaces as the chip label
 * above the result list; the chip itself uses the generic `.chip`
 * styling (no per-operator color — only the entity-root chips carry a
 * data-state for color/glyph pairing).
 */
function operatorChipLabel(
  op: ParsedPaletteQuery['recognisedOperators'][number],
): string {
  if (op.kind === 'type') {
    return t('palette.filter_chip_type', { root: t(`entity.root.${op.root}`) });
  }
  return op.negate
    ? t('palette.filter_chip_is_not', { predicate: op.predicate })
    : t('palette.filter_chip_is', { predicate: op.predicate });
}

/**
 * Per-root picker buttons — six explicit builders, no shared inner
 * helper. The `'data-root'` and `onClick` `root:` values are STRING
 * LITERALS, not function-parameter values; the analyzer resolves
 * them statically without emitting `__bindUncertain(…, root)` and
 * the file-wide opacity detector stays quiet. The pre-computed
 * `label` const captures the i18n string at view-construction so
 * the reactive `text(label)` accessor closes over a stable
 * function-scope binding (not a `.map` iteration variable).
 *
 * Each builder is ~12 lines but every value at the call site is
 * statically resolvable — this is the canonical llui shape per the
 * `composition-patterns.md` "static-text-with-literal-data" recipe.
 */
function buildLocationPickerItem(state: Signal<Model>, send: Send<Msg>): Mountable {
  const label = t('entity.new.location');
  return li({ role: 'none', class: 'root-picker-item' }, [
    uiButton({
      variant: 'ghost',
      className: 'root-picker-button',
      testid: 'root-picker-button',
      attrs: { role: 'menuitem', 'data-root': 'location' },
      onClick: () => send({ type: 'open-new-entity-modal', root: 'location' }),
      children: [text(label)],
    }),
  ]);
}
function buildCharacterPickerItem(state: Signal<Model>, send: Send<Msg>): Mountable {
  const label = t('entity.new.character');
  return li({ role: 'none', class: 'root-picker-item' }, [
    uiButton({
      variant: 'ghost',
      className: 'root-picker-button',
      testid: 'root-picker-button',
      attrs: { role: 'menuitem', 'data-root': 'character' },
      onClick: () => send({ type: 'open-new-entity-modal', root: 'character' }),
      children: [text(label)],
    }),
  ]);
}
function buildFactionPickerItem(state: Signal<Model>, send: Send<Msg>): Mountable {
  const label = t('entity.new.faction');
  return li({ role: 'none', class: 'root-picker-item' }, [
    uiButton({
      variant: 'ghost',
      className: 'root-picker-button',
      testid: 'root-picker-button',
      attrs: { role: 'menuitem', 'data-root': 'faction' },
      onClick: () => send({ type: 'open-new-entity-modal', root: 'faction' }),
      children: [text(label)],
    }),
  ]);
}
function buildEventPickerItem(state: Signal<Model>, send: Send<Msg>): Mountable {
  const label = t('entity.new.event');
  return li({ role: 'none', class: 'root-picker-item' }, [
    uiButton({
      variant: 'ghost',
      className: 'root-picker-button',
      testid: 'root-picker-button',
      attrs: { role: 'menuitem', 'data-root': 'event' },
      onClick: () => send({ type: 'open-new-entity-modal', root: 'event' }),
      children: [text(label)],
    }),
  ]);
}
function buildItemPickerItem(state: Signal<Model>, send: Send<Msg>): Mountable {
  const label = t('entity.new.item');
  return li({ role: 'none', class: 'root-picker-item' }, [
    uiButton({
      variant: 'ghost',
      className: 'root-picker-button',
      testid: 'root-picker-button',
      attrs: { role: 'menuitem', 'data-root': 'item' },
      onClick: () => send({ type: 'open-new-entity-modal', root: 'item' }),
      children: [text(label)],
    }),
  ]);
}
function buildLorePickerItem(state: Signal<Model>, send: Send<Msg>): Mountable {
  const label = t('entity.new.lore');
  return li({ role: 'none', class: 'root-picker-item' }, [
    uiButton({
      variant: 'ghost',
      className: 'root-picker-button',
      testid: 'root-picker-button',
      attrs: { role: 'menuitem', 'data-root': 'lore' },
      onClick: () => send({ type: 'open-new-entity-modal', root: 'lore' }),
      children: [text(label)],
    }),
  ]);
}

/**
 * #131 — root picker overlay. Rendered top-level by the main view;
 * gated on `model.rootPickerOpen`. Six labelled buttons (one per
 * EntityRoot), arranged in a single-column stack so phone breakpoints
 * keep every button at the 44px touch-target floor.
 */
function viewRootPicker(state: Signal<Model>, send: Send<Msg>): Mountable {
  return modal({
    open: state.at('rootPickerOpen'),
    onDismiss: () => send({ type: 'close-root-picker' }),
    labels: { close: t('mobile.bottom_sheet.close') },
    labelId: 'root-picker-heading',
    testid: 'root-picker',
    panelClass: 'root-picker-panel',
    size: 'sm',
    children: [
      h2({ id: 'root-picker-heading' }, [
        text(t('entity.new.picker_heading')),
      ]),
      p({ class: 'root-picker-help' }, [text(t('entity.new.picker_help'))]),
      // #schema-settings — custom entity types (quick-create templates). Shown
      // above the bare roots; selecting one seeds the new-entity modal with the
      // type's root AND traits so the entity is born with them attached. Hidden
      // when the campaign has no homebrew types.
      show(
      state.map((s) => allTypeRows(s).length > 0),
      () => [
          p({ class: 'root-picker-section-label' }, [
            text(t('entity.new.types_heading')),
          ]),
          ul(
            { class: 'root-picker-list', role: 'menu', 'data-testid': 'root-picker-types' },
            [each(state.map((s) => allTypeRows(s)), {
        key: (row) => row.id,
        render: (item) => [
                li({ role: 'none', class: 'root-picker-item' }, [
                  uiButton({
                    variant: 'ghost',
                    className: 'root-picker-button',
                    testid: 'root-picker-type-button',
                    attrs: { role: 'menuitem', 'data-type': item.at('id') },
                    onClick: () =>
                      send({
                        type: 'open-new-entity-modal',
                        root: item.at('root').peek() as EntityRoot,
                        traits: item.at('traits').peek(),
                      }),
                    children: [text(item.at('label'))],
                  }),
                ]),
              ],
      })],
          ),
        ],
    ),
      ul({ class: 'root-picker-list', role: 'menu' }, [
        buildLocationPickerItem(state, send),
        buildCharacterPickerItem(state, send),
        buildFactionPickerItem(state, send),
        buildEventPickerItem(state, send),
        buildItemPickerItem(state, send),
        buildLorePickerItem(state, send),
      ]),
      uiButton({
        variant: 'ghost',
        className: 'root-picker-close',
        testid: 'root-picker-close',
        onClick: () => send({ type: 'close-root-picker' }),
        children: [text(t('mobile.bottom_sheet.close'))],
      }),
    ],
  });
}

/**
 * Bug 4 — new-entity name modal. Replaces the eager auto-create flow
 * (`create-entity` Msg minted an id and submitted ops on click; the
 * GM landed on an entity page already populated with "New Location",
 * with a save-button-disabled race because the dirty flag stomped
 * against the eager-`fact-actually-saved` ACK). Now the entity is
 * created only when the GM submits a non-empty name.
 *
 * Activation: clicking the list-view "New Location" shortcut or any
 * root-picker button dispatches `open-new-entity-modal { root }`,
 * which closes the root-picker (if open) and seeds
 * `model.newEntityModal` with the chosen root and an empty name. This
 * view is mounted at the top of the tree (same level as the
 * root-picker), gated on `newEntityModal !== null` via `h.show`.
 *
 * Affordances:
 *   - Single text input with localised "name" placeholder
 *   - Save button — disabled while the trimmed name is empty
 *   - Cancel button + overlay click + Escape — discard, close modal
 *   - Enter on the input — submit (mirrors the save button click)
 *
 * The heading uses the same `entity.new.<root>` i18n key the
 * root-picker buttons use, so "New Location" / "New Character" / etc.
 * appear consistently across the two surfaces.
 */
function newEntityModalTrimmedName(s: Model): string {
  return s.newEntityModal === null ? '' : s.newEntityModal.name.trim();
}
function newEntityModalCanSubmit(s: Model): boolean {
  return newEntityModalTrimmedName(s).length > 0;
}

function viewNewEntityModal(state: Signal<Model>, send: Send<Msg>): Mountable {
  return modal({
    open: state.map((s) => s.newEntityModal !== null),
    onDismiss: () => send({ type: 'close-new-entity-modal' }),
    labels: { close: t('new_entity.modal.cancel') },
    labelId: 'new-entity-heading',
    testid: 'new-entity-modal',
    panelClass: 'new-entity-panel',
    size: 'sm',
    children: [
      h2({ id: 'new-entity-heading' }, [
          text(state.map((s) => s.newEntityModal === null
              ? ''
              : t(`entity.new.${s.newEntityModal.root}`),)),
        ]),
        label({ for: 'new-entity-name-input', class: 'new-entity-label' }, [
          text(t('new_entity.modal.name_label')),
        ]),
        textInput({
          className: 'new-entity-input',
          testid: 'new-entity-name-input',
          placeholder: state.map((s) => s.newEntityModal === null
              ? ''
              : t('new_entity.modal.name_placeholder', {
                  root: t(`entity.root.${s.newEntityModal.root}`),
                })),
          value: state.map((s) => (s.newEntityModal === null ? '' : s.newEntityModal.name)),
          ariaLabel: t('new_entity.modal.name_label'),
          onInput: (value: string) => {
            send({ type: 'update-new-entity-name', name: value });
          },
          attrs: {
            id: 'new-entity-name-input',
            // `autofocus` puts the caret in the input on mount — the
            // overlay is modal so stealing focus is the expected UX.
            autofocus: true,
            onKeyDown: (event: KeyboardEvent) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                send({ type: 'submit-new-entity' });
              } else if (event.key === 'Escape') {
                event.preventDefault();
                send({ type: 'close-new-entity-modal' });
              }
            },
          },
        }),
        div({ class: 'new-entity-actions' }, [
          uiButton({
            variant: 'secondary',
            testid: 'new-entity-cancel',
            onClick: () => send({ type: 'close-new-entity-modal' }),
            children: [text(t('new_entity.modal.cancel'))],
          }),
          uiButton({
            variant: 'primary',
            testid: 'new-entity-save',
            disabled: state.map((s) => !newEntityModalCanSubmit(s)),
            onClick: () => send({ type: 'submit-new-entity' }),
            children: [text(t('new_entity.modal.save'))],
          }),
        ]),
    ],
  });
}

/**
 * #81 — "Create instance" modal/bottom-sheet. Opened from a Lore
 * entity (desktop header action OR mobile entity-action cluster). Captures
 * a name + a root (six-root radio-group via `entityIcon`, default
 * `character`). Confirm reuses the `submit-entity-create` + name-fact path
 * and additionally writes an `instance_of` ref fact back to the source
 * Lore (scope option (a): minimal ref — no template-trait copy).
 *
 * The `modal` primitive is CSS-driven: a centred dialog on desktop, a
 * full-width stretched panel on the phone breakpoint (modal.css
 * `@media (max-width: 479px)`) — the same "modal→bottom-sheet" pattern the
 * new-entity modal uses, so the no-horizontal-overflow gate covers it.
 */
function instanceModalTrimmedName(s: Model): string {
  return s.instanceModal === null ? '' : s.instanceModal.name.trim();
}
function instanceModalCanSubmit(s: Model): boolean {
  return instanceModalTrimmedName(s).length > 0;
}

function viewInstanceModal(state: Signal<Model>, send: Send<Msg>): Mountable {
  return modal({
    open: state.map((s) => s.instanceModal !== null),
    onDismiss: () => send({ type: 'close-instance-modal' }),
    labels: { close: t('instance.sheet.cancel') },
    labelId: 'instance-heading',
    testid: 'instance-modal',
    panelClass: 'instance-panel',
    size: 'sm',
    children: [
      h2({ id: 'instance-heading' }, [text(t('instance.sheet.heading'))]),
      p({ class: 'instance-help' }, [text(t('instance.sheet.help'))]),
      label({ for: 'instance-name-input', class: 'new-entity-label' }, [
        text(t('instance.sheet.name_label')),
      ]),
      textInput({
        className: 'new-entity-input',
        testid: 'instance-name-input',
        placeholder: t('instance.sheet.name_placeholder'),
        value: state.map((s) => (s.instanceModal === null ? '' : s.instanceModal.name)),
        ariaLabel: t('instance.sheet.name_label'),
        onInput: (value: string) => {
          send({ type: 'update-instance-name', name: value });
        },
        attrs: {
          id: 'instance-name-input',
          autofocus: true,
          onKeyDown: (event: KeyboardEvent) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              send({ type: 'submit-instance' });
            } else if (event.key === 'Escape') {
              event.preventDefault();
              send({ type: 'close-instance-modal' });
            }
          },
        },
      }),
      // Root picker — a labelled radio-group. Each option pairs the Lucide
      // per-root icon with its localised label (icon never the only signal),
      // `aria-checked` carries the selection for assistive tech, and the
      // `data-selected` attr drives a non-colour visual cue (a ring/check).
      span({ id: 'instance-root-label', class: 'new-entity-label' }, [
        text(t('instance.sheet.root_label')),
      ]),
      div(
        {
          class: 'instance-root-picker',
          'data-testid': 'instance-root-picker',
          role: 'radiogroup',
          'aria-labelledby': 'instance-root-label',
        },
        [
          each(state.map(() => ALL_ENTITY_ROOTS), {
            key: (root) => root,
            render: (root) => [
              uiButton({
                variant: 'ghost',
                className: 'instance-root-option',
                attrs: {
                  'data-testid': root.map((r) => `instance-root-${r}`),
                  'data-root': root.map((r) => r),
                  role: 'radio',
                  // Row-aware model read: rebase `state` through the row ctx
                  // (per CLAUDE.md — a bare `.map` in a row reads the row ctx,
                  // not the Model). `derived([state, root])` combines both.
                  'aria-checked': derived([state, root], (s, r) =>
                    s.instanceModal !== null && s.instanceModal.root === r ? 'true' : 'false'),
                  'data-selected': derived([state, root], (s, r) =>
                    s.instanceModal !== null && s.instanceModal.root === r ? 'true' : 'false'),
                },
                onClick: () =>
                  send({ type: 'set-instance-root', root: root.peek() }),
                children: [
                  span({ class: 'instance-root-icon', 'aria-hidden': 'true' }, [
                    branch(root.map((r) => r), {
                      location: () => [entityIcon('location', { size: 18 })],
                      character: () => [entityIcon('character', { size: 18 })],
                      faction: () => [entityIcon('faction', { size: 18 })],
                      event: () => [entityIcon('event', { size: 18 })],
                      item: () => [entityIcon('item', { size: 18 })],
                      lore: () => [entityIcon('lore', { size: 18 })],
                    }),
                  ]),
                  span({ class: 'instance-root-name' }, [
                    text(root.map((r) => t(`entity.root.${r}`))),
                  ]),
                ],
              }),
            ],
          }),
        ],
      ),
      div({ class: 'new-entity-actions' }, [
        uiButton({
          variant: 'secondary',
          testid: 'instance-cancel',
          onClick: () => send({ type: 'close-instance-modal' }),
          children: [text(t('instance.sheet.cancel'))],
        }),
        uiButton({
          variant: 'primary',
          testid: 'instance-create',
          disabled: state.map((s) => !instanceModalCanSubmit(s)),
          onClick: () => send({ type: 'submit-instance' }),
          children: [text(t('instance.sheet.create'))],
        }),
      ]),
    ],
  });
}

/**
 * Phase C — per-page "Draft with AI" modal. A prompt + a read-only list
 * of the entity's empty prose fields that will be drafted. Submit fires
 * one suggest-prose per field (Phase-1-honest: N grounded text calls, not
 * a single structured multi-fact generation) and routes to the inbox.
 */
interface PageDraftRow {
  predicate: string;
  label: string;
}
function pageDraftPredicateRows(s: Model): PageDraftRow[] {
  if (s.pageDraftModal === null) return [];
  return s.pageDraftModal.predicates.map((p) => ({ predicate: p, label: predicateLabel(p) }));
}
function pageDraftCount(s: Model): number {
  return s.pageDraftModal === null ? 0 : s.pageDraftModal.predicates.length;
}
function viewPageDraftModal(state: Signal<Model>, send: Send<Msg>): Mountable {
  return modal({
    open: state.map((s) => s.pageDraftModal !== null),
    onDismiss: () => send({ type: 'close-page-draft' }),
    labels: { close: t('new_entity.modal.cancel') },
    labelId: 'page-draft-heading',
    testid: 'page-draft-modal',
    panelClass: 'page-draft-panel',
    size: 'sm',
    children: [
      h2({ id: 'page-draft-heading' }, [text(t('entity.page.ai_draft_heading'))]),
      p({ class: 'page-draft-help' }, [
        text(state.map((s) => t('entity.page.ai_draft_help', { n: pageDraftCount(s) }))),
      ]),
      label({ for: 'page-draft-prompt', class: 'page-draft-label' }, [
        text(t('entity.page.ai_draft_prompt_label')),
      ]),
      textArea({
        className: 'page-draft-prompt',
        testid: 'page-draft-prompt',
        rows: 3,
        placeholder: t('entity.page.ai_draft_prompt_placeholder'),
        value: state.map((s) => (s.pageDraftModal === null ? '' : s.pageDraftModal.prompt)),
        ariaLabel: t('entity.page.ai_draft_prompt_label'),
        onInput: (value: string) => {
          send({ type: 'page-draft-prompt', value });
        },
        attrs: { id: 'page-draft-prompt' },
      }),
      ul({ class: 'page-draft-fields', role: 'list', 'data-testid': 'page-draft-fields' },
        [each(state.map((s) => pageDraftPredicateRows(s)), {
        key: (row) => row.predicate,
        render: (item) => [li({ class: 'page-draft-field' }, [text(item.at('label'))])],
      })],
      ),
      div({ class: 'page-draft-actions' }, [
        uiButton({
          variant: 'secondary',
          testid: 'page-draft-cancel',
          onClick: () => send({ type: 'close-page-draft' }),
          children: [text(t('new_entity.modal.cancel'))],
        }),
        uiButton({
          variant: 'primary',
          testid: 'page-draft-submit',
          disabled: state.map((s) => pageDraftCount(s) === 0),
          onClick: () => send({ type: 'page-draft-submit' }),
          children: [text(state.map((s) => t('entity.page.ai_draft_submit', { n: pageDraftCount(s) })))],
        }),
      ]),
    ],
  });
}

/**
 * Fresh `<option>` nodes for the value-kind select — fully unrolled (no
 * loop variable or template-literal key), so the llui walker sees only
 * literal `t('…')` reads and never flags an opaque reactive reference
 * (which would force a file-wide full-mask). Returns fresh nodes per
 * call — a Node can't be shared across the per-row selects. `enum`
 * reveals a values sub-input; `ref` reveals a root-restriction select
 * (#76).
 */
/**
 * #units / ADR-0002 — the campaign units editor. Opened from the trait
 * picker's "Define units" button. Lists the campaign's existing dimensions
 * + units (read from the homebrew overlay) and offers two forms: one to
 * define a new dimension (id, label, base unit) and one to define a new
 * unit (id, label, symbol, dimension, to_base, optional offset). Each form
 * submits a `pack.define_dimension` / `pack.define_unit` op to the same
 * homebrew Pack chain the trait editor uses.
 */
function viewUnitsEditor(state: Signal<Model>, send: Send<Msg>): Mountable {
  return modal({
    open: state.map((s) => s.unitsEditor !== null),
    onDismiss: () => send({ type: 'close-units-editor' }),
    labels: { close: t('units_editor.close') },
    labelId: 'units-editor-heading',
    describedById: 'units-editor-desc',
    testid: 'units-editor-modal',
    panelClass: 'units-editor-panel',
    size: 'md',
    children: [
      h2({ id: 'units-editor-heading' }, [text(t('units_editor.heading'))]),
      p({ id: 'units-editor-desc', class: 'units-editor-desc' }, [
        text(t('units_editor.description')),
      ]),

      // Existing campaign dimensions + units (read-only list with retire).
      viewUnitsEditorExisting(state, send),

      // ── Define a new dimension ───────────────────────────────────
      // level 3: under the units-editor modal's h2 dialog title.
      sectionHeading({ title: text(t('units_editor.dimension_section')), level: 3 }),
      div({ class: 'units-editor-form units-editor-dimension-form' }, [
        // ADR-0007: the GM authors a NAME only — the opaque `dim_` id is
        // minted on submit. Each input inlines a bare-Identifier accessor
        // directly in `value:` (the llui compiler rejects passing an accessor
        // through a helper's function parameter — opaque-state-flow).
        label({ for: 'units-editor-dimension-name', class: 'units-editor-label' }, [
          span([t('units_editor.dimension_name_label')]),
          textInput({
            testid: 'units-editor-dimension-name',
            value: state.map((s) => unitsDimensionName(s)),
            placeholder: t('units_editor.dimension_name_placeholder'),
            ariaLabel: t('units_editor.dimension_name_label'),
            onInput: (value: string) =>
              send({
                type: 'units-editor-set-dimension',
                patch: { name: value },
              }),
            attrs: { id: 'units-editor-dimension-name' },
          }),
        ]),
        uiButton({
          variant: 'secondary',
          className: 'units-editor-submit-dimension',
          testid: 'units-editor-submit-dimension',
          onClick: () => send({ type: 'units-editor-submit-dimension' }),
          children: [text(t('units_editor.add_dimension'))],
        }),
      ]),

      // ── Define a new unit ────────────────────────────────────────
      sectionHeading({ title: text(t('units_editor.unit_section')), level: 3 }),
      div({ class: 'units-editor-form units-editor-unit-form' }, [
        // ADR-0007: name (+ symbol) only — the opaque `unit_` id is minted on
        // submit.
        label({ for: 'units-editor-unit-name', class: 'units-editor-label' }, [
          span([t('units_editor.unit_name_label')]),
          textInput({
            testid: 'units-editor-unit-name',
            value: state.map((s) => unitsUnitName(s)),
            placeholder: t('units_editor.unit_name_placeholder'),
            ariaLabel: t('units_editor.unit_name_label'),
            onInput: (value: string) =>
              send({
                type: 'units-editor-set-unit',
                patch: { name: value },
              }),
            attrs: { id: 'units-editor-unit-name' },
          }),
        ]),
        label({ for: 'units-editor-unit-symbol', class: 'units-editor-label' }, [
          span([t('units_editor.symbol_label')]),
          textInput({
            testid: 'units-editor-unit-symbol',
            value: state.map((s) => unitsUnitSymbol(s)),
            placeholder: t('units_editor.symbol_placeholder'),
            ariaLabel: t('units_editor.symbol_label'),
            onInput: (value: string) =>
              send({
                type: 'units-editor-set-unit',
                patch: { symbol: value },
              }),
            attrs: { id: 'units-editor-unit-symbol' },
          }),
        ]),
        label({ for: 'units-editor-unit-dimension', class: 'units-editor-label' }, [
          span([t('units_editor.dimension_label')]),
          // Pick the owning dimension by NAME; the option value is the opaque
          // `dim_` id (ADR-0007 §2). Common + campaign dimensions.
          select(
            {
              id: 'units-editor-unit-dimension',
              class: 'dl-select',
              'data-testid': 'units-editor-unit-dimension',
              'aria-label': t('units_editor.dimension_label'),
              value: state.map((s) => unitsUnitDimension(s)),
              onChange: (event: Event) =>
                send({
                  type: 'units-editor-set-unit',
                  patch: { dimension: (event.target as HTMLSelectElement).value },
                }),
            },
            [
              option({ value: '' }, [t('units_editor.dimension_choose')]),
              // Reactive option list: a dimension defined moments ago (this
              // session) must be pickable without reopening the modal, so the
              // options rebuild on the homebrew fold rather than freezing at
              // mount.
              each(
                state.map(() =>
                  campaignUnitRegistry()
                    .listDimensions()
                    .map((d) => ({ id: d.id, label: resolveLocalizedDisplay(d.name) || d.id })),
                ),
                {
                  key: (d) => d.id,
                  render: (item) => [
                    option({ value: item.at('id') }, [text(item.at('label'))]),
                  ],
                },
              ),
            ],
          ),
        ]),
        label({ for: 'units-editor-unit-to-base', class: 'units-editor-label' }, [
          span([t('units_editor.to_base_label')]),
          textInput({
            testid: 'units-editor-unit-to-base',
            value: state.map((s) => unitsUnitToBase(s)),
            placeholder: t('units_editor.to_base_placeholder'),
            ariaLabel: t('units_editor.to_base_label'),
            onInput: (value: string) =>
              send({
                type: 'units-editor-set-unit',
                patch: { toBase: value },
              }),
            attrs: { id: 'units-editor-unit-to-base' },
          }),
        ]),
        label({ for: 'units-editor-unit-offset', class: 'units-editor-label' }, [
          span([t('units_editor.offset_label')]),
          textInput({
            testid: 'units-editor-unit-offset',
            value: state.map((s) => unitsUnitOffset(s)),
            placeholder: t('units_editor.offset_placeholder'),
            ariaLabel: t('units_editor.offset_label'),
            onInput: (value: string) =>
              send({
                type: 'units-editor-set-unit',
                patch: { offset: value },
              }),
            attrs: { id: 'units-editor-unit-offset' },
          }),
        ]),
        label({ class: 'units-editor-checkbox' }, [
          input({
            type: 'checkbox',
            'data-testid': 'units-editor-unit-is-base',
            checked: state.map((s) => unitsUnitIsBase(s)),
            onChange: (event: Event) =>
              send({
                type: 'units-editor-set-unit',
                patch: { isBase: (event.target as HTMLInputElement).checked },
              }),
          }),
          span([text(t('units_editor.is_base_label'))]),
        ]),
        uiButton({
          variant: 'secondary',
          className: 'units-editor-submit-unit',
          testid: 'units-editor-submit-unit',
          onClick: () => send({ type: 'units-editor-submit-unit' }),
          children: [text(t('units_editor.add_unit'))],
        }),
      ]),

      p({ class: 'units-editor-error', role: 'alert', 'data-testid': 'units-editor-error' }, [
        text(state.map((s) => unitsEditorError(s))),
      ]),
    ],
  });
}

// Bare-Identifier reactive accessors for the units editor (the llui
// cross-file walker follows property-access chains landing on `s.<field>`).
function unitsDimensionName(s: Model): string {
  return s.unitsEditor?.dimensionDraft.name ?? '';
}
function unitsUnitName(s: Model): string {
  return s.unitsEditor?.unitDraft.name ?? '';
}
function unitsUnitSymbol(s: Model): string {
  return s.unitsEditor?.unitDraft.symbol ?? '';
}
function unitsUnitDimension(s: Model): string {
  return s.unitsEditor?.unitDraft.dimension ?? '';
}
function unitsUnitToBase(s: Model): string {
  return s.unitsEditor?.unitDraft.toBase ?? '';
}
function unitsUnitOffset(s: Model): string {
  return s.unitsEditor?.unitDraft.offset ?? '';
}
function unitsUnitIsBase(s: Model): boolean {
  return s.unitsEditor?.unitDraft.isBase ?? false;
}
function unitsEditorError(s: Model): string {
  return s.unitsEditor?.error ?? '';
}

/** Read-only list of the campaign's existing dimensions + units, with retire buttons. */
function viewUnitsEditorExisting(state: Signal<Model>, send: Send<Msg>): Mountable {
  return div({ class: 'units-editor-existing', 'data-testid': 'units-editor-existing' }, [
    sectionHeading({ title: text(t('units_editor.existing_dimensions')), level: 3 }),
    ul(
      { class: 'units-editor-existing-list', 'data-testid': 'units-editor-existing-dimensions' },
      [each(state.map(() => getHomebrewDefs().dimensions.map((d) => ({ id: d.id, label: localizedText(d.name) }))), {
        key: (d) => d.id,
        render: (item) => [
          li({ class: 'units-editor-existing-row', 'data-dimension-id': item.at('id') }, [
            // ADR-0004: the raw `dim_` id is NEVER user-facing — it lives on a
            // `data-*` attr (minting proof for the e2e), not in the label text.
            span([text(item.at('label'))]),
            uiButton({
              variant: 'icon',
              className: 'units-editor-retire',
              ariaLabel: t('units_editor.retire_dimension'),
              onClick: () => {
                const id = item.at('id').peek();
                if (typeof id !== 'string') return;
                send({ type: 'retire-dimension', dimensionId: id });
              },
              children: ['×'],
            }),
          ]),
        ],
      })],
    ),
    sectionHeading({ title: text(t('units_editor.existing_units')), level: 3 }),
    ul(
      { class: 'units-editor-existing-list', 'data-testid': 'units-editor-existing-units' },
      [each(state.map(() => getHomebrewDefs().units.map((u) => ({
            id: u.id,
            label: `${localizedText(u.name)} · ${localizedText(u.symbol)}`,
          }))), {
        key: (u) => u.id,
        render: (item) => [
          li({ class: 'units-editor-existing-row', 'data-unit-id': item.at('id') }, [
            // ADR-0004: the raw `unit_` id is NEVER user-facing — it lives on a
            // `data-*` attr (minting proof for the e2e), not in the label text.
            span([text(item.at('label'))]),
            uiButton({
              variant: 'icon',
              className: 'units-editor-retire',
              ariaLabel: t('units_editor.retire_unit'),
              onClick: () => {
                const id = item.at('id').peek();
                if (typeof id !== 'string') return;
                send({ type: 'retire-unit', unitId: id });
              },
              children: ['×'],
            }),
          ]),
        ],
      })],
    ),
  ]);
}

/** Resolve a `LocalizedString` to display text (string or `{en, …}`). */
function localizedText(value: unknown): string {
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object') {
    const rec = value as Record<string, string>;
    return rec['en'] ?? Object.values(rec)[0] ?? '';
  }
  return '';
}

function traitEditorKindOptions(): Renderable {
  // Every flat-authorable value kind (ADR-0002). `tuple` is intentionally
  // omitted — it needs nested field authoring (`tuple_fields`) the flat
  // single-field editor can't express; a tuple field is authored by
  // declaring the trait's homebrew pack op directly (Phase 2 editor work).
  return [
    option({ value: 'text' }, [t('trait_editor.kind.text')]),
    option({ value: 'rich_text' }, [t('trait_editor.kind.rich_text')]),
    option({ value: 'number' }, [t('trait_editor.kind.number')]),
    option({ value: 'boolean' }, [t('trait_editor.kind.boolean')]),
    option({ value: 'enum' }, [t('trait_editor.kind.enum')]),
    option({ value: 'ref' }, [t('trait_editor.kind.ref')]),
    option({ value: 'image' }, [t('trait_editor.kind.image')]),
    option({ value: 'file' }, [t('trait_editor.kind.file')]),
    option({ value: 'game_date' }, [t('trait_editor.kind.game_date')]),
    option({ value: 'game_date_range' }, [t('trait_editor.kind.game_date_range')]),
    option({ value: 'game_datetime' }, [t('trait_editor.kind.game_datetime')]),
    option({ value: 'game_datetime_range' }, [t('trait_editor.kind.game_datetime_range')]),
    option({ value: 'game_time' }, [t('trait_editor.kind.game_time')]),
    option({ value: 'real_date' }, [t('trait_editor.kind.real_date')]),
    option({ value: 'real_time' }, [t('trait_editor.kind.real_time')]),
    option({ value: 'real_datetime' }, [t('trait_editor.kind.real_datetime')]),
    option({ value: 'tuple' }, [t('trait_editor.kind.tuple')]),
  ];
}

/**
 * Fresh `<option>` nodes for a tuple sub-field's kind select (the subset a
 * tuple field may hold — `TupleFieldKindSchema`). No nesting / media /
 * ranges. Unrolled per the same walker constraint as the top-level kinds.
 */
function traitEditorTupleFieldKindOptions(): Renderable {
  return [
    option({ value: 'text' }, [t('trait_editor.kind.text')]),
    option({ value: 'rich_text' }, [t('trait_editor.kind.rich_text')]),
    option({ value: 'number' }, [t('trait_editor.kind.number')]),
    option({ value: 'boolean' }, [t('trait_editor.kind.boolean')]),
    option({ value: 'enum' }, [t('trait_editor.kind.enum')]),
    option({ value: 'ref' }, [t('trait_editor.kind.ref')]),
    option({ value: 'real_date' }, [t('trait_editor.kind.real_date')]),
    option({ value: 'real_time' }, [t('trait_editor.kind.real_time')]),
    option({ value: 'real_datetime' }, [t('trait_editor.kind.real_datetime')]),
    option({ value: 'game_date' }, [t('trait_editor.kind.game_date')]),
    option({ value: 'game_datetime' }, [t('trait_editor.kind.game_datetime')]),
    option({ value: 'game_time' }, [t('trait_editor.kind.game_time')]),
  ];
}

/**
 * Fresh `<option>` nodes for the ref-root restriction select — "Any
 * entity" plus one per entity root. Unrolled (no loop var) to keep the
 * llui walker from flagging an opaque reactive read (#76).
 */
function traitEditorRefRootOptions(): Renderable {
  return [
    option({ value: '' }, [t('trait_editor.ref_any')]),
    option({ value: 'location' }, [t('entity.root.location')]),
    option({ value: 'character' }, [t('entity.root.character')]),
    option({ value: 'faction' }, [t('entity.root.faction')]),
    option({ value: 'event' }, [t('entity.root.event')]),
    option({ value: 'item' }, [t('entity.root.item')]),
    option({ value: 'lore' }, [t('entity.root.lore')]),
  ];
}

/**
 * #73 / ADR-0001 — the trait editor. Opened from the trait picker's
 * "Define a new trait" button; writes a campaign-local homebrew trait
 * (one `pack.define_predicate` per field + one `pack.define_trait`) to
 * the op log. The new trait then surfaces in the picker for its
 * `applies_to` root and its fields render in the fact-row editor.
 */
/**
 * #schema-settings — the trait-editor FORM body, rendered inline in the
 * settings Traits tab (the modal wrapper was removed). Returns the field
 * nodes; `viewTraitEditorInline` supplies the heading + section chrome. The
 * `applies_to` roots multi-select replaces the old entity-seeded description.
 */
function traitEditorBody(state: Signal<Model>, send: Send<Msg>): Renderable {
  return [
      p({ id: 'trait-editor-desc', class: 'trait-editor-desc' }, [
        text(t('trait_editor.description')),
      ]),
      traitEditorAppliesToSelector(state, send),
      label({ for: 'trait-editor-id', class: 'trait-editor-label' }, [
        text(t('trait_editor.trait_id_label')),
      ]),
      textInput({
        className: 'trait-editor-id-input',
        testid: 'trait-editor-id',
        value: state.map((s) => s.traitEditor?.traitId ?? ''),
        // ADR-0004 §1: the trait's identity is its opaque `traitOriginalId`
        // (reused on save), NOT this field. This input is the display NAME (a
        // LocalizedString label), freely editable in BOTH create and edit mode —
        // renaming reuses the id, so no entity attachment is orphaned. (The old
        // edit-mode lock predated the opaque-id model.)
        placeholder: t('trait_editor.trait_id_placeholder'),
        ariaLabel: t('trait_editor.trait_id_label'),
        onInput: (value: string) => send({ type: 'trait-editor-set-id', value }),
        attrs: { id: 'trait-editor-id', autofocus: true },
      }),
      sectionHeading({ title: text(t('trait_editor.fields_heading')), level: 3 }),
      ul(
        { class: 'trait-editor-fields', 'data-testid': 'trait-editor-fields' },
        [each(state.map((s) => (s.traitEditor?.fields ?? []).map((f) => ({
              ...f,
              allowRisky: s.traitEditor?.allowRisky ?? false,
            }))), {
        key: (field) => field.key,
        render: (item, index) => {
            // Fresh per-row option nodes via same-module helpers (keeps
            // the loop var out of the walker's reactive analysis).
            const kindOptions = traitEditorKindOptions();
            const refRootOptions = traitEditorRefRootOptions();
            // Dimension <option>s for a number field's unit picker — the
            // campaign-merged dimension set (common + homebrew). Empty
            // option = unitless (#1).
            const dimensionOptions: Mountable[] = [
              option({ value: '' }, [t('trait_editor.dimension_none')]),
              ...campaignUnitRegistry()
                .listDimensions()
                .map((d) => option({ value: d.id }, [resolveLocalizedDisplay(d.name) || d.id])),
            ];
            return [
            li({
              class: 'trait-editor-field-row',
              // A dangling member reference reads read-only "unresolved" (CSS
              // dims + disables its inputs, keeping only the remove button).
              'data-unresolved': item.map((f) => (f.unresolved === true ? 'true' : 'false')),
            }, [
              textInput({
                className: 'trait-editor-field-id',
                testid: 'trait-editor-field-id',
                value: item.at('id'),
                placeholder: t('trait_editor.field_id_placeholder'),
                ariaLabel: t('trait_editor.field_id_label'),
                onInput: (value: string) =>
                  send({
                    type: 'trait-editor-set-field',
                    index: index.peek(),
                    patch: { id: value },
                  }),
              }),
              select(
                {
                  class: 'dl-select trait-editor-field-kind',
                  'data-testid': 'trait-editor-field-kind',
                  'aria-label': t('trait_editor.field_kind_label'),
                  value: item.at('valueKind'),
                  // Existing fields lock their type (changing it would
                  // re-interpret saved values) unless the GM has opted
                  // into risky changes (#77); new fields are always free.
                  disabled: item.map((f) => f.originalId != null && !f.allowRisky),
                  onChange: (event: Event) =>
                    send({
                      type: 'trait-editor-set-field',
                      index: index.peek(),
                      patch: { valueKind: (event.target as HTMLSelectElement).value as ValueKind },
                    }),
                },
                kindOptions,
              ),
              select(
                {
                  class: 'dl-select trait-editor-field-cardinality',
                  'data-testid': 'trait-editor-field-cardinality',
                  'aria-label': t('trait_editor.field_cardinality_label'),
                  value: item.at('cardinality'),
                  onChange: (event: Event) =>
                    send({
                      type: 'trait-editor-set-field',
                      index: index.peek(),
                      patch: {
                        cardinality: (event.target as HTMLSelectElement).value as
                          | 'single'
                          | 'many',
                      },
                    }),
                },
                [
                  option({ value: 'single' }, [t('trait_editor.cardinality_single')]),
                  option({ value: 'many' }, [t('trait_editor.cardinality_many')]),
                ],
              ),
              uiButton({
                variant: 'icon',
                className: 'trait-editor-field-remove',
                testid: 'trait-editor-field-remove',
                ariaLabel: t('trait_editor.remove_field'),
                onClick: () => send({ type: 'trait-editor-remove-field', index: index.peek() }),
                children: ['×'],
              }),
              // Enum choices sub-input — shown only when kind === 'enum'
              // (always in the DOM; CSS hides it via data-shown). Wraps to
              // its own line within the row.
              textInput({
                className: 'trait-editor-field-enum',
                testid: 'trait-editor-field-enum',
                value: item.map((f) => f.enumValues ?? ''),
                placeholder: t('trait_editor.enum_values_placeholder'),
                ariaLabel: t('trait_editor.enum_values_label'),
                onInput: (value: string) =>
                  send({
                    type: 'trait-editor-set-field',
                    index: index.peek(),
                    patch: { enumValues: value },
                  }),
                attrs: {
                  'data-shown': item.map((f) => (f.valueKind === 'enum' ? 'true' : 'false')),
                },
              }),
              // Ref-root restriction — shown only when kind === 'ref'.
              select(
                {
                  class: 'dl-select trait-editor-field-refroot',
                  'data-testid': 'trait-editor-field-refroot',
                  'data-shown': item.map((f) => (f.valueKind === 'ref' ? 'true' : 'false')),
                  'aria-label': t('trait_editor.ref_root_label'),
                  value: item.map((f) => f.refRoot ?? ''),
                  onChange: (event: Event) =>
                    send({
                      type: 'trait-editor-set-field',
                      index: index.peek(),
                      patch: { refRoot: (event.target as HTMLSelectElement).value as '' | EntityRoot },
                    }),
                },
                refRootOptions,
              ),
              // Reciprocal (inverse) label — shown only when kind === 'ref'
              // (#backlinks). How this relationship reads on the target's
              // Connections tab, e.g. "held by".
              textInput({
                className: 'trait-editor-field-reciprocal',
                testid: 'trait-editor-field-reciprocal',
                value: item.map((f) => f.reciprocal ?? ''),
                placeholder: t('trait_editor.reciprocal_placeholder'),
                ariaLabel: t('trait_editor.reciprocal_label'),
                onInput: (value: string) =>
                  send({
                    type: 'trait-editor-set-field',
                    index: index.peek(),
                    patch: { reciprocal: value },
                  }),
                attrs: {
                  'data-shown': item.map((f) => (f.valueKind === 'ref' ? 'true' : 'false')),
                },
              }),
              // Measurement dimension — shown only when kind === 'number'
              // (#1). Picking a dimension makes the per-fact editor offer
              // that dimension's units.
              select(
                {
                  class: 'dl-select trait-editor-field-dimension',
                  'data-testid': 'trait-editor-field-dimension',
                  'data-shown': item.map((f) => (f.valueKind === 'number' ? 'true' : 'false')),
                  'aria-label': t('trait_editor.dimension_label'),
                  value: item.map((f) => f.dimension ?? ''),
                  onChange: (event: Event) =>
                    send({
                      type: 'trait-editor-set-field',
                      index: index.peek(),
                      patch: { dimension: (event.target as HTMLSelectElement).value },
                    }),
                },
                dimensionOptions,
              ),
              // Yes/No state labels — shown only when kind === 'boolean'
              // (#2). Either may be blank (falls back to the default).
              textInput({
                className: 'trait-editor-field-true-label',
                testid: 'trait-editor-field-true-label',
                value: item.map((f) => f.trueLabel ?? ''),
                placeholder: t('trait_editor.boolean_true_placeholder'),
                ariaLabel: t('trait_editor.boolean_true_label'),
                onInput: (value: string) =>
                  send({
                    type: 'trait-editor-set-field',
                    index: index.peek(),
                    patch: { trueLabel: value },
                  }),
                attrs: {
                  'data-shown': item.map((f) => (f.valueKind === 'boolean' ? 'true' : 'false')),
                },
              }),
              textInput({
                className: 'trait-editor-field-false-label',
                testid: 'trait-editor-field-false-label',
                value: item.map((f) => f.falseLabel ?? ''),
                placeholder: t('trait_editor.boolean_false_placeholder'),
                ariaLabel: t('trait_editor.boolean_false_label'),
                onInput: (value: string) =>
                  send({
                    type: 'trait-editor-set-field',
                    index: index.peek(),
                    patch: { falseLabel: value },
                  }),
                attrs: {
                  'data-shown': item.map((f) => (f.valueKind === 'boolean' ? 'true' : 'false')),
                },
              }),
              // Tuple sub-field editor (ADR-0002) — shown only when kind ===
              // 'tuple'. One row per named sub-field; the Government
              // role-per-entity case (holder=ref, role=enum, note?=text).
              div(
                {
                  class: 'trait-editor-tuple-fields',
                  'data-testid': 'trait-editor-tuple-fields',
                  'data-shown': item.map((f) => (f.valueKind === 'tuple' ? 'true' : 'false')),
                },
                [
                  each(derived([state, index], (s, idx) => (s.traitEditor?.fields[idx]?.tupleFields ?? []).map((sf) => ({
                        ...sf,
                        parent: idx,
                      }))), {
        key: (sf) => sf.key,
        render: (sub, subIndex) => [
                      div({ class: 'trait-editor-tuple-field-row' }, [
                        textInput({
                          className: 'trait-editor-tuple-field-name',
                          testid: 'trait-editor-tuple-field-name',
                          value: sub.at('name'),
                          placeholder: t('trait_editor.tuple_field_name_placeholder'),
                          ariaLabel: t('trait_editor.tuple_field_name_label'),
                          onInput: (value: string) =>
                            send({
                              type: 'trait-editor-set-tuple-field',
                              field: index.peek(),
                              sub: subIndex.peek(),
                              patch: { name: value },
                            }),
                        }),
                        select(
                          {
                            class: 'dl-select trait-editor-tuple-field-kind',
                            'data-testid': 'trait-editor-tuple-field-kind',
                            'aria-label': t('trait_editor.field_kind_label'),
                            value: sub.at('valueKind'),
                            onChange: (event: Event) =>
                              send({
                                type: 'trait-editor-set-tuple-field',
                                field: index.peek(),
                                sub: subIndex.peek(),
                                patch: {
                                  valueKind: (event.target as HTMLSelectElement)
                                    .value as TupleFieldKind,
                                },
                              }),
                          },
                          traitEditorTupleFieldKindOptions(),
                        ),
                        textInput({
                          className: 'trait-editor-tuple-field-enum',
                          testid: 'trait-editor-tuple-field-enum',
                          value: sub.map((s) => s.enumValues ?? ''),
                          placeholder: t('trait_editor.enum_values_placeholder'),
                          ariaLabel: t('trait_editor.enum_values_label'),
                          onInput: (value: string) =>
                            send({
                              type: 'trait-editor-set-tuple-field',
                              field: index.peek(),
                              sub: subIndex.peek(),
                              patch: { enumValues: value },
                            }),
                          attrs: {
                            'data-shown': sub.map((s) => (s.valueKind === 'enum' ? 'true' : 'false')),
                          },
                        }),
                        label({ class: 'trait-editor-tuple-field-optional' }, [
                          input({
                            type: 'checkbox',
                            'data-testid': 'trait-editor-tuple-field-optional',
                            checked: sub.at('optional'),
                            onChange: (event: Event) =>
                              send({
                                type: 'trait-editor-set-tuple-field',
                                field: index.peek(),
                                sub: subIndex.peek(),
                                patch: { optional: (event.target as HTMLInputElement).checked },
                              }),
                          }),
                          text(t('trait_editor.tuple_field_optional')),
                        ]),
                        uiButton({
                          variant: 'icon',
                          className: 'trait-editor-tuple-field-remove',
                          testid: 'trait-editor-tuple-field-remove',
                          ariaLabel: t('trait_editor.tuple_field_remove'),
                          onClick: () =>
                            send({
                              type: 'trait-editor-remove-tuple-field',
                              field: index.peek(),
                              sub: subIndex.peek(),
                            }),
                          children: ['×'],
                        }),
                      ]),
                    ],
      }),
                  uiButton({
                    variant: 'secondary',
                    className: 'trait-editor-tuple-field-add',
                    testid: 'trait-editor-tuple-field-add',
                    onClick: () => send({ type: 'trait-editor-add-tuple-field', field: index.peek() }),
                    children: [text(t('trait_editor.tuple_field_add'))],
                  }),
                ],
              ),
            ]),
            ];
          },
      })],
      ),
      uiButton({
        variant: 'ghost',
        className: 'trait-editor-add-field',
        testid: 'trait-editor-add-field',
        onClick: () => send({ type: 'trait-editor-add-field' }),
        children: [text(t('trait_editor.add_field'))],
      }),
      // #77 — risky-change opt-in (edit mode only). Always in the DOM;
      // CSS hides it in create mode (no existing fields to protect).
      // When checked, existing fields' value kind unlocks and cardinality
      // narrowing is permitted, and the warning below appears.
      label(
        {
          class: 'trait-editor-risky',
          'data-testid': 'trait-editor-risky',
          'data-mode': state.map((s) => s.traitEditor?.mode ?? 'create'),
        },
        [
          input({
            type: 'checkbox',
            'data-testid': 'trait-editor-risky-toggle',
            checked: state.map((s) => s.traitEditor?.allowRisky ?? false),
            onChange: (event: Event) =>
              send({
                type: 'trait-editor-toggle-risky',
                value: (event.target as HTMLInputElement).checked,
              }),
          }),
          span([text(t('trait_editor.allow_risky'))]),
        ],
      ),
      show(
      state.map((s) => (s.traitEditor?.allowRisky ?? false)),
      () => [
          p({ class: 'trait-editor-risky-warning', role: 'status', 'data-testid': 'trait-editor-risky-warning' }, [
            text(t('trait_editor.risky_warning')),
          ]),
        ],
    ),
      // Validation error — only present when the last submit failed.
      show(
      state.map((s) => s.traitEditor?.error != null),
      () => [
          p({ class: 'trait-editor-error', role: 'alert', 'data-testid': 'trait-editor-error' }, [
            text(state.map((s) => s.traitEditor?.error ?? '')),
          ]),
        ],
    ),
      div({ class: 'trait-editor-actions' }, [
        // Delete (retire) — edit mode only. Always in the DOM; CSS hides
        // it in create mode (data-mode). Non-destructive: facts persist.
        uiButton({
          variant: 'danger',
          className: 'trait-editor-delete',
          testid: 'trait-editor-delete',
          attrs: { 'data-mode': state.map((s) => s.traitEditor?.mode ?? 'create') },
          onClick: () => send({ type: 'delete-current-trait' }),
          children: [text(t('trait_editor.delete'))],
        }),
        uiButton({
          variant: 'secondary',
          testid: 'trait-editor-cancel',
          onClick: () => send({ type: 'close-trait-editor' }),
          children: [text(t('trait_editor.cancel'))],
        }),
        uiButton({
          variant: 'primary',
          testid: 'trait-editor-save',
          onClick: () => send({ type: 'trait-editor-submit' }),
          children: [
            text(state.map((s) => t(s.traitEditor?.mode === 'edit' ? 'trait_editor.save_edit' : 'trait_editor.save'),)),
          ],
        }),
      ]),
  ];
}

/**
 * Palette query results (#134) — driven through the in-memory
 * router-equivalent. The operator-prefix parser + filter lives in
 * `./palette/query.ts` (a pure module so the parser can be unit-tested
 * without the render loop); this function is the thin Model adapter
 * that supplies the in-memory snapshot.
 *
 * The router itself (`@dl/search`) runs over `op_log` via SQL inside
 * the GM's local replica. The Cmd-K palette doesn't have a synchronous
 * handle to the replica from inside the render function (the replica
 * lives behind a Comlink boundary), so we mirror the same normalisation
 * + scoring pipeline (exact > prefix > substring) against the already-
 * folded `model.entities` snapshot. The OUTPUT shape is identical to
 * what `SearchRouter.search('name', …)` returns at merge time — same
 * cap (top 50 per name-lane contract).
 *
 * Migrating to the async path is a follow-up (the palette UX needs to
 * grow a debounce-then-search flow); when it lands, this function
 * collapses to "feed `parsed` into a `SearchRouter.search` call".
 */
function entityFactName(e: EntityState): string {
  return String(currentValue(e.facts[FACT_NAME]) ?? e.id);
}

function parsedQueryFromModel(s: Model): ParsedPaletteQuery {
  return parsePaletteQuery(s.paletteQuery);
}

function paletteMatches(s: Model): PaletteResult[] {
  return filterEntities(parsedQueryFromModel(s), Object.values(s.entities), {
    normalize: normalizeName,
    factName: entityFactName,
  });
}

/**
 * Does the parsed query exactly match an existing entity's name
 * (under the same normaliser the name-lane uses), respecting any
 * `type:` operator filter? When `true`, suppress the quick-create
 * rows — typing the name of an existing entity should surface that
 * entity, not offer to create a duplicate.
 */
function paletteHasExactMatch(s: Model): boolean {
  return parsedHasExactMatch(parsedQueryFromModel(s), Object.values(s.entities), {
    normalize: normalizeName,
    factName: entityFactName,
  });
}

function paletteRecognisedOperators(s: Model): readonly ParsedPaletteQuery['recognisedOperators'][number][] {
  return parsedQueryFromModel(s).recognisedOperators;
}

/**
 * Should the palette render its quick-create rows? GM-only, query
 * has some content (literal text OR a `type:` operator), and the
 * query does NOT exactly match an existing entity name.
 *
 * A pure-`type:` query (no literal text) keeps quick-create disabled
 * — creating "Create Character: ''" with an empty name has no UX
 * payoff. We only enable quick-create once the user has typed at
 * least one non-whitespace character of literal text.
 */
function paletteShowsQuickCreate(s: Model): boolean {
  if (s.session.mode !== 'gm') return false;
  const parsed = parsedQueryFromModel(s);
  if (parsed.literal.trim().length === 0) return false;
  return !paletteHasExactMatch(s);
}

/**
 * The palette's rendered row sequence — results first, then
 * quick-create rows. This is the single source of truth for both
 * arrow-key navigation (`palette-move` reducer) and Enter activation
 * (`palette-activate` reducer); the view layer derives DOM ids and
 * `aria-activedescendant` from the same sequence so the indices
 * stay in lockstep.
 */
type PaletteRow =
  | { kind: 'result'; id: string; name: string; root: EntityRoot }
  | { kind: 'create'; root: EntityRoot };

function paletteRows(s: Model): PaletteRow[] {
  const out: PaletteRow[] = paletteMatches(s).map((r) => ({
    kind: 'result',
    id: r.id,
    name: r.name,
    root: r.root,
  }));
  if (paletteShowsQuickCreate(s)) {
    for (const root of quickCreateRoots(parsedQueryFromModel(s))) {
      out.push({ kind: 'create', root });
    }
  }
  return out;
}

function paletteRowCount(s: Model): number {
  return paletteRows(s).length;
}

function paletteRowAt(s: Model, index: number): PaletteRow | null {
  const rows = paletteRows(s);
  if (rows.length === 0) return null;
  const clamped = ((index % rows.length) + rows.length) % rows.length;
  return rows[clamped] ?? null;
}

function paletteRowId(row: PaletteRow): string {
  return row.kind === 'result'
    ? `palette-row-result-${row.id}`
    : `palette-row-create-${row.root}`;
}

// ---------- effects ----------

/**
 * Derive the WebSocket sync URL from the Worker origin.
 *
 * `workerOrigin()` returns either an http(s) URL (env-pinned or
 * localhost dev) or the empty string (same-origin production deploy).
 * We translate the scheme accordingly and append the campaign id +
 * optional ws_token.
 */
function syncUrl(wsToken: string | null): string {
  const tokenSuffix =
    wsToken !== null && wsToken.length > 0 ? `&ws_token=${encodeURIComponent(wsToken)}` : '';
  const origin = workerOrigin();
  let wsBase: string;
  if (origin === '') {
    // Same-origin production deploy. Translate the document scheme.
    const protocol =
      typeof window !== 'undefined' && window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = typeof window !== 'undefined' ? window.location.host : 'localhost:8787';
    wsBase = `${protocol}//${host}`;
  } else {
    wsBase = origin.replace(/^http(s?):/, 'ws$1:');
  }
  const campaignIdParam = CAMPAIGN_ID ?? '';
  return `${wsBase}/v1/sync?campaign_id=${encodeURIComponent(campaignIdParam)}${tokenSuffix}`;
}

let activeWsToken: string | null = null;

/**
 * III.B.1 (#65) — Fetch the boot-resolution endpoint to derive the
 * authenticated User's Viewer for this campaign.
 *
 * Failure modes:
 *   - 401 → no active session; fall back to legacy GM identity so
 *     the demo flow keeps running. The shell renders as GM and the
 *     SyncClient connects with a pre-#65 token.
 *   - 403 → authenticated but not a Campaign member. Surface as a
 *     boot error; the shell still mounts in a degraded state.
 *   - network error → same path as 401.
 */
/**
 * The no-campaign boot path.
 *
 * Fired when `CAMPAIGN_ID` is null (no localStorage entry, no query
 * param, no global injection). Fetches `GET /v1/account` and routes:
 *
 *   - 401 → bounce to /sign-in (same shape as bootSession's 401 path)
 *   - 0 campaigns → campaigns-boot/empty, route to picker
 *   - 1 campaign → persist + reload (the GM lands directly in the shell)
 *   - 2+ campaigns → campaigns-boot/loaded, route to picker
 *   - network / 5xx → campaigns-boot/error
 */
async function bootChooseCampaign(send: (msg: Msg) => void): Promise<void> {
  // The Msg loop hasn't seen `campaigns-boot/loading` yet; start it
  // there so the picker view renders its loading state immediately.
  send({ type: 'campaigns-boot/loading' });
  // Navigate to the picker route so the view dispatcher mounts the
  // picker subtree (which then reads the boot state).
  if (typeof window !== 'undefined' && window.location.pathname !== '/campaigns') {
    window.history.pushState({}, '', '/campaigns');
  }
  send({ type: 'navigate', route: { kind: 'campaigns' } });

  const result = await apiListCampaigns();
  if (!result.ok) {
    if (result.status === 401) {
      if (!LEGACY_GM_SESSION_ENABLED) {
        if (typeof window !== 'undefined') {
          window.history.pushState({}, '', '/sign-in');
        }
        send({ type: 'navigate', route: { kind: 'sign-in' } });
        return;
      }
      send({ type: 'session/loaded', session: LEGACY_GM_SESSION });
      return;
    }
    send({
      type: 'campaigns-boot/error',
      message: result.error || t('campaigns.boot.error'),
    });
    return;
  }
  const campaigns = result.value.campaigns;
  if (campaigns.length === 0) {
    send({ type: 'campaigns-boot/empty' });
    return;
  }
  if (campaigns.length === 1) {
    // Auto-select; reload so the substrate boots clean against the
    // chosen campaign. The length-1 narrow is on the array, so the
    // index access needs a runtime non-null assertion to satisfy
    // `noUncheckedIndexedAccess`.
    const only = campaigns[0];
    if (only) {
      enterCampaign(only.id);
      return;
    }
  }
  send({ type: 'campaigns-boot/loaded', campaigns });
}

// ─── #152 account / profile settings Effects ──────────────────────────────

async function loadAccount(send: (msg: Msg) => void): Promise<void> {
  const result = await fetchCurrentUser();
  if (result.ok) {
    send({ type: 'account/loaded', user: result.value.user });
  } else {
    send({ type: 'account/load-failed', message: result.error });
  }
}

/**
 * #89 — localStorage key for the client-tracked "last export" timestamp,
 * scoped per campaign. Stamped whenever an export download is triggered.
 */
export function lastExportStorageKey(campaignId: string): string {
  return `dl:last-export:${campaignId}`;
}

/** #89 — read the last-export ISO timestamp for a campaign (`null` if never). */
export function readLastExport(campaignId: string): string | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    return localStorage.getItem(lastExportStorageKey(campaignId));
  } catch {
    return null;
  }
}

/** localStorage key for the per-campaign recently-viewed entity list. */
export function recentEntitiesStorageKey(campaignId: string): string {
  return `dl:recent-entities:${campaignId}`;
}

/** Read the recently-viewed entity ids for a campaign (most-recent-first; `[]` if none). */
export function readRecentEntities(campaignId: string): string[] {
  if (typeof localStorage === 'undefined') return [];
  try {
    const raw = localStorage.getItem(recentEntitiesStorageKey(campaignId));
    if (raw === null) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((x): x is string => typeof x === 'string').slice(0, RECENT_CAP);
  } catch {
    return [];
  }
}

/** Persist the recently-viewed entity ids for a campaign. */
export function writeRecentEntities(campaignId: string, recent: string[]): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(
      recentEntitiesStorageKey(campaignId),
      JSON.stringify(recent.slice(0, RECENT_CAP)),
    );
  } catch {
    // Quota / disabled storage — recent list is a best-effort affordance.
  }
}

/**
 * #89 — format an ISO instant as a locale-aware relative time (e.g.
 * "2 hours ago"). Uses `Intl.RelativeTimeFormat`. Returns the localized
 * "Never" string when `iso` is null/empty. Pure + exported for tests.
 */
export function formatRelativeTime(iso: string | null): string {
  if (!iso) return t('settings.backup.last_export_never');
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return t('settings.backup.last_export_never');
  const diffMs = then - Date.now();
  const locale =
    typeof navigator !== 'undefined' && navigator.language ? navigator.language : 'en';
  let rtf: Intl.RelativeTimeFormat;
  try {
    rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  } catch {
    rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
  }
  const abs = Math.abs(diffMs);
  const sec = 1000;
  const min = 60 * sec;
  const hour = 60 * min;
  const day = 24 * hour;
  if (abs < min) return rtf.format(Math.round(diffMs / sec), 'second');
  if (abs < hour) return rtf.format(Math.round(diffMs / min), 'minute');
  if (abs < day) return rtf.format(Math.round(diffMs / hour), 'hour');
  if (abs < 30 * day) return rtf.format(Math.round(diffMs / day), 'day');
  if (abs < 365 * day) return rtf.format(Math.round(diffMs / (30 * day)), 'month');
  return rtf.format(Math.round(diffMs / (365 * day)), 'year');
}

/** #89 — stamp the last-export time for a campaign (best-effort). */
function stampLastExport(campaignId: string, at: string): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(lastExportStorageKey(campaignId), at);
  } catch {
    // Quota / disabled storage — non-fatal; the export itself still runs.
  }
}

/**
 * #89 — trigger the campaign export downloads (JSON + Markdown ZIP). Both
 * are membership-gated GET endpoints; we kick them as file downloads via
 * transient anchors so the browser's native save dialog handles them
 * (free at every tier — never gated). Records the local "last export"
 * timestamp so the Settings "Data & backup" section can show it.
 */
function triggerCampaignExport(campaignId: string, send: (msg: Msg) => void): void {
  if (typeof document === 'undefined') return;
  const origin = workerOrigin();
  const urls = [
    `${origin}/v1/campaigns/${encodeURIComponent(campaignId)}/export.json`,
    `${origin}/v1/campaigns/${encodeURIComponent(campaignId)}/export.zip?format=md`,
  ];
  for (const url of urls) {
    const anchor = document.createElement('a');
    anchor.href = url;
    // `download` hints the browser to save rather than navigate; the
    // worker also sets Content-Disposition. Same-origin in prod; the
    // session cookie rides along (credentialed) on the navigation.
    anchor.download = '';
    anchor.rel = 'noopener';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  }
  const at = new Date().toISOString();
  stampLastExport(campaignId, at);
  send({ type: 'export/recorded', campaignId, at });
}

async function accountTwofaSetup(send: (msg: Msg) => void): Promise<void> {
  const result = await setupTotp();
  if (result.ok) {
    send({
      type: 'account/twofa-setup-ready',
      secretBase32: result.value.secret_base32,
      otpauthUri: result.value.otpauth_uri,
    });
  } else {
    send({ type: 'account/action-failed', message: result.error });
  }
}

async function accountTwofaConfirm(send: (msg: Msg) => void, code: string): Promise<void> {
  const result = await confirmTotp(code);
  if (result.ok) {
    send({ type: 'account/twofa-confirmed', backupCodes: result.value.backup_codes });
  } else {
    send({ type: 'account/action-failed', message: result.error });
  }
}

async function accountTwofaDisable(send: (msg: Msg) => void): Promise<void> {
  // The disable endpoint re-auths with the password. We can't collect it
  // inline without an in-place change-password flow, so route the GM to the
  // email-based disable path the same way the forgot-password flow works:
  // the worker mails a one-time disable link. We surface that as a notice.
  const password = typeof window !== 'undefined' ? window.prompt(t('account.security.twofa.password_prompt')) : null;
  if (password === null || password === '') {
    send({ type: 'account/twofa-cancel' });
    return;
  }
  const result = await requestTotpDisable(password);
  if (result.ok) {
    send({ type: 'account/notice', message: t('account.security.twofa.disable_sent') });
  } else {
    send({ type: 'account/action-failed', message: result.error });
  }
}

async function accountPasswordReset(send: (msg: Msg) => void, email: string): Promise<void> {
  const result = await requestPasswordReset(email);
  if (result.ok) {
    send({ type: 'account/notice', message: t('account.security.password.reset_sent') });
  } else {
    send({ type: 'account/action-failed', message: result.error });
  }
}

async function accountRecoveryEmailSave(
  send: (msg: Msg) => void,
  recoveryEmail: string | null,
  currentPassword: string,
): Promise<void> {
  const result = await updateRecoveryEmail(recoveryEmail, currentPassword);
  if (result.ok) {
    send({ type: 'account/recovery-email-saved', recoveryEmail: result.value.recovery_email });
  } else {
    send({ type: 'account/action-failed', message: result.error });
  }
}

async function accountDelete(send: (msg: Msg) => void): Promise<void> {
  const result = await apiDeleteAccount();
  if (result.ok) {
    // Account marked for deletion + session revoked server-side. Full reload
    // to /sign-in so every module-scope cache (replica, sync client) clears.
    if (typeof window !== 'undefined') {
      window.location.assign('/sign-in');
    }
  } else {
    send({ type: 'account/action-failed', message: result.error });
  }
}

async function bootSession(send: (msg: Msg) => void): Promise<void> {
  if (typeof window === 'undefined' || typeof fetch === 'undefined') {
    send({ type: 'session/loaded', session: LEGACY_GM_SESSION });
    return;
  }
  // If we are already on an unauthenticated auth route, skip the
  // session probe entirely — the user is deliberately on /sign-in or
  // similar and we should not flicker the GM shell behind it.
  const current = initialRoute();
  if (isAuthRoute(current)) {
    return;
  }
  // #84 / #89 — the public info pages (/a11y, /recovery) are reachable
  // without a session; skip the boot probe so a direct hit renders the
  // page instead of bouncing an unauthenticated visitor to /sign-in.
  if (
    current.kind === 'a11y' ||
    current.kind === 'recovery' ||
    current.kind === 'ui-gallery'
  ) {
    return;
  }
  // The legacy demo campaign has no membership row; skip the boot
  // fetch so the Walking-Skeleton flow doesn't pay a round-trip for
  // nothing.
  if (CAMPAIGN_ID === 'demo_campaign') {
    send({ type: 'session/loaded', session: LEGACY_GM_SESSION });
    return;
  }
  // No campaign selected. Hit GET /v1/account and route the shell
  // into the campaign picker / create-first flow.
  if (CAMPAIGN_ID === null) {
    await bootChooseCampaign(send);
    return;
  }
  try {
    const res = await fetch(
      `${workerOrigin()}/v1/me/campaigns/${encodeURIComponent(CAMPAIGN_ID)}/session`,
      { credentials: 'include' },
    );
    if (!res.ok) {
      if (res.status === 401) {
        // #117 — real auth path: redirect to /sign-in unless the
        // legacy GM session demo escape hatch is enabled.
        if (!LEGACY_GM_SESSION_ENABLED) {
          // Push the path so the URL reflects the route; the model.route
          // flips through the explicit navigate Msg below (history-mode
          // pushState doesn't fire the router listener, which is fine here).
          if (typeof window !== 'undefined') {
            window.history.pushState({}, '', '/sign-in');
          }
          send({ type: 'navigate', route: { kind: 'sign-in' } });
          return;
        }
        send({ type: 'session/loaded', session: LEGACY_GM_SESSION });
        return;
      }
      if (res.status === 403 || res.status === 404) {
        // Stale localStorage entry: the user persisted a campaign
        // they're no longer a member of (kicked, deleted, never existed).
        // Clear the active id and fall back to the picker so they can
        // pick again instead of staring at a "boot failed" wall.
        clearActiveCampaignId();
        CAMPAIGN_ID = null;
        await bootChooseCampaign(send);
        return;
      }
      const text = await res.text();
      send({ type: 'session/error', message: `boot failed (${res.status}): ${text}` });
      return;
    }
    const body = (await res.json()) as {
      user: { id: string; email: string; is_system_admin?: boolean };
      viewer: { kind: 'gm' | 'character' | 'party' | 'user'; id: string };
      controlled_characters: string[];
      campaign_role: 'owner' | 'admin' | 'member';
      ws_token: string;
      session_cursor?: number | null;
      session_cursor_calendar?: string | null;
    };
    activeWsToken = body.ws_token;
    const mode: 'gm' | 'player' = body.viewer.kind === 'gm' ? 'gm' : 'player';
    send({
      type: 'session/loaded',
      session: {
        userId: body.user.id,
        userEmail: body.user.email,
        viewer: body.viewer,
        campaignRole: body.campaign_role,
        controlledCharacters: body.controlled_characters,
        mode,
        isSystemAdmin: body.user.is_system_admin === true,
      },
    });
    // Hydrate the Campaign date from the server (06-timeline.md §13).
    // Server is the source of truth across devices / co-GMs; this
    // overrides the localStorage seed init() applied. Sent even when
    // null so a server-side clear propagates.
    send({
      type: 'cursor/hydrate',
      cursor: body.session_cursor ?? null,
      calendarId: body.session_cursor_calendar ?? null,
    });
    // Populate the switcher state in the background so the header
    // dropdown lists every campaign the user can access. This is a
    // fire-and-forget supplement to bootSession; failure leaves the
    // switcher hidden but doesn't break the main shell.
    void fetchSwitcherList(send);
    // And mark this campaign as last-active so the picker sorts it
    // to the top on the next sign-in.
    if (CAMPAIGN_ID !== null) {
      touchLastActive(CAMPAIGN_ID);
    }
  } catch (error) {
    send({
      type: 'session/error',
      message: `boot threw: ${error instanceof Error ? error.message : String(error)}`,
    });
  }
}

/**
 * Fetch the campaign list for the switcher dropdown. Runs after
 * bootSession's `session/loaded` dispatch so the header already has
 * a session. Failures are silent — the switcher stays hidden until
 * the next successful list-fetch.
 */
async function fetchSwitcherList(send: (msg: Msg) => void): Promise<void> {
  const result = await apiListCampaigns();
  if (!result.ok) return;
  send({ type: 'campaigns-boot/loaded', campaigns: result.value.campaigns });
}

async function loadPrivateNote(send: (msg: Msg) => void, entityId: string): Promise<void> {
  if (typeof fetch === 'undefined') return;
  const campaignId = CAMPAIGN_ID;
  if (campaignId === null) return;
  try {
    const res = await fetch(
      `${workerOrigin()}/v1/campaigns/${encodeURIComponent(campaignId)}/private-notes/${encodeURIComponent(entityId)}`,
      { credentials: 'include' },
    );
    if (!res.ok) return;
    const body = (await res.json()) as { body: string; updated_at: string | null };
    send({
      type: 'private-note/loaded',
      entityId,
      body: body.body ?? '',
      updatedAt: body.updated_at,
    });
  } catch {
    // ignore — keep the local model untouched
  }
}

/**
 * Fetch the Campaign's members for the controller picker (III.B.4). GM-gated
 * server-side; failures are silent (the picker just shows the empty state).
 */
async function loadCampaignMembers(send: (msg: Msg) => void): Promise<void> {
  if (typeof fetch === 'undefined') return;
  const campaignId = CAMPAIGN_ID;
  if (campaignId === null) return;
  try {
    const res = await fetch(
      `${workerOrigin()}/v1/campaigns/${encodeURIComponent(campaignId)}/members`,
      { credentials: 'include' },
    );
    if (!res.ok) return;
    const body = (await res.json()) as {
      members?: { user_id: string; email: string; role: string; is_owner?: boolean }[];
    };
    send({ type: 'campaign-members/loaded', members: body.members ?? [] });
  } catch {
    // ignore — keep the local model untouched
  }
}

// ─── #263 — Roster + invite effect implementations ────────────────────────────

async function loadInviteState(send: (msg: Msg) => void): Promise<void> {
  if (typeof fetch === 'undefined') return;
  const campaignId = CAMPAIGN_ID;
  if (campaignId === null) return;
  try {
    const res = await fetch(
      `${workerOrigin()}/v1/campaigns/${encodeURIComponent(campaignId)}/invites`,
      { credentials: 'include' },
    );
    if (!res.ok) return;
    const body = (await res.json()) as {
      email_invites?: { id: string; invited_email: string; role: string; expires_at: string }[];
      links?: { id: string; role: string; created_at: string; expires_at: string | null; use_count: number }[];
    };
    send({
      type: 'roster/invites-loaded',
      emailInvites: body.email_invites ?? [],
      links: body.links ?? [],
    });
  } catch {
    // ignore
  }
}

async function sendEmailInvite(
  send: (msg: Msg) => void,
  email: string,
  role: 'gm' | 'player',
): Promise<void> {
  if (typeof fetch === 'undefined') return;
  const campaignId = CAMPAIGN_ID;
  if (campaignId === null) return;
  try {
    await fetch(
      `${workerOrigin()}/v1/campaigns/${encodeURIComponent(campaignId)}/invites/email`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email, role }),
      },
    );
    send({ type: 'roster/invite-email-sent' });
  } catch {
    send({ type: 'roster/invite-email-sent' }); // reset even on error
  }
}

async function revokeEmailInvite(send: (msg: Msg) => void, inviteId: string): Promise<void> {
  if (typeof fetch === 'undefined') return;
  const campaignId = CAMPAIGN_ID;
  if (campaignId === null) return;
  try {
    await fetch(
      `${workerOrigin()}/v1/campaigns/${encodeURIComponent(campaignId)}/invites/email/${encodeURIComponent(inviteId)}`,
      { method: 'DELETE', credentials: 'include' },
    );
    void loadInviteState(send);
  } catch {
    // ignore
  }
}

async function createInviteLink(send: (msg: Msg) => void, role: 'gm' | 'player'): Promise<void> {
  if (typeof fetch === 'undefined') return;
  const campaignId = CAMPAIGN_ID;
  if (campaignId === null) return;
  try {
    await fetch(
      `${workerOrigin()}/v1/campaigns/${encodeURIComponent(campaignId)}/invites/link`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ role }),
      },
    );
    void loadInviteState(send);
  } catch {
    // ignore
  }
}

async function closeInviteLink(send: (msg: Msg) => void, linkId: string): Promise<void> {
  if (typeof fetch === 'undefined') return;
  const campaignId = CAMPAIGN_ID;
  if (campaignId === null) return;
  try {
    await fetch(
      `${workerOrigin()}/v1/campaigns/${encodeURIComponent(campaignId)}/invites/link/${encodeURIComponent(linkId)}`,
      { method: 'DELETE', credentials: 'include' },
    );
    void loadInviteState(send);
  } catch {
    // ignore
  }
}

async function removeMember(send: (msg: Msg) => void, userId: string): Promise<void> {
  if (typeof fetch === 'undefined') return;
  const campaignId = CAMPAIGN_ID;
  if (campaignId === null) return;
  try {
    await fetch(
      `${workerOrigin()}/v1/campaigns/${encodeURIComponent(campaignId)}/members/${encodeURIComponent(userId)}`,
      { method: 'DELETE', credentials: 'include' },
    );
    // Reload the full member list after removal.
    void loadCampaignMembers(send);
  } catch {
    // ignore
  }
}

// ─── end of #263 effect implementations ──────────────────────────────────────

async function savePrivateNote(
  send: (msg: Msg) => void,
  entityId: string,
  body: string,
): Promise<void> {
  if (typeof fetch === 'undefined') return;
  const campaignId = CAMPAIGN_ID;
  if (campaignId === null) return;
  try {
    const res = await fetch(
      `${workerOrigin()}/v1/campaigns/${encodeURIComponent(campaignId)}/private-notes/${encodeURIComponent(entityId)}`,
      {
        method: 'PUT',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ body }),
      },
    );
    if (!res.ok) return;
    const data = (await res.json()) as { updated_at: string };
    send({ type: 'private-note/saved', entityId, updatedAt: data.updated_at });
  } catch {
    // ignore — surface only on user action
  }
}

function announceMode(mode: 'gm' | 'player'): void {
  if (typeof document === 'undefined') return;
  const id = 'dl-mode-announcer';
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement('div');
    el.id = id;
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    el.style.position = 'absolute';
    el.style.left = '-10000px';
    el.style.width = '1px';
    el.style.height = '1px';
    el.style.overflow = 'hidden';
    document.body.appendChild(el);
  }
  el.textContent = mode === 'gm' ? t('mode.announce_gm') : t('mode.announce_player');
}

async function openSubstrate(send: (msg: Msg) => void, viewer: ViewerId): Promise<void> {
  if (typeof window === 'undefined' || typeof Worker === 'undefined') {
    // Non-browser env (the vitest happy-dom partial impl). The UI still
    // renders with an empty in-memory cache; tests don't depend on the
    // substrate.
    return;
  }
  // No campaign, no substrate. The shell is sitting on the
  // campaign picker; the SyncClient has nothing to connect to.
  if (CAMPAIGN_ID === null) return;
  const campaignId = CAMPAIGN_ID;
  // Bug 2 — `?dl_replica=memory` query-string lets the Playwright suite
  // exercise the in-memory fallback without a browser-version mock.
  // Production builds ignore the flag (the URL doesn't carry it); dev
  // builds honour it so the banner test stays cheap to write. Read from
  // the module-eval snapshot (`FORCE_MEMORY_REPLICA`) rather than the live
  // URL — by the time this async boot effect runs, the URL has been
  // normalized and the query string is gone (see the snapshot's comment).
  const forceMemory = FORCE_MEMORY_REPLICA;
  // Scope the local replica to THIS campaign. The replica is a fold of
  // an op log; a single shared OPFS db (the Walking-Skeleton default)
  // would accumulate every opened campaign's ops into one log and the
  // fold would mix them — entities from one campaign bleeding into
  // another. A per-campaign filename gives each Campaign its own local
  // op log, matching the per-Campaign DO on the server. Sanitised to a
  // safe OPFS filename.
  const replicaFilename = `/dl-${campaignId.replace(/[^a-zA-Z0-9_-]/g, '_')}.db`;
  try {
    // Bug 2 — `openBrowserReplica` returns `{ remote, persistent, mode, opfsError? }`
    // so we can render a "Storage non-persistent" banner when OPFS fell
    // back to `:memory:` AND surface the original error message in the
    // "Why?" dialog.
    const { remote, persistent, mode, opfsError } = await openBrowserReplica({
      forceMemory,
      filename: replicaFilename,
    });
    replica = remote as unknown as typeof replica;
    // Dev-only diagnostic handle so the Playwright suite can dump the
    // op log when investigating "saved badge appeared but the fact
    // didn't land" failures. The handle is a Comlink proxy; production
    // builds skip this assignment so we don't leak the replica into
    // global scope.
    if ((import.meta as { env?: { DEV?: boolean } }).env?.DEV) {
      (window as unknown as { __dlReplica?: unknown }).__dlReplica = remote;
    }
    send({
      type: 'set-substrate-status',
      status: persistent
        ? { kind: 'persistent' }
        : opfsError !== undefined
          ? { kind: 'memory', opfsError }
          : { kind: 'memory' },
    });
    // Diagnostic log — useful when remote-debugging a Chrome-148-style
    // OPFS failure. Goes to console.info, not console.warn, on the
    // happy path; on memory mode we also dump the underlying OPFS
    // error so the cause is visible without opening DevTools deeper.
    if (opfsError !== undefined) {
      console.warn('[dl] substrate opened (memory fallback)', { mode, persistent, opfsError });
    } else {
      console.info('[dl] substrate opened', { mode, persistent });
    }
    const client = new SyncClient(remote as unknown as ReplicaLike, {
      campaignId,
      url: syncUrl(activeWsToken),
      viewerKey: viewerKeyFn(viewer),
      onConnectionChange: (online) => {
        send({
          type: 'set-connection',
          state: { online, pendingCount: 0, conflictCount: 0 },
        });
        // On (re)connect, reconcile the Campaign date so a client that
        // was offline during a change catches up without a full reload.
        // Role-aware in the reducer (GM re-pushes, player re-pulls).
        if (online) send({ type: 'cursor/reconnect-sync' });
      },
      onConflict: (conflict) => {
        send({ type: 'replica/conflict', conflict });
      },
      onInvariantRejected: (rejection) => {
        // The server refused this write as invalid (not a stale conflict). The
        // provisional op has been removed from the replica; re-fold so it
        // disappears, and surface the reason rather than swallowing it.
        console.error(
          `[sync] op ${rejection.client_op_id} rejected (${rejection.code}): ${rejection.message}`,
        );
        scheduleRebuild();
        signalSyncActivity(send);
      },
      onLocalOpRemoved: () => {
        // A rejected provisional op was demoted out of the fold — re-fold so
        // the view stops showing the write the server refused.
        scheduleRebuild();
      },
      onOpApplied: () => {
        scheduleRebuild();
        signalSyncActivity(send);
      },
      onVisibilityAdded: (envelope: VisibilityAddedEnvelope) => {
        const msg: {
          type: 'visibility/added';
          subject: string;
          predicate: string;
          value: unknown;
          pending?: true;
          version?: string;
        } = {
          type: 'visibility/added',
          subject: envelope.subject,
          predicate: envelope.predicate,
          value: envelope.value,
        };
        if (envelope.pending) msg.pending = true;
        // Chain head op-id — the client edits conflict-aware against it (§7.2).
        if (envelope.version !== undefined) msg.version = envelope.version;
        send(msg);
      },
      onVisibilityRemoved: (envelope) => {
        send({
          type: 'visibility/removed',
          subject: envelope.subject,
          predicate: envelope.predicate,
        });
      },
      // ADR-0005: the live Campaign-date relay is gone — "now" now rides the
      // op log as `config.set session_cursor`, so a co-GM's advance arrives via
      // the normal op-push fold (`replica/entities-rebuilt` re-hydrates
      // `campaignCursor` from `config.session_cursor`). No bespoke WS relay.
    });
    syncClient = client;
    // Substrate handles are set; UI effect handlers waiting on
    // `awaitSubstrate()` can proceed. We resolve BEFORE
    // `client.connect()` so a slow WS handshake doesn't block local
    // appendLocal — the optimistic-local-fold path doesn't need an
    // active socket.
    markSubstrateReady();
    await client.connect();
    // Cover the case where the initial backfill produced no ops: still do
    // one rebuild so the cache reflects whatever the replica already
    // persisted from a previous session.
    scheduleRebuild();
  } catch (error) {
    // Bug 3 — OPFS-failure now triggers the in-memory fallback INSIDE
    // `openBrowserReplica`, so reaching this arm means a more serious
    // problem (sqlite-wasm load failure, missing worker module, blocked
    // WS handshake before the SyncClient could even attach). Surface
    // both the offline pill (general connection signal) AND the
    // catastrophic substrate banner — saves cannot happen.
    console.warn('[dl] substrate open failed', error);
    const rawMessage = error instanceof Error ? error.message : String(error);
    // Cap the rendered error so a runaway stack-y message can't blow out
    // the banner dialog; the full text is still in the console for the
    // developer. 500 chars comfortably fits the longest sqlite-wasm /
    // OPFS failure we've seen plus the combined-error wrapper.
    const message = rawMessage.length > 500 ? `${rawMessage.slice(0, 500)}…` : rawMessage;
    const errorName =
      error instanceof Error && typeof error.name === 'string' && error.name.length > 0
        ? error.name
        : undefined;
    send({
      type: 'set-substrate-status',
      status:
        errorName !== undefined
          ? { kind: 'failed', error: message, errorName }
          : { kind: 'failed', error: message },
    });
    send({
      type: 'set-connection',
      state: { online: false, pendingCount: 0, conflictCount: 0 },
    });
    // Catastrophic open: still resolve the barrier so pending writes
    // get the correct "no substrate" error instead of hanging on a
    // promise that will never resolve.
    markSubstrateReady();
  }
}

/**
 * Append ONE op to a `(subject, predicate)` chain, serialized against every
 * other writer to that same chain.
 *
 * This is the single chokepoint every op-log append in this file goes through,
 * and it exists because appending is a read-modify-write: `base_version` is the
 * chain's current head, so two appends that both read the head before either
 * has written fork the chain. The DO then correctly arbitrates the loser away
 * as a stale write, `removeLocalOp` drops it, and the fold reverts — the user
 * watches their edit land and silently undo itself a moment later.
 *
 * Serializing each function against only ITSELF is not enough, which is why
 * this is a shared helper rather than a wrapper at each call site: fact chains
 * are written by `submitFact`, `submitFactStatus` and three LLM draft paths,
 * and `__entity` is shared by create / attach-trait / soft-delete / restore.
 * Any pair of those racing on one chain loses a write.
 *
 * `build` receives the head to stamp as `base_version`; returning null abandons
 * the append (the caller has already surfaced why). Returns whether an op was
 * appended, so callers can gate their own post-append ACK.
 *
 * The chain is released on the LOCAL append — the moment `getHead` can see the
 * op, which is all a successor needs — never on the server ACK. That keeps
 * every path offline-first: a queued write must not block the next edit for a
 * round-trip, or forever while offline.
 */
async function appendToChain(
  subject: string,
  predicate: string,
  build: (baseVersion: OpId | null) => SubmittedOp<unknown> | null | Promise<SubmittedOp<unknown> | null>,
): Promise<boolean> {
  const substrate = replica;
  const sync = syncClient;
  if (!substrate || !sync) return false;
  let appended = false;
  await sequenceByKey(opChainKey(subject, predicate), async () => {
    const op = await build(await substrate.getHead(subject, predicate));
    if (op === null) return;
    appended = true;
    // `settled` is a belt-and-braces release for the case where `submit` throws
    // before appending, so one failed write cannot wedge the chain forever.
    await new Promise<void>((release) => {
      const settled = sync.submit(op, () => release());
      settled.then(
        () => release(),
        () => release(),
      );
    });
  });
  return appended;
}

async function submitEntityCreate(eff: SubmitEntityCreateEffect): Promise<void> {
  await awaitSubstrate();
  // `base_version: null` is correct — this op OPENS the chain. It still takes
  // the chain key so the attach-trait / rename that typically follows within
  // the same frame waits for this local append instead of racing it.
  await appendToChain(eff.subject, ENTITY_META_PREDICATE, () => ({
    subject: eff.subject,
    predicate: ENTITY_META_PREDICATE,
    payload: entityCreatePayload(eff.root, eff.traits),
    base_version: null,
    author: GM_AUTHOR,
    created_at_real: nowIso(),
  }));
}

/**
 * #259 — build the `bundle.declare` op for the client-emitted combat-log path.
 *
 * Exported for test, because the routing rule it has to satisfy is invisible
 * from here and fatal when broken: `assertBundleOpValid` in the DO requires
 * `payload.bundle_id === op.subject` (`apps/do-campaign/src/append.ts` — "the
 * subject is the addressable id"). This used to put the reserved `__bundle`
 * predicate in BOTH slots, so every GM-authored declare was refused outright
 * with an `OpInvariantError` — not sometimes, always. The combat-log facts then
 * landed tagged with a `bundle_id` whose declaration did not exist, leaving the
 * #251 review UI nothing to group them under.
 *
 * `created_hlc` is provenance only — the DO re-stamps the canonical op HLC on
 * append. We supply a wall-clock approximation here (same approach as the
 * worker bulk-gen path in `apps/worker/src/ai/routes.ts`).
 */
export function buildBundleDeclareOp(
  eff: SubmitBundleDeclareEffect,
  createdHlc: { wall_ms: number; logical: number },
): SubmittedOp<unknown> {
  return {
    // The bundle's own `bun_…` id — each bundle is its own chain, which is also
    // what makes `base_version: null` right: this op opens it.
    subject: eff.bundleId,
    predicate: BUNDLE_META_PREDICATE,
    payload: bundleDeclare(
      eff.bundleId,
      eff.source,
      createdHlc,
      eff.prompt !== undefined ? { prompt: eff.prompt } : {},
    ),
    base_version: null,
    author: GM_AUTHOR,
    created_at_real: nowIso(),
  };
}

async function submitBundleDeclare(eff: SubmitBundleDeclareEffect): Promise<void> {
  await awaitSubstrate();
  if (!syncClient) return;
  await syncClient.submit(buildBundleDeclareOp(eff, { wall_ms: Date.now(), logical: 0 }));
}

/**
 * #139 — append `entity.attach_trait` or `entity.detach_trait` to the
 * op log. Both flow through the same `ENTITY_META_PREDICATE` slot as
 * `entity.create`; the substrate folds them idempotently.
 */
async function submitTraitMutation(eff: SubmitTraitMutationEffect): Promise<void> {
  await awaitSubstrate();
  await appendToChain(eff.subject, ENTITY_META_PREDICATE, (baseVersion) => ({
    subject: eff.subject,
    predicate: ENTITY_META_PREDICATE,
    payload:
      eff.op === 'attach' ? entityAttachTrait(eff.traitId) : entityDetachTrait(eff.traitId),
    base_version: baseVersion,
    author: GM_AUTHOR,
    created_at_real: nowIso(),
  }));
}

/** Append `entity.soft_delete` (reversible Trash). Same chain as create/attach. */
async function submitEntitySoftDelete(eff: SubmitEntitySoftDeleteEffect): Promise<void> {
  await awaitSubstrate();
  await appendToChain(eff.subject, ENTITY_META_PREDICATE, (baseVersion) => ({
    subject: eff.subject,
    predicate: ENTITY_META_PREDICATE,
    payload: entitySoftDelete(),
    base_version: baseVersion,
    author: GM_AUTHOR,
    created_at_real: nowIso(),
  }));
}

/** Append `entity.restore` (un-hide from Trash). Same chain as soft-delete. */
async function submitEntityRestore(eff: SubmitEntityRestoreEffect): Promise<void> {
  await awaitSubstrate();
  await appendToChain(eff.subject, ENTITY_META_PREDICATE, (baseVersion) => ({
    subject: eff.subject,
    predicate: ENTITY_META_PREDICATE,
    payload: entityRestore(),
    base_version: baseVersion,
    author: GM_AUTHOR,
    created_at_real: nowIso(),
  }));
}


/**
 * #73 / ADR-0001 — append the homebrew Pack's definition meta-ops. All
 * pack ops share the single `(HOMEBREW_PACK_ID, PACK_META_PREDICATE)`
 * chain, so they MUST go one at a time: re-read the head before each so
 * the next op's `base_version` points at the prior op (no stale-conflict
 * against ourselves). The caller orders `payloads` predicates-first.
 */
async function submitPackOps(eff: SubmitPackOpsEffect): Promise<void> {
  await awaitSubstrate();
  // One op at a time, each stamped with the head the previous one produced.
  // `appendToChain` supplies both halves of that: it re-reads the head inside
  // the critical section, and it releases on the LOCAL append, so the next
  // iteration observes this op without waiting a server round-trip.
  //
  // Awaiting the server ACK per op (what this did before) meant a trait save
  // cost one full round-trip per definition it wrote — seconds on a loaded box,
  // during which the homebrew overlay still served the pre-save vocabulary and
  // the trait editor, which seeds its inputs once at mount, could capture it.
  for (const payload of eff.payloads) {
    await appendToChain(HOMEBREW_PACK_ID, PACK_META_PREDICATE, (baseVersion) => ({
      subject: HOMEBREW_PACK_ID,
      predicate: PACK_META_PREDICATE,
      payload,
      base_version: baseVersion,
      author: GM_AUTHOR,
      created_at_real: nowIso(),
    }));
  }
}

/**
 * #186 / ADR-0004 §5 — submit one campaign `config.set` op. Rides the
 * reserved `__campaign` subject; the predicate is the config key so each
 * setting is its own LWW chain (its `base_version` tracks only that key —
 * a no-silent-overwrite path the DO arbitrates, though config legitimately
 * opts out of draft-with-conflict: settings are GM operational preferences,
 * not canon facts). Offline-first like `submitFact`: we don't await the
 * server ACK; the optimistic local fold surfaces the new value immediately.
 */
async function submitConfig(eff: SubmitConfigEffect): Promise<void> {
  await awaitSubstrate();
  const predicate = eff.payload.key;

  // A composite setting commits its whole object under one key, so a GM filling
  // two fields of `gm_pref_profile` in quick succession fires two writes to the
  // same chain. Unserialized, the second read the head before the first had
  // appended, both carried the same `base_version`, and the DO arbitrated the
  // second away as stale — the edit vanished with no error surfaced, since
  // config opts out of draft-with-conflict. This is where that hazard was first
  // found (e2e `30-gm-pref-profile.spec.ts` 30.1: `tone` persisted,
  // `content_boundaries` did not); `appendToChain` now closes it everywhere.
  await appendToChain(CAMPAIGN_CONFIG_SUBJECT, predicate, (baseVersion) => ({
    subject: CAMPAIGN_CONFIG_SUBJECT,
    predicate,
    payload: eff.payload,
    base_version: baseVersion,
    author: GM_AUTHOR,
    created_at_real: nowIso(),
  }));
}

/**
 * Per-attribute reveal (Phase 2 / 30 §7.7) — append a write-once `reveal`
 * op. The wire envelope's `subject` is the entity, its `predicate` is the
 * synthetic `reveal:<targetPredicate>:<viewerKey>` slot (so each reveal is
 * its own independent chain), and the payload carries the real target
 * predicate + viewer. `base_version: null` — reveals never conflict. Offline-
 * first like `submitFact`: we don't await the server ACK; the optimistic
 * local fold surfaces the new reveal to the chip + popover immediately.
 */
async function submitReveal(eff: SubmitRevealEffect): Promise<void> {
  await awaitSubstrate();
  if (!replica || !syncClient) return;
  const op: SubmittedOp<unknown> = {
    subject: eff.subject,
    predicate: revealEnvelopePredicate(eff.predicate, eff.viewer),
    payload: revealFactPayload(eff.viewer, eff.predicate, eff.source),
    base_version: null,
    author: GM_AUTHOR,
    created_at_real: nowIso(),
  };
  void syncClient.submit(op);
}

async function submitFact(
  eff: SubmitFactEffect,
  send: (msg: Msg) => void,
): Promise<void> {
  // Wait for the substrate-open barrier (resolves once `replica` +
  // `syncClient` are set, OR once the open failed). Without this, a
  // user-initiated write that arrived during the boot race
  // — `session/loaded` → `open-substrate` → `openSubstrate()` is
  // still running — saw `replica === null` and fired the "no
  // substrate" error for what was really a timing issue. The
  // Playwright e2e (09 / 9.2 in headless Chromium) caught this
  // consistently.
  await awaitSubstrate();
  // Bug 1 — short-circuit when the substrate isn't open. Earlier code
  // silently early-returned here while the reducer had already stamped
  // "Saved ✓"; the GM saw a success cue for a write that never landed.
  // Now the reducer waits for `fact-actually-saved`, and we surface
  // `fact-save-failed` so the row keeps the dirty flag and the inline
  // error explains why.
  if (!replica || !syncClient) {
    send({
      type: 'fact-save-failed',
      entityId: eff.subject,
      predicate: eff.predicate,
      error: t('substrate.save_failed_no_substrate'),
    });
    return;
  }
  const substrate = replica;
  try {
    // Appending is a read-modify-write; `appendToChain` serializes it against
    // every other writer to this fact's chain. That is load-bearing, not
    // defensive: unserialized, creating an entity and immediately renaming it
    // fired two writes on its `name` chain inside one frame, both naming the
    // same predecessor. The DO arbitrated the second as a stale write and the
    // name REVERTED to what the create modal set ~150 ms later. Setting a
    // validity bound in that window was the same story — the picker commits
    // several writes and all but one were arbitrated away, so the bound snapped
    // back to "Always". (Found via e2e 3.17, which sampled the chip while the
    // doomed write was still on screen — so it passed on desktop and failed on
    // mobile, and the mobile failure was never a mobile bug.)
    const appended = await appendToChain(eff.subject, eff.predicate, async (chainHead) => {
      // §7.2: a player editing a projected fact supplies the projected chain head
      // explicitly (its value is not in the replica, so the head read is null).
      // The GM path takes the head `appendToChain` just read.
      const baseVersion =
        eff.baseVersionOverride !== undefined ? eff.baseVersionOverride : chainHead;
      let payload: unknown;
      switch (eff.payload.kind) {
        case 'fact.create': {
          const meta = {
            ...(eff.payload.validity !== undefined ? { validity: eff.payload.validity } : {}),
            ...(eff.payload.owner !== undefined ? { owner: eff.payload.owner } : {}),
            ...(eff.payload.source_belief !== undefined
              ? { source_belief: eff.payload.source_belief }
              : {}),
            ...(eff.payload.bundle_id !== undefined ? { bundle_id: eff.payload.bundle_id } : {}),
            ...(eff.payload.status !== undefined ? { status: eff.payload.status } : {}),
          };
          payload = factCreate(eff.payload.value, meta);
          break;
        }
        case 'fact.edit': {
          // Forward validity verbatim when present — including explicit
          // `null` (clear to timeless). Only an *absent* field means
          // "preserve"; `!== undefined` keeps null in the meta so the
          // fold sees the clear signal. `owner` likewise replaces when present.
          const meta = {
            ...(eff.payload.validity !== undefined ? { validity: eff.payload.validity } : {}),
            ...(eff.payload.owner !== undefined ? { owner: eff.payload.owner } : {}),
            ...(eff.payload.source_belief !== undefined
              ? { source_belief: eff.payload.source_belief }
              : {}),
            ...(eff.payload.bundle_id !== undefined ? { bundle_id: eff.payload.bundle_id } : {}),
            ...(eff.payload.status !== undefined ? { status: eff.payload.status } : {}),
          };
          payload = factEdit(eff.payload.value, meta);
          break;
        }
        case 'fact.set-validity': {
          // Resolve the value from the REPLICA (authoritative, synchronous) rather
          // than from the caller's asynchronously-refolded view — see the variant's
          // doc on SubmitFactEffect. Fold the chain with the shared entity fold so
          // current-value rules (latest version, soft-delete) are never re-derived
          // here.
          // The fold DROPS fact ops for an entity it hasn't seen created
          // (`entities-fold.ts` — "a fact op for a not-yet-created entity"), so the
          // entity's `__entity` chain has to go in alongside the fact's own.
          const [meta, chain] = await Promise.all([
            substrate.getChainOps(eff.subject, ENTITY_META_PREDICATE),
            substrate.getChainOps(eff.subject, eff.predicate),
          ]);
          // Scoped to ONE chain, so the folded entity carries exactly one fact —
          // whatever key the fold derived for it (a repeatable predicate's
          // occurrence folds under `predicate#instance`, not the bare predicate).
          // Taking that sole entry avoids re-deriving the key rule here.
          const folded = foldOpsIntoEntities([...meta, ...chain])[eff.subject];
          const value = currentFactValue(Object.values(folded?.facts ?? {})[0]);
          if (value === undefined) {
            // The fact genuinely does not exist (or is soft-deleted) — there is no
            // value to carry, and a `fact.edit` inventing one would corrupt the
            // chain. Surface it instead of dropping it on the floor, which is the
            // bug this variant exists to kill.
            send({
              type: 'fact-save-failed',
              entityId: eff.subject,
              predicate: eff.predicate,
              error: t('substrate.save_failed_no_fact'),
            });
            return null;
          }
          payload = factEdit(value, { validity: eff.payload.validity });
          break;
        }
        case 'fact.soft_delete':
          payload = factSoftDelete();
          break;
      }
      const op: SubmittedOp<unknown> = {
        subject: eff.subject,
        predicate: eff.predicate,
        payload,
        base_version: baseVersion,
        author: GM_AUTHOR,
        created_at_real: nowIso(),
      };
      return op;
    });
    // Only ACK a write that actually happened — the set-validity branch above
    // abandons the append when the fact has no value to carry.
    if (appended) {
      send({
        type: 'fact-actually-saved',
        entityId: eff.subject,
        predicate: eff.predicate,
        ...(eff.dirtyEpoch !== undefined ? { dirtyEpoch: eff.dirtyEpoch } : {}),
      });
    }
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    send({
      type: 'fact-save-failed',
      entityId: eff.subject,
      predicate: eff.predicate,
      error: t('substrate.save_failed_generic', { error: message }),
    });
  }
}

/**
 * LLM author mode (Phase B) — ask the worker to draft prose, then submit
 * the result as a draft-status fact authored by the LLM. The substrate
 * accepts llm+draft (the DO blocks llm canon/pending), and the draft
 * surfaces in the Draft Inbox. We do NOT await the server ACK (offline-
 * first, like submitFact); the optimistic local fold reveals the draft.
 */
interface SuggestProseResponse {
  text: string;
  provenance?: {
    provider_id?: string;
    model_id?: string;
    prompt_hash?: string;
    request_id?: string;
  };
}
async function llmSuggestProse(
  eff: LlmSuggestProseEffect,
  send: (msg: Msg) => void,
): Promise<void> {
  await awaitSubstrate();
  const fail = (error: string): void =>
    send({ type: 'llm-suggest-failed', entityId: eff.subject, predicate: eff.predicate, error });
  if (!replica || !syncClient) return fail(t('llm.draft_no_substrate'));
  const cid = CAMPAIGN_ID;
  if (cid === null) return fail(t('llm.draft_no_campaign'));
  try {
    // Streaming: tokens arrive live (preview popover); only the FINAL text
    // is committed to the draft on the `done` event.
    const res = await fetch(
      `${workerOrigin()}/v1/campaigns/${encodeURIComponent(cid)}/llm/suggest-prose/stream`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          subject: eff.subject,
          predicate: eff.predicate,
          ...(eff.instruction !== undefined ? { instruction: eff.instruction } : {}),
        }),
      },
    );
    if (!res.ok || !res.body) return fail(t('llm.draft_failed', { status: String(res.status) }));

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let finalText = '';
    let provenance: SuggestProseResponse['provenance'] = {};
    let errored = false;
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let sep: number;
      while ((sep = buffer.indexOf('\n\n')) >= 0) {
        const line = buffer.slice(0, sep).trim();
        buffer = buffer.slice(sep + 2);
        if (!line.startsWith('data:')) continue;
        let ev: { type: string; text?: string; provenance?: SuggestProseResponse['provenance']; error?: string };
        try {
          ev = JSON.parse(line.slice(5).trim());
        } catch {
          continue;
        }
        if (ev.type === 'thinking') {
          send({ type: 'llm-stream-thinking' });
        } else if (ev.type === 'delta' && typeof ev.text === 'string') {
          send({ type: 'llm-stream-delta', delta: ev.text });
        } else if (ev.type === 'done') {
          finalText = typeof ev.text === 'string' ? ev.text.trim() : '';
          provenance = ev.provenance ?? {};
        } else if (ev.type === 'error') {
          errored = true;
          fail(ev.error ?? t('llm.draft_failed_generic', { error: 'stream error' }));
        }
      }
    }
    if (errored) return;
    if (finalText === '') return fail(t('llm.draft_empty'));

    const prov = provenance ?? {};
    const author = llmAuthor({
      provider: prov.provider_id ?? 'unknown',
      model: prov.model_id ?? 'unknown',
      ...(prov.request_id !== undefined ? { request_id: prov.request_id } : {}),
      ...(prov.prompt_hash !== undefined ? { prompt_hash: prov.prompt_hash } : {}),
    });
    // Same chain the GM may be typing into right now — take the chain key so a
    // draft landing mid-edit queues behind that write instead of forking it.
    await appendToChain(eff.subject, eff.predicate, (baseVersion) => ({
      subject: eff.subject,
      predicate: eff.predicate,
      payload: factCreate(finalText, { status: 'draft' }),
      base_version: baseVersion,
      author,
      created_at_real: nowIso(),
    }));
    send({ type: 'llm-suggest-succeeded', entityId: eff.subject, predicate: eff.predicate });
  } catch (err) {
    fail(t('llm.draft_failed_generic', { error: err instanceof Error ? err.message : String(err) }));
  }
}

/**
 * Deferral 3 — structured multi-fact. One POST to draft-entity returns
 * several prose proposals; submit each as a draft fact. Per-key pending
 * is cleared via the existing succeeded/failed Msgs.
 */
interface DraftEntityResponse {
  proposals?: { predicate: string; value: string }[];
  provenance?: { provider_id?: string; model_id?: string; prompt_hash?: string; request_id?: string };
}
async function llmDraftEntity(eff: LlmDraftEntityEffect, send: (msg: Msg) => void): Promise<void> {
  await awaitSubstrate();
  const clearAll = (mk: (p: string) => Msg): void => {
    for (const p of eff.predicates) send(mk(p));
  };
  const failAll = (error: string): void =>
    clearAll((p) => ({ type: 'llm-suggest-failed', entityId: eff.subject, predicate: p, error }));
  if (!replica || !syncClient) return failAll(t('llm.draft_no_substrate'));
  const cid = CAMPAIGN_ID;
  if (cid === null) return failAll(t('llm.draft_no_campaign'));
  try {
    const res = await fetch(
      `${workerOrigin()}/v1/campaigns/${encodeURIComponent(cid)}/llm/draft-entity`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          subject: eff.subject,
          predicates: eff.predicates,
          ...(eff.instruction !== undefined ? { instruction: eff.instruction } : {}),
        }),
      },
    );
    if (!res.ok) return failAll(t('llm.draft_failed', { status: String(res.status) }));
    const data = (await res.json()) as DraftEntityResponse;
    const proposals = Array.isArray(data.proposals) ? data.proposals : [];
    const prov = data.provenance ?? {};
    const author = llmAuthor({
      provider: prov.provider_id ?? 'unknown',
      model: prov.model_id ?? 'unknown',
      ...(prov.request_id !== undefined ? { request_id: prov.request_id } : {}),
      ...(prov.prompt_hash !== undefined ? { prompt_hash: prov.prompt_hash } : {}),
    });
    const filled = new Set<string>();
    for (const proposal of proposals) {
      if (typeof proposal?.predicate !== 'string' || typeof proposal?.value !== 'string') continue;
      if (!eff.predicates.includes(proposal.predicate)) continue;
      await appendToChain(eff.subject, proposal.predicate, (baseVersion) => ({
        subject: eff.subject,
        predicate: proposal.predicate,
        payload: factCreate(proposal.value, { status: 'draft' }),
        base_version: baseVersion,
        author,
        created_at_real: nowIso(),
      }));
      filled.add(proposal.predicate);
      send({ type: 'llm-suggest-succeeded', entityId: eff.subject, predicate: proposal.predicate });
    }
    // Clear pending for any requested predicate the model didn't return.
    for (const p of eff.predicates) {
      if (!filled.has(p)) send({ type: 'llm-suggest-succeeded', entityId: eff.subject, predicate: p });
    }
  } catch (err) {
    failAll(t('llm.draft_failed_generic', { error: err instanceof Error ? err.message : String(err) }));
  }
}

/**
 * Ratify / shelve a draft — a GM-authored `fact.set_status` op. Kept
 * separate from `submitFact` so the dirty-epoch ACK contract there is
 * untouched. After the op folds, the inbox row reconciles out.
 */
async function submitFactStatus(eff: SubmitFactStatusEffect): Promise<void> {
  await awaitSubstrate();
  // Three GM-authored shapes, all → canon and all GM_AUTHOR (the #223 backdoor
  // stays closed):
  //   - reviewState present (#251 auto-promote) → one fact.edit carries
  //     (value + status + review_state) since fact.set_status is .strict();
  //   - restoreValue present (lossless reject / review-pass) → one fact.edit
  //     carries (value + status), restoring the shadowed canon / clearing the
  //     review marker by omitting review_state;
  //   - otherwise → a plain fact.set_status (ratify).
  const payload =
    eff.reviewState !== undefined
      ? factEdit(eff.reviewValue?.value, { status: eff.status, review_state: eff.reviewState })
      : eff.restoreValue
        ? factEdit(eff.restoreValue.value, { status: eff.status })
        : factSetStatus(eff.status, eff.reason);
  await appendToChain(eff.subject, eff.predicate, (baseVersion) => ({
    subject: eff.subject,
    predicate: eff.predicate,
    payload,
    base_version: baseVersion,
    author: GM_AUTHOR,
    created_at_real: nowIso(),
  }));
}

/**
 * #251 — POST the worker's bulk-gen route. The worker grounds + runs ONE LLM
 * pass + caps fan-out + ATOMICALLY appends the bundle (`bundle.declare` + N
 * llm-draft `fact.create` + entity stubs) via the DO `appendBundle` RPC. The
 * client appends NOTHING here — the drafts arrive via the sync broadcast and
 * fold into `bundles`. Dispatches `bundle-gen-done` on success (then the inbox
 * shows the new bundle) / `bundle-gen-failed` (incl. the #218 over-budget
 * refusal) otherwise.
 */
async function bulkGen(eff: BulkGenEffect, send: (msg: Msg) => void): Promise<void> {
  const cid = CAMPAIGN_ID;
  if (cid === null) {
    send({ type: 'bundle-gen-failed', error: t('llm.draft_no_campaign') });
    return;
  }
  try {
    const res = await fetch(
      `${workerOrigin()}/v1/campaigns/${encodeURIComponent(cid)}/llm/bulk-gen`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        // `regenerated_from` is recorded on the worker-minted bundle.declare;
        // the worker reads it off the body (the request schema ignores extras
        // it doesn't model, so we pass it as a sibling field).
        body: JSON.stringify(
          eff.regeneratedFrom !== undefined
            ? { ...eff.request, regenerated_from: eff.regeneratedFrom }
            : eff.request,
        ),
      },
    );
    if (!res.ok) {
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      send({ type: 'bundle-gen-failed', error: data.error ?? t('llm.draft_failed', { status: String(res.status) }) });
      return;
    }
    send({ type: 'bundle-gen-done' });
  } catch (err) {
    send({ type: 'bundle-gen-failed', error: err instanceof Error ? err.message : String(err) });
  }
}

// ── ADR-0008 §6 (T7, #281) LLM-led interactive authoring effects ──────────────

/**
 * The in-flight authoring `/turn` SSE controller, module-scoped so a new turn or
 * a `close-authoring` can abort the previous stream (ADR-0008 §6 — cancellable).
 */
let authoringTurnAbort: AbortController | null = null;

/**
 * ADR-0008 §6 (T7, #281) — open an authoring session. POST `/llm/authoring/
 * session` with `{ target }`; the DO mints the scratch session and returns
 * `{ session_id }`. Dispatches `authoring-session-created` / `authoring-error`.
 */
async function openAuthoringSession(
  eff: OpenAuthoringSessionEffect,
  send: (msg: Msg) => void,
): Promise<void> {
  const cid = CAMPAIGN_ID;
  if (cid === null) {
    send({ type: 'authoring-error', error: t('llm.draft_no_campaign') });
    return;
  }
  try {
    const res = await fetch(
      `${workerOrigin()}/v1/campaigns/${encodeURIComponent(cid)}/llm/authoring/session`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ target: eff.target }),
      },
    );
    if (!res.ok) {
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      send({
        type: 'authoring-error',
        error: data.error ?? t('llm.draft_failed', { status: String(res.status) }),
      });
      return;
    }
    const data = (await res.json()) as { session_id?: string };
    if (typeof data.session_id !== 'string' || data.session_id === '') {
      send({ type: 'authoring-error', error: t('llm.draft_failed', { status: String(res.status) }) });
      return;
    }
    send({ type: 'authoring-session-created', sessionId: data.session_id });
  } catch (err) {
    send({ type: 'authoring-error', error: err instanceof Error ? err.message : String(err) });
  }
}

/**
 * ADR-0008 §6 (T7, #281) — decode one SSE frame (`data: <json>` line(s) between
 * blank-line boundaries) into an {@link AuthoringServerEvent} and dispatch the
 * matching msg. Malformed / non-`data:` frames (comments, heartbeats) are
 * silently ignored.
 */
function dispatchAuthoringFrame(frame: string, send: (msg: Msg) => void): void {
  const dataLines = frame
    .split('\n')
    .filter((line) => line.startsWith('data:'))
    .map((line) => line.slice('data:'.length).replace(/^ /, ''));
  if (dataLines.length === 0) return;
  let event: AuthoringServerEvent;
  try {
    event = AuthoringServerEventSchema.parse(JSON.parse(dataLines.join('\n')));
  } catch {
    return;
  }
  switch (event.kind) {
    case 'token':
      send({ type: 'authoring-token', text: event.text });
      return;
    case 'tool':
      send(
        event.detail !== undefined
          ? { type: 'authoring-tool', name: event.name, status: event.status, detail: event.detail }
          : { type: 'authoring-tool', name: event.name, status: event.status },
      );
      return;
    case 'artifact_diff':
      // Only the `trait` kind is built today (ADR-0008 §3); ignore others.
      if (event.artifact.kind === 'trait') {
        send({ type: 'authoring-artifact-diff', artifact: event.artifact });
      }
      return;
    case 'assistant_message':
      send({ type: 'authoring-assistant-message', text: event.text });
      return;
    case 'turn_done':
      send({ type: 'authoring-turn-done' });
      return;
    case 'error':
      send({ type: 'authoring-error', error: event.message });
      return;
  }
}

/**
 * ADR-0008 §6 (T7, #281) — run one refine turn against a session by consuming
 * the `/turn` SSE stream. Reads `response.body` via a `ReadableStream` reader +
 * `TextDecoder`, buffers, splits on the `\n\n` frame boundary, and dispatches a
 * msg per {@link AuthoringServerEvent}. Cancellable: any prior stream is aborted
 * before this one starts, and `close-authoring` aborts via `cancel-authoring`.
 */
async function authoringTurn(eff: AuthoringTurnEffect, send: (msg: Msg) => void): Promise<void> {
  const cid = CAMPAIGN_ID;
  if (cid === null) {
    send({ type: 'authoring-error', error: t('llm.draft_no_campaign') });
    return;
  }
  // Cancel any still-running turn before opening a new stream.
  if (authoringTurnAbort) authoringTurnAbort.abort();
  const controller = new AbortController();
  authoringTurnAbort = controller;
  try {
    const res = await fetch(
      `${workerOrigin()}/v1/campaigns/${encodeURIComponent(cid)}/llm/authoring/${encodeURIComponent(eff.sessionId)}/turn`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        // The wire schema (AuthoringTurnRequest) carries both fields; the sid is
        // also in the path, but we send it in-body to satisfy the strict schema.
        body: JSON.stringify({ session_id: eff.sessionId, direction: eff.direction }),
        signal: controller.signal,
      },
    );
    if (!res.ok || res.body === null) {
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      send({
        type: 'authoring-error',
        error: data.error ?? t('llm.draft_failed', { status: String(res.status) }),
      });
      return;
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let boundary = buffer.indexOf('\n\n');
      while (boundary !== -1) {
        const frame = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);
        dispatchAuthoringFrame(frame, send);
        boundary = buffer.indexOf('\n\n');
      }
    }
    // Flush any trailing frame not terminated by a blank line.
    const tail = buffer.trim();
    if (tail !== '') dispatchAuthoringFrame(tail, send);
  } catch (err) {
    // A cancellation (new turn / close) aborts the fetch — swallow it.
    if (controller.signal.aborted) return;
    send({ type: 'authoring-error', error: err instanceof Error ? err.message : String(err) });
  } finally {
    if (authoringTurnAbort === controller) authoringTurnAbort = null;
  }
}

/**
 * ADR-0008 §5/§6 (T7, #281) — ratify the session's artifact. POST `/commit` with
 * `{ session_id }`; on `{ ok: true }` dispatch `authoring-commit-done` (close),
 * on `{ ok: false, advisories }` dispatch `authoring-commit-refused` (surface
 * the blocking auditor findings).
 */
async function commitAuthoring(
  eff: CommitAuthoringEffect,
  send: (msg: Msg) => void,
): Promise<void> {
  const cid = CAMPAIGN_ID;
  if (cid === null) {
    send({ type: 'authoring-error', error: t('llm.draft_no_campaign') });
    return;
  }
  try {
    const res = await fetch(
      `${workerOrigin()}/v1/campaigns/${encodeURIComponent(cid)}/llm/authoring/${encodeURIComponent(eff.sessionId)}/commit`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ session_id: eff.sessionId }),
      },
    );
    if (!res.ok) {
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      send({
        type: 'authoring-error',
        error: data.error ?? t('llm.draft_failed', { status: String(res.status) }),
      });
      return;
    }
    const data = AuthoringCommitResponseSchema.parse(await res.json());
    if (data.ok) {
      send({ type: 'authoring-commit-done' });
    } else {
      send({
        type: 'authoring-commit-refused',
        advisories: data.advisories ? [...data.advisories] : [],
      });
    }
  } catch (err) {
    send({ type: 'authoring-error', error: err instanceof Error ? err.message : String(err) });
  }
}

/**
 * Phase D — generate an image for an asset predicate, then submit the
 * asset id as a draft fact authored by the LLM. The signed URL comes
 * back from the route; we prefix it with workerOrigin() (the content
 * endpoint is token-gated, so a cross-origin <img> load needs no cookie).
 */
interface GenerateImageResponse {
  asset_id: string;
  url: string;
  provenance?: { generator?: string; model_key?: string };
}
async function llmGenerateImage(
  eff: LlmGenerateImageEffect,
  send: (msg: Msg) => void,
): Promise<void> {
  await awaitSubstrate();
  const fail = (error: string): void =>
    send({ type: 'llm-image-failed', entityId: eff.subject, predicate: eff.predicate, error });
  if (!replica || !syncClient) return fail(t('llm.draft_no_substrate'));
  const cid = CAMPAIGN_ID;
  if (cid === null) return fail(t('llm.draft_no_campaign'));
  try {
    const res = await fetch(
      `${workerOrigin()}/v1/campaigns/${encodeURIComponent(cid)}/llm/generate-image`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          subject: eff.subject,
          predicate: eff.predicate,
          ...(eff.instruction ? { prompt: eff.instruction } : {}),
        }),
      },
    );
    if (!res.ok) return fail(t('llm.image_failed', { status: String(res.status) }));
    const data = (await res.json()) as GenerateImageResponse;
    if (typeof data.asset_id !== 'string' || data.asset_id === '') return fail(t('llm.image_empty'));
    const prov = data.provenance ?? {};
    const author = llmAuthor({
      provider: prov.generator ?? 'stub',
      model: prov.model_key ?? 'stub-image-1',
    });
    await appendToChain(eff.subject, eff.predicate, (baseVersion) => ({
      subject: eff.subject,
      predicate: eff.predicate,
      payload: factCreate(data.asset_id, { status: 'draft' }),
      base_version: baseVersion,
      author,
      created_at_real: nowIso(),
    }));
    const url = assetContentUrl(data.url);
    send({
      type: 'llm-image-succeeded',
      entityId: eff.subject,
      predicate: eff.predicate,
      assetId: data.asset_id,
      url,
    });
  } catch (err) {
    fail(t('llm.image_failed_generic', { error: err instanceof Error ? err.message : String(err) }));
  }
}

/**
 * Resolve asset ids to signed content URLs via the campaign asset-metadata
 * endpoint, then stash each on the model so asset-kind facts render as
 * thumbnails. Best-effort + per-id: one failure (e.g. a 404 for an asset
 * from another campaign) never blocks the others.
 */
/**
 * Re-root a server-minted asset content URL onto the worker origin. The
 * worker builds it with `APP_ORIGIN` as the host, which in dev is the Vite
 * origin (:17320) — but `/v1/assets/:id/content` is always served by the
 * worker (:8787). Keep only the path + signed `?token=` query and root it
 * on `workerOrigin()`. In prod (same-origin) `workerOrigin()` is '' so the
 * URL stays relative — correct there too.
 */
function assetContentUrl(raw: string): string {
  try {
    const fallbackBase =
      typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
    const u = new URL(raw, fallbackBase);
    return `${workerOrigin()}${u.pathname}${u.search}`;
  } catch {
    return raw;
  }
}

/** GET the per-campaign auditor rule severity overrides into the model. */
async function loadAuditorOverrides(send: (msg: Msg) => void): Promise<void> {
  const cid = CAMPAIGN_ID;
  if (cid === null) return;
  try {
    const res = await fetch(
      `${workerOrigin()}/v1/campaigns/${encodeURIComponent(cid)}/auditor-overrides`,
      { credentials: 'include' },
    );
    if (!res.ok) return;
    const data = (await res.json()) as { overrides?: unknown };
    const parsed = AuditorRuleOverridesSchema.safeParse(data.overrides ?? {});
    send({ type: 'auditor-overrides-loaded', overrides: parsed.success ? parsed.data : {} });
  } catch {
    // best-effort — the audit runs with engine defaults until this lands
  }
}

async function resolveAssetUrls(assetIds: string[], send: (msg: Msg) => void): Promise<void> {
  const cid = CAMPAIGN_ID;
  if (cid === null) return;
  await Promise.all(
    assetIds.map(async (assetId) => {
      try {
        const res = await fetch(
          `${workerOrigin()}/v1/campaigns/${encodeURIComponent(cid)}/assets/${encodeURIComponent(assetId)}`,
          { credentials: 'include' },
        );
        if (!res.ok) return;
        const data = (await res.json()) as { url?: string };
        if (typeof data.url !== 'string' || data.url === '') return;
        send({ type: 'asset-url-resolved', assetId, url: assetContentUrl(data.url) });
      } catch {
        // best-effort — a transient failure just leaves the id un-thumbnailed
      }
    }),
  );
}

function onEffect(effect: Effect, api: EffectApi<Model, Msg>): void {
  const { send } = api;
  // Capture send on first invocation so async substrate callbacks (which
  // outlive any single effect handler) can dispatch back into the loop.
  if (!appSend) appSend = send;

  // Router effects (push, replace, navigate, back, forward, scroll) carry
  // type: '__router' and go through the router's plugin handler.
  if ('type' in effect && effect.type === '__router') {
    routing.handleEffect({
      effect: effect as unknown as { type: string },
      send: send as unknown,
      signal: api.signal,
    });
    return;
  }
  if (!('type' in effect)) return;
  switch (effect.type) {
    case 'bind-keyboard':
      if (typeof document === 'undefined') return;
      document.addEventListener('keydown', (event: KeyboardEvent) => {
        if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
          event.preventDefault();
          send({ type: 'open-palette' });
        } else if (event.key === 'Escape') {
          // The reducer arms are no-ops when the corresponding surface
          // isn't open, so dispatching both is safe and keeps the global
          // Escape behaviour consistent across overlays.
          send({ type: 'close-palette' });
          send({ type: 'close-new-entity-modal' });
          // #003 (llui-hud) — Escape also exits the GM "pointing" / attention
          // mode (idempotent: a no-op when not pointing).
          send({ type: 'table/attention-exit' });
          // #005 (llui-hud) — Escape closes the Actions overlay (idempotent).
          send({ type: 'actions-overlay/close' });
        }
      });
      return;
    case 'boot-session':
      void bootSession(send);
      return;
    case 'open-substrate':
      void openSubstrate(send, currentSessionViewer());
      return;
    case 'account/load':
      void loadAccount(send);
      return;
    case 'recovery-disclaimer/mark-seen':
      void markRecoveryDisclaimerSeen();
      return;
    case 'export-campaign':
      triggerCampaignExport(effect.campaignId, send);
      return;
    case 'account/twofa-setup':
      void accountTwofaSetup(send);
      return;
    case 'account/twofa-confirm':
      void accountTwofaConfirm(send, effect.code);
      return;
    case 'account/twofa-disable':
      void accountTwofaDisable(send);
      return;
    case 'account/password-reset':
      void accountPasswordReset(send, effect.email);
      return;
    case 'account/recovery-email-save':
      void accountRecoveryEmailSave(send, effect.recoveryEmail, effect.currentPassword);
      return;
    case 'account/delete':
      void accountDelete(send);
      return;
    case 'submit-entity-create':
      void submitEntityCreate(effect);
      return;
    case 'submit-bundle-declare':
      void submitBundleDeclare(effect);
      return;
    case 'submit-entity-soft-delete':
      void submitEntitySoftDelete(effect);
      return;
    case 'submit-entity-restore':
      void submitEntityRestore(effect);
      return;
    case 'submit-fact':
      void submitFact(effect, send);
      return;
    case 'submit-reveal':
      void submitReveal(effect);
      return;
    case 'llm-suggest-prose':
      void llmSuggestProse(effect, send);
      return;
    case 'llm-draft-entity':
      void llmDraftEntity(effect, send);
      return;
    case 'submit-fact-status':
      void submitFactStatus(effect);
      return;
    case 'bulk-gen':
      void bulkGen(effect, send);
      return;
    case 'open-authoring-session':
      void openAuthoringSession(effect, send);
      return;
    case 'authoring-turn':
      void authoringTurn(effect, send);
      return;
    case 'commit-authoring':
      void commitAuthoring(effect, send);
      return;
    case 'cancel-authoring':
      if (authoringTurnAbort) authoringTurnAbort.abort();
      return;
    case 'compute-preview-projection':
      void computePreviewProjection(effect, send);
      return;
    case 'broadcast-table':
      void broadcastTable(effect);
      return;
    case 'broadcast-attention':
      broadcastAttention(effect);
      return;
    case 'schedule-attention-ripple-clear': {
      // #271 — auto-clear the ripple after the TTL. The reducer guards on seq
      // so a stale timeout cannot clear a newer ripple.
      const { seq, delayMs } = effect;
      setTimeout(() => {
        if (appSend) appSend({ type: 'table/attention-ripple-clear', seq });
      }, delayMs);
      return;
    }
    case 'table-channel-init':
      initTableChannel(effect.role, send);
      return;
    case 'llm-generate-image':
      void llmGenerateImage(effect, send);
      return;
    case 'resolve-asset-urls':
      void resolveAssetUrls(effect.assetIds, send);
      return;
    case 'load-auditor-overrides':
      void loadAuditorOverrides(send);
      return;
    case 'submit-config':
      void submitConfig(effect);
      return;
    case 'submit-trait-mutation':
      void submitTraitMutation(effect);
      return;
    case 'submit-pack-ops':
      void submitPackOps(effect);
      return;
    case 'load-private-note':
      void loadPrivateNote(send, effect.entityId);
      return;
    case 'load-campaign-members':
      void loadCampaignMembers(send);
      return;
    // #263 — campaign roster + invite effects.
    case 'roster/load-invites':
      void loadInviteState(send);
      return;
    case 'roster/send-email-invite':
      void sendEmailInvite(send, effect.email, effect.role);
      return;
    case 'roster/revoke-invite':
      void revokeEmailInvite(send, effect.inviteId);
      return;
    case 'roster/create-link':
      void createInviteLink(send, effect.role);
      return;
    case 'roster/close-link':
      void closeInviteLink(send, effect.linkId);
      return;
    case 'roster/remove-member':
      void removeMember(send, effect.userId);
      return;
    case 'save-private-note':
      void savePrivateNote(send, effect.entityId, effect.body);
      return;
    case 'announce-mode':
      announceMode(effect.mode);
      return;
    case 'auth/sign-out':
      void (async (): Promise<void> => {
        await apiSignOut();
        // Navigate to /sign-in with a full load so every module-scope cache
        // (replica, sync client, cached viewer) starts from a clean slate.
        // assign() replaces the old hash-set + reload in one step.
        if (typeof window !== 'undefined') {
          window.location.assign('/sign-in');
        }
      })();
      return;
    case 'schedule-fact-saved-clear': {
      // #156 — after the badge timeout, dispatch the clear Msg. The
      // reducer's `fact-saved-clear` arm idempotently drops the saved-
      // at entry only if no newer save has happened in the meantime.
      const { entityId, predicate, delayMs } = effect;
      setTimeout(() => {
        send({ type: 'fact-saved-clear', entityId, predicate });
      }, delayMs);
      return;
    }
    case 'persist-cursor': {
      // Persist the Campaign date per-campaign so it survives a reload
      // (06-timeline.md §13; ADR-0005 §"Session cursor"). The localStorage
      // cache is always written (instant + offline pre-substrate read). When
      // toServer, the CANONICAL write is a `config.set session_cursor` op on
      // the op log — it folds into `config.session_cursor` (replay-safe) and
      // broadcasts over the same WS as canon, giving live multi-GM "now" sync
      // for free. The prior ephemeral `session_cursor_set` WS relay + the
      // D1-PUT-as-source-of-truth are demoted: D1 is a rebuildable boot cache
      // only, rebuilt from the folded op (the DO does that on accept). No-op
      // when there is no active campaign (legacy / unresolved boot).
      const campaignId = currentCampaignId();
      if (campaignId === null) return;
      setPersistedCursor(campaignId, effect.calendarId, effect.cursor);
      if (!effect.toServer) return;
      // Canonical, op-log-backed, live-synced write. The op-log fold
      // (`replica/entities-rebuilt`) re-hydrates `campaignCursor` from
      // `config.session_cursor`; the originator already applied it
      // optimistically, so the fold is reconciling, not a stomp.
      void submitConfig({
        type: 'submit-config',
        payload: configSet(
          'session_cursor',
          // `{ day, calendar_id }` when set, `null` when timeless — never a
          // half-set object (the shared-axis `day` and its authoring calendar
          // travel together; see `SessionCursorValueSchema`).
          effect.cursor === null ? null : { day: effect.cursor, calendar_id: effect.calendarId },
        ),
      });
      return;
    }
    case 'persist-recent': {
      // Cache the rail's recently-viewed list per campaign (local-only UI
      // state). No-op without an active campaign (legacy / unresolved boot).
      const campaignId = currentCampaignId();
      if (campaignId === null) return;
      writeRecentEntities(campaignId, effect.recent);
      return;
    }
    case 'fetch-session-cursor': {
      // Player reconnect path: re-pull the authoritative Campaign date
      // and hydrate. Fire-and-forget; a failure leaves the current
      // value untouched (no clobber).
      const campaignId = currentCampaignId();
      if (campaignId === null) return;
      void fetch(
        `${workerOrigin()}/v1/me/campaigns/${encodeURIComponent(campaignId)}/session-cursor`,
        { credentials: 'include' },
      )
        .then(async (res) => {
          if (!res.ok) return;
          const data = (await res.json()) as {
            session_cursor?: number | null;
            session_cursor_calendar?: string | null;
          };
          send({
            type: 'cursor/hydrate',
            cursor: data.session_cursor ?? null,
            calendarId: data.session_cursor_calendar ?? null,
          });
        })
        .catch(() => {
          // Offline / transient — keep the current value.
        });
      return;
    }
  }
}

/**
 * Module-scope holder for the resolved Viewer — needed because
 * `open-substrate` runs as a fire-and-forget effect that the loop
 * dispatched before we had a Model handle. The Msg `session/loaded`
 * sets this; `open-substrate` reads it. Default is the legacy GM
 * Viewer so the demo / fallback path keeps working.
 */
let cachedSessionViewer: ViewerId = LEGACY_GM_SESSION.viewer;
function currentSessionViewer(): ViewerId {
  return cachedSessionViewer;
}

// ---------- draft inbox (III.C.5 / #73) ----------

/**
 * GM draft inbox — III.C.5 (#73).
 *
 * Minimal a11y-compliant shell. Data fetching + ratify/edit/reject
 * mutations land via direct fetch() calls in `setupDraftInbox()` after
 * the view mounts. This intentionally keeps the inbox lightweight and
 * out of the TEA model state; the drafts come from the Replica's op
 * log (filtered by status=draft), refreshed when the replica fires
 * the rebuild signal — Phase 2 may bring this back into the TEA loop
 * for richer keyboard navigation across virtualised rows.
 *
 * Accessibility:
 *   - `role="region"` with `aria-labelledby="drafts-heading"`
 *   - `role="status" aria-live="polite"` on the queue-depth badge
 *   - `aria-label` on each ratify/edit/reject button
 *   - Keyboard: j/k to move selection, Enter to ratify, x to reject
 *     (handlers wired in `setupDraftInbox`)
 */
// ---------- Auditor panel (04-llm-role.md §5) ----------

/**
 * Run the deterministic auditor over a folded projection with the Campaign's
 * severity overrides applied. The single chokepoint for producing advisories
 * client-side — used by the entities-rebuild arm and whenever overrides change.
 */
function computeAdvisories(
  entities: Record<string, EntityState>,
  overrides: AuditorRuleOverrides,
  bundles?: BundleRegistry,
): Advisory[] {
  return runAudit({
    entities,
    registry: loadGmRegistry(),
    overrides,
    // #222 / 06-timeline.md §8 — feed the causal.cycle rule the official-core
    // caused_by / precedes binding so a directed loop in the happens-before
    // graph surfaces as a consistency advisory.
    causal_predicates: OFFICIAL_CORE_CAUSAL_PREDICATES,
    // #239 / 06-timeline.md §10.4 — feed the recurrence.open_cycle rule the
    // official-core `recurs_as` predicate id + the campaign's declared
    // calendars so it can flag a recurrence rule anchored against an open
    // cycle (held in suspense until `close_at`).
    recurrence_open_cycle: {
      recurs_as_predicates: [OFFICIAL_CORE_PREDICATE_IDS.recurs_as],
      calendars: declaredCalendars(),
    },
    // #259 — feed the combat.quick_log_unreviewed rule the bundle registry so
    // it can flag stale open combat_log bundles. Omit the key (exactOptionalPropertyTypes)
    // when the registry is absent so the rule no-ops safely.
    ...(bundles !== undefined ? { bundles } : {}),
  });
}

/**
 * Every calendar a `recurs_as` rule might anchor against — the homebrew
 * `pack.define_calendar` definitions (where OPEN cycles are authored — §4.1)
 * resolved to runtime `Calendar`s via the same `asCalendar` chokepoint
 * {@link loadActiveCalendar} uses, since bundled calendars (gregorian/harptos)
 * declare no open cycles. Used by the `recurrence.open_cycle` auditor binding.
 * A malformed definition is skipped (the auditor stays a no-op, never throws).
 */
function declaredCalendars(): Calendar[] {
  const out: Calendar[] = [];
  for (const def of getHomebrewDefs().calendars) {
    const parsed = CalendarDefinitionSchema.safeParse(def);
    if (parsed.success) out.push(asCalendar(parsed.data));
  }
  return out;
}

/** Total advisory count — drives the nav badge. */
function advisoryCount(s: Model): number {
  return s.advisories.length;
}

/** One render-ready advisory row, resolved against the current entities. */
interface AdvisoryRow {
  key: string;
  severity: Severity;
  severityLabel: string;
  rule: string;
  message: string;
  entityId: string;
  entityName: string;
  /** The one-click fix (§5.1), if this advisory carries one. */
  fix?: SuggestedFix;
  /** Localised fix-button label (empty when no fix). */
  fixLabel: string;
}

/**
 * Resolve one advisory into a render-ready row: the rule's i18n message from
 * its `message_key` + engine params, plus the affected entity's display name.
 */
function toAdvisoryRow(s: Model, a: Advisory): AdvisoryRow {
  const entityId = a.entity_ids[0] ?? '';
  return {
    key: a.id,
    severity: a.severity,
    severityLabel: t(`auditor.severity.${a.severity}`),
    rule: a.rule,
    message: t(a.message_key, a.params),
    entityId,
    entityName: entityId ? entityNameOrId(s, entityId) : '',
    ...(a.fix ? { fix: a.fix } : {}),
    fixLabel: a.fix ? t(a.fix.label_key, a.fix.params) : '',
  };
}

/**
 * Translate a §5.1 SuggestedFix into the existing op-log mutation Msg that
 * applies it — so a fix flows through the same path as any GM edit (it appends
 * an op; it never bypasses the log). Pure + exported for unit testing the apply
 * seam. Returns null for a fix kind the client doesn't know how to apply.
 */
export function auditorFixMsg(fix: SuggestedFix): Msg | null {
  if (fix.kind === 'remove_fact') {
    return { type: 'soft-delete-fact', entityId: fix.subject, predicate: fix.factKey };
  }
  return null;
}

/**
 * The "Apply fix" affordance for an advisory row (§5.1). Rendered only when the
 * advisory carries a fix. The fix dispatches through `auditorFixMsg` → the
 * existing op-log mutation (the GM explicitly accepts it; no draft-inbox step).
 * Shared by the global panel and the per-entity Issues tab.
 */
function viewAdvisoryFixButton(item: Signal<AdvisoryRow>, send: Send<Msg>): Mountable {
  return show(
    item.map((r) => r.fix !== undefined),
    () => [
      uiButton({
        variant: 'secondary',
        className: 'auditor-fix-button',
        testid: 'auditor-fix-button',
        onClick: () => {
          const fix = item.peek().fix;
          const msg = fix ? auditorFixMsg(fix) : null;
          if (msg) send(msg);
        },
        children: [text(item.at('fixLabel'))],
      }),
    ],
  );
}

/**
 * Project the Model's advisories into render-ready rows. Severity-ordered
 * already (the engine sorts); we keep that order so the most severe surface
 * first.
 */
function advisoryRows(s: Model): AdvisoryRow[] {
  return s.advisories.map((a) => toAdvisoryRow(s, a));
}

/** Advisories naming this entity (it appears in `entity_ids`). */
function entityAdvisoryRows(s: Model, entityId: string): AdvisoryRow[] {
  return s.advisories.filter((a) => a.entity_ids.includes(entityId)).map((a) => toAdvisoryRow(s, a));
}

/** Live advisory count for one entity — drives the entity-page badge + tab. */
function entityIssueCount(s: Model, entityId: string): number {
  let n = 0;
  for (const a of s.advisories) if (a.entity_ids.includes(entityId)) n++;
  return n;
}

/**
 * Entity-page Issues tab — this entity's auditor advisories. Reuses the
 * auditor-row chrome (severity label + message) but drops the entity link
 * (we're already on the entity). Empty state when the entity is clean.
 */
function viewEntityIssuesTab(
  state: Signal<Model>,
  send: Send<Msg>,
  entityId: string,
): Mountable {
  void send;
  return div(
    {
      id: 'entity-page-issues-panel',
      role: 'tabpanel',
      'aria-labelledby': 'entity-page-issues-tab',
      tabindex: 0,
      class: 'entity-page-panel entity-page-panel--issues',
      'data-testid': 'entity-page-panel-issues',
    },
    [
      show(
        state.map((s) => entityAdvisoryRows(s, entityId).length === 0),
        () => [
          emptyState({
            testid: 'entity-issues-empty',
            message: text(t('entity.issues.empty')),
          }),
        ],
      ),
      show(
        state.map((s) => entityAdvisoryRows(s, entityId).length > 0),
        () => [
          ul(
            { role: 'list', class: 'auditor-list', 'data-testid': 'entity-issues-list' },
            [
              each(state.map((s) => entityAdvisoryRows(s, entityId)), {
                key: (row) => row.key,
                render: (item) => [
                  li(
                    {
                      class: 'auditor-row',
                      'data-testid': 'entity-issues-row',
                      'data-severity': item.at('severity'),
                      'data-rule': item.at('rule'),
                    },
                    [
                      div({ class: 'auditor-row-head' }, [
                        span(
                          {
                            class: 'auditor-severity',
                            'data-testid': 'entity-issues-severity',
                            'data-severity': item.at('severity'),
                          },
                          [text(item.at('severityLabel'))],
                        ),
                        span({ class: 'auditor-message' }, [text(item.at('message'))]),
                      ]),
                      viewAdvisoryFixButton(item, send),
                    ],
                  ),
                ],
              }),
            ],
          ),
        ],
      ),
    ],
  );
}

/**
 * The auditor advisory panel — the GM-only deterministic-consistency surface.
 * Lists advisories most-severe-first; each row pairs a severity label (not
 * colour alone, per a11y) with the rule message and a link to the affected
 * entity. Empty + syncing states mirror the draft inbox.
 */
function viewAuditorPanel(state: Signal<Model>, send: Send<Msg>): Mountable {
  return main({ role: 'main', 'aria-labelledby': 'auditor-heading' }, [
    appRouteLink(
      send,
      { kind: 'list' },
      { 'data-testid': 'back-link' },
      [text(`← ${t('entity.back')}`)],
      navMsg,
    ),
    pageHeader({
      title: text(t('auditor.title')),
      titleId: 'auditor-heading',
      titleTestId: 'auditor-heading',
    }),
    div(
      {
        role: 'status',
        'aria-live': 'polite',
        class: 'auditor-count',
        'data-testid': 'auditor-count',
      },
      [
        text(
          state.map((s) =>
            advisoryCount(s) > 0 ? t('auditor.count', { n: advisoryCount(s) }) : '',
          ),
        ),
      ],
    ),
    p({ class: 'auditor-onboarding' }, [text(t('auditor.onboarding'))]),
    // Empty / syncing state.
    show(
      state.map((s) => advisoryRows(s).length === 0),
      () => [
        emptyState({
          testid: 'auditor-empty',
          message: text(
            state.map((s) => (s.syncing ? t('auditor.empty_syncing') : t('auditor.empty'))),
          ),
        }),
      ],
    ),
    // Populated — one row per advisory.
    show(
      state.map((s) => advisoryRows(s).length > 0),
      () => [
        ul(
          { role: 'list', class: 'auditor-list', 'data-testid': 'auditor-list' },
          [
            each(state.map((s) => advisoryRows(s)), {
              key: (row) => row.key,
              render: (item) => [
                li(
                  {
                    class: 'auditor-row',
                    'data-testid': 'auditor-row',
                    'data-severity': item.at('severity'),
                    'data-rule': item.at('rule'),
                  },
                  [
                    div({ class: 'auditor-row-head' }, [
                      span(
                        {
                          class: 'auditor-severity',
                          'data-testid': 'auditor-severity',
                          'data-severity': item.at('severity'),
                        },
                        [text(item.at('severityLabel'))],
                      ),
                      span({ class: 'auditor-message' }, [text(item.at('message'))]),
                    ]),
                    viewAdvisoryFixButton(item, send),
                    // Link to the affected entity. A button (not routing.link)
                    // because the target id is per-row reactive; the handler
                    // peeks the row and dispatches the standard navigate Msg.
                    uiButton({
                      variant: 'ghost',
                      className: 'auditor-row-entity',
                      testid: 'auditor-row-entity',
                      ariaLabel: t('auditor.row.view_entity'),
                      onClick: () => {
                        const row = item.peek();
                        if (row.entityId) send(navMsg({ kind: 'entity', id: row.entityId }));
                      },
                      children: [text(item.at('entityName'))],
                    }),
                  ],
                ),
              ],
            }),
          ],
        ),
      ],
    ),
  ]);
}

/**
 * Lucide `sparkles` (the LLM-authored marker per 30 §5), inlined as an
 * SVG so the provenance badge carries the AI icon without a runtime lucide
 * dependency. Decorative — the badge text ("AI") carries the meaning.
 */
function sparklesIcon(): Mountable {
  return svg(
    {
      class: 'inbox-spark-icon',
      width: '11',
      height: '11',
      viewBox: '0 0 24 24',
      fill: 'none',
      stroke: 'currentColor',
      'stroke-width': '1.75',
      'stroke-linecap': 'round',
      'stroke-linejoin': 'round',
      'aria-hidden': 'true',
      focusable: 'false',
    },
    [
      path({
        d: 'M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z',
      }),
      path({ d: 'M20 3v4' }),
      path({ d: 'M22 5h-4' }),
      path({ d: 'M4 17v2' }),
      path({ d: 'M5 18H3' }),
    ],
  );
}

/**
 * #251 — the suggestion-set bundle review surface (04-llm-role.md §14.1). Each
 * open bulk-gen bundle renders as ONE reviewable unit above the loose drafts:
 * per-bundle accept-all / reject-all / regenerate + auto-promote-with-flag
 * toggle, and per-member accept / reject / advisories / confidence. GM-only by
 * construction. Hidden entirely when there are no open bundles.
 */
function viewBundleReview(state: Signal<Model>, send: Send<Msg>): Mountable {
  return show(
    state.map((s) => openBundleCount(s) > 0),
    () => [
      section(
        { class: 'bundle-review', 'data-testid': 'bundle-review', 'aria-labelledby': 'bundle-review-heading' },
        [
          div({ class: 'bundle-review-head' }, [
            h2({ id: 'bundle-review-heading', class: 'bundle-review-heading' }, [
              text(t('bundle.section_heading')),
            ]),
            span({ class: 'bundle-review-count', 'data-testid': 'bundle-review-count' }, [
              text(state.map((s) => t('bundle.section_count', { n: openBundleCount(s) }))),
            ]),
          ]),
          ul(
            { class: 'bundle-list', role: 'list', 'data-testid': 'bundle-list' },
            [each(state.map((s) => bundleReviewGroups(s)), {
              key: (g) => g.bundleId,
              render: (group) => [viewBundleGroup(group, send)],
            })],
          ),
        ],
      ),
    ],
  );
}

/** One bundle group — header (prompt/mode/count + actions) + its member rows. */
function viewBundleGroup(group: Signal<BundleReviewGroup>, send: Send<Msg>): Mountable {
  return li(
    {
      class: 'bundle-group',
      'data-testid': 'bundle-group',
      'data-bundle': group.at('bundleId'),
    },
    [
      div({ class: 'bundle-group-head' }, [
        div({ class: 'bundle-group-meta' }, [
          span({ class: 'bundle-group-title' }, [
            text(group.map((g) => t('bundle.group_label', { n: g.memberCount }))),
          ]),
          show(
            group.map((g) => g.prompt !== ''),
            () => [
              p({ class: 'bundle-group-prompt', 'data-testid': 'bundle-group-prompt' }, [
                span({ class: 'bundle-group-prompt-label' }, [text(t('bundle.prompt_label'))]),
                span({ class: 'bundle-group-prompt-text' }, [text(group.at('prompt'))]),
              ]),
            ],
          ),
        ]),
        // Per-bundle actions (§14.1): accept-all / reject-all / regenerate.
        div({ class: 'bundle-group-actions' }, [
          uiButton({
            variant: 'primary',
            className: 'bundle-accept-all',
            testid: 'bundle-accept-all',
            onClick: () => {
              const id = group.at('bundleId').peek();
              if (typeof id === 'string') send({ type: 'bundle-accept-all', bundleId: id });
            },
            children: [text(t('bundle.accept_all'))],
          }),
          uiButton({
            variant: 'danger',
            className: 'bundle-reject-all',
            testid: 'bundle-reject-all',
            onClick: () => {
              const id = group.at('bundleId').peek();
              if (typeof id === 'string') send({ type: 'bundle-reject-all', bundleId: id });
            },
            children: [text(t('bundle.reject_all'))],
          }),
          uiButton({
            variant: 'ghost',
            className: 'bundle-regen',
            testid: 'bundle-regen',
            onClick: () => {
              const id = group.at('bundleId').peek();
              if (typeof id === 'string') send({ type: 'open-bundle-regen', bundleId: id });
            },
            children: [text(t('bundle.regen'))],
          }),
        ]),
      ]),
      // Auto-promote-with-flag toggle (opt-in, off by default).
      label({ class: 'bundle-auto-promote', 'data-testid': 'bundle-auto-promote' }, [
        input({
          type: 'checkbox',
          'data-testid': 'bundle-auto-promote-checkbox',
          'aria-label': t('bundle.auto_promote_label'),
          checked: group.at('autoPromote'),
          onChange: () => {
            const id = group.at('bundleId').peek();
            if (typeof id === 'string') send({ type: 'bundle-toggle-auto-promote', bundleId: id });
          },
        }),
        span({ class: 'bundle-auto-promote-text' }, [
          span({ class: 'bundle-auto-promote-title' }, [text(t('bundle.auto_promote_label'))]),
          span({ class: 'bundle-auto-promote-hint' }, [text(t('bundle.auto_promote_hint'))]),
        ]),
      ]),
      // Member rows — sorted by confidence desc.
      ul(
        { class: 'bundle-members', role: 'list', 'data-testid': 'bundle-members' },
        [each(group.at('members'), {
          key: (m) => m.key,
          render: (member) => [viewBundleMember(member, send)],
        })],
      ),
    ],
  );
}

/** One member draft row inside a bundle group. */
function viewBundleMember(member: Signal<BundleMemberRow>, send: Send<Msg>): Mountable {
  return li(
    {
      class: 'bundle-member',
      'data-testid': 'bundle-member',
      'data-predicate': member.at('predicate'),
      'data-confidence-band': member.at('confidenceBand'),
    },
    [
      div({ class: 'bundle-member-head' }, [
        div({ class: 'bundle-member-card' }, [
          span(
            { class: 'bundle-member-icon', 'data-root': member.at('root'), 'aria-hidden': 'true' },
            [
              branch(member.at('root'), {
                location: () => [entityIcon('location', { size: 14 })],
                character: () => [entityIcon('character', { size: 14 })],
                faction: () => [entityIcon('faction', { size: 14 })],
                event: () => [entityIcon('event', { size: 14 })],
                item: () => [entityIcon('item', { size: 14 })],
                lore: () => [entityIcon('lore', { size: 14 })],
              }),
            ],
          ),
          div({ class: 'bundle-member-body' }, [
            span({ class: 'bundle-member-entity' }, [text(member.at('entityName'))]),
            span({ class: 'bundle-member-predicate' }, [text(member.at('predicateLabel'))]),
          ]),
        ]),
        // Confidence chip — band label (non-colour cue) + percentage.
        span(
          {
            class: 'bundle-member-confidence',
            'data-band': member.at('confidenceBand'),
            'data-testid': 'bundle-member-confidence',
          },
          [
            text(
              member.map((m) =>
                m.confidence === null
                  ? t('bundle.confidence_none')
                  : t('bundle.confidence', { pct: Math.round(m.confidence * 100) }),
              ),
            ),
            span({ class: 'bundle-member-confidence-band' }, [
              text(member.map((m) => t(`bundle.confidence_band.${m.confidenceBand}`))),
            ]),
          ],
        ),
      ]),
      div(
        {
          class: 'bundle-member-value',
          'data-kind': member.map((m) => (m.isAsset ? 'asset' : m.isRichText ? 'rich_text' : 'text')),
        },
        [
          img({
            class: 'bundle-member-thumb',
            'data-testid': 'bundle-member-thumb',
            alt: member.map((m) => t('bundle.image_alt', { predicate: m.predicateLabel })),
            src: member.at('assetUrl'),
          }),
          div({ class: 'bundle-member-rich' }, [markdown(member.at('value'))]),
          p({ class: 'bundle-member-text', 'data-testid': 'bundle-member-text' }, [
            text(member.at('value')),
          ]),
        ],
      ),
      // Pre-emit advisories (§14.4) attributed to this member, before accept.
      show(
        member.map((m) => m.advisories.length > 0),
        () => [
          ul(
            { class: 'bundle-member-advisories', role: 'list', 'data-testid': 'bundle-member-advisories' },
            [each(member.at('advisories'), {
              key: (a) => a.key,
              render: (adv) => [
                li(
                  {
                    class: 'bundle-member-advisory',
                    'data-testid': 'bundle-member-advisory',
                    'data-severity': adv.at('severity'),
                  },
                  [
                    span({ class: 'bundle-member-advisory-severity' }, [text(adv.at('severityLabel'))]),
                    span({ class: 'bundle-member-advisory-message' }, [text(adv.at('message'))]),
                  ],
                ),
              ],
            })],
          ),
        ],
      ),
      div({ class: 'bundle-member-actions' }, [
        uiButton({
          variant: 'secondary',
          className: 'bundle-accept-member',
          testid: 'bundle-accept-member',
          ariaLabel: member.map((m) => t('inbox.row_label', { entity: m.entityName, predicate: m.predicateLabel })),
          onClick: () => {
            const id = member.at('entityId').peek();
            const p = member.at('predicate').peek();
            if (typeof id === 'string' && typeof p === 'string')
              send({ type: 'bundle-accept-member', entityId: id, predicate: p });
          },
          children: [text(t('bundle.accept_member'))],
        }),
        uiButton({
          variant: 'danger',
          className: 'bundle-reject-member',
          testid: 'bundle-reject-member',
          onClick: () => {
            const id = member.at('entityId').peek();
            const p = member.at('predicate').peek();
            if (typeof id === 'string' && typeof p === 'string')
              send({ type: 'bundle-reject-member', entityId: id, predicate: p });
          },
          children: [text(t('bundle.reject_member'))],
        }),
      ]),
    ],
  );
}

/**
 * #251 §14.8 — the bulk-gen config + cost-confirm modal (both entry surfaces).
 * Walks config → estimating → confirm → generating; the estimate (cost +
 * fan-out + resolved mode) is shown BEFORE the GM confirms. Uses the responsive
 * `modal` primitive (modal on desktop, full-width on phone).
 */
/**
 * The bulk-gen DEPTH select, with a static current value (the selectInput atom
 * takes a static value; the caller re-mounts it via a `branch` on the depth when
 * the value changes). `value` is the literal arm value supplied by that branch.
 */
function bundleGenDepthSelect(send: Send<Msg>, value: BundleDepth): Mountable {
  return selectInput({
    id: 'bundle-gen-depth',
    testid: 'bundle-gen-depth',
    ariaLabel: t('bundle.gen_depth_label'),
    value,
    size: 'full',
    options: [
      { value: 'sparse', label: t('bundle.gen_depth.sparse') },
      { value: 'normal', label: t('bundle.gen_depth.normal') },
      { value: 'dense', label: t('bundle.gen_depth.dense') },
    ],
    onChange: (next) => {
      if (next === 'sparse' || next === 'normal' || next === 'dense')
        send({ type: 'bundle-gen-depth', value: next });
    },
  });
}

/** The bulk-gen grounding-MODE select (`auto` lets the worker heuristic resolve). */
function bundleGenModeSelect(send: Send<Msg>, value: BundleMode): Mountable {
  return selectInput({
    id: 'bundle-gen-mode',
    testid: 'bundle-gen-mode',
    ariaLabel: t('bundle.gen_mode_label'),
    value,
    size: 'full',
    options: [
      { value: 'auto', label: t('bundle.gen_mode.auto') },
      { value: 'bootstrap', label: t('bundle.gen_mode.bootstrap') },
      { value: 'in_world', label: t('bundle.gen_mode.in_world') },
    ],
    onChange: (next) => {
      if (next === 'auto' || next === 'bootstrap' || next === 'in_world')
        send({ type: 'bundle-gen-mode', value: next });
    },
  });
}

function viewBundleGenModal(state: Signal<Model>, send: Send<Msg>): Mountable {
  return modal({
    open: state.map((s) => s.bundleGenModal !== null),
    onDismiss: () => send({ type: 'close-bundle-gen' }),
    labels: { close: t('bundle.gen_cancel') },
    labelId: 'bundle-gen-heading',
    testid: 'bundle-gen-modal',
    panelClass: 'bundle-gen-panel',
    size: 'sm',
    children: [
      h2({ id: 'bundle-gen-heading' }, [
        text(state.map((s) => (bundleGenIsBootstrap(s) ? t('bundle.gen_heading_bootstrap') : t('bundle.gen_heading_entity')))),
      ]),
      p({ class: 'bundle-gen-help' }, [
        text(state.map((s) => (bundleGenIsBootstrap(s) ? t('bundle.gen_help_bootstrap') : t('bundle.gen_help_entity')))),
      ]),
      // An error from a prior attempt (e.g. #218 over-budget refusal).
      show(
        state.map((s) => s.bundleGenModal?.error != null && s.bundleGenModal.error !== ''),
        () => [
          p({ class: 'bundle-gen-error', role: 'alert', 'data-testid': 'bundle-gen-error' }, [
            span({ class: 'bundle-gen-error-icon', 'aria-hidden': 'true' }, ['⚠']),
            text(state.map((s) => s.bundleGenModal?.error ?? '')),
          ]),
        ],
      ),
      label({ for: 'bundle-gen-prompt', class: 'bundle-gen-label' }, [text(t('bundle.gen_prompt_label'))]),
      textArea({
        className: 'bundle-gen-prompt',
        testid: 'bundle-gen-prompt',
        rows: 3,
        placeholder: t('bundle.gen_prompt_placeholder'),
        value: state.map((s) => s.bundleGenModal?.prompt ?? ''),
        ariaLabel: t('bundle.gen_prompt_label'),
        onInput: (value: string) => send({ type: 'bundle-gen-prompt', value }),
        attrs: { id: 'bundle-gen-prompt' },
      }),
      label({ for: 'bundle-gen-depth', class: 'bundle-gen-label' }, [text(t('bundle.gen_depth_label'))]),
      // The selectInput atom takes a STATIC value, so a `branch` on the modal's
      // depth re-mounts it when the value changes (the documented pattern — no
      // render-body `.peek()`). Each arm passes its literal value.
      branch(state.map((s): BundleDepth => s.bundleGenModal?.depth ?? 'sparse'), {
        sparse: () => [bundleGenDepthSelect(send, 'sparse')],
        normal: () => [bundleGenDepthSelect(send, 'normal')],
        dense: () => [bundleGenDepthSelect(send, 'dense')],
      }),
      label({ for: 'bundle-gen-mode', class: 'bundle-gen-label' }, [text(t('bundle.gen_mode_label'))]),
      branch(state.map((s): BundleMode => s.bundleGenModal?.mode ?? 'auto'), {
        auto: () => [bundleGenModeSelect(send, 'auto')],
        bootstrap: () => [bundleGenModeSelect(send, 'bootstrap')],
        in_world: () => [bundleGenModeSelect(send, 'in_world')],
      }),
      div({ class: 'bundle-gen-actions' }, [
        uiButton({
          variant: 'secondary',
          testid: 'bundle-gen-cancel',
          onClick: () => send({ type: 'close-bundle-gen' }),
          children: [text(t('bundle.gen_cancel'))],
        }),
        // One action: generate directly (no pre-run estimate). Disabled while a
        // generation is in flight or the prompt is empty.
        uiButton({
          variant: 'primary',
          testid: 'bundle-gen-confirm',
          disabled: state.map((s) => {
            const m = s.bundleGenModal;
            return m === null || m.prompt.trim() === '' || m.phase === 'generating';
          }),
          onClick: () => send({ type: 'bundle-gen-confirm' }),
          children: [
            text(
              state.map((s) =>
                s.bundleGenModal?.phase === 'generating'
                  ? t('bundle.gen_generating')
                  : t('bundle.gen_confirm'),
              ),
            ),
          ],
        }),
      ]),
    ],
  });
}

/** #251 §14.6 — the bundle-level regenerate-with-direction modal. */
function viewBundleRegenModal(state: Signal<Model>, send: Send<Msg>): Mountable {
  return modal({
    open: state.map((s) => s.bundleRegenModal !== null),
    onDismiss: () => send({ type: 'close-bundle-regen' }),
    labels: { close: t('bundle.gen_cancel') },
    labelId: 'bundle-regen-heading',
    testid: 'bundle-regen-modal',
    panelClass: 'bundle-regen-panel',
    size: 'sm',
    children: [
      h2({ id: 'bundle-regen-heading' }, [text(t('bundle.regen_heading'))]),
      label({ for: 'bundle-regen-prompt', class: 'bundle-gen-label' }, [
        text(t('bundle.regen_prompt_label')),
      ]),
      textArea({
        className: 'bundle-regen-prompt',
        testid: 'bundle-regen-prompt',
        rows: 3,
        placeholder: t('bundle.gen_prompt_placeholder'),
        value: state.map((s) => s.bundleRegenModal?.prompt ?? ''),
        ariaLabel: t('bundle.regen_prompt_label'),
        onInput: (value: string) => send({ type: 'bundle-regen-prompt', value }),
        attrs: { id: 'bundle-regen-prompt' },
      }),
      div({ class: 'bundle-gen-actions' }, [
        uiButton({
          variant: 'secondary',
          testid: 'bundle-regen-cancel',
          onClick: () => send({ type: 'close-bundle-regen' }),
          children: [text(t('bundle.gen_cancel'))],
        }),
        uiButton({
          variant: 'primary',
          testid: 'bundle-regen-submit',
          disabled: state.map((s) => (s.bundleRegenModal?.prompt ?? '').trim() === ''),
          onClick: () => {
            const m = state.at('bundleRegenModal').peek();
            if (m) send({ type: 'bundle-regen', bundleId: m.bundleId, prompt: m.prompt.trim() });
          },
          children: [text(t('bundle.regen_submit'))],
        }),
      ]),
    ],
  });
}

// ---------- ADR-0008 §1/§5 (T8, #282) — LLM-led trait-authoring panel ----------
//
// Renders the T7 (#281) `authoringSession` Model slice as the GM authoring
// surface: a streamed conversation thread (assistant text accretes token by
// token, with per-turn tool "thinking" affordances), a live trait-artifact card
// with per-turn diff highlighting, the direction input, and the auditor-gated
// Commit action. All rendering only — it dispatches the existing T7 Msgs
// (`authoring-direction-input`, `authoring-send-turn`, `authoring-commit`,
// `close-authoring`); it never touches the Model/Msgs/effects.
//
// Diff highlighting (ADR-0008 §1 — "every turn's output is a reviewable diff")
// is driven by `authoringSession.diff`. Every change is signalled by BOTH a
// color (via the `data-change` / `data-changed` attribute → CSS) AND a non-color
// cue: a text label ("Added" / "Removed" / "Changed") plus a leading glyph
// (`+` / `−` / `~`), so the a11y "color is never the only signal" rule holds.

/** Map a member proposal to a stable-ish ref the diff's `member_changes` key on. */
function authoringMemberRef(m: ProposedTraitMember): string {
  // Existing members carry the server-minted id the diff references directly;
  // a new member has no id yet, so we key on a provisional `new:<name>` handle
  // (the same convention the streamed diff uses for pre-mint members).
  return m.kind === 'existing' ? m.id : `new:${resolveLocalizedDisplay(m.name)}`;
}

/** The non-`removed` change class for a rendered member, or `null` if unchanged. */
function authoringMemberChange(
  diff: ArtifactDiff | null,
  ref: string,
): 'added' | 'changed' | null {
  const c = diff?.member_changes.find((mc) => mc.ref === ref && mc.change !== 'removed');
  return c ? (c.change as 'added' | 'changed') : null;
}

/** True when the top-level artifact field at `path` changed this turn. */
export function authoringFieldChanged(diff: ArtifactDiff | null, path: string): boolean {
  return diff !== null && diff.field_changes.some((fc) => fc.path === path);
}

/** The localized display for a member's meta line (value kinds / cardinality / merge). */
function authoringMemberMeta(m: ProposedTraitMember): string {
  if (m.kind === 'existing') return t('authoring.member.existing');
  // Render each kind, inlining its per-kind extras so the GM can VALIDATE them:
  // an `enum` shows its options, a `ref` shows the roots it may point at.
  const kinds = m.value_kind
    .map((vk) => {
      if (vk === 'enum' && m.enum_values && m.enum_values.length > 0) {
        return t('authoring.member.enum_kind', {
          options: m.enum_values.map((v) => resolveLocalizedDisplay(v.label)).join(', '),
        });
      }
      if (vk === 'ref' && m.ref_types && m.ref_types.length > 0) {
        return t('authoring.member.ref_kind', { roots: m.ref_types.join(', ') });
      }
      return vk;
    })
    .join(', ');
  return t('authoring.member.meta_new', {
    kinds,
    cardinality: t(`authoring.member.cardinality.${m.cardinality}`),
    merge: t(`authoring.member.merge.${m.merge}`),
  });
}

/** Localized label for a tool "thinking" affordance; unknown tools fall back generically. */
function authoringToolLabel(name: string): string {
  const key = `authoring.tool.${name}`;
  const label = t(key);
  return label !== key ? label : t('authoring.tool.generic', { name });
}

/** Localized status word for a tool entry (the text half of the non-color status cue). */
function authoringToolStatus(status: 'started' | 'succeeded' | 'failed'): string {
  return t(`authoring.tool.status.${status}`);
}

/** A message + its position, so the keyed `each` re-uses the streaming assistant row. */
export interface AuthoringThreadItem extends AuthoringMessage {
  idx: number;
}
export function authoringThreadItems(s: Model): AuthoringThreadItem[] {
  return (s.authoringSession?.messages ?? []).map((m, idx) => ({ ...m, idx }));
}

/** A tool entry + its position within a message's trace. */
interface AuthoringToolItem extends AuthoringToolEntry {
  idx: number;
}
function viewAuthoringTools(item: Signal<AuthoringThreadItem>): Mountable {
  // `derived` makes this nested structural source explicitly row-aware under
  // the LLui 0.14 combined row context.
  const tools = derived([item], (message): AuthoringToolItem[] =>
    (message.tools ?? []).map((tool, idx) => ({ ...tool, idx })),
  );
  return show(
    tools.map((entries) => entries.length > 0),
    () => [
      ul({ class: 'authoring-tools', 'aria-label': t('authoring.tools_label') }, [
        each(tools, {
          key: (tool) => tool.idx,
          render: (tool) => [
            li(
              {
                class: 'authoring-tool',
                'data-status': tool.at('status'),
                'data-testid': 'authoring-tool',
              },
              [
                span({ class: 'authoring-tool-label' }, [
                  text(tool.map((entry) => authoringToolLabel(entry.name))),
                ]),
                // The call's `detail` (query / entity id / root), when present
                // — GM insight into the grounding.
                show(
                  tool.map((entry) => entry.detail !== undefined && entry.detail !== ''),
                  () => [
                    span({ class: 'authoring-tool-detail' }, [
                      text(
                        tool.map((entry) =>
                          t('authoring.tool_detail', { detail: entry.detail ?? '' }),
                        ),
                      ),
                    ]),
                  ],
                ),
                span({ class: 'authoring-tool-status' }, [
                  text(tool.map((entry) => authoringToolStatus(entry.status))),
                ]),
              ],
            ),
          ],
        }),
      ]),
    ],
  );
}

/** A rendered member row of the live artifact, pre-resolved with its diff class. */
export interface AuthoringMemberItem {
  idx: number;
  ref: string;
  name: string;
  meta: string;
  isNew: boolean;
  change: 'added' | 'changed' | 'unchanged';
}
export function authoringMemberItems(s: Model): AuthoringMemberItem[] {
  const sess = s.authoringSession;
  if (!sess || sess.artifact === null) return [];
  const diff = sess.diff;
  return sess.artifact.proposed.members.map((m, idx) => {
    const ref = authoringMemberRef(m);
    return {
      idx,
      ref,
      name: m.kind === 'existing' ? predicateLabel(m.id) : resolveLocalizedDisplay(m.name),
      meta: authoringMemberMeta(m),
      isNew: m.kind === 'new',
      change: authoringMemberChange(diff, ref) ?? 'unchanged',
    };
  });
}

/** A member the current turn REMOVED — rendered from the diff (it's gone from `proposed`). */
export interface AuthoringRemovedMemberItem {
  idx: number;
  ref: string;
  label: string;
}
export function authoringRemovedMemberItems(s: Model): AuthoringRemovedMemberItem[] {
  const diff = s.authoringSession?.diff ?? null;
  if (diff === null) return [];
  return diff.member_changes
    .filter((mc: ArtifactMemberChange) => mc.change === 'removed')
    .map((mc, idx) => ({
      idx,
      ref: mc.ref,
      label: mc.ref.startsWith('new:')
        ? mc.ref.slice('new:'.length)
        : predicateLabel(mc.ref),
    }));
}

/** Auditor advisories from a refused commit, positioned for a keyed `each`. */
export interface AuthoringAdvisoryItem {
  idx: number;
  severity: 'blocking_error' | 'warning' | 'info';
  code: string;
  message: string;
}
export function authoringAdvisoryItems(s: Model): AuthoringAdvisoryItem[] {
  return (s.authoringSession?.advisories ?? []).map((a, idx) => ({
    idx,
    severity: a.severity,
    code: a.code,
    message: a.message,
  }));
}

/** True when ≥1 advisory blocks the commit (drives the "fix or cancel" banner). */
export function authoringHasBlocking(s: Model): boolean {
  return (s.authoringSession?.advisories ?? []).some((a) => a.severity === 'blocking_error');
}

/** The glyph paired with each diff class — the non-color half of the a11y cue. */
const AUTHORING_CHANGE_GLYPH: Record<'added' | 'removed' | 'changed', string> = {
  added: '+',
  removed: '−',
  changed: '~',
};

/**
 * The trait-authoring panel. Mounted (as a `show`) only while a session is open;
 * inside, every read tolerates a `null` session/artifact so a half-open session
 * (created but no artifact yet) renders cleanly. The `modal` primitive gives the
 * focus trap, Escape-dismiss, and — via the global `.dl-modal` phone rule — the
 * bottom-sheet reflow below 480px for free.
 */
function viewAuthoringPanel(state: Signal<Model>, send: Send<Msg>): Mountable {
  return modal({
    open: state.map((s) => s.authoringSession !== null),
    onDismiss: () => send({ type: 'close-authoring' }),
    labels: { close: t('authoring.close') },
    labelId: 'authoring-heading',
    describedById: 'authoring-subtitle',
    testid: 'authoring-panel',
    panelClass: 'authoring-panel',
    size: 'lg',
    children: [
      h2({ id: 'authoring-heading', class: 'authoring-title' }, [text(t('authoring.title'))]),
      p({ id: 'authoring-subtitle', class: 'authoring-subtitle' }, [text(t('authoring.subtitle'))]),

      // ── Conversation thread ────────────────────────────────────────────
      div(
        {
          class: 'authoring-thread',
          role: 'log',
          'aria-label': t('authoring.thread_label'),
          'aria-live': 'polite',
          'data-testid': 'authoring-thread',
        },
        [
          show(
            state.map((s) => authoringThreadItems(s).length === 0),
            () => [
              p({ class: 'authoring-thread-empty', 'data-testid': 'authoring-thread-empty' }, [
                text(t('authoring.empty_thread')),
              ]),
            ],
          ),
          each(state.map((s) => authoringThreadItems(s)), {
            key: (item) => item.idx,
            render: (item) => [
              div(
                {
                  class: item.map((m) => `authoring-msg authoring-msg--${m.role}`),
                  'data-role': item.at('role'),
                  'data-testid': 'authoring-msg',
                },
                [
                  span({ class: 'authoring-msg-role' }, [
                    text(
                      item.map((m) =>
                        m.role === 'user' ? t('authoring.role.you') : t('authoring.role.assistant'),
                      ),
                    ),
                  ]),
                  // Tool "thinking" affordances for this turn (assistant only).
                  viewAuthoringTools(item),
                  // Streamed / final message text.
                  p({ class: 'authoring-msg-text' }, [text(item.at('text'))]),
                ],
              ),
            ],
          }),
          // Live "thinking" indicator while a turn streams.
          show(
            state.map((s) => s.authoringSession?.phase === 'streaming'),
            () => [
              p({ class: 'authoring-streaming', role: 'status', 'data-testid': 'authoring-streaming' }, [
                span({ class: 'authoring-streaming-dots', 'aria-hidden': 'true' }, ['•••']),
                text(t('authoring.thinking')),
              ]),
            ],
          ),
        ],
      ),

      // ── Live trait artifact card ───────────────────────────────────────
      show(
        state.map((s) => s.authoringSession?.artifact == null),
        () => [
          p({ class: 'authoring-artifact-empty', 'data-testid': 'authoring-artifact-empty' }, [
            text(t('authoring.artifact.empty')),
          ]),
        ],
        () => [viewAuthoringArtifactCard(state)],
      ),

      // ── Auditor advisories (refused commit) ────────────────────────────
      show(
        state.map((s) => authoringAdvisoryItems(s).length > 0),
        () => [viewAuthoringAdvisories(state)],
      ),

      // ── Turn / commit error ────────────────────────────────────────────
      show(
        state.map((s) => s.authoringSession?.error != null && s.authoringSession.error !== ''),
        () => [
          div({ class: 'authoring-error', role: 'alert', 'data-testid': 'authoring-error' }, [
            span({ class: 'authoring-error-icon', 'aria-hidden': 'true' }, ['⚠']),
            span({ class: 'authoring-error-label' }, [text(t('authoring.error_label'))]),
            span({ class: 'authoring-error-text' }, [
              text(state.map((s) => s.authoringSession?.error ?? '')),
            ]),
          ]),
        ],
      ),

      // ── Direction input ────────────────────────────────────────────────
      label({ for: 'authoring-direction', class: 'authoring-direction-label' }, [
        text(t('authoring.direction_label')),
      ]),
      textArea({
        className: 'authoring-direction',
        testid: 'authoring-direction',
        rows: 2,
        placeholder: t('authoring.direction_placeholder'),
        value: state.map((s) => s.authoringSession?.pendingDirection ?? ''),
        ariaLabel: t('authoring.direction_label'),
        // Enabled between turns; disabled only while a turn streams (ADR-0008 §1).
        disabled: state.map((s) => s.authoringSession?.phase === 'streaming'),
        onInput: (value: string) => send({ type: 'authoring-direction-input', value }),
        attrs: { id: 'authoring-direction' },
      }),

      // ── Actions ────────────────────────────────────────────────────────
      div({ class: 'authoring-actions' }, [
        // Close / cancel — also the in-flight Stop affordance (aborts the SSE
        // turn via `close-authoring` → `cancel-authoring`).
        uiButton({
          variant: 'ghost',
          testid: 'authoring-cancel',
          onClick: () => send({ type: 'close-authoring' }),
          children: [
            text(
              state.map((s) =>
                s.authoringSession?.phase === 'streaming' ? t('authoring.stop') : t('authoring.close'),
              ),
            ),
          ],
        }),
        div({ class: 'authoring-actions-primary' }, [
          uiButton({
            variant: 'secondary',
            testid: 'authoring-send',
            disabled: state.map((s) => {
              const sess = s.authoringSession;
              return (
                sess == null ||
                sess.sessionId === null ||
                sess.phase === 'streaming' ||
                sess.pendingDirection.trim() === ''
              );
            }),
            onClick: () => send({ type: 'authoring-send-turn' }),
            children: [text(t('authoring.send'))],
          }),
          uiButton({
            variant: 'primary',
            testid: 'authoring-commit',
            // Needs a materialised artifact; blocked while a turn or commit runs.
            disabled: state.map((s) => {
              const sess = s.authoringSession;
              return sess == null || sess.artifact === null || sess.phase !== 'idle';
            }),
            onClick: () => send({ type: 'authoring-commit' }),
            children: [
              text(
                state.map((s) =>
                  s.authoringSession?.phase === 'committing'
                    ? t('authoring.committing')
                    : t('authoring.commit'),
                ),
              ),
            ],
          }),
        ]),
      ]),
      p({ class: 'authoring-commit-hint' }, [text(t('authoring.commit_hint'))]),
    ],
  });
}

/** The live trait-artifact card — name / description / applies_to / members, diff-lit. */
function viewAuthoringArtifactCard(state: Signal<Model>): Mountable {
  return card({
    className: 'authoring-artifact',
    testid: 'authoring-artifact',
    children: [
      h3({ class: 'authoring-artifact-heading' }, [text(t('authoring.artifact.heading'))]),

      // Name.
      div(
        {
          class: 'authoring-field',
          'data-changed': state.map((s) =>
            authoringFieldChanged(s.authoringSession?.diff ?? null, 'name') ? 'true' : 'false',
          ),
          'data-testid': 'authoring-field-name',
        },
        [
          span({ class: 'authoring-field-label' }, [
            text(t('authoring.artifact.name_label')),
            authoringFieldChangeMark(state, 'name'),
          ]),
          span({ class: 'authoring-field-value authoring-artifact-name' }, [
            text(
              state.map((s) =>
                s.authoringSession?.artifact
                  ? resolveLocalizedDisplay(s.authoringSession.artifact.proposed.name)
                  : '',
              ),
            ),
          ]),
        ],
      ),

      // Description.
      div(
        {
          class: 'authoring-field',
          'data-changed': state.map((s) =>
            authoringFieldChanged(s.authoringSession?.diff ?? null, 'description') ? 'true' : 'false',
          ),
          'data-testid': 'authoring-field-description',
        },
        [
          span({ class: 'authoring-field-label' }, [
            text(t('authoring.artifact.description_label')),
            authoringFieldChangeMark(state, 'description'),
          ]),
          span({ class: 'authoring-field-value' }, [
            text(
              state.map((s) => {
                const d = s.authoringSession?.artifact
                  ? resolveLocalizedDisplay(s.authoringSession.artifact.proposed.description)
                  : '';
                return d !== '' ? d : t('authoring.artifact.no_description');
              }),
            ),
          ]),
        ],
      ),

      // Applies-to roots.
      div(
        {
          class: 'authoring-field',
          'data-changed': state.map((s) =>
            authoringFieldChanged(s.authoringSession?.diff ?? null, 'applies_to') ? 'true' : 'false',
          ),
          'data-testid': 'authoring-field-applies-to',
        },
        [
          span({ class: 'authoring-field-label' }, [
            text(t('authoring.artifact.applies_to_label')),
            authoringFieldChangeMark(state, 'applies_to'),
          ]),
          ul({ class: 'authoring-roots' }, [
            each(
              state.map((s) =>
                (s.authoringSession?.artifact?.proposed.applies_to ?? []).map((root, idx) => ({
                  root,
                  idx,
                })),
              ),
              {
                key: (item) => item.root,
                render: (item) => [
                  li({ class: 'authoring-root', 'data-testid': 'authoring-root' }, [
                    text(item.map((r) => t(`entity.root.${r.root}`))),
                  ]),
                ],
              },
            ),
          ]),
        ],
      ),

      // Member predicates.
      div({ class: 'authoring-field authoring-members' }, [
        span({ class: 'authoring-field-label' }, [text(t('authoring.artifact.members_label'))]),
        show(
          state.map(
            (s) =>
              authoringMemberItems(s).length === 0 && authoringRemovedMemberItems(s).length === 0,
          ),
          () => [
            p({ class: 'authoring-members-empty' }, [text(t('authoring.artifact.no_members'))]),
          ],
        ),
        ul({ class: 'authoring-member-list', 'data-testid': 'authoring-member-list' }, [
          each(state.map((s) => authoringMemberItems(s)), {
            key: (item) => item.ref,
            render: (item) => [
              li(
                {
                  class: 'authoring-member',
                  'data-change': item.at('change'),
                  'data-testid': 'authoring-member',
                },
                [
                  // The change mark is emitted ONLY when the member changed, so
                  // an unchanged member carries no empty/hidden decoration.
                  show(
                    item.map((m) => m.change !== 'unchanged'),
                    () => [
                      span(
                        {
                          class: item.map(
                            (m) => `authoring-change-mark authoring-change-mark--${m.change}`,
                          ),
                          'data-testid': 'authoring-member-change',
                        },
                        [
                          span({ class: 'authoring-change-glyph', 'aria-hidden': 'true' }, [
                            text(
                              item.map((m) =>
                                m.change === 'added'
                                  ? AUTHORING_CHANGE_GLYPH.added
                                  : AUTHORING_CHANGE_GLYPH.changed,
                              ),
                            ),
                          ]),
                          // Non-color text cue for the change class.
                          text(item.map((m) => t(`authoring.change.${m.change}`))),
                        ],
                      ),
                    ],
                  ),
                  span({ class: 'authoring-member-name' }, [text(item.at('name'))]),
                  show(
                    item.at('isNew'),
                    () => [
                      span({ class: 'authoring-member-tag', 'data-testid': 'authoring-member-new' }, [
                        text(t('authoring.member.new')),
                      ]),
                    ],
                  ),
                  span({ class: 'authoring-member-meta' }, [text(item.at('meta'))]),
                ],
              ),
            ],
          }),
          // Removed members — shown struck-through with the "Removed" cue.
          each(state.map((s) => authoringRemovedMemberItems(s)), {
            key: (item) => `removed:${item.ref}`,
            render: (item) => [
              li(
                {
                  class: 'authoring-member authoring-member--removed',
                  'data-change': 'removed',
                  'data-testid': 'authoring-member-removed',
                },
                [
                  span({ class: 'authoring-change-mark authoring-change-mark--removed' }, [
                    span({ class: 'authoring-change-glyph', 'aria-hidden': 'true' }, [
                      text(AUTHORING_CHANGE_GLYPH.removed),
                    ]),
                    text(t('authoring.change.removed')),
                  ]),
                  span({ class: 'authoring-member-name' }, [text(item.at('label'))]),
                ],
              ),
            ],
          }),
        ]),
      ]),
    ],
  });
}

/** A small "changed this turn" mark next to a field label (glyph + SR text). */
function authoringFieldChangeMark(state: Signal<Model>, path: string): Mountable {
  return show(
    state.map((s) => authoringFieldChanged(s.authoringSession?.diff ?? null, path)),
    () => [
      span({ class: 'authoring-change-mark authoring-change-mark--changed', 'data-testid': 'authoring-field-mark' }, [
        span({ class: 'authoring-change-glyph', 'aria-hidden': 'true' }, [text(AUTHORING_CHANGE_GLYPH.changed)]),
        text(t('authoring.change.field')),
      ]),
    ],
  );
}

/** The auditor-advisory block on a refused commit (blocking vs warning distinguished). */
function viewAuthoringAdvisories(state: Signal<Model>): Mountable {
  return div(
    {
      class: 'authoring-advisories',
      role: 'alert',
      'data-testid': 'authoring-advisories',
    },
    [
      h3({ class: 'authoring-advisories-heading' }, [text(t('authoring.advisories_heading'))]),
      show(
        state.map((s) => authoringHasBlocking(s)),
        () => [
          p({ class: 'authoring-advisories-blocked', 'data-testid': 'authoring-advisories-blocked' }, [
            text(t('authoring.advisories_blocked')),
          ]),
        ],
      ),
      ul({ class: 'authoring-advisory-list' }, [
        each(state.map((s) => authoringAdvisoryItems(s)), {
          key: (item) => item.idx,
          render: (item) => [
            li(
              {
                class: item.map((a) => `authoring-advisory authoring-advisory--${a.severity}`),
                'data-severity': item.at('severity'),
                'data-testid': 'authoring-advisory',
              },
              [
                span(
                  {
                    class: 'authoring-advisory-tag',
                    'data-testid': 'authoring-advisory-tag',
                  },
                  [
                    span({ class: 'authoring-advisory-glyph', 'aria-hidden': 'true' }, [
                      text(item.map((a) => (a.severity === 'blocking_error' ? '⛔' : a.severity === 'warning' ? '⚠' : 'ℹ'))),
                    ]),
                    // Non-color text label for the severity.
                    text(item.map((a) => t(`authoring.advisory.${a.severity}`))),
                  ],
                ),
                span({ class: 'authoring-advisory-message' }, [text(item.at('message'))]),
              ],
            ),
          ],
        }),
      ]),
    ],
  );
}

function viewDraftInbox(state: Signal<Model>, send: Send<Msg>): Mountable {
  return main({ role: 'main', 'aria-labelledby': 'drafts-heading' }, [
    appRouteLink(
      send,
      { kind: 'list' },
      { 'data-testid': 'back-link' },
      [text(`← ${t('entity.back')}`)],
      navMsg,
    ),
    // #266 — in flat mode the inbox is reframed as "Session prep" rather than
    // a "GM inbox". The title and onboarding copy adapt reactively; the
    // underlying ratify/reject actions are unchanged (they still emit GM-authored
    // ops; LLM-never-canon invariant holds in all modes).
    pageHeader({
      title: text(state.map((s) =>
        s.config.facilitation_mode === 'flat'
          ? t('inbox.flat.title')
          : t('inbox.title'),
      )),
      titleId: 'drafts-heading',
    }),
    // #122 — queue-depth lives in its OWN region. It's the count
    // announcement (aria-live="polite") and is updated by
    // setupDraftInbox once the replica resolves how many drafts are
    // waiting. It is NOT the empty-state copy.
    div(
      {
        role: 'status',
        'aria-live': 'polite',
        class: 'inbox-queue-depth',
        id: 'inbox-queue-depth',
        'data-testid': 'inbox-queue-depth',
      },
      [
        text(state.map((s) => draftRows(s).length > 0 ? t('inbox.queue_depth', { n: draftRows(s).length }) : '',)),
      ],
    ),
    p({ class: 'inbox-empty-onboarding' }, [
      text(state.map((s) =>
        s.config.facilitation_mode === 'flat'
          ? t('inbox.flat.onboarding')
          : t('inbox.onboarding'),
      )),
    ]),
    // #251 — suggestion-set bundle review. Open bulk-gen bundles render ABOVE
    // the loose drafts, each as ONE reviewable unit (§14.1). GM-only by
    // construction (the whole inbox is a GM surface).
    viewBundleReview(state, send),
    // Empty state — drop `role="list"` (a list with no items fails axe's
    // aria-required-children). While the substrate is still backfilling
    // we say "Syncing drafts…" rather than "none waiting".
    show(
      state.map((s) => draftRows(s).length === 0),
      () => [
        div({ id: 'inbox-list', 'data-testid': 'inbox-list' }, [
          emptyState({
            testid: 'inbox-empty',
            attrs: {
              'data-thinking': state.map((s) => (aiDraftInFlight(s) > 0 ? 'true' : 'false')),
            },
            message: text(state.map((s) => aiDraftInFlight(s) > 0
                ? t('llm.generating', { n: aiDraftInFlight(s) })
                : s.syncing
                  ? t('inbox.empty_syncing')
                  // #266 — flat mode uses "Session prep" framing for the empty state.
                  : s.config.facilitation_mode === 'flat'
                    ? t('inbox.flat.empty')
                    : t('inbox.empty'),)),
          }),
        ]),
      ],
    ),
    // Populated — one row per draft fact, rendered with h.each (llui-safe:
    // stable key, field accessors, no `.map()` spread, no item.current()).
    show(
      state.map((s) => draftRows(s).length > 0),
      () => [
        ul(
          { id: 'inbox-list', role: 'list', class: 'inbox-list', 'data-testid': 'inbox-list' },
          [each(state.map((s) => draftRows(s)), {
        key: (row) => row.key,
        render: (item) => [
              li(
                {
                  // Draft semantic styling (30 §3.3/§7.1) — left rail in
                  // --draft-text; data-author tints the provenance gutter.
                  class: 'inbox-row',
                  'data-testid': 'inbox-row',
                  'data-predicate': item.at('predicate'),
                  'data-author': item.at('author'),
                },
                [
                  div({ class: 'inbox-row-head' }, [
                    // Subject entity — icon tile (per-root, remounted via a
                    // keyed single-row each so entityIcon gets a literal root) +
                    // Spectral name + predicate as the mono meta line. Mirrors
                    // the @dl/ui entity-card structure.
                    div({ class: 'inbox-entity-card' }, [
                      span(
                        {
                          class: 'inbox-entity-icon',
                          'data-root': item.at('root'),
                          'aria-hidden': 'true',
                        },
                        [
                          // entityIcon needs a literal root; branch on the
                          // closed root enum so each arm supplies it.
                          branch(item.at('root'), {
                            location: () => [entityIcon('location', { size: 14 })],
                            character: () => [entityIcon('character', { size: 14 })],
                            faction: () => [entityIcon('faction', { size: 14 })],
                            event: () => [entityIcon('event', { size: 14 })],
                            item: () => [entityIcon('item', { size: 14 })],
                            lore: () => [entityIcon('lore', { size: 14 })],
                          }),
                        ],
                      ),
                      div({ class: 'inbox-entity-body' }, [
                        span({ class: 'inbox-entity' }, [text(item.at('entityName'))]),
                        span({ class: 'inbox-predicate' }, [text(item.at('predicateLabel'))]),
                      ]),
                    ]),
                    // Status + provenance chips, right-aligned.
                    div({ class: 'inbox-row-chips' }, [
                      statusPill({ kind: 'draft', label: t('inbox.status.draft') }),
                      // Provenance badge — built inline so data-kind/text stay
                      // per-row reactive (the atom takes a static kind). Sparkles
                      // for AI (30 §5); the label carries the meaning (a11y).
                      span(
                        {
                          class: 'dl-provenance-badge inbox-provenance-chip',
                          'data-kind': item.at('author'),
                          'data-author': item.at('author'),
                          'data-testid': 'inbox-provenance-chip',
                        },
                        [
                          show(item.map((r) => r.author === 'llm'), () => [sparklesIcon()]),
                          text(item.at('authorLabel')),
                        ],
                      ),
                    ]),
                  ]),
                  // Asset drafts render a thumbnail; rich-text drafts render
                  // their markdown as formatted HTML; everything else renders
                  // the plain value. All three are present; data-kind + CSS
                  // pick which shows (a per-item h.show can't read `item`).
                  div(
                    {
                      class: 'inbox-body',
                      'data-kind': item.map((r) => r.isAsset ? 'asset' : r.isRichText ? 'rich_text' : 'text',),
                    },
                    [
                      img({
                        class: 'inbox-thumb',
                        'data-testid': 'inbox-thumb',
                        alt: item.map((r) => t('inbox.image_alt', { predicate: r.predicateLabel })),
                        src: item.at('assetUrl'),
                      }),
                      div(
                        { class: 'inbox-value inbox-value-rich', 'data-testid': 'inbox-value-rich' },
                        [markdown(item.at('value'))],
                      ),
                      p({ class: 'inbox-value', 'data-testid': 'inbox-value' }, [text(item.at('value'))]),
                    ],
                  ),
                  show(
                    item.map((r) => r.succeedsNote !== ''),
                    () => [
                      p(
                        { class: 'inbox-succeeds', 'data-testid': 'inbox-succeeds' },
                        [text(item.at('succeedsNote'))],
                      ),
                    ],
                  ),
                  // #164 — pre-emit auditor advisories the draft introduces,
                  // shown inline at review. Editorial: they annotate, never gate.
                  show(
                    item.map((r) => r.advisories.length > 0),
                    () => [
                      ul(
                        {
                          class: 'inbox-advisories',
                          role: 'list',
                          'data-testid': 'inbox-advisories',
                        },
                        [
                          each(
                            item.at('advisories'),
                            {
                              key: (a) => a.key,
                              render: (adv) => [
                                li(
                                  {
                                    class: 'inbox-advisory',
                                    'data-testid': 'inbox-advisory',
                                    'data-severity': adv.at('severity'),
                                  },
                                  [
                                    span({ class: 'inbox-advisory-severity' }, [
                                      text(adv.at('severityLabel')),
                                    ]),
                                    span({ class: 'inbox-advisory-message' }, [
                                      text(adv.at('message')),
                                    ]),
                                  ],
                                ),
                              ],
                            },
                          ),
                        ],
                      ),
                    ],
                  ),
                  div({ class: 'inbox-row-actions' }, [
                    uiButton({
                      variant: 'primary',
                      testid: 'inbox-ratify',
                      ariaLabel: item.map((r) => t('inbox.row_label', { entity: r.entityName, predicate: r.predicateLabel }),),
                      onClick: () => {
                        const id = item.at('entityId').peek();
                        const p = item.at('predicate').peek();
                        if (typeof id === 'string' && typeof p === 'string')
                          send({ type: 'ratify-draft', entityId: id, predicate: p });
                      },
                      children: [text(t('inbox.ratify'))],
                    }),
                    uiButton({
                      variant: 'danger',
                      testid: 'inbox-reject',
                      onClick: () => {
                        const id = item.at('entityId').peek();
                        const p = item.at('predicate').peek();
                        if (typeof id === 'string' && typeof p === 'string')
                          send({ type: 'reject-draft', entityId: id, predicate: p });
                      },
                      children: [text(t('inbox.reject'))],
                    }),
                  ]),
                ],
              ),
            ],
      })],
        ),
      ],
    ),
  ]);
}

// ---------- component ----------

export const App = component<Model, Msg, Effect>({
  name: 'App',
  init,
  update,
  // Inline arrow (not a shorthand identifier reference) so
  // @llui/compiler's `injectViewBag` pass emits the `__view` factory.
  // Without an inline arrow / function expression the compiler skips
  // `__view` synthesis but still stamps `__compilerVersion`, and the
  // runtime then throws "missing __view despite being compiled".
  view: ({ state, send }) => view(state, send),
  onEffect,
});
